import path from 'path';
import EventEmitter from 'events';

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
var SAMErrorCode;
(function (SAMErrorCode) {
    /* Unknown error. Should never be thrown for SAM.js */
    SAMErrorCode[SAMErrorCode["UNKNOWN"] = 1] = "UNKNOWN";
    /* Happens if user's device has insufficient space available for operation
     * User should make some space available and retry
     */
    SAMErrorCode[SAMErrorCode["FULL_LOCAL_DISK_SPACE"] = 2] = "FULL_LOCAL_DISK_SPACE";
    /* Happens if allocated cloud space to user is full.
     * User should be purchasing extra cloud space/ permanently deleting
       some assets, and retry
     */
    SAMErrorCode[SAMErrorCode["FULL_CLOUD_STORAGE"] = 3] = "FULL_CLOUD_STORAGE";
    /* Happens if no internet connection */
    SAMErrorCode[SAMErrorCode["DEVICE_OFFLINE"] = 4] = "DEVICE_OFFLINE";
    /* Happens if network is down */
    SAMErrorCode[SAMErrorCode["NETWORK_ERROR"] = 5] = "NETWORK_ERROR";
    /* Happens if user request is taking longer than expected.
     * Possible reason: backend service being in transient state
       (getting updated), flaky connections or bad network
     */
    SAMErrorCode[SAMErrorCode["REQUEST_TIMED_OUT"] = 6] = "REQUEST_TIMED_OUT";
    /* Happens if given request failed as it was cancelled by user */
    SAMErrorCode[SAMErrorCode["USER_CANCELLED"] = 7] = "USER_CANCELLED";
    /* Happens if there is some error related to coresync
     * There are associated sub-codes relating to this error
     */
    SAMErrorCode[SAMErrorCode["CORESYNC_FAILURE"] = 8] = "CORESYNC_FAILURE";
    /**
     * CoreSync is in a paused state.
     */
    SAMErrorCode[SAMErrorCode["SYNC_PAUSED"] = 9] = "SYNC_PAUSED";
    /* Happens if registration failed as more than permissible users are
       simultaneously logged in Coresync */
    SAMErrorCode[SAMErrorCode["USER_ALREADY_REGISTERED"] = 10] = "USER_ALREADY_REGISTERED";
    /* Happens if an asset operation is denied as there were uncommited
       changes for an asset, done from same app */
    SAMErrorCode[SAMErrorCode["PENDING_UNCOMMITTED_CHANGES_FROM_THIS_APP"] = 11] = "PENDING_UNCOMMITTED_CHANGES_FROM_THIS_APP";
    /* Happens if an asset operation is denied as there were uncommited
       changes for an asset, done from different app */
    SAMErrorCode[SAMErrorCode["PENDING_UNCOMMITTED_CHANGES_FROM_ANOTHER_APP"] = 12] = "PENDING_UNCOMMITTED_CHANGES_FROM_ANOTHER_APP";
    /* Happens if some error has occurred while processing user request
     * that might succeed upon retry (now or later). Client or end user
     * may retry again. Other error codes may also be retryable, this is
     * just a catch all for the leftovers.
     */
    SAMErrorCode[SAMErrorCode["INTERNAL_ERROR"] = 13] = "INTERNAL_ERROR";
    /* Happens if a request cannot be cancelled - Cancellation request gets
       ignored */
    SAMErrorCode[SAMErrorCode["OPERATION_IGNORED"] = 14] = "OPERATION_IGNORED";
    /**
     * Indicates that the request could not be processed because of conflict
     * in the current state of the resource.
     * <br>Client Action: Resolve conflict and try again.
     */
    SAMErrorCode[SAMErrorCode["CONFLICT"] = 15] = "CONFLICT";
    /**
     * Happens when interacting with an asset the user no longer has access to.
     */
    SAMErrorCode[SAMErrorCode["ACCESS_DENIED"] = 16] = "ACCESS_DENIED";
    /* Happens if user is trying a feature that is not currently supported */
    SAMErrorCode[SAMErrorCode["UNSUPPORTED"] = 17] = "UNSUPPORTED";
    /* Happens if user is attempting an operation that cannot be performed
       because the asset is not in sync [e.g. unpin] */
    SAMErrorCode[SAMErrorCode["ASSET_NOT_IN_SYNC"] = 18] = "ASSET_NOT_IN_SYNC";
    /* Happens when the request is malformed or the operation is not allowed
       on the asset for e.g. purging a folder */
    SAMErrorCode[SAMErrorCode["BAD_REQUEST"] = 19] = "BAD_REQUEST";
    /**
     * Happens if asset failed to sync.
     * There are associated sub-codes relating to this error which may have
     * more information
     */
    SAMErrorCode[SAMErrorCode["SYNC_ERROR"] = 20] = "SYNC_ERROR";
    /**
     * Happens if requests is made for an asset that cannot be found.
     */
    SAMErrorCode[SAMErrorCode["NOT_FOUND"] = 21] = "NOT_FOUND";
    /**
     * Happens when there is authorization error while performing the request
     * for e.g. if requests are made which require user to be registered with
     * ACPL, but user is not registered.
     */
    SAMErrorCode[SAMErrorCode["AUTHORIZATION_ERROR"] = 22] = "AUTHORIZATION_ERROR";
    /**
     * Happens while performing an invalid operation for e.g. when attempting
     * to migrate an asset to RepoNaming that has already been migrated
     */
    SAMErrorCode[SAMErrorCode["INVALID_OPERATION"] = 23] = "INVALID_OPERATION";
    /**
     * Happens when a method is called on a asset that require that the
     * asset have a context, but none is present or the asset does not
     * belong to specified context.
     */
    SAMErrorCode[SAMErrorCode["INVALID_CONTEXT"] = 24] = "INVALID_CONTEXT";
    /**
     * An error occured during committing changes.
     * Error sub-code may have more information around this
     */
    SAMErrorCode[SAMErrorCode["COMMIT_ERROR"] = 25] = "COMMIT_ERROR";
    /**
     *  Parent not found for the asset
     */
    SAMErrorCode[SAMErrorCode["PARENT_NOT_FOUND"] = 26] = "PARENT_NOT_FOUND";
    /**
     * Parent folder is discarded
     */
    SAMErrorCode[SAMErrorCode["PARENT_DISCARDED"] = 27] = "PARENT_DISCARDED";
    /**
     * Project limit exceeded
     */
    SAMErrorCode[SAMErrorCode["PROJECT_LIMIT_EXCEEDED"] = 28] = "PROJECT_LIMIT_EXCEEDED";
    /**
     * The user or organization is not provisioned
     * for the requested service access.
     */
    SAMErrorCode[SAMErrorCode["SERVICE_NOT_PROVISIONED"] = 29] = "SERVICE_NOT_PROVISIONED";
})(SAMErrorCode || (SAMErrorCode = {}));
var SAMErrorSubCode;
(function (SAMErrorSubCode) {
    /* No sub-error */
    SAMErrorSubCode[SAMErrorSubCode["NONE"] = 0] = "NONE";
    /* Happens if Coresync service is not running, could not be launched,
     * or is not responding */
    SAMErrorSubCode[SAMErrorSubCode["CORESYNC_NOT_REACHABLE"] = 1] = "CORESYNC_NOT_REACHABLE";
    /* Happens if CoreSync is not installed/found or it is running but
      incompatible (lesser than lowest supported version) */
    SAMErrorSubCode[SAMErrorSubCode["CORESYNC_NOT_COMPATIBLE"] = 2] = "CORESYNC_NOT_COMPATIBLE";
    /**
     * Happens if CoreSync binary is not present.  Ask end user install coresync
     */
    SAMErrorSubCode[SAMErrorSubCode["CORESYNC_BINARY_NOT_FOUND"] = 3] = "CORESYNC_BINARY_NOT_FOUND";
    /**
     * Happens if CoreSync is not installed. Ask end user to install/upgrade coresync.
     */
    SAMErrorSubCode[SAMErrorSubCode["CORESYNC_NOT_INSTALLED"] = 4] = "CORESYNC_NOT_INSTALLED";
    /**
     * Happens if asset failed to sync. Sync will be retried by ACPL.
     * Cient may wait for subsequent sync notification for the asset or retry
     * the operation.
     */
    SAMErrorSubCode[SAMErrorSubCode["SYNC_FAILED"] = 5] = "SYNC_FAILED";
    /**
     * Syncing the asset failed due to an EditEdit conflict, but the conflict
     * was resolved
     */
    SAMErrorSubCode[SAMErrorSubCode["SYNC_FAILED_EDIT_EDIT_RESOLVED"] = 6] = "SYNC_FAILED_EDIT_EDIT_RESOLVED";
    /**
     * Syncing the asset failed due to an EditDelete conflict, but the conflict
     * was resolved
     */
    SAMErrorSubCode[SAMErrorSubCode["SYNC_FAILED_EDIT_DELETE_RESOLVED"] = 7] = "SYNC_FAILED_EDIT_DELETE_RESOLVED";
    /**
     * An error occured while moving an orphan to the root folder.
     */
    SAMErrorSubCode[SAMErrorSubCode["COMMIT_ON_ORPHAN_ERROR"] = 8] = "COMMIT_ON_ORPHAN_ERROR";
    /**
     * Admin disabled creation of Projects
     * https://wiki.corp.adobe.com/pages/viewpage.action?spaceKey=assetscollab&title=Proposal%3A+Project+API+V3+-+Capability
     */
    SAMErrorSubCode[SAMErrorSubCode["PROJECTS_ADMIN_ONLY"] = 9] = "PROJECTS_ADMIN_ONLY";
    /**
     * 404 specific projects errors.
     * https://wiki.corp.adobe.com/pages/viewpage.action?spaceKey=assetscollab&title=Proposal%3A+Project+API+V3+-+Capability
     */
    SAMErrorSubCode[SAMErrorSubCode["PROJECTS_INVALID_REPOSITORY"] = 10] = "PROJECTS_INVALID_REPOSITORY";
    SAMErrorSubCode[SAMErrorSubCode["PROJECTS_NO_HOME_REPOSITORY"] = 11] = "PROJECTS_NO_HOME_REPOSITORY";
})(SAMErrorSubCode || (SAMErrorSubCode = {}));
const errorCodeInfos = new Map([
    [401, { code: SAMErrorCode.AUTHORIZATION_ERROR, message: (href) => `Authorization error: ${href}` }],
    [403, { code: SAMErrorCode.ACCESS_DENIED, message: (href) => `Access to resource denied: ${href}` }],
    [404, { code: SAMErrorCode.NOT_FOUND, message: (href) => `Resource not found: ${href}` }],
    [409, { code: SAMErrorCode.CONFLICT, message: () => `Resource has a naming conflict.` }],
    [412, { code: SAMErrorCode.CONFLICT, message: () => `Resource has a conflict. Etag mismatch.` }],
]);
/**
 * Convert server error codes to regular HTTP number codes.
 * '4041' -> 404
 * '404.1' -> 404
 * 4041 -> 404
 * 404 -> 404
 *
 * @param code from server
 * @returns http error code
 */
function validateRepositoryCode(code) {
    // Repo APIs return string codes instead of numbers. In that case
    if (typeof code === 'string') {
        code = parseInt(code);
    }
    // Remove any extra floating point.
    code = Math.floor(code);
    // HTTP error codes only use 3 digits, but repo APIs can have even 5 digits.
    // We only care about the most significant 3 digits though.
    // https://git.corp.adobe.com/DMA/dcx-lib/blob/729085f1ed77936a1718377ea6cd5e3b16f6aada/lib/dcxlib/src/main/java/com/adobe/dcx/storagesession/StorageErrorCode.java#L12
    while (code > 999) {
        code = Math.floor(code / 10);
    }
    return code;
}
var ProblemType;
(function (ProblemType) {
    ProblemType["LIMIT_CHANGELOGRETENTION"] = "http://ns.adobe.com/adobecloud/problem/limit/changelogretention";
    ProblemType["OPERATION_FAILED"] = "adobecloud/problem/operation_failed";
    ProblemType["SUBSCRIPTION_COUNT"] = "http://ns.adobe.com/adobecloud/problem/limit/subscriptioncount";
    ProblemType["QUOTA_EXCEEDED"] = "http://ns.adobe.com/adobecloud/problem/quotaexceeded";
    ProblemType["CHILDREN_LIMIT_EXCEEDED"] = "http://ns.adobe.com/adobecloud/problem/limit/childrencount";
    ProblemType["NO_HOME_REPOSITORY"] = "http://ns.adobe.com/projects/problem/no-home-repository";
    ProblemType["INVALID_HOME_REPOSITORY"] = "http://ns.adobe.com/projects/problem/invalid-repository";
    ProblemType["ADMIN_ONLY"] = "http://ns.adobe.com/projects/problem/admin-only";
    ProblemType["ACCESS_DENIED"] = "http://ns.adobe.com/adobecloud/problem/accessdenied";
})(ProblemType || (ProblemType = {}));
function formatUnderlyingError(underlyingError) {
    let errorStr = '';
    if (underlyingError !== null && underlyingError !== undefined) {
        try {
            errorStr = ` -> ${String(underlyingError)}`;
        }
        catch (_a) {
        }
    }
    return errorStr;
}
const SAMErrorName = 'SAMError';
/**
 * SAM.js would always throw back SAMError even when it is powered via SAM.cpp
 * The clients must rely only on `code` and `subCode` in order to write their business logic.
 * `description` can be used for logging purposes.
 * `_additionalInfo` should only be used for debugging purpose and there should not be any reference
 * to it in the code base.
 */
class SAMError extends Error {
    constructor(code, description, additionalInfo = {}, subCode) {
        // NOTE: clients should not rely on SAMError.message for fine-grain error handling; instead, use `code` and `subcode`.
        super(`[${SAMErrorCode[code]}${subCode === undefined ? '' : '-' + SAMErrorSubCode[subCode]}] ${description}${formatUnderlyingError(additionalInfo.underlyingError)}`);
        this.code = code;
        this.subCode = subCode;
        this.description = description;
        this._additionalInfo = additionalInfo;
    }
    get name() {
        return SAMErrorName;
    }
    static wrapErrorIfNeeded(code, description, underlyingError, subCode) {
        if (underlyingError instanceof SAMError) {
            return underlyingError;
        }
        return new SAMError(code, description, { underlyingError }, subCode);
    }
    static create(code, description, additionalInfo, subCode) {
        return new SAMError(code, description, additionalInfo, subCode);
    }
    static createWithResponse(response, href) {
        var _a, _b;
        const { status } = response;
        const info = errorCodeInfos.get(validateRepositoryCode(status));
        const responseUrl = href !== null && href !== void 0 ? href : response.url;
        const reqId = response.headers.get('X-Request-Id') || '';
        return new SAMError((_a = info === null || info === void 0 ? void 0 : info.code) !== null && _a !== void 0 ? _a : SAMErrorCode.UNKNOWN, ((_b = info === null || info === void 0 ? void 0 : info.message(href !== null && href !== void 0 ? href : responseUrl)) !== null && _b !== void 0 ? _b : `Unexpected response status: ${status} for ${responseUrl}`)
            + ` reqId: ${reqId}`, { response, reqId }, info === null || info === void 0 ? void 0 : info.subCode);
    }
    static createWithResponseAndDescription(response, description) {
        var _a;
        const info = errorCodeInfos.get(validateRepositoryCode(response.status));
        return new SAMError((_a = info === null || info === void 0 ? void 0 : info.code) !== null && _a !== void 0 ? _a : SAMErrorCode.UNKNOWN, description, { response }, info === null || info === void 0 ? void 0 : info.subCode);
    }
    static createWithRepositoryCode(code, problemType, description) {
        var _a;
        // Look out for Quota Exceeded errors.
        // RAPI returns 403 with problemType = http://ns.adobe.com/adobecloud/problem/quotaexceeded for such errors.
        // We want to return a more specific error code for this case.
        if (problemType === ProblemType.QUOTA_EXCEEDED) {
            return new SAMError(SAMErrorCode.FULL_CLOUD_STORAGE, description, { problemType });
        }
        const validHTTPCode = validateRepositoryCode(code);
        const info = code ? errorCodeInfos.get(validHTTPCode) : undefined;
        return new SAMError((_a = info === null || info === void 0 ? void 0 : info.code) !== null && _a !== void 0 ? _a : SAMErrorCode.UNKNOWN, description, { problemType }, info === null || info === void 0 ? void 0 : info.subCode);
    }
}
/**
 * Throws SAMAssertionError when `assertedCondition` is false.
 * @param assertedCondition
 * @param message
 */
function assert(assertedCondition, message) {
    if (!assertedCondition) {
        throw SAMError.create(SAMErrorCode.UNKNOWN, message);
    }
}
/**
 * Helper function used to accurately determine if an error
 * is a SAMError and not a plain Error.
 *
 * @param error An Error type object
 * @returns
 */
function isSAMError(error) {
    return error && error.name === SAMErrorName;
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __param(paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const KeysProperty = Symbol('@key parameterMap');
// @key decorator is used to indicate the argument to the @cache and @synchronized decorators
var key = (target, propertyKey, parameterIndex) => {
    const map = target[KeysProperty] = target[KeysProperty] || new Map;
    if (map.has(propertyKey)) {
        throw SAMError.create(SAMErrorCode.BAD_REQUEST, '@key cannot be applied to more than one parameter');
    }
    map.set(propertyKey, parameterIndex);
};

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
/**
 * Caches the result of the method, using the argument decorated with '@key' as the key (multiple arguments can also be
 * used, and they will be appended together). This ensures that the result is cached in memory and shared if multiple
 * people call the API at once. Once the value expires, it will be recomputed.
 *
 * Note:
 * 1) Keys are global, so different cached methods should use different key domains unless it's intended to be shared.
 *    We typically use URLs as keys to avoid such conflicts.
 * 2) Decorators are not passed the default value of parameters, so you can't write f(@key x = 'defaultValue') - you need
 *    to make sure the value is always passed in.
 */
function cacheImpl(validate) {
    return (target, propertyKey, descriptor) => {
        const method = descriptor.value;
        if (!method) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, '@cache decorator can only be applied to a method');
        }
        const newMethod = async function (...args) {
            var _a;
            const keyIndex = (_a = this[KeysProperty]) === null || _a === void 0 ? void 0 : _a.get(propertyKey);
            if (keyIndex === undefined) {
                throw SAMError.create(SAMErrorCode.BAD_REQUEST, '@cache decorator requires one of the parameters to be labelled with @key');
            }
            const key = args[keyIndex];
            return this._core.environment.cache.getItem(key, () => method.apply(this, args), validate);
        };
        descriptor.value = newMethod;
    };
}
function cache(arg1, arg2, arg3) {
    if (!arg2) {
        return cacheImpl(arg1);
    }
    // No parameter - invoke without validation
    // (allows using @cache without any parameters)
    return cacheImpl()(arg1, arg2, arg3);
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// Memoize the value of a getter or method - this just caches the return value
// forever, so it doesn't get recomputed each time it's called
var memoize = (target, propertyKey, descriptor) => {
    const memo = Symbol(`@memoize ${propertyKey}`);
    const property = descriptor.get ? 'get' : 'value';
    const method = descriptor[property];
    descriptor[property] = function (...args) {
        if (!Object.prototype.hasOwnProperty.call(this, memo)) {
            this[memo] = method.call(this, ...args);
        }
        return this[memo];
    };
};

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
/**
 * Base class for all events sent out from SAMJS.
 */
class SAMEvent {
    constructor(_type) {
        this._type = _type;
    }
    get type() {
        return this._type;
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const CHANGE = 'change';
class ChangeEvent extends SAMEvent {
    constructor() {
        super(CHANGE);
    }
}
const SharedChangeEvent = new ChangeEvent();

const $OBSERVABLE_MAP = Symbol('$OBSERVABLE_MAP');
function getEventForProp(obj, prop) {
    var _a;
    return (_a = (obj[$OBSERVABLE_MAP] || undefined)) === null || _a === void 0 ? void 0 : _a.get(prop);
}
function setEventForProp(obj, prop, event) {
    let map;
    // Detect if a map exists and whether or not it is inherited
    if (Object.getOwnPropertyDescriptor(obj, $OBSERVABLE_MAP)) {
        // not inherited, and it already exists
        map = obj[$OBSERVABLE_MAP];
    }
    else if (obj[$OBSERVABLE_MAP]) {
        // Inherited, and it exists on the parent. The properties of the child class should not be mapped in the parent
        // class, but the child class should have all the mapped properties of the parent class. So create a new map
        // for the child that is a copy of the parent's map.
        map = new Map(obj[$OBSERVABLE_MAP]);
        obj[$OBSERVABLE_MAP] = map;
    }
    else {
        // Not inherited, and it does not exist
        map = new Map();
        obj[$OBSERVABLE_MAP] = map;
    }
    // TODO: warn if event is changed for a prop?
    map.set(prop, event);
}
function observableImpl(event, privatePropNames) {
    return (target, key, descriptor) => {
        if (descriptor.get || descriptor.value) {
            privatePropNames === null || privatePropNames === void 0 ? void 0 : privatePropNames.forEach(prop => setEventForProp(target, prop, event));
        }
        if (descriptor.get) {
            const getter = descriptor.get;
            descriptor.get = function () {
                this._core.environment.mobx.reportObserved(this, event.type);
                return getter.call(this);
            };
        }
        else if (descriptor.value) {
            const method = descriptor.value;
            const newMethod = function (...args) {
                this._core.environment.mobx.reportObserved(this, event.type);
                return method.apply(this, args);
            };
            descriptor.value = newMethod;
        }
        else {
            throw new Error('@observable decorator can only be used with `getters` or `methods`.');
        }
    };
}
function observable(arg1, arg2, arg3) {
    // @observable without parens
    if (arg1 && arg2 && arg3) {
        return observableImpl(SharedChangeEvent)(arg1, arg2, arg3);
    }
    // @observable(SomeEvent)
    // @observable(SomeEvent, '_prop1')
    // @observable(SomeEvent, ['_prop1', '_prop2'])
    if (typeof arg1.type === 'string' && (typeof arg2 === 'string' || typeof arg2 === 'undefined' || Array.isArray(arg2))) {
        arg2 = arg2 ? [].concat(arg2) : undefined;
        return observableImpl(arg1, arg2);
    }
    throw new Error('Invalid arguments for @observable');
}

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

var uriTemplates = {exports: {}};

(function (module) {
(function (global, factory) {
	if (module.exports){
		module.exports = factory();
	} else {
		global.UriTemplate = factory();
	}
})(commonjsGlobal, function () {
	var uriTemplateGlobalModifiers = {
		"+": true,
		"#": true,
		".": true,
		"/": true,
		";": true,
		"?": true,
		"&": true
	};
	var uriTemplateSuffices = {
		"*": true
	};

	function notReallyPercentEncode(string) {
		return encodeURI(string).replace(/%25[0-9][0-9]/g, function (doubleEncoded) {
			return "%" + doubleEncoded.substring(3);
		});
	}

	function isPercentEncoded(string) {
		string = string.replace(/%../g, '');
		return encodeURIComponent(string) === string;
	}

	function uriTemplateSubstitution(spec) {
		var modifier = "";
		if (uriTemplateGlobalModifiers[spec.charAt(0)]) {
			modifier = spec.charAt(0);
			spec = spec.substring(1);
		}
		var separator = "";
		var prefix = "";
		var shouldEscape = true;
		var showVariables = false;
		var trimEmptyString = false;
		if (modifier == '+') {
			shouldEscape = false;
		} else if (modifier == ".") {
			prefix = ".";
			separator = ".";
		} else if (modifier == "/") {
			prefix = "/";
			separator = "/";
		} else if (modifier == '#') {
			prefix = "#";
			shouldEscape = false;
		} else if (modifier == ';') {
			prefix = ";";
			separator = ";",
			showVariables = true;
			trimEmptyString = true;
		} else if (modifier == '?') {
			prefix = "?";
			separator = "&",
			showVariables = true;
		} else if (modifier == '&') {
			prefix = "&";
			separator = "&",
			showVariables = true;
		}

		var varNames = [];
		var varList = spec.split(",");
		var varSpecs = [];
		var varSpecMap = {};
		for (var i = 0; i < varList.length; i++) {
			var varName = varList[i];
			var truncate = null;
			if (varName.indexOf(":") != -1) {
				var parts = varName.split(":");
				varName = parts[0];
				truncate = parseInt(parts[1]);
			}
			var suffices = {};
			while (uriTemplateSuffices[varName.charAt(varName.length - 1)]) {
				suffices[varName.charAt(varName.length - 1)] = true;
				varName = varName.substring(0, varName.length - 1);
			}
			var varSpec = {
				truncate: truncate,
				name: varName,
				suffices: suffices
			};
			varSpecs.push(varSpec);
			varSpecMap[varName] = varSpec;
			varNames.push(varName);
		}
		var subFunction = function (valueFunction) {
			var result = "";
			var startIndex = 0;
			for (var i = 0; i < varSpecs.length; i++) {
				var varSpec = varSpecs[i];
				var value = valueFunction(varSpec.name);
				if (value == null || (Array.isArray(value) && value.length == 0) || (typeof value == 'object' && Object.keys(value).length == 0)) {
					startIndex++;
					continue;
				}
				if (i == startIndex) {
					result += prefix;
				} else {
					result += (separator || ",");
				}
				if (Array.isArray(value)) {
					if (showVariables) {
						result += varSpec.name + "=";
					}
					for (var j = 0; j < value.length; j++) {
						if (j > 0) {
							result += varSpec.suffices['*'] ? (separator || ",") : ",";
							if (varSpec.suffices['*'] && showVariables) {
								result += varSpec.name + "=";
							}
						}
						result += shouldEscape ? encodeURIComponent(value[j]).replace(/!/g, "%21") : notReallyPercentEncode(value[j]);
					}
				} else if (typeof value == "object") {
					if (showVariables && !varSpec.suffices['*']) {
						result += varSpec.name + "=";
					}
					var first = true;
					for (var key in value) {
						if (!first) {
							result += varSpec.suffices['*'] ? (separator || ",") : ",";
						}
						first = false;
						result += shouldEscape ? encodeURIComponent(key).replace(/!/g, "%21") : notReallyPercentEncode(key);
						result += varSpec.suffices['*'] ? '=' : ",";
						result += shouldEscape ? encodeURIComponent(value[key]).replace(/!/g, "%21") : notReallyPercentEncode(value[key]);
					}
				} else {
					if (showVariables) {
						result += varSpec.name;
						if (!trimEmptyString || value != "") {
							result += "=";
						}
					}
					if (varSpec.truncate != null) {
						value = value.substring(0, varSpec.truncate);
					}
					result += shouldEscape ? encodeURIComponent(value).replace(/!/g, "%21"): notReallyPercentEncode(value);
				}
			}
			return result;
		};
		var guessFunction = function (stringValue, resultObj, strict) {
			if (prefix) {
				stringValue = stringValue.substring(prefix.length);
			}
			if (varSpecs.length == 1 && varSpecs[0].suffices['*']) {
				var varSpec = varSpecs[0];
				var varName = varSpec.name;
				var arrayValue = varSpec.suffices['*'] ? stringValue.split(separator || ",") : [stringValue];
				var hasEquals = (shouldEscape && stringValue.indexOf('=') != -1);	// There's otherwise no way to distinguish between "{value*}" for arrays and objects
				for (var i = 1; i < arrayValue.length; i++) {
					var stringValue = arrayValue[i];
					if (hasEquals && stringValue.indexOf('=') == -1) {
						// Bit of a hack - if we're expecting "=" for key/value pairs, and values can't contain "=", then assume a value has been accidentally split
						arrayValue[i - 1] += (separator || ",") + stringValue;
						arrayValue.splice(i, 1);
						i--;
					}
				}
				for (var i = 0; i < arrayValue.length; i++) {
					var stringValue = arrayValue[i];
					if (shouldEscape && stringValue.indexOf('=') != -1) {
						hasEquals = true;
					}
					var innerArrayValue = stringValue.split(",");
					if (innerArrayValue.length == 1) {
						arrayValue[i] = innerArrayValue[0];
					} else {
						arrayValue[i] = innerArrayValue;
					}
				}

				if (showVariables || hasEquals) {
					var objectValue = resultObj[varName] || {};
					for (var j = 0; j < arrayValue.length; j++) {
						var innerValue = stringValue;
						if (showVariables && !innerValue) {
							// The empty string isn't a valid variable, so if our value is zero-length we have nothing
							continue;
						}
						if (typeof arrayValue[j] == "string") {
							var stringValue = arrayValue[j];
							var innerVarName = stringValue.split("=", 1)[0];
							var stringValue = stringValue.substring(innerVarName.length + 1);
							if (shouldEscape) {
								if (strict && !isPercentEncoded(stringValue)) {
									return;
								}
								stringValue = decodeURIComponent(stringValue);
							}
							innerValue = stringValue;
						} else {
							var stringValue = arrayValue[j][0];
							var innerVarName = stringValue.split("=", 1)[0];
							var stringValue = stringValue.substring(innerVarName.length + 1);
							if (shouldEscape) {
								if (strict && !isPercentEncoded(stringValue)) {
									return;
								}
								stringValue = decodeURIComponent(stringValue);
							}
							arrayValue[j][0] = stringValue;
							innerValue = arrayValue[j];
						}
						if (shouldEscape) {
							if (strict && !isPercentEncoded(innerVarName)) {
								return;
							}
							innerVarName = decodeURIComponent(innerVarName);
						}

						if (objectValue[innerVarName] !== undefined) {
							if (Array.isArray(objectValue[innerVarName])) {
								objectValue[innerVarName].push(innerValue);
							} else {
								objectValue[innerVarName] = [objectValue[innerVarName], innerValue];
							}
						} else {
							objectValue[innerVarName] = innerValue;
						}
					}
					if (Object.keys(objectValue).length == 1 && objectValue[varName] !== undefined) {
						resultObj[varName] = objectValue[varName];
					} else {
						resultObj[varName] = objectValue;
					}
				} else {
					if (shouldEscape) {
						for (var j = 0; j < arrayValue.length; j++) {
							var innerArrayValue = arrayValue[j];
							if (Array.isArray(innerArrayValue)) {
								for (var k = 0; k < innerArrayValue.length; k++) {
									if (strict && !isPercentEncoded(innerArrayValue[k])) {
										return;
									}
									innerArrayValue[k] = decodeURIComponent(innerArrayValue[k]);
								}
							} else {
								if (strict && !isPercentEncoded(innerArrayValue)) {
									return;
								}
								arrayValue[j] = decodeURIComponent(innerArrayValue);
							}
						}
					}

					if (resultObj[varName] !== undefined) {
						if (Array.isArray(resultObj[varName])) {
							resultObj[varName] = resultObj[varName].concat(arrayValue);
						} else {
							resultObj[varName] = [resultObj[varName]].concat(arrayValue);
						}
					} else {
						if (arrayValue.length == 1 && !varSpec.suffices['*']) {
							resultObj[varName] = arrayValue[0];
						} else {
							resultObj[varName] = arrayValue;
						}
					}
				}
			} else {
				var arrayValue = (varSpecs.length == 1) ? [stringValue] : stringValue.split(separator || ",");
				var specIndexMap = {};
				for (var i = 0; i < arrayValue.length; i++) {
					// Try from beginning
					var firstStarred = 0;
					for (; firstStarred < varSpecs.length - 1 && firstStarred < i; firstStarred++) {
						if (varSpecs[firstStarred].suffices['*']) {
							break;
						}
					}
					if (firstStarred == i) {
						// The first [i] of them have no "*" suffix
						specIndexMap[i] = i;
						continue;
					} else {
						// Try from the end
						for (var lastStarred = varSpecs.length - 1; lastStarred > 0 && (varSpecs.length - lastStarred) < (arrayValue.length - i); lastStarred--) {
							if (varSpecs[lastStarred].suffices['*']) {
								break;
							}
						}
						if ((varSpecs.length - lastStarred) == (arrayValue.length - i)) {
							// The last [length - i] of them have no "*" suffix
							specIndexMap[i] = lastStarred;
							continue;
						}
					}
					// Just give up and use the first one
					specIndexMap[i] = firstStarred;
				}
				for (var i = 0; i < arrayValue.length; i++) {
					var stringValue = arrayValue[i];
					if (!stringValue && showVariables) {
						// The empty string isn't a valid variable, so if our value is zero-length we have nothing
						continue;
					}
					var innerArrayValue = stringValue.split(",");
					var hasEquals = false;

					if (showVariables) {
						var stringValue = innerArrayValue[0]; // using innerArrayValue
						var varName = stringValue.split("=", 1)[0];
						var stringValue = stringValue.substring(varName.length + 1);
						innerArrayValue[0] = stringValue;
						var varSpec = varSpecMap[varName] || varSpecs[0];
					} else {
						var varSpec = varSpecs[specIndexMap[i]];
						var varName = varSpec.name;
					}

					for (var j = 0; j < innerArrayValue.length; j++) {
						if (shouldEscape) {
							if (strict && !isPercentEncoded(innerArrayValue[j])) {
								return;
							}
							innerArrayValue[j] = decodeURIComponent(innerArrayValue[j]);
						}
					}

					if ((showVariables || varSpec.suffices['*'])&& resultObj[varName] !== undefined) {
						if (Array.isArray(resultObj[varName])) {
							resultObj[varName] = resultObj[varName].concat(innerArrayValue);
						} else {
							resultObj[varName] = [resultObj[varName]].concat(innerArrayValue);
						}
					} else {
						if (innerArrayValue.length == 1 && !varSpec.suffices['*']) {
							resultObj[varName] = innerArrayValue[0];
						} else {
							resultObj[varName] = innerArrayValue;
						}
					}
				}
			}
			return 1;
		};
		return {
			varNames: varNames,
			prefix: prefix,
			substitution: subFunction,
			unSubstitution: guessFunction
		};
	}

	function UriTemplate(template) {
		if (!(this instanceof UriTemplate)) {
			return new UriTemplate(template);
		}
		var parts = template.split("{");
		var textParts = [parts.shift()];
		var prefixes = [];
		var substitutions = [];
		var unSubstitutions = [];
		var varNames = [];
		while (parts.length > 0) {
			var part = parts.shift();
			var spec = part.split("}")[0];
			var remainder = part.substring(spec.length + 1);
			var funcs = uriTemplateSubstitution(spec);
			substitutions.push(funcs.substitution);
			unSubstitutions.push(funcs.unSubstitution);
			prefixes.push(funcs.prefix);
			textParts.push(remainder);
			varNames = varNames.concat(funcs.varNames);
		}
		this.fill = function (valueFunction) {
			if (valueFunction && typeof valueFunction !== 'function') {
				var value = valueFunction;
				valueFunction = function (varName) {
					return value[varName];
				};
			}

			var result = textParts[0];
			for (var i = 0; i < substitutions.length; i++) {
				var substitution = substitutions[i];
				result += substitution(valueFunction);
				result += textParts[i + 1];
			}
			return result;
		};
		this.fromUri = function (substituted, options) {
			options = options || {};
			var result = {};
			for (var i = 0; i < textParts.length; i++) {
				var part = textParts[i];
				if (substituted.substring(0, part.length) !== part) {
					return /*undefined*/;
				}
				substituted = substituted.substring(part.length);
				if (i >= textParts.length - 1) {
					// We've run out of input - is there any template left?
					if (substituted == "") {
						break;
					} else {
						return /*undefined*/;
					}
				}

				var prefix = prefixes[i];
				if (prefix && substituted.substring(0, prefix.length) !== prefix) {
					// All values are optional - if we have a prefix and it doesn't match, move along
					continue;
				}

				// Find the next part to un-substitute
				var nextPart = textParts[i + 1];
				var offset = i;
				while (true) {
					if (offset == textParts.length - 2) {
						var endPart = substituted.substring(substituted.length - nextPart.length);
						if (endPart !== nextPart) {
							return /*undefined*/;
						}
						var stringValue = substituted.substring(0, substituted.length - nextPart.length);
						substituted = endPart;
					} else if (nextPart) {
						var nextPartPos = substituted.indexOf(nextPart);
						var stringValue = substituted.substring(0, nextPartPos);
						substituted = substituted.substring(nextPartPos);
					} else if (prefixes[offset + 1]) {
						var nextPartPos = substituted.indexOf(prefixes[offset + 1]);
						if (nextPartPos === -1) nextPartPos = substituted.length;
						var stringValue = substituted.substring(0, nextPartPos);
						substituted = substituted.substring(nextPartPos);
					} else if (textParts.length > offset + 2) {
						// If the separator between this variable and the next is blank (with no prefix), continue onwards
						offset++;
						nextPart = textParts[offset + 1];
						continue;
					} else {
						var stringValue = substituted;
						substituted = "";
					}
					break;
				}
				if (!unSubstitutions[i](stringValue, result, options.strict)) {
					return /*undefined*/;
				}
			}
			return result;
		};
		this.varNames = varNames;
		this.template = template;
	}
	UriTemplate.prototype = {
		toString: function () {
			return this.template;
		},
		fillFromObject: function (obj) {
			return this.fill(obj);
		},
		test: function (uri, options) {
			return !!this.fromUri(uri, options)
		}
	};

	return UriTemplate;
});
}(uriTemplates));

var UriTemplate = uriTemplates.exports;

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const JSON_MIME_TYPE = 'application/json';
const JSON_PATCH_MIME_TYPE = 'application/json-patch+json';
var RelType;
(function (RelType) {
    // Repository API
    RelType["PRIMARY"] = "http://ns.adobe.com/adobecloud/rel/primary";
    RelType["ID"] = "http://ns.adobe.com/adobecloud/rel/id";
    RelType["PATH"] = "http://ns.adobe.com/adobecloud/rel/path";
    RelType["RENDITION"] = "http://ns.adobe.com/adobecloud/rel/rendition";
    RelType["COMPONENT_RENDITION"] = "http://ns.adobe.com/adobecloud/rel/componentRendition";
    RelType["MANIFEST"] = "http://ns.adobe.com/adobecloud/rel/manifest";
    RelType["QUOTA"] = "http://ns.adobe.com/adobecloud/rel/quota";
    RelType["COMPONENT"] = "http://ns.adobe.com/adobecloud/rel/component";
    RelType["PAGE"] = "http://ns.adobe.com/adobecloud/rel/page";
    RelType["DISCARD"] = "http://ns.adobe.com/adobecloud/rel/discard";
    RelType["RESTORE"] = "http://ns.adobe.com/adobecloud/rel/restore";
    RelType["DIRECTORY"] = "http://ns.adobe.com/adobecloud/rel/directory";
    RelType["DIRECTORY_BASE"] = "http://ns.adobe.com/adobecloud/rel/directory/base";
    RelType["AC_EFFECTIVE"] = "http://ns.adobe.com/adobecloud/rel/ac/effective";
    RelType["AC_POLICY"] = "http://ns.adobe.com/adobecloud/rel/ac/policy";
    RelType["AC_CHECK"] = "http://ns.adobe.com/adobecloud/rel/ac/check";
    RelType["CREATE"] = "http://ns.adobe.com/adobecloud/rel/create";
    RelType["REPOSITORY"] = "http://ns.adobe.com/adobecloud/rel/repository";
    RelType["RESOLVE_ID"] = "http://ns.adobe.com/adobecloud/rel/resolve/id";
    RelType["RESOLVE_PATH"] = "http://ns.adobe.com/adobecloud/rel/resolve/path";
    RelType["OPS"] = "http://ns.adobe.com/adobecloud/rel/ops";
    RelType["BLOCK_INITIALIZE"] = "http://ns.adobe.com/adobecloud/rel/block/init";
    RelType["BLOCK_TRANSFER"] = "http://ns.adobe.com/adobecloud/rel/block/transfer";
    RelType["BLOCK_FINALIZE"] = "http://ns.adobe.com/adobecloud/rel/block/finalize";
    RelType["REPO_METADATA"] = "http://ns.adobe.com/adobecloud/rel/metadata/repository";
    RelType["APP_METADATA"] = "http://ns.adobe.com/adobecloud/rel/metadata/application";
    RelType["EMBEDDED_METADATA"] = "http://ns.adobe.com/adobecloud/rel/metadata/embedded";
    RelType["PERUSER_METADATA"] = "http://ns.adobe.com/adobecloud/rel/metadata/peruser";
    RelType["ANNOTATIONS"] = "http://ns.adobe.com/adobecloud/rel/annotations";
    RelType["DOWNLOAD"] = "http://ns.adobe.com/adobecloud/rel/download";
    RelType["DESCRIBED_BY"] = "describedBy";
    RelType["VERSION"] = "version-history";
    // ONS
    RelType["SUBSCRIPTION_CREATE"] = "subscription/create";
    RelType["NOTIFICATIONS_LONGPOLL"] = "notifications/longpoll";
    RelType["ONS_CHANGELOG"] = "ons/changelog";
    // Projects API
    RelType["REPOSITORIES_PAGE"] = "http://ns.adobe.com/repositories/rel/page";
    RelType["TEAMS_CREATE"] = "http://ns.adobe.com/teams/rel/create";
    RelType["TEAMS_DISCARD"] = "http://ns.adobe.com/teams/rel/discard";
    RelType["TEAMS_RESTORE"] = "http://ns.adobe.com/teams/rel/restore";
    RelType["TEAMS_RENAME"] = "http://ns.adobe.com/teams/rel/rename";
    RelType["TEAMS_PROPERTIES"] = "http://ns.adobe.com/teams/rel/properties";
    RelType["TEAMS_PAGE"] = "http://ns.adobe.com/teams/rel/page";
    RelType["TEAMS_PRIMARY"] = "http://ns.adobe.com/teams/rel/primary";
    RelType["TEAM_PROVISION"] = "http://ns.adobe.com/teams/rel/provision";
    RelType["WORKSPACES_CONTEXT"] = "http://ns.adobe.com/ccprojects/rel/context";
    RelType["WORKSPACES_BOOKMARKS_BATCH"] = "http://ns.adobe.com/ccprojects/rel/bookmarks/batch";
    RelType["WORKSPACES_PRIMARY"] = "http://ns.adobe.com/ccprojects/rel/primary";
    RelType["WORKSPACES_CREATE"] = "http://ns.adobe.com/ccprojects/rel/create";
    RelType["WORKSPACES_PAGE"] = "http://ns.adobe.com/ccprojects/rel/page";
    RelType["PROJECTS_LIBRARIES_PAGE"] = "http://ns.adobe.com/ccprojects/rel/libraries/page";
    RelType["PROJECTS_LIBRARIES_CREATE"] = "http://ns.adobe.com/ccprojects/rel/libraries/create";
    RelType["PROJECTS_LIBRARIES_BATCH"] = "http://ns.adobe.com/ccprojects/rel/libraries/batch";
    RelType["GENERIC_PAGE"] = "page";
    RelType["GENERIC_NEXT"] = "next";
    // Asset Transfer Service
    RelType["ATS_MOVE"] = "http://ns.adobe.com/ats/rel/move";
    // Review Service
    RelType["REVIEW_SERVICE_PROVISION"] = "http://ns.adobe.com/ccassetreview/rel/provision";
    RelType["REVIEW_SERVICE_DISCARD"] = "http://ns.adobe.com/ccassetreview/rel/discard";
    RelType["REVIEW_SERVICE_OUTPUTS"] = "http://ns.adobe.com/ccassetreview/rel/outputs";
    RelType["REVIEW_SERIVCE_HAS_OUTPUTS"] = "http://ns.adobe.com/ccassetreview/rel/hasoutputs";
    // Projects service v3
    RelType["PROJECTS_CONTEXT"] = "http://ns.adobe.com/projects/rel/context";
    RelType["PROJECTS_PRIMARY"] = "http://ns.adobe.com/projects/rel/primary";
    RelType["PROJECTS_COLLECTION"] = "http://ns.adobe.com/projects/rel/collection";
    RelType["PROJECTS_CREATE"] = "http://ns.adobe.com/projects/rel/create";
    RelType["PROJECTS_RENAME"] = "http://ns.adobe.com/projects/rel/rename";
    RelType["PROJECTS_CREATOR"] = "http://ns.adobe.com/projects/rel/creator";
    RelType["PROJECTS_GRADIENTS_COLLECTION"] = "http://ns.adobe.com/gradients/rel/collection";
    RelType["PROJECTS_CAPABILITY"] = "http://ns.adobe.com/projects/rel/capability";
    // Assignment service
    RelType["ASSIGNMENTS_ASSIGN"] = "http://ns.adobe.com/assignments/rel/assign";
    RelType["ASSIGNMENTS_COLLECTION"] = "http://ns.adobe.com/assignments/rel/collection";
    RelType["ASSIGNMENTS_CREATE"] = "http://ns.adobe.com/assignments/rel/create";
    RelType["ASSIGNMENTS_PRIMARY"] = "http://ns.adobe.com/assignments/rel/primary";
    RelType["ASSIGNMENTS_RETURN"] = "http://ns.adobe.com/assignments/rel/return";
    RelType["ASSIGNMENTS_START"] = "http://ns.adobe.com/assignments/rel/start";
    RelType["ASSIGNMENTS_SUBMIT"] = "http://ns.adobe.com/assignments/rel/submit";
})(RelType || (RelType = {}));
function validateLinkParameters(links, key, params) {
    try {
        const templateString = getTemplateFromLinks(links, key);
        const template = UriTemplate(templateString);
        // Check that required arguments are available.
        return params.every((param) => template.varNames.indexOf(param) !== -1);
    }
    catch (_a) {
        // We only care if the link is valid. If it is not valid, then we will return false,
        // so that our caches could be invalidated correctly.
        return false;
    }
}
function validateLinks(links, requirements) {
    if (links === undefined) {
        return true;
    }
    if (Object(links) !== links) {
        return false;
    }
    // All links should have an href
    const hasMissingHrefs = Object.keys(links).some(key => {
        const link = links[key];
        if (!Array.isArray(link)) {
            return typeof link.href !== 'string';
        }
        return link.some(({ href }) => typeof href !== 'string');
    });
    if (hasMissingHrefs) {
        return false;
    }
    if (requirements) {
        const keys = Object.keys(requirements);
        const hasAllParameters = keys.every((key) => validateLinkParameters(links, key, requirements[key]));
        if (!hasAllParameters) {
            return false;
        }
    }
    return true;
}
function getLink(links, key) {
    let link = links && links[key];
    if (!link || (Array.isArray(link) && link.length === 0)) {
        // The callers of these methods would fail without an actual URL.
        // It is better to fail early by throwing a proper exception.
        throw SAMError.create(SAMErrorCode.BAD_REQUEST, `Missing link for relationship type: ${key}.`);
    }
    if (Array.isArray(link)) {
        link = link[0];
    }
    return link;
}
function getTemplateFromLinks(links, key) {
    return getLink(links, key).href;
}
function getURLFromLinks(links, key, params = {}) {
    const templateString = getTemplateFromLinks(links, key);
    const template = UriTemplate(templateString);
    return template && template.fill(params);
}
const RETRY_RANDOM_SECONDS = 30; // Choose random seconds between [0,x] for retry-after as a date
function getRetryAfterHeader(headers) {
    const retryAfterHeader = headers && headers.get('Retry-After');
    if (retryAfterHeader) {
        try {
            // First, try to parse it as a number (retry time in seconds)
            const retryTime = parseInt(retryAfterHeader, 10);
            if (retryTime) {
                return Math.max(0, retryTime);
            }
            // If that fails, try to parse it as a date
            const retryDate = Date.parse(retryAfterHeader);
            if (retryDate) {
                // Need to add a randomised element to ensure requests don't all come back at the same time
                const now = Date.now();
                const retrySeconds = Math.max(0, retryDate - now) / 1000;
                const retryRandom = Math.floor(Math.random() * RETRY_RANDOM_SECONDS);
                return retrySeconds + retryRandom;
            }
        }
        catch (e) {
            // ignore
        }
    }
    return 0;
}

var COMPATIBLE_ENCODING_PATTERN = /^utf-?8|ascii|utf-?16-?le|ucs-?2|base-?64|latin-?1$/i;
var WS_TRIM_PATTERN = /^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g;
var WS_CHAR_PATTERN = /\s|\uFEFF|\xA0/;
var WS_FOLD_PATTERN = /\r?\n[\x20\x09]+/g;
var DELIMITER_PATTERN = /[;,"]/;
var WS_DELIMITER_PATTERN = /[;,"]|\s/;

/**
 * Token character pattern
 * @type {RegExp}
 * @see https://tools.ietf.org/html/rfc7230#section-3.2.6
 */
var TOKEN_PATTERN = /^[!#$%&'*+\-\.^_`|~\da-zA-Z]+$/;

var STATE = {
  IDLE: 1 << 0,
  URI: 1 << 1,
  ATTR: 1 << 2,
};

function trim( value ) {
  return value.replace( WS_TRIM_PATTERN, '' )
}

function hasWhitespace( value ) {
  return WS_CHAR_PATTERN.test( value )
}

function skipWhitespace( value, offset ) {
  while( hasWhitespace( value[offset] ) ) {
    offset++;
  }
  return offset
}

function needsQuotes( value ) {
  return WS_DELIMITER_PATTERN.test( value ) ||
    !TOKEN_PATTERN.test( value )
}

/**
 * Shallow compares two objects to check if their properties match.
 * @param {object} object1 First object to compare.
 * @param {object} object2 Second object to compare.
 * @returns {boolean} Do the objects have matching properties.
 */
function shallowCompareObjects( object1, object2 ) {
  return (
    Object.keys( object1 ).length === Object.keys( object2 ).length &&
    Object.keys( object1 ).every(
      ( key ) => key in object2 && object1[ key ] === object2[ key ]
    )
  );
}

class Link {

  /**
   * Link
   * @constructor
   * @param {String} [value]
   * @returns {Link}
   */
  constructor( value ) {

    /** @type {Array} URI references */
    this.refs = [];

    if( value ) {
      this.parse( value );
    }

  }

  /**
   * Get refs with given relation type
   * @param {String} value
   * @returns {Array<Object>}
   */
  rel( value ) {

    var links = [];
    var type = value.toLowerCase();

    for( var i = 0; i < this.refs.length; i++ ) {
      if( this.refs[ i ].rel.toLowerCase() === type ) {
        links.push( this.refs[ i ] );
      }
    }

    return links

  }

  /**
   * Get refs where given attribute has a given value
   * @param {String} attr
   * @param {String} value
   * @returns {Array<Object>}
   */
  get( attr, value ) {

    attr = attr.toLowerCase();

    var links = [];

    for( var i = 0; i < this.refs.length; i++ ) {
      if( this.refs[ i ][ attr ] === value ) {
        links.push( this.refs[ i ] );
      }
    }

    return links

  }

  /** Sets a reference. */
  set( link ) {
    this.refs.push( link );
    return this
  }

  /**
   * Sets a reference if a reference with similar properties isn’t already set.
   */
  setUnique( link ) {

    if( !this.refs.some(( ref ) => shallowCompareObjects( ref, link )) ) {
      this.refs.push( link );
    }

    return this

  }

  has( attr, value ) {

    attr = attr.toLowerCase();

    for( var i = 0; i < this.refs.length; i++ ) {
      if( this.refs[ i ][ attr ] === value ) {
        return true
      }
    }

    return false

  }

  parse( value, offset ) {

    offset = offset || 0;
    value = offset ? value.slice( offset ) : value;

    // Trim & unfold folded lines
    value = trim( value ).replace( WS_FOLD_PATTERN, '' );

    var state = STATE.IDLE;
    var length = value.length;
    var offset = 0;
    var ref = null;

    while( offset < length ) {
      if( state === STATE.IDLE ) {
        if( hasWhitespace( value[offset] ) ) {
          offset++;
          continue
        } else if( value[offset] === '<' ) {
          if( ref != null ) {
            ref.rel != null ?
              this.refs.push( ...Link.expandRelations( ref ) ) :
              this.refs.push( ref );
          }
          var end = value.indexOf( '>', offset );
          if( end === -1 ) throw new Error( 'Expected end of URI delimiter at offset ' + offset )
          ref = { uri: value.slice( offset + 1, end ) };
          // this.refs.push( ref )
          offset = end;
          state = STATE.URI;
        } else {
          throw new Error( 'Unexpected character "' + value[offset] + '" at offset ' + offset )
        }
        offset++;
      } else if( state === STATE.URI ) {
        if( hasWhitespace( value[offset] ) ) {
          offset++;
          continue
        } else if( value[offset] === ';' ) {
          state = STATE.ATTR;
          offset++;
        } else if( value[offset] === ',' ) {
          state = STATE.IDLE;
          offset++;
        } else {
          throw new Error( 'Unexpected character "' + value[offset] + '" at offset ' + offset )
        }
      } else if( state === STATE.ATTR ) {
        if( value[offset] ===';' || hasWhitespace( value[offset] ) ) {
          offset++;
          continue
        }
        var end = value.indexOf( '=', offset );
        if( end === -1 ) throw new Error( 'Expected attribute delimiter at offset ' + offset )
        var attr = trim( value.slice( offset, end ) ).toLowerCase();
        var attrValue = '';
        offset = end + 1;
        offset = skipWhitespace( value, offset );
        if( value[offset] === '"' ) {
          offset++;
          while( offset < length ) {
            if( value[offset] === '"' ) {
              offset++; break
            }
            if( value[offset] === '\\' ) {
              offset++;
            }
            attrValue += value[offset];
            offset++;
          }
        } else {
          var end = offset + 1;
          while( !DELIMITER_PATTERN.test( value[end] ) && end < length ) {
            end++;
          }
          attrValue = value.slice( offset, end );
          offset = end;
        }
        if( ref[ attr ] && Link.isSingleOccurenceAttr( attr ) ) ; else if( attr[ attr.length - 1 ] === '*' ) {
          ref[ attr ] = Link.parseExtendedValue( attrValue );
        } else {
          attrValue = attr === 'type' ?
            attrValue.toLowerCase() : attrValue;
          if( ref[ attr ] != null ) {
            if( Array.isArray( ref[ attr ] ) ) {
              ref[ attr ].push( attrValue );
            } else {
              ref[ attr ] = [ ref[ attr ], attrValue ];
            }
          } else {
            ref[ attr ] = attrValue;
          }
        }
        switch( value[offset] ) {
          case ',': state = STATE.IDLE; break
          case ';': state = STATE.ATTR; break
        }
        offset++;
      } else {
        throw new Error( 'Unknown parser state "' + state + '"' )
      }
    }

    if( ref != null ) {
      ref.rel != null ?
        this.refs.push( ...Link.expandRelations( ref ) ) :
        this.refs.push( ref );
    }

    ref = null;

    return this

  }

  toString() {

    var refs = [];
    var link = '';
    var ref = null;

    for( var i = 0; i < this.refs.length; i++ ) {
      ref = this.refs[i];
      link = Object.keys( this.refs[i] ).reduce( function( link, attr ) {
        if( attr === 'uri' ) return link
        return link + '; ' + Link.formatAttribute( attr, ref[ attr ] )
      }, '<' + ref.uri + '>' );
      refs.push( link );
    }

    return refs.join( ', ' )

  }

}

/**
 * Determines whether an encoding can be
 * natively handled with a `Buffer`
 * @param {String} value
 * @returns {Boolean}
 */
Link.isCompatibleEncoding = function( value ) {
  return COMPATIBLE_ENCODING_PATTERN.test( value )
};

Link.parse = function( value, offset ) {
  return new Link().parse( value, offset )
};

Link.isSingleOccurenceAttr = function( attr ) {
  return attr === 'rel' || attr === 'type' || attr === 'media' ||
    attr === 'title' || attr === 'title*'
};

Link.isTokenAttr = function( attr ) {
  return attr === 'rel' || attr === 'type' || attr === 'anchor'
};

Link.escapeQuotes = function( value ) {
  return value.replace( /"/g, '\\"' )
};

Link.expandRelations = function( ref ) {
  var rels = ref.rel.split( ' ' );
  return rels.map( function( rel ) {
    var value = Object.assign( {}, ref );
    value.rel = rel;
    return value
  })
};

/**
 * Parses an extended value and attempts to decode it
 * @internal
 * @param {String} value
 * @return {Object}
 */
Link.parseExtendedValue = function( value ) {
  var parts = /([^']+)?(?:'([^']*)')?(.+)/.exec( value );
  return {
    language: parts[2].toLowerCase(),
    encoding: Link.isCompatibleEncoding( parts[1] ) ?
      null : parts[1].toLowerCase(),
    value: Link.isCompatibleEncoding( parts[1] ) ?
      decodeURIComponent( parts[3] ) : parts[3]
  }
};

/**
 * Format a given extended attribute and it's value
 * @param {String} attr
 * @param {Object} data
 * @return {String}
 */
Link.formatExtendedAttribute = function( attr, data ) {

  var encoding = ( data.encoding || 'utf-8' ).toUpperCase();
  var language = data.language || 'en';

  var encodedValue = '';

  if( Buffer.isBuffer( data.value ) && Link.isCompatibleEncoding( encoding ) ) {
    encodedValue = data.value.toString( encoding );
  } else if( Buffer.isBuffer( data.value ) ) {
    encodedValue = data.value.toString( 'hex' )
      .replace( /[0-9a-f]{2}/gi, '%$1' );
  } else {
    encodedValue = encodeURIComponent( data.value );
  }

  return attr + '=' + encoding + '\'' +
    language + '\'' + encodedValue

};

/**
 * Format a given attribute and it's value
 * @param {String} attr
 * @param {String|Object} value
 * @return {String}
 */
Link.formatAttribute = function( attr, value ) {

  if( Array.isArray( value ) ) {
    return value.map(( item ) => {
      return Link.formatAttribute( attr, item )
    }).join( '; ' )
  }

  if( attr[ attr.length - 1 ] === '*' || typeof value !== 'string' ) {
    return Link.formatExtendedAttribute( attr, value )
  }

  if( Link.isTokenAttr( attr ) ) {
    value = needsQuotes( value ) ?
      '"' + Link.escapeQuotes( value ) + '"' :
      Link.escapeQuotes( value );
  } else if( needsQuotes( value ) ) {
    value = encodeURIComponent( value );
    // We don't need to escape <SP> <,> <;> within quotes
    value = value
      .replace( /%20/g, ' ' )
      .replace( /%2C/g, ',' )
      .replace( /%3B/g, ';' );

    value = '"' + value + '"';
  }

  return attr + '=' + value

};

var link = Link;

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
/**
 * Implements the Response structure used as a return value for
 * the `parseAsyncResponse` method below.
 *
 * The class only supports json() and text(), we should not require more than
 * text encoding for the APIs that support Async requests.
 */
class AsyncResponse {
    constructor(url, headers, status, statusText, _text) {
        this.url = url;
        this.headers = headers;
        this.status = status;
        this.statusText = statusText;
        this._text = _text;
    }
    get ok() {
        const { status } = this;
        return status >= 200 && status <= 299;
    }
    get redirected() {
        return false;
    }
    get type() {
        return 'basic';
    }
    async json() {
        try {
            return JSON.parse(this._text);
        }
        catch (err) {
            throw SAMError.create(SAMErrorCode.INTERNAL_ERROR, `Failed to parse JSON response from '${this.url}' async request.`, { underlyingError: err });
        }
    }
    async text() {
        return this._text;
    }
    get trailer() {
        throw SAMError.create(SAMErrorCode.UNSUPPORTED, 'AsyncResponse.trailer not implemented.');
    }
    clone() {
        const clonedHeaders = new Headers();
        this.headers.forEach((value, key) => clonedHeaders.set(key, value));
        return new AsyncResponse(this.url, clonedHeaders, this.status, this.statusText, this._text);
    }
    get bodyUsed() {
        throw SAMError.create(SAMErrorCode.UNSUPPORTED, 'AsyncResponse.bodyUsed not implemented.');
    }
    get body() {
        throw SAMError.create(SAMErrorCode.UNSUPPORTED, 'AsyncResponse.body not implemented.');
    }
    async arrayBuffer() {
        throw SAMError.create(SAMErrorCode.UNSUPPORTED, 'AsyncResponse.arrayBuffer not implemented.');
    }
    async blob() {
        throw SAMError.create(SAMErrorCode.UNSUPPORTED, 'AsyncResponse.blob not implemented.');
    }
    async formData() {
        throw SAMError.create(SAMErrorCode.UNSUPPORTED, 'AsyncResponse.formData not implemented.');
    }
}
// https://www.w3.org/Protocols/rfc2616/rfc2616-sec2.html#sec2.1
// OCTET          = <any 8-bit sequence of data>
// CHAR           = <any US-ASCII character (octets 0 - 127)>
// CTL            = <any US-ASCII control character
//                  (octets 0 - 31) and DEL (127)>
// SP             = <US-ASCII SP, space (32)>
// HT             = <US-ASCII HT, horizontal-tab (9)>
// separators     = "(" | ")" | "<" | ">" | "@"
//                | "," | ";" | ":" | "\" | <">
//                | "/" | "[" | "]" | "?" | "="
//                | "{" | "}" | SP | HT
// token          = 1*<any CHAR except CTLs or separators>
// A header is just a <token>, the char ':' and the value.
function createHeaderRegexp() {
    const separators = '()<@,;:"/[]={}';
    // Start from the first character that is accepted by the <token>.
    let start = 33;
    const regexp = ['^(['];
    for (let i = 34; i <= 127; ++i) {
        // We know 127 is not accepted, so we use it as a terminator for our regexp segments.
        // None of the separators are allowed.
        if (i === 127 || separators.indexOf(String.fromCharCode(i)) !== -1) {
            regexp.push('\\' + start.toString(8));
            if (start < i - 1) {
                regexp.push('-\\' + (i - 1).toString(8));
            }
            start = i + 1;
        }
    }
    regexp.push(']+):(.*)$');
    return new RegExp(regexp.join(''));
}
const END_OF_LINE = '\n';
const CR = '\r';
const headerRegexp = createHeaderRegexp();
const statusRegexp = /^[^\s]+\s+(\d+)\s*(.*)$/;
class HttpResponseParser {
    constructor(_text) {
        this._text = _text;
        this._index = 0;
    }
    readLine() {
        const { _index, _text } = this;
        // The standard expects lines to end with CRLF by default.
        // However, it recommends to accept a single LF as well,
        // just in case some implementations do not implement the
        // standard correctly.
        // Async responses only use LF.
        const endOfLine = _text.indexOf(END_OF_LINE, _index);
        if (endOfLine === -1) {
            // We're done.
            return;
        }
        this._index = endOfLine + END_OF_LINE.length;
        let line = _text.slice(_index, endOfLine);
        if (line.endsWith(CR)) {
            // If we have the CR just before the LF, then we want to skip it too.
            line = line.slice(0, -1);
        }
        return line;
    }
    get remainderText() {
        return this._text.slice(this._index);
    }
}
/**
 * Implements parsing of the async response body.
 * https://git.corp.adobe.com/pages/caf/api-spec/chapters/advanced/asynchronous_invocation.html
 *
 * The caller should check for status code 202 then call this method to extract the contents
 * of final async result. The actual result is very similar to a nested HTTP reply inside the body
 * of the initial request.
 *
 */
function parseAsyncResponse(originalUrl, body) {
    if (!body) {
        throw SAMError.create(SAMErrorCode.INTERNAL_ERROR, 'Invalid async response body.');
    }
    const parser = new HttpResponseParser(body);
    let status;
    let statusText;
    const statusLine = parser.readLine();
    if (!statusLine) {
        // throw SAMError.create(SAMErrorCode.INTERNAL_ERROR, 'Could not find async response status line.');
        // TODO: R-API does not return the status line for operations api. Any errors are reported in data section.
        // When this is fixed, then missing status line should be an error.
        // https://developers.corp.adobe.com/supplemental-resources/docs/async-invocation.md/#operations-api-exception
        status = 200;
        statusText = '';
    }
    else {
        // The status line has 3 parts separated by spaces.
        // Example status line: HTTP/1.1 200 Status text
        const statusMatch = statusLine.match(statusRegexp);
        if (!statusMatch) {
            throw SAMError.create(SAMErrorCode.INTERNAL_ERROR, 'Invalid status line for async response.');
        }
        // The status code is the second element
        status = parseInt(statusMatch[1], 10);
        if (!status) {
            throw SAMError.create(SAMErrorCode.INTERNAL_ERROR, 'Could not find status code for async response.');
        }
        statusText = statusMatch[2];
    }
    // Now we extract the headers line by line.
    const headers = [];
    let headerName;
    for (;;) {
        const headerLine = parser.readLine();
        if (!headerLine) {
            break;
        }
        const match = headerLine.match(headerRegexp);
        if (!match) {
            if (headerName) {
                // If this is not a token, then we just continue with the value.
                headers.push([headerName, headerLine]);
            }
            continue;
        }
        headerName = match[1];
        headers.push([headerName, match[2]]);
    }
    // 1. The last `readLine()` call above will actually jump over the empty
    // line that separates the headers from the data section.
    // 2. The Headers constructor below takes care of multiple headers with the
    // same name and concatenates them as necessary.
    return new AsyncResponse(originalUrl, new Headers(headers), status, statusText, parser.remainderText);
}

/*************************************************************************
*   ADOBE CONFIDENTIAL
*
*   Copyright 2023 Adobe
*   All Rights Reserved.
*
*   NOTICE:  All information contained herein is, and remains
*   the property of Adobe and its suppliers, if any. The intellectual
*   and technical concepts contained herein are proprietary to Adobe
*   and its suppliers and are protected by all applicable intellectual
*   property laws, including trade secret and copyright laws.
*   Dissemination of this information or reproduction of this material
*   is strictly forbidden unless prior written permission is obtained
*   from Adobe.
**************************************************************************/
function clamp(value, min, max) {
    if (min !== undefined && value < min) {
        return min;
    }
    if (max !== undefined && value > max) {
        return max;
    }
    return value;
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const VALID_HTTP_RESPONSE_CODES = [200];
// See https://git.corp.adobe.com/pages/caf/api-spec/chapters/responses/service_responses.html
const RETRY_HTTP_RESPONSE_CODES = [
    429,
    500,
    // 501 -- Not implemented -- client must not retry
    502, 503, 504 // Upstream service (gateways/proxies) errors -- may be transient, client can retry
    // >=505 -- other non-retryable errors
];
// Make 1 request, then MAX_ERROR_RETRIES retries
const MAX_ERROR_RETRIES = 5;
/**
 * InterfaceBase and its subclasses are abstractions around calling into a service (not to be confused with typescript
 * interfaces). It extends fetch to provide retry and error handling logic.
 */
class InterfaceBase {
    constructor(_core) {
        this._core = _core;
        this._useHighPriorityRequestsIfEnabled = false;
        this._sharedResponses = new Map;
    }
    async _fetch(href, options = {
        successResponseCodes: VALID_HTTP_RESPONSE_CODES
    }, retryCount = 0) {
        const { additionalRetryErrorCodes, successResponseCodes = VALID_HTTP_RESPONSE_CODES } = options, 
        // fetchOptions should only contain IFetchOptions
        fetchOptions = __rest(options, ["additionalRetryErrorCodes", "successResponseCodes"]);
        const retryErrorCodes = [...RETRY_HTTP_RESPONSE_CODES, ...(additionalRetryErrorCodes || [])];
        const overrides = await this._getFetchOptionOverrides(href, fetchOptions);
        let response = await this._core.environment.fetch(overrides.href, overrides.options);
        const { status } = response;
        if (status === 202) {
            response = await this._monitorResponse(response);
        }
        // If the Retry-After header is present, then retry regardless of status code.
        if (status >= 400 && getRetryAfterHeader(response.headers)
            // Always retry the retryErrorCodes
            || retryErrorCodes.includes(status)) {
            if (retryCount < MAX_ERROR_RETRIES) {
                const delay = this._getRetryErrorDelay(response, retryCount);
                this._core.environment.log(`Retryable error ${status} retrying in ${delay}ms, ${retryCount} retries so far`);
                await new Promise(resolve => setTimeout(resolve, delay));
                return await this._fetch(href, options, retryCount + 1);
            }
            else {
                this._core.environment.log(`Retryable error ${status} attempt ${retryCount} exceeded ${MAX_ERROR_RETRIES} retries`);
            }
        }
        if (successResponseCodes !== 'any') {
            this._checkResponseStatus(response, href, successResponseCodes);
        }
        return response;
    }
    _checkResponseStatus(response, href, successResponseCodes) {
        if (!successResponseCodes.includes(response.status)) {
            const error = SAMError.createWithResponse(response, href);
            this._core.environment.error(`InterfaceBase error ${error}`);
            throw error;
        }
    }
    async _monitorResponse(response) {
        let href = response.headers.get('location');
        if (!href) {
            const json = await response.json();
            if (!json) {
                throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Failed to parse content for 202 monitor response.');
            }
            href = json.href;
            if (!href) {
                throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Missing href property for 202 monitor response.');
            }
        }
        const delay = this._getMonitorRetryAfter(response.headers);
        return this._fetchMonitorResponse(response.url, href, delay);
    }
    async _fetchMonitorResponse(originalUrl, asyncUrl, delay) {
        if (delay) {
            // Wait before trying again.
            await new Promise((resolve) => setTimeout(resolve, delay));
        }
        const asyncResponse = await this._core.environment.fetch(asyncUrl);
        const body = await asyncResponse.text();
        const shouldRetry = this._shouldRetryMonitorResponse(asyncResponse, body);
        if (shouldRetry) {
            // Try again later.
            const newDelay = this._getMonitorRetryAfter(asyncResponse.headers);
            return this._fetchMonitorResponse(originalUrl, asyncUrl, newDelay);
        }
        // Parse the response from the async call.
        return parseAsyncResponse(originalUrl, body);
    }
    /**
     * Determines whether or not the response indicates the server-side async process is still underway.
     */
    // Overrides can use `body`
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _shouldRetryMonitorResponse(asyncResponse, body) {
        return asyncResponse.status === 202;
    }
    /**
     * Calculate how long of a delay before re-requesting the status of a server-side async process.
     */
    _getMonitorRetryAfter(headers) {
        return getRetryAfterHeader(headers) * 1000 || this._core.environment.getValue('ASYNC_DEFAULT_DELAY');
    }
    /**
     * Calculate how long of a delay before retrying a request that failed
     */
    _getRetryErrorDelay(response, retryCount) {
        const min = this._core.environment.getValue('MIN_RETRY_INTERVAL');
        const max = this._core.environment.getValue('MAX_RETRY_INTERVAL');
        const delay = (response && getRetryAfterHeader(response.headers) || Math.pow(2, retryCount)) * 1000;
        return clamp(delay, min, max);
    }
    /**
     * Make a GET request to the Platform API.
     * @param href for request
     * @param additionalFetchOptions {InterfaceBaseFetchOptions} (optional)
     */
    async _doGet(href, additionalFetchOptions) {
        return this._fetch(href, Object.assign({ method: 'GET', successResponseCodes: VALID_HTTP_RESPONSE_CODES }, additionalFetchOptions));
    }
    /**
     * Make a HEAD request to the Platform API.
     * @param href for request
     * @param headers for request
     */
    async _doHead(href, headers = {}) {
        return this._fetch(href, { method: 'HEAD', headers });
    }
    /**
     * Make a POST request to the Platform API.
     * @param href for request
     * @param headers for request
     * @param body for request
     * @param successResponseCodes for request. Most POST requests that create something will return a 201 success
     *     code, but sometimes a different success code is used (e.g. 204 for discard/restore).
     */
    async _doPost(href, headers = {}, body, successResponseCodes = [201], noOtherHeaders = false) {
        return this._fetch(href, { method: 'POST', headers, noOtherHeaders, body, successResponseCodes });
    }
    /**
     * Make a PATCH request to the Platform API.
     * @param href for request
     * @param headers for request
     * @param body for request
     * @param successResponseCodes for request.
     */
    async _doPatch(href, headers = {}, body, successResponseCodes = [200]) {
        return this._fetch(href, { method: 'PATCH', headers, body, successResponseCodes });
    }
    /**
     * Make a PUT request to the Platform API.
     * @param href for request
     * @param headers for request
     * @param body for request
     * @param successResponseCodes for request.
     */
    async _doPut(href, headers = {}, body, successResponseCodes = [201], noOtherHeaders = false) {
        return this._fetch(href, { method: 'PUT', headers, body, noOtherHeaders, successResponseCodes });
    }
    /**
     * Make a DELETE request to the Platform API.
     * @param href for request
     * @param headers for request
     * @param successResponseCodes for request.
     */
    async _doDelete(href, headers = {}, successResponseCodes = [200], noOtherHeaders = false) {
        return this._fetch(href, { method: 'DELETE', headers, noOtherHeaders, successResponseCodes });
    }
    /**
     * Get a JSON resource from the platform API - handles parsing the response as JSON
     * @param href for request
     * @param additionalFetchOptions {InterfaceBaseFetchOptions} (optional)
     */
    async _getResponse(href, additionalFetchOptions) {
        const response = await this._doGet(href, additionalFetchOptions);
        return response.json();
    }
    /**
     * The same as _getResponse, except that it allows piggybacking on a request if it's
     * already in-flight. This is useful when we have multiple places in the code that
     * depend on data from the same request (e.g. getting both collaborators and invitations
     * from the same API), and we want to avoid making repeated requests.
     *
     * @param href for request
     * @param headers for request
     */
    async _getSharedResponse(href, headers = {}) {
        let response = this._sharedResponses.get(href);
        if (!response) {
            response = this._getResponse(href, { headers });
            this._sharedResponses.set(href, response);
            response.finally(() => this._sharedResponses.delete(href));
        }
        return response;
    }
    /**
     * Make a request to the Platform API. If it is paged, provide a next callback
     * @param href for page
     * @param headers to send with request
     */
    async _getPagedResponse(href, headers = {}) {
        var _a, _b;
        const result = await this._getResponse(href, headers);
        if (((_a = result === null || result === void 0 ? void 0 : result._page) === null || _a === void 0 ? void 0 : _a.next) && ((_b = result === null || result === void 0 ? void 0 : result._links) === null || _b === void 0 ? void 0 : _b[RelType.PAGE])) {
            const template = UriTemplate(result._links[RelType.PAGE].href);
            const nextHref = template.fill(Object.assign(Object.assign({}, result._page), { start: result._page.next, resource: RelType.PRIMARY }));
            const next = () => this._getPagedResponse(nextHref, headers);
            return { result, next };
        }
        return { result };
    }
    /**
     * Get all of the pages of a page (or not) Platform API request
     * @param href starting href
     * @param headers to send along with the requests
     */
    _getAllPages(href, headers = {}) {
        let fullResult;
        const readPages = async (requestFn) => {
            const result = await requestFn();
            const children = result && result.result && result.result.children;
            if (!children) {
                return fullResult;
            }
            if (!fullResult) {
                fullResult = result.result;
            }
            else {
                fullResult.children = fullResult.children.concat(children);
            }
            return result.next ? readPages(result.next) : fullResult;
        };
        return readPages(() => this._getPagedResponse(href, headers));
    }
    /**
     * Get and parse the links header for an object in the Platform API
     * @param href of the object
     */
    _getLinksFromHeaders(headers) {
        const linkHeader = headers && headers.get('Link');
        if (!linkHeader) {
            throw SAMError.create(SAMErrorCode.UNKNOWN, 'HEAD request did not return link header');
        }
        const link$1 = link.parse(linkHeader);
        return (link$1
            && link$1.refs.reduce((memo, _a) => {
                var { rel, uri } = _a, otherData = __rest(_a, ["rel", "uri"]);
                if (rel) {
                    memo[rel] = memo[rel] || [];
                    memo[rel].push(Object.assign({ href: uri }, otherData));
                }
                return memo;
            }, {}));
    }
    _getETagFromHeaders(headers) {
        return headers.get('ETag');
    }
    // Sub-classes can refine requests if needed
    async _getFetchOptionOverrides(href, options) {
        if (this._useHighPriorityRequestsIfEnabled && this._core.environment.getValue('HIGH_PRIORITY_RAPI_REQUESTS')) {
            // See https://wiki.corp.adobe.com/pages/viewpage.action?spaceKey=CA&title=Proposal%3A+Priority+Header#Proposal:PriorityHeader-UrgencySummary
            //
            // If enabled, R-API requests will be made using with Priority u=1 header to enable HTTP/2 and HTTP/3, and
            // to direct R-API to optimize calls for response time. HTTP/2+ reduces delays caused by too many
            // connections open on the same domain. Currently, this can only be done on the edge network (CDN), which
            // incurs some cost, but will likely be brought in-house.
            //
            // User-facing web clients SHOULD typically enable this to optimize user experience, but is off by default
            // so that clients take responsibility for determining what priority to use.
            href = !href.includes('platform-cs-edge')
                ? href.replace('platform-cs', 'platform-cs-edge')
                : href;
            options.headers = Object.assign(Object.assign({}, (options.headers || {})), { 'Priority': 'u=1' });
        }
        return { href, options };
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// Support retrying an asynchronous method on failure, with a backoff
var retry = (target, propertyKey, descriptor) => {
    const timeout = Symbol(`@retry ${propertyKey}`);
    const method = descriptor.value;
    descriptor.value = async function (attempt = 0) {
        var _a, _b;
        const timeoutId = this[timeout];
        if (timeoutId) {
            clearTimeout(timeoutId);
            this[timeout] = undefined;
        }
        try {
            await method.call(this);
        }
        catch (e) {
            let interval = 0;
            const response = (_a = e._additionalInfo) === null || _a === void 0 ? void 0 : _a.response;
            if (RETRY_HTTP_RESPONSE_CODES.includes(response === null || response === void 0 ? void 0 : response.status)) {
                // InterfaceBase already auto-retried, so back off using the maximum retry interval.
                interval = this._core.environment.getValue('MAX_RETRY_INTERVAL') || 20000;
            }
            else {
                // Retry all other errors, but at a different interval
                const retryAfter = ((_b = e._additionalInfo) === null || _b === void 0 ? void 0 : _b.response) ? getRetryAfterHeader(e._additionalInfo.response.headers) * 1000 : 0;
                // If we're unable to resolve (unable to connect to services or unexpected response), we schedule a retry
                interval = Math.max(this._core.environment.getRetryInterval(attempt), retryAfter);
            }
            this._core.environment.log(`Retrying ${propertyKey} in ${interval}ms`);
            this[timeout] = setTimeout(() => descriptor.value.call(this, attempt + 1), interval);
        }
    };
};

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
/**
 * The synchronized decorator ensures that only one caller can be executing the method at once,
 * similar to the synchronized keyword in Java for critical blocks. This only makes sense
 * to apply to asynchronous methods, since JavaScript is single-threaded.
 *
 * If an @synchronized method is called while another caller is already executing it, the second
 * caller will wait until the first caller finishes (regardless of whether the promise rejects or succeeds),
 * before executing.
 *
 * A parameter can be marked as @key, allowing multiple concurrent calls so long as the keys are different
 * (this is similar to passing a value to synchronized() in Java).
 */
var synchronized = (target, propertyKey, descriptor) => {
    const promisesMapProperty = Symbol(`@synchronized ${propertyKey}`);
    const method = descriptor.value;
    if (!method) {
        throw SAMError.create(SAMErrorCode.BAD_REQUEST, '@synchronized decorator can only be applied to a method');
    }
    descriptor.value = async function (...args) {
        var _a;
        const keyIndex = (_a = this[KeysProperty]) === null || _a === void 0 ? void 0 : _a.get(propertyKey);
        const key = keyIndex === undefined ? 'default' : args[keyIndex];
        const map = this[promisesMapProperty] || (this[promisesMapProperty] = new Map);
        let promise;
        do {
            promise = map.get(key);
            if (promise) {
                // Somebody else was already executing, so we wait for that to finish first
                try {
                    await promise;
                }
                catch (e) {
                    // Ignore any errors - we'll execute after the existing caller resolves or rejects
                }
            }
        } while (promise);
        // Now enter the critical block ourself - record the promise so others need to wait.
        // Note that we use different promise so that we ensure other callers continue only after
        // we've deleted the previous promise.
        let outerResolve;
        map.set(key, new Promise(resolve => outerResolve = resolve));
        try {
            return await method.apply(this, args);
        }
        finally {
            // Always delete the promise even if we reject, to allow others to continue
            map.delete(key);
            outerResolve && outerResolve();
        }
    };
};

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const BECOME_OBSERVED = 'observed';
const BECOME_UNOBSERVED = 'unobserved';
/**
 * Base class that can be used to create Typed SAMEvent Emitters.
 * @example:
 * class ClickEvent extends SAMEvent {
 *     myClickProp: string;
 *     constructor() {
 *         super('click');
 *     }
 * }
 *
 * class MyClass extends TypedEventEmitter<{ click: ClickEvent }>() {
 *     baseProp : string;
 * }
 *
 * const obj = new MyClass();
 * obj.on('click', (ev, sender, payload) => {
 *     // The ev object is typed based on the 'click' event.
 *     ev.myClickProp;
 * });
 *
 * subClass.dispatch(new ClickEvent());
 */
class TypedEventEmitter extends EventEmitter {
    dispatch(ev, payload) {
        return super.emit(ev.type, ev, this, payload);
    }
    once(type, callback) {
        const result = super.once(type, callback);
        this._onEventListenerAdded();
        return result;
    }
    on(type, callback) {
        // Prevent adding duplicate listeners
        if (this.listeners(type).includes(callback)) {
            return this;
        }
        const result = super.on(type, callback);
        if (type !== BECOME_OBSERVED && type !== BECOME_UNOBSERVED) {
            this._onEventListenerAdded();
            if (this.listenerCount(type) === 1) {
                this.emit(BECOME_OBSERVED, type);
            }
        }
        return result;
    }
    off(type, callback) {
        const result = super.off(type, callback);
        if (type !== BECOME_OBSERVED && type !== BECOME_UNOBSERVED) {
            this._onEventListenerRemoved();
            if (this.listenerCount(type) === 0) {
                this.emit(BECOME_UNOBSERVED, type);
            }
        }
        return result;
    }
    // Called when a new event listener is added on the object.
    _onEventListenerAdded() {
    }
    _onEventListenerRemoved() {
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
/**
 * Dummy implementation of the WeakRef interface to be used
 * in browsers that do not support WeakRefs natively.
 * Note that the associated objects will not be reclaimed by the
 * garbage collector. We don't expect this to be an actual issue
 * in practice for SAM though.
 */
class WeakRefPolyfill {
    constructor(_ref) {
        this._ref = _ref;
    }
    /**
     * Needed in order to fully implement the WeakRef interface.
     * The `Symbol.toStringTag` symbol is a property that
     * is used by the default implementation of the toString method.
     */
    get [Symbol.toStringTag]() {
        return 'WeakRef';
    }
    /**
     * Returns the WeakRef target object.
     * Note that this implementation emulates the API, but cannot
     * actually keep a weak reference.
     *
     * Based on MDN data, most modern browsers support WeakRef.
     */
    deref() {
        return this._ref;
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
var WeakRef$1 = typeof WeakRef !== 'undefined' ? WeakRef : WeakRefPolyfill;

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
function updateLastKnownClientMutationValues(item) {
    item.updateLastKnownClientMutation();
}
function updateLastKnownClientMutationKeys(_, item) {
    item.updateLastKnownClientMutation();
}
class UpdateSession {
    constructor(_core, _delegate, updateContext, mostRecentValidMutation, type = 'fetch') {
        this._core = _core;
        this._delegate = _delegate;
        this.updateContext = updateContext;
        this.mostRecentValidMutation = mostRecentValidMutation;
        this.type = type;
    }
    get hasChanges() {
        return Boolean(this._addedItems || this._changedItems || this._discardedItems);
    }
    get addedItems() {
        return this._addedItems && Array.from(this._addedItems.values());
    }
    get changedItems() {
        return this._changedItems && Array.from(this._changedItems.keys());
    }
    get discardedItems() {
        return this._discardedItems && Array.from(this._discardedItems);
    }
    get changedItemsWithEvents() {
        return this._changedItems && Array.from(this._changedItems);
    }
    checkIfItemAllowsMutations(item) {
        const { lastKnownClientMutation } = item;
        const { mostRecentValidMutation } = this;
        // Return false only when the item has a mutation time that is more recent than the
        // window of latency that the server APIs can guarantee.
        return !lastKnownClientMutation || !mostRecentValidMutation || lastKnownClientMutation < mostRecentValidMutation;
    }
    ensureItemIsAdded(item) {
        var _a;
        const { id } = item;
        if (((_a = this._addedItems) === null || _a === void 0 ? void 0 : _a.get(id)) === item) {
            // It was already part of this session.
            return;
        }
        // Check if we already had it in the pool. If it was discarded before,
        // then we want to recover it by adding it again.
        const delegate = this._delegate;
        if (delegate.getLocalItemById(id) === item && !delegate.isItemDiscarded(item)) {
            return;
        }
        // Finally, let's just add this item again.
        this.queueAddedItem(item);
    }
    queueAddedItem(item) {
        var _a;
        let addedItems = this._addedItems;
        if (!addedItems) {
            addedItems = new Map();
            this._addedItems = addedItems;
        }
        addedItems.set(item.id, item);
        // Make sure we don't send any changed events on new items.
        (_a = this._changedItems) === null || _a === void 0 ? void 0 : _a.delete(item);
    }
    queueChangedItem(item, events = [SharedChangeEvent]) {
        var _a;
        if ((_a = this._addedItems) === null || _a === void 0 ? void 0 : _a.has(item.id)) {
            // We don't send any events on new items.
            return;
        }
        let changedItems = this._changedItems;
        if (!changedItems) {
            changedItems = new Map();
            this._changedItems = changedItems;
        }
        let itemEvents = changedItems.get(item);
        if (!itemEvents) {
            itemEvents = new Set();
            changedItems.set(item, itemEvents);
        }
        events.forEach((event) => itemEvents === null || itemEvents === void 0 ? void 0 : itemEvents.add(event));
    }
    queueDiscardedItem(item) {
        let discardedItems = this._discardedItems;
        if (!discardedItems) {
            discardedItems = new Set();
            this._discardedItems = discardedItems;
        }
        discardedItems.add(item);
    }
    getItemById(id) {
        var _a;
        return ((_a = this._addedItems) === null || _a === void 0 ? void 0 : _a.get(id)) || this._delegate.getLocalItemById(id);
    }
    commit() {
        var _a, _b;
        // Committing the update session first to make sure the pool is up-to-date
        // with the latest changes before we move control back to the client code.
        this._delegate.commitUpdateSession(this);
        // Dispatch all events on the changed items.
        const changedItems = this._changedItems;
        if (changedItems) {
            for (const [item, events] of changedItems.entries()) {
                for (const event of events) {
                    item.dispatch(event);
                }
            }
        }
        if (this.type === 'mutation') {
            // If this was the result of a mutation change, then we have to update the timestamps
            // of all the items that were used.
            (_a = this._addedItems) === null || _a === void 0 ? void 0 : _a.forEach(updateLastKnownClientMutationValues);
            (_b = this._discardedItems) === null || _b === void 0 ? void 0 : _b.forEach(updateLastKnownClientMutationValues);
            changedItems === null || changedItems === void 0 ? void 0 : changedItems.forEach(updateLastKnownClientMutationKeys);
        }
    }
}

var rngBrowser = {exports: {}};

// Unique ID creation requires a high quality random # generator.  In the
// browser this is a little complicated due to unknown quality of Math.random()
// and inconsistent support for the `crypto` API.  We do the best we can via
// feature-detection

// getRandomValues needs to be invoked in a context where "this" is a Crypto
// implementation. Also, find the complete implementation of crypto on IE11.
var getRandomValues = (typeof(crypto) != 'undefined' && crypto.getRandomValues && crypto.getRandomValues.bind(crypto)) ||
                      (typeof(msCrypto) != 'undefined' && typeof window.msCrypto.getRandomValues == 'function' && msCrypto.getRandomValues.bind(msCrypto));

if (getRandomValues) {
  // WHATWG crypto RNG - http://wiki.whatwg.org/wiki/Crypto
  var rnds8 = new Uint8Array(16); // eslint-disable-line no-undef

  rngBrowser.exports = function whatwgRNG() {
    getRandomValues(rnds8);
    return rnds8;
  };
} else {
  // Math.random()-based (RNG)
  //
  // If all else fails, use Math.random().  It's fast, but is of unspecified
  // quality.
  var rnds = new Array(16);

  rngBrowser.exports = function mathRNG() {
    for (var i = 0, r; i < 16; i++) {
      if ((i & 0x03) === 0) r = Math.random() * 0x100000000;
      rnds[i] = r >>> ((i & 0x03) << 3) & 0xff;
    }

    return rnds;
  };
}

/**
 * Convert array of 16 byte values to UUID string format of the form:
 * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
 */

var byteToHex = [];
for (var i = 0; i < 256; ++i) {
  byteToHex[i] = (i + 0x100).toString(16).substr(1);
}

function bytesToUuid$2(buf, offset) {
  var i = offset || 0;
  var bth = byteToHex;
  // join used to fix memory issue caused by concatenation: https://bugs.chromium.org/p/v8/issues/detail?id=3175#c4
  return ([bth[buf[i++]], bth[buf[i++]], 
	bth[buf[i++]], bth[buf[i++]], '-',
	bth[buf[i++]], bth[buf[i++]], '-',
	bth[buf[i++]], bth[buf[i++]], '-',
	bth[buf[i++]], bth[buf[i++]], '-',
	bth[buf[i++]], bth[buf[i++]],
	bth[buf[i++]], bth[buf[i++]],
	bth[buf[i++]], bth[buf[i++]]]).join('');
}

var bytesToUuid_1 = bytesToUuid$2;

var rng$1 = rngBrowser.exports;
var bytesToUuid$1 = bytesToUuid_1;

// **`v1()` - Generate time-based UUID**
//
// Inspired by https://github.com/LiosK/UUID.js
// and http://docs.python.org/library/uuid.html

var _nodeId;
var _clockseq;

// Previous uuid creation time
var _lastMSecs = 0;
var _lastNSecs = 0;

// See https://github.com/broofa/node-uuid for API details
function v1$1(options, buf, offset) {
  var i = buf && offset || 0;
  var b = buf || [];

  options = options || {};
  var node = options.node || _nodeId;
  var clockseq = options.clockseq !== undefined ? options.clockseq : _clockseq;

  // node and clockseq need to be initialized to random values if they're not
  // specified.  We do this lazily to minimize issues related to insufficient
  // system entropy.  See #189
  if (node == null || clockseq == null) {
    var seedBytes = rng$1();
    if (node == null) {
      // Per 4.5, create and 48-bit node id, (47 random bits + multicast bit = 1)
      node = _nodeId = [
        seedBytes[0] | 0x01,
        seedBytes[1], seedBytes[2], seedBytes[3], seedBytes[4], seedBytes[5]
      ];
    }
    if (clockseq == null) {
      // Per 4.2.2, randomize (14 bit) clockseq
      clockseq = _clockseq = (seedBytes[6] << 8 | seedBytes[7]) & 0x3fff;
    }
  }

  // UUID timestamps are 100 nano-second units since the Gregorian epoch,
  // (1582-10-15 00:00).  JSNumbers aren't precise enough for this, so
  // time is handled internally as 'msecs' (integer milliseconds) and 'nsecs'
  // (100-nanoseconds offset from msecs) since unix epoch, 1970-01-01 00:00.
  var msecs = options.msecs !== undefined ? options.msecs : new Date().getTime();

  // Per 4.2.1.2, use count of uuid's generated during the current clock
  // cycle to simulate higher resolution clock
  var nsecs = options.nsecs !== undefined ? options.nsecs : _lastNSecs + 1;

  // Time since last uuid creation (in msecs)
  var dt = (msecs - _lastMSecs) + (nsecs - _lastNSecs)/10000;

  // Per 4.2.1.2, Bump clockseq on clock regression
  if (dt < 0 && options.clockseq === undefined) {
    clockseq = clockseq + 1 & 0x3fff;
  }

  // Reset nsecs if clock regresses (new clockseq) or we've moved onto a new
  // time interval
  if ((dt < 0 || msecs > _lastMSecs) && options.nsecs === undefined) {
    nsecs = 0;
  }

  // Per 4.2.1.2 Throw error if too many uuids are requested
  if (nsecs >= 10000) {
    throw new Error('uuid.v1(): Can\'t create more than 10M uuids/sec');
  }

  _lastMSecs = msecs;
  _lastNSecs = nsecs;
  _clockseq = clockseq;

  // Per 4.1.4 - Convert from unix epoch to Gregorian epoch
  msecs += 12219292800000;

  // `time_low`
  var tl = ((msecs & 0xfffffff) * 10000 + nsecs) % 0x100000000;
  b[i++] = tl >>> 24 & 0xff;
  b[i++] = tl >>> 16 & 0xff;
  b[i++] = tl >>> 8 & 0xff;
  b[i++] = tl & 0xff;

  // `time_mid`
  var tmh = (msecs / 0x100000000 * 10000) & 0xfffffff;
  b[i++] = tmh >>> 8 & 0xff;
  b[i++] = tmh & 0xff;

  // `time_high_and_version`
  b[i++] = tmh >>> 24 & 0xf | 0x10; // include version
  b[i++] = tmh >>> 16 & 0xff;

  // `clock_seq_hi_and_reserved` (Per 4.2.2 - include variant)
  b[i++] = clockseq >>> 8 | 0x80;

  // `clock_seq_low`
  b[i++] = clockseq & 0xff;

  // `node`
  for (var n = 0; n < 6; ++n) {
    b[i + n] = node[n];
  }

  return buf ? buf : bytesToUuid$1(b);
}

var v1_1 = v1$1;

var rng = rngBrowser.exports;
var bytesToUuid = bytesToUuid_1;

function v4$1(options, buf, offset) {
  var i = buf && offset || 0;

  if (typeof(options) == 'string') {
    buf = options === 'binary' ? new Array(16) : null;
    options = null;
  }
  options = options || {};

  var rnds = options.random || (options.rng || rng)();

  // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`
  rnds[6] = (rnds[6] & 0x0f) | 0x40;
  rnds[8] = (rnds[8] & 0x3f) | 0x80;

  // Copy bytes to buffer, if provided
  if (buf) {
    for (var ii = 0; ii < 16; ++ii) {
      buf[i + ii] = rnds[ii];
    }
  }

  return buf || bytesToUuid(rnds);
}

var v4_1 = v4$1;

var v1 = v1_1;
var v4 = v4_1;

var uuid = v4;
uuid.v1 = v1;
uuid.v4 = v4;

var uuid_1 = uuid;

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// NOTE: since UXP doesn't provide `window.crypto` (https://jira.corp.adobe.com/browse/UXP-12909), SAM.js must include
// an old version of uuid (3.3.2) that does not require `window.crypto` at all. Otherwise, each host app has to
// inject the crypto API, which is not ideal, as it then becomes a hidden dependency for SAM.js. Host apps may still
// choose to provide the crypto API for more cryptographically secure uuid, but there does not appear to be any
// security holes in SAM.js using the older algorithm.
function samJsUuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return uuid_1.v4();
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
function getFiltersRelation(requestedFilter, other) {
    if (requestedFilter === other) {
        // If the exact same filter is given, then it must be a complete list.
        return 'subset';
    }
    if (requestedFilter && other) {
        // If the hash is the same, then we have the same filter.
        if (requestedFilter.hash === other.hash) {
            return 'subset';
        }
        // Since the hashes don't match, they aren't both EverythingFilter. So if `other`
        // is everything filter, then the requested filter is a subset.
        if (other.hash === 'everything') {
            return 'subset';
        }
        // If requested filter is the EverythingFilter, then it is a superset
        if (requestedFilter.hash === 'everything') {
            return 'unknown';
        }
        return requestedFilter.getRelationToFilter(other);
    }
    // If we have a filter, it means the other list had no filter, so this is a complete subset of an unfiltered list.
    if (requestedFilter) {
        return 'subset';
    }
    // If the other list had a filter and this new list doesn't have a filter, then this new list will be incomplete.
    return 'unknown';
}
/**
 * Useful for when you have an array as a value in a filter
 * and you want to compare the existing filter array value
 * with the new array value to see if they share some or all of their
 * items or even none.
 */
function compareFilterArrayValues(arry1, arry2) {
    let lengthOfSharedElements = 0;
    arry1.forEach((element1) => {
        if (arry2.find((element2) => element1 === element2)) {
            lengthOfSharedElements++;
        }
    });
    if (lengthOfSharedElements === arry1.length && lengthOfSharedElements === arry2.length) {
        return 'all';
    }
    if (lengthOfSharedElements > 0) {
        return 'some';
    }
    return 'none';
}

// Must match GenericListStatus enum from SAM.cpp
var AssetListStatus;
(function (AssetListStatus) {
    AssetListStatus[AssetListStatus["NotStarted"] = 1] = "NotStarted";
    AssetListStatus[AssetListStatus["HadLoaderErrors"] = 2] = "HadLoaderErrors";
    AssetListStatus[AssetListStatus["Loading"] = 3] = "Loading";
    AssetListStatus[AssetListStatus["ReadyHasMorePages"] = 4] = "ReadyHasMorePages";
    AssetListStatus[AssetListStatus["ReadyFullyLoaded"] = 5] = "ReadyFullyLoaded";
})(AssetListStatus || (AssetListStatus = {}));
const LOADER_STATE_CHANGED = 'loaderStateChanged';
class ItemLoaderStateChangeEvent extends SAMEvent {
    constructor(hasUpdatedItems = false) {
        super(LOADER_STATE_CHANGED);
        this.hasUpdatedItems = hasUpdatedItems;
    }
}
const SharedEventWithoutItemChanges = new ItemLoaderStateChangeEvent(false);
const SharedEventWithItemChanges = new ItemLoaderStateChangeEvent(true);
/**
 * This is the structure of a page in memory.
 */
class ItemLoaderPage {
    constructor() {
        this.startedGeneration = 0;
        this.loadedGeneration = 0;
    }
}
/**
 * A list can have multiple loaders that provide items.
 * The ItemLoaderState maintains the state of each loader attached in a list:
 * - The loaded pages and their items
 * - The error state if we had one
 * - The next available page if any
 *
 * The Loader can also refresh the items starting with page 0. The items
 * in the pre-loaded pages will only refresh once those pages are fully loaded again.
 *
 * If a previous pending request is finished after we start a refresh cycle, then
 * the items in that page will update if and only if the new cycle didn't fetch that page yet.
 */
class ItemLoaderState extends TypedEventEmitter {
    constructor(_loader, _order, _filter, _implicitFilter) {
        super();
        this._loader = _loader;
        this._order = _order;
        this._filter = _filter;
        this._implicitFilter = _implicitFilter;
        this.status = AssetListStatus.NotStarted;
        this._pages = [];
        /**
         * Each new refresh of the pages increments the generation index.
         * We use this to differentiate between async network calls that
         * might end up providing content after a new refresh has already
         * been scheduled.
         */
        this._generation = 0;
        /**
         * Store the index and link of the next page. Once we reach
         * the last page, the callback will become null.
         */
        this._nextPageIndex = 0;
        /**
         * Flag used to automatically load all pages without waiting for user intervention.
         * This is useful for cases when all items are needed. For example, in the sidebar
         * we want to display all the teams and bookmarked spaces.
         */
        this._loadAllPages = false;
    }
    get mostRecentValidMutation() {
        return this._mostRecentValidMutation;
    }
    /**
     * Triggers a new generation of loading.
     * @param generation    The generation index. This number must always increment for new loads.
     * @param loadAllPages  Forces loading all the pages in the chain.
     * @param limit (optional) override default items per page
     * @returns Promise that can be used to know when the entire loading chain finished.
     */
    async load(generation, loadAllPages, limit) {
        this._loadAllPages = loadAllPages;
        // If we were loading something before, we are aborting
        // that in favor of the new generation.
        // Reset the previous page token.
        this._generation = generation;
        this._nextPageCallback = undefined;
        this._nextPageIndex = 0;
        this._pendingLoad = undefined;
        await this._loadPage(limit);
    }
    /**
     * Load the next page.
     * @returns Promise that can be used to know when the entire loading chain finished.
     */
    async loadNextPage(limit) {
        await this._loadPage(limit);
    }
    /**
     * Load all the remaining pages.
     * @returns Promise that can be used to know when the entire loading chain finished.
     */
    async loadAllPages(limit) {
        this._loadAllPages = true;
        await this._loadPage(limit);
    }
    /**
     * Pushes the items from all the loaded pages into the provided `dest` set.
     *
     * @param dest Set object that is populated with items from all the pages.
     */
    collectAllItems(dest) {
        for (const page of this._pages) {
            if (page && page.items) {
                for (const item of page.items) {
                    dest.add(item);
                }
            }
        }
    }
    /**
     * Loads or creates a page at the specified index.
     * @param pageIndex
     * @returns The page at index `pageIndex`.
     */
    _ensurePage(pageIndex) {
        let page = this._pages[pageIndex];
        if (!page) {
            page = new ItemLoaderPage();
            this._pages[pageIndex] = page;
        }
        return page;
    }
    /**
     * Handle only one page load at a time by storing the promise and waiting on the same
     * promise.
     *
     * Note that the promise chain can still be restarted via the `load()` method which
     * starts loading everything from the first page again.
     */
    async _loadPage(limit) {
        // Check if there is a load already in progress. We wait on that one before making more loads.
        // This should prevent users from trying to load too many pages ahead of time.
        let pendingLoad = this._pendingLoad;
        if (!pendingLoad) {
            // We don't have anything in progress, we can go ahead and load the page.
            pendingLoad = this._loadPageImpl(limit);
            // Store the pending load to make sure no requests are executed at the same time.
            this._pendingLoad = pendingLoad;
        }
        // Wait for this load to finish.
        await pendingLoad;
        if (this._pendingLoad === pendingLoad) {
            // If the load is still relevant for this generation of loading,
            // then we can go ahead and remove the promise. Note that we will
            // have to do this for all returning cases, even the paths that didn't
            // actually trigger a load above. That's because we don't know which path
            // continues the execution first and we want to make the caller can initiate
            // another call immediately.
            this._pendingLoad = undefined;
        }
    }
    /**
     * Loads the next page queued up for the loader.
     * It will also update the current page and recursively call
     * itself to load the next page if needed.
     */
    async _loadPageImpl(limit) {
        const nextPageCallback = this._nextPageCallback;
        const pageIndex = this._nextPageIndex;
        // If this is not the first page and we don't have a callback, then we are out of pages to load.
        if (pageIndex && !nextPageCallback) {
            return;
        }
        // Ensure we have the next page in memory.
        const page = this._ensurePage(pageIndex);
        // Store the generation that we are in when the loading starts. If the results
        // come by the time we've already switched to a new generation, then we will
        // ignore the errors and keep the results only if no other newer generation has
        // finished loading the same page already.
        const startGeneration = this._generation;
        if (page.startedGeneration >= startGeneration) {
            // We've already loaded this or a newer generation in a different loading chain.
            return;
        }
        // Make sure the page knows we are about to reload content using the new generation.
        const previousPageGeneration = page.startedGeneration;
        page.startedGeneration = startGeneration;
        // Remove the error state in case we had one before.
        this.error = null;
        // Update the status to loading state before we start loading.
        this._updateStatus(AssetListStatus.Loading);
        let result;
        let loaderError;
        try {
            const pageLimit = limit !== null && limit !== void 0 ? limit : page.limit;
            const options = {
                order: this._order,
                implicitFilter: this._implicitFilter,
                filter: this._filter,
                limit: pageLimit
            };
            if (nextPageCallback) {
                result = await nextPageCallback(options);
                // We delete the callback only after the page fully loaded.
                // That's so that we can retry the load if the first try fails.
                this._nextPageCallback = undefined;
            }
            else {
                // Load the first page.
                result = await this._loader.loadPage(options);
                // We can update the window of valid changes at this point.
                // Note that we don't want to update the value for the rest of the pages
                // as we would still have items from the first page that might not be included yet.
                // For example, if a new asset is created and it shows up on the first page,
                // but the server didn't know about it just yet, then follow up pages
                // are not allowed to change this value until we do another reload cycle.
                this._mostRecentValidMutation = result.mostRecentValidMutation;
            }
            // Once the page is loaded, save the limit we've used for that particular page.
            // If we refresh the page we want to run it with the same page limit.
            page.limit = pageLimit;
        }
        catch (err) {
            loaderError = err;
        }
        if (page.loadedGeneration >= startGeneration) {
            // Ignore the results as a newer loader generation already finished loading this page.
            return;
        }
        if (loaderError) {
            if (page.startedGeneration !== startGeneration) {
                // Note that we only revert if this load is still the most recent generation
                // that started the load. Otherwise, we let the newer loader handle it eventually.
                // Note that we return early in here to avoid bubbling up the error state while
                // the newer loader is still in progress.
                return;
            }
            // Revert the status of the page back to the original generation before we've started.
            // This is needed so that we can retry loading this page again next time.
            page.startedGeneration = previousPageGeneration;
        }
        else if (result) {
            // Note that we still use the results of the page even if the client started a new generation of loading.
            // If we've reached this point, it means that the newer generation of loading has not yet reached this page.
            // In that case, the result we just received is still new enough to considering it better than what we had before.
            page.items = result.items.filter(item => this._implicitFilter.matches(item) && this._filter.matches(item));
            this.total = result.total;
            page.loadedGeneration = startGeneration;
        }
        // Update the state before sending any events.
        const loadMorePages = this._updateStatusAndLinkToNextPage(startGeneration, pageIndex, result, loaderError);
        // If we decided to load more pages, this is a good time to make that recursive async call.
        if (loadMorePages) {
            await this._loadPageImpl(limit);
        }
    }
    /**
     * Checks if the loader result can be used to load the next page.
     * @param startGeneration   The generation that started this loader session.
     * @param pageIndex         The index of the loaded page.
     * @param result            The result received from the loader.
     * @param loaderError       The error caught during the execution of the loader, if any.
     * @returns true if the caller should continue loading the next page automatically.
     */
    _updateStatusAndLinkToNextPage(startGeneration, pageIndex, result, loaderError) {
        if (this._generation !== startGeneration || this._nextPageIndex !== pageIndex) {
            // If the result is from a previous generation, we cannot update the `next page` link because we
            // reserve the page chaining only to the current generation of the loader. We don't want to keep
            // loading pages using the previous generation anyway, so this is where we bail out
            // that previous generation.
            if (result) {
                // If we had a result and we got as far as this point, then we have new items
                // already injected. However, there's a newer load already in progress, so there's no actual
                // state change here. We only want to inform of the possibility of new items
                // being added to the page, so we dispatch an event to let the ItemList update its 'items' contents
                // to the most recent version loaded in this load chain.
                this.dispatch(SharedEventWithItemChanges);
            }
            return false;
        }
        if (!result) {
            // This page failed to load, so loading any other pages is compromised too.
            this.error = loaderError;
            this._updateStatus(AssetListStatus.HadLoaderErrors);
            return false;
        }
        // Increment the page index and store the link to the next page that we've received from the loader.
        this._nextPageIndex = pageIndex + 1;
        this._nextPageCallback = result.loadNextPage;
        if (!result.loadNextPage) {
            // There are no other pages left. If we had any pages beyond this point
            // before, this is a good time to remove them.
            this._pages.splice(this._nextPageIndex);
        }
        // If we have a `loadNextPage` callback available then it means there are more pages to load.
        this._updateStatus(result.loadNextPage ? AssetListStatus.ReadyHasMorePages : AssetListStatus.ReadyFullyLoaded);
        // There are two cases when we want to automatically load the next pages:
        // 1) If the client requested to load all pages automatically via the `this._loadAllPages` flag.
        // 2) If the client already requested these pages before in a previous generation, we will keep reloading
        //    the pages automatically. This is the use-case when the client already scrolled past the first page and
        //    the client triggers a refresh of the list in the background. In that case the list must automatically
        //    update up-to the last scroll position. The pages will refresh one-by-one until all new items are available.
        // If the page was loaded before (case #2 above), then the page must be in the list of pages already.
        // That means the page index is lower than the size of the pages array.
        const didLoadNextPageBefore = this._nextPageIndex < this._pages.length;
        return this._loadAllPages || didLoadNextPageBefore;
    }
    /**
     * Triggers a status update event.
     * @param newStatus
     */
    _updateStatus(newStatus) {
        // We always want to send an event when we have item changes,
        // so that the ItemList can update the item list even if the status didn't change.
        const hasItemChanges = newStatus === AssetListStatus.ReadyFullyLoaded || newStatus === AssetListStatus.ReadyHasMorePages;
        if (hasItemChanges || newStatus !== this.status) {
            this.status = newStatus;
            this.dispatch(hasItemChanges ? SharedEventWithItemChanges : SharedEventWithoutItemChanges);
        }
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const ITEM_LIST_STATE_CHANGED = 'itemListStateChanged';
const MESSAGE_LOGGED = 'messageLogged';
const CONFIG_CHANGE = 'configChanged';
class ItemListStateChangedEvent extends Event {
    constructor(list) {
        super(ITEM_LIST_STATE_CHANGED);
        this.list = list;
    }
}
class MessageLoggedEvent extends Event {
    constructor(message, logLevel) {
        super(MESSAGE_LOGGED);
        this.message = message;
        this.logLevel = logLevel;
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const COLLABORATOR_CHANGED = 'collaborator_changed';
const CollaboratorEvent = {
    type: COLLABORATOR_CHANGED
};
class CollaboratorChangedEvent extends SAMEvent {
    constructor() {
        super(COLLABORATOR_CHANGED);
    }
}
const SharedCollaboratorChangedEvent = new CollaboratorChangedEvent();

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// Top-level application metadata fields that should be embedded in all R-API and USS listings
const AppMetadataField = {
    SHELL_VISIBILITY: 'shell:visibility',
    CC_INHERITED: 'cc:inherited',
    REVIEW_SOURCE_ASSET: 'review:sourceAsset',
    CONVERTED_TO: 'convertedTo',
    NON_AUTO_MIGRATABLE: 'ccx:nonAutoMigratableCopiedFromId',
    COPIED_IN_LEGACY_SPARK_TO: 'ccx:copiedLegacySparkId',
    CC_INHERITABLE: 'cc:inheritable',
    PROJECT_RENDITION: 'project:rendition' // required for Projects showing up in USS listings
};
const RapiAppMetadataWildcardNamespaces = {
    CC: 'cc',
    CLASSROOM_ASSIGNMENTS: 'assignment',
    CCX: 'ccx',
};
// This should be fix once https://jira.corp.adobe.com/browse/SEARCH-45142 is done
const NewFormatAppMetadataField = {
    HZ_DOCUMENT_METADATA: 'hz:documentMetadata',
    CC_CREATED_FROM_UPLOAD: 'cc:createdFromUpload'
};
// The following fields must be escaped as-is to get all fields in the namespace
// See: https://wiki.corp.adobe.com/pages/viewpage.action?spaceKey=CA&title=Proposal%3A+Enhanced+Filtering+and+Embedding+Support+for+JSON+Resources#Proposal:EnhancedFilteringandEmbeddingSupportforJSONResources-Selectors
// eslint-disable-next-line no-useless-escape
const AppMetadataNamespacesRapiFormat = Object.values(RapiAppMetadataWildcardNamespaces).map((element) => `\"${element}:\"*`);
// Rapi and USS have different formats for these core AppMetadataFields
const EmbedAppMetadataFieldsRapiFormat = Object.values(AppMetadataField)
    .concat(Object.values(NewFormatAppMetadataField))
    .filter((element) => {
    // Filter out all single field selectors that will be included by a wildcard selector
    const ns = element.split(':')[0];
    return !Object.values(RapiAppMetadataWildcardNamespaces).includes(ns);
})
    .map((element) => '"' + element + '"')
    .concat(AppMetadataNamespacesRapiFormat);
const EmbedAppMetadataFieldsUSSFormat = Object.values(AppMetadataField).map((element) => {
    return ('app_metadata$$' + element);
}).concat(Object.values(NewFormatAppMetadataField).map((element) => {
    return ('api:metadata/application$$' + element);
}));
function getRapiAppMetadataSelectors() {
    return EmbedAppMetadataFieldsRapiFormat;
}
// USSFetchFields should be embedded in USS calls only
const USSFetchField = {
    COLLABORATION_METADATA: 'collaboration_metadata',
    COLLABMETADATA_MINE: 'collab_metadata#mine',
    REPO_CREATED_BY_CLIENT_ID: 'repo:createdByClientId',
};
const USSFetchFields = Array.from(Object.values(USSFetchField));
// Include all fields from AppMetadataFields and FetchFields in USS search results.
function getUSSFetchFields() {
    return [...EmbedAppMetadataFieldsUSSFormat, ...USSFetchFields];
}
var Permission;
(function (Permission) {
    // Should match AssetPermission.h
    Permission[Permission["None"] = 0] = "None";
    Permission[Permission["Ack"] = 1] = "Ack";
    Permission[Permission["Read"] = 2] = "Read";
    Permission[Permission["Write"] = 4] = "Write";
    Permission[Permission["Delete"] = 8] = "Delete";
    // TODO: get SAM.cpp to match
    // synthetic permissions
    Permission[Permission["Share"] = 16] = "Share";
    Permission[Permission["Rename"] = 32] = "Rename";
    // Asset can be moved out of its parent folder, but the
    // destination folder must have Write permission
    Permission[Permission["Move"] = 64] = "Move";
    Permission[Permission["Discard"] = 128] = "Discard";
    Permission[Permission["Restore"] = 256] = "Restore";
    // Asset can be duplicated in its current folder. To copy to a different folder, the Asset
    // must have Read permission and the target folder must have Write permission.
    Permission[Permission["Duplicate"] = 512] = "Duplicate";
    Permission[Permission["ReadAppMetadata"] = 1024] = "ReadAppMetadata";
    // Typically, assets will have all permissions above for the logged-in user, but the
    // service doesn't specify Ack because it is implied by the fact that the asset
    // was included in the response.
    Permission[Permission["All"] = 2046] = "All";
})(Permission || (Permission = {}));
function parseEffectivePermissions(permissions) {
    // https://git.corp.adobe.com/pages/caf/api-spec/chapters/concepts/access.html#privileges
    // Each permission type should look for its relevant key first, then fall back to "*" entry.
    const primary = permissions[RelType.PRIMARY] || permissions['*'] || []; // permissions directly on this obj
    const policy = permissions[RelType.AC_POLICY] || permissions['*'] || []; // contains "share" permission
    const directory = permissions[RelType.DIRECTORY] || permissions['*'] || []; // parent folder's permissions
    const metadata = permissions[RelType.APP_METADATA] || permissions['*'] || []; // metadata's permissions
    let mask = Permission.None;
    primary.forEach((perm) => {
        switch (perm) {
            case 'ack':
                mask |= Permission.Ack;
                break;
            case 'read':
                mask |= Permission.Read;
                if (directory.includes('write')) {
                    // Can duplicate this file in its current directory
                    mask |= Permission.Duplicate;
                }
                break;
            case 'write':
                // Clients need 'write' permission to edit the contents of a file, and they
                // need 'write' permission on the parent folder to rename, discard, or restore
                mask |= Permission.Write;
                if (directory.includes('write')) {
                    mask |= Permission.Rename;
                    mask |= Permission.Discard;
                    mask |= Permission.Restore;
                }
                break;
            case 'delete':
                // Clients need Delete permission on the asset and Write permission on the
                // parent directory in order to delete an asset.
                if (directory.includes('write')) {
                    mask |= Permission.Delete;
                    mask |= Permission.Move;
                }
                break;
        }
    });
    // synthetic permission
    if (policy.includes('write')) {
        mask |= Permission.Share;
    }
    // appmetadata permission
    if (metadata.includes('read')) {
        mask |= Permission.ReadAppMetadata;
    }
    return mask;
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const PROJECTS_INDEX_LINKS = {
    [RelType.PROJECTS_PRIMARY]: ['assetId', 'embed'],
    [RelType.PROJECTS_COLLECTION]: ['embed', 'limit', 'orderBy', 'start', 'view'],
    [RelType.PROJECTS_CREATE]: ['embed'],
    [RelType.RESOLVE_PATH]: ['path'],
    [RelType.PROJECTS_GRADIENTS_COLLECTION]: [],
    [RelType.PROJECTS_CONTEXT]: []
};
var IProjectPatchKeys;
(function (IProjectPatchKeys) {
    IProjectPatchKeys["GRADIENT_ID"] = "/project:rendition/gradientId";
})(IProjectPatchKeys || (IProjectPatchKeys = {}));
function _projectLinkKeys() {
    return [RelType.CREATE, RelType.RESTORE, RelType.DISCARD, RelType.PROJECTS_RENAME, RelType.PAGE];
}
var ProjectViews;
(function (ProjectViews) {
    /**
     * Combination of Projects returned from yours and sharedWithYou.
     */
    ProjectViews[ProjectViews["All"] = 0] = "All";
    /**
     *  Only the Projects that are considered yours:
     *  For CCI profile, only the projects within /projects directory are returned.
     *  For CCT/CCE profile, a combination of projects within the assigned user storage for the profile,
     *  and the shared storage of Org are returned. If the profile only has access to the
     *  Project via inheritance or through orgEverybody group, then it will not be included.
     */
    ProjectViews[ProjectViews["Yours"] = 1] = "Yours";
    /**
     * Only the Projects that are shared with the requestor. For both CCI and CCT/CCE profiles,
     * the collection would be the negation of yours with the exception that the Projects shared
     * via orgEverybody group are still not included.
     */
    ProjectViews[ProjectViews["SWY"] = 2] = "SWY";
    /**
     * The full list of Projects regardless of how it is accessed. This includes Projects accessible
     * via inheritance and shared via orgEverybody group.
     */
    ProjectViews[ProjectViews["Admin"] = 3] = "Admin";
})(ProjectViews || (ProjectViews = {}));
/**
 * A utility that converts our Enumeration to
 * the services string enumeration.
 *
 * @param projectView The enumeration value we provide to clients.
 * @returns
 */
function projectEnumToString(projectView) {
    switch (projectView) {
        case ProjectViews.SWY:
            return 'sharedWithYou';
        case ProjectViews.Yours:
            return 'yours';
        case ProjectViews.Admin:
            return 'admin';
        default:
            return;
    }
}
function parseProjectEffectivePermissions(permissions) {
    // We get special permissions for projects which are present in the project primary relation
    const projectPrimary = permissions[RelType.PROJECTS_PRIMARY] || permissions['*'] || []; // permissions on the project
    const primary = permissions[RelType.PRIMARY] || permissions['*'] || []; // permissions on the folder representing the project
    const directory = permissions[RelType.DIRECTORY] || permissions['*'] || []; // parent folder's permissions
    let mask = Permission.None;
    // We get the 'ack', 'read', 'rename', 'discard', 'restore' permissions from the project primary relation
    projectPrimary.forEach((perm) => {
        switch (perm) {
            case 'ack':
                mask |= Permission.Ack;
                break;
            case 'read':
                mask |= Permission.Read;
                break;
            case 'rename':
                mask |= Permission.Rename;
                break;
            case 'discard':
                mask |= Permission.Discard;
                break;
            case 'restore':
                mask |= Permission.Restore;
                break;
        }
    });
    // We get the 'write' and 'delete' permissions from the folder representing the project and the parent folder
    primary.forEach((perm) => {
        switch (perm) {
            case 'write':
                mask |= Permission.Write;
                break;
            case 'delete':
                if (directory.includes('write')) {
                    mask |= Permission.Delete;
                }
                break;
        }
    });
    return mask;
}
var ProjectCapabilities;
(function (ProjectCapabilities) {
    ProjectCapabilities["CREATE"] = "create";
})(ProjectCapabilities || (ProjectCapabilities = {}));
var ProjectLeaveReason;
(function (ProjectLeaveReason) {
    ProjectLeaveReason[ProjectLeaveReason["PENDING"] = 0] = "PENDING";
    ProjectLeaveReason[ProjectLeaveReason["CAN_LEAVE"] = 1] = "CAN_LEAVE";
    /**
     * All false reasons.
     */
    ProjectLeaveReason[ProjectLeaveReason["CANNOT_LEAVE_NO_COLLABORATORS"] = 2] = "CANNOT_LEAVE_NO_COLLABORATORS";
    ProjectLeaveReason[ProjectLeaveReason["CANNOT_LEAVE_ADDRESS_BOOK_GROUP_MEMBER"] = 3] = "CANNOT_LEAVE_ADDRESS_BOOK_GROUP_MEMBER";
    ProjectLeaveReason[ProjectLeaveReason["CANNOT_LEAVE_LAST_COLLABORATOR"] = 4] = "CANNOT_LEAVE_LAST_COLLABORATOR";
    /**
     * @TODO_Deprecation
     * CANNOT_LEAVE_LAST_DIRECT_COLLABORATOR will be marked as deprecated on or after 2024-07-01 and removed on or after
     * 2024-10-01.
     */
    ProjectLeaveReason[ProjectLeaveReason["CANNOT_LEAVE_LAST_DIRECT_COLLABORATOR"] = 5] = "CANNOT_LEAVE_LAST_DIRECT_COLLABORATOR";
    ProjectLeaveReason[ProjectLeaveReason["CANNOT_LEAVE_IS_ADMIN_OF_PROJECT"] = 6] = "CANNOT_LEAVE_IS_ADMIN_OF_PROJECT";
    ProjectLeaveReason[ProjectLeaveReason["CANNOT_LEAVE_LAST_DIRECT_COLLAB_WITH_SHARE_PERMISSION"] = 7] = "CANNOT_LEAVE_LAST_DIRECT_COLLAB_WITH_SHARE_PERMISSION";
    ProjectLeaveReason[ProjectLeaveReason["CANNOT_LEAVE_IS_OWNER"] = 8] = "CANNOT_LEAVE_IS_OWNER";
})(ProjectLeaveReason || (ProjectLeaveReason = {}));
/**
 * Reasons for why the user can or cannot create a project.
 */
var CanCreateProjectReason;
(function (CanCreateProjectReason) {
    CanCreateProjectReason[CanCreateProjectReason["UNKNOWN"] = 0] = "UNKNOWN";
    CanCreateProjectReason[CanCreateProjectReason["ALLOWED"] = 1] = "ALLOWED";
    CanCreateProjectReason[CanCreateProjectReason["ADMIN_DISABLED"] = 2] = "ADMIN_DISABLED";
    CanCreateProjectReason[CanCreateProjectReason["INVALID_REPOSITORY"] = 3] = "INVALID_REPOSITORY";
    CanCreateProjectReason[CanCreateProjectReason["NO_HOME_REPOSITORY"] = 4] = "NO_HOME_REPOSITORY";
})(CanCreateProjectReason || (CanCreateProjectReason = {}));

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
var CustomModelPublishedState;
(function (CustomModelPublishedState) {
    CustomModelPublishedState["DRAFT"] = "draft";
    CustomModelPublishedState["READY"] = "ready";
    CustomModelPublishedState["PUBLISHED"] = "published";
    CustomModelPublishedState["UNPUBLISHED"] = "unpublished";
    CustomModelPublishedState["QUEUED"] = "queued";
    CustomModelPublishedState["TRAINING"] = "training";
    CustomModelPublishedState["FAILED"] = "failed";
    CustomModelPublishedState["CANCELLED"] = "cancelled";
    CustomModelPublishedState["ALL"] = "all";
})(CustomModelPublishedState || (CustomModelPublishedState = {}));
var CustomModelOrderBy;
(function (CustomModelOrderBy) {
    CustomModelOrderBy["MODIFY_DATE"] = "modifyDate";
    CustomModelOrderBy["CREATE_DATE"] = "createDate";
    CustomModelOrderBy["NAME"] = "name";
})(CustomModelOrderBy || (CustomModelOrderBy = {}));

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const shownWarnings = new Set();
/**
 * A utility logging function that warns a client once per page load using
 * the params provided by the caller.
 * @param topic
 * @param message
 */
function oneTimeWarning(topic, message) {
    if (shownWarnings.has(topic)) {
        return;
    }
    console.warn('One time warning (%s): %s', topic, message);
    shownWarnings.add(topic);
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
/**
* A special filter used as a default to simplify logic around filtering rather than having special cases
* for filter being undefined.
*/
class EverythingFilter {
    constructor() {
    }
    get hash() {
        return 'everything';
    }
    matches(item) {
        return item !== undefined;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getRelationToFilter(other) {
        return 'subset';
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// STATE_CHANGED must be a different than the SAM.cpp equivalent so that patchAssetList can throttle
// the SAM.cpp notifications, and map the SAM.js event listeners to the throttled pseudo-events.
const STATE_CHANGED = 'status_changed';
const LIST_CHANGED = 'listChanged';
// NOTE: listFullyLoaded should only be fired when its loaders completed a page and no more pages are available.
//       It should *not* be fired for any other reason that list status changes to ReadyFullyLoaded.
const LIST_FULLY_LOADED = 'listFullyLoaded';
// For identifying updates of ItemList that originate from ItemLoaders. ONLY ItemLoaders should
// use this updateContext.
const LOADER_UPDATE_CONTEXT = 'LoaderUpdateContext';
class StateChangedEvent extends SAMEvent {
    constructor() {
        super(STATE_CHANGED);
    }
}
class ListChangedEvent extends SAMEvent {
    constructor() {
        super(LIST_CHANGED);
    }
}
const SharedListChangedEvent = new ListChangedEvent();
const SharedStateChangedEvent = new StateChangedEvent();
let GlobalGeneration = 0;
/**
 * Make sure we can sort lists based on how recent they've
 * started their server load. The more recent loads will have higher
 * priority over lists that have not been updated in a while.
 */
function allocGenerationIndex() {
    return ++GlobalGeneration;
}
class ItemList extends TypedEventEmitter {
    constructor(_itemPool, loaders, order, implicitFilter, filter = new EverythingFilter(), similarItemList, _loadOptions) {
        super();
        this._itemPool = _itemPool;
        this.order = order;
        this.implicitFilter = implicitFilter;
        this.filter = filter;
        this._loadOptions = _loadOptions;
        this._status = AssetListStatus.NotStarted;
        this._items = [];
        // Every time we refresh the list we increment the generation. This is used
        // to make sure that older lists are updated once the new pages are loaded.
        this._generation = 0;
        this._completedGeneration = 0;
        this._loaderStates = [];
        this._lastFetchTime = 0;
        this._needsRefresh = false;
        // We will have to keep the client side items around until we fully fetch again the entire list.
        // Example: We fetched the latest favorite spaces for the sidebar. Then we add a new space and make it favorite.
        // The new space will be added to the list automatically without doing another fetch from the server.
        // This list will refresh in the background eventually. Once the fetch is complete (ie. all pages are loaded),
        // we can get rid of the assets added in the previous generation. At that point we can know for sure,
        // that the items added in the previous generation have already been captured in the new generation of loaded pages.
        // If an item is created after the load started, we have to keep it around until the next generation ends.
        // That's because the initial pages might have contained the item, but we've started the load too early and the
        // item might not be captured in the result yet.
        // NOTE: only external changes, such as ONS refreshes, should cause entries to be made in _itemUpdatesByGeneration.
        //       Changes from an ItemLoader loading a page should NOT be reflected here. To avoid that, ItemLoader should
        //       always use `createUpdateSession()` or `createMutationSession()` with an `updateContext`.
        this._itemUpdatesByGeneration = new Map();
        this._listId = samJsUuid();
        // In case there is an error in load/loadNextPage that does not come from the Loader,
        // append the error to the errors list.
        this._onUnhandledPromiseError = (err) => {
            var _a;
            this._updateListStatusFromItemLoaderStates();
            if (!((_a = this._errors) === null || _a === void 0 ? void 0 : _a.length)) {
                this._errors = [];
            }
            this._errors.push(err);
            this._status = AssetListStatus.HadLoaderErrors;
            this.emit(STATE_CHANGED);
        };
        this._handlePendingLoad = async () => {
            const pendingLoad = this._pendingLoad;
            if (pendingLoad) {
                const { loadAllPages, limit } = pendingLoad;
                this._pendingLoad = undefined;
                this._debug('_handlePendingLoad() awaiting _runLoaders for loaderState.load');
                await this._runLoaders((loaderState) => loaderState.load(this._generation, loadAllPages, limit));
            }
        };
        this._onItemLoaderStateChanged = (ev) => {
            const { hasUpdatedItems } = ev;
            this._cleanPreviousGenerationItemsIfNeeded();
            // Only dispatch ItemsChanged events in response to ItemLoaderState events. ItemList will broadcast the
            // StatusChanged event when exiting the load/loadNextPage methods so that all ItemList state is settled
            // when the StatusChanged event fires.
            if (hasUpdatedItems) {
                this._updateListItemsFromItemLoaderStates();
            }
        };
        // Reusable event that we use to avoid creating a new event object all the times.
        this._globalStateChangedEvent = new ItemListStateChangedEvent(this);
        /**
         * Handles the change notifications from the pool.
         * This method executes until the GC collects the List reference.
         * This handler must be as quick as possible and postpone any list
         * updates until the items are actually needed by the client.
         * @param ev
         */
        this._itemPoolDelegate = {
            onItemsUpdated: (event) => {
                if (event.updateContext === LOADER_UPDATE_CONTEXT) {
                    // Ignore changes from ItemLoaders, which are handled in _updateListItemsFromItemLoaderStates
                    // but not _applyItemsChangedEvents. This is so partially loaded lists can maintain independence
                    // from other partially loaded lists whose filters intersect. E.g., if listA has items sorted ascending,
                    // and listB has the same filter but sorted descending, loading a page from listA won't add items to
                    // listB, and vice versa. Updates still go through ItemPool, so if both lists have loaded the same item,
                    // there will only be one instance of the item.
                    return;
                }
                let pendingNotifications = this._pendingNotifications;
                if (!pendingNotifications) {
                    pendingNotifications = [];
                    this._pendingNotifications = pendingNotifications;
                }
                pendingNotifications.push({
                    generation: this._generation,
                    event
                });
                this._updateListItemsFromItemLoaderStates();
            }
        };
        for (const loader of loaders) {
            if (loader.supportsFilter && !loader.supportsFilter(filter)) {
                // We don't need want to use loaders that do not support the filters.
                // Otherwise, we are better of with running this list entirely on the client side.
                // For example, the discarded items are better handled on the client for now.
                continue;
            }
            const state = new ItemLoaderState(loader, order, filter, implicitFilter);
            state.on(LOADER_STATE_CHANGED, this._onItemLoaderStateChanged);
            this._loaderStates.push(state);
        }
        if (similarItemList) {
            this._copyInitialItemsFrom(similarItemList);
        }
        _itemPool.addDelegate(this._itemPoolDelegate, 'list');
    }
    _debug(msg) {
        this._core.environment.debug(`[ItemList ${this._itemPool.type} ${this._listId.slice(-4)}] ${msg}`);
    }
    /**
     * When client calls `Source.getList()` and an existing List is returned, the existing List's
     * loadOptions might be different than the loadOptions that were requested. ItemSource calls
     * this method to allow some adjustments.
     *
     * @package -- only to be used internally by SAM.js
     */
    _updateLoadOptions(loadOptions = {}) {
        var _a, _b;
        if (!this._loadOptions) {
            this._loadOptions = {};
        }
        // Can only increase the limit if there is a limit set, otherwise keep the default limit
        // because it's not possible to tell if it is being increased or decreased since it could
        // be defined on the service and not anywhere in SAM.js.
        if (this._loadOptions.limit && loadOptions.limit && this._loadOptions.limit < loadOptions.limit) {
            this._loadOptions.limit = loadOptions.limit;
        }
        // Can turn on loadAllPages, but cannot turn it off
        if (!this._loadOptions.loadAllPages && loadOptions.loadAllPages) {
            this._loadOptions.loadAllPages = loadOptions.loadAllPages;
        }
        // It can only lower the refreshTimeout duration. Annoyingly, -1 means "infinite future", while 0 means "now".
        if (loadOptions.refreshTimeout !== -1
            && (this._loadOptions.refreshTimeout === -1
                || ((_a = this._loadOptions.refreshTimeout) !== null && _a !== void 0 ? _a : 0) >= ((_b = loadOptions.refreshTimeout) !== null && _b !== void 0 ? _b : 0))) {
            this._loadOptions.refreshTimeout = loadOptions.refreshTimeout;
        }
        this._loadAsync(Object.assign(Object.assign({}, this._loadOptions), { 
            // if new refreshTimeout is -1, then it shouldn't refresh, but it can still turn on
            // loadAllPages, which might require loading more pages.
            refreshTimeout: loadOptions.refreshTimeout === -1 ? -1 : this._loadOptions.refreshTimeout })).catch(this._onUnhandledPromiseError);
    }
    get type() {
        return this._itemPool.type;
    }
    get generation() {
        return this._generation;
    }
    get completedGeneration() {
        return this._completedGeneration;
    }
    get hasLoaders() {
        return this._loaderStates.length > 0;
    }
    get lastFetchTime() {
        return this._lastFetchTime;
    }
    get _core() {
        return this._itemPool._core;
    }
    /**
     * In many cases the items already exist in a different list,
     * we just need to reorder the items or just apply a different filter.
     * In that case, we can bootstrap the list with items borrowed from a different
     * list until the items are refreshed in the background if necessary.
     * @package -- only for use in SAM.js
     * @param other
     */
    _copyInitialItemsFrom(other) {
        const itemGeneration = new Map();
        const filter = this.filter;
        for (const item of other.items) {
            if (this.implicitFilter.matches(item) && filter.matches(item)) {
                itemGeneration.set(item, true);
            }
        }
        // Inject the items as if they were just new items.
        // These items will be invalidated once the next generation loads,
        // which happened when `load()` finishes to load all pages.
        // This list starts with the items from a previously downloaded list,
        // so it inherits the generation from the other list too.
        this._generation = other.generation;
        this._completedGeneration = other.completedGeneration;
        this._itemUpdatesByGeneration.set(this._generation, itemGeneration);
        // Set the status to ready if other list is also set to ready and the other
        // list matches the filtering of this list.
        if (other.status === AssetListStatus.ReadyFullyLoaded && getFiltersRelation(filter, other.filter) === 'subset') {
            this._status = AssetListStatus.ReadyFullyLoaded;
            // SAM should copy the lastFetchTime from the other list as it is Fully loaded.
            // Essentially, the same items sorted in a different direction should be just as stale as the source list.
            this._lastFetchTime = other.lastFetchTime;
        }
        // Make sure we recompute the items once the user calls the `items` getter.
        this._updateListItemsFromItemLoaderStates();
    }
    /**
     * When a list completely loads, its items can be copied to other compatible lists. E.g., ListA and ListB have the
     * same filter and both are partially loaded. When ListA finishes loading, all of its items can be copied to
     * ListB, and ListB can be considered fully loaded.
     *
     * @package -- only for use in SAM.js
     */
    _applyListItemsFrom(other) {
        if (other.status !== AssetListStatus.ReadyFullyLoaded) {
            throw SAMError.create(SAMErrorCode.INTERNAL_ERROR, '_applyListItems other list must be fully loaded');
        }
        if (getFiltersRelation(this.filter, other.filter) !== 'subset') {
            throw SAMError.create(SAMErrorCode.INTERNAL_ERROR, '_applyListItems other list must be subset');
        }
        this._items = other.items.filter(item => this.implicitFilter.matches(item) && this.filter.matches(item));
        this.order.sortItems(this._items);
        this._dispatchItemsChangedEvent();
        if (this._status !== AssetListStatus.ReadyFullyLoaded) {
            this._status = AssetListStatus.ReadyFullyLoaded;
            this._dispatchStateChangedEvents();
        }
    }
    get status() {
        if (!this._loaderStates.length) {
            // If we don't have any loaders, we always use the in-memory data for this list.
            return AssetListStatus.ReadyFullyLoaded;
        }
        return this._status;
    }
    get errors() {
        return this._errors;
    }
    get items() {
        return this._items;
    }
    get total() {
        return this._total;
    }
    _cleanPreviousGenerationItemsIfNeeded() {
        // Check if all loaders are fully loaded and trim our previous generations.
        let globalMostRecentValidMutation;
        for (const loader of this._loaderStates) {
            const { status, mostRecentValidMutation } = loader;
            if (status === AssetListStatus.NotStarted || loader.status === AssetListStatus.HadLoaderErrors) {
                // Ignore loaders that are not available.
                continue;
            }
            if (status === AssetListStatus.Loading || status === AssetListStatus.ReadyHasMorePages) {
                // We cannot trim any of the local items just yet. There are still more pages to load.
                return;
            }
            // This loader is ready to trim some pages.
            if (mostRecentValidMutation) {
                // We have to take the minimum timestamps of all the loaders.
                // If we have two loaders that have different latencies, then we have to keep items
                // in the list until the most latent on has a chance to validate our local changes.
                // The most recent mutations that we can eliminate from the local
                // list are the ones outside of any latency window. The minimum is the most recent
                // timestamp that is known to be valid across all loaders. Anything more recent than that
                // would remove changes that were not yet replicated on at least one of the loaders.
                globalMostRecentValidMutation = globalMostRecentValidMutation
                    ? Math.min(globalMostRecentValidMutation, mostRecentValidMutation)
                    : mostRecentValidMutation;
            }
        }
        // We've loaded the entire set of pages, we are now ready to
        // throw away the items that were added on the client side in
        // the previous generations. These items should have been
        // captured already by the new fetches from the server.
        this._cleanPreviousGenerationItems(globalMostRecentValidMutation);
    }
    /**
     * Removes items from the previous generations.
     */
    _cleanPreviousGenerationItems(mostRecentValidMutation) {
        // Convert any pending events into actual maps to make the changes available below.
        this._applyItemsChangedEvents();
        const currentGeneration = this._generation;
        this._completedGeneration = currentGeneration;
        // Pick all items added in the previous generations. If these items were indeed added, the updated
        // lists should already contain the new items and we can go ahead and remove them from our in-memory store.
        // Note that we cannot remove items added in the current generation as those were added
        // after we've fetched the requests from the server and might not be up-to-date yet.
        const newState = new Map();
        let hadChanges = false;
        let mostRecentDeletedGeneration = 0;
        for (const [key, generation] of this._itemUpdatesByGeneration) {
            if (key >= currentGeneration) {
                continue;
            }
            // Deletes in JS Maps do not break the iterators, so it should be safe to delete generations while reading the map.
            hadChanges = true;
            this._itemUpdatesByGeneration.delete(key);
            mostRecentDeletedGeneration = Math.max(mostRecentDeletedGeneration, key);
            // If we have a most recent valid mutation timeframe, then we need to look for items that
            // might still be inside that update time window.
            if (mostRecentValidMutation) {
                // Consolidate all the pending changes into a single generation.
                for (const [item, state] of generation) {
                    const { lastKnownClientMutation } = item;
                    // If the item was already added to the list, then we have to keep updating its final state.
                    if (newState.has(item) || lastKnownClientMutation && lastKnownClientMutation > mostRecentValidMutation) {
                        // This item had a mutation too close to the last reload, so we cannot trust
                        // that the listing returns the most up-to-date values. We have to preserve the item state (added / deleted)
                        // until a future refresh.
                        newState.set(item, state);
                    }
                }
            }
        }
        if (newState.size) {
            // Inject the new state in place of the most recent deleted generation.
            this._itemUpdatesByGeneration.set(mostRecentDeletedGeneration, newState);
        }
        if (hadChanges) {
            // Let the clients know the list must be recomputed.
            this._dispatchItemsChangedEvent();
        }
    }
    refresh() {
        this._loadAsync(this._loadOptions).catch(this._onUnhandledPromiseError);
    }
    /**
     * @deprecated -- Use {@link ItemList.refresh} instead. Will be removed on or after 2024-10-01.
     */
    load(options) {
        oneTimeWarning('ItemList.load', 'ItemList.load() is deprecated and will be removed on or after'
            + ' 2024-10-01. Use ItemList.refresh() instead. List load parameters are passed as the 3rd parameter to'
            + ' ItemSource.getList(), which now automatically starts loading.');
        // Previously, `ItemList.load()` with no parameters would always refresh the list because it implied a default
        // value for `options.refreshTimeout` of `0` ("refresh now"). However, `_updateLoadOptions` assumes a
        // default value for `options.refreshTimeout` of `-1` ("never refresh"). For better backward-compatibility,
        // force a default for refreshTimeout of `0`. A defined value for refreshTimeout will override.
        options = Object.assign({ refreshTimeout: 0 }, options);
        this._updateLoadOptions(options);
    }
    /**
     * Other internal SAM.js code relies on getting a Promise.
     * This should only be used inside SAM.js, not by clients.
     * @package
     */
    async _loadAsync(options) {
        var _a, _b;
        if (!this._loaderStates.length) {
            // We don't have any loaders.
            return;
        }
        const refreshTimeout = (_a = options === null || options === void 0 ? void 0 : options.refreshTimeout) !== null && _a !== void 0 ? _a : 0;
        const loadAllPages = (_b = options === null || options === void 0 ? void 0 : options.loadAllPages) !== null && _b !== void 0 ? _b : false;
        // Check if the list needs to be refreshed:
        // 1. The user didn't provide a refresh timeout, meaning we always load the contents.
        // 2. There is a need to refresh the contents because the list has changed outside of SAMJS.
        // 3. There's a refresh timeout and the timeout expired. We can use this mechanism to prevent using
        //    data that is considered to be too old without forcing a refresh all the times.
        // 4. refreshTimeout === -1 means the client does NOT want to refresh, only wait for existing
        //    load requests to finish, possibly for the purpose of modifying other loadOptions like limit
        //    or loadAllPages.
        const lastFetchTime = this._lastFetchTime;
        if (this._needsRefresh || !lastFetchTime || !refreshTimeout || (refreshTimeout !== -1 && lastFetchTime + refreshTimeout < Date.now())) {
            this._debug(`_loadAsync() calling _load(): _needsRefresh: ${this._needsRefresh}, lastFetchTime: ${lastFetchTime}`
                + ` refreshTimeout: ${refreshTimeout}`);
            await this._load(loadAllPages, options === null || options === void 0 ? void 0 : options.limit);
            return;
        }
        else {
            this._debug(`_loadAsync() not time to refresh: _needsRefresh: ${this._needsRefresh}, lastFetchTime: ${lastFetchTime}`
                + ` refreshTimeout: ${refreshTimeout}`);
        }
        // If there's a pending load, wait until it's finished.
        const previousLoadPromise = this._previousLoadPromise;
        if (previousLoadPromise) {
            // Wait until the previous load is complete.
            this._debug('_loadAsync() awaiting previousLoadPromise');
            await previousLoadPromise;
        }
        if (loadAllPages) {
            // Make sure the previous load didn't load just the first page.
            await this._ensureAllPagesAreLoaded(options === null || options === void 0 ? void 0 : options.limit);
        }
    }
    async _load(loadAllPages, limit) {
        // Make sure we don't keep refreshing this list.
        this._needsRefresh = false;
        // Coalesce multiple load requests into one.
        let pendingLoad = this._pendingLoad;
        if (!pendingLoad) {
            // Since _handlePendingLoad execution is deferred, control is returned to the caller where it
            // should expect the List to already be loading, even though the Loader hasn't actually
            // started.
            this._status = AssetListStatus.Loading;
            // _lastFetchTime must be defined now, because the _handlePendingLoad execution is deferred, which
            // causes control to be passed back to the client. If the client then calls getList(), the refreshTimeout
            // option would not be evaluated properly since _lastFetchTime would be stale.
            this._lastFetchTime = Date.now();
            // We use the generation to ignore results that are still in-flight.
            // When a new load is issued, we will slowly cleanup the
            // results as soon as the prior loading actions finish.
            this._generation = allocGenerationIndex();
            this._debug('_load() creating _pendingLoad');
            this._pendingLoad = pendingLoad = {
                loadAllPages,
                limit,
                // Make sure the load executes after the stack unrolls.
                promise: Promise.resolve().then(this._handlePendingLoad)
            };
            // Alert clients of state change
            this._dispatchStateChangedEvents();
        }
        else if (loadAllPages) {
            // Make sure that the pending load fetches all the pages.
            pendingLoad.loadAllPages = true;
        }
        this._debug('_load() awaiting _pendingLoad');
        await pendingLoad.promise;
    }
    loadNextPage(loadOptions) {
        this._loadNextPageAsync(loadOptions || this._loadOptions).catch(this._onUnhandledPromiseError);
    }
    /**
     * Other internal SAM.js code relies on getting a Promise.
     * This should only be used inside SAM.js, not by clients.
     * @package
     */
    async _loadNextPageAsync(options) {
        if (!this._loaderStates.length) {
            // We don't have any loaders.
            return;
        }
        if (this._pendingLoad) {
            this._debug('_loadNextPageAsync() awaiting _pendingLoad');
            await this._pendingLoad.promise;
        }
        else {
            // Since _loadAsync doesn't immediately start loading, but it does update its status and broadcast the
            // status change event, _loadNextPageAsync must do the same. It can't be handled in _runLoaders only,
            // because without the status event fired in _loadAsync, it'd be fired too late; with the status event
            // fired in _loadAsync, it'd be fired redundantly.
            this._status = AssetListStatus.Loading;
            this._dispatchStateChangedEvents();
            this._debug('_loadNextPageAsync() awaiting _runLoaders for loaderState.loadNextPage');
            await this._runLoaders((loaderState) => loaderState.loadNextPage(options === null || options === void 0 ? void 0 : options.limit));
        }
    }
    async _runLoaders(fn) {
        // We need to update this first to avoid multiple calls to load
        // from trying to load too often.
        this._lastFetchTime = Date.now();
        // Save the promise that triggered the last load.
        const promise = Promise.all(this._loaderStates.map(fn));
        this._previousLoadPromise = promise;
        try {
            this._debug('_runLoaders() awaiting loaders');
            await promise;
        }
        finally {
            this._debug('_runLoaders() loaders complete');
            // Remove the load promise.
            this._previousLoadPromise = undefined;
            // Notify listeners that the List.status has changed (computed from ItemLoaders). This must be the
            // last line processed that alters any state in List, so that event listeners can immediately utilize
            // the List in a completely settled state.
            this._updateListStatusFromItemLoaderStates();
            this._dispatchStateChangedEvents();
            // Notify ItemSource that it can copy this list to other compatible lists.
            // NOTE: this event not intended for clients.
            if (this._status === AssetListStatus.ReadyFullyLoaded) {
                this.emit(LIST_FULLY_LOADED);
            }
        }
    }
    setNeedsRefresh() {
        if (this.status === AssetListStatus.NotStarted || !this._loaderStates.length) {
            // List has never been loaded, so doesn't need refresh
            return;
        }
        if (this.listenerCount(LIST_CHANGED)) {
            // We have listeners waiting for changes, so go ahead and reload this list now.
            this._needsRefresh = true;
            this.refresh();
            return;
        }
        // There are no listeners, we will keep a note to refresh this list
        // once the list becomes active again.
        this._needsRefresh = true;
    }
    _onEventListenerAdded() {
        if (this._needsRefresh && this.listenerCount(LIST_CHANGED)) {
            // Make sure the list is updated once the client
            // needs to use the items of this list again.
            this.refresh();
        }
    }
    async _ensureAllPagesAreLoaded(limit) {
        if (this.status !== AssetListStatus.ReadyHasMorePages) {
            // No more pages to load. Bail out.
            return;
        }
        // Looks like the previous load didn't finish all the pages.
        // Note that if another prefetch is issued, we will just wait on the promise
        // created by the _runLoaders below via `this._previousLoadPromise`.
        await this._runLoaders((loaderState) => loaderState.loadAllPages(limit));
    }
    /**
     * @package -- only for use in SAM.js
     */
    _dispatchStateChangedEvents() {
        this._debug(`_dispatchStateChangedEvents status: ${AssetListStatus[this.status]}`);
        this.dispatch(SharedStateChangedEvent);
        // Dispatch a global event that can be used to handle global
        // state updates without adding individual events on all the item lists.
        this._core.dispatch(this._globalStateChangedEvent);
    }
    _updateListStatusFromItemLoaderStates() {
        var _a;
        let newStatus = this._pendingLoad ? AssetListStatus.Loading : AssetListStatus.NotStarted;
        // Remove any previous error.
        this._errors = undefined;
        for (const loaderState of this._loaderStates) {
            switch (loaderState.status) {
                case AssetListStatus.HadLoaderErrors:
                    // We only surface the error in cases when there's no loader pending to start.
                    if (this._pendingLoad) {
                        break;
                    }
                    // We surface errors immediately.
                    this._status = AssetListStatus.HadLoaderErrors;
                    this._errors = [loaderState.error];
                    // Return early to avoid cleaning previous generation items.
                    return;
                case AssetListStatus.ReadyHasMorePages:
                    if (newStatus !== AssetListStatus.Loading && !((_a = this._loadOptions) === null || _a === void 0 ? void 0 : _a.loadAllPages)) {
                        // If we don't have a loader that is still loading, and we're not loading all pages, then
                        // change status to ReadyHasMorePages
                        newStatus = AssetListStatus.ReadyHasMorePages;
                    }
                    break;
                case AssetListStatus.Loading:
                    // We always want to know when a page is still loading.
                    newStatus = AssetListStatus.Loading;
                    break;
                case AssetListStatus.ReadyFullyLoaded:
                    if (newStatus !== AssetListStatus.ReadyHasMorePages && newStatus !== AssetListStatus.Loading) {
                        // If we don't have a loader that still has pages to load and if we don't actually load something.
                        newStatus = AssetListStatus.ReadyFullyLoaded;
                    }
                    break;
            }
        }
        this._status = newStatus;
    }
    _dispatchItemsChangedEvent() {
        // Let the client know they have to read the item list again.
        this.dispatch(SharedListChangedEvent);
    }
    _updateListItemsFromItemLoaderStates() {
        this._applyItemsChangedEvents();
        const itemSet = new Set();
        let total;
        // Collect the items from all the loaders.
        for (const loaderState of this._loaderStates) {
            loaderState.collectAllItems(itemSet);
            if (loaderState.total !== undefined) {
                total = Math.max(total || 0, loaderState.total);
            }
        }
        // Collect any items that were changed on the client
        // side and might not be captured in the requests from
        // the server side yet.
        for (const itemGeneration of this._itemUpdatesByGeneration.values()) {
            for (const [item, state] of itemGeneration.entries()) {
                if (state) {
                    itemSet.add(item);
                }
                else {
                    itemSet.delete(item);
                }
            }
        }
        const items = Array.from(itemSet);
        // Sort the items based on the current ordering.
        this.order.sortItems(items);
        this._items = items;
        this._total = total;
        this._dispatchItemsChangedEvent();
    }
    /**
     * Handles changes injected through the item pool. These can be either local changes
     * made by the user, or changes detected while fetching other lists.
     *
     * We use this mechanism to propagate the changes immediately across the entire app.
     * For example, if a different call detects a Space has been updated, all the active
     * lists will get the notification and automatically apply the change to their own listing.
     *
     * We are only interested about additions and deletions. Note that items
     * can be added (or removed) to the list if the mutations of the item properties make the
     * updated item pass (or fail) the filter of the current list.
     */
    _applyItemsChangedEvents() {
        const pendingNotifications = this._pendingNotifications;
        if (!pendingNotifications) {
            return;
        }
        this._pendingNotifications = undefined;
        // Break down changes into their own generations of added/removed items.
        for (const notification of pendingNotifications) {
            const { event, generation } = notification;
            if (generation < this._completedGeneration) {
                // Ignore changes from completed generations. We already have all
                // the correct items received from the server.
                continue;
            }
            let itemGeneration = this._itemUpdatesByGeneration.get(generation);
            if (!itemGeneration) {
                itemGeneration = new Map();
                this._itemUpdatesByGeneration.set(generation, itemGeneration);
            }
            if (event.addedItems) {
                for (const item of event.addedItems) {
                    if (this.implicitFilter.matches(item) && this.filter.matches(item)) {
                        itemGeneration.set(item, true);
                    }
                }
            }
            if (event.changedItems) {
                for (const item of event.changedItems) {
                    // Check if we have any items that pass the filter test or not.
                    // We need to store both matching and non-matching, just in case
                    // an non-matching item was matching before the update and
                    // we now have to manually remove it from the list fetched from
                    // the server.
                    const included = this.implicitFilter.matches(item) && this.filter.matches(item);
                    itemGeneration.set(item, included);
                }
            }
            if (event.discardedItems) {
                for (const item of event.discardedItems) {
                    // Any discarded items must be removed entirely.
                    itemGeneration.set(item, false);
                }
            }
        }
    }
}
__decorate([
    observable(SharedStateChangedEvent)
], ItemList.prototype, "status", null);
__decorate([
    observable(SharedStateChangedEvent)
], ItemList.prototype, "errors", null);
__decorate([
    observable(SharedListChangedEvent)
], ItemList.prototype, "items", null);
__decorate([
    observable(SharedListChangedEvent)
], ItemList.prototype, "total", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const ITEMS_CHANGED = 'itemsChanged';
class ItemsChangedEvent extends SAMEvent {
    constructor(addedItems, changedItems, discardedItems, updateContext) {
        super(ITEMS_CHANGED);
        this.addedItems = addedItems;
        this.changedItems = changedItems;
        this.discardedItems = discardedItems;
        this.updateContext = updateContext;
    }
}
function compareDelegatesByPriority(a, b) {
    if (a.priority === b.priority) {
        return 0;
    }
    return a.priority === 'list' ? -1 : 1;
}
/**
 * The ItemPool class is used to keep unique references in memory for objects that
 * represent cloud items like assets, teams or spaces.
 * The ItemPool helps with mapping between a remote id and a local reference in memory.
 *
 * On supported browsers the pool uses the WeakRef object to keep the objects alive.
 * only as long as they are used by the application.
 */
class ItemPool extends TypedEventEmitter {
    constructor(_core, type) {
        super();
        this._core = _core;
        this.type = type;
        this._objectsById = new Map();
        this._discardedObjects = new WeakSet();
        this._delegates = [];
        this._hasUnsortedDelegates = false;
        /**
         * Looks up the item in the local in-memory storage.
         * The items are stored used WeakRef to avoid leaking. The client of this
         * method is responsible with keeping these objects alive for as long as
         * they are needed.
         * @param id
         * @returns
         */
        this.getLocalItemById = (id) => {
            const ref = this._getObjectsById().get(id);
            return ref === null || ref === void 0 ? void 0 : ref.deref();
        };
        this.removeLocalItems = (ids) => {
            if (Array.isArray(ids)) {
                ids.forEach((id) => {
                    this._objectsById.delete(id);
                });
                return;
            }
            this._objectsById.delete(ids);
        };
        this.isItemDiscarded = (item) => {
            return this._discardedObjects.has(item);
        };
        this._updateSessionDelegate = () => {
            return {
                getLocalItemById: this.getLocalItemById,
                isItemDiscarded: this.isItemDiscarded,
                commitUpdateSession: (session) => {
                    if (!session.hasChanges) {
                        return;
                    }
                    const objectsById = this._objectsById;
                    const discardedObjects = this._discardedObjects;
                    // Make a note about discarded objects.
                    const { discardedItems } = session;
                    if (discardedItems) {
                        for (const item of discardedItems) {
                            discardedObjects.add(item);
                        }
                    }
                    // Inject the new items in the local map.
                    const { addedItems } = session;
                    if (addedItems) {
                        for (const item of addedItems) {
                            objectsById.set(item.id, new WeakRef$1(item));
                            discardedObjects.delete(item);
                        }
                    }
                    const event = new ItemsChangedEvent(session.addedItems, session.changedItems, session.discardedItems, session.updateContext);
                    const delegates = this._delegates;
                    if (this._hasUnsortedDelegates) {
                        delegates.sort(compareDelegatesByPriority);
                        this._hasUnsortedDelegates = false;
                    }
                    let hasEmptyWeakRefs = false;
                    for (const delegateRef of delegates) {
                        const delegate = delegateRef.delegate.deref();
                        if (!delegate) {
                            // Remember to filter out the expired weak refs.
                            hasEmptyWeakRefs = true;
                            continue;
                        }
                        delegate.onItemsUpdated(event, this);
                    }
                    if (hasEmptyWeakRefs) {
                        // Make another pass and keep only the delegates that are still alive.
                        this._delegates = delegates.filter((ref) => ref.delegate.deref());
                    }
                    this.dispatch(event);
                }
            };
        };
    }
    _getObjectsById() {
        return this._objectsById;
    }
    /**
     * Creates an UpdateSession instance for add/update/delete of models in the SAM system, outside of ItemLoaders.
     * UpdateSessions let you delay firing events until you're ready to notify clients via the commit--typically
     * right after updating the model with remote data via one or more ItemUpdaters.
     *
     * @param updateContext {string} (optional) Useful to identify what is causing a particular change, especially
     *        in ItemPool delegates.
     * @param mostRecentValidMutation {number} (optional) Determines if UpdateSession will allow the update (see
     *        {@link createLoaderUpdateSession})
     */
    createUpdateSession(updateContext, mostRecentValidMutation) {
        return new UpdateSession(this._core, this._updateSessionDelegate(), updateContext, mostRecentValidMutation);
    }
    /**
     * For use in USS-based ItemLoaders.
     *
     * @param mostRecentValidMutation {number} UpdateSession only allows changes to items that that have a
     *        `lastKnownClientMutation` before this timestamp. Since USS index lag can cause data to be 2-3 seconds
     *        stale, all USS-based ItemLoaders *must* pass `mostRecentValidMutation` of
     *        `Date.now() - core.environment.getValue('USS_LIST_UPDATE_LATENCY')`
     */
    createLoaderUpdateSession(mostRecentValidMutation) {
        return new UpdateSession(this._core, this._updateSessionDelegate(), LOADER_UPDATE_CONTEXT, mostRecentValidMutation);
    }
    /**
     * Creates an UpdateSession for add/update/delete of models in the SAM system that get tracked as
     * `item.lastKnownClientMutation`. Used when the data source is the "source of truth" for this model, in order to
     * prevent stale data from overwriting it. E.g., for AdobeAsset, it should be used when loading a single Asset
     * from R-API.
     */
    createMutationSession() {
        return new UpdateSession(this._core, this._updateSessionDelegate(), undefined, undefined, 'mutation');
    }
    /**
     * Creates an UpdateSession for ItemLoader implementations, as well as updates `item.lastKnownClientMutation`.
     * when models are added/updated/deleted. Used when the data source is the "source of truth" for this model,
     * in order to prevent stale data from overwriting it via `create*UpdateSession()`.
     */
    createLoaderMutationSession() {
        return new UpdateSession(this._core, this._updateSessionDelegate(), LOADER_UPDATE_CONTEXT, undefined, 'mutation');
    }
    /**
     * Adds a delegate to receive notifications when items change.
     * The delegates cannot be removed, so the connection is alive
     * as long as both the pool and the delegate are still referenced.
     * The client must ensure the delegate is kept alive until the events
     * are not necessary anymore.
     *
     * Performance: Adding too many delegates can obviously hurt performance.
     * Make sure that the callbacks on the delegate are as fast as possible.
     * If the user is not interested in any updates, it is best to make a note
     * that the list is dirty and return as quick as possible. Make sure to delay
     * any of the actual work until the data is requested by the client.
     *
     * Note that we are not adding this on top of the event dispatcher to avoid
     * making the weak ref model a wide spread solution across the repo.
     *
     * @param delegate
     */
    addDelegate(delegate, priority) {
        this._delegates.push({ priority, delegate: new WeakRef$1(delegate) });
        this._hasUnsortedDelegates = true;
    }
    forEach(fn) {
        const objectsById = this._objectsById;
        for (const [key, itemRef] of objectsById) {
            const item = itemRef.deref();
            if (!item) {
                // Make sure to cleanup the interface while we do this.
                objectsById.delete(key);
                continue;
            }
            fn(item);
        }
    }
    map(fn) {
        const results = [];
        this.forEach((item) => {
            results.push(fn(item));
        });
        return results;
    }
    /**
     * Returns all items currently in the ItemPool as an Array
     */
    toArray() {
        return this.map(item => item);
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
var SearchProps;
(function (SearchProps) {
    SearchProps["STORAGE_ASSIGNEE_TYPE"] = "storage:assignee$$type";
    SearchProps["VISIBLITY"] = "app_metadata$$shell:visibility";
    SearchProps["CREATIVE_CLOUD_TOPLEVEL_COLLECTION_NAME"] = "creative_cloud_toplevel_collection_name";
    SearchProps["FIELD_EXISTS"] = "op_field_exists";
    SearchProps["INCLUDES"] = "includes";
    SearchProps["TYPE"] = "type";
    SearchProps["ARTIFACT_FIELD"] = "app_metadata$$review:sourceAsset$$dc:format";
    SearchProps["STORAGE_ASSIGNEE_ID"] = "storage:assignee$$id";
    SearchProps["SHARED_VIA_INVITE"] = "shared_via_invite";
    SearchProps["OP_AND"] = "op_and";
    SearchProps["OP_OR"] = "op_or";
    SearchProps["REPO_ACL_MODIFIER"] = "repo:acl$$repo:modifier";
    SearchProps["REPO_ACL_PRIVILEGES"] = "repo:acl$$repo:privileges";
    SearchProps["REPO_ACL_RELATIONS"] = "repo:acl$$repo:relations";
    SearchProps["CREATED_FROM_UPLOAD"] = "app_metadata$$cc:createdFromUpload";
    SearchProps["CREATED_BY_CLIENT_ID"] = "repo:createdByClientId";
})(SearchProps || (SearchProps = {}));
const LastAccessedOrderField = 'api:metadata/peruser#mine$$shell:lastAccessDate';
const OrderByToUSSOrderBy = {
    name: 'asset_name',
    created: 'create_date',
    modified: 'modify_date',
    shared: 'shared_date',
    lastAccessed: LastAccessedOrderField
};

var name = "@ccx-public/shared-asset-model-js";
var version = "9.2.40";
var publishConfig = {
	registry: "https://artifactory.corp.adobe.com/artifactory/api/npm/npm-adobe-release-local/",
	scope: "@ccx-public"
};
var repository = {
	type: "git",
	url: "https://git.corp.adobe.com/torq/acpl-model"
};
var license = "Apache-2.0";
var main = "./dist/cjs/src/index.js";
var module = "./dist/mjs/src/index.js";
var types = "./dist/mjs/src/index.d.ts";
var exports = {
	".": {
		"import": "./dist/mjs/src/index.js",
		require: "./dist/cjs/src/index.js"
	}
};
var dependencies = {
	"@types/json-patch": "0.0.30",
	"@types/semver": "^7.3.13",
	"abort-controller": "3.0.0",
	"http-link-header": "1.1.0",
	"uri-templates": "0.2.0",
	uuid: "3.3.2"
};
var engines = {
	node: ">=16.13.2"
};
var devDependencies = {
	"@relative-ci/agent": "^4.2.3",
	"@rollup/plugin-commonjs": "^20.0.0",
	"@rollup/plugin-json": "^4.1.0",
	"@rollup/plugin-node-resolve": "^13.0.4",
	"@types/http-link-header": "1.0.3",
	"@types/jest": "^26.0.23",
	"@types/nock": "^11.1.0",
	"@types/node-fetch": "^2.5.10",
	"@types/react-dom": "^17.0.9",
	"@types/uri-templates": "file:@types/uri-templates",
	"@types/uuid": "^3.3.2",
	"@typescript-eslint/eslint-plugin": "^4.22.0",
	"@typescript-eslint/parser": "^4.22.0",
	"cross-fetch": "^3.1.4",
	eslint: "^7.25.0",
	events: "^3.0.0",
	"fake-indexeddb": "^3.1.3",
	husky: "4.3.3",
	jest: "^26.6.3",
	"lint-staged": "^10.3.0",
	mobx: "^6.3.2",
	nock: "^13.0.5",
	postcss: "^8.3.6",
	react: "^17.0.2",
	"react-dom": "^17.0.2",
	rollup: "^2.56.2",
	"rollup-plugin-copy": "^3.4.0",
	"rollup-plugin-livereload": "^2.0.5",
	"rollup-plugin-polyfill-node": "^0.7.0",
	"rollup-plugin-postcss": "^4.0.1",
	"rollup-plugin-serve": "^1.1.0",
	"rollup-plugin-typescript2": "^0.32.0",
	"rollup-plugin-webpack-stats": "^0.2.3",
	"ts-jest": "^26.4.4",
	"ts-node": "^9.0.0",
	tslib: "^2.1.0",
	typedoc: "^0.19.1",
	"typedoc-plugin-external-module-name": "^4.0.3",
	typescript: "4.2.4",
	"typescript-plugin-css-modules": "^3.4.0"
};
var scripts = {
	test: "node --expose-gc --allow-natives-syntax ../../node_modules/jest/bin/jest.js --no-cache",
	"test:debugnock": "DEBUG=nock.* yarn test",
	"test:watch": "node --allow-natives-syntax ../../node_modules/jest/bin/jest.js --watchAll --no-cache",
	lint: "eslint --ext .ts,.tsx,.js --quiet ./src",
	"lint:fix": "eslint --fix --ext .ts,.tsx,.js --quiet ./src",
	prebuild: "yarn tsc --build --force",
	build: "yarn rollup -c ./rollup.config.js",
	postbuild: "node ./scripts/postbuild.js && npx relative-ci-agent",
	watch: "yarn rollup -c ./rollup.config.js --watch",
	typedoc: "typedoc --name 'Shared Asset Model (SAM) JS' --exclude '**/*.js' src/",
	"publish-docs": "./scripts/generateDocs.sh"
};
var husky = {
	hooks: {
		"pre-commit": "lint-staged"
	}
};
var packageJSON = {
	name: name,
	version: version,
	"private": false,
	publishConfig: publishConfig,
	repository: repository,
	license: license,
	main: main,
	module: module,
	types: types,
	exports: exports,
	dependencies: dependencies,
	engines: engines,
	devDependencies: devDependencies,
	scripts: scripts,
	husky: husky,
	"lint-staged": {
	"**/*.{ts,tsx,js}": [
		"eslint --fix",
		"yarn test --bail --findRelatedTests --coverage=false"
	]
}
};

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const DefaultSearchQuery = {
    scope: ['creative_cloud'],
    // Ensures assetIds in results are returned with the correct casing. E.g. ":US:" instead of ":us:"
    asset_id_opacity: true
};
class USSInterface extends InterfaceBase {
    _getEndpointHref(endpoint) {
        return `https://${this._core.environment.endpoints.USS_HOST}${endpoint}`;
    }
    async getQueryResponse(searchQuery) {
        searchQuery = Object.assign(Object.assign({}, DefaultSearchQuery), searchQuery);
        const endpointHref = this._getEndpointHref('/universal-search/v2/search');
        const { PRODUCT_ID } = this._core.environment.getValue('HOST_PRODUCT_DATA');
        const response = await this._doPost(endpointHref, {
            'Content-Type': 'application/vnd.adobe.search-request+json',
            'X-Product': PRODUCT_ID,
            'X-Product-Location': `SAM.js/${packageJSON.version}`
        }, JSON.stringify(searchQuery), [200]);
        return response.json();
    }
    async searchSpace(parentFolderId, searchArtifacts, embedPermissions, options) {
        var _a, _b, _c, _d;
        const query = {
            hints: [{ acp_platform_v2: true, creative_cloud_rendition_v2: true }],
            fetch_fields: { includes: ['app_metadata', 'collaboration_metadata'] },
            sort_orderby: (_a = options === null || options === void 0 ? void 0 : options.sort_orderby) !== null && _a !== void 0 ? _a : 'modify_date',
            sort_order: (_b = options === null || options === void 0 ? void 0 : options.sort_order) !== null && _b !== void 0 ? _b : 'desc',
            limit: (_c = options === null || options === void 0 ? void 0 : options.limit) !== null && _c !== void 0 ? _c : 50,
            start_index: (_d = options === null || options === void 0 ? void 0 : options.start_index) !== null && _d !== void 0 ? _d : 0,
            [SearchProps.TYPE]: (options === null || options === void 0 ? void 0 : options.type) || [],
            container: { asset_id: [parentFolderId] },
        };
        if (!searchArtifacts) {
            query.op_none_of = [
                // exclude Artifacts
                { op_field_exists: [SearchProps.ARTIFACT_FIELD] },
                // exclude hidden
                { [SearchProps.VISIBLITY]: ['hiddenSelf', 'hiddenRecursive'] }
            ];
        }
        else {
            // Only include artifacts
            query.op_field_exists = [SearchProps.ARTIFACT_FIELD];
        }
        if (embedPermissions) {
            query.fetch_fields.includes.push('_embedded');
        }
        return this.getQueryResponse(query);
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class InvitationInterface extends InterfaceBase {
    constructor() {
        super(...arguments);
        this._batchRequestLimit = 50;
    }
    get batchRequestLimit() {
        return this._batchRequestLimit;
    }
    _getUrl(endpoint) {
        return `https://${this._core.environment.endpoints.INVITATIONS_HOST}${endpoint}`;
    }
    async getCollaborators(urn) {
        // This allows piggybacking on a single request, so that we can support loading both the collaborators
        // and invitations at the same time, without making two separate requests (the data for both of these
        // is in the same response).
        return await this._getSharedResponse(this._getUrl(`/api/v4/share/${encodeURIComponent(urn)}?extraFeatures=direct_acl`));
    }
    async addCollaborators(urn, request) {
        await this._doPost(this._getUrl(`/api/v4/share/${encodeURIComponent(urn)}`), {}, JSON.stringify(request), [200]);
    }
    async modifyCollaborators(urn, request) {
        const response = await this._doPatch(this._getUrl(`/api/v4/share/${encodeURIComponent(urn)}`), {}, JSON.stringify(request), [200]);
        return await response.json();
    }
    async getCollaboratorListForUrns(urns) {
        const request = {
            'operation': 'bulkGetShares',
            'parameters': {
                urns,
                'queryParams': {
                    'extraFeatures': 'direct_acl,legacy_role'
                }
            }
        };
        const response = await this._doPost(this._getUrl(`/ops/v4/shares`), { 'Content-Type': 'application/json' }, JSON.stringify(request), [200]);
        const result = await response.json();
        // response is 200, but result is not in proper format
        if (!result.result || !Array.isArray(result.result)) {
            throw SAMError.create(SAMErrorCode.UNKNOWN, 'Failed to parse response for Invitation Service bulk API.');
        }
        return result;
    }
    async confirmAccess(urn) {
        await this._doPost(this._getUrl(`/api/v4/invitations/urn/${encodeURIComponent(urn)}`), {}, undefined, [200]);
    }
    // might be able to remove this after invitation service handles https://jira.corp.adobe.com/browse/CPX-7447
    _shouldRetryMonitorResponse(asyncResponse, body) {
        if (asyncResponse.status === 200) {
            const responseBody = JSON.parse(body);
            if (responseBody.jobStatus !== 'completed') {
                return true;
            }
        }
        return false;
    }
    async declineAccess(urn) {
        await this._doDelete(this._getUrl(`/api/v4/invitations/urn/${encodeURIComponent(urn)}`), {}, [200]);
    }
    async getPendingInvitations() {
        const response = await this._doGet(this._getUrl('/api/v4/invitations'), { headers: { 'Content-Type': 'application/json' } });
        return await response.json();
    }
}

function isStrictEqual(a, b) {
    return a === b;
}
function deepClone(value) {
    return JSON.parse(JSON.stringify(value));
}
/**
 * Compares two items, including all their iterable properties, and all their nested properties.
 *
 * @param a {any} is compared to b
 * @param b {any} is compared to a
 * @param ignoreArrayOrder {boolean} (optional) when true, order of arrays does not matter.
 * @return {boolean} true if they match
 */
function isDeepEqual(a, b, ignoreArrayOrder = false) {
    if (a === b) {
        return true;
    }
    if (typeof a !== typeof b || a.constructor !== b.constructor) {
        return false;
    }
    if (a instanceof Date) {
        return a.toISOString() === b.toISOString();
    }
    if (Array.isArray(a)) {
        const count = a.length;
        if (count !== b.length) {
            return false;
        }
        let arry1 = a;
        let arry2 = b;
        if (ignoreArrayOrder) {
            // copy array to avoid mutating the original arrays
            // .slice(0) is fastest, even faster than .slice()
            arry1 = a.slice(0).sort();
            arry2 = b.slice(0).sort();
        }
        for (let i = 0; i < count; ++i) {
            if (!isDeepEqual(arry1[i], arry2[i], ignoreArrayOrder)) {
                return false;
            }
        }
        return true;
    }
    if (a instanceof Set) {
        if (a.size !== b.size) {
            return false;
        }
        for (const key of a.keys()) {
            if (!b.has(key)) {
                return false;
            }
        }
        return true;
    }
    if (a instanceof Map) {
        if (a.size !== b.size) {
            return false;
        }
        for (const key of a.keys()) {
            if (!b.has(key)) {
                return false;
            }
            if (!isDeepEqual(a.get(key), b.get(key), ignoreArrayOrder)) {
                return false;
            }
        }
        return true;
    }
    if (typeof a === 'object') {
        const keysA = Object.keys(a);
        const keysB = Object.keys(b);
        const count = keysA.length;
        if (count !== keysB.length) {
            return false;
        }
        keysA.sort();
        keysB.sort();
        for (let i = 0; i < count; ++i) {
            const key = keysA[i];
            if (key !== keysB[i]) {
                return false;
            }
            if (!isDeepEqual(a[key], b[key], ignoreArrayOrder)) {
                return false;
            }
        }
        return true;
    }
    // We don't support other types.
    return false;
}
const secondsPerHour = 60 * 60;
const millisecondsPerHour = secondsPerHour * 1000;

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// @TODO Verify this solution and address the following task.
// We should also write unit tests precisely verifying this behavior.
// https://jira.corp.adobe.com/browse/SAM-2677
const finalizationRegistry = new FinalizationRegistry(objUrl => {
    URL.revokeObjectURL(objUrl);
});
var RenditionStatus;
(function (RenditionStatus) {
    /**
     * Initial status
     */
    RenditionStatus[RenditionStatus["NotStarted"] = 0] = "NotStarted";
    RenditionStatus[RenditionStatus["Fetching"] = 1] = "Fetching";
    RenditionStatus[RenditionStatus["Done"] = 2] = "Done";
    /**
     * Occurs when there are any errors around fetching the
     * rendition from the service, AdobeAsset.clearRenditionError can
     * be used to clear the error and fetch again.
     */
    RenditionStatus[RenditionStatus["Error"] = 3] = "Error";
})(RenditionStatus || (RenditionStatus = {}));
var RenditionSize;
(function (RenditionSize) {
    RenditionSize[RenditionSize["Size64"] = 64] = "Size64";
    RenditionSize[RenditionSize["Size128"] = 128] = "Size128";
    RenditionSize[RenditionSize["Size256"] = 256] = "Size256";
    RenditionSize[RenditionSize["Size512"] = 512] = "Size512";
    RenditionSize[RenditionSize["Size1024"] = 1024] = "Size1024";
})(RenditionSize || (RenditionSize = {}));
var RenditionType;
(function (RenditionType) {
    RenditionType["PNG"] = "image/png";
    RenditionType["JPEG"] = "image/jpeg";
})(RenditionType || (RenditionType = {}));
const THUMBNAIL_SIZE = RenditionSize.Size512; // matches ACPL/SAM.cpp thumbnail size
const SUPPORTED_SIZES = [
    RenditionSize.Size64,
    RenditionSize.Size128,
    RenditionSize.Size256,
    RenditionSize.Size512,
    RenditionSize.Size1024
];
class RenditionFetcher extends TypedEventEmitter {
    constructor(_core) {
        super();
        this._core = _core;
        this._entries = [];
        this._etag = '';
    }
    /**
     * Given any number, this method will return a predefined
     * size that is greater than or equal to the target size.
     * You may also specify an existing sizes array.
     *
     * @param targetSize The preferred or target size.
     * @param sizes An array of sizes to sort and find.
     * @returns
     */
    _getStandardSize(targetSize, sizes = SUPPORTED_SIZES) {
        // Clone the provided array so we don't alter it.
        const sortedArray = [...sizes].sort((a, b) => a - b);
        const index = sortedArray.findIndex((s) => s >= targetSize);
        // If no sizes are large enough, return the largest
        if (index === -1) {
            return sortedArray[sortedArray.length - 1];
        }
        return sortedArray[index];
    }
    _findURLEntry(size, type) {
        const clampedSize = this._getStandardSize(size);
        if (this._entries.length > 0) {
            return this._entries.find((u) => u.size === clampedSize && u.type === type && u.status !== RenditionStatus.Error);
        }
        return undefined;
    }
    /**
     * Removes all entries matching the provided outdated
     * etag.
     *
     * @param outdatedEtag
     */
    _clearStaleEntries(outdatedEtag) {
        const filteredEntries = this._entries.filter((entry) => {
            if (entry.etag === outdatedEtag) {
                URL.revokeObjectURL(entry.url);
                return false;
            }
            return true;
        });
        this._entries = filteredEntries;
    }
    /**
     * Finds an entry based on the provided size/type and returns
     * a fallback size that is greater than the target, if available.
     *
     * @param size
     * @param type
     * @returns {IURLEntry}
     */
    _getEntryWithFallback(size, type) {
        const foundEntry = this._findURLEntry(size, type);
        if (foundEntry) {
            return foundEntry;
        }
        // If the exact size is not available and there
        // are other sizes and types, return the size
        // that is at-least greater than or equal to the current size.
        const sizesArray = this._entries.map((entry) => entry.size);
        if (this._entries.length > 0) {
            // Since we are passing in our own sizes array
            // this should return the closest size already
            // available.
            const foundSize = this._getStandardSize(size, sizesArray);
            return this._entries.find(s => s.size === foundSize);
        }
        return undefined;
    }
    /**
     * Returns the download status of the requested rendition.
     *
     * @param size
     * @param type
     * @param etag
     * @returns
     */
    renditionURLStatus(size, type, etag) {
        var _a;
        const clampedSize = this._getStandardSize(size);
        const urlStatus = this._entries.find(e => e.etag === etag
            && e.size >= clampedSize
            && e.type === type);
        return (_a = (urlStatus && urlStatus.status)) !== null && _a !== void 0 ? _a : RenditionStatus.NotStarted;
    }
    get etag() {
        return this._etag;
    }
    /**
     *
     * @param size Target size for the rendition
     * @param type String type for example image/png.
     * @param etag The etag string for cache busting.
     * @param assetInterface {AssetInterface}
     * @returns {string} The URL for the rendition.
     */
    getURL(size, type, etag, assetInterface) {
        const etagChanged = etag !== this.etag;
        const renditionEntry = this._getEntryWithFallback(size, type);
        const clampedSize = this._getStandardSize(size);
        // If we do have a rendition wether it be exact or a fallback
        // we could return it but if the requested size is larger
        // than the one we have in the cache we will need to add a new entry
        // and start fetching.
        if (renditionEntry && renditionEntry.size >= clampedSize && !etagChanged) {
            return renditionEntry.url;
        }
        const newEntry = {
            size: clampedSize,
            status: RenditionStatus.Fetching,
            url: '',
            type,
            etag
        };
        // If we find an existing entry that exactly matches our new
        // entry then we wont need to fetch again.
        const recentlyAddedEntry = this._entries.find(e => isDeepEqual(e, newEntry)
            // We also want to check if there is an identical entry
            // but with an error so that we don't keep fetching over and
            // over again in case of a thumbnail fetching error.
            || isDeepEqual(e, Object.assign(Object.assign({}, newEntry), { status: RenditionStatus.Error })));
        // If we know we have an existing entry and we have
        // a new entry that was recently added but it's state
        // is loading then we need to just return the existing url
        // until this is no longer true.
        // @note Without this logic, the ui will go into an infinite
        // loop causing the browser to crash because we are fetching
        // and pushing change notifications over and over again.
        if (renditionEntry && recentlyAddedEntry) {
            return renditionEntry.url;
        }
        this._entries.push(newEntry);
        this._fetch(newEntry, assetInterface);
        // If we have a rendition entry after starting to fetch
        // it means that we have a new size requested with a fallback.
        // Return the fallback while the new size is fetching.
        if (renditionEntry) {
            return renditionEntry.url;
        }
        // If the etag hasn't changed and we have an entry we should
        // return the existing entry while waiting for the new one.
        return newEntry.url;
    }
    // First we find if there is an existing entry
    // then if there isn't, we create one
    // if there is, we update the existing entry
    async _fetch(entry, assetInterface) {
        const { size, type, etag } = entry;
        const ai = await assetInterface;
        this.dispatch({ type: 'change' });
        // We want to fetch the URL contents and create an objectURL and return the path
        // to clients instead of the raw URL because services in future will not
        // allow the authorization token to be passed directly in the rendition URL
        try {
            const renditionBlob = await ai.getRendition(size, type);
            // If there is a new etag all the old URLS that are now
            // out of date so let's remove the existing ones.
            if (etag && etag !== this._etag) {
                this._clearStaleEntries(this._etag);
                this._etag = etag;
            }
            entry.size = size;
            entry.type = type;
            entry.url = URL.createObjectURL(renditionBlob);
            finalizationRegistry.register(this, entry.url, entry);
            entry.status = RenditionStatus.Done;
            entry.etag = etag;
            this.dispatch({ type: 'change' });
        }
        catch (e) {
            // IF there was an error fetching the thumbnail, we don't
            // want clients stuck in a bad state so we set the rendition
            // to an error dispatch a change event.
            entry.status = RenditionStatus.Error;
            this.dispatch({ type: 'change' });
        }
    }
    /**
     * Resets the rendition status on the requested rendition to RenditionStatus.NotStarted
     */
    clearRenditionError(size, type, etag) {
        const clampedSize = this._getStandardSize(size);
        // Find an entry that has all the properties
        // of the request one including the error status.
        // Leaving out the URL case if for some reason it has a value
        // but we still want to reset the error.
        const foundErrorEntryIndex = this._entries.findIndex(e => e.size >= clampedSize
            && e.type === type
            && e.etag === etag
            && e.status === RenditionStatus.Error);
        if (foundErrorEntryIndex === -1) {
            return;
        }
        // Remove the entry so that when getURL is called again
        // it will add a new entry and begin the fetch process
        // again.
        this._entries.splice(foundErrorEntryIndex, 1);
        this.dispatch({ type: 'change' });
    }
    release() {
        if (this._entries.length === 0) {
            return;
        }
        this._entries.forEach((value) => {
            URL.revokeObjectURL(value.url);
            finalizationRegistry.unregister(this);
        });
        this._entries = [];
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const MAX_LENGTH = 255;
const RESERVED_NAME_REGEX = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])$/i;
const RESERVED_CHAR_REGEX = /[\\/:*?"|><]/;
function validatePlatformRepoName(name) {
    return (name.length > 0
        && name.length <= MAX_LENGTH
        && !RESERVED_NAME_REGEX.test(name)
        && !RESERVED_CHAR_REGEX.test(name));
}
const PATH_REGEX = /^(.*\/)?(.*?)(?:[\u00A0][^.]*)?(\.[^.]*)?$/;
/**
 * Called by SAM to get new name according to the specified format and counter
 * @param fullPath path of the asset, it can be full path/relative path/filename
 * @param format format should contain %@ and %d
 *      %@ to be replace with filename
 *      %d to be replace with supplied counter. counter value will change in following cases:
 *          - filename matches the format and counter supplied. counter will increment by 1
 *          - filename matches the formatWithoutCount and counter = 0. counter will be made 1
 *            e.g name = test copy, format = %@ copy %d, counter = 0 -> newName = test copy 1, countUsed = 1
 * @param counter count to be used in newName
 * @returns the IFormatFileNameResult object which contains new name and count used in new name
 */
function formatFileName(fullPath, format, counter) {
    // create regex from client passed format
    function getRegexFormat(format, extPresent) {
        // replace '(', ')' with backspace '\(', '\)'
        let regexFormat = format.replace(/[()]/g, '\\$&');
        // insert match patern for %d and %@
        regexFormat = regexFormat.replace('%d', '([0-9]+)');
        regexFormat = regexFormat.replace('%@', '(.*)?');
        // if extPresent is true, we expect to find extension, otherwise it is optional
        regexFormat = regexFormat + (extPresent ? '(\\.[^.]*)$' : '(\\.[^.]*)?$');
        return regexFormat;
    }
    const extPresent = fullPath.lastIndexOf('.') > fullPath.lastIndexOf('/');
    const regexFormat = getRegexFormat(format, extPresent);
    // remove count from string
    const formatWithoutCount = format.replace(/ [^ ]*$/, '');
    const regexFormatWithoutCount = getRegexFormat(formatWithoutCount, extPresent);
    // get parsed result
    let result = fullPath.match(regexFormat);
    let resultMatchedRegexWithoutCount = false;
    if (result === null) {
        result = fullPath.match(regexFormatWithoutCount);
        resultMatchedRegexWithoutCount = result !== null;
    }
    let newPath = '';
    let fileExtension = '';
    if (result) {
        let path = '';
        let extension = '';
        let currentCount = '';
        if (resultMatchedRegexWithoutCount) {
            [, path, extension] = result;
        }
        else {
            [, path, currentCount, extension] = result;
        }
        newPath = path;
        fileExtension = extension;
        if (resultMatchedRegexWithoutCount && counter === 0) {
            // since the name does not contain the count
            // but matches the regex without count, so
            // if counter=0, next name in iteration is with counter=1
            counter = 1;
        }
        else if (!resultMatchedRegexWithoutCount && parseInt(currentCount) === counter) {
            // since the name matches the format with count
            // we should skip the counter that matches the current name
            counter += 1;
        }
    }
    else {
        const [, directory, filename, extension] = fullPath.match(PATH_REGEX); // All strings match the regexp above
        newPath = `${directory || ''}${filename || ''}`;
        fileExtension = extension;
    }
    let newName = '';
    if (counter !== 0) {
        newName = format.replace('%@', `${newPath || ''}`);
        newName = newName.replace('%d', counter.toString());
    }
    else {
        newName = formatWithoutCount.replace('%@', `${newPath || ''}`);
    }
    return {
        newName: `${newName || ''}${fileExtension || ''}`,
        countUsed: counter
    };
}
/**
 * Called to get newName as per format in case of batch ops. It considers name of other files as well in the operation for finding conflicts
 * @param core SAMCore
 * @param assetId Asset Id
 * @param newName FileName
 * @param sameFolder true if copying in same folder
 * @param assetConflictMap It saves the newName and count used for all files in batch
 */
function batchFormatFileName(core, assetId, newName, sameFolder, assetConflictMap) {
    var _a;
    let localCounter = ((_a = assetConflictMap.get(assetId)) === null || _a === void 0 ? void 0 : _a.countUsed) || 0;
    let conflictWithOtherFilesInBatch = false;
    const conflictedAssetIds = [];
    // check if we are copying in the same folder
    // if yes, then it should follow ASSET_COPY_NAME_FORMAT i.e. %@ copy (%d)
    // if no and conflict, then it should follow ASSET_CONFLICT_NAME_FORMAT i.e. %@ (%d)
    do {
        const formatFileResult = sameFolder ? formatFileName(newName, core.environment.getValue('ASSET_COPY_NAME_FORMAT'), localCounter)
            : (localCounter > 0 ? formatFileName(newName, core.environment.getValue('ASSET_CONFLICT_NAME_FORMAT'), localCounter) : { newName, countUsed: localCounter });
        newName = formatFileResult.newName;
        localCounter = formatFileResult.countUsed;
        conflictWithOtherFilesInBatch = false;
        for (const [key, value] of assetConflictMap) {
            if (value.newName === newName) {
                conflictWithOtherFilesInBatch = true;
                conflictedAssetIds.push(key);
                break;
            }
        }
        if (conflictWithOtherFilesInBatch) {
            localCounter++;
        }
        else {
            assetConflictMap.set(assetId, formatFileResult);
        }
    } while (conflictWithOtherFilesInBatch);
    for (const id of conflictedAssetIds) {
        // we should update count of all files which this file conflicted with
        // so that they also start with the new count next time
        const currentValue = assetConflictMap.get(id);
        assetConflictMap.set(id, { newName: currentValue.newName, countUsed: localCounter });
    }
    return newName;
}
const ARCHIVE_GUID_REGEX = /^(.*)\^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[12345][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
// Archived assets get a guid suffix appended, so need to remove it when restoring
function removeArchiveGuidFromName(name) {
    const match = name.match(ARCHIVE_GUID_REGEX);
    return match ? match[1] : name;
}
// Clients that haven't yet migrated to R-API still use the old archive locations when deleting assets (as a 2-step delete).
// These locations are treated as discarded by SAM.js, but to implement recover() we need to know where to move the assets back to.
// This is a mapping from the archive location to the restore location.
const ARCHIVE_TO_RESTORE_LOCATION = {
    'cloud-content-archive': 'cloud-content',
    'assets-archive': 'assets',
    'archive': 'files'
};
function getRestorePath(path, newName) {
    const newPath = path.split('/');
    // Paths always start with "/" so newPath[0] is ''
    // ESM paths are of the form /users/<userid>/...
    const archiveIndex = newPath[1] === 'users' ? 3 : 1;
    const restoreFolder = ARCHIVE_TO_RESTORE_LOCATION[newPath[archiveIndex]];
    if (!restoreFolder) {
        // Not archived, so no restore location
        return undefined;
    }
    newPath[archiveIndex] = restoreFolder;
    newPath[newPath.length - 1] = newName || removeArchiveGuidFromName(newPath[newPath.length - 1]);
    return newPath.join('/');
}
/**
 * Replaces the ending filename in a path with the newName
 * and returns the result as a single string.
 *
 * @param newName
 * @param asset
 * @returns
 */
function renameFileInPath(newName, asset) {
    const path = asset.repoPath.split('/').slice(0, -1);
    path.push(newName);
    return path.join('/');
}
function updateCachedDescendents(params) {
    const { assetPool, asset, session, operation } = params;
    if (!asset.isFolder || !asset.repoPath.trim()) {
        // The asset must be a folder with a non-empty path
        return;
    }
    // make sure we update the repo:path of all the descendants of this folder
    assetPool.forEach(item => {
        var _a;
        if (item.repoPath.startsWith(asset.repoPath + '/') // make sure the item is a descendant of the asset
        ) {
            switch (operation.type) {
                case 'rename': {
                    // Used by both rename and move operations.
                    // Checking if the item's repoPath starts with the folder's repo:path with '/' appended
                    // this also rules out the folder itself.
                    if (!item.repoPath.startsWith(operation.newPath)) {
                        // We need to take care of recursive folders with the same name as well
                        const newChildPath = item.repoPath.replace(asset.repoPath, operation.newPath);
                        asset._core.hostAssetContext._adobeAssetUpdater.updateLocalItem(session, item, { 'repo:path': newChildPath });
                    }
                    break;
                }
                case 'discard': {
                    // If item is a descendant of the asset, set repo:state to DISCARDED_PARENT to prevent children of
                    // discarded parents from being added to the discarded list.
                    let repoState = 'DISCARDED';
                    if ((_a = item.ancestorIDs) === null || _a === void 0 ? void 0 : _a.includes(asset.id)) {
                        repoState = 'DISCARDED_PARENT';
                    }
                    asset._core.hostAssetContext._adobeAssetUpdater.updateLocalItem(session, item, { 'repo:state': repoState });
                    break;
                }
                case 'restore': {
                    // Restore operations for assets may include a new path to restore to.
                    const newChildPath = operation.newPath ? item.repoPath.replace(asset.repoPath, operation.newPath) : item.repoPath;
                    asset._core.hostAssetContext._adobeAssetUpdater.updateLocalItem(session, item, { 'repo:state': 'ACTIVE', 'repo:path': newChildPath });
                    break;
                }
            }
        }
    });
}

function getMaxTransferSizeFromLinks(links) {
    return getLink(links, RelType.BLOCK_INITIALIZE)['repo:maxsingletransfersize'];
}
const MEGABYTE = 1024 * 1024;
const BLOCK_SIZE = 6000000;
const BULK_TRANSFER_MIME_TYPE = 'application/vnd.adobecloud.bulk-transfer+json';
const DENY_SHARE_ACE = {
    'repo:principal': 'all',
    'repo:modifier': 'deny',
    'repo:privileges': [
        'write'
    ],
    'repo:relations': [RelType.AC_POLICY],
    'repo:inheritance': 'deep'
};
const DENY_EDIT_ACE = {
    'repo:principal': 'all',
    'repo:modifier': 'deny',
    'repo:privileges': ['write', 'delete'],
    'repo:relations': [
        'describedby',
        RelType.ANNOTATIONS,
        RelType.APP_METADATA,
        RelType.EMBEDDED_METADATA,
        RelType.REPO_METADATA,
        RelType.PRIMARY,
        RelType.RENDITION
    ],
    'repo:inheritance': 'deep'
};
function getRestrictEditUserGrantACEs(userId) {
    return [{
            'repo:principal': {
                'xdm:provider': { '@id': 'https://ims-na1.adobelogin.com/' },
                '@id': userId,
                '@type': 'https://ns.adobe.com/adobecloudplatform/ims/user'
            },
            'repo:modifier': 'grant',
            'repo:privileges': ['read', 'write'],
            'repo:relations': ['http://ns.adobe.com/adobecloud/rel/ac/policy'],
            'repo:inheritance': 'deep'
        },
        {
            'repo:principal': {
                'xdm:provider': { '@id': 'https://ims-na1.adobelogin.com/' },
                '@id': userId,
                '@type': 'https://ns.adobe.com/adobecloudplatform/ims/user'
            },
            'repo:modifier': 'grant',
            'repo:privileges': ['read', 'write', 'delete'],
            'repo:relations': [
                'describedby',
                'http://ns.adobe.com/adobecloud/rel/annotations',
                'http://ns.adobe.com/adobecloud/rel/metadata/application',
                'http://ns.adobe.com/adobecloud/rel/metadata/embedded',
                'http://ns.adobe.com/adobecloud/rel/metadata/repository',
                'http://ns.adobe.com/adobecloud/rel/primary',
                'http://ns.adobe.com/adobecloud/rel/rendition'
            ],
            'repo:inheritance': 'deep'
        }];
}
var VersionPatchTypes;
(function (VersionPatchTypes) {
    VersionPatchTypes["add"] = "add";
    VersionPatchTypes["remove"] = "remove";
    VersionPatchTypes["replace"] = "replace";
})(VersionPatchTypes || (VersionPatchTypes = {}));
const DefaultAssetsListingOptions = {
    orderDirection: 'descending',
    orderBy: 'storage:deviceModifyDate',
    start: '',
    limit: 0
};
const DefaultVersionsListingOptions = {
    orderDirection: 'descending',
    orderBy: 'created',
    start: '',
    limit: 0,
    version: ''
};
function createVersionTemplate(options) {
    const userOptions = Object.assign(Object.assign({}, DefaultVersionsListingOptions), options);
    const templateValues = createPageTemplate$3(userOptions);
    templateValues.orderBy = getDirectionValue$8(userOptions);
    templateValues.resource = RelType.VERSION;
    return templateValues;
}
function createPageTemplate$3(options) {
    const templateValues = {};
    const { start, limit, version } = options;
    if (start) {
        templateValues.start = start;
    }
    if (limit) {
        templateValues.limit = limit;
    }
    if (version) {
        templateValues.version = version;
    }
    return templateValues;
}
function getDirectionValue$8(userOptions) {
    const directionFlag = userOptions.orderDirection === 'ascending' ? '+' : '-';
    return directionFlag + userOptions.orderBy;
}
function getAssetsListingURL(links, options) {
    const userOptions = Object.assign(Object.assign({}, DefaultAssetsListingOptions), options);
    const templateValues = createPageTemplate$3(userOptions);
    templateValues.orderBy = getDirectionValue$8(userOptions);
    templateValues.type = userOptions.type;
    templateValues.embed = JSON.stringify(userOptions.embed);
    return getURLFromLinks(links, RelType.PAGE, templateValues);
}
function getAssetVersionsListingURL(links, options) {
    const templateValues = createVersionTemplate(options);
    return getURLFromLinks(links, RelType.PAGE, templateValues);
}
class AssetInterface extends InterfaceBase {
    constructor(_rapi, _assetId, _links, _etag) {
        super(_rapi._core);
        this._rapi = _rapi;
        this._assetId = _assetId;
        this._links = _links;
        this._etag = _etag;
        this._useHighPriorityRequestsIfEnabled = true;
    }
    get assetID() {
        return this._assetId;
    }
    get links() {
        return this._links;
    }
    get etag() {
        return this._etag;
    }
    async getChildAssets(options) {
        const href = getAssetsListingURL(this._links, options);
        const response = await this._doGet(href, {
            // Ensure browser doesn't automatically add If-None-Match, otherwise
            // service may respond with cached data FROM ANOTHER USER.
            // https://jira.corp.adobe.com/browse/ACPCSS-5544
            // Also causes SAM-1813, SAM-2066
            headers: { ['Cache-Control']: 'no-cache, no-store, max-age=0' }
        });
        return response.json();
    }
    async getRepoMetadata() {
        const href = getURLFromLinks(this._links, RelType.REPO_METADATA);
        const response = await this._doGet(href);
        const repoMetadata = await response.json();
        // Note: Do NOT use the ETag header in the repo metadata response, because that's the ETag for the repo metadata
        // resource, not the asset itself. The ETag of the asset itself is inside the repo metadata response:
        this._etag = repoMetadata['repo:etag'];
        try {
            // Update the persistent cache with the updated links
            const resolveHref = this._rapi.getAssetResolveByIdHref(this._assetId);
            await this._core.environment.cache.setItem(resolveHref, { links: this._links, etag: this._etag });
        }
        catch (e) {
            // it is not a failure if we're unable to update the persistent cache
        }
        return repoMetadata;
    }
    async getEmbeddedMetadata() {
        const href = getURLFromLinks(this._links, RelType.EMBEDDED_METADATA);
        // Embedded metadata is returned as application/rdf+xml by default - switch to JSON-LD
        try {
            const metadata = await this._getResponse(href, { headers: { Accept: JSON_MIME_TYPE } });
            return metadata;
        }
        catch (e) {
            if (e.code === SAMErrorCode.NOT_FOUND) {
                // If an asset doesn't have embedded metadata, just return empty metadata rather than
                // treating it as a failure.
                return {};
            }
            throw e;
        }
    }
    async getAppMetadata() {
        const href = getURLFromLinks(this._links, RelType.APP_METADATA);
        try {
            const metadata = await this._getResponse(href);
            return metadata;
        }
        catch (e) {
            if (e.code === SAMErrorCode.NOT_FOUND) {
                // If an asset doesn't have app metadata, just return empty metadata rather than
                // treating it as a failure.
                return {};
            }
            throw e;
        }
    }
    async patchAppMetadata(body) {
        const href = getURLFromLinks(this._links, RelType.APP_METADATA);
        return await this._doPatch(href, {
            'Content-Type': JSON_PATCH_MIME_TYPE
        }, JSON.stringify(body), [204]);
    }
    async getEffectivePermissions() {
        const href = getURLFromLinks(this._links, RelType.AC_EFFECTIVE);
        const response = await this._getResponse(href);
        // NOTE: Missing permissions is not an error case. This collection is sparse, so the key may just not be there.
        return parseEffectivePermissions(response);
    }
    async getAssetPolicy() {
        const href = getURLFromLinks(this._links, RelType.AC_POLICY);
        return await this._doGet(href);
    }
    async getManifest() {
        const href = getURLFromLinks(this._links, RelType.MANIFEST);
        const headers = {};
        const response = await this._doGet(href, headers);
        const manifest = await response.json();
        return { manifest, etag: response.headers.get('ETag') };
    }
    async getVersions(options) {
        const href = getAssetVersionsListingURL(this._links, options);
        const response = await this._doGet(href);
        return response.json();
    }
    async getLastAccessed() {
        const href = getURLFromLinks(this._links, RelType.PERUSER_METADATA);
        const response = await this._doGet(href, { headers: { 'Content-Type': JSON_MIME_TYPE } });
        const perUserMetadata = await response.json();
        const lastAccessedISOString = perUserMetadata === null || perUserMetadata === void 0 ? void 0 : perUserMetadata['shell:lastAccessDate'];
        return lastAccessedISOString ? new Date(lastAccessedISOString) : undefined;
    }
    async restoreVersionManifest(versionManifestLink) {
        // first fetch the version manifest
        const response = await this._doGet(versionManifestLink, {});
        const versionManifest = await response.json();
        //doput to current asset manifest url
        const assetManifest = getURLFromLinks(this.links, RelType.MANIFEST);
        await this._doPut(assetManifest, {
            'Content-Type': 'application/vnd.adobe.dcx-manifest+json',
            'If-Match': '*'
        }, JSON.stringify(versionManifest), [204]);
    }
    async patchVersion(body) {
        const href = getURLFromLinks(this.links, RelType.VERSION);
        const response = await this._doPatch(href, {
            'Content-Type': JSON_PATCH_MIME_TYPE
        }, JSON.stringify(body), [204]);
        return response;
    }
    async createFolder(relativePath) {
        const href = getURLFromLinks(this._links, RelType.CREATE, { path: relativePath, intermediates: 'true', respondWith: 'http://ns.adobe.com/adobecloud/rel/metadata/repository' });
        const response = await this._doPost(href, {
            'Content-Type': 'application/vnd.adobecloud.directory+json'
        }, undefined, [200, 201]);
        // when 200 that means asset created, but error while fetching metadata
        return response.status === 201 ? await response.json() : undefined;
    }
    async _uploadIndividualBlock(href, file, start, end) {
        const headers = {};
        return await this._doPut(href, headers, file.slice(start, end), [200], true /* noOtherHeaders */);
    }
    async _blockUpload(file, relativePath, mediaType) {
        const initialBlockUploadHref = getURLFromLinks(this._links, RelType.BLOCK_INITIALIZE);
        const initBody = JSON.stringify({
            'repo:size': file.size,
            'repo:blocksize': BLOCK_SIZE,
            'repo:reltype': RelType.PRIMARY,
            'dc:format': mediaType,
        });
        const initResponse = await this._doPost(initialBlockUploadHref, {
            'Content-Type': BULK_TRANSFER_MIME_TYPE
        }, initBody, [200]);
        const blockUploadInfo = await initResponse.json();
        const blockSize = blockUploadInfo['repo:blocksize'];
        const transferHrefs = blockUploadInfo._links[RelType.BLOCK_TRANSFER];
        const finalizeHref = getURLFromLinks(blockUploadInfo._links, RelType.BLOCK_FINALIZE, { path: relativePath, respondWith: RelType.REPO_METADATA });
        // Split the file into chunks, and upload each to one of the transfer links
        // (platform guarantees we get enough links for the size we requested)
        const uploadRequests = [];
        for (let i = 0, start = 0; start < file.size; i++, start += blockSize) {
            const href = transferHrefs[i].href;
            const end = Math.min(file.size, start + blockSize);
            uploadRequests.push([href, file, start, end]);
        }
        // Only send a max of 3 blocks simultaneously, so the browser's request queue doesn't get
        // over-saturated, and intermittent outages (like wifi blips) will only cause up to 3 requests to
        // need retries.
        const MAX_SIMULTANEOUS_BLOCKS = 3;
        while (uploadRequests.length) {
            await Promise.all(uploadRequests.splice(0, MAX_SIMULTANEOUS_BLOCKS).map(args => this._uploadIndividualBlock(...args)));
        }
        // Note: When finalizing, pass in the response we got from the initialize request
        const response = await this._doPost(finalizeHref, {
            'Content-Type': BULK_TRANSFER_MIME_TYPE
        }, JSON.stringify(blockUploadInfo), [200, 201]);
        return response.json();
    }
    async importFile(file, relativePath, mediaType) {
        // check if the size of the file to be imported is greater than maxSingleTransferSize.
        const maxSingleTransferSize = getMaxTransferSizeFromLinks(this._links) || 9 * MEGABYTE;
        if (file.size >= maxSingleTransferSize) {
            // The platform requires a block upload for components > 10MB.
            // See https://git.corp.adobe.com/pages/CAF/api-spec/#block-upload
            return this._blockUpload(file, relativePath, mediaType !== null && mediaType !== void 0 ? mediaType : file.type);
        }
        const href = getURLFromLinks(this._links, RelType.CREATE, { path: relativePath, respondWith: RelType.REPO_METADATA });
        const response = await this._doPost(href, {
            'Content-Type': mediaType !== null && mediaType !== void 0 ? mediaType : file.type
        }, file, [200, 201]);
        // when 200 that means asset created, but error while fetching metadata
        return response.status === 201 ? await response.json() : undefined;
    }
    async getBlockDownloadURL(url) {
        const response = await this._doGet(url);
        const json = await response.json();
        if (!(json === null || json === void 0 ? void 0 : json.href)) {
            throw SAMError.create(SAMErrorCode.INTERNAL_ERROR, 'Cannot get block download URL');
        }
        return json.href;
    }
    async getContent() {
        const href = getURLFromLinks(this._links, RelType.PRIMARY);
        const response = await this._doGet(href, {});
        return { content: await response.blob(), etag: this._getETagFromHeaders(response.headers) };
    }
    async commitFileChanges(file, localETag) {
        const href = getURLFromLinks(this._links, RelType.PRIMARY);
        const headers = {
            'Content-type': file.type
        };
        localETag && (headers['If-Match'] = localETag);
        const response = await this._doPut(href, headers, file, [200, 201, 204]);
        return { MetaData: response.status === 201 ? await response.json() : undefined, eTag: this._getETagFromHeaders(response.headers) };
    }
    async restore(name, _counter = 0) {
        const operation = {
            op: 'restore',
            target: { 'repo:assetId': this.assetID },
            name
        };
        try {
            const repoMetadata = (await this.runOperation(operation)).asset;
            return repoMetadata;
        }
        catch (error) {
            if (error.code === SAMErrorCode.CONFLICT) {
                const { newName, countUsed } = formatFileName(name, this._core.environment.getValue('ASSET_CONFLICT_NAME_FORMAT'), _counter);
                return this.restore(newName, countUsed);
            }
            throw error;
        }
    }
    async discard() {
        const operation = {
            op: 'discard',
            target: { 'repo:assetId': this.assetID }
        };
        const { asset } = await this.runOperation(operation);
        return asset;
    }
    // Last access date is a string so replace has the same effect as add.
    async patchLastAccessed(date) {
        const href = getURLFromLinks(this._links, RelType.PERUSER_METADATA);
        const bodyData = [];
        // We should always perform an add and not a replace as a replace will cause a 400 bad request
        // if the property is not present.
        // As we don't have ONS notifications for the PUPA metadata yet we should always do an add
        // as it will have the same effect as replace.
        bodyData.push({
            op: 'add',
            value: date.toISOString(),
            path: '/shell:lastAccessDate'
        });
        return await this._doPatch(href, {
            'Content-Type': JSON_PATCH_MIME_TYPE
        }, JSON.stringify(bodyData), [204]);
    }
    async runOperation(op) {
        const response = await this._rapi.runOperation(op);
        const { asset, error } = response[0];
        if (!(asset || error)) {
            throw SAMError.wrapErrorIfNeeded(SAMErrorCode.UNKNOWN, 'Missing Asset metadata in operation response', error);
        }
        else if (error) {
            throw error;
        }
        return response[0];
    }
    async getRendition(size, type) {
        const renditionURL = getURLFromLinks(this._links, RelType.RENDITION, {
            size: String(size),
            type
        });
        const newURL = new URL(renditionURL);
        newURL.searchParams.append('etag', this.etag);
        const response = await this._doGet(newURL.toString());
        return await response.blob();
    }
    /**
     * Use case: When clients need to fine tune the rendition requests for cases other than the lazy-load and cache
     * mechanism in SAM. Clients are responsible for
     *   1. Determining when these requests should be made – SAM will not attempt to automatically download renditions
     *      requested with this method when etag or whatnot changes
     *   2. Ensuring efficient use of server resources – clients should only make requests as needed and avoid papering
     *      over issues with repeated calls
     *   3. Parsing the Response – the image in the Response.body and any additional metadata embedded by the server
     *   4. Caching the response – clients will need to use URL.createObjectURL() to make the response usable to render
     *      in DOM. This causes the browser to keep the rendition in memory until they call URL.releaseObjectURL(url).
     *      Failing to do so could cause memory leaks. To fine-tune the browser caching behavior, clients will need to
     *      set explicit caching headers in the `additionalHeaders` parameter rather than relying on adding
     *      cache-busting fake parameters to the URL.
     *
     * SAM.js will
     *   1. Get the correct URL template for the AdobeAsset or AssetVersion and fill it with metadata from the
     *      Asset/Version, plus additional data from `urlTemplateOptions`
     *   2. Add the API key and auth token headers, as well as additional headers specified in `additionalHeaders`
     *   3. Handle some auto-retries for error responses
     *   4. Reject with SAMError if there is a service error that can't be resolved with retry
     *
     * @param size
     * @param type
     * @param urlTemplateOptions
     * @param additionalHeaders
     */
    async getCustomRendition({ size = THUMBNAIL_SIZE, type = RenditionType.PNG, urlTemplateOptions = {}, additionalHeaders }) {
        const url = getURLFromLinks(this._links, RelType.RENDITION, Object.assign({ size: String(size), type }, urlTemplateOptions));
        return await this._doGet(url, { headers: additionalHeaders });
    }
    /**
     * Clients may have a requirement to get the JSON object describing all the available sizes, rather than returning
     * a JSON object describing only the specified size. Especially useful in case of fetching rendition resource of
     * type "application/vnd.adobe.ccv.videometadata" for video assets
     * @param type
     * @param urlTemplateOptions
     * @param additionalHeaders
     */
    async getAllCustomRenditions({ type, urlTemplateOptions = {}, additionalHeaders }) {
        const url = type ? getURLFromLinks(this._links, RelType.RENDITION, Object.assign({ type }, urlTemplateOptions)) : getURLFromLinks(this._links, RelType.RENDITION, Object.assign({}, urlTemplateOptions));
        return await this._doGet(url, { headers: additionalHeaders });
    }
    async setEditingInheritanceRestricted(restrictEdit, addCurrentUserAsEditor) {
        // API documentation: https://developers.corp.adobe.com/acp/acl-front-service/documentation/src/main/docs/swagger.json#operation/jsonPatchPolicy
        const userId = this._core.environment.getValue('USER_ID');
        if (restrictEdit) {
            // NOTE: attempting to add these ACEs when they are already set has no effect
            const addPatches = [{
                    op: 'add',
                    // Index for 'add' probably doesn't matter
                    path: '/repo:acl/0',
                    value: DENY_EDIT_ACE
                }, {
                    op: 'add',
                    path: '/repo:acl/1',
                    value: DENY_SHARE_ACE
                }];
            if (addCurrentUserAsEditor) {
                const userACEs = getRestrictEditUserGrantACEs(userId);
                addPatches.push({ op: 'add', path: '/repo:acl/2', value: userACEs[0] });
                addPatches.push({ op: 'add', path: '/repo:acl/3', value: userACEs[1] });
            }
            await this._patchACL(addPatches);
        }
        else {
            // Find the index of the deny ACEs. This is critical to ensure only the desired ACEs get removed.
            const { policyResponse, denyEditAceIndex, denyShareAceIndex } = await this._getEditingInheritanceRestrictedInfo();
            // If either or both ACE is missing, the toggle is not on
            if (denyEditAceIndex < 0 || denyShareAceIndex < 0) {
                return;
            }
            const removePatches = [{
                    op: 'remove',
                    path: `/repo:acl/${denyShareAceIndex}`
                }, {
                    op: 'remove',
                    path: `/repo:acl/${denyEditAceIndex}`
                }];
            // The patches must be sorted by index descending or else the wrong items will be removed, because they
            // are applied in order. If we have acl = [A, B, C] and try to remove index 0 before 1, it will first remove
            // A, making B be index 0; removing index 1 after that will erroneously remove C. Instead, removing 1 first
            // will result in [A, C] and then removing index 0 will leave the desired [C] remaining.
            removePatches.sort((ace1, ace2) => ace1.path < ace2.path ? 0 : -1);
            await this._patchACL(removePatches, policyResponse.headers.get('etag') || undefined);
        }
    }
    async _patchACL(patches, etag) {
        const url = getURLFromLinks(this._links, RelType.AC_POLICY);
        await this._doPatch(url, {
            'Content-Type': 'application/json-patch+json',
            'If-Match': etag
        }, JSON.stringify(patches), [200]);
    }
    async getEditingInheritanceRestricted() {
        const { denyEditAceIndex, denyShareAceIndex } = await this._getEditingInheritanceRestrictedInfo();
        // both must be present for the toggle to be on
        return denyEditAceIndex >= 0 && denyShareAceIndex >= 0;
    }
    async _getEditingInheritanceRestrictedInfo() {
        const policyResponse = await this.getAssetPolicy();
        const acl = (await policyResponse.json())['repo:acl'];
        const denyEditAce = acl.find(ace => isDeepEqual(ace, DENY_EDIT_ACE, true));
        const denyShareAce = acl.find(ace => isDeepEqual(ace, DENY_SHARE_ACE, true));
        return {
            policyResponse,
            denyEditAceIndex: acl.indexOf(denyEditAce),
            denyShareAceIndex: acl.indexOf(denyShareAce)
        };
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const NotFound$1 = Symbol('NotFound');
// Persistent cache based on indexedDB
class IndexedDBCache {
    constructor(_name, _core) {
        this._name = _name;
        this._core = _core;
    }
    static isSupported() {
        if (typeof window === 'undefined' || !window.indexedDB) {
            return false;
        }
        try {
            const request = window.indexedDB.open('sam_indexeddb_test', 1);
            request.onsuccess = () => request.result.close();
            return true;
        }
        catch (error) {
            return false;
        }
    }
    async _transaction(transactionType, requestsFn) {
        const database = await this._connect();
        if (!database) {
            return;
        }
        return new Promise((resolve) => {
            try {
                const transaction = database.transaction(this._name, transactionType);
                const objectStore = transaction.objectStore(this._name);
                const promResult = requestsFn(objectStore);
                transaction.onerror = (err) => {
                    this._core.environment.error(`transaction error: ${err}`);
                    resolve(undefined);
                };
                transaction.oncomplete = () => {
                    resolve(promResult);
                };
            }
            catch (ex) {
                // this can happens due to version upgrade when we close db.
                this._core.environment.error(`transaction exception: ${ex}`);
                resolve(undefined);
            }
        });
    }
    async getItem(itemKey) {
        return this._transaction('readonly', async (objectStore) => {
            return new Promise((resolve) => {
                const request = objectStore.get(itemKey);
                request.onsuccess = () => {
                    resolve(request.result ? {
                        value: request.result.value,
                        expiry: request.result.expiry
                    } : undefined);
                };
            });
        });
    }
    async setItem(itemKey, value, expiry) {
        return this._transaction('readwrite', (objectStore) => {
            objectStore.put({ id: itemKey, expiry, value });
            return;
        });
    }
    async deleteItem(itemKey) {
        return this._transaction('readwrite', (objectStore) => {
            objectStore.delete(itemKey);
            return;
        });
    }
    async _connect() {
        if (this._database) {
            return this._database;
        }
        const version = this._core.environment.getValue('INDEXED_DB_VERSION');
        const request = window.indexedDB.open(this._name, version);
        return new Promise((resolve) => {
            request.onerror = (err) => {
                this._core.environment.error(`CONNECTION_FAILED Unable to connect to IndexedDB: ${this._name}, error: ${err}`);
                resolve(undefined);
            };
            request.onupgradeneeded = () => {
                const database = request.result;
                if (database.objectStoreNames.contains(this._name)) {
                    database.deleteObjectStore(this._name);
                }
                database.createObjectStore(this._name, { keyPath: 'id' });
            };
            request.onsuccess = () => {
                const database = this._database = request.result;
                database.onversionchange = (evt) => {
                    this._core.environment.error(`IndexedDb version changed detected ${this._name}.${version}, ${evt}`);
                    // close current db connection allowing the other client to upgrade a version
                    database.close();
                    database.onversionchange = null;
                    this._database = undefined;
                };
                resolve(database);
            };
        });
    }
    // @ts-ignore -- for use in console debugging
    async _clear() {
        return this._transaction('readwrite', async (objectStore) => {
            return new Promise((resolve) => {
                const request = objectStore.clear();
                request.onsuccess = () => {
                    // eslint-disable-next-line no-console
                    console.log('localDB cleared for', objectStore.name);
                    resolve(undefined);
                };
            });
        });
    }
}
// Persistent cache based on localStorage
class LocalStorageCache {
    constructor(_name, _core) {
        this._name = _name;
        this._core = _core;
    }
    static isSupported() {
        try {
            return typeof window !== 'undefined' && !!window.localStorage;
        }
        catch (e) {
            // Firefox throws an exception when accessing localStorage in private mode
            return false;
        }
    }
    _getKey(itemKey) {
        return `${this._name}#${itemKey}`;
    }
    async getItem(itemKey) {
        const storedValue = window.localStorage.getItem(this._getKey(itemKey));
        if (storedValue) {
            try {
                const { value, expiry } = JSON.parse(storedValue);
                return {
                    value,
                    expiry: expiry ? new Date(expiry) : undefined
                };
            }
            catch (e) {
                // ignore
            }
        }
        return undefined;
    }
    async setItem(itemKey, value, expiry) {
        const storedValue = JSON.stringify({ expiry, value });
        window.localStorage.setItem(this._getKey(itemKey), storedValue);
    }
    async deleteItem(itemKey) {
        window.localStorage.removeItem(this._getKey(itemKey));
    }
}
/**
 * Persistent cache of values using indexedDB - if the browser doesn't support indexedDB it falls back to localStorage,
 * and if neither is supported it'll just use a local in-memory cache that doesn't persist between sessions.
 */
class Cache {
    constructor(_userId, _core, _persistentCache) {
        this._userId = _userId;
        this._core = _core;
        // Local cache layer - this is an optimization if there's no persistent cache layer
        this._localCache = new Map;
        this._name = 'SAM.js-' + this._userId;
        if (_persistentCache) {
            // Clients can override SAM.js to use their own caching layer if required
            this._persistentCache = _persistentCache;
        }
        else if (IndexedDBCache.isSupported()) {
            this._persistentCache = new IndexedDBCache(this._name, _core);
        }
        else if (LocalStorageCache.isSupported()) {
            this._persistentCache = new LocalStorageCache(this._name, _core);
        }
    }
    get name() {
        return this._name;
    }
    async getItem(itemKey, computeValue, validateValue, expiryMs) {
        let cachedItem = this._localCache.get(itemKey);
        if (!cachedItem && this._persistentCache) {
            cachedItem = await this._persistentCache.getItem(itemKey);
        }
        // Check if the cached item expired or invalid - if so, ignore it
        const isInvalid = validateValue && cachedItem && !validateValue(cachedItem.value);
        const isExpired = (cachedItem === null || cachedItem === void 0 ? void 0 : cachedItem.expiry) && cachedItem.expiry <= new Date(Date.now());
        if (isInvalid || isExpired) {
            await this.deleteItem(itemKey);
            cachedItem = undefined;
        }
        // If we're passed in a computeValue function, use it to update the cache if there's no
        // cached value (or it expired)
        if (!cachedItem && computeValue) {
            const value = await computeValue();
            if (validateValue && !validateValue(value)) {
                throw SAMError.create(SAMErrorCode.UNKNOWN, `Value for cache key ${itemKey} is invalid: ${JSON.stringify(value)}`);
            }
            await this.setItem(itemKey, value, expiryMs);
            return value;
        }
        return cachedItem ? cachedItem.value : NotFound$1;
    }
    async setItem(itemKey, value, expirationDurMsec = this._core.environment.getValue('CACHE_DEFAULT_TTL')) {
        const expiry = expirationDurMsec ? new Date(Date.now() + expirationDurMsec) : undefined;
        this._localCache.set(itemKey, { value, expiry });
        if (this._persistentCache) {
            await this._persistentCache.setItem(itemKey, value, expiry);
        }
    }
    async deleteItem(itemKey) {
        this._localCache.delete(itemKey);
        if (this._persistentCache) {
            await this._persistentCache.deleteItem(itemKey);
        }
    }
}
__decorate([
    synchronized,
    __param(0, key)
], Cache.prototype, "getItem", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const MAX_INDEX_RETRIES = 5;
class RepositoryInterface extends InterfaceBase {
    constructor() {
        super(...arguments);
        // Index document is a well-known endpoint, so does not need to come from discovery.
        // https://developers.corp.adobe.com/index-repository-api/docs/index-document.md
        this.repositoryIndex = `https://${this._core.environment.endpoints.ACP_CS_HOST}/index`;
        this.repositoryNameIndex = `https://${this._core.environment.endpoints.ACP_CS_HOST}/repositories`;
        this._useHighPriorityRequestsIfEnabled = true;
        this._cache = this._core.environment.cache;
        this._hasFailedExtendedHeaderRequest = false;
    }
    get _fetchIndexState() { return this._core._fetchIndexState; }
    set _fetchIndexState(state) { this._core._fetchIndexState = state; }
    _getAndCacheDocument(href) {
        return this._getAllPages(href);
    }
    async getRootLink(relType, params = {}) {
        // The only thing uses in SAM.js from the `/` resource is the `_links` metadata, which is identical to the
        // `_links` in the `/index` resource, so use the `/index` resource for the root links
        const rootDocument = await this.getIndex();
        return getURLFromLinks(rootDocument._links, relType, params);
    }
    async getIndex() {
        var _a, _b;
        const fetchIndexState = this._fetchIndexState;
        // If something triggered clearing the cache, wait for it to finish
        if (fetchIndexState.pendingCacheClear) {
            await fetchIndexState.pendingCacheClear;
        }
        if (fetchIndexState.response && fetchIndexState.hasChildren) {
            // Data is complete, so return it now
            return fetchIndexState.response;
        }
        const existing = await this._cache.getItem(this.repositoryIndex);
        if (existing !== NotFound$1) {
            fetchIndexState.response = existing;
            fetchIndexState.hasChildren = Boolean((_b = (_a = fetchIndexState.response) === null || _a === void 0 ? void 0 : _a.children) === null || _b === void 0 ? void 0 : _b.length);
        }
        // Refresh index in background if it hasn't been refreshed successfully this session. This allows typical
        // calls to return quickly from cache while still updating the cache regularly [SAM-3165].
        if (!fetchIndexState.promise) {
            fetchIndexState.promise = new Promise((resolve, reject) => {
                this._getResponse(this.repositoryIndex, {
                    // R-API returns an E-Tag which doesn't change when the content changes, causing the browser to
                    // use stale cached data. Using Cache-Control header to disable browser cache so this doesn't happen.
                    // [SAM-2952]
                    headers: { 'Cache-Control': 'no-cache, no-store, max-age=0' },
                    // R-API might be temporarily returning 404 on the /index document for some users
                    additionalRetryErrorCodes: [404],
                }).then(async (indexJson) => {
                    var _a;
                    // Only cache /index if it has children [SAM-2952]
                    if ((_a = indexJson.children) === null || _a === void 0 ? void 0 : _a.length) {
                        await this._cache.setItem(this.repositoryIndex, indexJson);
                        fetchIndexState.hasChildren = true;
                    }
                    else {
                        // Allow getHomeDirectoryMetadata() to retry fetching /index if there were no children
                        fetchIndexState.hasChildren = false;
                        fetchIndexState.promise = undefined;
                    }
                    // Response can be used for _links even if there are no children
                    fetchIndexState.response = indexJson;
                    resolve(indexJson);
                })
                    .catch(reject);
            });
        }
        // If the /index is not in cache, or missing data, wait for fetch, otherwise use the
        // cache value while the index is refreshed in the background.
        if (fetchIndexState.promise && (!fetchIndexState.response || !fetchIndexState.hasChildren)) {
            await fetchIndexState.promise;
        }
        // The _links property of the response will still be usable even if children are not present.
        return fetchIndexState.response;
    }
    /**
     * @package -- for use in SAM.js only
     */
    _clearIndexCache() {
        this._fetchIndexState.pendingCacheClear = new Promise(resolve => {
            // Allow pending request to complete before clearing the cache so the final state is
            // a clear cache.
            Promise.resolve(this._fetchIndexState.promise)
                .finally(() => {
                return this._cache.deleteItem(this.repositoryIndex);
            })
                .finally(() => {
                this._fetchIndexState = {};
                resolve();
            });
        });
    }
    async _getAndCacheLinksFromHref(href) {
        const response = await this._doGet(href);
        // The etag header from Links API no longer represents the Asset or its repo metadata, so it
        // isn't being stored in the SAM.js persistent cache.
        const { _links } = await response.json();
        return { links: _links };
    }
    getAssetResolveByIdHref(assetId) {
        // Links API is a "well-known endpoint" and so does not need to use discovery
        // https://developers.corp.adobe.com/storage-api/docs/reference/links.md
        const url = new URL(`https://${this._core.environment.endpoints.ACP_CS_HOST}/links`);
        url.searchParams.set('assetId', assetId);
        return url.toString();
    }
    async getCloudQuotaUsedAndMax() {
        const metadata = await this.getHomeDirectoryMetadata();
        const url = getURLFromLinks(metadata._links, RelType.QUOTA);
        const responseJson = await this._getResponse(url);
        return { used: responseJson['xdm:consumed'], total: responseJson['xdm:amount'] };
    }
    /**
     * Takes an asset id and returns the links for the resource.
     *
     * @param id {string} assetId of the object
     * @TODO_Deprecation to be removed after it is no longer used in SAMCpp.ts patchAdobeAsset
     */
    async resolveById(assetId) {
        const resolveHref = this.getAssetResolveByIdHref(assetId);
        const { links } = await this._getAndCacheLinksFromHref(resolveHref);
        return new AssetInterface(this, assetId, links, '');
    }
    async getRepoMetadataById(assetId) {
        var _a;
        // GET on the resolve link will return the repo metadata
        const resolveHref = await this.getRootLink(RelType.RESOLVE_ID, { id: assetId });
        let response;
        const headersWithExtendedProperties = {
            'Accept': 'application/vnd.adobecloud.metadata-repository+json;extended=true'
        };
        // If we have already failed the request before, we know
        // that all future requests will fail as well so no need
        // to add an additional request just go ahead and request
        // without the special extended headers.
        if (this._hasFailedExtendedHeaderRequest) {
            return await this._getResponse(resolveHref);
        }
        try {
            response = await this._getResponse(resolveHref, {
                headers: headersWithExtendedProperties
            });
        }
        catch (e) {
            if (isSAMError(e)) {
                const samError = e;
                if (((_a = samError._additionalInfo.response) === null || _a === void 0 ? void 0 : _a.status) === 406) {
                    // A 406 is returned that means it's possible the client has not
                    // been allowlisted with RAPI to retrieve extended properties like repo:createdByClientId
                    // so we should just do the request again but without the extended=true Accept header
                    // and throw a oneTimeWarning.
                    oneTimeWarning('RepositoryInterface', 'Client id has not been allowlisted for getting extended properties from RAPI');
                    this._hasFailedExtendedHeaderRequest = true;
                    response = await this._getResponse(resolveHref);
                }
                else {
                    // Regular SAMerror just continue with the throw
                    throw e;
                }
            }
            else {
                // Other non SAMError but just continue with throw anyway.
                throw e;
            }
        }
        return response;
    }
    async getRepoMetadataByPath(repositoryId, path) {
        const resolveHref = await this.getRootLink(RelType.RESOLVE_PATH, { repositoryId, path, mode: 'id' });
        const repoMetadata = await this._getResponse(resolveHref);
        const assetId = repoMetadata['repo:assetId'];
        const links = repoMetadata._links;
        // We want to cache the links, but we do so under the resolveById href - that's because the path can
        // change over time, but asset ids are immutable.
        const resolveByIdHref = this.getAssetResolveByIdHref(assetId);
        await this._cache.setItem(resolveByIdHref, { links });
        return repoMetadata;
    }
    async getHomeDirectoryMetadata() {
        return await this._getHomeDirectoryMetadata(0);
    }
    async _getHomeDirectoryMetadata(retryCount) {
        var _a;
        // Fire off background refresh of index data
        const cachedIndex = await this.getIndex();
        // Wait for background refresh, which may or may not be the same as the cached value
        const index = await ((_a = this._fetchIndexState.promise) !== null && _a !== void 0 ? _a : cachedIndex);
        // Likely cause: Enterprise user has not been granted quota by their administrator, but this error
        // could change. Host apps should verify that the user has quota before trying to get their home
        // directory info, and if they have none, show a message suggesting they contact their administrator.
        if (!index.children.length) {
            if (retryCount >= MAX_INDEX_RETRIES) {
                throw SAMError.create(SAMErrorCode.INVALID_OPERATION, `Home repository not found after ${MAX_INDEX_RETRIES} retries`);
            }
            else {
                // NOTE: assumes that getIndex() does not cache if there are no children, so no need to clear cache here
                this._core.environment.log(`/index has no children, ${MAX_INDEX_RETRIES - retryCount} retries left`);
                await new Promise(resolve => setTimeout(resolve, this._getRetryErrorDelay(undefined, retryCount)));
                return await this._getHomeDirectoryMetadata(retryCount + 1);
            }
        }
        return index.children[0]._embedded[RelType.REPO_METADATA];
    }
    /**
     * Run a cloud operation.
     * https://git.corp.adobe.com/pages/caf/api-spec/schemas/repository/operation.html
     *
     * @param ops {IOperationQuery}
     */
    async runOperation(ops) {
        var _a, _b, _c, _d;
        const opHref = await this.getRootLink(RelType.OPS);
        const headers = {
            'Content-type': 'application/vnd.adobe.asset-operation+json'
        };
        const firstOp = Array.isArray(ops) ? ops[0] : ops;
        // if the operation is copy and SAM is configured to copy all resources
        // by default then we add the directive header in RAPI call as RAPI needs
        // this header to copy secondary resources by default.
        const copyResources = ((_a = firstOp) === null || _a === void 0 ? void 0 : _a.op) === 'copy';
        copyResources && (headers['directive'] = 'ops-2021');
        // If we send a single operation, then errors will fail the entire request.
        // If we send multiple operations, we always get a 200 and then separate error
        // codes inside the response. We want to normalize this behavior here, in order
        // to make it easier to handle the response.
        // Note that some errors are still going to return a proper array even
        // for single items, so we need to be prepared for both types of replies.
        // We are using 'any' below to disable the status code validation and allow
        // this method to handle the error codes.
        const response = await this._doPost(opHref, headers, JSON.stringify(ops), 'any');
        let result;
        try {
            result = await response.json();
        }
        catch (_e) {
            // Ignore the error here.
        }
        if (result && !Array.isArray(result) && response.status !== 200) {
            // If this was a single operation request, then sometimes the service will fail the entire request
            // instead of returning the array for the item. Try to normalize the reply to avoid bugs in the callers.
            // https://jira.corp.adobe.com/browse/ACPCSS-3613
            return [
                Object.assign(Object.assign({}, firstOp), { error: SAMError.createWithRepositoryCode(
                    // We cannot use the result.code as that uses non-standard HTTP error codes like 4041 instead of 404.
                    response.status, (_b = result.type) !== null && _b !== void 0 ? _b : ProblemType.OPERATION_FAILED, (_d = (_c = result.title) !== null && _c !== void 0 ? _c : result.message) !== null && _d !== void 0 ? _d : 'Operation failed') })
            ];
        }
        // If we don't actually get a response or if we get an array,
        // then we should expect to get a 200 response code.
        this._checkResponseStatus(response, opHref, [200]);
        if (!result) {
            // We've got a 200 status, but there's no actual result or the JSON parsing failed.
            throw SAMError.create(SAMErrorCode.UNKNOWN, 'Failed to parse response for repo bulk API.');
        }
        // Convert the result to ensure it is always an array.
        // If we have a single item, we know it cannot be an error as that was handled above already.
        const list = Array.isArray(result) ? result : [result];
        // Map errors to SAMErrors before returning.
        return list.map((item) => {
            const { error } = item;
            return Object.assign(Object.assign({}, item), { error: error ? SAMError.createWithRepositoryCode(error.status, error.type, error.title) : undefined });
        });
    }
    /**
     * Runs the runOperation API for any specific context
     * Initiate session of that context
     * Check every model operation's result and update model meta data
     * commit session
     * returns ILocalOperationResult array collected over all results collectively
     *
     * @param ops Opeartions to run
     * @param modelsById A map of model to it's id
     * @param itemPool ItemPool of the specific context used to create session
     * @param updateLocalModel for updating Model metaData
     * @param errorHandlers If needed to validate or check any specific error code, and respond differently
     */
    async runOperationsForContext(ops, modelsById, itemPool, updateLocalModel, errorHandlers) {
        var _a;
        const result = await this.runOperation(ops);
        const resultOps = { failedResults: [], newModels: [] };
        const session = itemPool.createMutationSession();
        const promises = [];
        for (const op of result) {
            const currentModel = op.id && modelsById.get(op.id);
            if (!currentModel) {
                continue;
            }
            const { asset: updatedModelMetaData, error } = op;
            if (error) {
                if (errorHandlers) {
                    const onError = (_a = (errorHandlers.find(errorHandler => errorHandler.match(error)))) === null || _a === void 0 ? void 0 : _a.action;
                    if (onError) {
                        promises.push(onError(currentModel, error).then(failedOp => {
                            if (failedOp) {
                                resultOps.failedResults.push(failedOp);
                            }
                        }));
                        continue;
                    }
                }
                resultOps.failedResults.push({ model: currentModel, error });
                continue;
            }
            try {
                const newModel = updateLocalModel(session, currentModel, updatedModelMetaData);
                if (newModel) {
                    resultOps.newModels.push(newModel);
                }
            }
            catch (error) {
                const samError = error;
                if (samError && samError.code === SAMErrorCode.UNKNOWN) {
                    resultOps.failedResults.push({ model: currentModel, error: samError });
                }
                else {
                    throw error;
                }
            }
        }
        session.commit();
        // Wait for any Promises from errorHandlers callback
        await Promise.all(promises);
        return resultOps;
    }
    /* Fetches data from the repositories endpoint.
     * These are all the repositories that contain at least one discoverable asset for this user.
     */
    async getRepositoriesData() {
        const fetchRepositories = () => this._getResponse(this.repositoryNameIndex);
        const dayInMs = 24 * 60 * 60 * 1000; //30 day expiration on cache entry
        return await this._cache.getItem(this.repositoryNameIndex, fetchRepositories, undefined, 30 * dayInMs);
    }
    async getRepositoryName(id) {
        var _a, _b;
        const response = await this.getRepositoriesData();
        if (!response) {
            return undefined;
        }
        const children = response.children;
        const data = children.find(data => data._embedded[RelType.PRIMARY]['repo:repositoryId'] === id);
        return (_b = (_a = data === null || data === void 0 ? void 0 : data._embedded[RelType.PRIMARY]['dc:title']) === null || _a === void 0 ? void 0 : _a['rdf:_1']) === null || _b === void 0 ? void 0 : _b['@value'];
    }
    /* Call the Links API to get the asset links resulting in a service call instead of using local cache.
     * This is intended as an eventual replacement for resolveById which uses the local cache.
     * Fixes [SAM-3444]. NOTE: the /links API does not include the restore and discard links, but since
     * SAM.js uses the /ops API for these operations, it shouldn't be needed. Meanwhile, SAM.js
     * needs the download and block transfer links, which do not appear in the /:repometadata
     * body (they're only in the header), but do appear in the /links body.
     */
    async getAssetLinks(assetId) {
        const resolveHref = this.getAssetResolveByIdHref(assetId);
        const { _links } = await this._getResponse(resolveHref);
        return _links;
    }
}
__decorate([
    cache,
    __param(0, key)
], RepositoryInterface.prototype, "_getAndCacheDocument", null);
__decorate([
    cache,
    __param(0, key)
], RepositoryInterface.prototype, "_getAndCacheLinksFromHref", null);
__decorate([
    synchronized
], RepositoryInterface.prototype, "getRepositoriesData", null);

/**
 * @author Toru Nagashima <https://github.com/mysticatea>
 * @copyright 2015 Toru Nagashima. All rights reserved.
 * See LICENSE file in root directory for full license.
 */
/**
 * @typedef {object} PrivateData
 * @property {EventTarget} eventTarget The event target.
 * @property {{type:string}} event The original event object.
 * @property {number} eventPhase The current event phase.
 * @property {EventTarget|null} currentTarget The current event target.
 * @property {boolean} canceled The flag to prevent default.
 * @property {boolean} stopped The flag to stop propagation.
 * @property {boolean} immediateStopped The flag to stop propagation immediately.
 * @property {Function|null} passiveListener The listener if the current listener is passive. Otherwise this is null.
 * @property {number} timeStamp The unix time.
 * @private
 */

/**
 * Private data for event wrappers.
 * @type {WeakMap<Event, PrivateData>}
 * @private
 */
const privateData = new WeakMap();

/**
 * Cache for wrapper classes.
 * @type {WeakMap<Object, Function>}
 * @private
 */
const wrappers = new WeakMap();

/**
 * Get private data.
 * @param {Event} event The event object to get private data.
 * @returns {PrivateData} The private data of the event.
 * @private
 */
function pd(event) {
    const retv = privateData.get(event);
    console.assert(
        retv != null,
        "'this' is expected an Event object, but got",
        event
    );
    return retv
}

/**
 * https://dom.spec.whatwg.org/#set-the-canceled-flag
 * @param data {PrivateData} private data.
 */
function setCancelFlag(data) {
    if (data.passiveListener != null) {
        if (
            typeof console !== "undefined" &&
            typeof console.error === "function"
        ) {
            console.error(
                "Unable to preventDefault inside passive event listener invocation.",
                data.passiveListener
            );
        }
        return
    }
    if (!data.event.cancelable) {
        return
    }

    data.canceled = true;
    if (typeof data.event.preventDefault === "function") {
        data.event.preventDefault();
    }
}

/**
 * @see https://dom.spec.whatwg.org/#interface-event
 * @private
 */
/**
 * The event wrapper.
 * @constructor
 * @param {EventTarget} eventTarget The event target of this dispatching.
 * @param {Event|{type:string}} event The original event to wrap.
 */
function Event$1(eventTarget, event) {
    privateData.set(this, {
        eventTarget,
        event,
        eventPhase: 2,
        currentTarget: eventTarget,
        canceled: false,
        stopped: false,
        immediateStopped: false,
        passiveListener: null,
        timeStamp: event.timeStamp || Date.now(),
    });

    // https://heycam.github.io/webidl/#Unforgeable
    Object.defineProperty(this, "isTrusted", { value: false, enumerable: true });

    // Define accessors
    const keys = Object.keys(event);
    for (let i = 0; i < keys.length; ++i) {
        const key = keys[i];
        if (!(key in this)) {
            Object.defineProperty(this, key, defineRedirectDescriptor(key));
        }
    }
}

// Should be enumerable, but class methods are not enumerable.
Event$1.prototype = {
    /**
     * The type of this event.
     * @type {string}
     */
    get type() {
        return pd(this).event.type
    },

    /**
     * The target of this event.
     * @type {EventTarget}
     */
    get target() {
        return pd(this).eventTarget
    },

    /**
     * The target of this event.
     * @type {EventTarget}
     */
    get currentTarget() {
        return pd(this).currentTarget
    },

    /**
     * @returns {EventTarget[]} The composed path of this event.
     */
    composedPath() {
        const currentTarget = pd(this).currentTarget;
        if (currentTarget == null) {
            return []
        }
        return [currentTarget]
    },

    /**
     * Constant of NONE.
     * @type {number}
     */
    get NONE() {
        return 0
    },

    /**
     * Constant of CAPTURING_PHASE.
     * @type {number}
     */
    get CAPTURING_PHASE() {
        return 1
    },

    /**
     * Constant of AT_TARGET.
     * @type {number}
     */
    get AT_TARGET() {
        return 2
    },

    /**
     * Constant of BUBBLING_PHASE.
     * @type {number}
     */
    get BUBBLING_PHASE() {
        return 3
    },

    /**
     * The target of this event.
     * @type {number}
     */
    get eventPhase() {
        return pd(this).eventPhase
    },

    /**
     * Stop event bubbling.
     * @returns {void}
     */
    stopPropagation() {
        const data = pd(this);

        data.stopped = true;
        if (typeof data.event.stopPropagation === "function") {
            data.event.stopPropagation();
        }
    },

    /**
     * Stop event bubbling.
     * @returns {void}
     */
    stopImmediatePropagation() {
        const data = pd(this);

        data.stopped = true;
        data.immediateStopped = true;
        if (typeof data.event.stopImmediatePropagation === "function") {
            data.event.stopImmediatePropagation();
        }
    },

    /**
     * The flag to be bubbling.
     * @type {boolean}
     */
    get bubbles() {
        return Boolean(pd(this).event.bubbles)
    },

    /**
     * The flag to be cancelable.
     * @type {boolean}
     */
    get cancelable() {
        return Boolean(pd(this).event.cancelable)
    },

    /**
     * Cancel this event.
     * @returns {void}
     */
    preventDefault() {
        setCancelFlag(pd(this));
    },

    /**
     * The flag to indicate cancellation state.
     * @type {boolean}
     */
    get defaultPrevented() {
        return pd(this).canceled
    },

    /**
     * The flag to be composed.
     * @type {boolean}
     */
    get composed() {
        return Boolean(pd(this).event.composed)
    },

    /**
     * The unix time of this event.
     * @type {number}
     */
    get timeStamp() {
        return pd(this).timeStamp
    },

    /**
     * The target of this event.
     * @type {EventTarget}
     * @deprecated
     */
    get srcElement() {
        return pd(this).eventTarget
    },

    /**
     * The flag to stop event bubbling.
     * @type {boolean}
     * @deprecated
     */
    get cancelBubble() {
        return pd(this).stopped
    },
    set cancelBubble(value) {
        if (!value) {
            return
        }
        const data = pd(this);

        data.stopped = true;
        if (typeof data.event.cancelBubble === "boolean") {
            data.event.cancelBubble = true;
        }
    },

    /**
     * The flag to indicate cancellation state.
     * @type {boolean}
     * @deprecated
     */
    get returnValue() {
        return !pd(this).canceled
    },
    set returnValue(value) {
        if (!value) {
            setCancelFlag(pd(this));
        }
    },

    /**
     * Initialize this event object. But do nothing under event dispatching.
     * @param {string} type The event type.
     * @param {boolean} [bubbles=false] The flag to be possible to bubble up.
     * @param {boolean} [cancelable=false] The flag to be possible to cancel.
     * @deprecated
     */
    initEvent() {
        // Do nothing.
    },
};

// `constructor` is not enumerable.
Object.defineProperty(Event$1.prototype, "constructor", {
    value: Event$1,
    configurable: true,
    writable: true,
});

// Ensure `event instanceof window.Event` is `true`.
if (typeof window !== "undefined" && typeof window.Event !== "undefined") {
    Object.setPrototypeOf(Event$1.prototype, window.Event.prototype);

    // Make association for wrappers.
    wrappers.set(window.Event.prototype, Event$1);
}

/**
 * Get the property descriptor to redirect a given property.
 * @param {string} key Property name to define property descriptor.
 * @returns {PropertyDescriptor} The property descriptor to redirect the property.
 * @private
 */
function defineRedirectDescriptor(key) {
    return {
        get() {
            return pd(this).event[key]
        },
        set(value) {
            pd(this).event[key] = value;
        },
        configurable: true,
        enumerable: true,
    }
}

/**
 * Get the property descriptor to call a given method property.
 * @param {string} key Property name to define property descriptor.
 * @returns {PropertyDescriptor} The property descriptor to call the method property.
 * @private
 */
function defineCallDescriptor(key) {
    return {
        value() {
            const event = pd(this).event;
            return event[key].apply(event, arguments)
        },
        configurable: true,
        enumerable: true,
    }
}

/**
 * Define new wrapper class.
 * @param {Function} BaseEvent The base wrapper class.
 * @param {Object} proto The prototype of the original event.
 * @returns {Function} The defined wrapper class.
 * @private
 */
function defineWrapper(BaseEvent, proto) {
    const keys = Object.keys(proto);
    if (keys.length === 0) {
        return BaseEvent
    }

    /** CustomEvent */
    function CustomEvent(eventTarget, event) {
        BaseEvent.call(this, eventTarget, event);
    }

    CustomEvent.prototype = Object.create(BaseEvent.prototype, {
        constructor: { value: CustomEvent, configurable: true, writable: true },
    });

    // Define accessors.
    for (let i = 0; i < keys.length; ++i) {
        const key = keys[i];
        if (!(key in BaseEvent.prototype)) {
            const descriptor = Object.getOwnPropertyDescriptor(proto, key);
            const isFunc = typeof descriptor.value === "function";
            Object.defineProperty(
                CustomEvent.prototype,
                key,
                isFunc
                    ? defineCallDescriptor(key)
                    : defineRedirectDescriptor(key)
            );
        }
    }

    return CustomEvent
}

/**
 * Get the wrapper class of a given prototype.
 * @param {Object} proto The prototype of the original event to get its wrapper.
 * @returns {Function} The wrapper class.
 * @private
 */
function getWrapper(proto) {
    if (proto == null || proto === Object.prototype) {
        return Event$1
    }

    let wrapper = wrappers.get(proto);
    if (wrapper == null) {
        wrapper = defineWrapper(getWrapper(Object.getPrototypeOf(proto)), proto);
        wrappers.set(proto, wrapper);
    }
    return wrapper
}

/**
 * Wrap a given event to management a dispatching.
 * @param {EventTarget} eventTarget The event target of this dispatching.
 * @param {Object} event The event to wrap.
 * @returns {Event} The wrapper instance.
 * @private
 */
function wrapEvent(eventTarget, event) {
    const Wrapper = getWrapper(Object.getPrototypeOf(event));
    return new Wrapper(eventTarget, event)
}

/**
 * Get the immediateStopped flag of a given event.
 * @param {Event} event The event to get.
 * @returns {boolean} The flag to stop propagation immediately.
 * @private
 */
function isStopped(event) {
    return pd(event).immediateStopped
}

/**
 * Set the current event phase of a given event.
 * @param {Event} event The event to set current target.
 * @param {number} eventPhase New event phase.
 * @returns {void}
 * @private
 */
function setEventPhase(event, eventPhase) {
    pd(event).eventPhase = eventPhase;
}

/**
 * Set the current target of a given event.
 * @param {Event} event The event to set current target.
 * @param {EventTarget|null} currentTarget New current target.
 * @returns {void}
 * @private
 */
function setCurrentTarget(event, currentTarget) {
    pd(event).currentTarget = currentTarget;
}

/**
 * Set a passive listener of a given event.
 * @param {Event} event The event to set current target.
 * @param {Function|null} passiveListener New passive listener.
 * @returns {void}
 * @private
 */
function setPassiveListener(event, passiveListener) {
    pd(event).passiveListener = passiveListener;
}

/**
 * @typedef {object} ListenerNode
 * @property {Function} listener
 * @property {1|2|3} listenerType
 * @property {boolean} passive
 * @property {boolean} once
 * @property {ListenerNode|null} next
 * @private
 */

/**
 * @type {WeakMap<object, Map<string, ListenerNode>>}
 * @private
 */
const listenersMap = new WeakMap();

// Listener types
const CAPTURE = 1;
const BUBBLE = 2;
const ATTRIBUTE = 3;

/**
 * Check whether a given value is an object or not.
 * @param {any} x The value to check.
 * @returns {boolean} `true` if the value is an object.
 */
function isObject(x) {
    return x !== null && typeof x === "object" //eslint-disable-line no-restricted-syntax
}

/**
 * Get listeners.
 * @param {EventTarget} eventTarget The event target to get.
 * @returns {Map<string, ListenerNode>} The listeners.
 * @private
 */
function getListeners(eventTarget) {
    const listeners = listenersMap.get(eventTarget);
    if (listeners == null) {
        throw new TypeError(
            "'this' is expected an EventTarget object, but got another value."
        )
    }
    return listeners
}

/**
 * Get the property descriptor for the event attribute of a given event.
 * @param {string} eventName The event name to get property descriptor.
 * @returns {PropertyDescriptor} The property descriptor.
 * @private
 */
function defineEventAttributeDescriptor(eventName) {
    return {
        get() {
            const listeners = getListeners(this);
            let node = listeners.get(eventName);
            while (node != null) {
                if (node.listenerType === ATTRIBUTE) {
                    return node.listener
                }
                node = node.next;
            }
            return null
        },

        set(listener) {
            if (typeof listener !== "function" && !isObject(listener)) {
                listener = null; // eslint-disable-line no-param-reassign
            }
            const listeners = getListeners(this);

            // Traverse to the tail while removing old value.
            let prev = null;
            let node = listeners.get(eventName);
            while (node != null) {
                if (node.listenerType === ATTRIBUTE) {
                    // Remove old value.
                    if (prev !== null) {
                        prev.next = node.next;
                    } else if (node.next !== null) {
                        listeners.set(eventName, node.next);
                    } else {
                        listeners.delete(eventName);
                    }
                } else {
                    prev = node;
                }

                node = node.next;
            }

            // Add new value.
            if (listener !== null) {
                const newNode = {
                    listener,
                    listenerType: ATTRIBUTE,
                    passive: false,
                    once: false,
                    next: null,
                };
                if (prev === null) {
                    listeners.set(eventName, newNode);
                } else {
                    prev.next = newNode;
                }
            }
        },
        configurable: true,
        enumerable: true,
    }
}

/**
 * Define an event attribute (e.g. `eventTarget.onclick`).
 * @param {Object} eventTargetPrototype The event target prototype to define an event attrbite.
 * @param {string} eventName The event name to define.
 * @returns {void}
 */
function defineEventAttribute(eventTargetPrototype, eventName) {
    Object.defineProperty(
        eventTargetPrototype,
        `on${eventName}`,
        defineEventAttributeDescriptor(eventName)
    );
}

/**
 * Define a custom EventTarget with event attributes.
 * @param {string[]} eventNames Event names for event attributes.
 * @returns {EventTarget} The custom EventTarget.
 * @private
 */
function defineCustomEventTarget(eventNames) {
    /** CustomEventTarget */
    function CustomEventTarget() {
        EventTarget.call(this);
    }

    CustomEventTarget.prototype = Object.create(EventTarget.prototype, {
        constructor: {
            value: CustomEventTarget,
            configurable: true,
            writable: true,
        },
    });

    for (let i = 0; i < eventNames.length; ++i) {
        defineEventAttribute(CustomEventTarget.prototype, eventNames[i]);
    }

    return CustomEventTarget
}

/**
 * EventTarget.
 *
 * - This is constructor if no arguments.
 * - This is a function which returns a CustomEventTarget constructor if there are arguments.
 *
 * For example:
 *
 *     class A extends EventTarget {}
 *     class B extends EventTarget("message") {}
 *     class C extends EventTarget("message", "error") {}
 *     class D extends EventTarget(["message", "error"]) {}
 */
function EventTarget() {
    /*eslint-disable consistent-return */
    if (this instanceof EventTarget) {
        listenersMap.set(this, new Map());
        return
    }
    if (arguments.length === 1 && Array.isArray(arguments[0])) {
        return defineCustomEventTarget(arguments[0])
    }
    if (arguments.length > 0) {
        const types = new Array(arguments.length);
        for (let i = 0; i < arguments.length; ++i) {
            types[i] = arguments[i];
        }
        return defineCustomEventTarget(types)
    }
    throw new TypeError("Cannot call a class as a function")
    /*eslint-enable consistent-return */
}

// Should be enumerable, but class methods are not enumerable.
EventTarget.prototype = {
    /**
     * Add a given listener to this event target.
     * @param {string} eventName The event name to add.
     * @param {Function} listener The listener to add.
     * @param {boolean|{capture?:boolean,passive?:boolean,once?:boolean}} [options] The options for this listener.
     * @returns {void}
     */
    addEventListener(eventName, listener, options) {
        if (listener == null) {
            return
        }
        if (typeof listener !== "function" && !isObject(listener)) {
            throw new TypeError("'listener' should be a function or an object.")
        }

        const listeners = getListeners(this);
        const optionsIsObj = isObject(options);
        const capture = optionsIsObj
            ? Boolean(options.capture)
            : Boolean(options);
        const listenerType = capture ? CAPTURE : BUBBLE;
        const newNode = {
            listener,
            listenerType,
            passive: optionsIsObj && Boolean(options.passive),
            once: optionsIsObj && Boolean(options.once),
            next: null,
        };

        // Set it as the first node if the first node is null.
        let node = listeners.get(eventName);
        if (node === undefined) {
            listeners.set(eventName, newNode);
            return
        }

        // Traverse to the tail while checking duplication..
        let prev = null;
        while (node != null) {
            if (
                node.listener === listener &&
                node.listenerType === listenerType
            ) {
                // Should ignore duplication.
                return
            }
            prev = node;
            node = node.next;
        }

        // Add it.
        prev.next = newNode;
    },

    /**
     * Remove a given listener from this event target.
     * @param {string} eventName The event name to remove.
     * @param {Function} listener The listener to remove.
     * @param {boolean|{capture?:boolean,passive?:boolean,once?:boolean}} [options] The options for this listener.
     * @returns {void}
     */
    removeEventListener(eventName, listener, options) {
        if (listener == null) {
            return
        }

        const listeners = getListeners(this);
        const capture = isObject(options)
            ? Boolean(options.capture)
            : Boolean(options);
        const listenerType = capture ? CAPTURE : BUBBLE;

        let prev = null;
        let node = listeners.get(eventName);
        while (node != null) {
            if (
                node.listener === listener &&
                node.listenerType === listenerType
            ) {
                if (prev !== null) {
                    prev.next = node.next;
                } else if (node.next !== null) {
                    listeners.set(eventName, node.next);
                } else {
                    listeners.delete(eventName);
                }
                return
            }

            prev = node;
            node = node.next;
        }
    },

    /**
     * Dispatch a given event.
     * @param {Event|{type:string}} event The event to dispatch.
     * @returns {boolean} `false` if canceled.
     */
    dispatchEvent(event) {
        if (event == null || typeof event.type !== "string") {
            throw new TypeError('"event.type" should be a string.')
        }

        // If listeners aren't registered, terminate.
        const listeners = getListeners(this);
        const eventName = event.type;
        let node = listeners.get(eventName);
        if (node == null) {
            return true
        }

        // Since we cannot rewrite several properties, so wrap object.
        const wrappedEvent = wrapEvent(this, event);

        // This doesn't process capturing phase and bubbling phase.
        // This isn't participating in a tree.
        let prev = null;
        while (node != null) {
            // Remove this listener if it's once
            if (node.once) {
                if (prev !== null) {
                    prev.next = node.next;
                } else if (node.next !== null) {
                    listeners.set(eventName, node.next);
                } else {
                    listeners.delete(eventName);
                }
            } else {
                prev = node;
            }

            // Call this listener
            setPassiveListener(
                wrappedEvent,
                node.passive ? node.listener : null
            );
            if (typeof node.listener === "function") {
                try {
                    node.listener.call(this, wrappedEvent);
                } catch (err) {
                    if (
                        typeof console !== "undefined" &&
                        typeof console.error === "function"
                    ) {
                        console.error(err);
                    }
                }
            } else if (
                node.listenerType !== ATTRIBUTE &&
                typeof node.listener.handleEvent === "function"
            ) {
                node.listener.handleEvent(wrappedEvent);
            }

            // Break if `event.stopImmediatePropagation` was called.
            if (isStopped(wrappedEvent)) {
                break
            }

            node = node.next;
        }
        setPassiveListener(wrappedEvent, null);
        setEventPhase(wrappedEvent, 0);
        setCurrentTarget(wrappedEvent, null);

        return !wrappedEvent.defaultPrevented
    },
};

// `constructor` is not enumerable.
Object.defineProperty(EventTarget.prototype, "constructor", {
    value: EventTarget,
    configurable: true,
    writable: true,
});

// Ensure `eventTarget instanceof window.EventTarget` is `true`.
if (
    typeof window !== "undefined" &&
    typeof window.EventTarget !== "undefined"
) {
    Object.setPrototypeOf(EventTarget.prototype, window.EventTarget.prototype);
}

/**
 * @author Toru Nagashima <https://github.com/mysticatea>
 * See LICENSE file in root directory for full license.
 */

/**
 * The signal class.
 * @see https://dom.spec.whatwg.org/#abortsignal
 */
class AbortSignal extends EventTarget {
    /**
     * AbortSignal cannot be constructed directly.
     */
    constructor() {
        super();
        throw new TypeError("AbortSignal cannot be constructed directly");
    }
    /**
     * Returns `true` if this `AbortSignal`'s `AbortController` has signaled to abort, and `false` otherwise.
     */
    get aborted() {
        const aborted = abortedFlags.get(this);
        if (typeof aborted !== "boolean") {
            throw new TypeError(`Expected 'this' to be an 'AbortSignal' object, but got ${this === null ? "null" : typeof this}`);
        }
        return aborted;
    }
}
defineEventAttribute(AbortSignal.prototype, "abort");
/**
 * Create an AbortSignal object.
 */
function createAbortSignal() {
    const signal = Object.create(AbortSignal.prototype);
    EventTarget.call(signal);
    abortedFlags.set(signal, false);
    return signal;
}
/**
 * Abort a given signal.
 */
function abortSignal(signal) {
    if (abortedFlags.get(signal) !== false) {
        return;
    }
    abortedFlags.set(signal, true);
    signal.dispatchEvent({ type: "abort" });
}
/**
 * Aborted flag for each instances.
 */
const abortedFlags = new WeakMap();
// Properties should be enumerable.
Object.defineProperties(AbortSignal.prototype, {
    aborted: { enumerable: true },
});
// `toString()` should return `"[object AbortSignal]"`
if (typeof Symbol === "function" && typeof Symbol.toStringTag === "symbol") {
    Object.defineProperty(AbortSignal.prototype, Symbol.toStringTag, {
        configurable: true,
        value: "AbortSignal",
    });
}

/**
 * The AbortController.
 * @see https://dom.spec.whatwg.org/#abortcontroller
 */
class AbortController$1 {
    /**
     * Initialize this controller.
     */
    constructor() {
        signals.set(this, createAbortSignal());
    }
    /**
     * Returns the `AbortSignal` object associated with this object.
     */
    get signal() {
        return getSignal(this);
    }
    /**
     * Abort and signal to any observers that the associated activity is to be aborted.
     */
    abort() {
        abortSignal(getSignal(this));
    }
}
/**
 * Associated signals.
 */
const signals = new WeakMap();
/**
 * Get the associated signal of a given controller.
 */
function getSignal(controller) {
    const signal = signals.get(controller);
    if (signal == null) {
        throw new TypeError(`Expected 'this' to be an 'AbortController' object, but got ${controller === null ? "null" : typeof controller}`);
    }
    return signal;
}
// Properties should be enumerable.
Object.defineProperties(AbortController$1.prototype, {
    signal: { enumerable: true },
    abort: { enumerable: true },
});
if (typeof Symbol === "function" && typeof Symbol.toStringTag === "symbol") {
    Object.defineProperty(AbortController$1.prototype, Symbol.toStringTag, {
        configurable: true,
        value: "AbortController",
    });
}

/*globals self, window */

/*eslint-disable @mysticatea/prettier */
const g =
    typeof self !== "undefined" ? self :
    typeof window !== "undefined" ? window :
    typeof global !== "undefined" ? global :
    /* otherwise */ undefined;
/*eslint-enable @mysticatea/prettier */

if (g) {
    if (typeof g.AbortController === "undefined") {
        g.AbortController = AbortController$1;
    }
    if (typeof g.AbortSignal === "undefined") {
        g.AbortSignal = AbortSignal;
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe Systems Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any. The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const ONS_AUTH_HEADER$1 = 'X-User-Token';
const MAX_RETRY_ATTEMPTS = 3;
const MIN_RETRY_INTERVAL_MS = 10; // 10 msec
// By default we want to monitor all resources that ONS supports:
const defaultONSObjectProperties = {
    includeResources: [
        { reltype: 'api:metadata/repository' },
        { reltype: 'api:metadata/application' },
        { reltype: 'api:ac/policy' }
    ]
};
// @TODO Since we are creating more subscriptions more often due to
// us no longer caching subscriptions we will need to set
// the TTL to a more recent time in order for subscriptions
// to get garbage collected faster. We should update this once
// SAM-2845 is done.
const subscriptionTTL = millisecondsPerHour * 6;
/**
* Interface to register ONS subscription and listen for change events.
*/
class ONSSubscription extends EventEmitter {
    constructor(_onsInterface, cachedSubscription) {
        super();
        this._onsInterface = _onsInterface;
        this._assets = new Map();
        this._watchedAssets = new Map();
        this._initializedFromCache = false;
        if (cachedSubscription) {
            const { id, href, assets } = cachedSubscription;
            this._subscriptionId = id;
            this._subscriptionHref = href;
            this._watchedAssets = new Map(Object.entries(assets));
            // We check if we are given watchedAssetId's and this will
            // indicate that we are loading them from cache.
            this._initializedFromCache = this._watchedAssets.size > 0;
        }
    }
    get _core() { return this._onsInterface._core; }
    get subscriptionId() { return this._subscriptionId; }
    get subscriptionHref() { return this._subscriptionHref; }
    _log(msg) {
        this._core.environment.log(msg);
    }
    get lastError() { return this._lastError; }
    get assets() { return this._assets; }
    get watchedAssets() { return this._watchedAssets; }
    abort() {
        if (this._retryTimeout) {
            clearTimeout(this._retryTimeout);
            this._retryTimeout = undefined;
            this._log(`subscription ${this.subscriptionId} retry got aborted`);
            return true;
        }
        return false;
    }
    set assets(assets) {
        this._assets = new Map(assets);
        this._update();
    }
    async _update(tryAttempt = 0) {
        var _a;
        // Abort any retry attempts as we might need to remove, add
        // or update subscriptions which may cancel out previous
        // requests.
        this.abort();
        const assets = this._assets;
        // This makes a comparison of the "new" id's just added and the
        // previous assets that were added (watchedAssets);
        if (isDeepEqual(assets, this._watchedAssets)) {
            if (this._lastError) {
                // If we had an error creating or updating the subscription, but then we revert back to the
                // previous state, we need to clear the error otherwise we'll be stuck with an error!
                this._lastError = undefined;
                // Emit a change event to tell ONSInterface that we can begin
                // the longpoll request.
                this.emit('change');
            }
            else if (this._initializedFromCache) {
                // If we initialized the subscription with information
                // from the cache and the assets being added by the client
                // are exactly the same, we still want to notify the ONSInterface
                // that it can begin the longpoll request otherwise it wont.
                this.emit('change', { fromCache: true });
            }
            return;
        }
        this._log(`Setting ${assets.size} asset ids for ${this.subscriptionId || '<pending subscription id>'}`);
        try {
            if (assets.size === 0) {
                this._log(`deleting subscription ${this.subscriptionId} requested assets no longer exist`);
                await this._deleteSubscriptionFromServer();
            }
            else if (this._subscriptionHref) {
                this._log(`updating subscription on the server ${this.subscriptionId}`);
                await this._updateSubscriptionOnServer(assets);
            }
            else {
                this._log(`creating subscription`);
                await this._createSubscriptionOnServer(assets);
            }
            // Add the new ids to the "old" ids list
            // so we can track changes later on when even newer
            // ids are added to this subscription.
            this._watchedAssets = assets;
            this._lastError = undefined;
            this.emit('change');
        }
        catch (err) {
            this._log(`Error updating subscription: ${err}`);
            this._lastError = err;
            this.emit('change');
            // Schedule a retry for later if there's an error
            if (tryAttempt < MAX_RETRY_ATTEMPTS) {
                let retryAfter = (getRetryAfterHeader((_a = err.response) === null || _a === void 0 ? void 0 : _a.headers) || ONSSubscription.DEFAULT_RETRY_INTERVAL_SEC) * 1000;
                retryAfter = Math.max(MIN_RETRY_INTERVAL_MS, retryAfter);
                this._retryTimeout = setTimeout(() => {
                    this._retryTimeout = undefined;
                    this._update(tryAttempt + 1);
                }, retryAfter);
            }
        }
    }
    async _parseSubscriptionResponse(operation, getResponse) {
        const response = await getResponse();
        if (response.status < 200 || response.status >= 300) {
            let errMsg = `operation ${operation} with http response.status: ${response.status}`;
            if (response.status === 403) {
                const jsonError = await response.json();
                if (jsonError.type === ProblemType.SUBSCRIPTION_COUNT) {
                    errMsg = `Maximum number of subscriptions per user per device exceeded (${jsonError.max})`;
                }
            }
            this._log(errMsg);
            throw SAMError.createWithResponseAndDescription(response, errMsg);
        }
        const json = await response.json();
        if (!json.id || !json._links || !json._links.self || !json._links.self.href) {
            const errMsg = `No subscription ID or href was returned in subscribe response.`;
            this._log(errMsg);
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, errMsg, { response });
        }
        this._subscriptionId = json.id;
        this._subscriptionHref = json._links.self.href;
    }
    async _createSubscriptionOnServer(assets) {
        const href = await this._onsInterface._getLink(RelType.SUBSCRIPTION_CREATE);
        const ids = Array.from(assets.keys());
        const deviceId = await this._core.environment.getDeviceId();
        const requestBody = JSON.stringify({
            objects: ids.map(id => (Object.assign(Object.assign({ id }, defaultONSObjectProperties), assets.get(id)))),
            device: {
                id: deviceId
            },
            expiresAt: Date.now() + subscriptionTTL
        });
        this._log(`Creating subscription for ${assets.size} assets: ${ids.join(', ')}`);
        return this._parseSubscriptionResponse('create', () => this._core.environment.fetch(href, {
            method: 'POST',
            body: requestBody,
            authHeader: ONS_AUTH_HEADER$1,
            headers: {
                'Content-Type': 'application/vnd.adobecloud.subscription+json'
            }
        }));
    }
    async _updateSubscriptionOnServer(assets) {
        // finding out which assetID(s) to add and which to remove
        const oldAssets = this._watchedAssets;
        const newAssets = assets;
        const operations = [];
        newAssets.forEach((properties, id) => {
            if (!oldAssets.has(id)) {
                const item = { 'op': 'add', 'path': '/objects/-', 'value': Object.assign(Object.assign({ id }, defaultONSObjectProperties), properties) };
                operations.push(item);
            }
        });
        oldAssets.forEach((properties, id) => {
            if (!newAssets.has(id)) {
                const item = { 'op': 'remove', 'path': `/objects/id=${id}` };
                operations.push(item);
            }
        });
        const requestBody = JSON.stringify(operations);
        this._log(`Updating subscription ${this._subscriptionId}: ${requestBody}`);
        try {
            await this._parseSubscriptionResponse('update', () => this._core.environment.fetch(this._subscriptionHref, {
                method: 'PATCH',
                body: requestBody,
                authHeader: ONS_AUTH_HEADER$1,
                headers: {
                    'Content-Type': JSON_PATCH_MIME_TYPE
                }
            }));
        }
        catch (err) {
            // If we get a 404 updating a subscription, we should fall back to creating a new subscription instead
            if (err.code === SAMErrorCode.NOT_FOUND) {
                this._log(`The subscription we tried to update no longer exists (${this._subscriptionId}) - creating a new subscription instead`);
                return this._createSubscriptionOnServer(assets);
            }
            throw err;
        }
    }
    async _deleteSubscriptionFromServer() {
        this._log(`Deleting subscription ${this._subscriptionId}`);
        try {
            // Note: There's no way to reach this point with _subscriptionHref undefined, so we assert that it's defined
            await this._core.environment.fetch(this._subscriptionHref, {
                method: 'DELETE',
                authHeader: ONS_AUTH_HEADER$1
            });
        }
        catch (error) {
            // We tried.
            this._log(`Unable to delete subscription: ${this._subscriptionHref}. Error: ${error}`);
        }
        // We clear the subscription even if we fail to delete it - ONS will auto-delete orphaned subscriptions after some time anyway,
        // so we don't need to be aggressive about retrying
        this._subscriptionId = undefined;
        this._subscriptionHref = undefined;
    }
    async _fetchChangeLogPage(changedAssets) {
        var _a, _b, _c, _d;
        const options = { subscriptionId: this._subscriptionId };
        const href = this._nextChangeLogLinks
            ? getURLFromLinks(this._nextChangeLogLinks, RelType.GENERIC_NEXT, options)
            : await this._onsInterface._getLink(RelType.ONS_CHANGELOG, options);
        // Note: Unlike the other ONS calls, this uses the standard Authorization header (other ONS calls use X-User-Token)
        // This is confusing and error-prone, so I'd like them to standardize on the Authorization header for all calls, but
        // we have to live with this inconsistency for now.
        // ONS introduced a new directive header, all clients are expected to pass ons-repometa to
        // indicate they are ready to handle compact URI for repository metadata resource
        const response = await this._core.environment.fetch(href, { headers: { 'directive': 'ons-repometa' } });
        if (response.status !== 200) {
            let errMsg = `Unexpected response fetching change log: ${response.status}`;
            // In the case that it's 403 and the type is
            // ONS_PROBLEM_LIMIT_CHANGELOGRETENTION this should mean that the
            // the current Changelog Token is expired and the we should get
            // a new "next" link.
            if (response.status === 403) {
                const parsedResponse = await response.json();
                if (parsedResponse.type === ProblemType.LIMIT_CHANGELOGRETENTION) {
                    errMsg = `Changelog retention period expired, discarding "next" link and fetching changelog again.`;
                    // When we throw an error below, it will cause the caller
                    // to retry this call and by setting _nextChangeLogLinks to undefined
                    // it will then fetch a new "next" link above.
                    this._nextChangeLogLinks = undefined;
                }
            }
            this._log(errMsg);
            throw SAMError.createWithResponseAndDescription(response, errMsg);
        }
        const json = await response.json();
        // From the response, determine which asset ids changed
        const changedSubscriptions = (_a = json['ons:changedSubscriptions']) !== null && _a !== void 0 ? _a : [];
        const changedSubscription = changedSubscriptions.find((subscriptionInfo) => subscriptionInfo['ons:subscriptionId'] === this._subscriptionId);
        const changes = (_b = changedSubscription === null || changedSubscription === void 0 ? void 0 : changedSubscription['ons:changes']) !== null && _b !== void 0 ? _b : [];
        changes.forEach((assetInfo) => {
            var _a;
            const assetId = assetInfo['repo:assetId'];
            const onsResourceInfo = (_a = assetInfo['ons:resources']) !== null && _a !== void 0 ? _a : [];
            const onsResourcesChanged = [...Object.keys(onsResourceInfo)];
            const parentAssetId = assetInfo['ons:subscribedDirectoryAssetId'];
            if (assetId) {
                // If the asset is already in changedAssets, merge the next changed resource so we
                // don't lose any previous resources that were added here.
                if (changedAssets.has(assetId)) {
                    const existingAsset = changedAssets.get(assetId);
                    existingAsset.changedResources = [...new Set([...existingAsset.changedResources, ...onsResourcesChanged])];
                    changedAssets.set(assetId, existingAsset);
                }
                else {
                    changedAssets.set(assetId, { parentAssetId, changedResources: onsResourcesChanged });
                }
            }
        });
        this._nextChangeLogLinks = json === null || json === void 0 ? void 0 : json._links;
        return (_d = (_c = this._nextChangeLogLinks) === null || _c === void 0 ? void 0 : _c.next) === null || _d === void 0 ? void 0 : _d.nextPage;
    }
    /**
     * Fetch the changed assets ids in the subscription, and emit a change event on the parent ONS Interface for any changed assets.
     * The reason this API emits events rather than just returning the changed assets is so we can have robust retry logic - if it fails
     * to fetch the change log it'll schedule a retry with an appropriate backoff, so it automatically recovers. The ONS Interface can
     * simply call fetchAndNotifyChanges on a subscription that it knows has changes, and the subscription will take responsibility for
     * eventually fetching and emitting notifications for these changes, even if it has to retry.
     *
     * This API is stateful, so if you call it when the subscription changed, it will emit a change event for those assets, but if you call it
     * again (and there are no further changes), it will not emit an event.
     */
    async fetchAndNotifyChanges(attempt = 0) {
        this._log(`Fetching ChangeLog for subscription ${this._subscriptionId}...`);
        // If we got another notification of a change, this causes us to retry immediately, and cancels any pending retry
        if (this._retryChangeLogTimeout) {
            clearTimeout(this._retryChangeLogTimeout);
        }
        const retry = (error) => {
            var _a;
            const retryAfter = getRetryAfterHeader((_a = error.response) === null || _a === void 0 ? void 0 : _a.headers) * 1000;
            const interval = Math.max(retryAfter, this._core.environment.getRetryInterval(attempt));
            this._log(`Retrying change log fetch in ${interval}ms`);
            this._retryChangeLogTimeout = setTimeout(() => this.fetchAndNotifyChanges(attempt + 1), interval);
        };
        const changedAssets = new Map();
        try {
            let hasMorePages = true;
            while (hasMorePages) {
                hasMorePages = await this._fetchChangeLogPage(changedAssets);
            }
        }
        catch (error) {
            this._log(`Error fetching change log: ${error}`);
            retry(error);
        }
        // Even if there's an error, we always notify of any changes we did manage to fetch - for example, if the
        // first page succeeded, but the second page didn't, when we retry we'll retry from the second page.
        if (changedAssets.size) {
            this._log(`Changed Assets: ${[...changedAssets.keys()].join(', ')}`);
            this._onsInterface.emit('change', changedAssets);
        }
    }
    // This is called to check whether the subscription still exists on the server.
    // The ONS long poll will sometimes request the client to
    // validate its subscriptions. If the subscription was deleted, it'll recreate it.
    async validate() {
        if (!this._subscriptionHref) {
            // If we're requesting to watch at least one asset id, and don't have a subscription at all,
            // something must have gone wrong creating it - we attempt to create again.
            if (this._assets.size > 0) {
                this._log(`assets exist for subscription but no _subscriptionHref was found, trying to _update() again...`);
                return this._update();
            }
            this._log(`subscription has no assets and no _subscriptionHref set`);
            return;
        }
        let checkResponse;
        try {
            // ONS spec says to GET (vs HEAD). Why?
            checkResponse = await this._core.environment.fetch(this._subscriptionHref, {
                authHeader: ONS_AUTH_HEADER$1
            });
            this._log(`Subscription ${this._subscriptionId} exists on the server`);
        }
        catch (error) {
            this._log(`Error checking ${this._subscriptionId}, it does not exist on the server`);
            this._log(error);
            return;
        }
        // If we can't find the subscription, we need to make a new one.
        if (![200, 201, 204].includes(checkResponse.status)) {
            this._log(`ONS subscription has been invalidated. Creating new subscription...`);
            this._subscriptionHref = undefined;
            this._subscriptionId = undefined;
            this._watchedAssets = new Map();
            return this._update();
        }
    }
}
// Default interval between retry attempts in seconds if no Retry-After header value is available.
ONSSubscription.DEFAULT_RETRY_INTERVAL_SEC = 0;
__decorate([
    synchronized
], ONSSubscription.prototype, "_update", null);
__decorate([
    synchronized
], ONSSubscription.prototype, "fetchAndNotifyChanges", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe Systems Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any. The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const REQUIRED_LINKS = [RelType.SUBSCRIPTION_CREATE, RelType.NOTIFICATIONS_LONGPOLL, RelType.ONS_CHANGELOG];
const ONS_AUTH_HEADER = 'X-User-Token';
// Set the x-poll-timeout request header value (in seconds) for ONS long poll requests
// This controls the frequency of the ONS server long poll responses.
const ONS_POLLTIMEOUT_SECONDS = 160;
const FIRST_LONG_POLL_STARTED = 'firstLongPollStarted';
const FIRST_LONG_POLL_COMPLETED = 'firstLongPollCompleted';
const validateRootDocument = (json) => json._links && REQUIRED_LINKS.every(key => json._links[key]);
const cacheKey = 'ONS_SUBSCRIPTIONS';
/**
 * Interface to register ONS subscription and listen for change events.
 * Design notes:
 * Polling will only happen when the following three conditions are ALL met:
 *  1. start() has been called (and stop() has not been called).
 *  2. watch() has been called with a non-empty set of asset ids.
 *  3. The document is visible (document.visibilityState === 'visible').
 */
class ONSInterface extends EventEmitter {
    constructor(_core, 
    // @TODO SAM-2845 This may be enabled the same in the future but working
    // with the SharedWorker. For now we want to return undefined.
    _cacheEnabled = _core.environment.getValue('ENABLE_ONS_CACHE')) {
        super();
        this._core = _core;
        this._cacheEnabled = _cacheEnabled;
        // This flag is indicating if the top level start()/stop() has been called.
        this._isStarted = false;
        this._subscriptions = [];
        this._maxAssetsPerSubscription = 5;
        this._assetIds = new Set();
        this._cachedSubscriptionsUsed = false;
        this._onSubscriptionChange = ({ fromCache = false } = {}) => {
            // IF change are notified let's make sure to update the cache as well.
            if (!fromCache) {
                this._cachedSubscriptionsUsed = false;
            }
            this._updateCachedSubscriptions();
            this._startLongPoll();
        };
        this._onVisibilityChange = () => {
            var _a;
            if (this._stopTimeout) {
                clearTimeout(this._stopTimeout);
                this._stopTimeout = undefined;
            }
            if (((_a = this._document) === null || _a === void 0 ? void 0 : _a.visibilityState) === 'visible') {
                this._start(); // Won't do anything if we didn't stop yet, since still active
            }
            else {
                // we're de-bouncing to avoid repeatedly stopping/restarting if the user switches between
                // Web browser tabs frequently.
                this._stopTimeout = setTimeout(() => this._stop(), this._core.environment.getValue('ONS_DOCUMENT_VISIBILITY_TIMEOUT')); // 10s
            }
        };
        this._cache = _core.environment.cache;
    }
    async _getSubscriptionConfig() {
        var _a, _b;
        try {
            const rootDocument = await this._getRootDocument();
            return (_b = (_a = rootDocument === null || rootDocument === void 0 ? void 0 : rootDocument.config) === null || _a === void 0 ? void 0 : _a.subscriptions) !== null && _b !== void 0 ? _b : {};
        }
        catch (e) {
            this._log(`Error fetching subscription config from ONS root document: ${e}`);
            return {};
        }
    }
    async _updateCachedSubscriptions() {
        // @TODO SAM-2845 where this is defined for more information.
        // Prevent data from being written to cache
        // until it is enabled again in the future.
        if (!this._cacheEnabled) {
            return;
        }
        const newCachedSubscriptions = [];
        this._subscriptions.forEach((subscription) => {
            const { subscriptionId, subscriptionHref, assets } = subscription;
            // If the ONSSubscription doesn't have an ID or Href
            // that means it wasn't created on the server and
            // it doesn't have any information to cache.
            if (!subscriptionId || !subscriptionHref) {
                return;
            }
            const updatedCache = {
                id: subscriptionId,
                href: subscriptionHref,
                assets: Object.fromEntries(assets)
            };
            newCachedSubscriptions.push(updatedCache);
        });
        await this._cache.setItem(cacheKey, JSON.stringify({ subscriptions: newCachedSubscriptions }), 
        // This ensures that the cached subscription will expire the same time
        // that the subscription expires on the server.
        subscriptionTTL);
    }
    async _getAndParseCachedSubscriptions() {
        // @TODO SAM-2845 Prevent data from being retrieved from cache
        // until it is enabled again in the future.
        if (!this._cacheEnabled) {
            return [];
        }
        const existingCreatedSubscriptions = await this._cache.getItem(cacheKey);
        if (existingCreatedSubscriptions !== NotFound$1) {
            try {
                return JSON.parse(existingCreatedSubscriptions).subscriptions;
            }
            catch (e) {
                this._log('error parsing cached subscriptions');
            }
        }
        return [];
    }
    async _ensureSubscriptions() {
        const subscriptionConfig = await this._getSubscriptionConfig();
        const maxSubscriptions = subscriptionConfig.maxSubscriptionPerLongPoll || this._core.environment.getValue('ONS_DEFAULT_MAX_SUBSCRIPTIONS');
        this._maxAssetsPerSubscription = subscriptionConfig.maxAssetsPerRequest || this._core.environment.getValue('ONS_DEFAULT_MAX_ASSETS_PER_SUBSCRIPTION');
        if (maxSubscriptions > this._subscriptions.length) {
            // Just because we are creating an ONSSubscription does not mean
            // we are creating a subscription on the service. We actually
            // create the max number of local ONSSubscription allowed by ONS but only
            // create the subscription on the service if there are assets on that subscription.
            const parsedCachedSubscriptions = await this._getAndParseCachedSubscriptions();
            this._cachedSubscriptionsUsed = parsedCachedSubscriptions.length > 0;
            // Add new subscriptions
            for (let i = this._subscriptions.length; i < maxSubscriptions; ++i) {
                // If we have a corresponding cached subscription
                // pull the information from cache and construct
                // the ONSSubscription with data from the cache.
                // If the cached subscription information is out of date,
                // it will be handled inside the ONSSubscription.
                const sub = new ONSSubscription(this, parsedCachedSubscriptions[i]);
                this._subscriptions.push(sub);
                sub.on('change', this._onSubscriptionChange);
            }
        }
        else if (maxSubscriptions < this._subscriptions.length) {
            // Remove subscriptions over the limit
            for (let i = maxSubscriptions; i < this._subscriptions.length; ++i) {
                const sub = this._subscriptions[i];
                sub.assets = new Map();
                sub.off('change', this._onSubscriptionChange);
            }
            this._subscriptions.length = maxSubscriptions; // Removes the overflow from the array
        }
    }
    get _activeSubscriptionIds() {
        return Array.from(this._subscriptions)
            .filter(sub => sub.subscriptionId && sub.watchedAssets.size > 0) // Note: watchedAssets not assets
            .map(sub => `${sub.subscriptionId}`)
            .sort();
    }
    async _startLongPoll() {
        const subscriptionIds = this._activeSubscriptionIds;
        // we don't want to create a bogus long pollURL if
        // there is no subscriptions...
        const newPollURL = subscriptionIds.length === 0 ? undefined : await this._getLink(RelType.NOTIFICATIONS_LONGPOLL, {
            includeJournal: 'false', subscriptionId: subscriptionIds
        });
        const isUrlChanged = this._pollURL !== newPollURL;
        this._pollURL = newPollURL;
        if (this.isActive && isUrlChanged) {
            this._stop();
            this._start();
        }
    }
    _log(message) {
        this._core.environment.debug(`ONS: ${message}`);
    }
    get isActive() {
        return Boolean(this._abortController);
    }
    get assetIds() {
        return new Set(this._assetIds); // lets return copy it's safer
    }
    get watchedAssetIds() {
        const assetIds = new Set();
        this._subscriptions.forEach((sub) => {
            sub.watchedAssets.forEach((properties, assetId) => assetIds.add(assetId));
        });
        return assetIds;
    }
    get _document() {
        if (typeof window !== 'undefined') {
            return window.document;
        }
        return undefined;
    }
    /**
     * Start ONS session
     */
    start() {
        var _a;
        if (this._isStarted) {
            return;
        }
        (_a = this._document) === null || _a === void 0 ? void 0 : _a.addEventListener('visibilitychange', this._onVisibilityChange);
        this._isStarted = true;
        if (!this._document || this._document.visibilityState === 'visible') {
            this._start();
        }
    }
    /**
     * Stop ONS session
     */
    stop() {
        var _a;
        if (!this._isStarted) {
            return;
        }
        (_a = this._document) === null || _a === void 0 ? void 0 : _a.removeEventListener('visibilitychange', this._onVisibilityChange);
        this._isStarted = false;
        this._subscriptions.forEach((sub) => sub.abort());
        this._stop();
    }
    _stop() {
        if (!this.isActive) {
            return;
        }
        if (this._stopTimeout) {
            clearTimeout(this._stopTimeout);
            this._stopTimeout = undefined;
        }
        this._log(`Stopping...`);
        const abortController = this._abortController;
        delete this._abortController;
        this._abortController = undefined;
        abortController === null || abortController === void 0 ? void 0 : abortController.abort();
    }
    _start() {
        if (this.isActive) {
            this._log(`Already starting skipping...`);
            return;
        }
        this._log(`Starting...`);
        this._abortController = new AbortController();
        this._poll();
    }
    _truncateAssetIdsByMaxSizeIfNeeded(assetIds) {
        const maxTotalAssets = this._maxAssetsPerSubscription * this._subscriptions.length;
        if (assetIds.size > maxTotalAssets) {
            this._log(`Number of assets ${assetIds.size} exceeds the maximum allowed by ONS. Limiting to ${maxTotalAssets}`);
            // TODO:
            //this._analytics.reportError('ONSSubscriptionError', `Requested assets exceed number allowed. Count: ${assetIds.length}, Max: ${maxTotalAssets}`);
            assetIds = new Set(Array.from(assetIds).slice(0, maxTotalAssets));
        }
        return assetIds;
    }
    /**
     * Watches an array of assetIds.
     *
     * @param {Array} assetIds
     */
    async watch(assetIds) {
        // This will create and store instances of ONSSubscription
        // the number created will be the number of MAX_SUBSCRIPTIONS
        // set by SAM.js or service (we don't actually create subscriptions
        // on the server though, unless they have any assets to monitor)
        await this._ensureSubscriptions();
        // Firstly we want to look for all the asset ids for which we don't also
        // have their parent loaded. If we're also monitoring the parent, we can just
        // do a shallow directory monitor, which covers the children as well.
        const assetIdsToMonitor = new Set();
        assetIds.forEach(id => {
            var _a;
            const asset = this._core.hostAssetContext.assetPool.getLocalItemById(id);
            const ancestorIds = (_a = asset === null || asset === void 0 ? void 0 : asset.ancestorIDs) !== null && _a !== void 0 ? _a : [];
            const parentId = ancestorIds[ancestorIds.length - 1];
            // Note: we monitor all folder assets regardless of if we're monitoring the parent,
            // otherwise we'd have to individually monitor children of nested folders. We
            // could maybe optimize this if we haven't loaded any children of the directory,
            // but this seems sufficient
            if (!assetIds.has(parentId) || (asset === null || asset === void 0 ? void 0 : asset.isFolder)) {
                assetIdsToMonitor.add(id);
            }
        });
        this._assetIds = this._truncateAssetIdsByMaxSizeIfNeeded(assetIdsToMonitor);
        // map from subscription id to current assets being monitored,
        // (in the form of a map from the asset id to its properties)
        // minus ones we no longer need to monitor:
        // Creates an array and each entry is a
        // Map() that represents the assets in the subscription.
        const existingAssetIds = new Set();
        const subscriptionToAssetIds = Array.from(this._subscriptions.map(sub => {
            const ids = new Map();
            sub.assets.forEach((properties, id) => {
                if (this._assetIds.has(id) && existingAssetIds.add(id)) {
                    ids.set(id, properties);
                }
            });
            return ids;
        }));
        // We assign assets to subscriptions such that we fill up the first subscription first
        // before creating new ones. Note that when this was first implemented in SAM.js, we
        // would try to spread assets across as many subscriptions as possible, because ONS didn't
        // tell us which asset in a subscription changed - but now that we have the changelog API,
        // it's better to concentrate assets in as few subscriptions as possible.
        let nextIndex = 0;
        this._assetIds.forEach(id => {
            // Skip any assets that are already part of a subscription
            if (existingAssetIds.has(id)) {
                return;
            }
            // Find the next subscription with space in it
            while (subscriptionToAssetIds[nextIndex].size >= this._maxAssetsPerSubscription) {
                nextIndex++;
            }
            // Determine if the asset is a directory or not - since we want to do a self monitor of directories:
            const asset = this._core.hostAssetContext.assetPool.getLocalItemById(id);
            subscriptionToAssetIds[nextIndex].set(id, (asset === null || asset === void 0 ? void 0 : asset.isFolder) ? { directoryLevel: 'shallow' } : {});
        });
        // Finally, update the actual subscriptions
        this._subscriptions.forEach((sub, index) => sub.assets = subscriptionToAssetIds[index]);
    }
    // This is used to notify the client that the initial long poll has been started along with the information
    // whether cached subscriptions were used or not. It can be used by the clients to avoid any refresh of its cached
    // data if cached subscriptions were used.
    _notifyFirstLongPollStarted(cachedSubscriptionsUsed = false) {
        this.emit(FIRST_LONG_POLL_STARTED, { cachedSubscriptionsUsed });
    }
    // This is used to notify the client that the initial long poll has been completed along with the information
    // whether they were expired or not. It can be used by the clients to avoid any refresh of its cached
    // data if cached subscriptions were used.
    _notifyFirstLongPollCompleted(expired) {
        this.emit(FIRST_LONG_POLL_COMPLETED, { expired });
    }
    /**
     * This sets up the longpoll request with retries if needed.
     *
     * @param attempt This tracks the number of attempts already made so we can back off in needed.
     * @returns void;
     */
    async _poll(attempt = 0) {
        const retry = (error, interval = 0) => {
            interval = Math.max(this._core.environment.getRetryInterval(attempt), interval);
            const abortController = this._abortController;
            if (!abortController) {
                return;
            }
            this._log(`Retrying long poll in ${interval}ms`);
            const timeout = setTimeout(() => this._poll(attempt + 1), interval);
            abortController.signal.addEventListener('abort', () => {
                if (abortController !== this._abortController) {
                    clearTimeout(timeout);
                }
            });
        };
        const abortController = this._abortController;
        if (!abortController) {
            this._log(`No abort controller...`);
            return;
        }
        try {
            // this.pollURL can be undefined at start up when watch didn't have a time to build it yet,
            // also if we deleted all subscriptions calling watch with empty set.
            //
            // _pollURL is set by calling watch() which calls _ensureSubscriptions() which then sets
            // an event handler for a "change" event on each subscription created (ONSSubscription). The subscription
            // will emit a "change" event for various cases but mainly for when assetIds are updated on
            // the subscription. Once a change is emitted, the
            // the assigned _onSubscriptionChangeFun() is called and then calls _onSubScriptionChange().
            // then finally sets the URL. @TODO maybe we can streamline this process a bit more.
            if (this._pollURL === undefined) {
                this._log(`Poll URL undefined...`);
                // just returning here because when pollUrl get populated it will call _stop(), _start()
                return;
            }
            // Go ahead and notify clients about first long poll.
            this._notifyFirstLongPollStarted(this._cachedSubscriptionsUsed);
            const href = this._pollURL;
            // Give the service a 20s buffer to respond on their end before terminating on our end
            const requestTimeoutMs = (ONS_POLLTIMEOUT_SECONDS + 20) * 1000;
            const response = await this._core.environment.fetch(href, {
                signal: abortController.signal,
                authHeader: ONS_AUTH_HEADER,
                headers: {
                    'x-poll-timeout': `${ONS_POLLTIMEOUT_SECONDS}`
                },
                requestTimeoutMs
            });
            if ([200, 201, 204].includes(response.status)) {
                const json = await response.json();
                this._notifyFirstLongPollCompleted(false);
                this._log(`Successful long poll for ${href}`);
                // Sometimes (frequently) we get a 20x response, and nothing has changed.
                if (json && json.subscriptions && json.subscriptions.length > 0) {
                    this._log(`Subscriptions changed: ${json.subscriptions.join(', ')}`);
                    // Trigger the changed subscriptions to fetch changes via the changelog API:
                    // Note that ONS does not yet support a single changelog for multiple subscriptions, so we have
                    // to call the changelog separately for each subscription right now.
                    json.subscriptions.forEach((id) => {
                        const subscription = this._subscriptions.find(sub => sub.subscriptionId === id);
                        subscription === null || subscription === void 0 ? void 0 : subscription.fetchAndNotifyChanges();
                    });
                    // We don't really need this as part of the public API, but it makes the tests much easier,
                    // so we can abort the polling.
                    this.emit('notified');
                }
                // If ONS is worried that our subscription is invalid, verify it is still there. (ONS will do this
                // every few hours no matter what.)
                if (json && json.checkSubscriptions && json.checkSubscriptions.length > 0) {
                    this._log(`Checking subscriptions for subscriptions to check ${json.checkSubscriptions.length}`);
                    json.checkSubscriptions.forEach((id) => {
                        const subscription = this._subscriptions.find(sub => sub.subscriptionId === id);
                        if (subscription) {
                            subscription.validate();
                        }
                    });
                }
                this._pollURL = json && json._links && json._links.next && json._links.next.href;
                this._poll();
            }
            else if (response.status === 404) {
                this._notifyFirstLongPollCompleted(true);
                this._log(`404 Response long poll for ${href}, subscription missing or expired`);
                // If the response is a 404, we've somehow lost the subscriptions. Make a new one.
                this._subscriptions.forEach((subscription) => {
                    if (subscription.subscriptionId) {
                        subscription.validate();
                    }
                });
                // We need to retry here because if subscription will be validated on the server
                // then 'change' event will not be emitted and we will stop polling here.
                retry(undefined, this._core.environment.getValue('MAX_RETRY_INTERVAL'));
            }
            else {
                this._log(`Unexpected response, trying again ${href}`);
                // Otherwise, follow the retry logic defined by the ONS team.
                retry(undefined, getRetryAfterHeader(response.headers) * 1000);
            }
        }
        catch (error) {
            // If we aborted this request, we don't want to retry.
            // But only if we aborted it locally. If it was aborted elsewhere, we want to retry.
            // (AbortController is used for timeouts in Proxy#fetch)
            if (abortController.signal.aborted && abortController !== this._abortController) {
                this.emit('aborted');
                this._notifyFirstLongPollCompleted(true);
                return;
            }
            this._log(`Error trying to create long poll request`);
            retry();
        }
    }
    async _getAndCacheDocument(href) {
        const response = await this._core.environment.fetch(href, {
            headers: {
                Accept: 'application/vnd.syncservice.v2.hal+json'
            },
            authHeader: ONS_AUTH_HEADER
        });
        return response.json();
    }
    _getRootDocument() {
        return this._getAndCacheDocument(`https://${this._core.environment.endpoints.SYNC_SERVICE_HOST}/api/v1`);
    }
    async _getLink(key, options = {}) {
        const rootDocument = await this._getRootDocument();
        return getURLFromLinks(rootDocument._links, key, options);
    }
}
__decorate([
    synchronized
], ONSInterface.prototype, "_updateCachedSubscriptions", null);
__decorate([
    synchronized
], ONSInterface.prototype, "_startLongPoll", null);
__decorate([
    memoize
], ONSInterface.prototype, "_notifyFirstLongPollStarted", null);
__decorate([
    memoize
], ONSInterface.prototype, "_notifyFirstLongPollCompleted", null);
__decorate([
    cache(validateRootDocument),
    __param(0, key)
], ONSInterface.prototype, "_getAndCacheDocument", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class ItemExclusiveTaskChain {
    constructor(task, onGotOwnership) {
        this.task = task;
        this.onGotOwnership = onGotOwnership;
    }
    // Add the task at the end of the linked list.
    registerNewTask(newTask) {
        let itemTask = this;
        while (itemTask) {
            const next = itemTask.next;
            if (!next) {
                // We've found the first empty slot.
                itemTask.next = newTask;
                return;
            }
            // Keep going till the end.
            itemTask = next;
        }
    }
}
/**
 * Base object used for all assets, teams, spaces etc.
 */
class ItemBase extends TypedEventEmitter {
    constructor(_core, id) {
        super();
        this._core = _core;
        this.id = id;
    }
    get lastKnownClientMutation() {
        return this._lastKnownClientMutation;
    }
    updateLastKnownClientMutation() {
        this._lastKnownClientMutation = Date.now();
    }
    /**
     * Waits for at least `delay` ms since the last known asset mutation.
     * @param delay
     */
    async waitForMutationPropagationIfNeeded(delay) {
        const mutation = this._lastKnownClientMutation;
        if (mutation) {
            const waitTime = mutation + delay - Date.now();
            if (waitTime > 0) {
                // We still have some time left before the asset change is propagated for sure.
                await new Promise((resolve) => setTimeout(resolve, waitTime));
            }
        }
    }
    get exclusiveTask() {
        var _a;
        return (_a = this._exclusiveTaskChain) === null || _a === void 0 ? void 0 : _a.task;
    }
    _setExclusiveTaskChain(itemTaskChain) {
        this._exclusiveTaskChain = itemTaskChain;
        this.dispatch(SharedChangeEvent);
    }
    /**
     * Locks exclusive ownership for a subset of items during execution of the `schedule`.
     * It is guaranteed that the `schedule` method will receive ownership of the items
     * before it starts execution and all the way up to the point when the returned promise
     * gets resolved.
     *
     * Multiple tasks can run at the same time as long as they do not share any of their items.
     * For example, task A working with assets (i1, i2) will be scheduled to run in parallel with
     * task B with assets (i3, i4).
     *
     * If multiple tasks are registered on same items, the tasks will be executed in their
     * order of scheduling. For example, task C working with assets (i1, i2) will not be allowed
     * to run at the same time with task D that needs assets (i2, i3). Task D will only schedule
     * once all the previously scheduled tasks have released ownership of i2 and i3.
     *
     * Recursivity is not supported, a task should not lock same items again while it holds ownership.
     *
     * A single item can only be part of a single exclusive task at any moment of time.
     *
     * @param name Name of the task to schedule. Useful for debugging.
     * @param items The items to lock during the execution of the task.
     * @param schedule The function to execute once the task has ownership of the items.
     * @returns A promise resolved once the task is complete. The value of the returned promise
     * is propagated from the promise returned by the `schedule` function.
     */
    static runExclusiveTask(name, items, schedule) {
        return new Promise((resolve) => {
            const task = { name, items };
            const runTask = () => resolve(schedule(task).finally(() => {
                // Make sure all the items pick up their next task
                // before we give up ownership.
                for (const item of items) {
                    // We know the item must have a task. We just finished it.
                    const next = item._exclusiveTaskChain.next;
                    // Assign ownership to the next waiting task.
                    // If tasks are still pending, then there should
                    // never be a gap of of ownership on the item,
                    // to avoid letting other tasks inject themselves
                    //in the middle. The tasks needs to execute in the order
                    // of scheduling to avoid interlacing issues.
                    item._setExclusiveTaskChain(next);
                    // Let the next task know it received the ownership now.
                    // Once the next task acquires all the locks, then it will
                    // continue with its own execution.
                    // Note that the callback uses use a timer once the execution
                    // actually starts to avoid crashing this promise chain.
                    next === null || next === void 0 ? void 0 : next.onGotOwnership();
                }
            }));
            // We don't want to schedule the task until all the items are ready to execute the task.
            let waitCount = 0;
            const onGotOwnership = () => {
                // Wait for all other task to finish first.
                if (--waitCount === 0) {
                    // Using a separate stack to avoid crashing the previous task
                    // that might have invoked the `onGotOwnership` method.
                    setTimeout(runTask, 0);
                }
            };
            for (const item of items) {
                // Each item gets its own task object. We cannot share the objects as
                // we require strict task ordering for each individual item.
                const newTask = new ItemExclusiveTaskChain(task, onGotOwnership);
                const itemTask = item._exclusiveTaskChain;
                if (!itemTask) {
                    // Assign the new task right away. The task will maintain
                    // owneship until it gets scheduled.
                    item._setExclusiveTaskChain(newTask);
                    continue;
                }
                // Increment the number of items that we are still waiting ownership for.
                ++waitCount;
                // Register the task to be executed immediately after the last task in this item's task chain.
                itemTask.registerNewTask(newTask);
            }
            if (waitCount === 0) {
                // We've got owernship of all the required assets already,
                // we can proceed with running the task.
                runTask();
            }
        });
    }
}
__decorate([
    observable
], ItemBase.prototype, "exclusiveTask", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
function compareStrings(a, b) {
    return a.localeCompare(b);
}
function compareValues(a, b) {
    if (a === b) {
        return 0;
    }
    return a < b ? -1 : 1;
}
function reverseComparator(comparator) {
    return (a, b) => -comparator(a, b);
}
class ItemOrder {
    constructor(direction, field, comparator) {
        this.direction = direction;
        this.field = field;
        this.comparator = comparator;
    }
    get hash() {
        return this.direction + ':' + this.field;
    }
    sortItems(items) {
        items.sort(this.comparator);
    }
}
function createItemOrderFactory(comparatorMap) {
    return class extends ItemOrder {
        constructor(direction, field) {
            let comparator = comparatorMap[field];
            if (direction === 'descending') {
                comparator = reverseComparator(comparator);
            }
            super(direction, field, comparator);
        }
    };
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const OrderFieldToISWYAdobeAssetOrderBy = {
    'name': 'asset_name',
    'created': 'create_date',
    'modified': 'modify_date',
    'shared': 'shared_date',
};
const OrderFieldToComparator$f = {
    name(a, b) {
        return compareStrings(a.name, b.name);
    },
    shared(a, b) {
        return compareValues(a.shared, b.shared);
    },
    created(a, b) {
        return compareValues(a.created, b.created);
    },
    modified(a, b) {
        return compareValues(a.modified, b.modified);
    }
};
class SWYAdobeAssetOrder extends createItemOrderFactory(OrderFieldToComparator$f) {
}
SWYAdobeAssetOrder.default = new SWYAdobeAssetOrder('descending', 'shared');

const USSAssetFilterName = 'USSAdobeAssetFilter';

const DEFAULT_LIMIT$1 = 50;
/**
 * AdobeAsset Loader classes should use this class to do the page loading. Using composition rather
 * than inheritance because a base USSLoader class was causing circular dependencies at runtime that
 * would cause the base USSLoader class to be undefined when the Loader class that was extending it
 * was declared.
 */
class USSLoaderHelper {
    constructor(_assetPool, _core, _loader) {
        this._assetPool = _assetPool;
        this._core = _core;
        this._loader = _loader;
    }
    async loadPage(options, start) {
        var _a, _b;
        const { order, limit = DEFAULT_LIMIT$1, filter, implicitFilter } = options;
        const latency = this._core.environment.getValue('USS_LIST_UPDATE_LATENCY');
        const mostRecentValidMutation = Date.now() - latency;
        start = start || 0;
        const adobeAssetFilter = filter;
        const { mediaTypes, excludeMediaTypes } = adobeAssetFilter;
        let uploadedFrom;
        let userId;
        if (adobeAssetFilter.name === USSAssetFilterName) {
            const ussAssetFilter = adobeAssetFilter;
            uploadedFrom = ussAssetFilter.uploadedFrom;
            userId = ussAssetFilter.userId;
        }
        const result = await this._loader._getUSSResults(Object.assign(Object.assign({ order,
            limit,
            filter,
            implicitFilter,
            start, mediaTypes: Array.from(mediaTypes !== null && mediaTypes !== void 0 ? mediaTypes : []), excludeMediaTypes: Array.from(excludeMediaTypes !== null && excludeMediaTypes !== void 0 ? excludeMediaTypes : []) }, (uploadedFrom ? { uploadedFrom } : {})), (userId ? { userId } : {})));
        const resultSet = result.result_sets[0];
        const session = this._assetPool.createLoaderUpdateSession(mostRecentValidMutation);
        const items = this._extractItems(session, resultSet.items);
        let loadNextPage;
        const total = resultSet.total_items || 0;
        const pageItemCount = resultSet.items.length;
        // [SEARCH-30755] -- May be caused by USS filtering out items due to permissions for the
        // current user, which aren't subtracted from the total. Try to ensure that the Loader paginates
        // as though the total_items is correct.
        const nextStartIndex = (pageItemCount === 0 ? start + limit : start + pageItemCount);
        if (nextStartIndex < total) {
            loadNextPage = (options) => this.loadPage(options, nextStartIndex);
        }
        const page = {
            items,
            total,
            loadNextPage,
            mostRecentValidMutation
        };
        // allow Loader to do additional internal state management (optional)
        (_b = (_a = this._loader)._postProcessPage) === null || _b === void 0 ? void 0 : _b.call(_a, options, page);
        session.commit();
        return page;
    }
    _getItemData(item) {
        let publicLink = false;
        let directCount = 0;
        const collaboration_metadata = item['collaboration_metadata'];
        if (collaboration_metadata !== undefined) {
            publicLink = collaboration_metadata.some((collaborator) => {
                return collaborator.collaborator_id === 'all';
            });
            directCount = collaboration_metadata.length;
        }
        return Object.assign(Object.assign({}, item), { 
            // Presence of collab_metadata#mine indicates that the item is shared with the user via an invite
            sharedViaInvite: !!item['collab_metadata#mine'], isShared: directCount > 0, isPublicLink: publicLink, directCollaboratorCount: directCount, 
            // No need to refresh collaborators for share flags, directCollaboratorCount, since they are computed
            // above. Not clearing the collaboratorDataIsDirty flag, because we cannot populate collaboratorArray
            // from USS.
            shareFlagsAreDirty: false });
    }
    /**
     * @package -- for use by USSLoaderHelper
     */
    _extractItems(session, items) {
        var _a;
        return this._core._adobeAssetFactory.assetsFromUSSMetadata(session, items, this._loader._getItemData || this._getItemData, (_a = this._loader) === null || _a === void 0 ? void 0 : _a._libraryContext);
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class SWYAdobeAssetLoader {
    constructor(assetPool, _core, _swyInterface) {
        this._core = _core;
        this._swyInterface = _swyInterface;
        this._ussLoaderHelper = new USSLoaderHelper(assetPool, _core, this);
    }
    supportsFilter(filter) {
        // If we don't provide a filter at all, then we cannot use the loader
        return filter !== undefined;
    }
    async loadPage(options) {
        if (!this._libraryContext) {
            this._libraryContext = await this._core.getLibraryContext();
        }
        return this._ussLoaderHelper.loadPage(options);
    }
    /**
     * @package -- for use by USSLoaderHelper
     */
    async _getUSSResults({ start, limit, mediaTypes, excludeMediaTypes, order }) {
        return this._swyInterface.getSWYAssets({
            orderBy: OrderFieldToISWYAdobeAssetOrderBy[order.field],
            orderDirection: order.direction,
            start,
            limit,
            mediaTypes,
            excludeMediaTypes,
            embedPermissions: true
        });
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
function updateAssetTypeForUSS(type) {
    // directories in uss contain mime type 'application/x-sharedcloud-collection+json'
    // adding it to the list if clients wants to search for directories
    if (type === null || type === void 0 ? void 0 : type.find((item) => { return item === 'application/vnd.adobecloud.directory+json'; })) {
        type.push('application/x-sharedcloud-collection+json');
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const DefaultSBMAssetsListingOptions = {
    orderDirection: 'descending',
    orderBy: 'shared_date',
    start: 0,
    limit: 0
};
function getDirectionValue$7(userOptions) {
    const directionFlag = userOptions.orderDirection === 'ascending' ? 'asc' : 'desc';
    return directionFlag;
}
class SBMInterface extends InterfaceBase {
    async getSharedByMeAssets(parentId, options) {
        options = Object.assign(Object.assign({}, DefaultSBMAssetsListingOptions), options);
        updateAssetTypeForUSS(options.type);
        const filters = {
            hints: {
                acp_platform_v2: true,
            },
            'fetch_fields': {
                'includes': [
                    'collab_metadata$$collaborators'
                ]
            },
            'storage:assignee$$type': ['user'],
            op_none_of: [
                {
                    'app_metadata$$shell:visibility': 'hiddenSelf',
                },
            ],
            parent_id: [parentId],
            [SearchProps.TYPE]: options.type || [],
            sort_orderby: options.orderBy,
            sort_order: getDirectionValue$7(options),
            limit: options.limit,
            start_index: options.start
        };
        const response = await this._core.hostAssetContext.ussInterface.getQueryResponse(filters);
        return response;
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const DefaultSWYAssetsListingOptions = {
    orderDirection: 'descending',
    orderBy: 'shared_date',
    start: 0,
    limit: 0,
    mediaTypes: [],
    excludeMediaTypes: [],
};
function getDirectionValue$6(userOptions) {
    const directionFlag = userOptions.orderDirection === 'ascending' ? 'asc' : 'desc';
    return directionFlag;
}
class SWYInterface extends InterfaceBase {
    async getSWYAssets(options) {
        options = Object.assign(Object.assign({}, DefaultSWYAssetsListingOptions), options);
        updateAssetTypeForUSS(options.mediaTypes);
        updateAssetTypeForUSS(options.excludeMediaTypes);
        const filters = {
            shared_via_invite: true,
            hints: {
                acp_platform_v2: true,
            },
            fetch_fields: { includes: getUSSFetchFields() },
            op_none_of: [
                {
                    op_field_exists: [
                        'app_metadata$$cc:inheritable$$cc:team$$repo:assetId',
                        // for excluding the older spaces and its children
                        // We would want to show project children, but not projects themselves in swy
                        'app_metadata$$cc:inherited$$cc:space$$repo:assetId',
                        'app_metadata$$cc:inheritable$$cc:space$$repo:assetId'
                    ],
                },
                {
                    [SearchProps.VISIBLITY]: ['hiddenSelf', 'hiddenRecursive'],
                },
                {
                    [SearchProps.CREATIVE_CLOUD_TOPLEVEL_COLLECTION_NAME]: 'appdata',
                },
                {
                    [SearchProps.TYPE]: options.excludeMediaTypes || [],
                },
                {
                    [SearchProps.STORAGE_ASSIGNEE_ID]: this._core.environment.getValue('USER_ID'),
                },
            ],
            [SearchProps.TYPE]: options.mediaTypes || [],
            sort_orderby: options.orderBy,
            sort_order: getDirectionValue$6(options),
            limit: options.limit,
            start_index: options.start,
        };
        if (options.embedPermissions) {
            filters.fetch_fields.includes.push('_embedded');
        }
        const response = await this._core.hostAssetContext.ussInterface.getQueryResponse(filters);
        return response;
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const DefaultAssetsListingSearchOptions = {
    orderDirection: 'descending',
    orderBy: 'modify_date',
    start: 0,
    limit: 0
};
function getDirectionValue$5(userOptions) {
    const directionFlag = userOptions.orderDirection === 'ascending' ? 'asc' : 'desc';
    return directionFlag;
}
class AdobeAssetSearchInterface extends InterfaceBase {
    async getFilteredAssets(options) {
        options = Object.assign(Object.assign({}, DefaultAssetsListingSearchOptions), options);
        updateAssetTypeForUSS(options.mediaTypes);
        updateAssetTypeForUSS(options.excludeMediaTypes);
        const filters = Object.assign(Object.assign(Object.assign({ hints: {
                acp_platform_v2: true,
            }, fetch_fields: { includes: getUSSFetchFields() }, [SearchProps.STORAGE_ASSIGNEE_TYPE]: ['user'], op_none_of: [
                {
                    op_field_exists: [
                        'app_metadata$$cc:inheritable$$cc:team$$repo:assetId',
                        'app_metadata$$cc:inherited$$cc:team$$repo:assetId',
                    ],
                },
                {
                    [SearchProps.VISIBLITY]: ['hiddenSelf', 'hiddenRecursive'],
                },
                {
                    [SearchProps.CREATIVE_CLOUD_TOPLEVEL_COLLECTION_NAME]: 'appdata',
                },
                {
                    [SearchProps.TYPE]: options.excludeMediaTypes || [],
                },
            ] }, (options.assetId ? Object.assign({}, options.deepSearch ? { container: { 'asset_id': [options.assetId] } }
            : { parent_id: [options.assetId] }) : {})), { [SearchProps.TYPE]: options.mediaTypes || [], sort_orderby: options.orderBy, sort_order: getDirectionValue$5(options), limit: options.limit, start_index: options.start }), (options.query ? { q: options.query } : {}));
        const response = await this._core.hostAssetContext.ussInterface.getQueryResponse(filters);
        return response;
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const DefaultAssetsListingGlobalSearchOptions = {
    orderDirection: 'descending',
    orderBy: 'modify_date',
    start: 0,
    limit: 0
};
function getDirectionValue$4(userOptions) {
    const directionFlag = userOptions.orderDirection === 'ascending' ? 'asc' : 'desc';
    return directionFlag;
}
class AdobeAssetGlobalSearchInterface extends InterfaceBase {
    /**
     * @TODO We should open this up to allow clients to configure the resctrictions
     * on this query through a filter.
     * https://jira.corp.adobe.com/browse/SAM-2542
     */
    async getFilteredAssets(options) {
        options = Object.assign(Object.assign({}, DefaultAssetsListingGlobalSearchOptions), options);
        updateAssetTypeForUSS(options.mediaTypes);
        updateAssetTypeForUSS(options.excludeMediaTypes);
        const filters = Object.assign({ hints: {
                acp_platform_v2: true,
            }, fetch_fields: { includes: getUSSFetchFields() }, [SearchProps.STORAGE_ASSIGNEE_TYPE]: ['user'], op_none_of: [
                {
                    op_field_exists: [
                        // for excluding the older spaces and its children
                        // We would want to show project and its children in global search
                        'app_metadata$$cc:inheritable$$cc:space$$repo:assetId',
                        'app_metadata$$cc:inherited$$cc:space$$repo:assetId',
                    ],
                },
                {
                    [SearchProps.VISIBLITY]: ['hiddenSelf', 'hiddenRecursive'],
                },
                {
                    [SearchProps.CREATIVE_CLOUD_TOPLEVEL_COLLECTION_NAME]: 'appdata',
                },
                {
                    [SearchProps.TYPE]: options.excludeMediaTypes || [],
                },
            ], op_or: [
                // TODO: Update _getItemData in AdobeAssetGlobalSearchLoader if we support Global
                // search loader to show other items as well. For e.g. projects
                {
                    [SearchProps.STORAGE_ASSIGNEE_ID]: this._core.environment.getValue('USER_ID')
                },
                {
                    [SearchProps.SHARED_VIA_INVITE]: true
                }
            ], [SearchProps.TYPE]: options.mediaTypes || [], sort_orderby: options.orderBy, sort_order: getDirectionValue$4(options), limit: options.limit, start_index: options.start }, (options.query ? { q: options.query } : {}));
        if (options.embedPermissions) {
            filters.fetch_fields.includes.push('_embedded');
        }
        const response = await this._core.hostAssetContext.ussInterface.getQueryResponse(filters);
        return response;
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const DefaultRecentAssetsListingOptions = {
    start: 0,
    limit: 0,
    mediaTypes: [],
    excludeMediaTypes: [],
};
class RecentInterface extends InterfaceBase {
    _getCommonFilters(options) {
        options = Object.assign(Object.assign({}, DefaultRecentAssetsListingOptions), options);
        const { uploadedFrom } = options !== null && options !== void 0 ? options : {};
        updateAssetTypeForUSS(options.mediaTypes);
        updateAssetTypeForUSS(options.excludeMediaTypes);
        // Folders should never be included in Recents listing
        options.excludeMediaTypes = Array.from(new Set(['application/x-sharedcloud-collection+json', ...(options.excludeMediaTypes || [])]));
        const filters = {
            hints: {
                acp_platform_v2: true,
            },
            fetch_fields: { includes: getUSSFetchFields() },
            sort_order: 'desc',
            limit: options.limit,
            start_index: options.start,
            [SearchProps.TYPE]: options.mediaTypes || [],
            op_none_of: [
                {
                    [SearchProps.FIELD_EXISTS]: [
                        // for excluding the older spaces and its children
                        'app_metadata$$cc:inheritable$$cc:space$$repo:assetId',
                        'app_metadata$$cc:inherited$$cc:space$$repo:assetId',
                        // for excluding only the projects
                        'app_metadata$$cc:inheritable$$cc:team$$repo:assetId',
                    ],
                },
                {
                    [SearchProps.VISIBLITY]: ['hiddenSelf', 'hiddenRecursive'],
                },
                {
                    [SearchProps.CREATIVE_CLOUD_TOPLEVEL_COLLECTION_NAME]: ['appdata'],
                },
                {
                    [SearchProps.TYPE]: options.excludeMediaTypes || [],
                },
                {
                    // For excluding legacy artifacts.
                    [SearchProps.CREATIVE_CLOUD_TOPLEVEL_COLLECTION_NAME]: 'pubs'
                },
                {
                    // For excluding more modern artifacts.
                    [SearchProps.OP_AND]: [
                        {
                            [SearchProps.CREATIVE_CLOUD_TOPLEVEL_COLLECTION_NAME]: 'cloud-content'
                        },
                        {
                            [SearchProps.FIELD_EXISTS]: ['app_metadata$$review:sourceAsset$$dc:format']
                        }
                    ],
                }
            ],
        };
        if (options.embedPermissions) {
            filters.fetch_fields.includes.push('_embedded');
        }
        if (uploadedFrom) {
            filters.op_and = [];
            filters.op_and.push({
                [SearchProps.CREATED_FROM_UPLOAD]: true
            });
        }
        if (uploadedFrom && Array.isArray(uploadedFrom)) {
            const length = filters.op_and.push({
                [SearchProps.OP_OR]: []
            });
            uploadedFrom.forEach((value) => {
                const filter = filters.op_and[length - 1];
                filter[SearchProps.OP_OR].push({
                    [SearchProps.CREATED_BY_CLIENT_ID]: value
                });
            });
        }
        if (uploadedFrom && !Array.isArray(uploadedFrom) && uploadedFrom !== 'ALL') {
            filters.op_and.push({
                [SearchProps.CREATED_BY_CLIENT_ID]: uploadedFrom
            });
        }
        return filters;
    }
    async getRecentAssets(options) {
        const filters = this._getCommonFilters(options);
        const { userId } = options !== null && options !== void 0 ? options : {};
        // should not give order given by client in this request
        // because Recent assets mean descending direction of modified date
        filters.sort_orderby = 'modify_date';
        const storageAssigneeQuery = {
            [SearchProps.STORAGE_ASSIGNEE_ID]: userId !== null && userId !== void 0 ? userId : this._core.environment.getValue('USER_ID')
        };
        // If the userId is present we want to add this to the and
        // operation of the query.
        if (userId) {
            if (!filters.op_and) {
                filters.op_and = [];
            }
            filters.op_and.push(storageAssigneeQuery);
        }
        else {
            // If the userId is present then we don't want to include the
            // SHARED_VIA_INVITED as well since that will include results that are not
            // equal to the userId provided.
            if (!filters.op_or) {
                filters.op_or = [];
            }
            filters.op_or.push(storageAssigneeQuery);
            filters.op_or.push({
                [SearchProps.SHARED_VIA_INVITE]: true
            });
        }
        const response = await this._core.hostAssetContext.ussInterface.getQueryResponse(filters);
        return response;
    }
    async getMRUAssets(options) {
        const filters = this._getCommonFilters(options);
        filters.fetch_fields.includes.push(LastAccessedOrderField);
        const { userId } = options !== null && options !== void 0 ? options : {};
        if (userId) {
            if (!filters.op_and) {
                filters.op_and = [];
            }
            filters.op_and.push({
                [SearchProps.STORAGE_ASSIGNEE_ID]: userId
            });
        }
        // should not give order given by client in this request
        // because Recent assets mean descending direction of last accessed date
        filters.sort_orderby = LastAccessedOrderField;
        filters.op_field_exists = [LastAccessedOrderField];
        const response = await this._core.hostAssetContext.ussInterface.getQueryResponse(filters);
        return response;
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class ShareableItem extends ItemBase {
    constructor() {
        super(...arguments);
        this._collaboratorsArray = [];
        this._collaboratorDataIsDirty = true;
        this._shareFlagsAreDirty = true;
        this._itemPoolDelegate = {
            onItemsUpdated: () => {
                this._setCollaboratorData();
            }
        };
    }
    get isShared() {
        // just trigger the load for collaborators info if _shareFlagsAreDirty === true
        // TODO: Add the setting of collaborator data from SAM.cpp USS-based lists as well
        // https://jira.corp.adobe.com/browse/SAM-3353
        this._shareFlagsAreDirty && this._queueCollaborators();
        return this._isShared;
    }
    get isSharedWithYouAsset() {
        // just trigger the load for collaborators info if _shareFlagsAreDirty === true
        this._shareFlagsAreDirty && this._queueCollaborators();
        return this._isSharedWithYouAsset;
    }
    get directCollaboratorCount() {
        // just trigger the load for collaborators info if not already loaded
        this._directCollaboratorCount === undefined && this._queueCollaborators();
        return this._directCollaboratorCount;
    }
    get isPublicLink() {
        // just trigger the load for collaborators info if not already loaded
        this._isPublicLink === undefined && this._queueCollaborators();
        return this._isPublicLink;
    }
    /**
     * @package, used internally by filters, tests, etc.
     */
    get _isSWYAsset_NoLazyLoad() {
        return this._isSharedWithYouAsset;
    }
    /**
     * @package, used internally by filters, tests, etc.
     */
    get _isShared_NoLazyLoad() {
        return this._isShared;
    }
    /**
     * @package, used internally by filters, tests, etc.
     */
    get _directCollaboratorCount_NoLazyLoad() {
        return this._directCollaboratorCount;
    }
    /**
     * @package, used internally by filters, tests, etc.
     */
    get _isPublicLink_NoLazyLoad() {
        return this._isPublicLink;
    }
    /**
     * @deprecated Please use isSharedWithYouAsset -- will be removed on or after 2024-10-01
     */
    get isSharedWithMe() {
        oneTimeWarning('isSharedWithMe', 'isSharedWithMe is deprecated and will be removed on or after'
            + ' 2024-10-01. Please use isSharedWithYouAsset instead.');
        return this.isSharedWithYouAsset;
    }
    /**
     * @package -- used internally to check if collaborator data has been lazy-loaded without triggering lazy-loading.
     */
    get _isCollaboratorDataLoaded() {
        return !this._collaboratorDataIsDirty;
    }
    /**
     * @package, used internally.
     */
    _queueCollaborators() {
        if (this._collaboratorDataIsDirty) {
            this._core.collaborationContext._collaboratorRequestQueue.enqueue(this);
        }
    }
    get collaboratorArray() {
        this._queueCollaborators();
        return this._collaboratorsArray;
    }
    get collaborators() {
        if (!this._collaboratorSources) {
            this._collaboratorSources = this._core.collaborationContext._getCollaboratorSources(this.id);
            this._collaboratorSources.collaboratorPool.addDelegate(this._itemPoolDelegate, 'context');
        }
        return this._collaboratorSources.collaboratorSource;
    }
    async removeCollaborator(collaborator) {
        await this._core.collaborationContext.removeCollaborator(this.id, collaborator);
    }
    async removeCurrentUserFromCollaborationList() {
        return this._core.collaborationContext._removeCurrentUserFromCollaborationList(this);
    }
    get invitedCollaborators() {
        if (!this._collaboratorSources) {
            this._collaboratorSources = this._core.collaborationContext._getCollaboratorSources(this.id);
        }
        return this._collaboratorSources.invitedCollaboratorSource;
    }
    removeInvitedCollaborator(invitedCollaborator) {
        return this._core.collaborationContext.removeInvitedCollaborator(this.id, invitedCollaborator);
    }
    _onEventListenerRemoved() {
        var _a;
        if (this.listeners(SharedCollaboratorChangedEvent.type).length === 0) {
            (_a = this._core.collaborationContext._collaboratorRequestQueue) === null || _a === void 0 ? void 0 : _a.dequeue(this);
        }
    }
    /**
     * @package Internal use only
     *
     * @param sources
     * @param collaborators
     */
    _setCollaboratorData(collaborators) {
        this._collaboratorSources = this._core.collaborationContext._getCollaboratorSources(this.id);
        const collaboratorItemPool = this._collaboratorSources.collaboratorPool;
        this._collaboratorsArray = collaborators !== null && collaborators !== void 0 ? collaborators : collaboratorItemPool.toArray();
        this._collaboratorDataIsDirty = false;
        this._shareFlagsAreDirty = false;
        // set the isShared flag. The collaborators array contains the owner of the asset
        // as well the organization that owns the asset.
        // We use the isDirect flag to determine if the asset is explicitly shared or not.
        this._isShared = this._collaboratorsArray.some((collaborator) => {
            return collaborator.isDirect;
        });
        // set the isSharedWithYouAsset flag
        this._isSharedWithYouAsset = this._collaboratorsArray.some((collaborator) => {
            return collaborator.isDirect && collaborator.isCurrentUser;
        });
        // set the isPublicLink flag
        this._isPublicLink = this._collaboratorsArray.some((collaborator) => {
            return collaborator.id === 'all';
        });
        // calculate the number of direct collaborators
        this._directCollaboratorCount = this._collaboratorsArray.filter(collaborator => collaborator.isDirect).length;
        this.dispatch(CollaboratorEvent);
    }
}
__decorate([
    observable(SharedCollaboratorChangedEvent, '_isShared')
], ShareableItem.prototype, "isShared", null);
__decorate([
    observable(SharedCollaboratorChangedEvent, '_isSharedWithYouAsset')
], ShareableItem.prototype, "isSharedWithYouAsset", null);
__decorate([
    observable(SharedCollaboratorChangedEvent, '_directCollaboratorCount')
], ShareableItem.prototype, "directCollaboratorCount", null);
__decorate([
    observable(SharedCollaboratorChangedEvent, '_isPublicLink')
], ShareableItem.prototype, "isPublicLink", null);
__decorate([
    observable(SharedCollaboratorChangedEvent, '_isSharedWithYouAsset')
], ShareableItem.prototype, "isSharedWithMe", null);
__decorate([
    observable(SharedCollaboratorChangedEvent, '_collaboratorsArray')
], ShareableItem.prototype, "collaboratorArray", null);
__decorate([
    memoize
], ShareableItem.prototype, "collaborators", null);
__decorate([
    memoize
], ShareableItem.prototype, "invitedCollaborators", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class Resource extends EventEmitter {
    constructor(_core) {
        super();
        this._core = _core;
        this._isLoaded = false;
    }
    async load() {
        await this._loadData();
        this._isLoaded = true;
        this.emit('change');
    }
    unload() {
        this._data = undefined;
        this._isLoaded = false;
        this.emit('change');
    }
    /**
     * @package -- for use in SAM.js only
     */
    _mergeData(data) {
        this._data = Object.assign(Object.assign({}, (this._data || {})), (data || {}));
        this.emit('change');
    }
    get isLoaded() {
        return this._isLoaded;
    }
    get data() {
        return this._data;
    }
    /**
     * @package -- for use in SAM.js only
     */
    _setData(data) {
        this._data = data;
        this._isLoaded = true;
        this.emit('change');
    }
}
__decorate([
    observable
], Resource.prototype, "isLoaded", null);
__decorate([
    observable
], Resource.prototype, "data", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class EmbeddedMetadataResource extends Resource {
    constructor(_core, _parent) {
        super(_core);
        this._core = _core;
        this._parent = _parent;
    }
    async _loadData() {
        await this._parent._ensureLink(RelType.EMBEDDED_METADATA);
        const assetInterface = await this._parent._getAssetInterface();
        this._data = await assetInterface.getEmbeddedMetadata();
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// import { Permission } from '../interface/AssetModel';
// import { SAMErrorCode } from '../core/SAMError';
class AppMetadataResource extends Resource {
    constructor(_core, _parent) {
        super(_core);
        this._core = _core;
        this._parent = _parent;
    }
    /*
        A note regarding the following commented code:

        While the code below is what we want, the issue is that, at the time load() is being called,
        the parent's permissions have not been set yet.

        In AdobeAsset _load(), the permissions are updated by AdobeAssetUpdater.updateLocalItem at the end,
        but AppMetadataResource/load() is being called by await Promise.all(resourcesToReload.map(resource => resource.load()));
        that happens before.

        As long as we do not have a better mechanism to handle the permissions, we have to unsatisfy this request from
        https://jira.corp.adobe.com/browse/SAM-3326:

        6. Update `AppMetadataResource._loadData()` to check the permission on the parent, and if it does not have Permission.ReadAppMetadata, throw ACCESS_DENIED error instead of making the call.
    */
    // async load() {
    //     if (this._parent.hasPermission(Permission.ReadAppMetadata)) {
    //         await super.load();
    //     } else {
    //         throw SAMErrorCode.ACCESS_DENIED;
    //     }
    // }
    async _loadData() {
        await this._parent._ensureLink(RelType.APP_METADATA);
        const assetInterface = await this._parent._getAssetInterface();
        this._data = await assetInterface.getAppMetadata();
    }
    /**
     * @package to be used internally by SAM.js
     */
    async _patch(patches) {
        await this._parent._ensureLink(RelType.APP_METADATA);
        const assetInterface = await this._parent._getAssetInterface();
        await assetInterface.patchAppMetadata(patches);
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// ManifestResource is a Resource that represents the manifest of an AdobeAsset or AssetVersion.
class ManifestResource extends Resource {
    constructor(_core, _parent) {
        super(_core);
        this._core = _core;
        this._parent = _parent;
        this._etag = '';
    }
    get etag() {
        return this._etag;
    }
    async _loadData() {
        const assetInterface = await this._parent._getAssetInterface();
        const { manifest, etag } = await assetInterface.getManifest();
        this._data = manifest;
        this._etag = etag || '';
    }
}
__decorate([
    observable
], ManifestResource.prototype, "etag", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const AssetClassChoices = ['file', 'composite', 'directory', 'entity', 'element'];
const AssetStateChoices = ['ACTIVE', 'DISCARDED', 'DISCARDED_PARENT'];

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class LocalItemUpdater {
    constructor(options) {
        this._propertyUpdater = options.propertyUpdater;
        this._changeEvent = options.changeEvent || SharedChangeEvent;
        this._changeEventsMap = options.changeEventMap;
    }
    /**
     * Update the local item with values from the remote item.
     * @param localItem
     * @param remoteItem
     */
    updateLocalItem(updateSession, localItem, remoteItem) {
        if (!updateSession.checkIfItemAllowsMutations(localItem)) {
            // The item had local changes that we don't want to overwrite because
            // the server might not yet be consistent with the change.
            return;
        }
        // If the item is not already added, add it to the list.
        updateSession.ensureItemIsAdded(localItem);
        const changedFields = this._propertyUpdater.update(localItem, remoteItem);
        const changeEvents = new Set(changedFields
            .map(changedProperty => {
            var _a, _b, _c;
            return (_c = (_b = (_a = this._changeEventsMap) === null || _a === void 0 ? void 0 : _a.get(changedProperty)) !== null && _b !== void 0 ? _b : getEventForProp(localItem, changedProperty)) !== null && _c !== void 0 ? _c : this._changeEvent;
        }));
        if (changedFields.length) {
            updateSession.queueChangedItem(localItem, [...changeEvents]);
        }
    }
}
class ItemUpdaterBase extends LocalItemUpdater {
    constructor(options) {
        super(options);
        this._localItemType = options.localItemType;
        this._getRemoteId = options.getRemoteId;
    }
    /**
     * If you have an instance of the item already (such `this` in the item class), use updateLocalItem directly.
     * If you don't, use this method to create a new instance of the item from the remote data and update it locally.
     */
    updateRemoteItem(updateSession, remoteItem) {
        const id = this._getRemoteId(remoteItem);
        if (!id) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Missing id on the remote item structure.');
        }
        let localItem = updateSession.getItemById(id);
        if (!localItem) {
            const LocalItemType = this._localItemType;
            localItem = new LocalItemType(updateSession._core, id);
        }
        // Inject remote data.
        this.updateLocalItem(updateSession, localItem, remoteItem);
        return localItem;
    }
    updateRemoteItems(updateSession, remoteItems) {
        if (!Array.isArray(remoteItems)) {
            // Invalid remote data provided.
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Invalid data received, expected list of items.');
        }
        return remoteItems.map((remoteItem) => this.updateRemoteItem(updateSession, remoteItem));
    }
}
class ItemUpdater extends ItemUpdaterBase {
    constructor(options) {
        super(Object.assign({
            getRemoteId(remoteItem) {
                // The generic types in TS are not lazy evaluated, so TS will try to resolve this based entirely
                // on the constraints added to the generic type. Maybe one day TS will be able to evaluate
                // based on the actual interface that is passed as the `RemoteType` and we could fix this model.
                // As a result, we have use `any` because TS doesn't know the type of the generic RemoteType.
                return remoteItem[options.remoteIdProperty];
            }
        }, options));
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const NotFound = Symbol('NotFound');
const formats = {
    string: {
        // In general, parse strings as strings, but special case null's as undefined, and undefined's as undefined since String() will convert them to "undefined".
        parse: (value) => (value === null) || (value === undefined) ? undefined : String(value),
        isEqual: isStrictEqual
    },
    number: {
        parse: Number,
        isEqual: isStrictEqual
    },
    date: {
        parse: (value) => {
            if (value === null) {
                return undefined;
            }
            if (value === undefined || value instanceof Date) {
                return value;
            }
            return new Date(String(value));
        },
        isEqual: (a, b) => (a === null || a === void 0 ? void 0 : a.toISOString()) === (b === null || b === void 0 ? void 0 : b.toISOString())
    },
    // Some APIs like the invitation service return dates as a number (Unix epoch time) rather than a string
    // These still map onto the JS Date object, but need to be parsed differently.
    date_as_number: {
        parse: (value) => value === undefined ? undefined : new Date(Number(value)),
        isEqual: (a, b) => (a === null || a === void 0 ? void 0 : a.toISOString()) === (b === null || b === void 0 ? void 0 : b.toISOString())
    },
    // Support actual booleans, the string 'true', or non-zero numbers.
    boolean: {
        parse: (value) => value === true
            || (typeof value === 'string' && value.toLowerCase() === 'true')
            || (typeof value === 'number' && Boolean(value)),
        isEqual: isStrictEqual
    },
    object: {
        // Make a deep copy of the object if it is defined.
        parse: (value) => value !== undefined ? deepClone(value) : undefined,
        isEqual: isDeepEqual
    },
    // Sometimes the source data is only going to be merged into the destination property, so
    // comparison only needs to happen among the top-level keys that are present in the source.
    object_subset: {
        parse: deepClone,
        isEqual(destObj, sourceObj) {
            // if both are undefined, then they are equal
            if (destObj === sourceObj) {
                return true;
            }
            // if destObj is undefined, then it can be overwritten with sourceObj
            // NOTE: sourceObj is expected to not be undefined because JSON doesn't support undefined properties,
            // and so will get skipped since the sourceObj will be NotFound.
            if (destObj === undefined) {
                return false;
            }
            const sourceKeys = Object.keys(sourceObj);
            const destObjSubset = sourceKeys.reduce((result, key) => {
                result[key] = destObj[key];
                return result;
            }, {});
            return isDeepEqual(destObjSubset, sourceObj);
        }
    }
};
const KEY_REGEXP = /([^.[]+)|(\[[^\]]+\])/g;
function getKeys(name) {
    let keys = name.match(KEY_REGEXP) || [];
    if (!keys.length) {
        throw SAMError.create(SAMErrorCode.BAD_REQUEST, `Un-parseable key ${name}`);
    }
    keys = keys.map(key => {
        // Remove brackets
        if (key[0] === '[' && key.slice(-1) === ']') {
            return key.slice(1, -1);
        }
        return key;
    });
    return keys;
}
function makeGetFn(name) {
    const keys = getKeys(name);
    return (source) => {
        let value = source;
        for (const key of keys) {
            if (!Object.prototype.hasOwnProperty.call(value, key)) {
                return NotFound;
            }
            value = value[key];
        }
        return value;
    };
}
function makeSetFn(name) {
    if (name.includes('.')) {
        // For complex properties of the Item being updated, assumptions can't be made about how to set them,
        // because some might be a class instance.
        throw SAMError.create(SAMErrorCode.BAD_REQUEST, `Cannot use dot delimiter (${name}) without a custom setter`);
    }
    return (parsedSource, dest) => {
        dest[name] = parsedSource;
    };
}
function makeMergeSetFn(name) {
    if (name.includes('.')) {
        // For complex properties of the Item being updated, assumptions can't be made about how to set them,
        // because some might be a class instance.
        throw SAMError.create(SAMErrorCode.BAD_REQUEST, `Cannot use dot delimiter (${name}) without a custom setter`);
    }
    return (parsedSource, dest) => {
        var _a;
        dest[name] = Object.assign(Object.assign({}, ((_a = dest[name]) !== null && _a !== void 0 ? _a : {})), (parsedSource !== null && parsedSource !== void 0 ? parsedSource : {}));
    };
}
function normalizeFormat(name, format, mapByKey) {
    if (!format) {
        // When we use mapByKey, the format is always an object as needs to have the actual mapByKey property.
        return mapByKey ? formats.object : formats.string;
    }
    if (typeof format === 'string') {
        if (!Object.prototype.hasOwnProperty.call(formats, format)) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, `Unexpected type format ${format} for ${name} property updater.`);
        }
        return formats[format];
    }
    return format;
}
function normalizeType(key, type) {
    // When the property type is a simple string, then the source property is a simple
    // string parameter
    if (typeof type === 'string') {
        return {
            name: key,
            format: formats.string,
            get: makeGetFn(type),
            set: makeSetFn(key),
            isArray: false
        };
    }
    const { name, format, choices, get, set, isArray, mapByKey } = type;
    const sourceName = name !== null && name !== void 0 ? name : key;
    return {
        name: sourceName,
        format: normalizeFormat(sourceName, format, mapByKey),
        choices,
        mapByKey,
        isArray: isArray !== null && isArray !== void 0 ? isArray : false,
        get: get !== null && get !== void 0 ? get : makeGetFn(name !== null && name !== void 0 ? name : key),
        set: set !== null && set !== void 0 ? set : (format === 'object_subset' ? makeMergeSetFn(key) : makeSetFn(key))
    };
}
function validateValue(dest, name, choices, value) {
    if (choices && !choices.includes(value)) {
        dest._core.environment.error(`Invalid value ${value} passed for the ${name} property. Expected one of ${choices.join(', ')}.`);
        return false;
    }
    return true;
}
function createPropertyUpdater(key, userType) {
    const type = normalizeType(key, userType);
    const { name, format, choices, get, set, isArray, mapByKey } = type;
    return (dest, source) => {
        const anyDest = dest;
        const destValGetter = makeGetFn(key);
        // If the dest property does not exist, treat it as undefined so it can be updated
        // This prevents the `a.toISOString is not a function` error for date fields
        const destVal = (destValGetter(anyDest) === NotFound ? undefined : destValGetter(anyDest));
        let value = get(source, dest);
        if (value === NotFound) {
            return false;
        }
        if (mapByKey) {
            /**
             * The input value is just an array like the following example:
             *
             * [
             *     {
             *         name: 'working_files',
             *         id: 'urn:1'
             *     },
             *     {
             *         name: 'canvases',
             *         id: 'urn:2'
             *     }
             * ]
             *
             * The result will be a Map object like the following:
             *
             *  'working_files' => {
             *      name: 'working_files',
             *      id: 'urn:1'
             *  },
             *  'canvases' => {
             *      name: 'canvases',
             *      id: 'urn:1'
             *  },
             *
             * - In this case the 'name' property becomes the key of the map. The caller can change the key using the
             * `mapByKey` variable.
             * - The actual value of the map entries is just the object itself.
             * - This value can be customized using the `format.parse` function provided by the caller.
             * - The parsed value can be validated against a predefined list of `choices` provided by the caller.
             */
            if (!Array.isArray(value)) {
                dest._core.environment.error(`Invalid value ${value} passed for the ${name} property. Expected an array.`);
                return false;
            }
            const map = new Map();
            for (const item of value) {
                if (!Object.prototype.hasOwnProperty.call(item, mapByKey)) {
                    dest._core.environment.error(`Invalid item passed for the ${name} array property. Missing ${mapByKey} property on the item of the array.`);
                    continue;
                }
                const key = item[mapByKey];
                const parsedItem = format.parse(item);
                if (!validateValue(dest, name, choices, parsedItem)) {
                    continue;
                }
                map.set(key, parsedItem);
            }
            if (isDeepEqual(destVal, map)) {
                return false;
            }
            value = map;
        }
        else if (isArray) {
            if (!Array.isArray(value)) {
                dest._core.environment.error(`Invalid value ${value} passed for the ${name} property. Expected an array.`);
                return false;
            }
            // Arrays must make a copy of their initial content to avoid mutations
            // of the original array after the value is stored inside the object.
            const array = [];
            for (const item of value) {
                const parsedItem = format.parse(item);
                if (validateValue(dest, name, choices, parsedItem)) {
                    array.push(parsedItem);
                }
            }
            if (isDeepEqual(destVal, value)) {
                return false;
            }
            value = array;
        }
        else {
            value = format.parse(value);
            if (!validateValue(dest, name, choices, value)) {
                return false;
            }
            if (format.isEqual(destVal, value)) {
                return false;
            }
        }
        set(value, anyDest);
        return true;
    };
}
class PropertyUpdater {
    constructor(map) {
        this._updaters = Object.keys(map).map((key) => {
            return {
                updater: createPropertyUpdater(key, map[key]),
                key
            };
        });
    }
    update(dest, source) {
        const propertyChanges = [];
        for (const updateObj of this._updaters) {
            const { updater, key } = updateObj;
            if (updater(dest, source)) {
                propertyChanges.push(key);
            }
        }
        return propertyChanges;
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
/**
 * Class for handling common filtering for AdobeAsset ItemSources. All implicit filters for AdobeAsset
 * should extend this class.
 */
class ImplicitAdobeAssetFilterBase {
    constructor(options) {
        this._isDiscarded = false;
        this._isPublished = false;
        this._isSharedWithYou = false;
        this._includeHiddenRecursive = false;
        this._includeHiddenRecursive = !!(options === null || options === void 0 ? void 0 : options._includeHiddenRecursive);
        this._isDiscarded = !!(options === null || options === void 0 ? void 0 : options._isDiscarded);
        this._isPublished = !!(options === null || options === void 0 ? void 0 : options._isPublished);
        this._isSharedWithYou = !!(options === null || options === void 0 ? void 0 : options._isSharedWithYou);
    }
    matches(item) {
        // If a Composite has zero size, it's probably an intermediate that is pending content, so
        // it should not be visible until the content is available. Client apps are responsible for cleaning
        // up Composites that never get content.
        if (item.isComposite && item.sizeInBytes === 0) {
            return false;
        }
        if (item.isDiscarded !== this._isDiscarded) {
            return false;
        }
        if ((item._isSWYAsset_NoLazyLoad) && item._isSWYAsset_NoLazyLoad !== this._isSharedWithYou) {
            return false;
        }
        // Do not include children of directly discarded Assets in isDiscarded
        if (this._isDiscarded && item['_assetState'] === 'DISCARDED_PARENT') {
            return false;
        }
        if (item.isPublished !== this._isPublished) {
            return false;
        }
        // ONS subscriptions may cause loading of /cloud-content-archive or /archive when monitoring discardSource list
        // items but these folders should never appear in any list.
        if (item.repoPath === '/cloud-content-archive' || item.repoPath === '/archive') {
            return false;
        }
        // For all sources of AdobeAsset, the shellVisibility field dictates whether it can be seen in UI,
        // except for when items in hidden folders (hiddenRecursive) must be included.
        return !((item.shellVisibility === 'hiddenSelf' || (item.shellVisibility === 'hiddenRecursive' && !this._includeHiddenRecursive)));
    }
    get hash() {
        return (this._isDiscarded ? 'discarded' : '')
            + (this._isPublished ? 'published' : '')
            + (this._isSharedWithYou ? 'shared' : '')
            + (this._includeHiddenRecursive ? 'hiddenRecursive' : '');
    }
    getRelationToFilter(other) {
        if (this._isDiscarded !== other._isDiscarded) {
            return 'disjoint';
        }
        if (this._isPublished !== other._isPublished) {
            return 'disjoint';
        }
        if (this._includeHiddenRecursive !== other._includeHiddenRecursive) {
            return 'disjoint';
        }
        if (this._isSharedWithYou !== other._isSharedWithYou) {
            return 'disjoint';
        }
        return 'subset';
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class AdobeAssetSearchFilter extends ImplicitAdobeAssetFilterBase {
    constructor(options) {
        super();
        this._query = options === null || options === void 0 ? void 0 : options.query;
        this._deepSearch = options === null || options === void 0 ? void 0 : options.deepSearch;
        this._asset = options === null || options === void 0 ? void 0 : options.asset;
    }
    get hash() {
        var _a;
        return [
            (_a = this._asset) === null || _a === void 0 ? void 0 : _a.id,
            super.hash,
            this.deepSearch !== undefined ? `deepSearch=${this._deepSearch}` : undefined,
            this.query ? `query=${this._query}` : undefined,
        ].filter(x => x).join(':');
    }
    get deepSearch() {
        return this._deepSearch;
    }
    get asset() {
        return this._asset;
    }
    get query() {
        return this._query;
    }
    // The matching rules USS are deceptively complex, and could change over time. Attempting to
    // replicate here could result in items being incorrectly filtered out or incorrectly included.
    // Search Lists should not try to share items from Lists with similar queries, other
    // than treating the queries as case-insensitve.
    // TODO: Lists filtered with AdobeAssetSearchFilter currently take on unwanted items:
    // https://jira.corp.adobe.com/browse/SAM-3118
    matches(item) {
        // USS does not give repository id of an item. So compare repository id
        // only when it is available on both assets
        if (this.asset && this.asset.repositoryID && item.repositoryID
            && this.asset.repositoryID !== item.repositoryID) {
            return false;
        }
        // If asset is set, check whether item should belong this search or not based on
        // deep search. If deep search is true this asset should be container for incoming
        // item else this asset should be immediate parent of incoming asset
        if (this.asset && this.asset.repoPath && item.repoPath) {
            const assetRepoPath = this.asset.repoPath;
            if (this.deepSearch) {
                if (!item.repoPath.startsWith(path.join(assetRepoPath, '/'))) {
                    return false;
                }
            }
            else if (assetRepoPath !== item.repoPath.substring(0, item.repoPath.lastIndexOf('/'))) {
                return false;
            }
        }
        return super.matches(item);
    }
    getRelationToFilter(other) {
        var _a, _b, _c, _d;
        const thisFilterRepoID = (_a = this.asset) === null || _a === void 0 ? void 0 : _a.repositoryID;
        const otherFilterRepoID = (_b = other.asset) === null || _b === void 0 ? void 0 : _b.repositoryID;
        if (thisFilterRepoID && otherFilterRepoID && thisFilterRepoID !== otherFilterRepoID) {
            return 'disjoint';
        }
        if (this.deepSearch !== other.deepSearch
            || ((_c = this.query) === null || _c === void 0 ? void 0 : _c.toLowerCase()) !== ((_d = other.query) === null || _d === void 0 ? void 0 : _d.toLowerCase())) {
            return 'disjoint';
        }
        return super.getRelationToFilter(other);
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const OrderFieldToIAdobeAssetOrderBy = {
    'name': 'repo:name',
    'created': 'storage:deviceCreateDate',
    'modified': 'storage:deviceModifyDate',
    // R-API doesn't sort on share date
    'shared': 'storage:deviceModifyDate'
};
const OrderFieldToComparator$e = {
    name(a, b) {
        return compareStrings(a.name, b.name);
    },
    created(a, b) {
        return compareValues(a.created, b.created);
    },
    modified(a, b) {
        return compareValues(a.modified, b.modified);
    },
    shared(a, b) {
        return compareValues(a.shared, b.shared);
    }
};
class AdobeAssetOrder extends createItemOrderFactory(OrderFieldToComparator$e) {
}
AdobeAssetOrder.default = new AdobeAssetOrder('descending', 'modified');

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class AdobeAssetDirectoryLoader {
    constructor(_assetPool, _asset, _core, _includePermissions, _loadLibraryData) {
        this._assetPool = _assetPool;
        this._asset = _asset;
        this._core = _core;
        this._includePermissions = _includePermissions;
        this._loadLibraryData = _loadLibraryData;
    }
    supportsFilter(filter) {
        // TODO: ?? can be undefined but can't be undefined ??
        return filter !== undefined;
    }
    async loadPage(options) {
        return await this._loadPage(options);
    }
    async _loadPage(options, start) {
        const { order, limit, filter, implicitFilter } = options;
        const mediaTypes = [...(('mediaTypes' in implicitFilter && implicitFilter['mediaTypes']) || ('mediaTypes' in filter && filter['mediaTypes'])
                || [])];
        /* Note: re-evaluate this after we have more data about the Project.libraries use case
         * We're currently calling for melville data for every page load.
         * We don't think it's a problem because it reduces code complexity and
         * we predict the standard use case to be just one page of libraries.
         * If that changes in future, we should re-evaluate this strategy.
         */
        let librariesDataPromise = undefined;
        if (this._loadLibraryData) {
            if (!this._asset.isProject) {
                throw SAMError.create(SAMErrorCode.UNSUPPORTED, `Asking for child libraries from non-project asset with id: ${this._asset.id}`);
            }
            else {
                // TODO [SHARED-902]: Move fetch to LibraryContext
                librariesDataPromise = this._core.hostAssetContext._libraryInterface.getLibrariesForProject(this._asset.id);
            }
        }
        // We will ignore the update if we had local changes sooner than this timestamp.
        const latency = this._assetPool._core.environment.getValue('ASSETS_LIST_UPDATE_LATENCY');
        const mostRecentValidMutation = Date.now() - latency;
        const embed = [{
                'resource': {
                    'reltype': RelType.APP_METADATA
                },
                'selectors': getRapiAppMetadataSelectors()
            }];
        if (this._includePermissions) {
            embed.push({
                'resource': {
                    'reltype': RelType.AC_EFFECTIVE
                }
            });
        }
        // SAM-3087: USS doesn't include PAGE link
        await this._asset._ensureLink(RelType.PAGE);
        const assetInterface = await this._asset._getAssetInterface();
        const rapiPage = await assetInterface.getChildAssets({
            orderBy: OrderFieldToIAdobeAssetOrderBy[order.field],
            orderDirection: order.direction,
            start,
            limit,
            type: mediaTypes,
            embed
        });
        // A "mutation" session is used here to track the time when a change is made to an Asset that is known to be
        // fresh (since it came from R-API). This helps avoid problems where fresh data from R-API gets overwritten by
        // stale data (e.g. from USS due to indexing lag). It also should use createLoaderMutationSession
        // (SAM-2973, SAM-3017).
        const session = this._assetPool.createLoaderMutationSession();
        if (this._asset.isProject) {
            // Update the project's children with the repo:ancestors from the parent project. This is to ensure that
            // the user's root and the project's root are included in the child's repo:ancestors. This is only an issue
            // for children of Project, so restricting this to only Projects.
            this._core.hostAssetContext._adobeAssetUpdater.updateLocalItem(session, this._asset, { 'repo:ancestors': rapiPage['repo:ancestors'] });
        }
        const libraryContext = await this._core.getLibraryContext();
        const items = this._core._adobeAssetFactory.assetsFromRepoMetadata(session, rapiPage.children.map((item) => {
            var _a;
            // Directory listings don't include repo:ancestors, but since we know the parent asset,
            // we can construct it based on the parent's repo:ancestors
            if (!item['repo:ancestors']) {
                item['repo:ancestors'] = [...((_a = this._asset.ancestorIDs) !== null && _a !== void 0 ? _a : []), this._asset.id];
            }
            return item;
        }), libraryContext);
        let librariesInPage;
        if (this._loadLibraryData) {
            librariesInPage = items.filter((item) => item.isLibrary);
            librariesInPage.forEach(library => library._setLibraryLoadState('LOADING'));
        }
        session.commit();
        if (this._loadLibraryData) {
            librariesDataPromise
                .then((librariesData) => this._updateLibrariesData(librariesInPage, librariesData));
        }
        // Check if we have another page to load.
        let loadNextPage;
        const nextStart = rapiPage._page.next;
        if (nextStart) {
            loadNextPage = (options) => this._loadPage(options, nextStart);
        }
        return {
            items,
            loadNextPage,
            mostRecentValidMutation
        };
    }
    // Libraries listing may have stale data due to USS indexing delays. Thus, this method
    // retrieves LibrarySpecificData from LibraryData, which is always safe to use as it will not overwrite RAPI data.
    // TODO [SHARED-902]: Move to LibraryContext or other library utils
    _getSafeLibraryData(libraryData) {
        const { library_urn, id, bookmark, name, ownership, roles } = libraryData;
        return { library_urn, id, bookmark, name, ownership, roles };
    }
    /**
     * Given a set of libraries that have been loading, update their data and loading state with the data from Melville.
     */
    // TODO [SHARED-902]: Move to LibraryContext
    async _updateLibrariesData(librariesToUpdate, librariesData) {
        const session = this._core.hostAssetContext.assetPool.createUpdateSession();
        const libraryContext = await this._core.getLibraryContext();
        librariesToUpdate.forEach(library => {
            const libraryData = librariesData.find(item => item.library_urn === library.id);
            if (libraryData) {
                const librarySpecificData = this._getSafeLibraryData(libraryData);
                libraryContext._libraryUpdater.updateLocalItem(session, library, librarySpecificData);
                library._setLibraryLoadState('LOADED');
            }
            else {
                // USS indexing delays may cause Melville non-public libraries listing data to be stale.
                // Thus it is possible that we have RAPI data for a new library but no matching melville data in the listing.
                // Setting the library to NOT_LOADED will trigger lazy loading if the library is being used,
                // which will cause Melville to pull the up-to-date data for this library from platform.
                library._setLibraryLoadState('NOT_LOADED');
            }
        });
        session.commit();
    }
}

/*************************************************************************
*   ADOBE CONFIDENTIAL
*
*   Copyright 2023 Adobe
*   All Rights Reserved.
*
*   NOTICE:  All information contained herein is, and remains
*   the property of Adobe and its suppliers, if any. The intellectual
*   and technical concepts contained herein are proprietary to Adobe
*   and its suppliers and are protected by all applicable intellectual
*   property laws, including trade secret and copyright laws.
*   Dissemination of this information or reproduction of this material
*   is strictly forbidden unless prior written permission is obtained
*   from Adobe.
**************************************************************************/
var USSv3OrderField;
(function (USSv3OrderField) {
    // TODO: need last access date before switching RecentsMRU to USSv3
    USSv3OrderField["Created"] = "rm.storage:deviceCreateDate";
    USSv3OrderField["Modified"] = "rm.storage:deviceModifyDate";
    USSv3OrderField["Name"] = "rm.repo:name";
})(USSv3OrderField || (USSv3OrderField = {}));
var USSv3OrderDir;
(function (USSv3OrderDir) {
    USSv3OrderDir["Ascending"] = "ASC";
    USSv3OrderDir["Descending"] = "DESC";
})(USSv3OrderDir || (USSv3OrderDir = {}));
var USSv3FilterField;
(function (USSv3FilterField) {
    USSv3FilterField["MEDIA_TYPE"] = "rm.dc:format";
    USSv3FilterField["DIRECTORY_TYPE"] = "am.project:directoryType";
    USSv3FilterField["INHERITANCE_TYPE"] = "function:missing";
})(USSv3FilterField || (USSv3FilterField = {}));
var FetchFields;
(function (FetchFields) {
    FetchFields["SharedBy"] = "cm.sharedBy";
    FetchFields["LastAccessDate"] = "am.shell:lastActivity.date";
    FetchFields["Collaborator"] = "cm.collaborator";
    FetchFields["PublicCollaborator"] = "cm.publicCollaborator";
    FetchFields["ParentProjectId"] = "am.cc:inherited.cc:team.repo:assetId";
    FetchFields["StudentAssignmentAssetId"] = "am.cc:inherited.cc:studentAssignment.repo:assetId";
    FetchFields["StudentAssignmentDirectoryId"] = "am.cc:inheritable.cc:studentAssignment.repo:assetId";
    // We need both projects and its children to show up in global search
    FetchFields["CCInherited"] = "am.cc:inherited";
    FetchFields["CCInheritable"] = "am.cc:inheritable";
    // App metadata field required for Express
    FetchFields["HzDocumentMetadata"] = "am.hz:documentMetadata";
    FetchFields["CreatedFromUpload"] = "am.cc:createdFromUpload";
    FetchFields["CreatedByClientId"] = "rm.createdByClientId";
})(FetchFields || (FetchFields = {}));

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const OrderFieldToIUSSAdobeAssetOrderBy = {
    'name': 'asset_name',
    'created': 'create_date',
    'modified': 'modify_date',
    'lastAccessed': LastAccessedOrderField
};
const OrderFieldToUSSv3OrderField = {
    'name': USSv3OrderField.Name,
    'created': USSv3OrderField.Created,
    'modified': USSv3OrderField.Modified,
    // TODO: update with correct field once V3 supports it
    'lastAccessed': USSv3OrderField.Modified
};
const OrderFieldToComparator$d = {
    name(a, b) {
        return compareStrings(a.name, b.name);
    },
    created(a, b) {
        return compareValues(a.created, b.created);
    },
    modified(a, b) {
        return compareValues(a.modified, b.modified);
    },
    lastAccessed(a, b) {
        return compareValues(a.lastAccessed, b.lastAccessed);
    }
};
class USSAdobeAssetOrder extends createItemOrderFactory(OrderFieldToComparator$d) {
}
USSAdobeAssetOrder.default = new USSAdobeAssetOrder('descending', 'modified');

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class AdobeAssetDirectorySearchLoader {
    constructor(assetPool, core, _adobeAssetSearchInterface, _asset, _implicitFilter) {
        this._adobeAssetSearchInterface = _adobeAssetSearchInterface;
        this._asset = _asset;
        this._implicitFilter = _implicitFilter;
        /**
         * @package -- only for use by USSLoaderHelper
         */
        this._getItemData = this._getItemDataWithRepoId(this._asset.repositoryID);
        this._ussLoaderHelper = new USSLoaderHelper(assetPool, core, this);
    }
    supportsFilter(filter) {
        // If we don't provide a filter at all, then we cannot use the loader
        return filter !== undefined;
    }
    async loadPage(options) {
        return this._ussLoaderHelper.loadPage(options);
    }
    /**
     * @package -- only for use by USSLoaderHelper
     */
    async _getUSSResults({ start, limit, order, mediaTypes, excludeMediaTypes }) {
        var _a;
        const query = (_a = this._implicitFilter) === null || _a === void 0 ? void 0 : _a._query;
        const deepSearch = Boolean(this._implicitFilter.deepSearch);
        return this._adobeAssetSearchInterface.getFilteredAssets({
            orderBy: OrderFieldToIUSSAdobeAssetOrderBy[order.field],
            orderDirection: order.direction,
            start,
            limit,
            mediaTypes,
            excludeMediaTypes,
            query,
            deepSearch,
            assetId: this._asset.cloudID
        });
    }
    /**
     * @package -- only for use by USSLoaderHelper
     */
    _getItemDataWithRepoId(repoID) {
        // TODO: Patching should be removed once https://jira.corp.adobe.com/browse/SRCH-1989 is fixed
        //       and re-indexing is finished, which has ETA of Feb 28 2023
        return (item) => {
            return Object.assign(Object.assign({}, item), { 'repo:repositoryId': repoID });
        };
    }
}

/*************************************************************************
*   ADOBE CONFIDENTIAL
*
*   Copyright 2022 Adobe
*   All Rights Reserved.
*
*   NOTICE:  All information contained herein is, and remains
*   the property of Adobe and its suppliers, if any. The intellectual
*   and technical concepts contained herein are proprietary to Adobe
*   and its suppliers and are protected by all applicable intellectual
*   property laws, including trade secret and copyright laws.
*   Dissemination of this information or reproduction of this material
*   is strictly forbidden unless prior written permission is obtained
*   from Adobe.
**************************************************************************/
/**
 * Returns a Promise that resolves when a List is loaded.
 * @param list to monitor for loading
 */
function waitForListLoad(list) {
    const listEventSource = list;
    return new Promise(resolve => {
        function onStatusChange() {
            if (list.status !== AssetListStatus.Loading) {
                listEventSource.off(STATE_CHANGED, onStatusChange);
                resolve();
            }
        }
        listEventSource.on(STATE_CHANGED, onStatusChange);
        // if List is already loaded, resolve now
        onStatusChange();
    });
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// TODO: change OrderDirection/Field to use SAM.cpp enums
// https://jira.corp.adobe.com/browse/SAM-1644
var OrderDirection;
(function (OrderDirection) {
    OrderDirection[OrderDirection["Ascending"] = 0] = "Ascending";
    OrderDirection[OrderDirection["Descending"] = 1] = "Descending";
})(OrderDirection || (OrderDirection = {}));
var OrderField;
(function (OrderField) {
    OrderField[OrderField["Name"] = 0] = "Name";
    OrderField[OrderField["Modified"] = 1] = "Modified";
    OrderField[OrderField["Created"] = 2] = "Created";
    OrderField[OrderField["Shared"] = 3] = "Shared";
})(OrderField || (OrderField = {}));
function candidateSort(a, b) {
    if (a.filterRelation !== b.filterRelation) {
        // We prefer to start from a list that contains as many items as possible,
        // so a list that can generate a complete `subset` must be moved to the left.
        return a.filterRelation === 'subset' ? -1 : 1;
    }
    if (a.generation !== b.generation) {
        // Newer generations must be moved to the left.
        // Return a negative number if a.generation is larger.
        return b.generation - a.generation;
    }
    // Lists with more items move left.
    // Return a negative number if a.length is larger.
    return b.length - a.length;
}
/**
 * ItemSource is the primary class and interface for interacting with lists of items
 * throughout SAM.js. It holds the only reference to the cache of lists which allows
 * SAM.js to be much more efficient when applying new filters and fetching new lists.
 *
 * @usage Instances of to this class must be cached so clients can
 * take advantage of shared cached lists.
 */
class ItemSource {
    // To ensure that we always have an implicit filter, we will use the EverythingFilter as a default
    constructor(_itemPool, _loaders, _implicitFilter = new EverythingFilter()) {
        this._itemPool = _itemPool;
        this._loaders = _loaders;
        this._implicitFilter = _implicitFilter;
        this._lists = new Map();
    }
    // To ensure that we always have an explicit filter, we will use the EverythingFilter as a default
    // eslint-disable-next-line no-dupe-class-members
    getList(order, filter = new EverythingFilter(), loadOptions) {
        if (typeof order === 'number' || typeof filter === 'number') {
            throw SAMError.create(SAMErrorCode.UNSUPPORTED, 'Using OrderDirection and OrderField is not implemented in SAM.js, and will be deprecated in SAM.cpp');
        }
        let list = this._getExistingList(order, filter);
        // If the list has been deactivated, then we cannot use the list anymore
        // and we have to create a new one.
        if (!list) {
            // If this is an internal list, then we can bootstrap the
            // contents of the list based on a similar list in the cache.
            // This way we don't have to reload the contents when changing
            // sort order.
            const mostCompleteList = this._getMostCompleteCompatibleList(filter);
            list = new ItemList(this._itemPool, this._loaders, order, this._implicitFilter, filter, mostCompleteList, loadOptions);
            // When a list fully loads, copy its items to other compatible lists
            list.on(LIST_FULLY_LOADED, () => this._onListFullyLoaded(list));
            this._lists.set(this._getListHash(order, filter, this._implicitFilter), new WeakRef$1(list));
        }
        else {
            // The client may be passing in different loadOptions than what was specified for the
            // existing List. Try to accommodate these new options without negatively impacting
            // other clients of the same List. If the client did not explicitly specify refreshTimeout, assume
            // that the existing list should not be refreshed. This is for better backward-compatibility for
            // cases where clients expected that `getList()` would not automatically load data in order to make use
            // of a cached List.
            list._updateLoadOptions(Object.assign({ refreshTimeout: -1 }, loadOptions));
        }
        if (list.status === AssetListStatus.NotStarted) {
            // Start loading.
            list.refresh();
        }
        return list;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getListWithTypes(orderDir, orderField, types) {
        throw SAMError.create(SAMErrorCode.UNSUPPORTED, 'getListWithTypes is not implemented in SAM.js and will be deprecated in SAM.cpp');
    }
    _getListHash(order, filter, implicitFilter) {
        return `${implicitFilter.hash}:${filter.hash}:${order.hash}`;
    }
    /**
     * @package -- for use in SAM.js only
     */
    _getExistingList(order, filter) {
        const existingHash = this._getListHash(order, filter, this._implicitFilter);
        const listWeakRef = this._lists.get(existingHash);
        if (listWeakRef) {
            return listWeakRef.deref();
        }
    }
    setNeedsRefresh() {
        for (const list of this._getLists()) {
            list.setNeedsRefresh();
        }
    }
    /**
     * Find an already loaded list that is a good candidate to borrow items for when creating a new list.
     *
     * For example, switching the sorting order of a list would have the same filter,
     * so it should be ok to just run that entirely on the client side.
     *
     * Another example is filtering down some items. If we already loaded a list that contained these items,
     * then we can build the filtered down list by just executing the same filter on the client side.
     *
     * @param requestedFilter Filter of the new list.
     * @returns The best candidate list that could be used as a starting point.
     */
    _getMostCompleteCompatibleList(requestedFilter) {
        var _a;
        const candidates = [];
        for (const list of this._getLists()) {
            if (list.status === AssetListStatus.ReadyFullyLoaded) {
                const filterRelation = getFiltersRelation(requestedFilter, list.filter);
                if (filterRelation !== 'disjoint') {
                    candidates.push({ list, filterRelation, generation: list.generation, length: list.items.length });
                }
            }
        }
        // We want to pick the item with the newest items, or the list that has the largest subset of items.
        candidates.sort(candidateSort);
        // The first item in the list is the best candidate.
        return (_a = candidates[0]) === null || _a === void 0 ? void 0 : _a.list;
    }
    *_getLists() {
        const lists = this._lists;
        for (const [hash, listRef] of lists.entries()) {
            const list = listRef.deref();
            if (!list) {
                // The weak ref has expired, remove it from the map.
                // Note that JS Maps are ok with deletions during iteration.
                lists.delete(hash);
                continue;
            }
            yield list;
        }
    }
    /**
     * Copies fully loaded lists to compatible lists when load completes.
     */
    _onListFullyLoaded(loadedList) {
        if (loadedList.status !== AssetListStatus.ReadyFullyLoaded) {
            return;
        }
        for (const list of this._getLists()) {
            // Other lists that are subsets of the loaded list can be inferred to be fully loaded, too.
            if (list !== loadedList
                && getFiltersRelation(list.filter, loadedList.filter) === 'subset') {
                // Don't update lists that are loading, as it could interfere with its generation tracking
                waitForListLoad(list).then(() => {
                    // Skip lists that are fresher than the loaded list
                    if (list.completedGeneration >= loadedList.completedGeneration) {
                        return;
                    }
                    list._applyListItemsFrom(loadedList);
                });
            }
        }
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class AssetVersion extends ItemBase {
    constructor() {
        super(...arguments);
        // We are intentially setting a static etag.
        // Using the HEAD version of the asset's etag will cause unintended side affects.
        this._etag = 'static-etag';
        // These fields come from the parent Asset
        this._assetId = '';
        this._assetEtag = '';
        // These fields come from the AssetVersionLoader
        this._createdAt = new Date();
        this._createdBy = '';
        this._manifest = new ManifestResource(this._core, this);
    }
    get _headAssetInterface() {
        // NOTE: this AssetInterface is tied to the parent AdobeAsset for all AssetVersion instances. Where API
        // calls are version-specific, the links need to come from this.__links; if the API call is not specific to
        // a particular version, then the links need to come from `this._assetLinks`.
        return new AssetInterface(this._core.hostAssetContext.repoInterface, this._assetId, this._assetLinks, 
        // etag only applies to the head version, and isn't needed
        // by version history R-API calls, but is required for AssetInterface.
        this._assetEtag);
    }
    async _getAssetInterface() {
        return new AssetInterface(this._core.hostAssetContext.repoInterface, this._assetId, this.__links, this._etag);
    }
    get versionId() {
        return this.id;
    }
    get created() {
        return this._createdAt;
    }
    get createdBy() {
        return this._createdBy;
    }
    get assetId() {
        return this._assetId;
    }
    get milestone() {
        return this._label ? {
            label: this._label,
            description: this._description,
        } : undefined;
    }
    get manifest() {
        return this._manifest;
    }
    get links() {
        // Make a copy to avoid the client accidentally manipulating the links
        return Object.assign({}, this.__links);
    }
    _getRenditionFetcher() {
        if (!this._renditionFetcher) {
            this._renditionFetcher = new RenditionFetcher(this._core);
            // Forward change events from the rendition fetcher
            // to this class.
            this._renditionFetcher.on('change', () => {
                this.dispatch({ type: 'change' });
            });
        }
        return this._renditionFetcher;
    }
    getRenditionURL(size, type = RenditionType.PNG) {
        const renditionFetcher = this._getRenditionFetcher();
        return renditionFetcher.getURL(size, type, this._etag, this._getAssetInterface());
    }
    renditionURLStatus(size, type) {
        const renditionFetcher = this._getRenditionFetcher();
        return renditionFetcher.renditionURLStatus(size, type, this._etag);
    }
    /**
     * See {@link AssetInterface#getCustomRendition} for usage
     */
    async getCustomRendition({ size, type, urlTemplateOptions, additionalHeaders } = {}) {
        const assetInterface = await this._getAssetInterface();
        return assetInterface.getCustomRendition({ size, type, urlTemplateOptions, additionalHeaders });
    }
    /**
     * See {@link AssetInterface#getCustomRendition} for usage
     */
    async getAllCustomRenditions({ type, urlTemplateOptions, additionalHeaders } = {}) {
        const assetInterface = await this._getAssetInterface();
        return assetInterface.getAllCustomRenditions({ type, urlTemplateOptions, additionalHeaders });
    }
    async restore() {
        const asset = await this._core.hostAssetContext.getAssetWithCloudID(this._assetId);
        const isComposite = asset.isComposite;
        if (isComposite) {
            const href = getURLFromLinks(this.__links, RelType.MANIFEST);
            await this._headAssetInterface.restoreVersionManifest(href);
            await asset._load();
        }
        else {
            const target = getURLFromLinks(asset.links, RelType.PRIMARY);
            await this.copy(target);
        }
    }
    async copy(target) {
        const asset = await this._core.hostAssetContext.getAssetWithCloudID(this._assetId);
        const isComposite = asset.isComposite;
        const source = getURLFromLinks(this.__links, RelType.PRIMARY);
        if (isComposite) {
            // Copy for composite assets not yet implemented, JIRA: https://jira.corp.adobe.com/browse/ACPCSS-1875
            throw SAMError.create(SAMErrorCode.UNSUPPORTED, 'AssetVersion.copy not implemented for composite assets');
        }
        else {
            const result = await this._headAssetInterface.runOperation({
                op: 'copy',
                id: this._assetId,
                source: { 'href': source, 'if-match': '*' },
                target: { 'href': target, 'if-match': '*' }
            });
            return result;
        }
    }
    async setMilestone(_a) {
        var _b, _c;
        var { label = ((_b = this.milestone) === null || _b === void 0 ? void 0 : _b.label) || '', description = ((_c = this.milestone) === null || _c === void 0 ? void 0 : _c.description) || '' } = _a;
        const jsonParams = [{
                op: this.milestone ? VersionPatchTypes.replace : VersionPatchTypes.add,
                path: `/children/version=${this.versionId}/milestone`,
                value: { label, description }
            }];
        await this._headAssetInterface.patchVersion(jsonParams);
        const asset = await this._core.hostAssetContext.getAssetWithCloudID(this._assetId);
        const session = asset._versionPool.createUpdateSession();
        VersionUpdater.updateLocalItem(session, this, {
            milestone: {
                label,
                description
            }
        });
        session.commit();
    }
    async removeMilestone() {
        if (!this.milestone) {
            return;
        }
        const jsonParams = [{
                op: VersionPatchTypes.remove,
                path: `/children/version=${this.versionId}/milestone`,
            }];
        await this._headAssetInterface.patchVersion(jsonParams);
        const asset = await this._core.hostAssetContext.getAssetWithCloudID(this._assetId);
        const session = asset._versionPool.createUpdateSession();
        VersionUpdater.updateLocalItem(session, this, {
            milestone: {
                label: ''
            }
        });
        session.commit();
    }
}
__decorate([
    observable
], AssetVersion.prototype, "versionId", null);
__decorate([
    observable
], AssetVersion.prototype, "created", null);
__decorate([
    observable
], AssetVersion.prototype, "createdBy", null);
__decorate([
    observable
], AssetVersion.prototype, "assetId", null);
__decorate([
    observable
], AssetVersion.prototype, "milestone", null);
__decorate([
    observable
], AssetVersion.prototype, "links", null);
__decorate([
    observable
], AssetVersion.prototype, "getRenditionURL", null);
__decorate([
    observable
], AssetVersion.prototype, "renditionURLStatus", null);
const VersionUpdater = new ItemUpdater({
    localItemType: AssetVersion,
    remoteIdProperty: 'version',
    propertyUpdater: new PropertyUpdater({
        '_createdAt': { name: 'created', format: 'date' },
        '_createdBy': 'created_by',
        '_label': 'milestone.label',
        '_description': 'milestone.description',
        '_assetId': 'assetId',
        '_assetEtag': 'assetEtag',
        '_assetLinks': { name: 'assetLinks', format: 'object' },
        '__links': { name: '_links', format: 'object' },
    })
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class AssetVersionLoader {
    constructor(_versionPool, _assetInterfacePromise) {
        this._versionPool = _versionPool;
        this._assetInterfacePromise = _assetInterfacePromise;
    }
    async loadPage(options, start) {
        return await this._loadPage(options, start);
    }
    async _loadPage(options, start) {
        var _a;
        const { limit } = options;
        // We will ignore the update if we had local changes sooner than this timestamp.
        const latency = this._versionPool._core.environment.getValue('VERSIONS_LIST_UPDATE_LATENCY');
        const mostRecentValidMutation = Date.now() - latency;
        const assetInterface = await this._assetInterfacePromise;
        const filter = options.filter;
        const version = (filter === null || filter === void 0 ? void 0 : filter.version) || undefined;
        const result = await assetInterface.getVersions({ start, limit, version });
        const session = this._versionPool.createLoaderUpdateSession(mostRecentValidMutation);
        const versions = result.children.map((version) => (Object.assign(Object.assign({}, version), { 
            // assetId, assetEtag, assetLinks are needed to construct the AssetInterface for the AssetVersion
            assetId: assetInterface.assetID, assetEtag: assetInterface.etag, assetLinks: assetInterface.links })));
        const items = VersionUpdater.updateRemoteItems(session, versions);
        session.commit();
        // Check if we have another page to load.
        let loadNextPage;
        const currentCount = result.children.length;
        // TODO: unit test to ensure this doesn't throw an exception if there are 0 pages
        const nextStart = (_a = result.children[currentCount - 1]) === null || _a === void 0 ? void 0 : _a.created;
        if (nextStart && limit && currentCount === limit) {
            loadNextPage = (options) => this._loadPage(options, nextStart);
        }
        return {
            items,
            total: result.total_children,
            loadNextPage,
            mostRecentValidMutation
        };
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class FileContentResource extends Resource {
    constructor(_core, _parent) {
        super(_core);
        this._core = _core;
        this._parent = _parent;
        this._etag = '';
    }
    get etag() {
        return this._etag;
    }
    /**
     * @package -- only for use inside SAM.js
     */
    _setContent(data, etag) {
        this._etag = etag || '';
        this._data = data;
        this.emit('change');
    }
    async _loadData() {
        await this._parent._ensureLink(RelType.PRIMARY);
        const assetInterface = await this._parent._getAssetInterface();
        const { content, etag } = await assetInterface.getContent();
        this._data = content;
        this._etag = etag || '';
    }
}
__decorate([
    observable
], FileContentResource.prototype, "etag", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class AssetVersionFilter {
    constructor(options) {
        this._isMilestone = options === null || options === void 0 ? void 0 : options.isMilestone;
        this._createdAfter = options === null || options === void 0 ? void 0 : options.createdAfter;
        this._createdBefore = options === null || options === void 0 ? void 0 : options.createdBefore;
        this._createdBy = options === null || options === void 0 ? void 0 : options.createdBy;
        this._version = options === null || options === void 0 ? void 0 : options.version;
    }
    static AssetVersionFilter(isMilestone, createdAfter, createdBefore, createdBy, version) {
        return new AssetVersionFilter({
            createdBy,
            createdAfter,
            createdBefore,
            isMilestone,
            version
        });
    }
    get isMilestone() {
        return this._isMilestone;
    }
    get createdBy() {
        return this._createdBy;
    }
    get createdAfter() {
        return this._createdAfter;
    }
    get createdBefore() {
        return this._createdBefore;
    }
    get version() {
        return this._version;
    }
    get hash() {
        var _a, _b, _c;
        const collaborator = (_a = this.createdBy) === null || _a === void 0 ? void 0 : _a.join(',');
        const createdAfter = ((_b = this.createdAfter) === null || _b === void 0 ? void 0 : _b.getTime()) || 0;
        const createdBefore = ((_c = this.createdBefore) === null || _c === void 0 ? void 0 : _c.getTime()) || 0;
        const version = this.version || undefined;
        return String(this.isMilestone) + ':'
            + collaborator + ':'
            + createdAfter + ':' + createdBefore + ':' + version;
    }
    matches(item) {
        const matchesBookmark = this.isMilestone === undefined || this.isMilestone === Boolean(item.milestone);
        const matchesCollab = this.createdBy === undefined || this.createdBy.includes(item.createdBy);
        const matchesDateRange = (!this.createdAfter || item.created >= this.createdAfter) && (!this.createdBefore || item.created <= this.createdBefore);
        const matchesVersion = this.version === undefined || this.version === item.versionId;
        return matchesBookmark && matchesCollab && matchesDateRange && matchesVersion;
    }
    _getRelationWrtMilestone(other) {
        return this.isMilestone === other.isMilestone ? 'subset' : this.isMilestone === undefined || other.isMilestone === undefined ? 'unknown' : 'disjoint';
    }
    _getRelationWrtCreatedBy(other) {
        if (!this.createdBy && !other.createdBy) {
            return 'subset';
        }
        if (this.createdBy && other.createdBy) {
            const createdBy = new Set(this.createdBy);
            if (other.createdBy.every(user => createdBy.has(user))) {
                // Every user in the other filter is also in this filter, so this filter is a subset
                return 'subset';
            }
            if (!other.createdBy.some(user => createdBy.has(user))) {
                // No user in the other filter is in this filter, so the intersection is empty
                return 'disjoint';
            }
            return 'unknown';
        }
        return this.createdBy ? 'subset' : 'unknown';
    }
    _getRelationWrtDateRange(other) {
        var _a, _b, _c, _d;
        const thisRange = { start: ((_a = this.createdAfter) === null || _a === void 0 ? void 0 : _a.getTime()) || -Infinity, end: ((_b = this.createdBefore) === null || _b === void 0 ? void 0 : _b.getTime()) || Infinity };
        const otherRange = { start: ((_c = other.createdAfter) === null || _c === void 0 ? void 0 : _c.getTime()) || -Infinity, end: ((_d = other.createdBefore) === null || _d === void 0 ? void 0 : _d.getTime()) || Infinity };
        if (otherRange.start <= thisRange.start && otherRange.end >= thisRange.end) {
            // other contains all dates, or both ranges contain all dates
            return 'subset';
        }
        if (otherRange.start > thisRange.end || otherRange.end < thisRange.start) {
            // there is no overlap
            return 'disjoint';
        }
        // the only remaining possibility is that there's some overlap
        return 'unknown';
    }
    _getRelationWrtVersion(other) {
        if (this.version === other.version) {
            return 'subset';
        }
        if (this.version === undefined || other.version === undefined) {
            return 'unknown';
        }
        return 'disjoint';
    }
    getRelationToFilter(other) {
        const relations = [
            this._getRelationWrtMilestone(other),
            this._getRelationWrtCreatedBy(other),
            this._getRelationWrtDateRange(other),
            this._getRelationWrtVersion(other)
        ];
        if (relations.includes('disjoint')) {
            return 'disjoint';
        }
        if (relations.every(relation => relation === 'subset')) {
            return 'subset';
        }
        // only remaining possibility is 'unknown'
        return 'unknown';
    }
}
AssetVersionFilter.BookmarkedVersions = new AssetVersionFilter({
    isMilestone: true
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class ImplicitDirectoryListingFilter extends ImplicitAdobeAssetFilterBase {
    constructor(options) {
        const { asset } = options, superOptions = __rest(options, ["asset"]);
        super(superOptions);
        this._asset = asset;
    }
    get asset() {
        return this._asset;
    }
    get hash() {
        return [
            this._asset.id, super.hash
        ].filter(x => x).join(':');
    }
    matches(item) {
        // Only check repositoryID if defined
        // TODO: this was to fix a USS bug that doesn't exist anymore, should it always verify repositoryID?
        if (this._asset.repositoryID && item.repositoryID
            && this._asset.repositoryID !== item.repositoryID) {
            return false;
        }
        // check item is a direct child of current asset
        const assetRepoPath = this._asset.repoPath;
        if (assetRepoPath && (assetRepoPath !== item.repoPath.substring(0, item.repoPath.lastIndexOf('/')))) {
            return false;
        }
        return super.matches(item);
    }
    getRelationToFilter(other) {
        const thisFilterRepoPath = this._asset.repoPath;
        const otherFilterRepoPath = other._asset.repoPath;
        if (thisFilterRepoPath && otherFilterRepoPath && thisFilterRepoPath !== otherFilterRepoPath) {
            // assetRepoPath are entirely different, no shared elements are expected.
            return 'disjoint';
        }
        const thisFilterRepoID = this._asset.repositoryID;
        const otherFilterRepoID = other._asset.repositoryID;
        if (thisFilterRepoID && otherFilterRepoID && thisFilterRepoID !== otherFilterRepoID) {
            return 'disjoint';
        }
        if (!thisFilterRepoPath && otherFilterRepoPath) {
            // This list isn't filtered by repoPath, but the other is. They
            // could overlap, but can't know for sure.
            return 'unknown';
        }
        return super.getRelationToFilter(other);
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
function addProjectIdToMetadata(metadata, parentId) {
    var _a, _b, _c, _d;
    metadata._embedded || (metadata._embedded = {});
    (_a = metadata._embedded)[_b = RelType.APP_METADATA] || (_a[_b] = {});
    (_c = metadata._embedded[RelType.APP_METADATA])['cc:inherited'] || (_c['cc:inherited'] = {});
    (_d = metadata._embedded[RelType.APP_METADATA]['cc:inherited'])['cc:team'] || (_d['cc:team'] = {});
    metadata._embedded[RelType.APP_METADATA]['cc:inherited']['cc:team']['repo:assetId'] = parentId;
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const BLOCK_DOWNLOAD_MIN_SIZE = 10485760;
/**
 * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01.
 * Clients should use {@link AdobeAsset.isFolder} etc. instead.
 */
var AssetType;
(function (AssetType) {
    AssetType[AssetType["Folder"] = 256] = "Folder";
    AssetType[AssetType["File"] = 512] = "File";
    AssetType[AssetType["Composite"] = 1024] = "Composite";
})(AssetType || (AssetType = {}));
// This must be kept in sync with AdobeAsset.h
var AssetSyncStatus;
(function (AssetSyncStatus) {
    AssetSyncStatus[AssetSyncStatus["InSync"] = 0] = "InSync";
    AssetSyncStatus[AssetSyncStatus["OutOfSync"] = 1] = "OutOfSync";
    AssetSyncStatus[AssetSyncStatus["SyncConflict"] = 2] = "SyncConflict";
    AssetSyncStatus[AssetSyncStatus["SyncError"] = 3] = "SyncError";
    AssetSyncStatus[AssetSyncStatus["Syncing"] = 4] = "Syncing";
})(AssetSyncStatus || (AssetSyncStatus = {}));
// This must be kept in sync with AdobeAsset.h
var AssetSyncSuspensionReason;
(function (AssetSyncSuspensionReason) {
    AssetSyncSuspensionReason[AssetSyncSuspensionReason["None"] = 0] = "None";
    AssetSyncSuspensionReason[AssetSyncSuspensionReason["CloudContentCorrupted"] = 1] = "CloudContentCorrupted";
    AssetSyncSuspensionReason[AssetSyncSuspensionReason["LocalContentCorrupted"] = 2] = "LocalContentCorrupted";
    AssetSyncSuspensionReason[AssetSyncSuspensionReason["HasTooManyComponents"] = 3] = "HasTooManyComponents";
    AssetSyncSuspensionReason[AssetSyncSuspensionReason["HasTooManyChildren"] = 4] = "HasTooManyChildren";
    AssetSyncSuspensionReason[AssetSyncSuspensionReason["MaximumSizeExceeded"] = 5] = "MaximumSizeExceeded";
    AssetSyncSuspensionReason[AssetSyncSuspensionReason["Unknown"] = 6] = "Unknown";
})(AssetSyncSuspensionReason || (AssetSyncSuspensionReason = {}));
/**
 * Response from the server for the commit request.
 **/
var CommitStatus;
(function (CommitStatus) {
    CommitStatus[CommitStatus["NoErr"] = 0] = "NoErr";
    CommitStatus[CommitStatus["Conflict"] = 1] = "Conflict";
    CommitStatus[CommitStatus["DeleteConflict"] = 2] = "DeleteConflict";
    CommitStatus[CommitStatus["ParentDeletedConflict"] = 3] = "ParentDeletedConflict";
})(CommitStatus || (CommitStatus = {}));
// It's unfortunate that SAM uses different constants for asset types than the R-API, but we need to be consistent with SAM-CPP
const assetClassToTypeMap = new Map([
    ['file', AssetType.File],
    ['composite', AssetType.Composite],
    ['directory', AssetType.Folder],
    // TODO: determine if this is needed, add to JS Bindings & AssetType
    // [ 'entity', 'Entity' ]
]);
// Do not refresh app metadata for root folders like / or /cloud-content or /cloud-content-archive etc. They do not
// show up in listings, but they can get loaded due to ONS notifications. Since the Filters prevent them from appearing
// in listings already, there's no need to load the app metadata to see that they should be hidden due to
// shell:visibility.
// TODO: also ignore root folders of ESM stores.
const APP_METADATA_REFRESH_IGNORE_REGEX = /^\/[^/]+$|^\/$/;
class AdobeAsset extends ShareableItem {
    // end [SAM-2268][SAM-2494]
    constructor(_core, id, metadata) {
        super(_core, id);
        this._core = _core;
        this._name = ''; // don't change this in subclasses
        this._etag = '';
        this._repositoryID = '';
        this._path = '';
        this._storageRegion = '';
        this._type = '';
        this._size = 0;
        this._version = '';
        this._assetClass = 'file';
        this._assetState = 'ACTIVE';
        this._created = new Date();
        this._modified = new Date();
        this._createdBy = '';
        this._modifiedBy = '';
        // Assume user has full permissions for any Asset they run across, as it's better to
        // err on allowing an action in the UI and have it fail on the service. Sources that
        // require permission checks are responsible for ensuring the permissions are set
        // appropriately.
        this._permissions = Permission.All;
        this._isPublished = false;
        this._storageAssignee = { id: '', type: 'user' };
        // Library Elements in USS discarded results are pseudo-Assets that are not in
        // /cloud-content-archive and do not have state=DISCARDED. This ensures that these items only appear in discarded
        // search results. In case Library Elements appear in other collections, we are not inferring that all Library
        // Elements are discarded just because today the discarded list is the only place they appear.
        this._isArchivedLibraryElement = false;
        // SAM should never create an AdobeAsset for a project. Therefore isProject should always be false.
        this.isProject = false;
        this.isLibrary = false;
        this.isCustomModel = false;
        /**
         * @package -- only for use in SAM.js
         */
        this._versionPool = new ItemPool(this._core, 'version');
        /**
         * Determines if __links came from resolve API (true) or a listing (false)
         * @package -- only for use in SAM.js
         */
        this._linksCameFromResolveAPI = false;
        // SAM web doesn't support caching yet
        this.isAvailableOffline = false;
        // This field isn't useful for feature-detection, so hard-coding as false, which is a sensible value for web
        this.isUserPinned = false;
        // Expecting that a data migration will occur that makes this field obsolete. If that doesn't happen, then
        // this field will need to be populated with an async call.
        /**
         * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
         */
        this.isNameDCTitle = false;
        this._embeddedMetadata = new EmbeddedMetadataResource(_core, this);
        this._appMetadata = new AppMetadataResource(_core, this);
        this._manifest = new ManifestResource(_core, this);
        this._fileContent = new FileContentResource(_core, this);
        this.__links = metadata === null || metadata === void 0 ? void 0 : metadata.links;
        this._etag = (metadata === null || metadata === void 0 ? void 0 : metadata.etag) || '';
        this._renditionFetcher = new RenditionFetcher(this._core);
        // Forward change events from the rendition fetcher
        // to this class.
        this._renditionFetcher.on('change', () => {
            this.dispatch({ type: 'change' });
        });
        // Create a weakReference to track if the
        // the object has been GC'd
        // Ensure working per https://jira.corp.adobe.com/browse/SAM-2677
        this.fr = new FinalizationRegistry(() => {
            this._finalizer();
        });
        this.fr.register(this, 'adobe-asset');
    }
    /**
     * A property that indicates wether or not a file was created from upload. If it's undefined
     * that means it has not been set previously and doesn't necessarily mean
     * that it was not created from an upload action in an application. Only a 'true' value
     * can guarantee that this file was created from an upload action.
     */
    get createdFromUpload() {
        const ccCreatedFromUploadPropertyName = 'cc:createdFromUpload';
        // If the data was provided by a USS result, return that result.
        if (this.appMetadata.data && this.appMetadata.data[ccCreatedFromUploadPropertyName]) {
            return !!this.appMetadata.data[ccCreatedFromUploadPropertyName];
        }
        return this._createdFromUpload;
    }
    /**
     * The ID of the client who created the repository asset.
     */
    get createdByClientId() {
        return this._createdByClientId;
    }
    _finalizer() {
        this._renditionFetcher.release();
    }
    /**
     * @package -- for use in SAM.js only
     */
    _loadWithRetry() {
        return this._load();
    }
    /**
     * @package -- for use in SAM.js only
     */
    async _getAssetInterface() {
        return new AssetInterface(this._core.hostAssetContext.repoInterface, this.id, this.links, this.etag);
    }
    get _resources() {
        return new Map([
            ['embeddedMetadata', this._embeddedMetadata],
            ['appMetadata', this._appMetadata],
            ['manifest', this._manifest],
            ['fileContent', this._fileContent]
        ]);
    }
    /**
     * @package -- only for use in SAM.js
     */
    async _reloadPermissions() {
        const assetMetadata = {};
        let error = undefined;
        try {
            await this._ensureLink(RelType.AC_EFFECTIVE);
            const assetInterface = await this._getAssetInterface();
            const permissions = await assetInterface.getEffectivePermissions();
            if (permissions) {
                assetMetadata.effectivePermission = permissions;
            }
        }
        catch (e) {
            error = e;
        }
        if (error) {
            if ([SAMErrorCode.NOT_FOUND, SAMErrorCode.ACCESS_DENIED].includes(error.code)) {
                assetMetadata.effectivePermission = (error.code === SAMErrorCode.ACCESS_DENIED ? Permission.Ack : Permission.None);
            }
            else {
                throw error;
            }
        }
        const hasLostAccess = [Permission.Ack, Permission.None].includes(assetMetadata.effectivePermission);
        if (hasLostAccess && this._core.hostAssetContext.assetPool.isItemDiscarded(this)) {
            // The asset has already been removed from the asset pool so we can just
            // skip any further updates. This typically means it's been permanently deleted.
            this._isShared = false;
            this._isSharedWithYouAsset = false;
            this._shareFlagsAreDirty = false;
            this._collaboratorDataIsDirty = false;
            return;
        }
        // A "mutation" session is used here to track the time when a change is made to an Asset that is known to be
        // fresh (since it came from R-API). This helps avoid problems where fresh data from R-API gets overwritten by
        // stale data (e.g. from USS due to indexing lag).
        const session = this._core.hostAssetContext.assetPool.createMutationSession();
        // We always update the asset with the new permissions even if it's
        // been permanently deleted or permissions have been removed. This
        // ensures subscribers to "change" events of AdobeAsset are notified
        // AssetLink being one example.
        if (hasLostAccess) {
            // If the user lost access, it is effectively not shared with them anymore
            assetMetadata.isShared = false;
            assetMetadata.sharedViaInvite = false;
            // Refreshing collaborators will likely fail, so clear the dirty flags to prevent the refresh.
            assetMetadata.shareFlagsAreDirty = false;
            assetMetadata.collaboratorDataIsDirty = false;
            session.queueDiscardedItem(this);
        }
        else {
            // Since we are explicitly reloading the permissions we want to mark
            // that the collaboration data is out of date which will
            // fetch new data when the ShareableItem.collaboratorList is read.
            assetMetadata.shareFlagsAreDirty = true;
            assetMetadata.collaboratorDataIsDirty = true;
        }
        this._core.hostAssetContext._adobeAssetUpdater.updateLocalItem(session, this, assetMetadata);
        session.commit();
    }
    /**
     * @param repoMetadata {RepositoryMetadata} If passed in, it will be used instead of loading from the service
     * @param appMetadata {any} If passed in, it will be used instead of loading from the service
     * @param effectivePermission {Permission} If passed in, it will be used instead of loading from the service
     *
     * @package -- only for use in SAM.js
     */
    async _load({ repoMetadata, appMetadata, effectivePermission } = {}) {
        let error = undefined;
        let assetInterface;
        let assetMetadata = {};
        try {
            await this._ensureLink(RelType.REPO_METADATA);
            assetInterface = await this._getAssetInterface();
            // Do these requests in parallel for better performance
            const resolutions = await Promise.allSettled([
                repoMetadata ? Promise.resolve(repoMetadata) : assetInterface.getRepoMetadata(),
                effectivePermission ? Promise.resolve(effectivePermission) : assetInterface.getEffectivePermissions()
            ]);
            repoMetadata = resolutions[0].status === 'fulfilled' ? resolutions[0].value : undefined;
            const permissions = resolutions[1].status === 'fulfilled' ? resolutions[1].value : undefined;
            error = resolutions.map(resolution => resolution.status === 'rejected' && resolution.reason).find(x => x);
            this._linksCameFromResolveAPI = repoMetadata !== undefined;
            // if repoMetadata failed to load, the permissions should still be set
            assetMetadata = repoMetadata || {};
            if (permissions) {
                assetMetadata.effectivePermission = permissions;
            }
        }
        catch (e) {
            error = e;
        }
        // Check for the asset missing (404) or having restricted access (403)
        if (error) {
            if ([SAMErrorCode.NOT_FOUND, SAMErrorCode.ACCESS_DENIED].includes(error.code)) {
                assetMetadata.effectivePermission = (error.code === SAMErrorCode.ACCESS_DENIED ? Permission.Ack : Permission.None);
            }
            else {
                throw error;
            }
        }
        else {
            // Refresh any resources that are already loaded
            const resourcesToReload = [...this._resources.values()].filter(resource => resource.isLoaded);
            // If shellVisibility is undefined, then app metadata must be loaded to ensures hidden Assets do not
            // appear in lists due to ONS notifications.
            if (this.shellVisibility === undefined
                // Root folders don't show up in lists anyway, so no need to check shell:visibility
                && !APP_METADATA_REFRESH_IGNORE_REGEX.test(assetMetadata['repo:path'])
                // Already scheduled to reload
                && !resourcesToReload.includes(this._appMetadata)
                // Do we have the permission
                && assetMetadata.effectivePermission && ((assetMetadata.effectivePermission & Permission.ReadAppMetadata) === Permission.ReadAppMetadata)) {
                resourcesToReload.push(this._appMetadata);
            }
            // If the app metadata is passed in, use it instead of loading it again
            if (appMetadata) {
                // setting the data also sets isLoaded to true
                this._appMetadata._setData(appMetadata);
                // Remove the app metadata from the list of resources to reload
                const index = resourcesToReload.indexOf(this._appMetadata);
                if (index !== -1) {
                    resourcesToReload.splice(index, 1);
                }
            }
            if (resourcesToReload.length) {
                await Promise.all(resourcesToReload.map(resource => resource.load()));
            }
        }
        const hasLostAccess = [Permission.Ack, Permission.None].includes(assetMetadata.effectivePermission);
        if (hasLostAccess && this._core.hostAssetContext.assetPool.isItemDiscarded(this)) {
            // The asset has already been removed from the asset pool so we can just
            // skip any further updates. This typically means it's been permanently deleted.
            this._isShared = false;
            this._isSharedWithYouAsset = false;
            this._shareFlagsAreDirty = false;
            this._collaboratorDataIsDirty = false;
            this.dispatch(CollaboratorEvent);
            return;
        }
        const isFolder = assetMetadata['repo:assetClass'] === 'directory' || this.isFolder;
        // get the last access date by the user to refresh the lastAccessed property
        const lastAccessed = isFolder || hasLostAccess ? undefined : await assetInterface.getLastAccessed();
        // A "mutation" session is used here to track the time when a change is made to an Asset that is known to be
        // fresh (since it came from R-API). This helps avoid problems where fresh data from R-API gets overwritten by
        // stale data (e.g. from USS due to indexing lag).
        const session = this._core.hostAssetContext.assetPool.createMutationSession();
        if (isFolder && assetMetadata['repo:path'] && assetMetadata['repo:path'] !== this.repoPath) {
            // If it's a directory, and its path changed, we also need to update the repo:path of all its children!
            updateCachedDescendents({
                assetPool: this._core.hostAssetContext.assetPool,
                asset: this,
                session,
                operation: { type: 'rename', newPath: assetMetadata['repo:path'] }
            });
        }
        // We always update the asset with the new permissions even if it's
        // been permanently deleted or permissions have been removed. This
        // ensures subscribers to "change" events of AdobeAsset are notified
        // AssetLink being one example.
        this._core.hostAssetContext._adobeAssetUpdater.updateLocalItem(session, this, Object.assign(Object.assign({}, assetMetadata), { lastAccessed }));
        if (hasLostAccess) {
            this._isShared = false;
            this._isSharedWithYouAsset = false;
            this._shareFlagsAreDirty = false;
            this._collaboratorDataIsDirty = false;
            session.queueDiscardedItem(this);
        }
        session.commit();
    }
    get versions() {
        const loader = new AssetVersionLoader(this._versionPool, this._ensureLink(RelType.PAGE).then(() => this._getAssetInterface()));
        return new ItemSource(this._versionPool, [loader], new AssetVersionFilter());
    }
    async getCommentContext() {
        return this._core.hostAssetContext.getCommentContext(this.id);
    }
    /**
     * Note:  Clients should avoid using this field and instead rely on the "is*" properties, like `isFolder`,
     * `isFile`, `isComposite`, etc.
     *
     * @deprecated - will be removed on or after 2024-10-01
     */
    get assetType() {
        oneTimeWarning('AdobeAsset.assetType', 'AdobeAsset.assetType is deprecated and will be removed on or after 2024-10-01. Please use isFolder,'
            + ' isFile, isComposite, etc. instead');
        return assetClassToTypeMap.get(this._assetClass);
    }
    get isFolder() {
        return this._assetClass === 'directory';
    }
    get isAssignment() {
        // if appMetadata isn't loaded, it's not yet known
        return this.appMetadata.data ? this.appMetadata.data['project:directoryType'] === 'assignment' : undefined;
    }
    get isFile() {
        return this._assetClass === 'file';
    }
    get isComposite() {
        return this._assetClass === 'composite';
    }
    get isLibraryElement() {
        return this._assetClass === 'element';
    }
    get imageLength() {
        return this._imageLength;
    }
    get imageWidth() {
        return this._imageWidth;
    }
    // Similar to Project, we rely on app metadata to determine if an asset is a child of an assignment.
    // If app metadata is not loaded, then we don't know if it has a parent assignment id and it is undefined.
    get parentAssignmentID() {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.appMetadata.data) === null || _a === void 0 ? void 0 : _a['cc:inherited']) === null || _b === void 0 ? void 0 : _b['cc:assignment']) === null || _c === void 0 ? void 0 : _c['repo:assetId'];
    }
    // Similar to isChildOfProject
    get isChildOfAssignment() {
        return !!this.parentAssignmentID;
    }
    get parentClassroomId() {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.appMetadata.data) === null || _a === void 0 ? void 0 : _a['cc:inherited']) === null || _b === void 0 ? void 0 : _b['cc:classroom']) === null || _c === void 0 ? void 0 : _c['repo:assetId'];
    }
    get isChildOfClassroom() {
        return !!this.parentClassroomId;
    }
    get parentAssignmentMaterialsId() {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.appMetadata.data) === null || _a === void 0 ? void 0 : _a['cc:inherited']) === null || _b === void 0 ? void 0 : _b['cc:assignmentMaterials']) === null || _c === void 0 ? void 0 : _c['repo:assetId'];
    }
    get isChildOfAssignmentMaterials() {
        return !!this.parentAssignmentMaterialsId;
    }
    get parentStudentAssignmentId() {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.appMetadata.data) === null || _a === void 0 ? void 0 : _a['cc:inherited']) === null || _b === void 0 ? void 0 : _b['cc:studentAssignment']) === null || _c === void 0 ? void 0 : _c['repo:assetId'];
    }
    get isChildOfStudentAssignment() {
        return !!this.parentStudentAssignmentId;
    }
    /**
     * Returns true if the asset path is inside of a project, otherwise undefined.
     */
    _isPathInsideProject() {
        // It is assumed that the folder and filenames have already been validated
        // as these regular expressions allow for invalid characters.
        const projectChildPatterns = [
            '/projects/[^/]+/[^/]+',
            '/content/assets/projects/[^/]+/[^/]+',
            '/users/[^/]+/projects/[^/]+/[^/]+'
        ];
        return projectChildPatterns.some(pattern => new RegExp(pattern).test(this._path));
    }
    // We rely on app metadata to determine if an asset is a child of a project.
    // If app metadata is not loaded, then we don't know if it has a parent project id and it is undefined.
    get parentProjectID() {
        var _a, _b, _c;
        // EBroker handling will ensure that all descendants have the following, corresponding, application metadata property
        const parentProjectID = (_c = (_b = (_a = this.appMetadata.data) === null || _a === void 0 ? void 0 : _a['cc:inherited']) === null || _b === void 0 ? void 0 : _b['cc:team']) === null || _c === void 0 ? void 0 : _c['repo:assetId'];
        // [SAM-3590] If the parent project ID is not found in the app metadata or the ancestorIDs array,
        // and it's inside a project, call the Context API to obtain the parent projet ID.
        const parentIsMissingFromAncestorIDs = this._ancestorIDs && !this._ancestorIDs.includes(parentProjectID);
        if ((!parentProjectID || parentIsMissingFromAncestorIDs) && this._isPathInsideProject()) {
            this._fixParentProjectID();
        }
        return parentProjectID;
    }
    /**
     * Fix the parentProjectID in appMetadata and then notify clients.
     */
    async _fixParentProjectID() {
        const response = await this._core.projectContext._callContextAPI(this.cloudID, this.repositoryID);
        // Fix app metadata from response.
        const parentProjectID = response['repo:assetId'];
        if (parentProjectID) {
            // Update the app metadata with the parent project ID.
            this.appMetadata._mergeData({ 'cc:inherited': { 'cc:team': { 'repo:assetId': parentProjectID } } });
        }
    }
    get isChildOfProject() {
        return !!this.parentProjectID;
    }
    get isArtifact() {
        // if appMetadata isn't loaded, it's not yet known
        if (!this.appMetadata.data) {
            return undefined;
        }
        // if `review:sourceAsset` is present, then it's an Artifact
        return !!this.appMetadata.data['review:sourceAsset'];
    }
    // NOTE: isReviewLink includes legacy items that are not Artifacts
    get isReviewLink() {
        // legacy reviews were published in /pubs but might not have review:sourceAsset app metadata
        if (this.repoPath.startsWith('/pubs/')) {
            return true;
        }
        return this.isArtifact;
    }
    /**
    * Returns the media type of the source document used to generate this Review Link. If not a Review Link, or no
    * source document can be identified, it will return ''
    * Hard coding these strings was a conscious decision to avoid enums and the harndeing any logic around mediaTypes because they are sill
    * evolving. The ideal solution is for no app specific logic here, we want to simply reference dc:format for all artifacts.
    */
    get reviewLinkSourceType() {
        var _a;
        if (!this.isReviewLink) {
            return '';
        }
        // current S4R-compliant artifacts store the source type in app metadata
        const dcFormat = (_a = this.appMetadata.data) === null || _a === void 0 ? void 0 : _a[AppMetadataField.REVIEW_SOURCE_ASSET]['dc:format']; // dcFormat equates to mediaType
        if (dcFormat) {
            return dcFormat;
        }
        // XD "prototype" and "spec" have media type 'application/vnd.adobe.xd.cloudproject+dcx' -- return this.mediaType
        // There's nothing that dictates what media type Artifacts must be, the source app can pick. For Xd, they
        // chose another Xd-specific format. For S4R, most clients that implemented it chose PDF, so we are only
        // checking for the XD format here and not any others.
        if (this.mediaType === 'application/vnd.adobe.xd.cloudproject+dcx') {
            return this.mediaType;
        }
        // legacy Aero links are in /pubs/aero/v5/ -- return Aero media type
        if (this.repoPath.startsWith('/pubs/aero/v5/')) {
            return 'application/vnd.adobe.real+dcx';
        }
        // Legacy InDesign artifacts may be in /pubs/id_review
        if (this.repoPath.startsWith('/pubs/id_review/')) {
            return '';
        }
        // legacy 3d scenes are in /pubs/<review creator ID> (to be confirmed) -- return media type (needs research)
        if (this.repoPath.startsWith('/pubs/')) {
            return '3d/vnd.adobe.asd+dcx';
        }
        return '';
    }
    // NOTE: this should only ever be the repo:name of the Asset. For user-friendly names, use `displayName`
    get name() {
        // NOTE: includes file extension
        return removeArchiveGuidFromName(this._name);
    }
    // Subclasses should override if the user-facing name is something other than repo:name. Do not return `this.name`
    // (the repo:name) as displayName, as in some cases, that might be a guid that the client may want to exclude, in
    // which case the client would have to write a regex to hide guids...but if a user chooses a guid as a file name,
    // that'd cause the user's legit file name to be hidden. Keeping these concepts distinct will ensure clients can
    // have full control over their UI.
    get displayName() {
        return undefined;
    }
    // NOTE: when _appMetadata changes, it won't trigger any observers of shellVisibility to update. But since
    // shellVisibility shouldn't change after initial load (from listing, or getAssetWith*()), this isn't currently
    // necessary. If it becomes necessary, we'll need to have AdobeAsset fire a change even when _appMetadata changes.
    get shellVisibility() {
        // If _appMetadata.data is undefined, then it has not been loaded from listings or
        // by _appMetadata.load(), so shell visibility is unknown.
        if (this._appMetadata.data === undefined) {
            return undefined;
        }
        // '' means that the shell:visibility is known to be visible
        return this._appMetadata.data['shell:visibility'] || '';
    }
    get ancestorIDs() {
        return this._ancestorIDs;
    }
    get storageAssignee() {
        return this._storageAssignee;
    }
    get extension() {
        // To match SAM C++, extension does not include the "."
        // Do not return extension if file name starts with '.'
        if (this.name.lastIndexOf('.') > 0) {
            return this.name.substring(this.name.lastIndexOf('.') + 1);
        }
        else {
            return '';
        }
    }
    get mediaType() {
        return this._type;
    }
    get cloudID() {
        return this.id;
    }
    get repositoryID() {
        return this._repositoryID;
    }
    get repositoryName() {
        if (this._repositoryName === undefined) {
            this._populateRepositoryName();
        }
        return this._repositoryName;
    }
    get repoPath() {
        // TODO: Switch to new repo:relativePath once implemented by platform
        return this._path;
    }
    get storageRegion() {
        return this._storageRegion;
    }
    get created() {
        return this._created;
    }
    get modified() {
        return this._modified;
    }
    // returns the last access date for the user.
    // returns undefined if last access date is not set or the asset is not
    // populated via USS.
    get lastAccessed() {
        return this._lastAccessed;
    }
    // updates the user's last access date.
    // Does not apply to folders
    // This internally makes a call to PUPA service to patch the last access date.
    async updateLastAccessed() {
        if (this.isFolder) {
            oneTimeWarning('AdobeAsset.updateLastAccessed', 'updateLastAccessed API is not allowed on folders');
            return;
        }
        // We probably need to make sure that peruser metadata link is present.
        // It may not be present if this AdobeAsset is created as a result of
        // USS call.
        await this._ensureLink(RelType.PERUSER_METADATA);
        const assetInterface = await this._getAssetInterface();
        const accessDate = new Date();
        await assetInterface.patchLastAccessed(accessDate);
        const session = this._core.hostAssetContext.assetPool.createUpdateSession();
        this._core.hostAssetContext._adobeAssetUpdater.updateLocalItem(session, this, { lastAccessed: accessDate });
        session.commit();
    }
    get shared() {
        return this._shared;
    }
    get sizeInBytes() {
        return this._size;
    }
    get isDiscarded() {
        return this._isArchivedLibraryElement
            || getRestorePath(this.repoPath) !== undefined
            || ['DISCARDED', 'DISCARDED_PARENT'].includes(this._assetState);
    }
    get assetRefString() {
        return JSON.stringify({
            cloud_id: this.cloudID,
            path: this.repoPath,
            display_name: this.name,
            repository_id: this.repositoryID
        });
    }
    get isPublished() {
        return this._isPublished;
    }
    get children() {
        if (!this.isFolder) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Children list can be fetched only for folder');
        }
        // https://jira.corp.adobe.com/browse/SAM-3206
        // We will embed permissions for projects and children of projects
        const embedPermissions = this.isProject || this.isChildOfProject;
        const loader = new AdobeAssetDirectoryLoader(this._core.hostAssetContext.assetPool, this, this._core, embedPermissions);
        // Children of Project folders can be SWY items
        const implicitFilter = new ImplicitDirectoryListingFilter({ asset: this, _isSharedWithYou: true });
        return new ItemSource(this._core.hostAssetContext.assetPool, [loader], implicitFilter);
    }
    searchChildren(opts) {
        if (!this.isFolder) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Children can be searched only in the folder');
        }
        const { deepSearch, query } = opts;
        const implicitFilter = new AdobeAssetSearchFilter({ asset: this, deepSearch, query });
        const loader = new AdobeAssetDirectorySearchLoader(this._core.hostAssetContext.assetPool, this._core, this._core.hostAssetContext._assetSearchInterface, this, implicitFilter);
        return new ItemSource(this._core.hostAssetContext.assetPool, [loader], implicitFilter);
    }
    /**
     * Discard the asset - this changes its repo:state to DISCARDED, but it's still present as an
     * asset, and can later be restored, or permanently deleted.
     */
    async discard() {
        if (this.isDiscarded) {
            throw SAMError.create(SAMErrorCode.INVALID_OPERATION, 'Asset is already discarded');
        }
        const assetInterface = await this._getAssetInterface();
        const repoMetadata = await assetInterface.discard();
        // Artifacts are from Share For Review, for assets where you can create a Review artifact via the Share For Review workflow
        if (this._hasArtifacts) {
            // NOTE: this is done in the background and if it fails will not count as a failure
            // for discard. This also means when `discard()` resolves, the output folder may not
            // be deleted yet.
            this._core.hostAssetContext._deleteAssetOutputsFolder(this);
        }
        if (repoMetadata) {
            // Use response metadata to reflect the new state of the Asset
            const session = this._core.hostAssetContext.assetPool.createUpdateSession();
            this._core.hostAssetContext._adobeAssetUpdater.updateLocalItem(session, this, repoMetadata);
            if (this.isFolder) {
                // If it's a directory, we also need to update the repo:state of all its children.
                updateCachedDescendents({
                    assetPool: this._core.hostAssetContext.assetPool,
                    asset: this,
                    session,
                    operation: { type: 'discard' }
                });
            }
            session.commit();
        }
    }
    /**
     * Simple getter to see if the flag for the outputs folder has been set
     * requires calling AssetContext.assetsHaveArtifacts() before reading this value.
     */
    get hasArtifacts() {
        return this._hasArtifacts;
    }
    /**
     * @package -- for use in SAM.js only
     */
    _clearRepositoryName() {
        this._repositoryName = undefined;
        this.emit('change');
    }
    async _populateRepositoryName() {
        if (this._repositoryName) {
            return;
        }
        const repositoryName = await this._core.hostAssetContext._repositoryNameFromID(this._repositoryID);
        if (repositoryName) {
            const session = this._core.hostAssetContext.assetPool.createUpdateSession();
            this._core.hostAssetContext._adobeAssetUpdater.updateLocalItem(session, this, { repositoryName });
            session.commit();
        }
    }
    /**
     * create a Folder asset - this create a empty Folder inside this Folder asset
     * returns the newly created folder asset
     */
    async createFolder(name, retryOnConflict = false, counter = 0) {
        if (!this.isFolder) {
            throw SAMError.create(SAMErrorCode.INVALID_OPERATION, 'Asset is not Folder');
        }
        if (!validatePlatformRepoName(name)) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Invalid name provided for asset create folder operation.');
        }
        try {
            const relativePath = name;
            await this._ensureLink(RelType.CREATE);
            const assetInterface = await this._getAssetInterface();
            const newFolderMetaData = await assetInterface.createFolder(relativePath);
            if (newFolderMetaData) {
                if (this.isProject || this.isChildOfProject) {
                    addProjectIdToMetadata(newFolderMetaData, this.isChildOfProject ? this.parentProjectID : this.id);
                }
                return this._core.hostAssetContext._createAssetFromMetadata(newFolderMetaData);
            }
            return await this._core.hostAssetContext.getAssetWithRepoPath(`${this.repoPath}/${relativePath}`, this.repositoryID);
        }
        catch (error) {
            if (error.code === SAMErrorCode.CONFLICT && retryOnConflict) {
                const formatFileResult = formatFileName(name, this._core.environment.getValue('ASSET_CONFLICT_NAME_FORMAT'), counter + 1);
                return this.createFolder(formatFileResult.newName, retryOnConflict, formatFileResult.countUsed);
            }
            throw error;
        }
    }
    /**
     * A small private helper to update the createdFromUpload data and PUPA.
     * @param uploadedAsset The asset that was newly created.
     * @package Used only internally.
     */
    async _setCreatedFromUploadData() {
        await this.patchAppMetadata([{ op: 'add', path: '/cc:createdFromUpload', value: true }]);
        // Update last accessed to make it easier for clients to sort/filter uploaded files.
        await this.updateLastAccessed();
    }
    /**
     * Imports content as a new file inside of the current folder
     *
     * Returns the newly created asset.
     */
    async importFile(file, name, mediaType, retryOnConflict = false, counter = 0) {
        if (!this.isFolder) {
            throw SAMError.create(SAMErrorCode.INVALID_OPERATION, 'Asset is not Folder');
        }
        if (typeof file === 'string') {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Unsupported string for file to import');
        }
        if (!validatePlatformRepoName(name)) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Invalid name provided for file import operation.');
        }
        try {
            const relativePath = name;
            await this._ensureLink(RelType.CREATE);
            await this._ensureLink(RelType.BLOCK_INITIALIZE);
            const assetInterface = await this._getAssetInterface();
            const newFileMetaData = await assetInterface.importFile(file, relativePath, mediaType);
            if (newFileMetaData) {
                if (this.isProject || this.isChildOfProject) {
                    addProjectIdToMetadata(newFileMetaData, this.isChildOfProject ? this.parentProjectID : this.id);
                }
                const assetFromMetadata = this._core.hostAssetContext._createAssetFromMetadata(newFileMetaData);
                await assetFromMetadata._setCreatedFromUploadData();
                return assetFromMetadata;
            }
            const asset = await this._core.hostAssetContext.getAssetWithRepoPath(`${this.repoPath}/${relativePath}`, this.repositoryID);
            await asset._setCreatedFromUploadData();
            return asset;
        }
        catch (error) {
            if (error.code === SAMErrorCode.CONFLICT && retryOnConflict) {
                const formatFileResult = formatFileName(name, this._core.environment.getValue('ASSET_CONFLICT_NAME_FORMAT'), counter + 1);
                return this.importFile(file, formatFileResult.newName, mediaType, retryOnConflict, counter + 1);
            }
            throw error;
        }
    }
    async getDownloadURL() {
        await this._ensureLink(RelType.PRIMARY);
        const assetInterface = await this._getAssetInterface();
        if (this._size < BLOCK_DOWNLOAD_MIN_SIZE) {
            const blob = await assetInterface.getContent();
            if (!blob.content) {
                throw SAMError.create(SAMErrorCode.INTERNAL_ERROR, 'Cannot fetch blob');
            }
            // For now we are leaking the memory allocated for the blob url.
            // Filed this ticket to address: https://jira.corp.adobe.com/browse/SAM-2996
            const blobUrl = URL.createObjectURL(blob.content);
            if (!(blobUrl === null || blobUrl === void 0 ? void 0 : blobUrl.length)) {
                throw SAMError.create(SAMErrorCode.INTERNAL_ERROR, 'Cannot convert blob to URL');
            }
            return blobUrl;
        }
        else {
            // If the download link is missing, we need to fetch the download link from the service (SAM-3396). This is a no-op if the link is already present.
            await this._ensureLink(RelType.DOWNLOAD);
            const blockDownloadServiceURL = getURLFromLinks(this.__links, RelType.DOWNLOAD);
            return await assetInterface.getBlockDownloadURL(blockDownloadServiceURL);
        }
    }
    /**
     * Contents of the file as a binary blob.
     */
    get fileContent() {
        if (!this.isFile) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Cannot get content of an asset that is not a file');
        }
        return this._fileContent;
    }
    /**
     * Commit the file
     *
     * content are the changes that needs to be committed.
     * localETag is the ETag associated with the file on the server
     * Returns NoErr if successfully committed. Returns Conflict if committing the asset causes a conflict.
     */
    async commitFileChanges(file, localETag) {
        if (!this.isFile) {
            throw SAMError.create(SAMErrorCode.INVALID_OPERATION, 'Asset is not File');
        }
        try {
            await this._ensureLink(RelType.PRIMARY);
            const assetInterface = await this._getAssetInterface();
            const newFileDetails = await assetInterface.commitFileChanges(file, localETag);
            if (this._fileContent.isLoaded) {
                this._fileContent._setContent(file, newFileDetails.eTag);
            }
            return { status: CommitStatus.NoErr, committedEtag: newFileDetails.eTag };
        }
        catch (error) {
            if (error instanceof SAMError) {
                if (error.code === SAMErrorCode.CONFLICT) {
                    return { status: CommitStatus.Conflict };
                }
                if (error.code === SAMErrorCode.NOT_FOUND) {
                    // The file has been deleted. We need to create a new asset and then provide that to the user.
                    const parentRepoPath = this.repoPath.substring(0, this.repoPath.lastIndexOf('/'));
                    let parentDeleted = false;
                    const parentAsset = await this._core.hostAssetContext
                        .getAssetWithRepoPath(parentRepoPath, this.repositoryID)
                        .catch(error => {
                        const samError = error;
                        if (SAMErrorCode.NOT_FOUND === samError.code) {
                            parentDeleted = true;
                            return undefined;
                        }
                        throw error;
                    });
                    if (parentDeleted) {
                        return { status: CommitStatus.ParentDeletedConflict };
                    }
                    const newFile = await (parentAsset === null || parentAsset === void 0 ? void 0 : parentAsset.importFile(file, this.name));
                    return { status: CommitStatus.DeleteConflict, newAsset: newFile };
                }
            }
            throw error;
        }
    }
    /**
     * Restore the asset - can be given an optional name to restore to, otherwise it
     * restores to the previous repo:name of the asset.
     * @param {string} name - name to use when restoring, defaults to current name
     */
    async restore(name, _counter) {
        var _a;
        if (!this.isDiscarded) {
            throw SAMError.create(SAMErrorCode.INVALID_OPERATION, 'Cannot restore an asset that is not discarded');
        }
        if (this.isLibraryElement) {
            // If asset is a Library Element, we need to use the Melville service to restore
            await this._core.hostAssetContext.restoreLibraryElement(this.cloudID);
            // We create fake AdobeAssets for Library Elements returned from the DiscardedSource so they can appear
            // in the discarded list with other Assets. They are not monitored by ONS and do not otherwise exist as Assets in SAM.
            // So when we restore them, rather than updating the state, we remove it from the assetPool and thereby the discardedSource lists.
            const session = this._core.hostAssetContext.assetPool.createUpdateSession();
            session.queueDiscardedItem(this);
            session.commit();
        }
        else {
            let repoMetadata;
            const restoreLocation = getRestorePath(this.repoPath, name);
            if (restoreLocation) {
                // If archived rather than discarded, we need to move to the restore location
                repoMetadata = await this._move(restoreLocation, true);
            }
            else {
                const assetInterface = await this._getAssetInterface();
                try {
                    repoMetadata = await assetInterface.restore(name || this.name);
                }
                catch (e) {
                    const error = e;
                    if (error.code === SAMErrorCode.CONFLICT) {
                        const { newName, countUsed } = formatFileName(name || this.name, this._core.environment.getValue('ASSET_CONFLICT_NAME_FORMAT'), (_counter || 0) + 1);
                        return this.restore(newName, countUsed);
                    }
                    else if (error.code === SAMErrorCode.ACCESS_DENIED) {
                        if ((_a = this.ancestorIDs) === null || _a === void 0 ? void 0 : _a.length) {
                            const parentId = this.ancestorIDs[this.ancestorIDs.length - 1];
                            const parentAsset = await this._core.hostAssetContext.getAssetWithCloudID(parentId);
                            if (parentAsset.isDiscarded) {
                                throw SAMError.create(SAMErrorCode.PARENT_DISCARDED, 'Parent folder is discarded');
                            }
                        }
                    }
                    throw error;
                }
            }
            if (repoMetadata) {
                // Optimistically update repo state from operation result
                const session = this._core.hostAssetContext.assetPool.createUpdateSession();
                this._core.hostAssetContext._adobeAssetUpdater.updateLocalItem(session, this, repoMetadata);
                if (this.isFolder && repoMetadata['repo:path']) {
                    // If it's a directory, we also need to update the repo:path of all its children!
                    updateCachedDescendents({
                        assetPool: this._core.hostAssetContext.assetPool,
                        asset: this,
                        session,
                        operation: { type: 'restore', newPath: repoMetadata['repo:path'] }
                    });
                }
                session.commit();
            }
        }
    }
    /**
     * Permanently delete the asset
     */
    async permanentlyDelete() {
        if (this.isLibraryElement) {
            await this._core.hostAssetContext.permanentlyDeleteLibraryElement(this.cloudID);
            const session = this._core.hostAssetContext.assetPool.createUpdateSession();
            session.queueDiscardedItem(this);
            session.commit();
        }
        else {
            const resultItems = await this._core.hostAssetContext.permanentlyDeleteAssets([this]);
            if (resultItems.failedResults.length) {
                throw resultItems.failedResults[0].error;
            }
        }
    }
    // Note - The APIs below aren't present in SAM-CPP:
    get etag() {
        return this._etag;
    }
    get version() {
        return this._version;
    }
    get createdBy() {
        return this._createdBy;
    }
    get modifiedBy() {
        return this._modifiedBy;
    }
    get discarded() {
        return this._discarded;
    }
    get discardedBy() {
        return this._discardedBy;
    }
    /**
     * Detect if the user has a specific permission or permissions
     * @param permissions
     * @usage
     *    // single permission check
     *    asset.hasPermission(Permission.Delete);
     *    // multiple
     *    asset.hasPermission(Permission.Write, Permission.Read);
     *    // multiple via bitwise OR
     *    asset.hasPermission(Permission.Write | Permission.Read);
     *    // multiple via spread operator
     *    asset.hasPermission(...permsToCheck);
     */
    hasPermission(...permissions) {
        return permissions.every((permission) => (this._permissions & permission) === permission);
    }
    // SAM-CPP doesn't expose the embedded metadata directly - you need to access it via DCX-CPP. We should port this API
    // back to SAM. Since not everybody needs to load the embedded metadata, this returns an object that the user can
    // explicitly load.
    get embeddedMetadata() {
        return this._embeddedMetadata;
    }
    get appMetadata() {
        return this._appMetadata;
    }
    /**
     * Patches application metadata for an Asset.
     * CAUTION: SAM does not validate the JSONPatch array. Wherever possible, use a service that is responsible for
     * maintaining the app metadata fields you want to modify, rather then modifying them directly. E.g., to modify
     * Project related data, use the Projects Service; for Reviews, use the Review service; allow server-side events
     * to populate fields as needed; etc.
     *
     * @param patches {jsonpatch.OpPatch[]} JSONPatch array of operations (https://jsonpatch.com/).
     */
    async patchAppMetadata(patches) {
        await this._appMetadata._patch(patches);
    }
    get manifest() {
        if (!this.isComposite) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Cannot get manifest of an asset that is not a composite');
        }
        return this._manifest;
    }
    /**
     * For use internally to release renditions after
     * the asset has been deleted permanently.
     * @package
     */
    _releaseRenditions() {
        this._renditionFetcher.release();
    }
    // NOTE: this is only guaranteed to work as advertised on web. Clients writing for desktop and
    // web should use `AdobeAsset.thumbnailURL` instead
    getRenditionURL(size, type = RenditionType.PNG) {
        // Do not try to fetch rendition for folders, or LEs that have no rendition link
        if (this.isFolder || (this.isLibraryElement && !this.links[RelType.RENDITION])) {
            return '';
        }
        return this._renditionFetcher.getURL(size, type, this.etag, this._getAssetInterface());
    }
    get thumbnailURL() {
        return this.getRenditionURL(THUMBNAIL_SIZE, RenditionType.PNG);
    }
    get thumbnailStatus() {
        return this.renditionURLStatus(THUMBNAIL_SIZE, RenditionType.PNG);
    }
    /**
     * See {@link AssetInterface#getCustomRendition} for usage
     */
    async getCustomRendition({ size, type, urlTemplateOptions, additionalHeaders } = {}) {
        await this._ensureLink(RelType.RENDITION);
        const assetInterface = await this._getAssetInterface();
        return assetInterface.getCustomRendition({ size, type, urlTemplateOptions, additionalHeaders });
    }
    /**
     * See {@link AssetInterface#getCustomRendition} for usage
     */
    async getAllCustomRenditions({ type, urlTemplateOptions, additionalHeaders } = {}) {
        await this._ensureLink(RelType.RENDITION);
        const assetInterface = await this._getAssetInterface();
        return assetInterface.getAllCustomRenditions({ type, urlTemplateOptions, additionalHeaders });
    }
    /**
     * Clears the error status on the request rendition and sets it
     * back to RenditionStatus.NotStarted
     *
     * @note After attempting to fetch a thumbnail and an error has occurred during
     * the fetch request, we assign an error status to the rendition. At this
     * point, we won't try to refetch the thumbnail until this method is called.
     * It is meant to give clients a means to handle when they attempt to refresh
     * instead of SAM.js making that decisions internally which could cause thumbnails
     * to begin loading when that may not be the desired behavior. This is also
     * not meant to fix every issue with thumbnails fetching so in the case where
     * the user might be offline or something similar, it's not recommended for
     * clients to continue to call this indefinitely.
     *
     *
     * @param size Defaults to 512
     * @param type Defaults to PNG
     * @returns {void} If the rendition requested is not found or the etag requested is different
     * than the one that was already fetched, this method will do nothing.
     */
    clearRenditionError(size = THUMBNAIL_SIZE, type = RenditionType.PNG) {
        this._renditionFetcher.clearRenditionError(size, type, this.etag);
    }
    /**
     * Returns one the enumeration values of RenditionStatus.
     *
     * @returns {RenditionStatus}
     */
    renditionURLStatus(size, type) {
        // Do not try to fetch rendition for folders, or LEs that have no rendition link
        if (this.isFolder || (this.isLibraryElement && !this.links[RelType.RENDITION])) {
            return RenditionStatus.Done;
        }
        return this._renditionFetcher.renditionURLStatus(size, type, this.etag);
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     */
    get spaceID() {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.appMetadata.data) === null || _a === void 0 ? void 0 : _a['cc:inherited']) === null || _b === void 0 ? void 0 : _b['cc:space']) === null || _c === void 0 ? void 0 : _c['repo:assetId'];
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     */
    get isSpaceAsset() {
        return !!this.spaceID;
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     */
    async getSpace() {
        const space = await this._core.spaceContext.getEnclosingSpace(this.cloudID);
        if (!space) {
            throw SAMError.create(SAMErrorCode.NOT_FOUND, 'Asset does not belong to a Space');
        }
        return space;
    }
    // Exposes the raw links for the underlying asset. This is so we can share them with clients like Fred, that don't have
    // access to SAM (since they're running in a web worker). SAM-CPP doesn't expose this.
    get links() {
        // Make a copy to avoid the client accidentally manipulating the links
        return Object.assign({}, this.__links);
    }
    /**
     * [SAM-2443] If missing links, then Asset probably came from USS, which doesn't have all R-API links
     *  (SEARCH-32676). In that case, try to update the links from R-API via the resolve API.
     * @package -- only for use in SAM.js
     */
    async _ensureLink(relType) {
        var _a;
        if ((_a = this.__links) === null || _a === void 0 ? void 0 : _a[relType]) {
            return;
        }
        const links = await this._core.hostAssetContext.repoInterface.getAssetLinks(this.cloudID);
        const session = this._core.hostAssetContext.assetPool.createUpdateSession();
        this._core.hostAssetContext._adobeAssetUpdater.updateLocalItem(session, this, { '_links': Object.assign(Object.assign({}, this.__links), links) });
        session.commit();
    }
    /**
     * Move within same repository
     *
     * @package -- for use in SAM.js only
     */
    async _move(newPath, retryOnConflict = false, counter = 0) {
        try {
            // In case of Asset created from USS response, repo id is not present
            // Jira on USS https://jira.corp.adobe.com/browse/SRCH-1989
            if (!this._repositoryID) {
                await this._load();
            }
            const operation = {
                op: 'move',
                source: {
                    'repo:assetId': this.id,
                    'repo:repositoryId': this._repositoryID
                },
                target: {
                    'repo:path': newPath,
                    'repo:repositoryId': this._repositoryID
                }
            };
            const assetInterface = await this._getAssetInterface();
            const result = await assetInterface.runOperation(operation);
            return result.asset;
        }
        catch (e) {
            if (e.code === SAMErrorCode.CONFLICT && retryOnConflict) {
                // If we can't restore because an asset already exists with the same name, we append a counter and try again
                const formatFileResult = formatFileName(newPath, this._core.environment.getValue('ASSET_CONFLICT_NAME_FORMAT'), counter + 1);
                return this._move(formatFileResult.newName, true, formatFileResult.countUsed);
            }
            throw e;
        }
    }
    async _copy(destFolder, destName, retryOnConflict = true, counter = 0, modelUpdater) {
        let resultOps = { failedResults: [], newModels: [] };
        await ItemBase.runExclusiveTask('copy', [this], async () => {
            let targetName = this.name;
            if (destName !== undefined) {
                if (!validatePlatformRepoName(destName)) {
                    throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Invalid name provided for asset copy operation.');
                }
                targetName = destName;
                if (counter) {
                    const formatFileResult = formatFileName(targetName, this._core.environment.getValue('ASSET_CONFLICT_NAME_FORMAT'), counter);
                    targetName = formatFileResult.newName;
                    counter = formatFileResult.countUsed;
                }
            }
            else {
                // check if we are copying in the same folder
                // if yes, then it should follow ASSET_COPY_NAME_FORMAT i.e. %@ copy (%d)
                // if no and conflict, then it should follow ASSET_CONFLICT_NAME_FORMAT i.e. %@ (%d)
                const sameFolder = destFolder.repoPath === this.repoPath.substring(0, this.repoPath.lastIndexOf('/'));
                const formatFileResult = sameFolder ? formatFileName(targetName, this._core.environment.getValue('ASSET_COPY_NAME_FORMAT'), counter)
                    : (counter > 0 ? formatFileName(targetName, this._core.environment.getValue('ASSET_CONFLICT_NAME_FORMAT'), counter) : { newName: targetName, countUsed: counter });
                targetName = formatFileResult.newName;
                counter = formatFileResult.countUsed;
            }
            const newPath = `${destFolder.repoPath}/${targetName}`;
            const assetsById = new Map();
            assetsById.set(this.id, this);
            if (!destFolder.repositoryID) {
                await destFolder._load();
            }
            // We only want to copy App metadata for PDFs and no other file format
            // This is a temporary solution and we will have a better solution on the service side
            const includeResources = [{ 'reltype': RelType.PRIMARY }];
            if (this.mediaType === 'application/pdf') {
                includeResources.push({ 'reltype': RelType.APP_METADATA });
            }
            const ops = {
                op: 'copy',
                id: this.id,
                source: {
                    'repo:assetId': this.id
                },
                target: {
                    'repo:path': newPath,
                    'repo:repositoryId': destFolder.repositoryID
                },
                // The following copies Content Resources for all formats
                // and Application Metadata for PDFs only
                includeResources
            };
            if (this.repositoryID) {
                const source = ops.source;
                source['repo:repositoryId'] = this.repositoryID;
            }
            resultOps = await this._core.hostAssetContext._runOperations([ops], assetsById, modelUpdater ? modelUpdater : (session, asset, remoteItem) => {
                if (!remoteItem) {
                    throw SAMError.create(SAMErrorCode.UNKNOWN, 'Missing metadata from asset copy operation.');
                }
                const newAsset = this._core.hostAssetContext._adobeAssetUpdater.updateRemoteItem(session, remoteItem);
                return newAsset;
            });
        });
        if (resultOps.failedResults.length) {
            if (resultOps.failedResults[0].error.code === SAMErrorCode.CONFLICT && retryOnConflict === true) {
                return await this._copy(destFolder, destName, retryOnConflict, counter + 1);
            }
            throw resultOps.failedResults[0].error;
        }
        return resultOps.newModels[0];
    }
    /**
     * Make a copy of this Asset within the same repository
     *
     * @param destFolder -- AdobeAsset instance representing the folder in which the copy of this AdobeAsset will be
     *     made
     * @param destName -- (not available on SAM.cpp) -- preferred name for the copy
     */
    async copy(destFolder, destName) {
        return await this._copy(destFolder, destName);
    }
    /**
     * Duplicate this Asset within the same folder.
     *
     * @param destName -- (not available on SAM.cpp) -- preferred name for the duplicate
     */
    async duplicate(destName) {
        const parentId = this.ancestorIDs.slice(-1)[0];
        const parentAsset = await this._core.hostAssetContext.getAssetWithCloudID(parentId);
        return await this._copy(parentAsset, destName);
    }
    async rename(newName) {
        if (!validatePlatformRepoName(newName)) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Invalid name provided for asset rename operation.');
        }
        const newPath = renameFileInPath(newName, this);
        const newMetadata = await this._move(newPath);
        if (newMetadata) {
            const assetPool = this._core.hostAssetContext.assetPool;
            // Update the metadata after a rename, since both name and path have changed
            const session = assetPool.createUpdateSession();
            if (this.isFolder) {
                updateCachedDescendents({
                    assetPool,
                    asset: this,
                    session,
                    operation: { type: 'rename', newPath }
                });
            }
            this._core.hostAssetContext._adobeAssetUpdater.updateLocalItem(session, this, newMetadata);
            session.commit();
        }
    }
    async move(destFolder) {
        const resultItems = await this._core.hostAssetContext.moveAssets([this], destFolder);
        if (resultItems.failedResults.length) {
            throw resultItems.failedResults[0].error;
        }
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     */
    transferToSpace(target) {
        return this._core.hostAssetContext.transfer(this, target);
    }
}
__decorate([
    observable
], AdobeAsset.prototype, "createdFromUpload", null);
__decorate([
    observable
], AdobeAsset.prototype, "createdByClientId", null);
__decorate([
    retry
], AdobeAsset.prototype, "_loadWithRetry", null);
__decorate([
    memoize
], AdobeAsset.prototype, "versions", null);
__decorate([
    observable
], AdobeAsset.prototype, "assetType", null);
__decorate([
    observable
], AdobeAsset.prototype, "isFolder", null);
__decorate([
    observable
], AdobeAsset.prototype, "isAssignment", null);
__decorate([
    observable
], AdobeAsset.prototype, "isFile", null);
__decorate([
    observable
], AdobeAsset.prototype, "isComposite", null);
__decorate([
    observable
], AdobeAsset.prototype, "isLibraryElement", null);
__decorate([
    observable
], AdobeAsset.prototype, "imageLength", null);
__decorate([
    observable
], AdobeAsset.prototype, "imageWidth", null);
__decorate([
    observable
], AdobeAsset.prototype, "parentAssignmentID", null);
__decorate([
    observable
], AdobeAsset.prototype, "isChildOfAssignment", null);
__decorate([
    observable
], AdobeAsset.prototype, "parentClassroomId", null);
__decorate([
    observable
], AdobeAsset.prototype, "isChildOfClassroom", null);
__decorate([
    observable
], AdobeAsset.prototype, "parentAssignmentMaterialsId", null);
__decorate([
    observable
], AdobeAsset.prototype, "isChildOfAssignmentMaterials", null);
__decorate([
    observable
], AdobeAsset.prototype, "parentStudentAssignmentId", null);
__decorate([
    observable
], AdobeAsset.prototype, "isChildOfStudentAssignment", null);
__decorate([
    observable
], AdobeAsset.prototype, "parentProjectID", null);
__decorate([
    memoize
], AdobeAsset.prototype, "_fixParentProjectID", null);
__decorate([
    observable
], AdobeAsset.prototype, "isChildOfProject", null);
__decorate([
    observable
], AdobeAsset.prototype, "isArtifact", null);
__decorate([
    observable
], AdobeAsset.prototype, "name", null);
__decorate([
    observable
], AdobeAsset.prototype, "shellVisibility", null);
__decorate([
    observable
], AdobeAsset.prototype, "ancestorIDs", null);
__decorate([
    observable
], AdobeAsset.prototype, "storageAssignee", null);
__decorate([
    observable
], AdobeAsset.prototype, "extension", null);
__decorate([
    observable
], AdobeAsset.prototype, "mediaType", null);
__decorate([
    observable
], AdobeAsset.prototype, "cloudID", null);
__decorate([
    observable
], AdobeAsset.prototype, "repositoryID", null);
__decorate([
    observable
], AdobeAsset.prototype, "repositoryName", null);
__decorate([
    observable
], AdobeAsset.prototype, "repoPath", null);
__decorate([
    observable
], AdobeAsset.prototype, "storageRegion", null);
__decorate([
    observable
], AdobeAsset.prototype, "created", null);
__decorate([
    observable
], AdobeAsset.prototype, "modified", null);
__decorate([
    observable
], AdobeAsset.prototype, "lastAccessed", null);
__decorate([
    observable
], AdobeAsset.prototype, "shared", null);
__decorate([
    observable
], AdobeAsset.prototype, "sizeInBytes", null);
__decorate([
    observable
], AdobeAsset.prototype, "isDiscarded", null);
__decorate([
    observable
], AdobeAsset.prototype, "assetRefString", null);
__decorate([
    observable
], AdobeAsset.prototype, "isPublished", null);
__decorate([
    memoize
], AdobeAsset.prototype, "children", null);
__decorate([
    observable
], AdobeAsset.prototype, "hasArtifacts", null);
__decorate([
    observable
], AdobeAsset.prototype, "etag", null);
__decorate([
    observable
], AdobeAsset.prototype, "version", null);
__decorate([
    observable
], AdobeAsset.prototype, "createdBy", null);
__decorate([
    observable
], AdobeAsset.prototype, "modifiedBy", null);
__decorate([
    observable
], AdobeAsset.prototype, "discarded", null);
__decorate([
    observable
], AdobeAsset.prototype, "discardedBy", null);
__decorate([
    observable
], AdobeAsset.prototype, "hasPermission", null);
__decorate([
    observable
], AdobeAsset.prototype, "appMetadata", null);
__decorate([
    observable
], AdobeAsset.prototype, "getRenditionURL", null);
__decorate([
    observable
], AdobeAsset.prototype, "thumbnailURL", null);
__decorate([
    observable
], AdobeAsset.prototype, "thumbnailStatus", null);
__decorate([
    observable
], AdobeAsset.prototype, "renditionURLStatus", null);
__decorate([
    observable
], AdobeAsset.prototype, "spaceID", null);
__decorate([
    observable
], AdobeAsset.prototype, "links", null);
function getPermission$1(source) {
    var _a;
    // from Asset._load()
    if (source.effectivePermission !== undefined) {
        return source.effectivePermission;
    }
    // From R-API directory listing or USS listings
    if ((_a = source._embedded) === null || _a === void 0 ? void 0 : _a[RelType.AC_EFFECTIVE]) {
        return parseEffectivePermissions(source._embedded[RelType.AC_EFFECTIVE]);
    }
    // If the permissions are not present in the metadata then maintain the current value
    return NotFound;
}
const StorageAssigneeFormat = {
    parse: (value) => {
        return value;
    },
    isEqual: (a, b) => {
        return (a === null || a === void 0 ? void 0 : a.id) === (b === null || b === void 0 ? void 0 : b.id) && (a === null || a === void 0 ? void 0 : a.type) === (b === null || b === void 0 ? void 0 : b.type);
    }
};
const AssetItemUpdaterOptions = {
    localItemType: AdobeAsset,
    remoteIdProperty: 'repo:assetId',
    // We want the SharedCollaboratorChangedEvent to fire when these properties update
    // so that internally and externally we can response to this specific event to do
    // things like update collaborators etc.
    changeEventMap: new Map([
        ['_isShared', SharedCollaboratorChangedEvent],
        ['_isSharedWithYouAsset', SharedCollaboratorChangedEvent]
    ]),
    // Mapping AdobeAssetUpdaterMetadata | AssetReviewServiceData to AdobeAsset
    propertyUpdater: new PropertyUpdater({
        '_name': 'repo:name',
        '_etag': 'repo:etag',
        '_repositoryID': 'repo:repositoryId',
        '_repositoryName': 'repositoryName',
        '_path': 'repo:path',
        '_storageRegion': 'storage:region',
        '_type': 'dc:format',
        '_size': { name: 'repo:size', format: 'number' },
        '_version': 'repo:version',
        '_assetClass': { name: 'repo:assetClass', choices: AssetClassChoices },
        '_assetState': { name: 'repo:state', choices: AssetStateChoices },
        '_created': { name: 'storage:deviceCreateDate', format: 'date' },
        '_modified': { name: 'storage:deviceModifyDate', format: 'date' },
        '_lastAccessed': { name: 'lastAccessed', format: 'date' },
        '_storageAssignee': { name: 'storage:assignee', format: StorageAssigneeFormat },
        '_discarded': { name: 'repo:discardDate', format: 'date' },
        '_createdBy': 'repo:createdBy',
        '_modifiedBy': 'repo:modifiedBy',
        '_discardedBy': 'repo:discardedBy',
        '_imageLength': { name: 'tiff:imageLength', format: 'number' },
        '_imageWidth': { name: 'tiff:imageWidth', format: 'number' },
        '_permissions': { get: getPermission$1, format: 'number' },
        '_isShared': { name: 'isShared', format: 'boolean' },
        '_isSharedWithYouAsset': { name: 'sharedViaInvite', format: 'boolean' },
        '_shareFlagsAreDirty': { name: 'shareFlagsAreDirty', format: 'boolean' },
        '_collaboratorDataIsDirty': { name: 'collaboratorDataIsDirty', format: 'boolean' },
        // PropertyUpdater reads from _appMetadata._data for comparison
        '_appMetadata._data': {
            name: `_embedded[${RelType.APP_METADATA}]`,
            // Use object_subset format to avoid unnecessary change events. E.g., if _appMetadata.data is
            // `{ 'shell:visibility': 'hiddenSelf', foo: 32 }` and the listing contains
            // `{ 'shell:visibility': 'hiddenSelf' }` then the _mergeData will cause no change, but using format
            // 'object', isEqual() will see source and dest aren't equal and thus assume that a change did occur.
            format: 'object_subset',
            // Only update the appMetadata properties that are in the listing's embedded appMetadata
            set: (parsedSource, dest) => {
                const asset = dest;
                asset.appMetadata._mergeData(parsedSource);
            }
        },
        '_ancestorIDs': { name: 'repo:ancestors', isArray: true, format: 'string' },
        '__links': { name: '_links', format: 'object' },
        '_hasArtifacts': { name: 'hasArtifacts', format: 'boolean' }
    })
};
const AdobeAssetUpdater = new ItemUpdater(AssetItemUpdaterOptions);
function getUSSAssetType(source) {
    // uss type is 'application/x-sharedcloud-collection+json' for directory
    // changing it to standard mime directory type to abstract it for SAM clients
    if (source.type === 'application/x-sharedcloud-collection+json') {
        return 'application/vnd.adobecloud.directory+json';
    }
    return source.type;
}
function getUSSAssetClass(source) {
    // for directories, asset_sub_type is not present in USS response
    if (source.type === 'application/x-sharedcloud-collection+json' || source.type === 'application/vnd.adobecloud.directory+json') {
        return 'directory';
    }
    // for files, asset_sub_type is not present in USS response
    // so we assign anything else that is not dcx or directory as file
    if (source.creative_cloud_asset_type !== 'dcx') {
        return 'file';
    }
    return source.asset_sub_type;
}
function getUSSLastAccessed(source) {
    var _a, _b;
    const lastAccessedISOString = (_b = (_a = source['api:metadata/peruser']) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b['shell:lastAccessDate'];
    // If the property is not present, the current value should be left alone
    if (lastAccessedISOString === undefined) {
        return NotFound;
    }
    // If the property is present, but empty, clear the current value
    if (lastAccessedISOString === '') {
        return undefined;
    }
    return lastAccessedISOString;
}
function getCreatedFromUpload(source) {
    var _a;
    return (_a = source['api:metadata/application']) === null || _a === void 0 ? void 0 : _a['cc:createdFromUpload'];
}
function getUSSStorageRegion(source) {
    var _a, _b;
    // USS stores 'region' as 'US', 'AP', 'EU' for some Assets, and 'va6c2', 'va7' for other Assets. According to USS
    // team, the US/AP/EU strings can be mapped to VA6/JPN3/IRL1, and the others match the R-API storage:region.
    switch ((_a = source.region) === null || _a === void 0 ? void 0 : _a.toLowerCase()) {
        case 'us':
            return 'VA6';
        case 'ap':
            return 'JPN3';
        case 'eu':
            return 'IRL1';
        default:
            return ((_b = source.region) === null || _b === void 0 ? void 0 : _b.toUpperCase()) || '';
    }
}
// USS result item does not contain asset name in path, to keep it sync with repo path
// received from RAPI, we will add asset name in asset path while creating/updating asset
// from USS result
function getAssetPath(source) {
    if (source.asset_name_path && source.asset_name) {
        return path.join(source.asset_name_path, source.asset_name);
    }
    return source.asset_name_path || source.asset_name || '';
}
function getEtag(source) {
    var _a;
    return source.composite_manifest_etag || ((_a = source.etag) === null || _a === void 0 ? void 0 : _a.primary) || '';
}
const USSAssetItemUpdaterOptions = {
    localItemType: AdobeAsset,
    remoteIdProperty: 'asset_id',
    // We want the SharedCollaboratorChangedEvent to fire when these properties update
    // so that internally and externally we can respond to this specific event to do
    // things like update collaborators etc.
    changeEventMap: new Map([
        ['_isShared', SharedCollaboratorChangedEvent],
        ['_isSharedWithYouAsset', SharedCollaboratorChangedEvent],
        ['_isPublicLink', SharedCollaboratorChangedEvent],
        ['_directCollaboratorCount', SharedCollaboratorChangedEvent]
    ]),
    propertyUpdater: new PropertyUpdater({
        '_name': 'asset_name',
        '_etag': { get: getEtag },
        // NOTE: USS team added this field recently, so only some Assets will have this field until
        //       re-indexing is finished, which has ETA of Feb 28 2023
        '_repositoryID': 'repo:repositoryId',
        '_path': { get: getAssetPath, format: 'string' },
        '_type': { get: getUSSAssetType, format: 'string' },
        '_size': { name: 'size', format: 'number' },
        '_version': 'asset_version_id',
        '_assetClass': { get: getUSSAssetClass, choices: AssetClassChoices, format: 'string' },
        '_permissions': { get: getPermission$1, format: 'number' },
        '_created': { name: 'create_date', format: 'date' },
        '_modified': { name: 'modify_date', format: 'date' },
        '_shared': { name: 'collab_metadata#mine.shared_date', format: 'date_as_number' },
        '_discarded': { name: 'discarded_date', format: 'date' },
        '_discardedBy': 'discarded_by',
        '_assetState': { name: 'repo:state', choices: AssetStateChoices, format: 'string' },
        '_isArchivedLibraryElement': { name: 'isArchivedLibraryElement', format: 'boolean' },
        '_lastAccessed': { get: getUSSLastAccessed, format: 'date' },
        '_createdFromUpload': { get: getCreatedFromUpload, format: 'boolean' },
        '_storageAssignee': { name: 'storage:assignee', format: StorageAssigneeFormat },
        '_storageRegion': { name: 'region', get: getUSSStorageRegion, format: 'string' },
        '_createdBy': 'creative_cloud_creator_id',
        '_isSharedWithYouAsset': { name: 'sharedViaInvite', format: 'boolean' },
        '_isShared': { name: 'isShared', format: 'boolean' },
        '_isPublicLink': { name: 'isPublicLink', format: 'boolean' },
        '_directCollaboratorCount': { name: 'directCollaboratorCount', format: 'number' },
        '_isPublished': { name: 'published', format: 'boolean' },
        '_imageLength': { name: 'image_height', format: 'number' },
        '_imageWidth': { name: 'image_width', format: 'number' },
        '_shareFlagsAreDirty': { name: 'shareFlagsAreDirty', format: 'boolean' },
        // See notes in AdobeAssetUpdater
        '_appMetadata._data': {
            name: 'app_metadata',
            format: 'object_subset',
            set: (parsedSource, dest) => {
                const asset = dest;
                asset.appMetadata._mergeData(parsedSource);
            }
        },
        '_ancestorIDs': { name: 'path', isArray: true, format: 'string' },
        '__links': { name: '_links', format: 'object' },
        '_createdByClientId': 'repo:createdByClientId'
    })
};
const USSAdobeAssetUpdater = new ItemUpdater(USSAssetItemUpdaterOptions);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class AssetTransferInterface extends InterfaceBase {
    async transfer(data) {
        const links = await this.discoverLinks();
        const response = await this._doPost(getURLFromLinks(links, RelType.ATS_MOVE), { 'Content-Type': 'application/json' }, JSON.stringify(data), [200]);
        // Asset Transfer Service will return a 200 response when a dependent service fails.
        const json = await response.json();
        if (json.status && json.status !== 200) {
            throw SAMError.createWithResponse(response);
        }
        // Returns the transferred asset's Cloud ID.
        return json.assetId;
    }
    async discoverLinks() {
        const response = await this._getResponse(this.getUrl('/'));
        return response._links;
    }
    getUrl(endpoint) {
        return `https://${this._core.environment.endpoints.TRANSFER_SERVICE_HOST}${endpoint}`;
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const REVIEW_SERVICE_LINKS = {
    [RelType.REVIEW_SERVICE_PROVISION]: [
        'documentId',
        'assetRegion',
        'repositoryId'
    ],
    [RelType.REVIEW_SERVICE_DISCARD]: ['assetId', 'assetRegion', 'repositoryId'],
    [RelType.REVIEW_SERVICE_OUTPUTS]: [
        'assetId',
        'assetRegion',
        'documentId',
        'embed',
        'limit',
        'orderBy',
        'outputType',
        'repositoryId',
        'start'
    ],
    [RelType.REVIEW_SERIVCE_HAS_OUTPUTS]: ['assetRegion']
};
const mapAssetsToAssetIndentifier = function (assets) {
    return assets.map((asset) => {
        return {
            'repo:assetId': asset.id,
            'repo:repositoryId': asset.repositoryID
        };
    });
};
function validateReviewServiceLinks(links) {
    return validateLinks(links, REVIEW_SERVICE_LINKS);
}
//  end parameters for frameLink endpoint
//--------------------------------------------------------------------
class AssetReviewServiceInterface extends InterfaceBase {
    constructor() {
        super(...arguments);
        this.baseURL = `https://${this._core.environment.endpoints.REVIEW_SERVICE_HOST}`;
        this.baseEndpoint = '/api/v1';
        this.indexEndpoint = this.baseEndpoint + '/index';
    }
    getEndpointURL(endpoint) {
        return `${this.baseURL}${endpoint}`;
    }
    async _discoverLinks(rootHref) {
        const response = await this._getResponse(rootHref, this._getHeaders());
        return response._links;
    }
    _getHeaders(contentType) {
        const headers = {};
        if (contentType) {
            headers['Content-Type'] = contentType;
        }
        return headers;
    }
    async discoverLinks() {
        return this._discoverLinks(this.getEndpointURL(this.indexEndpoint));
    }
    async discardOutputsForAsset(assetId, assetRegion, repositoryID) {
        const links = await this.discoverLinks();
        const url = getURLFromLinks(links, RelType.REVIEW_SERVICE_DISCARD, Object.assign(Object.assign({ assetId }, (assetRegion && { assetRegion })), (repositoryID && { repositoryID })));
        await this._doPost(url, this._getHeaders(), undefined, [204]);
    }
    async hasOutputs(assets, assetRegion) {
        const links = await this.discoverLinks();
        const url = getURLFromLinks(links, RelType.REVIEW_SERIVCE_HAS_OUTPUTS, Object.assign({}, (assetRegion && { assetRegion })));
        const response = await this._doPost(url, this._getHeaders(JSON_MIME_TYPE), JSON.stringify(assets), [200]);
        return await response.json();
    }
    _createFrameLinkUrl(params) {
        const url = new URL(`${this.baseURL}/api/v1/frameioLink`);
        Object.entries(params).forEach(([key, val]) => url.searchParams.set(key, val));
        return url.toString();
    }
    /*
     * See https://wiki.corp.adobe.com/display/CreativeCloudAssets/Proposal+v2%3A+CCARS+Frame+S4R+APIs
     *
     * We use the frameLink endpoint for all four CRUD operations, we just change the method:
     *  POST -> associate a new review with source document
     */
    async createReviewLink(param) {
        const { reviewLinkId, artifactId, projectId } = param, query = __rest(param, ["reviewLinkId", "artifactId", "projectId"]);
        const response = await this._doPost(this._createFrameLinkUrl(query), { 'Content-Type': 'application/json' }, JSON.stringify({
            reviewLinkId: `${reviewLinkId}`,
            artifactId: `${artifactId}`,
            projectId: `${projectId}`
        }));
        return await response.json();
    }
    /*
     * See https://wiki.corp.adobe.com/display/CreativeCloudAssets/Proposal+v2%3A+CCARS+Frame+S4R+APIs
     *
     * We use the frameLink endpoint for all four CRUD operations, we just change the method:
     *  GET -> return array of all reviews associated with source document
     */
    async getReviewLinks(param) {
        const response = await this._doGet(this._createFrameLinkUrl(param));
        return await response.json();
    }
    /*
     * See https://wiki.corp.adobe.com/display/CreativeCloudAssets/Proposal+v2%3A+CCARS+Frame+S4R+APIs
     *
     * We use the frameLink endpoint for all four CRUD operations, we just change the method:
     *  PUT -> update an existing review associated with source document
     */
    async updateReviewLink(param) {
        const { artifactId, projectId, etag = '*' } = param, query = __rest(param, ["artifactId", "projectId", "etag"]);
        const response = await this._doPut(this._createFrameLinkUrl(query), {
            'Content-Type': 'application/json',
            'If-Match': `${etag}`
        }, JSON.stringify({
            artifactId: `${artifactId}`,
            projectId: `${projectId}`
        }));
        return await response.json();
    }
    /*
     * See https://wiki.corp.adobe.com/display/CreativeCloudAssets/Proposal+v2%3A+CCARS+Frame+S4R+APIs
     *
     * We use the frameLink endpoint for all four CRUD operations, we just change the method:
     *  DELETE -> update an existing review associated with source document
     */
    async deleteReviewLink(param) {
        const { etag } = param, query = __rest(param, ["etag"]);
        let headers = {};
        if (etag) {
            headers = {
                'If-Match': `${etag}`
            };
        }
        // there is no reponse body to a DELETE returning status 204
        await this._doDelete(this._createFrameLinkUrl(query), headers, [204]);
    }
}
__decorate([
    cache(validateReviewServiceLinks),
    __param(0, key)
], AssetReviewServiceInterface.prototype, "_discoverLinks", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const reloadSpace = Symbol('reloadSpace');
const clearLastReloadError = Symbol('clearLastReloadError');

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class SpaceSearchAssetLoader {
    constructor(assetPool, core, _asset, _ussInterface, _artifactFilter, _embedPermissions = false) {
        this._asset = _asset;
        this._ussInterface = _ussInterface;
        this._artifactFilter = _artifactFilter;
        this._embedPermissions = _embedPermissions;
        /**
         * @package -- for use by USSLoaderHelper
         */
        this._getItemData = this._getItemDataWithRepoId(this._asset.repositoryID);
        this._ussLoaderHelper = new USSLoaderHelper(assetPool, core, this);
    }
    supportsFilter(filter) {
        const supportedFilters = [];
        supportedFilters.push(filter);
        // Client can call getList with SpaceSearchAssetFilter.artifactFilter undefined, but can't pass
        // SSAF.artifactFilter that doesn't match this._artifactFilter
        if (!supportedFilters.every(f => (f.artifactFilter === this._artifactFilter || f.artifactFilter === undefined))) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'SpaceSearchAssetFilter.artifactFilter does not match SpaceSearchAssetLoader.artifactFilter');
        }
        return true;
    }
    loadPage(options) {
        return this._ussLoaderHelper.loadPage(options);
    }
    /**
     * @package -- for use by USSLoaderHelper
     */
    async _getUSSResults({ start, limit, order, mediaTypes }) {
        return this._ussInterface.searchSpace(this._asset.id, this._artifactFilter === 'onlyArtifacts', this._embedPermissions, {
            type: mediaTypes,
            sort_orderby: OrderByToUSSOrderBy[order.field],
            sort_order: order.direction === 'descending' ? 'desc' : 'asc',
            limit,
            start_index: start
        });
    }
    _getItemDataWithRepoId(repoID) {
        // TODO: Patching should be removed once https://jira.corp.adobe.com/browse/SRCH-1989 is fixed
        //       and re-indexing is finished, which has ETA of Feb 28 2023
        return (item) => {
            return Object.assign(Object.assign({}, item), { 'repo:repositoryId': repoID });
        };
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class SpaceSearchAssetFilter extends ImplicitAdobeAssetFilterBase {
    constructor(options) {
        super({ _includeHiddenRecursive: options === null || options === void 0 ? void 0 : options._includeHiddenRecursive });
        this._asset = options === null || options === void 0 ? void 0 : options.asset;
        this.artifactFilter = options === null || options === void 0 ? void 0 : options.artifactFilter;
    }
    get hash() {
        var _a;
        return [
            // include 'spaceSearch' so a match on hash can only happen
            // for other spaceSearch lists
            'spaceSearch',
            (_a = this._asset) === null || _a === void 0 ? void 0 : _a.id,
            this.artifactFilter, super.hash
        ].filter(x => x).join(':');
    }
    matches(item) {
        var _a;
        // check if item is a descendant of the space/working Files folder
        if (((_a = this._asset) === null || _a === void 0 ? void 0 : _a.repoPath) && !item.repoPath.startsWith(`${this._asset.repoPath}/`)) {
            return false;
        }
        // check item meets artifactFilter criteria
        if (item.isArtifact && this.artifactFilter === 'excludeArtifacts') {
            return false;
        }
        if (!item.isArtifact && this.artifactFilter === 'onlyArtifacts') {
            return false;
        }
        return super.matches(item);
    }
    getRelationToFilter(other) {
        var _a, _b;
        const repoPath = (_a = this._asset) === null || _a === void 0 ? void 0 : _a.repoPath;
        const otherRepoPath = (_b = other._asset) === null || _b === void 0 ? void 0 : _b.repoPath;
        if (repoPath
            && otherRepoPath
            && !repoPath.startsWith(`${otherRepoPath}/`)
            && !otherRepoPath.startsWith(`${repoPath}/`)) {
            // no overlap in descendants
            return 'disjoint';
        }
        if (this.artifactFilter
            && other.artifactFilter
            && this.artifactFilter !== other.artifactFilter) {
            // mutually exclusive artifactFilter
            return 'disjoint';
        }
        const mediaTypeRelation = super.getRelationToFilter(other);
        if (mediaTypeRelation === 'disjoint') {
            return 'disjoint';
        }
        //----- All 'disjoint' cases must be handled above this line
        if (mediaTypeRelation === 'unknown') {
            // This filter shares some, but not all of the mediaTypes of other. They
            // could overlap, but can't know for sure.
            return 'unknown';
        }
        if (!repoPath && otherRepoPath) {
            // This list isn't filtered by asset's path, but the other could contain
            // some of this list's items, but possibly not all of them.
            return 'unknown';
        }
        if (repoPath
            && otherRepoPath
            && repoPath.startsWith(`${otherRepoPath}/`)) {
            // This asset is a descendant of the other's, so it could have
            // some of the other's items, but possibly not all of them.
            return 'unknown';
        }
        if (!this.artifactFilter && other.artifactFilter) {
            // This list isn't filtered by Artifact status, but the other is.
            // It could have some
            return 'unknown';
        }
        //------ All 'unknown' cases must be handled above this line
        // Since the filters can't be 'disjoint', there must be some overlap, leaving
        // only 'unknown' and 'subset' as a possibility. Since all 'unknown' cases have
        // been eliminated, the only remaining possibility is 'subset'.
        return 'subset';
    }
}
SpaceSearchAssetFilter.default = new SpaceSearchAssetFilter();

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const TeamRoles = ['UNKNOWN', 'NONE', 'MEMBER'];
const SpaceAccessChoices = ['private', 'team'];
const SpaceRoleChoices = ['NONE', 'CONTRIBUTOR', 'REVIEWER'];
const SpaceStates = ['active', 'archived', 'in_progress'];
/// Represents the notion of a "space" where multiple Adobe account holders collaborate
class Space extends ShareableItem {
    constructor() {
        super(...arguments);
        this._name = '';
        this._repositoryID = '';
        this._repositoryName = '';
        this._pathName = '';
        this._created = new Date();
        this._modified = new Date();
        this._contributorCount = 0;
        this._role = 'NONE';
        this._color = '';
        this._isBookmarked = false;
        this._teamID = '';
        this._teamName = '';
        this._thumbnail = '';
        this._access = 'private';
        this._etag = '';
        this._version = 0;
        this._state = 'active';
        this._loadedGeneration = 0;
        // Note that we maintain this flag based on where the item was loaded from.
        // Spaces loaded via the teamAccess=false API will have teamRole='NONE', all other
        // spaces will assume a 'UNKNOWN' role until they are updated with the correct role
        // on the client side.
        this._teamRole = 'UNKNOWN';
    }
    /// The unique id that represents the space.
    get spaceID() {
        return this.id;
    }
    /// The user-visible name assigned to the space.
    get name() {
        return this._name;
    }
    get access() {
        return this._access;
    }
    get etag() {
        return this._etag;
    }
    get state() {
        return this._state;
    }
    /// The unique id of the repository that owns this space.
    get repositoryID() {
        return this._repositoryID;
    }
    /// The user-visible name of the repository (i.e. the organization name) that owns this space.
    get repositoryName() {
        return this._repositoryName;
    }
    /// The full path name for the space (e.g. "/content/assets/Everyone/Test Space").
    get pathName() {
        return this._pathName;
    }
    /// The created date of the space, relative to 00:00:00 UTC on Jan 1, 1970.
    get created() {
        return this._created;
    }
    /// The modified date of the space, relative to 00:00:00 UTC on Jan 1, 1970.
    get modified() {
        return this._modified;
    }
    /// Total number of contributors for this space. Any groups that are added as a Contributor
    /// count as 1 toward this total.
    get contributorCount() {
        return this._contributorCount;
    }
    /// Role on the space for the current user.
    get role() {
        return this._role;
    }
    /// The color assigned to the space.
    get color() {
        return this._color;
    }
    /// Indicates whether the space is bookmarked (i.e. followed) by the current user.
    get isBookmarked() {
        return this._isBookmarked;
    }
    /// The teamID of the parent team.
    get teamID() {
        return this._teamID;
    }
    /// The user-visible name of the parent team.
    get teamName() {
        return this._teamName;
    }
    /// Description of the space.
    get description() {
        return this._description;
    }
    /// Thumbnail URL of the space.
    get thumbnail() {
        return this._thumbnail;
    }
    get version() {
        return this._version;
    }
    get teamRole() {
        return this._teamRole;
    }
    get _links() {
        return this.__links;
    }
    async ensureDescription() {
        var _a;
        if (this._description === undefined) {
            await this.load({ refreshTimeout: -1 });
        }
        return (_a = this.description) !== null && _a !== void 0 ? _a : '';
    }
    async getChild(name) {
        var _a;
        if (!this._children) {
            // Retrieve the children of the space if needed.
            await this.load({ refreshTimeout: -1 });
        }
        return (_a = this._children) === null || _a === void 0 ? void 0 : _a.get(name);
    }
    // Only used for 1-Off Invitees.
    // Memoized so the same Source and its Lists can be reused.
    async searchWorkingFiles() {
        const { assetPool, ussInterface } = this._core.hostAssetContext;
        const workingFilesFolder = await this._core.hostAssetContext
            .getAssetWithRepoPath(`${this.pathName}/working_files`, this.repositoryID);
        const spaceSearchAssetLoader = new SpaceSearchAssetLoader(assetPool, this._core, workingFilesFolder, ussInterface, 'excludeArtifacts', true // always include permissions for 1-off invitees
        );
        const implicitFilter = new SpaceSearchAssetFilter({
            asset: workingFilesFolder,
            artifactFilter: 'excludeArtifacts'
        });
        return new ItemSource(assetPool, [spaceSearchAssetLoader], implicitFilter);
    }
    // All Space members should use `searchArtifacts(false)`, as the UI can assume the user has
    // full permissions. For 1-Off Invitees, use `searchArtifacts(true)`
    // Memoized so the same Source and its Lists can be reused -- note that this means you
    // cannot switch between embedPermissions = true and = false.
    async searchArtifacts(embedPermissions = false) {
        const { assetPool, ussInterface } = this._core.hostAssetContext;
        const spaceAdobeAsset = await this._core.hostAssetContext.getAssetWithCloudID(this.id);
        const spaceSearchAssetLoader = new SpaceSearchAssetLoader(assetPool, this._core, spaceAdobeAsset, ussInterface, 'onlyArtifacts', embedPermissions);
        const implicitFilter = new SpaceSearchAssetFilter({
            asset: spaceAdobeAsset,
            artifactFilter: 'onlyArtifacts',
            // Some artifacts may be in hidden folders
            _includeHiddenRecursive: true
        });
        return new ItemSource(assetPool, [spaceSearchAssetLoader], implicitFilter);
    }
    async getWorkingFiles() {
        // Assuming repo path of working_files rather than getting it via discovery so it is compatible
        // with SAM.cpp AssetContext. When all of Spaces can be handled in SAM.cpp, this should switch back
        // to using discovery.
        // const workingFiles = await this.getChild('working_files');
        // if (!workingFiles) {
        //     throw SAMError.create(SAMErrorCode.NOT_FOUND, 'could not load working_files');
        // }
        // const folder = await this._core.hostAssetContext.getAssetWithCloudID(workingFiles.id);
        const folder = await this._core.hostAssetContext
            .getAssetWithRepoPath(`${this.pathName}/working_files`, this.repositoryID);
        return folder.children;
    }
    async load(options) {
        var _a;
        const refreshTimeout = options === null || options === void 0 ? void 0 : options.refreshTimeout;
        let lastReload = this._lastReload;
        const canReuseLastReload = lastReload && (
        // The previous load is still pending execution, so we could reuse the same call.
        lastReload.pending
            // Just wanted to do at least one load.
            || refreshTimeout === -1
            // Just in case the data didn't expire already.
            || (refreshTimeout && lastReload.requestTime + refreshTimeout > Date.now()));
        if (!canReuseLastReload) {
            // Otherwise let's load the space again.
            // Note that generation of reloads, we want to make sure that only the very last reload
            // gets a chance to update the space.
            lastReload = this._lastReload = {
                pending: true,
                inProgress: true,
                generation: ((_a = lastReload === null || lastReload === void 0 ? void 0 : lastReload.generation) !== null && _a !== void 0 ? _a : this._loadedGeneration) + 1,
                requestTime: Date.now(),
                promise: new Promise((resolve, reject) => {
                    // Wait until the loop ends to avoid multiple queries on the same data.
                    setTimeout(() => this._executeReload(lastReload).finally(() => lastReload.error ? reject(lastReload.error) : resolve()), 0);
                })
            };
            // Notify the users we have a new loading session.
            this.dispatch(SharedChangeEvent);
        }
        await lastReload.promise;
    }
    async _executeReload(reload) {
        if (reload !== this._lastReload) {
            // The same reload will be used inside the load method up to the point when we actually execute the request.
            // However, there's a case when the `clearLastReloadError` was used while we were waiting. in the setTimeout above.
            // In that case we can just avoid fetching the request entirely.
            assert(!reload.pending, 'The pending flag must be false after the reload was cleared out.');
            return;
        }
        // Make a note that the request was executed.
        reload.pending = false;
        try {
            await this._core.spaceContext[reloadSpace](this);
        }
        catch (error) {
            reload.error = error;
        }
        // Check if the asset was removed. We will want to remove it from the lists.
        reload.inProgress = false;
        const lastReload = this._lastReload;
        if (lastReload !== reload && reload.generation <= this._loadedGeneration) {
            // This result is older than a newer request that was already applied to this object.
            // Ignore the results. This can happen if a particular request takes longer to execute
            // and a new reload was already good to go.
            return;
        }
        if (reload.error instanceof SAMError && reload.error.code === SAMErrorCode.NOT_FOUND) {
            const session = this._core.spaceContext.spacePool.createUpdateSession();
            session.queueDiscardedItem(this);
            session.commit();
        }
        // Notify about the end of the load sequence.
        this.dispatch(SharedChangeEvent);
    }
    /**
     * If the item gets updated through the listing API then we need to clear any errors from the previous reload.
     * This usually happens if the user gets re-invited to a space while the space is still in memory.
     * At that point, we need to clear the previous error when the listing API detects the new space.
     */
    [clearLastReloadError]() {
        const lastReload = this._lastReload;
        if (lastReload === null || lastReload === void 0 ? void 0 : lastReload.error) {
            lastReload.error = undefined;
            // Setting pending to false to be able to track it inside `_executeReload` above.
            lastReload.pending = false;
            this.dispatch(SharedChangeEvent);
        }
    }
    get isReloading() {
        var _a, _b;
        return (_b = (_a = this._lastReload) === null || _a === void 0 ? void 0 : _a.inProgress) !== null && _b !== void 0 ? _b : false;
    }
    get lastReloadError() {
        var _a;
        return (_a = this._lastReload) === null || _a === void 0 ? void 0 : _a.error;
    }
    async update(data) {
        await this._core.spaceContext.updateSpace(this, data);
    }
    async rename(newName) {
        await this._core.spaceContext.updateSpace(this, { name: newName });
    }
    async archive() {
        const resultItems = await this._core.spaceContext.archiveSpaces([this]);
        if (resultItems.failedResults.length) {
            throw resultItems.failedResults[0].error;
        }
    }
    async restore(name) {
        let nameMap;
        if (name !== undefined) {
            nameMap = new Map();
            nameMap.set(this, name);
        }
        const resultItems = await this._core.spaceContext.restoreSpaces([this], nameMap);
        if (resultItems.failedResults.length) {
            throw resultItems.failedResults[0].error;
        }
    }
}
__decorate([
    observable
], Space.prototype, "name", null);
__decorate([
    observable
], Space.prototype, "access", null);
__decorate([
    observable
], Space.prototype, "etag", null);
__decorate([
    observable
], Space.prototype, "state", null);
__decorate([
    observable
], Space.prototype, "repositoryID", null);
__decorate([
    observable
], Space.prototype, "repositoryName", null);
__decorate([
    observable
], Space.prototype, "pathName", null);
__decorate([
    observable
], Space.prototype, "created", null);
__decorate([
    observable
], Space.prototype, "modified", null);
__decorate([
    observable
], Space.prototype, "contributorCount", null);
__decorate([
    observable
], Space.prototype, "role", null);
__decorate([
    observable
], Space.prototype, "color", null);
__decorate([
    observable
], Space.prototype, "isBookmarked", null);
__decorate([
    observable
], Space.prototype, "teamID", null);
__decorate([
    observable
], Space.prototype, "teamName", null);
__decorate([
    observable
], Space.prototype, "description", null);
__decorate([
    observable
], Space.prototype, "thumbnail", null);
__decorate([
    observable
], Space.prototype, "version", null);
__decorate([
    observable
], Space.prototype, "teamRole", null);
__decorate([
    observable
], Space.prototype, "_links", null);
__decorate([
    memoize
], Space.prototype, "searchWorkingFiles", null);
__decorate([
    memoize
], Space.prototype, "searchArtifacts", null);
__decorate([
    memoize
], Space.prototype, "getWorkingFiles", null);
__decorate([
    observable
], Space.prototype, "isReloading", null);
__decorate([
    observable
], Space.prototype, "lastReloadError", null);
const SpaceChildFormat = {
    parse: (value) => {
        return {
            id: value.id,
            _links: value._links
        };
    },
    isEqual: (a, b) => {
        return a.id === b.id && isDeepEqual(a._links, b._links);
    }
};
const SpaceUpdater = new ItemUpdater({
    localItemType: Space,
    remoteIdProperty: 'id',
    propertyUpdater: new PropertyUpdater({
        '_name': 'name',
        '_access': { name: 'access', choices: SpaceAccessChoices },
        '_etag': 'etag',
        '_repositoryID': 'repositoryId',
        '_repositoryName': 'repositoryName',
        '_pathName': 'pathName',
        '_created': { name: 'createdDate', format: 'date' },
        '_modified': { name: 'modifiedDate', format: 'date' },
        '_contributorCount': { name: 'contributorCount', format: 'number' },
        '_role': { name: 'role', choices: SpaceRoleChoices },
        '_color': { name: 'color' },
        '_isBookmarked': { name: 'bookmarked', format: 'boolean' },
        '_teamID': 'teamId',
        '_teamName': 'teamName',
        '_description': 'description',
        '_thumbnail': 'thumbnail',
        '_children': { name: 'children', mapByKey: 'name', format: SpaceChildFormat },
        '_version': { name: 'version', format: 'number' },
        '_teamRole': { name: 'teamRole', choices: TeamRoles },
        '_state': { name: 'state', choices: SpaceStates },
        '__links': { name: '_links', format: 'object' }
    })
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class RecentAdobeAssetLoader {
    constructor(assetPool, _core, _recentInterface, _implicitFilter) {
        this._core = _core;
        this._recentInterface = _recentInterface;
        this._implicitFilter = _implicitFilter;
        this._ussLoaderHelper = new USSLoaderHelper(assetPool, _core, this);
    }
    supportsFilter(filter) {
        // If we don't provide a filter at all, then we cannot use the loader
        return filter !== undefined;
    }
    async loadPage(options) {
        if (!this._libraryContext) {
            this._libraryContext = await this._core.getLibraryContext();
        }
        return this._ussLoaderHelper.loadPage(options);
    }
    /**
     * @package -- only for use by USSLoaderHelper
     */
    async _getUSSResults({ start, limit, mediaTypes, excludeMediaTypes, filter }) {
        const options = {
            start,
            limit,
            mediaTypes,
            excludeMediaTypes,
            embedPermissions: true
        };
        if (filter.name === USSAssetFilterName) {
            const ussFilter = filter;
            options.userId = ussFilter.userId;
            options.uploadedFrom = ussFilter.uploadedFrom;
        }
        return this._recentInterface.getRecentAssets(options);
    }
    /**
     * @package -- only for use by USSLoaderHelper
     */
    _postProcessPage(loaderOptions, page) {
        const { items } = page;
        // update modification time of last asset on implicit filter (assets are already sorted by modification time),
        // so that these items can be matched with this filter in ItemLoaderState before adding them in ItemLoaderState._pages
        if (items.length) {
            this._implicitFilter._lastModificationDate = items[items.length - 1].modified;
        }
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class RecentAdobeAssetFilter extends ImplicitAdobeAssetFilterBase {
    constructor() {
        super({ _isSharedWithYou: true });
    }
    get hash() {
        return super.hash;
    }
    matches(item) {
        // We don't want folders to match the Recents filter
        if (item.isFolder) {
            return false;
        }
        if (this._lastModificationDate && item.modified < this._lastModificationDate) {
            return false;
        }
        return super.matches(item);
    }
    getRelationToFilter(other) {
        var _a, _b;
        const otherAdobeAssetFilter = other;
        if (((_a = this._lastModificationDate) === null || _a === void 0 ? void 0 : _a.valueOf()) !== ((_b = otherAdobeAssetFilter._lastModificationDate) === null || _b === void 0 ? void 0 : _b.valueOf())) {
            return 'disjoint';
        }
        return super.getRelationToFilter(other);
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
/**
 * Used by batch operations to retry failed ops and rename the conflicted assets
 * @param resultOps result for batch operation. Contains failed results and new models.
 * @param destination target asset location where the source assets are moved/copied
 * @param conflictMap saves the newName and count used for all files in batch
 * @param conflictRetryOp the original batch op method that is called after renaming conflicted assets
 * @returns the result for batch operation after retrying for failed assets.
 */
async function retryFailedOpsDueToConflict(resultOps, destination, conflictMap, conflictRetryOp) {
    const failedAssetsDueToConflict = [];
    const otherFailedOps = [];
    resultOps.failedResults.forEach((failedOp) => {
        if (failedOp.error.code === SAMErrorCode.CONFLICT) {
            failedAssetsDueToConflict.push(failedOp.model);
        }
        else {
            otherFailedOps.push(failedOp);
        }
    });
    if (failedAssetsDueToConflict.length) {
        for (const [key, value] of conflictMap) {
            conflictMap.set(key, { newName: value.newName, countUsed: value.countUsed + 1 });
        }
        const newResultOps = await conflictRetryOp(failedAssetsDueToConflict, destination, true, conflictMap);
        newResultOps.failedResults = [...newResultOps.failedResults, ...otherFailedOps];
        newResultOps.newModels = [...newResultOps.newModels, ...resultOps.newModels];
        return newResultOps;
    }
    return resultOps;
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class MRUAdobeAssetLoader {
    constructor(assetPool, _core, _recentInterface) {
        this._recentInterface = _recentInterface;
        this._ussLoaderHelper = new USSLoaderHelper(assetPool, _core, this);
    }
    supportsFilter(filter) {
        // If we don't provide a filter at all, then we cannot use the loader
        return filter !== undefined;
    }
    async loadPage(options) {
        return this._ussLoaderHelper.loadPage(options);
    }
    /**
     * @package -- only for use by USSLoaderHelper
     */
    async _getUSSResults({ start, limit, mediaTypes, excludeMediaTypes }) {
        return this._recentInterface.getMRUAssets({
            start,
            limit,
            mediaTypes,
            excludeMediaTypes,
            embedPermissions: true
        });
    }
}

class MRUAdobeAssetFilter extends ImplicitAdobeAssetFilterBase {
    constructor() {
        super({ _isSharedWithYou: true });
    }
    get hash() {
        return `${super.hash}`;
    }
    matches(item) {
        // We don't want folders to match the Recents filter
        if (item.isFolder) {
            return false;
        }
        // do we need to allow certain items up to a threshold in the MRU list
        // as we do in the recents list by keeping record of lastModified
        if (item.lastAccessed === undefined) {
            return false;
        }
        return super.matches(item);
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
function parseLibraryAndElementID(libraryElementID) {
    const parts = libraryElementID.split(':');
    const elementID = parts.pop();
    const libraryID = parts.join(':');
    return [elementID, libraryID];
}
function getLibraryAccessOption(restrictEdit) {
    if (restrictEdit === true) {
        return 'team_view';
    }
    else if (restrictEdit === false) {
        return 'team_edit';
    }
    else {
        return '';
    }
}
class LibraryInterface extends InterfaceBase {
    _getUrl(endpoint) {
        return `https://${this._core.environment.endpoints.MELVILLE_HOST}${endpoint}`;
    }
    async getLibraryData(libraryId) {
        return await this._getResponse(this._getUrl(`/api/v1/libraries/${libraryId}?selector=rendition_grid`), {
            // Ensure browser doesn't automatically add If-None-Match, otherwise
            // service may respond with cached data for a library
            headers: { ['Cache-Control']: 'no-cache, no-store, max-age=0' }
        });
    }
    // Warning: the libraries listing returned from /libraries may have stale data due to USS indexing delays.
    // The exception to this is public libraries as those are fetched from platform.
    // Update only LibrarySpecificData to avoid overwriting RAPI data if you are worried about this.
    async getLibrariesForProject(projectId) {
        const jsonResponse = await this._getResponse(this._getUrl(`/api/v1/libraries?team=${projectId}&selector=rendition_grid`), {
            // Ensure browser doesn't automatically add If-None-Match, otherwise
            // service may respond with cached data for a library
            headers: { ['Cache-Control']: 'no-cache, no-store, max-age=0' }
        });
        return jsonResponse.libraries;
    }
    // The assetId's passed to restoreLibraryElement() and permanentlyDeleteLibraryElement() are in the
    // form 'LIBRARY_CLOUD_ID:ELEMENT_GUID', i.e:
    // 'urn:aaid:sc:us:2be71fea-f24b-4c10-a6a9-4f0926bda772:0fad1009-3bc1-4119-9227-5d1889ffcaf2'
    // I filed https://jira.corp.adobe.com/browse/SAM-2683 to support batch restore/permanentDelete operations which in
    // this workflow would allow us to make just one request to restore/permanentlyDelete multiple CCLibrary Elements that
    // belong to the same CCLibrary.
    async restoreLibraryElement(assetId) {
        const [elementID, libraryID] = parseLibraryAndElementID(assetId);
        const body = { self: { id: libraryID.toString(), elements: [elementID] } };
        await this._doPost(this._getUrl(`/api/v1/libraries/${libraryID}/archive`), { 'Content-Type': 'application/json' }, JSON.stringify(body), [200]);
    }
    async permanentlyDeleteLibraryElement(assetId) {
        const [elementID, libraryID] = parseLibraryAndElementID(assetId);
        await this._doDelete(this._getUrl(`/api/v1/libraries/${libraryID}/archive/${elementID}`), { 'Content-Type': 'application/json' }, [204]);
    }
    async discardLibrary(assetId) {
        await this._doDelete(this._getUrl(`/api/v1/libraries/${assetId}`), { 'Content-Type': 'application/json' }, [204]);
    }
    async restoreLibrary(assetId) {
        const body = { self: `/api/v1/archive/${assetId}` };
        await this._doPost(this._getUrl(`/api/v1/libraries`), { 'Content-Type': 'application/json' }, JSON.stringify(body), [201]);
    }
    async copyLibrary(assetId, destName, parentAssetID, restrictEdit) {
        const accessString = getLibraryAccessOption(restrictEdit);
        const body = { source: assetId, name: destName };
        if (accessString !== '') {
            body['access'] = accessString;
        }
        if (parentAssetID !== undefined) {
            body['parent_assetId'] = parentAssetID;
        }
        const response = await this._doPost(this._getUrl(`/api/v1/libraries`), { 'Content-Type': 'application/json' }, JSON.stringify(body), [200, 201]);
        return await response.json();
    }
    async moveLibrary(assetId, parentAssetID, restrictEdit) {
        const accessString = getLibraryAccessOption(restrictEdit);
        const body = {
            self: assetId,
            parent_assetId: parentAssetID,
            'repo:manifestPatch': [{ 'op': 'replace', 'path': '/id', 'value': assetId }]
        };
        if (accessString !== '') {
            body['access'] = accessString;
        }
        const response = await this._doPost(this._getUrl(`/api/v1/libraries`), { 'Content-Type': 'application/json' }, JSON.stringify(body), [200, 201]);
        return await response.json();
    }
    async createLibrary(libraryName, parentProjectID) {
        const body = { name: libraryName, parent_assetId: parentProjectID };
        const response = await this._doPost(this._getUrl(`/api/v1/libraries`), { 'Content-Type': 'application/json' }, JSON.stringify(body), [201, 200]);
        return await response.json();
    }
    /**
     * Follow aka bookmark one or more libraries.
     * @param bookmarks - array of BookmarkData objects
     * Required properties: In each bookmark, besides the id, you must include either urn for collaborated libraries or url for public libraries.
     * @returns BookmarksResponse - contains all the bookmarks for the user of the same type(s) that were created (public or collab or both)
     */
    async bookmarkLibraries(bookmarks) {
        bookmarks = bookmarks.map(bookmark => {
            if (!bookmark.id || (!bookmark.urn && !bookmark.url) || (bookmark.type === 'collab' && !bookmark.urn) || (bookmark.type === 'public' && !bookmark.url)) {
                throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'BookmarkData must include `id` and either `urn` for collaborated libraries or `url` for public libraries.');
            }
            bookmark.type = bookmark.type || (bookmark.url ? 'public' : 'collab');
            return bookmark;
        });
        const body = { bookmarks };
        // Will return 201 even if the library is already bookmarked
        const response = await this._doPost(this._getUrl(`/api/v1/libraries/bookmarks`), { 'Content-Type': 'application/json' }, JSON.stringify(body), [200, 201]);
        return await response.json();
    }
    /**
     * Get all bookmarks for the user from Melville.
     */
    async getBookmarks() {
        const response = await this._getResponse(this._getUrl(`/api/v1/libraries/bookmarks?useCache=false&ussCache=false`), { headers: { 'Content-Type': JSON_MIME_TYPE, 'Cache-Control': 'no-cache, no-store, max-age=0' } });
        return await response.bookmarks;
    }
    /**
     * Unfollow aka unbookmark the library. (User can always bookmark again as long as they maintain access.)
     */
    async unbookmarkLibrary(bookmarkId) {
        await this._doDelete(this._getUrl(`/api/v1/libraries/bookmarks/${bookmarkId}`), { 'Content-Type': 'application/json' }, [204]);
    }
    /**
     * Set metadata on a library - eg setting roles, renaming, etc.
     */
    async updateMetadata(libraryId, body) {
        await this._doPut(this._getUrl(`/api/v1/libraries/${libraryId}/metadata`), { 'Content-Type': 'application/json' }, JSON.stringify(body), [204, 200]);
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const DefaultPublishedAssetsListingOptions = {
    orderDirection: 'descending',
    orderBy: 'modify_date',
    start: 0,
    limit: 0,
    mediaTypes: [],
    excludeMediaTypes: [],
};
function getDirectionValue$3(userOptions) {
    return userOptions.orderDirection === 'ascending' ? 'asc' : 'desc';
}
class PublishedInterface extends InterfaceBase {
    async getPublishedAssets(options) {
        options = Object.assign(Object.assign({}, DefaultPublishedAssetsListingOptions), options);
        updateAssetTypeForUSS(options.mediaTypes);
        updateAssetTypeForUSS(options.excludeMediaTypes);
        const filters = {
            sort_orderby: options.orderBy,
            hints: {
                acp_platform_v2: true,
            },
            fetch_fields: { includes: ['app_metadata'] },
            op_or: [
                {
                    [SearchProps.CREATIVE_CLOUD_TOPLEVEL_COLLECTION_NAME]: 'pubs'
                },
                {
                    [SearchProps.OP_AND]: [
                        {
                            [SearchProps.CREATIVE_CLOUD_TOPLEVEL_COLLECTION_NAME]: 'cloud-content'
                        },
                        {
                            [SearchProps.FIELD_EXISTS]: ['app_metadata$$review:sourceAsset$$dc:format']
                        }
                    ],
                }
            ],
            op_none_of: [
                {
                    [SearchProps.TYPE]: options.excludeMediaTypes || [],
                },
            ],
            [SearchProps.STORAGE_ASSIGNEE_ID]: this._core.environment.getValue('USER_ID'),
            asset_id_opacity: true,
            [SearchProps.TYPE]: options.mediaTypes || [],
            sort_order: getDirectionValue$3(options),
            limit: options.limit,
            start_index: options.start,
        };
        return await this._core.hostAssetContext.ussInterface.getQueryResponse(filters);
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class PublishedAdobeAssetLoader {
    constructor(assetPool, core, _publishedInterface) {
        this._publishedInterface = _publishedInterface;
        this._ussLoaderHelper = new USSLoaderHelper(assetPool, core, this);
    }
    supportsFilter(filter) {
        // If we don't provide a filter at all, then we cannot use the loader
        return filter !== undefined;
    }
    async loadPage(options) {
        return this._ussLoaderHelper.loadPage(options);
    }
    /**
     * @package -- only for use inside USSLoaderHelper
     */
    async _getUSSResults({ start, limit, mediaTypes, excludeMediaTypes, order }) {
        return this._publishedInterface.getPublishedAssets({
            orderBy: OrderFieldToIUSSAdobeAssetOrderBy[order.field],
            orderDirection: order.direction,
            start,
            limit,
            mediaTypes,
            excludeMediaTypes
        });
    }
    /**
     * @package -- only for use inside USSLoaderHelper
     */
    _getItemData(item) {
        return Object.assign(Object.assign({}, item), { published: true });
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class PublishedAdobeAssetFilter extends ImplicitAdobeAssetFilterBase {
    constructor() {
        super({ _isPublished: true });
    }
}

/*************************************************************************
*   ADOBE CONFIDENTIAL
*
*   Copyright 2023 Adobe
*   All Rights Reserved.
*
*   NOTICE:  All information contained herein is, and remains
*   the property of Adobe and its suppliers, if any. The intellectual
*   and technical concepts contained herein are proprietary to Adobe
*   and its suppliers and are protected by all applicable intellectual
*   property laws, including trade secret and copyright laws.
*   Dissemination of this information or reproduction of this material
*   is strictly forbidden unless prior written permission is obtained
*   from Adobe.
**************************************************************************/
class DiscardedAdobeAssetFilter extends ImplicitAdobeAssetFilterBase {
    constructor() {
        super({
            _isDiscarded: true
        });
    }
    matches(item) {
        if (this.isSyncedAsset(item) || this.isMobileCreation(item)) {
            return false;
        }
        // TODO: We should also handle this in the USS query when it's supported, but keep this check here for good measure.
        // https://jira.corp.adobe.com/browse/SEARCH-41079
        if (!item.hasPermission(Permission.Restore) && !item.hasPermission(Permission.Delete)) {
            return false;
        }
        return super.matches(item);
    }
    isSyncedAsset(item) {
        return item.repoPath.startsWith('/files/');
    }
    isMobileCreation(item) {
        return item.repoPath.startsWith('/assets/') && !item.repoPath.startsWith('/assets/adobe-libraries/');
    }
}

/*************************************************************************
*   ADOBE CONFIDENTIAL
*
*   Copyright 2023 Adobe
*   All Rights Reserved.
*
*   NOTICE:  All information contained herein is, and remains
*   the property of Adobe and its suppliers, if any. The intellectual
*   and technical concepts contained herein are proprietary to Adobe
*   and its suppliers and are protected by all applicable intellectual
*   property laws, including trade secret and copyright laws.
*   Dissemination of this information or reproduction of this material
*   is strictly forbidden unless prior written permission is obtained
*   from Adobe.
**************************************************************************/
const DEFAULT_OPTIONS = {
    limit: 50,
    start: 0,
    orderDirection: 'descending',
    orderBy: 'modified',
    mediaTypes: [],
    excludeMediaTypes: []
};
const DISCARD_FETCH_FIELDS = [
    'creative_cloud_colortheme',
    'creative_cloud_gradient',
    '_embedded'
];
const MOBILE_CREATIONS_MIMETYPES = [
    'application/vnd.adobe.draw.project+dcx',
    'application/vnd.adobe.sketch.project+dcx',
    'application/vnd.adobe.photoshop-mix.composition+dcx',
    'application/vnd.adobe.photoshop-fix.composition+dcx',
    'application/vnd.adobe.line.sketchbook+dcx',
    'application/vnd.adobe.layup.project+dcx',
    'application/vnd.adobe.comp.project+dcx',
    'application/vnd.adobe.clip.project+dcx',
    'application/vnd.adobe.clsproject+dcx',
    'image/vnd.adobe.cls+dcx'
];
class DiscardedInterface extends InterfaceBase {
    async getDiscardedAssets(options) {
        options = Object.assign(Object.assign({}, DEFAULT_OPTIONS), options);
        updateAssetTypeForUSS(options.mediaTypes);
        updateAssetTypeForUSS(options.excludeMediaTypes);
        const sort_order = options.orderDirection === 'ascending' ? 'asc' : 'desc';
        const sort_orderby = OrderByToUSSOrderBy[options.orderBy || 'modified'];
        const userId = this._core.environment.getValue('USER_ID');
        const filters = {
            hints: { acp_platform_v2: true },
            creative_cloud_archive: true,
            creative_cloud_discarded_directly: true,
            [SearchProps.TYPE]: options.mediaTypes || [],
            fetch_fields: { includes: [
                    ...getUSSFetchFields(),
                    ...DISCARD_FETCH_FIELDS
                ] },
            // Asset must either be owned by the current user, or a Library (composite) that exists in /projects
            // and the current user has write access to it.
            op_or: [
                { [SearchProps.STORAGE_ASSIGNEE_ID]: userId },
                { op_and: [
                        { [SearchProps.STORAGE_ASSIGNEE_TYPE]: 'org' },
                        { [SearchProps.TYPE]: ['application/vnd.adobe.library+dcx'] },
                        {
                            [SearchProps.REPO_ACL_MODIFIER]: 'grant',
                            [SearchProps.REPO_ACL_PRIVILEGES]: ['write'],
                            [SearchProps.REPO_ACL_RELATIONS]: [RelType.PRIMARY]
                        }
                    ] },
                {
                    // we just want the projects that are either owned by the current user
                    // or are in shared-storage
                    op_and: [
                        { op_or: [
                                { [SearchProps.STORAGE_ASSIGNEE_ID]: userId },
                                { [SearchProps.STORAGE_ASSIGNEE_TYPE]: 'org' }
                            ] },
                        { op_field_exists: [
                                'app_metadata$$cc:inheritable$$cc:team$$repo:assetId',
                                'app_metadata$$cc:inherited$$cc:team$$repo:assetId',
                            ] }
                    ]
                }
            ],
            op_none_of: [
                { [SearchProps.TYPE]: options.excludeMediaTypes || [] },
                { [SearchProps.VISIBLITY]: ['hiddenSelf', 'hiddenRecursive'] },
                { [SearchProps.FIELD_EXISTS]: ['app_metadata$$review:sourceAsset$$dc:format'] },
                { [SearchProps.CREATIVE_CLOUD_TOPLEVEL_COLLECTION_NAME]: ['archive', 'files'] },
                { [SearchProps.TYPE]: MOBILE_CREATIONS_MIMETYPES }
            ],
            sort_orderby,
            sort_order,
            limit: options.limit,
            start_index: options.start
        };
        return this._core.hostAssetContext.ussInterface.getQueryResponse(filters);
    }
}

/*************************************************************************
*   ADOBE CONFIDENTIAL
*
*   Copyright 2023 Adobe
*   All Rights Reserved.
*
*   NOTICE:  All information contained herein is, and remains
*   the property of Adobe and its suppliers, if any. The intellectual
*   and technical concepts contained herein are proprietary to Adobe
*   and its suppliers and are protected by all applicable intellectual
*   property laws, including trade secret and copyright laws.
*   Dissemination of this information or reproduction of this material
*   is strictly forbidden unless prior written permission is obtained
*   from Adobe.
**************************************************************************/
class DiscardedAdobeAssetLoader {
    constructor(assetPool, _core, _discardedInterface) {
        this._core = _core;
        this._discardedInterface = _discardedInterface;
        this._ussLoaderHelper = new USSLoaderHelper(assetPool, _core, this);
    }
    supportsFilter(filter) {
        return filter !== undefined;
    }
    async loadPage(options) {
        if (!this._libraryContext) {
            this._libraryContext = await this._core.getLibraryContext();
        }
        // Check if the user can restore the project in the current repository
        // TODO: Not sure how to handle this for the Admin Console deleted view (if there is one)
        // Maybe we need to do this for the list of repositories the user has access to
        const repositories = await this._core.hostAssetContext._getListOfRepositoryIDs();
        repositories === null || repositories === void 0 ? void 0 : repositories.forEach(repository => this._core.projectContext._canRestoreProject(repository));
        return this._ussLoaderHelper.loadPage(options);
    }
    _getUSSResults({ start, limit, mediaTypes, excludeMediaTypes, order }) {
        return this._discardedInterface.getDiscardedAssets({
            start,
            limit,
            orderBy: order.field,
            orderDirection: order.direction,
            mediaTypes,
            excludeMediaTypes
        });
    }
    _getItemData(item) {
        // TODO: come up with proposal for Melville to provide rendering service for non-image LEs
        // Colors/gradients have RENDITION links that cause 404 error. Clear them out so that
        // AdobeAsset does not try to fetch their renditions.
        var _a;
        const LERenditionURL = item.creative_cloud_colortheme || item.creative_cloud_gradient
            ? undefined
            : (_a = item._links) === null || _a === void 0 ? void 0 : _a[RelType.RENDITION];
        const isArchivedLibraryElement = item.asset_sub_type === 'element';
        if (isArchivedLibraryElement) {
            item._links = Object.assign(Object.assign({}, (item._links || {})), { [RelType.RENDITION]: LERenditionURL });
        }
        return Object.assign(Object.assign({}, item), { isArchivedLibraryElement });
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class AdobeAssetGlobalSearchFilter extends ImplicitAdobeAssetFilterBase {
    constructor(options) {
        super({ _isSharedWithYou: true });
        this._query = '';
        /**
         * @package only for use by AdobeAssetGlobalSearchFilter/Loader
         */
        this._projectsOnly = false;
        this._query = (options === null || options === void 0 ? void 0 : options._query) || '';
        this._projectsOnly = (options === null || options === void 0 ? void 0 : options._projectsOnly) || false;
    }
    get hash() {
        return [
            super.hash,
            this.query ? `query=${this._query}` : undefined,
            this.projectsOnly ? `projects_only=${this._projectsOnly}` : undefined,
        ].filter(x => x).join(':');
    }
    get query() {
        return this._query;
    }
    get projectsOnly() {
        return this._projectsOnly;
    }
    // The matching rules USS are deceptively complex, and could change over time. Attempting to
    // replicate here could result in items being incorrectly filtered out or incorrectly included.
    // Global search Lists should not try to share items from Lists with similar queries, other
    // than treating the queries as case-insensitive.
    matches(item) {
        // Check if the item's id is in the current filter's id list
        if (this._matchingIDsFromSearch && !this._matchingIDsFromSearch.has(item.id)) {
            return false;
        }
        // If the query exists but there's no id list, we probably haven't finished loading yet
        if ((this._query) && !this._matchingIDsFromSearch) {
            return false;
        }
        return super.matches(item);
    }
    getRelationToFilter(other) {
        var _a, _b;
        if (((_a = this.query) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== ((_b = other.query) === null || _b === void 0 ? void 0 : _b.toLowerCase())) {
            return 'disjoint';
        }
        return super.getRelationToFilter(other);
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
/**
 * Manager for observability via MobX - classes should call reportObserved
 * for any getters or methods that should be observable if used in the context
 * of MobX. Since SAM.js does not include MobX, clients need to makeObservable()
 * with their MobX instance
 */
class MobX {
    constructor() {
        this._contexts = new Map();
    }
    /**
     * Register a MobX instance to make SAM.js observable.
     * Returns a function that can be called to unregister.
     */
    register(createAtom) {
        // It can be the case the register is called multiple times with the same createAtom function,
        // which can happen if multiple components are using SAM.js but sharing the same MobX instance.
        // In this case, we don't want to create more atoms than necessary since that would cause performance
        // problems - instead, we detect if the createAtom was already registered, and just keep a reference
        // count instead of registering it multiple times.
        let instanceInfo = this._contexts.get(createAtom);
        if (!instanceInfo) {
            instanceInfo = { id: samJsUuid(), references: new Set() };
            this._contexts.set(createAtom, instanceInfo);
        }
        const refId = samJsUuid();
        instanceInfo.references.add(refId);
        return () => {
            const instanceInfo = this._contexts.get(createAtom);
            if (instanceInfo) {
                instanceInfo.references.delete(refId);
                if (instanceInfo.references.size === 0) {
                    this._contexts.delete(createAtom);
                }
            }
        };
    }
    /**
     * Called to report an observation on an object. Any reaction that observes this
     * will be triggered whenever the given change event on the object triggers.
     * In the simple case where we only have one change event for an object ('change'),
     * getters and methods can be instrumented with just environment.mobx.reportObserved(this).
     */
    reportObserved(object, event = 'change') {
        for (const [createAtom, { id }] of this._contexts) {
            const key = `_atom_${id}_${event}`;
            let atom = object[key];
            if (!atom) {
                const onChange = () => atom.reportChanged();
                atom = createAtom(`${object.constructor.name}_${event}`, () => {
                    object.on(event, onChange);
                }, () => {
                    object.off(event, onChange);
                });
                object[key] = atom;
            }
            atom.reportObserved();
        }
    }
}

// Utility for combining multiple signals:
const combineSignals = (options, ...signals) => {
    const controller = new AbortController();
    const removeListeners = () => {
        signals.forEach(signal => signal.removeEventListener('abort', onAbort));
    };
    const onAbort = () => {
        controller.abort();
        removeListeners();
    };
    if (signals.some(signal => signal.aborted)) {
        onAbort();
    }
    else {
        signals.forEach(signal => signal.addEventListener('abort', onAbort));
    }
    options.signal = controller.signal;
    return removeListeners;
};
var Env;
(function (Env) {
    Env[Env["Stage"] = 0] = "Stage";
    Env[Env["Production"] = 1] = "Production";
})(Env || (Env = {}));
const DEFAULT_HOST_PRODUCT_DATA = {
    PRODUCT_ID: '',
    PRODUCT_LOCATION: ''
};
// Default settings
const DEFAULT_CONFIG = {
    USER_ID: '',
    USER_EMAIL: '',
    CLIENT_ID: '',
    ACCESS_TOKEN: '',
    ENVIRONMENT: Env.Production,
    HOST_PRODUCT_DATA: DEFAULT_HOST_PRODUCT_DATA,
    FETCH_REQUEST_TIMEOUT: 60 * 1000,
    ASYNC_DEFAULT_DELAY: 1 * 1000,
    CACHE_DEFAULT_TTL: 30 * 24 * 60 * 60 * 1000,
    MIN_RETRY_INTERVAL: 1 * 1000,
    MAX_RETRY_INTERVAL: 2 * 60 * 1000,
    ONS_DOCUMENT_VISIBILITY_TIMEOUT: 10000,
    ONS_DEFAULT_MAX_SUBSCRIPTIONS: 5,
    ONS_DEFAULT_MAX_ASSETS_PER_SUBSCRIPTION: 100,
    INDEXED_DB_VERSION: 1,
    PROJECTS_LIST_UPDATE_LATENCY: 3000,
    PROJECTS_ITEM_UPDATE_LATENCY: 1000,
    CLASSROOMS_LIST_UPDATE_LATENCY: 3000,
    DOVETAIL_LIST_UPDATE_LATENCY: 3000,
    ASSETS_LIST_UPDATE_LATENCY: 100,
    USS_LIST_UPDATE_LATENCY: 3000,
    VERSIONS_LIST_UPDATE_LATENCY: 100,
    ASSET_COPY_NAME_FORMAT: '%@ copy (%d)',
    ASSET_CONFLICT_NAME_FORMAT: '%@ (%d)',
    DISABLE_ONS: false,
    ENABLE_ONS_CACHE: false,
    _VERSION_OVERRIDE_FOR_TEST: '',
    HIGH_PRIORITY_RAPI_REQUESTS: false,
    LOCALE: 'en-US',
    USE_SECONDARY_ACCOUNT: false,
    SESSION_TYPE: 'logged-in'
};
const ENVIRONMENTS = {
    [Env.Stage]: {
        LABEL: 'Staging',
        ACP_CS_HOST: 'platform-cs-stage.adobe.io',
        SYNC_SERVICE_HOST: 'acp-ss-stage.adobe.io',
        PROJECTS_HOST: 'ccprojects-stage.adobe.io',
        ASSIGNMENTS_HOST: 'hume-stage.adobe.io',
        INVITATIONS_HOST: 'invitations-stage.adobe.io',
        TRANSFER_SERVICE_HOST: 'ats-stage.adobe.io',
        USS_HOST: 'adobesearch-stage.adobe.io',
        XDCE_HOST: 'xdce-stage.adobe.io',
        REVIEW_SERVICE_HOST: 'ccassetreview-stage.adobe.io',
        MELVILLE_HOST: 'ccx-melville-stage.adobe.io',
        // TODO: get final endpoint that doesn't reference ax-projects
        USSv3_HOST: 'ax-projects-search-stage.adobe.io',
        PPS_HOST: 'pps-stage.adobe.io',
        BEHANCE_HOST: 'cc-api-behance-stage.adobe.io',
        CLASSROOMS_HOST: 'edu-classrooms-stage.adobe.io',
        DOVETAIL_HOST: 'dovetail-services-stage.adobe.io',
        COMMENT_HOST: 'comments-stage.adobe.io'
    },
    [Env.Production]: {
        LABEL: 'Production',
        ACP_CS_HOST: 'platform-cs.adobe.io',
        SYNC_SERVICE_HOST: 'acp-ss.adobe.io',
        PROJECTS_HOST: 'ccprojects.adobe.io',
        ASSIGNMENTS_HOST: 'hume.adobe.io',
        INVITATIONS_HOST: 'invitations.adobe.io',
        TRANSFER_SERVICE_HOST: 'ats.adobe.io',
        USS_HOST: 'adobesearch.adobe.io',
        XDCE_HOST: 'xdce.adobe.io',
        REVIEW_SERVICE_HOST: 'ccassetreview.adobe.io',
        MELVILLE_HOST: 'libraries.adobe.io',
        // TODO: get final endpoint that doesn't reference ax-projects
        USSv3_HOST: 'ax-projects-search.adobe.io',
        PPS_HOST: 'pps.adobe.io',
        BEHANCE_HOST: 'cc-api-behance.adobe.io',
        CLASSROOMS_HOST: 'edu-classrooms.adobe.io',
        DOVETAIL_HOST: 'dovetail-services.adobe.io',
        COMMENT_HOST: 'comments-stage.adobe.io'
    }
};
const isNonEmptyString = (x) => (typeof x === 'string') && Boolean(x);
const isNumber = (x) => (typeof x === 'number');
const configItemValidators = [{
        key: 'CLIENT_ID',
        validator: isNonEmptyString
    }, {
        key: 'USER_ID',
        validator: isNonEmptyString
    }, {
        key: 'ENVIRONMENT',
        validator: (x) => isNumber(x) && (x in Env)
    }, {
        key: 'HOST_PRODUCT_DATA',
        validator(hostProductData) {
            if (!hostProductData) {
                return false;
            }
            const { PRODUCT_ID } = hostProductData;
            // required for USS queries
            return isNonEmptyString(PRODUCT_ID);
        }
    }];
// Only allow one retry on a 401 response to avoid getting into an infinite loop if we keep being given an invalid access token
const MAX_401_RETRIES = 1;
class Environment {
    constructor(_core, userConfig, _hooks) {
        this._core = _core;
        this._hooks = _hooks;
        this._isTokenInvalid = true;
        this.mobx = new MobX;
        const config = Object.assign({}, DEFAULT_CONFIG, userConfig);
        const invalidOptions = configItemValidators.filter(({ key, validator }) => !validator(config[key]));
        if (invalidOptions.length) {
            const invalidKeys = invalidOptions.map(({ key }) => key).join(', ');
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, `Invalid required options: ${invalidKeys}`);
        }
        if (config.ACCESS_TOKEN) {
            this._isTokenInvalid = false;
        }
        else if (!_hooks.refreshAccessToken) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, `refreshAccessToken hook must be provided if ACCESS_TOKEN is not set`);
        }
        this._config = config;
        this._cache = new Cache(config.USER_ID, _core, _hooks.persistentCache);
    }
    get cache() {
        return this._cache;
    }
    getValue(key) {
        return this._config[key];
    }
    /**
     * Set the environment global configuration value.
     *
     * @param key Configuration KeyName
     * @param value The value of the Configuration.
     * @event IEvent Dispatches an event when a particular key has changed.
     */
    setValue(key, value) {
        this._config[key] = value;
        const event = {
            type: CONFIG_CHANGE
        };
        const eventPayload = {
            configurationKeyName: key
        };
        this._core.dispatch(event, eventPayload);
    }
    get endpoints() {
        const env = this._config.ENVIRONMENT;
        return ENVIRONMENTS[env];
    }
    // Returns a unique device id for the client - this can be provided as a config option, or a guid can
    // be generated (which will be cached across instances for the same user). Note that if a value is passed in,
    // this takes precedence even if there's a cached device id.
    async getDeviceId() {
        return this._config['DEVICE_ID'] || await this.cache.getItem('DEVICE_ID', async () => samJsUuid(), (value) => typeof value === 'string');
    }
    getUserId() {
        return this.getValue('USER_ID');
    }
    _loggerEvent(message, level) {
        if (this._core.listenerCount(MESSAGE_LOGGED) > 0) {
            this._core.emit(MESSAGE_LOGGED, new MessageLoggedEvent(message, level));
        }
    }
    // Reserve for informational messages that may be useful in debugging infrequent but major
    // SAM.js system events, such as initializing, or refreshing auth token.
    log(message) {
        this._loggerEvent(message, LogLevel.Info);
    }
    // Reserve for detailed messages about complex coordinated async processes, such as List loading, that
    // are difficult to trace without log messages, but are too noisy to include in regular production apps.
    debug(message) {
        this._loggerEvent(message, LogLevel.Debug);
    }
    // For error conditions that do not warrant throwing an exception that bubbles to the client, but could be
    // useful for debugging purposes. E.g., an ItemUpdater failing to parse a field from a service response --
    // devs should see and fix these errors immediately and have unit tests to validate it, but the same error
    // in a production system should not necessarily be raised to the user.
    error(message) {
        this._loggerEvent(message, LogLevel.Err);
    }
    // For deprecation notices and possibly (but not definitively) bad configuration options, etc. These sorts of
    // messages should include instructions on how to resolve the issue. E.g. how React treats warnings about missing
    // `key` on children -- this isn't necessarily a breaking condition but often can cause bugs; since they are
    // entirely avoidable, devs should work to eradicate the warnings so they do not become red herrings that must
    // be investigated when an unrelated bug emerges.
    warn(message) {
        this._loggerEvent(message, LogLevel.Warn);
    }
    async _refreshAccessToken() {
        if (this._config.SESSION_TYPE !== 'logged-out' && this._isTokenInvalid) {
            if (!this._hooks.refreshAccessToken) {
                // If a refresh hook wasn't provided, we just clear the token so we don't keep on
                // attempting requests with an invalid token
                return '';
            }
            this.log(`Refreshing access token from host...`);
            try {
                this._config.ACCESS_TOKEN = await this._hooks.refreshAccessToken(this._config.ACCESS_TOKEN);
            }
            catch (e) {
                // The error could already be a SAM error due to the recursive fetch calls above.
                throw SAMError.wrapErrorIfNeeded(SAMErrorCode.AUTHORIZATION_ERROR, 'Failed to refresh access token', e);
            }
            this._isTokenInvalid = false;
            this.log(`Refreshing access token ${this._config.ACCESS_TOKEN ? 'successful' : 'unsuccessful'}`);
        }
        return this._config.ACCESS_TOKEN;
    }
    async fetch(url, options = {}, numRetries = 0) {
        if (this._hooks.fetch) {
            return this._hooks.fetch(url, options);
        }
        const session = this._config.SESSION_TYPE;
        // If the access token is invalid, we wait for it to be refreshed:
        const token = this._isTokenInvalid ? await this._refreshAccessToken() : this._config.ACCESS_TOKEN;
        if ((session !== 'logged-out') && !token) {
            throw SAMError.create(SAMErrorCode.AUTHORIZATION_ERROR, `Unable to obtain access token - did you provide a refreshAccessToken hook?`);
        }
        // Extract parameters that won't be passed to `fetch`, and clone the ones that will so that a) there are no
        // side-effects to the caller, and b) 401 retries can use the same `options` as the original request.
        const { headers = {}, authHeader, noOtherHeaders, requestTimeoutMs } = options, requestInit = __rest(options, ["headers", "authHeader", "noOtherHeaders", "requestTimeoutMs"]);
        // ONS uses a different auth header from other services, so this needs to be configurable
        const authorizationHeader = 'Authorization';
        const auth = (session !== 'logged-out')
            ? { [authHeader || authorizationHeader]: `${(session === 'guest') ? 'AnonBearer' : 'Bearer'} ${token}` }
            : {};
        // After this line, `requestHeaders` and `headers` should not be modified to avoid side-effects for the caller.
        // deepClone removes headers with undefined values -- this makes it easier to assemble Headers where some
        // headers could be optional.
        const requestHeaders = noOtherHeaders ? deepClone(headers) : Object.assign(Object.assign(Object.assign({}, deepClone(headers)), auth), { 'X-Api-Key': this._config.CLIENT_ID });
        // Include a timeout on the request (fetch doesn't have a timeout, so it will never resolve if the socket hangs up)
        const abortController = new AbortController();
        let uncombineSignals = () => { };
        if (requestInit.signal) {
            uncombineSignals = combineSignals(requestInit, requestInit.signal, abortController.signal);
        }
        else {
            requestInit.signal = abortController.signal;
        }
        const timeoutLength = requestTimeoutMs || this.getValue('FETCH_REQUEST_TIMEOUT');
        const timeout = setTimeout(() => abortController.abort(), timeoutLength);
        const startTime = Date.now();
        try {
            const response = await fetch(url, Object.assign(Object.assign({}, requestInit), { headers: requestHeaders }));
            const requestId = response.headers.get('X-Request-Id');
            const requestIdString = requestId || `no requestId`;
            this.debug(`[${requestIdString}] ${options.method || 'GET'} ${url} ${response.status} (${Date.now() - startTime}ms)`);
            clearTimeout(timeout);
            if (response.status === 401) {
                // If we get a 401 response it usually means that access token has expired.
                if (token !== this._config.ACCESS_TOKEN) {
                    // If the access token was already refreshed during the request, just retry and
                    // it will pick up the new token.
                    // NOTE: We are not using await here to avoid capturing the errors in the parent try block.
                    // NOTE: using `options` instead of `requestInit` so the retry will use the same settings as the original request.
                    return this.fetch(url, options, numRetries + 1);
                }
                else {
                    // Otherwise it means we discovered the current token is invalid. We need to invalidate the token and retry. The retry
                    // will detect that the token is invalid and refresh it (if the host has provided a hook).
                    this._isTokenInvalid = true;
                    if (numRetries < MAX_401_RETRIES) {
                        // NOTE: We are not using await here to avoid capturing the errors in the parent try block.
                        // NOTE: using `options` instead of `requestInit` so the retry will use the same settings as the original request.
                        return this.fetch(url, options, numRetries + 1);
                    }
                }
            }
            return response;
        }
        catch (e) {
            this.debug(`Error - ${e} (${Date.now() - startTime}ms)`);
            throw SAMError.wrapErrorIfNeeded(SAMErrorCode.NETWORK_ERROR, `Failed network request when fetching: ${url}`, e);
        }
        finally {
            // If we combined signals, we need to uncombine after the request is done, otherwise we'll continue to add
            // event listeners to the original signal and hit the maximum limit. This would both be a memory leak and
            // result in some aborts not being handled.
            uncombineSignals();
        }
    }
    // Returns a retry time based on the retry attempt number.
    // For now, this uses the minimum retry interval a few times, before jumping to the maximum interval.
    // We may want to change this to an exponential or Fibonacci backoff in future.
    getRetryInterval(attempt) {
        // We retry quickly a few times on failure, before backing off to a slower interval:
        return this.getValue(attempt < 3 ? 'MIN_RETRY_INTERVAL' : 'MAX_RETRY_INTERVAL');
    }
}
__decorate([
    memoize
], Environment.prototype, "getDeviceId", null);
__decorate([
    synchronized
], Environment.prototype, "_refreshAccessToken", null);

/*************************************************************************
*   ADOBE CONFIDENTIAL
*
*   Copyright 2023 Adobe
*   All Rights Reserved.
*
*   NOTICE:  All information contained herein is, and remains
*   the property of Adobe and its suppliers, if any. The intellectual
*   and technical concepts contained herein are proprietary to Adobe
*   and its suppliers and are protected by all applicable intellectual
*   property laws, including trade secret and copyright laws.
*   Dissemination of this information or reproduction of this material
*   is strictly forbidden unless prior written permission is obtained
*   from Adobe.
**************************************************************************/
const DEFAULT_MAX_LIMIT = 50;
// These are for USS internal tracing and are not secrets
const STAGE_APP_TOKEN = '4c7f3f73-27a6-48f9-8447-d2c8404661fa';
const PROD_APP_TOKEN = '4c988f33-6435-4469-b37b-7dbe33687bd8';
const USS_CONTENT_TYPE = 'application/vnd.adobe.search-request+json';
const DEFAULT_EXPERIENCE_ID = 'Default-Global-Search-Experience';
class USSv3Interface extends InterfaceBase {
    constructor() {
        super(...arguments);
        this.queryPath = '/uss/v3/query';
    }
    async getQueryResponse(queryEntry) {
        const url = `https://${this._core.environment.endpoints.USSv3_HOST}${this.queryPath}`;
        const queryEntryWithDefaults = Object.assign({ 'id': 'query1', 'scope': {
                'entities': ['AcpAsset']
            }, 'fetchFields': [FetchFields.SharedBy, FetchFields.LastAccessDate, FetchFields.CCInheritable, FetchFields.CCInherited, FetchFields.HzDocumentMetadata] }, queryEntry);
        const query = {
            experienceId: DEFAULT_EXPERIENCE_ID,
            queries: [queryEntryWithDefaults]
        };
        const response = await this._doPost(url, {
            'X-App-Token': this._core.environment.getValue('ENVIRONMENT') === Env.Stage
                ? STAGE_APP_TOKEN
                : PROD_APP_TOKEN,
            'content-type': USS_CONTENT_TYPE,
        }, JSON.stringify(query), [200]);
        return response.json();
    }
}

const DEFAULT_LIMIT = 50;
/**
 * AdobeAsset Loader classes should use this class to do the page loading. Using composition rather
 * than inheritance because a base USSLoader class was causing circular dependencies at runtime that
 * would cause the base USSLoader class to be undefined when the Loader class that was extending it
 * was declared.
 */
class USSv3LoaderHelper {
    constructor(_assetPool, _core, _loader) {
        this._assetPool = _assetPool;
        this._core = _core;
        this._loader = _loader;
        //curry this function to get the user id here
        this._getItemData = this._getItemDataWithUserId(this._core.environment.getUserId());
    }
    async loadPage(options, start) {
        var _a, _b;
        const { order, limit = DEFAULT_LIMIT, filter, implicitFilter } = options;
        const latency = this._core.environment.getValue('USS_LIST_UPDATE_LATENCY');
        const mostRecentValidMutation = Date.now() - latency;
        start = start || 0;
        const { mediaTypes, excludeMediaTypes, excludeInheritanceTypes } = filter;
        const result = await this._loader._getUSSResults({
            order,
            limit,
            filter,
            implicitFilter,
            start,
            mediaTypes: Array.from(mediaTypes !== null && mediaTypes !== void 0 ? mediaTypes : []),
            excludeMediaTypes: Array.from(excludeMediaTypes !== null && excludeMediaTypes !== void 0 ? excludeMediaTypes : []),
            excludeInheritanceTypes: Array.from(excludeInheritanceTypes !== null && excludeInheritanceTypes !== void 0 ? excludeInheritanceTypes : [])
        });
        const queryResult = result.queryResults[0];
        // TODO: it'd be nice if USS returned empty array when there are no matches
        const resultItems = queryResult.items || [];
        const session = this._assetPool.createLoaderUpdateSession(mostRecentValidMutation);
        const items = this._loader._extractItems ? this._loader._extractItems(session, resultItems) : this._extractItems(session, resultItems);
        let loadNextPage;
        const total = queryResult.metadata.totalHits || 0;
        const pageItemCount = resultItems.length;
        // [SEARCH-30755] -- May be caused by USS filtering out items due to permissions for the
        // current user, which aren't subtracted from the total. Try to ensure that the Loader paginates
        // as though the total_items is correct.
        const nextStartIndex = (pageItemCount === 0 ? start + limit : start + pageItemCount);
        if (nextStartIndex < total) {
            loadNextPage = (options) => this.loadPage(options, nextStartIndex);
        }
        const page = {
            items,
            total,
            loadNextPage,
            mostRecentValidMutation
        };
        // allow Loader to do additional internal state management (optional)
        (_b = (_a = this._loader)._postProcessPage) === null || _b === void 0 ? void 0 : _b.call(_a, options, page);
        session.commit();
        return page;
    }
    _getItemDataWithUserId(userId) {
        return (item) => {
            var _a, _b;
            return Object.assign(Object.assign(Object.assign(Object.assign({}, item.rm), { 
                // cm.sharedBy is an array of user IDs that have shared the asset
                // assuming that it is an ordered array, the first item is the first sharer
                // Even after this assumption we can't be sure that first sharer is the owner
                // We will need more info to figure out whether the asset is sharedViaInvite.
                // For example consider an asset inside a project that is not owned by the user
                // and that asset is 1-off shared with some user C by user B.
                // Now user A does not know whether this asset is shared via invite with him because
                // he is not the owner of the asset and cm.sharedBy will have user B's ID but it is not
                // shared via invite with user A.
                sharedViaInvite: ((_a = item.cm) === null || _a === void 0 ? void 0 : _a.sharedBy.length) > 0 && item.cm.sharedBy[0] !== userId, isShared: ((_b = item.cm) === null || _b === void 0 ? void 0 : _b.sharedBy.length) > 0 }), (item.am && { _embedded: { [RelType.APP_METADATA]: item.am } })), { 
                // No need to refresh collaborators for share flags, directCollaboratorCount, since they are computed
                // above. Not clearing the collaboratorDataIsDirty flag, because we cannot populate collaboratorArray
                // from USS.
                shareFlagsAreDirty: false });
        };
    }
    _extractItems(session, items) {
        var _a, _b;
        return this._core._adobeAssetFactory.assetsFromUSSV3AppMetadata(session, items, (_a = this._loader._getItemData) !== null && _a !== void 0 ? _a : this._getItemData, (_b = this._loader) === null || _b === void 0 ? void 0 : _b._libraryContext);
    }
}

/*************************************************************************
*   ADOBE CONFIDENTIAL
*
*   Copyright 2023 Adobe
*   All Rights Reserved.
*
*   NOTICE:  All information contained herein is, and remains
*   the property of Adobe and its suppliers, if any. The intellectual
*   and technical concepts contained herein are proprietary to Adobe
*   and its suppliers and are protected by all applicable intellectual
*   property laws, including trade secret and copyright laws.
*   Dissemination of this information or reproduction of this material
*   is strictly forbidden unless prior written permission is obtained
*   from Adobe.
**************************************************************************/
class GlobalSearchLoader {
    constructor(assetPool, _core, _globalSearchInterface, _implicitFilter) {
        this._core = _core;
        this._globalSearchInterface = _globalSearchInterface;
        this._implicitFilter = _implicitFilter;
        this._ussLoaderHelper = new USSv3LoaderHelper(assetPool, this._core, this);
    }
    supportsFilter(filter) {
        return filter !== undefined;
    }
    async loadPage(options) {
        if (!this._libraryContext) {
            this._libraryContext = await this._core.getLibraryContext();
        }
        return this._ussLoaderHelper.loadPage(options);
    }
    _getUSSResults({ start, limit = DEFAULT_MAX_LIMIT, order, mediaTypes, excludeMediaTypes, excludeInheritanceTypes, }) {
        const { _query: query, _projectsOnly: projectsOnly } = this._implicitFilter;
        return this._globalSearchInterface.globalSearch({
            start,
            limit,
            query,
            order,
            mediaTypes: [...(mediaTypes || [])],
            excludeMediaTypes: [...(excludeMediaTypes || [])],
            excludeInheritanceTypes: [...(excludeInheritanceTypes || [])],
            projectsOnly
        });
    }
    _postProcessPage(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { filter }, { items }) {
        this._implicitFilter._matchingIDsFromSearch = new Set([
            // copy existing
            ...(this._implicitFilter._matchingIDsFromSearch || []),
            ...(items.map(item => item.id))
        ]);
    }
}

class GlobalSearchInterface extends InterfaceBase {
    constructor(_core, _ussInterface) {
        super(_core);
        this._ussInterface = _ussInterface;
    }
    async globalSearch({ query, start = 0, limit = DEFAULT_MAX_LIMIT, order, mediaTypes, excludeInheritanceTypes, projectsOnly }) {
        var _a, _b;
        const { field = 'modified', direction = 'descending' } = order || {};
        const filters = [];
        if (projectsOnly) {
            // As per the recent discussions this is how the project:directoryType is defined as of now
            // The old spaces were identified with project:directoryType = project
            // The old workspaces were identified with project:directoryType = team
            // The new projects are now identified with project:directoryType = team
            filters.push({ [USSv3FilterField.DIRECTORY_TYPE]: ['team'] });
        }
        else {
            if ((_a = mediaTypes === null || mediaTypes === void 0 ? void 0 : mediaTypes.length) !== null && _a !== void 0 ? _a : 0 > 0) {
                filters.push({
                    [USSv3FilterField.MEDIA_TYPE]: mediaTypes,
                });
            }
            if ((_b = excludeInheritanceTypes === null || excludeInheritanceTypes === void 0 ? void 0 : excludeInheritanceTypes.length) !== null && _b !== void 0 ? _b : 0 > 0) {
                filters.push({
                    [USSv3FilterField.INHERITANCE_TYPE]: {
                        funcParams: { fields: excludeInheritanceTypes }
                    }
                });
            }
        }
        return this._ussInterface.getQueryResponse({
            start,
            limit,
            textQuery: query,
            sorts: [{
                    sortOrderBy: OrderFieldToUSSv3OrderField[field],
                    sortOrder: (direction === 'ascending' ? USSv3OrderDir.Ascending : USSv3OrderDir.Descending)
                }],
            // TODO: figure out how to exclude media types once USS v3 supports it
            filters
        });
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class AdobeAssetGlobalSearchLoader {
    constructor(assetPool, _implicitFilter, _core, _adobeAssetGlobalSearchInterface) {
        this._implicitFilter = _implicitFilter;
        this._core = _core;
        this._adobeAssetGlobalSearchInterface = _adobeAssetGlobalSearchInterface;
        this._ussLoaderHelper = new USSLoaderHelper(assetPool, _core, this);
    }
    supportsFilter(filter) {
        // If we don't provide a filter at all, then we cannot use the loader
        return filter !== undefined;
    }
    async loadPage(options) {
        if (!this._libraryContext) {
            this._libraryContext = await this._core.getLibraryContext();
        }
        return this._ussLoaderHelper.loadPage(options);
    }
    /**
     * @package -- only for use by USSLoaderHelper
     */
    _getUSSResults({ start, limit, order, mediaTypes, excludeMediaTypes }) {
        var _a;
        const query = (_a = this._implicitFilter) === null || _a === void 0 ? void 0 : _a._query;
        return this._adobeAssetGlobalSearchInterface.getFilteredAssets({
            orderBy: OrderFieldToIUSSAdobeAssetOrderBy[order.field],
            orderDirection: order.direction,
            start,
            limit,
            mediaTypes,
            excludeMediaTypes,
            query,
            embedPermissions: true,
        });
    }
    /**
     * @package -- only for use by USSLoaderHelper
     */
    _postProcessPage(loaderOptions, page) {
        const { items } = page;
        this._implicitFilter._matchingIDsFromSearch = new Set([
            // copy existing, if needed
            ...(this._implicitFilter._matchingIDsFromSearch || []),
            ...(items.map(item => item.id))
        ]);
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class SWYAdobeAssetFilter extends ImplicitAdobeAssetFilterBase {
    constructor() {
        super({
            _isSharedWithYou: true
        });
    }
    get hash() {
        return super.hash;
    }
    matches(item) {
        if (!item._isSWYAsset_NoLazyLoad) {
            return false;
        }
        return super.matches(item);
    }
    getRelationToFilter(other) {
        const otherAdobeAssetFilter = other;
        return super.getRelationToFilter(otherAdobeAssetFilter);
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const DefaultEmbeddedResources = [
    [RelType.AC_EFFECTIVE],
    [RelType.APP_METADATA],
    [RelType.PROJECTS_CREATOR],
    [RelType.REPOSITORY]
];
const DefaultProjectsListingOptions = {
    orderDirection: 'ascending',
    orderBy: 'repo:name',
    start: 0,
    limit: 0,
    // we want to embed all the resources by default
    embed: DefaultEmbeddedResources
};
function getDefaultOptions$2(Defaults, options) {
    return Object.assign({}, Defaults, options);
}
function getDirectionValue$2(userOptions) {
    const directionFlag = userOptions.orderDirection === 'ascending' ? '+' : '-';
    return directionFlag + userOptions.orderBy;
}
function createPageTemplate$2(options) {
    const templateValues = {};
    if (options.start) {
        templateValues.start = options.start;
    }
    if (options.limit) {
        templateValues.limit = options.limit;
    }
    return templateValues;
}
function getProjectsListingPath(links, options) {
    const userOptions = getDefaultOptions$2(DefaultProjectsListingOptions, options);
    const templateValues = createPageTemplate$2(userOptions);
    templateValues.orderBy = getDirectionValue$2(userOptions);
    const { embed, view, repositoryId } = userOptions;
    if (embed === null || embed === void 0 ? void 0 : embed.length) {
        templateValues.embed = embed.join(',');
    }
    if (view) {
        templateValues.view = view;
    }
    if (repositoryId) {
        templateValues.repositoryId = repositoryId;
    }
    return getURLFromLinks(links, RelType.PROJECTS_COLLECTION, templateValues);
}
function validateRequiredProjectLinks$1(links) {
    return validateLinks(links, PROJECTS_INDEX_LINKS);
}
/**
 * Small utility function that returns an OpPatch operation
 * for gradients. Seperated to make it easier to share here and
 * in tests.
 *
 * @param path
 * @param gradientID
 * @returns
 */
function getProjectGradientPatchOP(path, gradientID) {
    return {
        'op': 'replace',
        path,
        'value': gradientID
    };
}
class ProjectsInterface extends InterfaceBase {
    constructor() {
        super(...arguments);
        /**
         * We should fetch gradient data once per session
         * to ensure that if there are updates to the gradients
         * we get the updates if the page refreshes.
         */
        this._gradientLanguageFetched = '';
    }
    async _discoverLinks(rootHref) {
        const response = await this._getResponse(rootHref);
        return response._links;
    }
    _getEndpointHref(endpoint) {
        return `https://${this._core.environment.endpoints.PROJECTS_HOST}${endpoint}`;
    }
    async discoverLinks() {
        return this._discoverLinks(this._getEndpointHref(`/api/v3/index`));
    }
    /**
     * Fetches the list of projects based on the options provided
     * @param options : options to sort and filter the projects listing
     * @returns the list of projects along with the page information and links to the next page
     */
    async getProjects(options) {
        const links = await this.discoverLinks();
        const url = getProjectsListingPath(links, options);
        return await this._getResponse(url);
    }
    /**
     *
     * @package Internal use only
     */
    async _getProjectLink(id, embed = DefaultEmbeddedResources) {
        const links = await this.discoverLinks();
        const url = getURLFromLinks(links, RelType.PROJECTS_PRIMARY, Object.assign({ assetId: id }, (embed.length ? { embed: embed.join(',') } : undefined)));
        return url;
    }
    /**
     *
     * @param id Gets the project metadata for the given project id from the projects service
     * @param embed the resources that are to be embedded in the response. By default all the resources are embedded
     * @returns the project metadata for the given project id
     */
    async getProject(id, embed = DefaultEmbeddedResources) {
        const url = await this._getProjectLink(id, embed);
        return await this._getResponse(url);
    }
    async getProjectsWithLink(pageLink) {
        return await this._getResponse(pageLink.href);
    }
    /**
     *
     * @package Internal use only
     *
     */
    async _getCreateLink(embed) {
        const links = await this.discoverLinks();
        return getURLFromLinks(links, RelType.PROJECTS_CREATE, embed.length ? { embed: embed.join(',') } : undefined);
    }
    /**
     * Creates a new project
     * @param data needed to create a project like name and color
     * @param embed the resources to be returned in the response of the newly created project. By default all the resources are embedded
     * @returns the newly created project
     */
    async createProject(data, embed = DefaultEmbeddedResources) {
        const url = await this._getCreateLink(embed);
        // 403 is included as a success code below so we can special case it after _doPost returns.
        const response = await this._doPost(url, { 'Content-Type': JSON_MIME_TYPE }, JSON.stringify(data), [201, 403]);
        let jsonResponse = {};
        // Use try/catch in case body is empty or not JSON. It's expected that success cases will always return
        // JSON, but not sure about all error cases. Otherwise, it could throw an exception here and hide the
        // service call that caused the problem.
        try {
            jsonResponse = await response.json();
        }
        catch (_a) {
            // Ignore the error here.
        }
        if (jsonResponse.status === 403) {
            if (jsonResponse.type === ProblemType.CHILDREN_LIMIT_EXCEEDED) {
                throw SAMError.create(SAMErrorCode.PROJECT_LIMIT_EXCEEDED, 'Maximum number of Projects exceeded', { response, reqId: response.headers.get('x-request-id') || '' });
            }
            else {
                const error = SAMError.createWithResponse(response, response.url);
                this._core.environment.error(`ProjectsInterface error ${error}`);
                throw error;
            }
        }
        return jsonResponse;
    }
    /**
     * Renames the project with the new name
     * @param newName new name for the project
     * @param links links associated with the project. We use the rename link to rename the project
     */
    async renameProject(newName, links) {
        const href = getURLFromLinks(links, RelType.PROJECTS_RENAME);
        await this._doPost(href, { 'Content-Type': JSON_MIME_TYPE }, JSON.stringify({ 'repo:name': newName }), [204]);
    }
    /**
     *
     * @package Internal use only
     *
     */
    async _getResolveByPathLink(path, repositoryID) {
        const links = await this.discoverLinks();
        const url = getURLFromLinks(links, RelType.RESOLVE_PATH, Object.assign({ path }, (repositoryID ? { repositoryId: repositoryID } : {})));
        return url;
    }
    /**
     * Validate the project name either for creation or renaming a project.
     * If the head call responds with 404, it means the project name is valid
     * and no other project exists with the same name/path.
     * 200 and 403 responses are considered name conflicts.
     * Please refer https://wiki.corp.adobe.com/display/assetscollab/Proposal%3A+Project+API+V3+-+Resolve
     * for more details.
     * @param path The relative path or absolute path of a project. If the path is relative,
     * project service prepends a prefix to form an absolute path
     */
    async validateProjectName(path, repositoryID) {
        const url = await this._getResolveByPathLink(path, repositoryID);
        await this._doHead(url);
    }
    /**
     * Discards a project.
     */
    async discardProject(id, links) {
        if (!(links === null || links === void 0 ? void 0 : links[RelType.DISCARD])) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, `Discard link is missing for this project${id}`);
        }
        const href = getURLFromLinks(links, RelType.DISCARD);
        await this._doPost(href, undefined, undefined, [204]);
    }
    /**
     * Restores a project.
     */
    async restoreProject(id, links) {
        if (!(links === null || links === void 0 ? void 0 : links[RelType.RESTORE])) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, `Restore link is missing for this project${id}`);
        }
        const href = getURLFromLinks(links, RelType.RESTORE);
        await this._doPost(href, undefined, undefined, [204]);
    }
    async getGradients(languageCode) {
        const cacheKey = `${languageCode}-gradient-data`;
        const cacheData = await this._core.environment.cache.getItem(cacheKey);
        if (cacheData === NotFound$1) {
            // No cache, so need to refresh
            return this._refreshGradients(languageCode);
        }
        if (this._gradientLanguageFetched !== languageCode) {
            // Haven't yet refreshed the gradient data for this language
            // this session, so refresh
            return this._refreshGradients(languageCode);
        }
        return cacheData;
    }
    /**
     *
     * @package Internal use only
     *
     */
    async _getGradientsLink() {
        const links = await this.discoverLinks();
        const url = getURLFromLinks(links, RelType.PROJECTS_GRADIENTS_COLLECTION);
        return url;
    }
    async _refreshGradients(languageCode) {
        const url = await this._getGradientsLink();
        const response = await this._doGet(url, {
            headers: {
                'Content-Type': JSON_MIME_TYPE,
                'Accept-Language': languageCode
            }
        });
        const responseJson = await response.json();
        // set the data to the cache
        const cacheKey = `${languageCode}-gradient-data`;
        await this._core.environment.cache.setItem(cacheKey, responseJson);
        this._gradientLanguageFetched = languageCode;
        return responseJson;
    }
    async patchProject(links, data) {
        const jsonData = [];
        if (data.gradientID) {
            jsonData.push(getProjectGradientPatchOP(IProjectPatchKeys.GRADIENT_ID, data.gradientID));
        }
        const url = getURLFromLinks(links, RelType.APP_METADATA);
        await this._doPatch(url, { 'Content-Type': JSON_PATCH_MIME_TYPE, 'If-Match': '*' }, JSON.stringify(jsonData), [204]);
    }
    /**
     *
     * @package Internal use only
     *
     */
    async _getCapabilityLink(capability, repositoryID) {
        const links = await this.discoverLinks();
        return getURLFromLinks(links, RelType.PROJECTS_CAPABILITY, Object.assign({ privilege: capability }, (repositoryID ? { repositoryId: repositoryID } : {})));
    }
    async getCapability(capability, repositoryID) {
        const url = await this._getCapabilityLink(capability, repositoryID);
        try {
            await this._doGet(url);
        }
        catch (e) {
            // If the error is not a SAMError then we can
            // just throw it normally. We don't want to obfuscate a regular error
            // that could occur just because it's not a SAMError.
            if (!isSAMError(e)) {
                throw e;
            }
            const samError = e;
            const errorResponse = samError._additionalInfo.response;
            // If the response is not defined then let's just
            // throw the error as well since it's not an error created by
            // a response from a request in SAM.
            if (!errorResponse) {
                throw e;
            }
            const failedResponseCode = [404, 403];
            if (!failedResponseCode.includes(errorResponse.status)) {
                throw e;
            }
            const responseJson = await errorResponse.json();
            switch (responseJson.type) {
                case ProblemType.ADMIN_ONLY:
                    throw SAMError.create(SAMErrorCode.ACCESS_DENIED, 'Admin disabled creation of projects for user', undefined, SAMErrorSubCode.PROJECTS_ADMIN_ONLY);
                case ProblemType.NO_HOME_REPOSITORY:
                    throw SAMError.create(SAMErrorCode.NOT_FOUND, 'No home repository for the user', undefined, SAMErrorSubCode.PROJECTS_NO_HOME_REPOSITORY);
                case ProblemType.INVALID_HOME_REPOSITORY:
                    throw SAMError.create(SAMErrorCode.NOT_FOUND, 'Invalid home Repository for the user', undefined, SAMErrorSubCode.PROJECTS_INVALID_REPOSITORY);
                default:
                    throw e;
            }
        }
    }
    /**
     *
     * @package Internal use only
     *
     */
    async _issueContextAPIRequest(assetId, repoId, href) {
        const body = JSON.stringify({
            'repo:assetId': assetId,
            'repo:repositoryId': repoId
        });
        const response = await this._doPost(href, {
            'Content-Type': 'application/json'
        }, body, [200]);
        return response.json();
    }
}
__decorate([
    cache(validateRequiredProjectLinks$1),
    __param(0, key)
], ProjectsInterface.prototype, "_discoverLinks", null);
__decorate([
    synchronized
], ProjectsInterface.prototype, "getGradients", null);
__decorate([
    synchronized
], ProjectsInterface.prototype, "_refreshGradients", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const libraryMediaTypeString = 'application/vnd.adobe.library+dcx';
function repoMetadataIsLibrary(repoMetadata) {
    return (repoMetadata === null || repoMetadata === void 0 ? void 0 : repoMetadata['dc:format']) === libraryMediaTypeString;
}
function appMetadataIsProject(app_metadata) {
    var _a, _b;
    return !!((_b = (_a = app_metadata === null || app_metadata === void 0 ? void 0 : app_metadata['cc:inheritable']) === null || _a === void 0 ? void 0 : _a['cc:team']) === null || _b === void 0 ? void 0 : _b['repo:assetId']);
}
function ussV3ItemMetadataIsProject(item) {
    var _a, _b, _c;
    return !!((_c = (_b = (_a = item.am) === null || _a === void 0 ? void 0 : _a['cc:inheritable']) === null || _b === void 0 ? void 0 : _b['cc:team']) === null || _c === void 0 ? void 0 : _c['repo:assetId']);
}
function ussV3ItemMetadataIsLibrary(item) {
    var _a;
    return ((_a = item.rm) === null || _a === void 0 ? void 0 : _a['dc:format']) === libraryMediaTypeString;
}
function ussMetadataIsLibrary(item) {
    return item.type === libraryMediaTypeString;
}
/* Utils to translate incoming data to the type expected by the subclass's PropertyUpdater: */
// (This mapping is necessary at minimum to translate the id to the remoteIdProperty expected by the PropertyUpdater,
// because we use the updaters to indirectly create an instance of the subclass in the call to
// updater.updateRemoteItems({remoteIdProperty}), regardless of whether the updater supports the incoming data type.)
function ussV3ToProjectMetadata(item) {
    // TODO: Add more Project attributes here as they become available in USS.
    // Minimal data needed for ProjectUpdater. When additional Project metadata
    // is available from the USSv3 query result, add them here.
    return { 'repo:assetId': item.id };
}
function ussToProjectData(item) {
    var _a;
    return {
        'repo:assetId': item.asset_id,
        'repo:repositoryId': item['repo:repositoryId'],
        _embedded: {
            [RelType.AC_EFFECTIVE]: (_a = item._embedded) === null || _a === void 0 ? void 0 : _a[RelType.AC_EFFECTIVE],
            [RelType.APP_METADATA]: item.app_metadata
        }
    };
}
function ussToLibraryData(item) {
    return { 'library_urn': item.asset_id };
}
function ussV3ToLibraryData(item) {
    return { 'library_urn': item.id };
}
function repoToLibraryData(item) {
    return { 'library_urn': item['repo:assetId'] };
}
class AdobeAssetFactory {
    constructor(_core) {
        this._core = _core;
    }
    // Creates assets from a list of USSItemMetadata returned from USSv3
    // getItemData maps the repo metadata fields, as well as isShared, sharedViaInvite, and app metadata fields
    // from USSv3 format to RAPI format
    // NOTE: Caller is responsible for committing the session after this is called
    assetsFromUSSV3AppMetadata(session, appMetadata, getItemData, libraryContext) {
        if (libraryContext) {
            libraryContext._libraryUpdater.updateRemoteItems(session, appMetadata.filter(ussV3ItemMetadataIsLibrary).map(ussV3ToLibraryData));
        }
        this._core.projectContext._projectUpdater.updateRemoteItems(session, appMetadata.filter(ussV3ItemMetadataIsProject).map(ussV3ToProjectMetadata));
        return this._core.hostAssetContext._adobeAssetUpdater.updateRemoteItems(session, 
        // allow Loader to provide additional item metadata (optional)
        appMetadata.map((item) => {
            return getItemData(item);
        }));
    }
    // Create assets from a list of USSMetadata
    // getItemData allows the caller to add additional metadata
    // NOTE: Caller is responsible for committing the session after this is called
    assetsFromUSSMetadata(session, items, getItemData, libraryContext) {
        // Ensure that Projects are added to the discarded list as a "Project" instance.
        // ProjectUpdater creates a blank Project instance and relies on the USSAdobeAssetUpdater
        // to fill in the blanks.
        const projects = this._core.projectContext._projectUpdater.updateRemoteItems(session, items.filter(item => appMetadataIsProject(item.app_metadata)).map(ussToProjectData));
        // USS won't have all the permissions required for the deleted view
        // Loaders are responsible for populating the _canRestoreProject cache before it gets here.
        for (const project of projects) {
            project._applyRestorePermission(session);
        }
        // Ensure that Libraries are added to the discarded list as a "Library" instance
        if (libraryContext) {
            libraryContext._libraryUpdater.updateRemoteItems(session, items.filter(ussMetadataIsLibrary).map(ussToLibraryData));
        }
        return USSAdobeAssetUpdater.updateRemoteItems(session, 
        // allow Loader to provide additional item metadata (optional)
        items.map(item => (getItemData === null || getItemData === void 0 ? void 0 : getItemData(item)) || item));
    }
    // Create assets from a list of RepositoryMetadata returned from RAPI
    // NOTE: Caller is responsible for committing the session after this is called
    assetsFromRepoMetadata(session, repoMetadatas, libraryContext) {
        return repoMetadatas.map(repoMetadata => {
            var _a, _b, _c;
            let permissions = {};
            if ((_a = repoMetadata._embedded) === null || _a === void 0 ? void 0 : _a[RelType.AC_EFFECTIVE]) {
                permissions = parseEffectivePermissions((_b = repoMetadata._embedded) === null || _b === void 0 ? void 0 : _b[RelType.AC_EFFECTIVE]);
            }
            return this.assetFromRepoMetadata(session, repoMetadata, permissions, (_c = repoMetadata._embedded) === null || _c === void 0 ? void 0 : _c[RelType.APP_METADATA], libraryContext);
        });
    }
    assetFromRepoMetadata(session, repoMetadata, permissions, appMetadata, libraryContext, projectMetadata) {
        let asset;
        if (appMetadata && Object.keys(appMetadata).length > 0) {
            // We should have a better way to do this
            repoMetadata._embedded = Object.assign(Object.assign({}, repoMetadata._embedded), { [RelType.APP_METADATA]: appMetadata });
        }
        const assetMetadata = repoMetadata || {};
        if (permissions && Object.keys(permissions).length > 0) {
            assetMetadata.effectivePermission = permissions;
        }
        if (projectMetadata && Object.keys(projectMetadata).length > 0) {
            asset = this._core.projectContext._projectUpdater.updateRemoteItem(session, projectMetadata);
            // Since ProjectUpdater doesn't handle all repo metadata fields, use AdobeAssetUpdater
            // For example we need the links coming in from the repoMetadata to be used in AdobeAsset
            this._core.hostAssetContext._adobeAssetUpdater.updateLocalItem(session, asset, assetMetadata);
            // Apply Project-specific metadata and overrides
            // An example of this is the createdBy field which is different for Projects
            this._core.projectContext._projectUpdater.updateLocalItem(session, asset, projectMetadata);
        }
        else if (repoMetadataIsLibrary(assetMetadata) && libraryContext) {
            const libraryData = repoToLibraryData(assetMetadata);
            libraryContext._libraryUpdater.updateRemoteItem(session, libraryData);
            // Update repo metadata fields that are not handled by LibraryUpdater with the AdobeAssetUpdater
            asset = this._core.hostAssetContext._adobeAssetUpdater.updateRemoteItem(session, assetMetadata);
        }
        else {
            // TODO: add Assignments if needed
            asset = this._core.hostAssetContext._adobeAssetUpdater.updateRemoteItem(session, assetMetadata);
        }
        return asset;
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class CommentInterface extends InterfaceBase {
    constructor(_core, _assetId) {
        super(_core);
        this._core = _core;
        this._assetId = _assetId;
    }
    _getUrl(endpoint) {
        // TODO: Should we be using a rel links ?
        //  const url = getURLFromLinks(links, RelType.ASSIGNMENTS_START, { assignmentId });
        return `https://${this._core.environment.endpoints.COMMENT_HOST}${endpoint}`;
    }
    _getCommonHeaders(etag) {
        const headers = {
            'Content-Type': 'application/ld+json; profile="https://www.w3.org/ns/anno.jsonld"',
            'Accept': 'application/ld+json; profile="https://www.w3.org/ns/anno.jsonld"',
            'Prefer': 'return=representation;include="https://www.w3.org/ns/oa#PreferContainedDescriptions"',
            'x-ccx-workflow': 'true'
        };
        etag && (headers['If-Match'] = etag);
        return headers;
    }
    /**
     * @description Get a list of the comments associated with an asset
     * from the Red Hawk Commenting Service.
     * @param data The information necessary for calling the API.
     * @returns A promise that resolves to the server's response,
     * an Array of CommentCore's listing the asset's comments.
     */
    async getCommentList() {
        const url = this._getUrl(`/assets/${this._assetId}/annots/?status=open%2Cresolved`);
        return this._getResponse(url, {
            headers: this._getCommonHeaders()
        });
    }
    /**
     * @description Get a comment from the Red Hawk Commenting Service.
     * @param data The information necessary for calling the create API.
     * @returns A promise that resolves to the server's response,
     * a CommentCore corresponding to the comment requested for by id.
     */
    async getComment(data) {
        const { annotationId, etag } = data;
        const url = this._getUrl(`/assets/${this._assetId}/annots/${annotationId}?recursive=true`);
        return this._getResponse(url, {
            headers: this._getCommonHeaders(etag)
        });
    }
    /**
     * @description Creates a comment by POST-ing to the Red Hawk Commenting Service.
     * @param data The information necessary for calling the create API.
     * @returns A promise that resolves to the server's response.
     * a CommentCore corresponding to the comment created.
     */
    async createComment(data) {
        // RE: https://git.corp.adobe.com/redhawk/native_client_sdk/blob/fddd524698ec5ad2cb4df23748c845a3c2129243/EurekaClientSDK/api/WebAnnotationsApi.cpp#L469
        // NOTE : CPP supports ANONBEARER, but for a first draft, we'll
        // only support BEARER, which we get for free from InterfaceBase
        // NOTE : We don't need to include User-Agent either
        // NOTE : Investigate whether we can get the URL from links
        const url = this._getUrl(`/assets/${this._assetId}/annots`);
        const response = await this._doPost(url, this._getCommonHeaders(), JSON.stringify(data));
        return response.json();
    }
    /**
     * @description Edit a comment from the Red Hawk Commenting Service.
     * @param data The information necessary for calling the API.
     * @returns A promise that resolves to the server's response,
     * a CommentCore corresponding to the updated comment.
     */
    async editComment(data) {
        const { annotationId, etag, body } = data;
        const url = this._getUrl(`/assets/${this._assetId}/annots/${annotationId}`);
        const response = await this._doPut(url, this._getCommonHeaders(etag), JSON.stringify(body), [200]);
        // response should be a new annotation.
        return response.json();
    }
    /**
     * @description Delete a comment from the Red Hawk Commenting Service.
     * @param data The information necessary for calling the delete API.
     * @returns A promise that resolves to void.
     */
    async deleteComment(data) {
        const { annotationId, etag } = data;
        const url = this._getUrl(`/assets/${this._assetId}/annots/${annotationId}?recursive=true`);
        await this._doDelete(url, this._getCommonHeaders(etag), [204]);
        return;
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// RE: https://wiki.corp.adobe.com/display/ACPCSDK/Commenting+Service+Integration+Into+SAM
// TODO : Commenting can be used by Guest Users
// RE: https://git.corp.adobe.com/redhawk/native_client_sdk/blob/f0cd86aa3047c1392403b4444b5338189d7302e5/EurekaClientSDK/api/WebAnnotationsApi.cpp
//  I haven't researched how it's done, but the native code supports
//  AnonBearer tokens for guest users.
/**
 * Class representing the notion of a comment ( aka annotation ).
 * @extends ItemBase
 *
 * @example
 * // Instantiate list and load contents
 * const classroomsList = core.classroomContext.getClassrooms().getList(...options);
 * await waitForListLoad(classroomsList);
 *
 * // Get a specific classroom
 * const load = useMemo(() => core.classroomContext.getClassroomById(id), [classroomContext, id]);
 * const { value: classroom, error, loading } = useLoading<Classroom | undefined>(undefined, load);
 */
class Comment extends ItemBase {
    constructor() {
        super(...arguments);
        // WARNING: the server response for a comment includes
        //  "id": "/assets/${assetUrn}/annots/${annotationId}",
        //  "creationId": "${annotationId}",
        //
        // so we map "creationId" to comment.id
        // because ItemBase.id is expected to be the object's cloud-id,
        // and "id" to comment.uri to avoid a naming conflict.
        this._creator = undefined;
        this._created = new Date(0);
        this._bodyValue = undefined;
        this._motivation = undefined;
        this._generator = 'https://comments.acrobat.com/Comment';
        this._type = 'Annotation';
        this._target = undefined;
        this._mentions = undefined;
        this._modified = new Date(0);
        this._etag = undefined;
        this._uri = undefined;
        this._status = undefined;
        this._replies = undefined;
    }
    //-----------------------------------------------------------------
    // Simple accessors
    get creator() {
        return this._creator;
    }
    get created() {
        return this._created;
    }
    get bodyValue() {
        return this._bodyValue || '';
    }
    get motivation() {
        // Possible values are ('commenting' | 'replying')
        return this._motivation || 'commenting';
    }
    get generator() {
        // Possible value is 'https://comments.acrobat.com/Comment'
        return this._generator || 'https://comments.acrobat.com/Comment';
    }
    get type() {
        // Possible value is 'Annotation'
        return this._type || 'Annotation';
    }
    get target() {
        return this._target;
    }
    get mentions() {
        return this._mentions;
    }
    get modified() {
        return this._modified;
    }
    get etag() {
        return this._etag || '';
    }
    get uri() {
        return this._uri || '';
    }
    get status() {
        // Possible values are ('open' | 'resolved')
        return this._status || 'open';
    }
    get replies() {
        // Possible values are ('open' | 'resolved')
        return this._replies;
    }
    toJSON() {
        // When an object implements toJSON()
        // JSON.stringify(object) serializes object.toJSON()
        // instead of object, allowing you to control
        // the keys and values written.
        //
        // This method is returning a Partial<CommentCore>
        // which can be used in a POST or PUT to the server.
        return {
            '@context': [
                'https://www.w3.org/ns/anno.jsonld',
                'https://comments.acrobat.com/ns/anno.jsonld'
            ],
            'creator': this._creator,
            'created': this.created.toISOString(),
            'bodyValue': this.bodyValue,
            'motivation': this.motivation,
            'generator': this.generator,
            'type': this.type,
            'target': this.target,
            /* TODO : 'mentions' : this.mentions */
            'modified': this.modified.toISOString(),
            'etag': this.etag,
            // Map ItemBase.id back to 'creationId',
            // and Comment.uri back to 'id'.
            'id': this.uri,
            'creationId': this.id,
            'status': this.status,
            /* TODO :  'replies': this.replies, */
        };
    }
    // Simple accessors
    //-----------------------------------------------------------------
    get assetUrn() {
        // Split "/assets/${assetUrn}/annots/${annotationId}"
        // into ['', 'assets', assetUrn, 'annots', annotationId]
        // so we can return assetUrn
        const uri = this.uri;
        const array = uri.split('/');
        return (array.length > 2) ? array[2] : '';
    }
    get _pool() {
        return this._commentContext._commentPool;
    }
    get _interface() {
        return this._commentContext._commentInterface;
    }
    //-----------------------------------------------------------------
    // Public methods
    async edit(changes) {
        const commentInterface = this._interface;
        const commentPool = this._pool;
        // Get our current state as a CommentCore
        const current = this.toJSON();
        // combine current with changes
        const body = Object.assign(Object.assign({}, current), changes);
        // update the server with the edited body
        const response = await commentInterface.editComment({
            annotationId: this.id,
            etag: this.etag,
            body
        });
        // update our local object with the server response, triggering notifications
        const session = commentPool.createUpdateSession();
        CommentUpdater.updateLocalItem(session, this, response);
        session.commit();
        return this;
    }
    async updateResolvedStatus(isResolved) {
        const status = isResolved ? 'resolved' : 'open';
        return this.edit({ status });
    }
    async delete() {
        const commentInterface = this._interface;
        const commentPool = this._pool;
        const id = this.id;
        await commentInterface.deleteComment({
            annotationId: id,
            etag: this.etag
        });
        // after the server deletes the remote object,
        // discard our local object which triggers notifications
        const session = commentPool.createUpdateSession();
        session.queueDiscardedItem(this);
        session.commit();
        // the session doesn't actually remove the item from the pool
        commentPool.removeLocalItems([id]);
    }
    /**
     * @description Create a reply to this comment.
     * @param data The information necessary for calling the API.
     * @returns A promise that resolves to the newly created reply.
     */
    async reply(data) {
        const required = {
            motivation: 'replying',
            target: {
                source: this.uri
            }
        };
        // overwrite data with the reply required fields
        return this._commentContext.createComment(Object.assign(Object.assign({}, data), required));
    }
}
__decorate([
    observable
], Comment.prototype, "creator", null);
__decorate([
    observable
], Comment.prototype, "created", null);
__decorate([
    observable
], Comment.prototype, "bodyValue", null);
__decorate([
    observable
], Comment.prototype, "motivation", null);
__decorate([
    observable
], Comment.prototype, "generator", null);
__decorate([
    observable
], Comment.prototype, "type", null);
__decorate([
    observable
], Comment.prototype, "target", null);
__decorate([
    observable
], Comment.prototype, "mentions", null);
__decorate([
    observable
], Comment.prototype, "modified", null);
__decorate([
    observable
], Comment.prototype, "etag", null);
__decorate([
    observable
], Comment.prototype, "uri", null);
__decorate([
    observable
], Comment.prototype, "status", null);
__decorate([
    observable
], Comment.prototype, "replies", null);
const CommentUpdater = new ItemUpdater({
    localItemType: Comment,
    // NOTE: We are using property 'creationId' in the response as the ItemBase.id
    remoteIdProperty: 'creationId',
    propertyUpdater: new PropertyUpdater({
        // TODO : create nested updater for CommentCreator ?
        _creator: { name: 'creator', format: 'object' },
        // TODO : this is a Date.toISOString(); should this be Date ?
        _created: { name: 'created', format: 'date' },
        _bodyValue: { name: 'bodyValue', format: 'string' },
        // should be ( 'commenting' | 'replying' ), but allow any string (future proof)
        _motivation: { name: 'motivation', format: 'string' },
        // should be 'https://comments.acrobat.com/Comment', but allow any string (future proof)
        _generator: { name: 'generator', format: 'string' },
        // should be 'Annotation', but allow any string (future proof)
        _type: { name: 'type', format: 'string' },
        // TODO : create nested updater for CommentTarget ?
        _target: { name: 'target', format: 'object' },
        // TODO : create nested updater for CommentMention[] ?
        _mentions: { name: 'mentions', format: 'object' },
        // TODO : this is a Date.toISOString(); should this be Date ?
        _modified: { name: 'modified', format: 'date' },
        _etag: { name: 'etag', format: 'string' },
        // NOTE: 'creationId' is mapped to ItemBase.id,
        // and 'id' is mapped to comment.uri.
        _uri: { name: 'id', format: 'string' },
        // should be ( 'open' | 'resolved' ), but allow any string (future proof)
        _status: { name: 'status', format: 'string' },
        // TODO : create nested updater for Comment[] ?
        _replies: { name: 'replies', format: 'object' },
    }),
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class CommentLoader {
    constructor(_commentContext) {
        this._commentContext = _commentContext;
    }
    async loadPage(options) {
        return await this._loadPage(options);
    }
    get _pool() {
        return this._commentContext._commentPool;
    }
    get _interface() {
        return this._commentContext._commentInterface;
    }
    async _loadPage(options, start) {
        const result = await this._interface.getCommentList();
        const items = result.first.items;
        const session = this._pool.createLoaderUpdateSession(Date.now());
        const comments = CommentUpdater.updateRemoteItems(session, items);
        session.commit();
        comments.forEach((comment) => {
            // @ts-ignore : bit of a hack
            comment._commentContext = this._commentContext;
        });
        return {
            items: comments,
            total: comments.length,
            loadNextPage: undefined,
        };
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const OrderFieldToComparator$c = {
    created(a, b) {
        return compareValues(a.created.getTime(), b.created.getTime());
    },
};
class CommentOrder extends createItemOrderFactory(OrderFieldToComparator$c) {
}
CommentOrder.default = new CommentOrder('ascending', 'created');

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class CommentContext extends TypedEventEmitter {
    constructor(_core, _assetId) {
        super();
        this._core = _core;
        this._assetId = _assetId;
        /**
         * @package -- for SAM.js internal use only
         */
        this._commentInterface = new CommentInterface(this._core, this._assetId);
        /**
         * @package -- for SAM.js internal use only
         */
        this._commentPool = new ItemPool(this._core, 'comment');
    }
    /**
     * @description Get a list of the comments associated with an asset
     * from the Red Hawk Commenting Service.
     * @param data The information necessary for calling the API.
     * @returns A promise that resolves to the server's response,
     * an Array of CommentCore's listing the asset's comments.
     */
    async createComment(data) {
        // Update the remote object with our proposed edit
        const response = await this._commentInterface.createComment(data);
        // The server has successfully updated the remote object,
        // so update our local object to match the final state.
        const session = this._commentPool.createUpdateSession();
        const comment = CommentUpdater.updateRemoteItem(session, response);
        session.commit();
        // @ts-ignore : bit of a hack
        comment._commentContext = this;
        return comment;
    }
    /**
     * @experimental API is not ready and mock data is being used
     *
     * @param data creation data (e.g. title, instructions, etc)
     *
     * @returns a promise that resolves to the assignment recently and successfully created
     */
    async getComment(data) {
        let comment = this._commentPool.getLocalItemById(data.annotationId);
        if (!comment) {
            // fetch remote asset with a server call
            const response = await this._commentInterface.getComment(data);
            const session = this._commentPool.createUpdateSession();
            comment = CommentUpdater.updateRemoteItem(session, response);
            session.commit();
            // @ts-ignore : bit of a hack
            comment._commentContext = this;
        }
        return comment;
    }
    /**
     * @experimental API is not ready and mock data is being used
     *
     * @param data creation data (e.g. title, instructions, etc)
     *
     * @returns a promise that resolves to the assignment recently and successfully created
     */
    getCommentSource() {
        const loader = new CommentLoader(this);
        return new ItemSource(this._commentPool, [loader]);
    }
    getCommentList() {
        const source = this.getCommentSource();
        return source.getList(CommentOrder.default, undefined, { refreshTimeout: -1 });
    }
}
__decorate([
    memoize
], CommentContext.prototype, "getCommentSource", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// Must be kept in sync with CloudSyncStatus in CCAssetContext.h
var CloudSyncStatus;
(function (CloudSyncStatus) {
    CloudSyncStatus[CloudSyncStatus["Initializing"] = 0] = "Initializing";
    CloudSyncStatus[CloudSyncStatus["Offline"] = 1] = "Offline";
    CloudSyncStatus[CloudSyncStatus["NotStarted"] = 2] = "NotStarted";
    CloudSyncStatus[CloudSyncStatus["Disabled"] = 3] = "Disabled";
    CloudSyncStatus[CloudSyncStatus["Synchronized"] = 4] = "Synchronized";
    CloudSyncStatus[CloudSyncStatus["InProgress"] = 5] = "InProgress";
    CloudSyncStatus[CloudSyncStatus["ErrorQuotaExceeded"] = 6] = "ErrorQuotaExceeded";
    CloudSyncStatus[CloudSyncStatus["ErrorSyncDown"] = 7] = "ErrorSyncDown";
    CloudSyncStatus[CloudSyncStatus["ErrorOther"] = 8] = "ErrorOther";
})(CloudSyncStatus || (CloudSyncStatus = {}));
// Must be kept in sync with NetworkStatus in AssetContext.h
var NetworkStatus;
(function (NetworkStatus) {
    NetworkStatus[NetworkStatus["Offline"] = 0] = "Offline";
    NetworkStatus[NetworkStatus["Wan"] = 1] = "Wan";
    NetworkStatus[NetworkStatus["Cellular"] = 2] = "Cellular";
    // on web, it might not be known if the connection is Wan or Cellular
    NetworkStatus[NetworkStatus["OnlineUnknown"] = 3] = "OnlineUnknown";
})(NetworkStatus || (NetworkStatus = {}));
const ONS_FIRST_LONG_POLL_STARTED = FIRST_LONG_POLL_STARTED;
const ONS_FIRST_LONG_POLL_COMPLETED = FIRST_LONG_POLL_COMPLETED;
class ONSFirstLongPollStartedEvent extends Event {
    constructor(cachedSubscriptionsUsed) {
        super(ONS_FIRST_LONG_POLL_STARTED);
        this.cachedSubscriptionsUsed = cachedSubscriptionsUsed;
    }
}
class ONSFirstLongPollCompletedEvent extends Event {
    constructor(expired) {
        super(ONS_FIRST_LONG_POLL_COMPLETED);
        this.expired = expired;
    }
}
class AssetContext extends TypedEventEmitter {
    constructor(_core, 
    /**
     * @package -- only for use in SAM.js
     */
    _assetPool) {
        super();
        this._core = _core;
        this._assetPool = _assetPool;
        this._rapi = new RepositoryInterface(this._core);
        this._ons = new ONSInterface(this._core);
        this._invitationService = new InvitationInterface(this._core);
        this._USSInterface = new USSInterface(this._core);
        this._SBMInterface = new SBMInterface(this._core);
        /**
         * @package -- for use in SAM.js only
         */
        this._SWYInterface = new SWYInterface(this._core);
        /**
         * @package -- for use in SAM.js only
         */
        this._RecentInterface = new RecentInterface(this._core);
        this._adobeAssetSearchInterface = new AdobeAssetSearchInterface(this._core);
        this._adobeAssetGlobalSearchInterface = new AdobeAssetGlobalSearchInterface(this._core);
        this._assetTransferService = new AssetTransferInterface(this._core);
        this._assetReviewServiceInterface = new AssetReviewServiceInterface(this._core);
        this._publishedInterface = new PublishedInterface(this._core);
        this._discardedInterface = new DiscardedInterface(this._core);
        this._USSv3Interface = new USSv3Interface(this._core);
        /**
         * @package -- only for use in SAM.js
         */
        this._globalSearchInterfaceV3 = new GlobalSearchInterface(this._core, this._USSv3Interface);
        /**
         * @package -- only for use in SAM.js
         */
        this._libraryInterface = new LibraryInterface(this._core);
        this._commentsById = new Map();
        /**
         * @package -- only for use in SAM.js
         * Updaters shouldn't be accessed directly.
         */
        this._adobeAssetUpdater = AdobeAssetUpdater;
        this._onHomeFolderChange = () => {
            // Clear cached /index so it gets refreshed on next request
            this._rapi._clearIndexCache();
        };
        // Called when we get a notification from ONS - we reload any assets when we find out they changed
        this._onONSNotification = (changedAssets) => {
            // Update any assets in the pool
            changedAssets.forEach((assetInfo, assetID) => {
                var _a, _b, _c;
                const asset = this._assetPool.getLocalItemById(assetID);
                const assignmentId = (asset === null || asset === void 0 ? void 0 : asset.parentAssignmentID) || assetInfo.parentAssetId;
                if (asset) {
                    if ((_a = assetInfo.changedResources) === null || _a === void 0 ? void 0 : _a.includes('api:metadata/repository')) {
                        // TODO: bypass loading app metadata if changedResources includes api:metadata/application
                        // TODO: bypass permission refresh if changedResources includes api:ac/policy
                        // https://jira.corp.adobe.com/browse/SAM-3499
                        asset._loadWithRetry();
                    }
                    if ((_b = assetInfo.changedResources) === null || _b === void 0 ? void 0 : _b.includes('api:metadata/application')) {
                        asset.appMetadata.load();
                    }
                    if ((_c = assetInfo.changedResources) === null || _c === void 0 ? void 0 : _c.includes('api:ac/policy')) {
                        // Load the permissions and set collaborator data dirty flags
                        asset._reloadPermissions();
                    }
                }
                else if (assetInfo.parentAssetId && this._assetPool.getLocalItemById(assetInfo.parentAssetId)) {
                    // Check for items that were added remotely to a folder that we're monitoring (e.g. move, restore, discard,\
                    // and copy can cause an item to appear in a folder that is being monitored). This will cause any list of children
                    // for the folder to be updated when a new asset is added to it.
                    //
                    // Note that we ONLY do this if the parent asset is loaded - in the case where we're monitoring asset ids
                    // directly via the link list, as the CC Library process does, no AdobeAssets are loaded (SAM is only used
                    // to get the notifications). So if we always loaded the asset here it would introduce unnecessary network
                    // calls. In the folder listing case, we always have the parent asset loaded, so this is safe.
                    this.getAssetWithCloudID(assetID);
                }
                if (assignmentId) {
                    const assignment = this.assetPool.getLocalItemById(assignmentId);
                    if (assignment === null || assignment === void 0 ? void 0 : assignment.isAssignment) {
                        // found parent assignment, can refresh so StudentAssignment gets changes
                        assignment._loadWithRetry();
                    }
                }
            });
            // Also refresh any links that changed
            const changedIDs = new Set(changedAssets.keys());
            this._core.assetLinkManager.lists.forEach(list => list._onONSNotification(changedIDs));
        };
        /**
         * Called whenever any new assets are added to the asset pool, or when links / link
         * lists are modified. When this happens, we gather the set of all assets we need to
         *  monitor and trigger watching this with ONS.
         *
         *  @package -- only for use in SAM.js
         */
        this._onAssetsChanged = (e) => {
            var _a;
            // Compute the set of asset ids to watch - start with all the monitored lists:
            const assetIDs = new Set();
            // We want to monitor all assets in the asset pool (so they get updated when they change)
            this._assetPool.forEach((asset) => assetIDs.add(asset.id));
            // Ignore Assets queued for removal from the pool, if this is an ItemsChangedEvent
            (_a = e === null || e === void 0 ? void 0 : e.discardedItems) === null || _a === void 0 ? void 0 : _a.forEach((asset) => assetIDs.delete(asset.id));
            // And also any assets referenced by links (since the link might be broken and we want
            // to re-resolve when the asset comes in)
            this._core.assetLinkManager.lists.forEach(list => list.monitoredAssetIDs.forEach(id => assetIDs.add(id)));
            // Update the list of assetIds that ONS is watching - note that this only modifies the
            // subscription if the list of assets changes, so calling ONS with the same assetIds won't
            // cause any new API calls.
            if (assetIDs.size > 0 && !this._core.environment.getValue('DISABLE_ONS')) {
                this._ons.watch(assetIDs);
                this._ons.start();
            }
            else {
                this._ons.stop();
            }
        };
        // We want to know all the asset ids that we care about, so we can register for change
        // notifications with ONS. This consists of all loaded assets in the asset pool.
        this._assetPool.on(ITEMS_CHANGED, this._onAssetsChanged);
        // When an ONS notification comes in, we go through and refresh any assets that changed
        this._ons.on('change', this._onONSNotification);
        this._ons.on(FIRST_LONG_POLL_STARTED, data => {
            this.dispatch(new ONSFirstLongPollStartedEvent(data.cachedSubscriptionsUsed));
        });
        this._ons.on(FIRST_LONG_POLL_COMPLETED, data => {
            this.dispatch(new ONSFirstLongPollCompletedEvent(data.expired));
        });
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     *  For internal use only
     */
    get repoInterface() {
        return this._rapi;
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     *  For internal use only
     */
    get onsInterface() {
        return this._ons;
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     *    For internal use only
     */
    get ussInterface() {
        return this._USSInterface;
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     *  For internal use only
     */
    get ussInterfaceV3() {
        return this._USSv3Interface;
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     *  No replacement.
     */
    get sbmInterface() {
        return this._SBMInterface;
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     *  For internal use only
     */
    get swyInterface() {
        return this._SWYInterface;
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     *  For internal use only
     */
    get recentInterface() {
        return this._RecentInterface;
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     *  For internal use only
     */
    get invitationInterface() {
        return this._invitationService;
    }
    /**
     * @package -- for use in SAM.js only
     */
    get _assetSearchInterface() {
        return this._adobeAssetSearchInterface;
    }
    /**
     * @package -- for use in SAM.js only
     */
    get _globalSearchInterface() {
        return this._adobeAssetGlobalSearchInterface;
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     *  For internal use only
     */
    get publishedInterface() {
        return this._publishedInterface;
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     *     This API was not intended to be public.
     */
    get assetPool() {
        return this._assetPool;
    }
    get sharedSource() {
        const { hostAssetContext } = this._core;
        const implicitFilter = new SWYAdobeAssetFilter();
        const swyloader = new SWYAdobeAssetLoader(hostAssetContext.assetPool, this._core, hostAssetContext.swyInterface);
        return new ItemSource(this.assetPool, [swyloader], implicitFilter);
    }
    get recentAssetsSource() {
        const { hostAssetContext } = this._core;
        const implicitFilter = new RecentAdobeAssetFilter();
        const recentAssetsLoader = new RecentAdobeAssetLoader(hostAssetContext.assetPool, this._core, hostAssetContext.recentInterface, implicitFilter);
        implicitFilter._lastModificationDate = new Date(9999, 0, 1);
        return new ItemSource(this.assetPool, [recentAssetsLoader], implicitFilter);
    }
    get MRUAssetsSource() {
        const { hostAssetContext } = this._core;
        const implicitFilter = new MRUAdobeAssetFilter();
        const MRUAssetsLoader = new MRUAdobeAssetLoader(hostAssetContext.assetPool, this._core, hostAssetContext.recentInterface);
        return new ItemSource(this.assetPool, [MRUAssetsLoader], implicitFilter);
    }
    get publishedSource() {
        const { hostAssetContext } = this._core;
        const implicitFilter = new PublishedAdobeAssetFilter();
        const publishedLoader = new PublishedAdobeAssetLoader(hostAssetContext.assetPool, this._core, hostAssetContext.publishedInterface);
        return new ItemSource(this.assetPool, [publishedLoader], implicitFilter);
    }
    get discardedSource() {
        const implicitFilter = new DiscardedAdobeAssetFilter();
        const loader = new DiscardedAdobeAssetLoader(this.assetPool, this._core, this._discardedInterface);
        return new ItemSource(this._assetPool, [loader], implicitFilter);
    }
    getGlobalSearchAssetsSource(query) {
        const implicitFilter = new AdobeAssetGlobalSearchFilter({
            _query: query,
            _matchingIDsFromSearch: new Set()
        });
        const globalSearchLoader = new AdobeAssetGlobalSearchLoader(this._core.hostAssetContext.assetPool, implicitFilter, this._core, this._core.hostAssetContext._adobeAssetGlobalSearchInterface);
        return new ItemSource(this.assetPool, [globalSearchLoader], implicitFilter);
    }
    /**
     * Experimental -- this is for early integration by Project X and will be removed
     * in the future.  When the real API is ready, this method will be marked deprecated
     * at that time.
     */
    getGlobalSearchAssetsSourceV3(query) {
        const implicitFilter = new AdobeAssetGlobalSearchFilter({
            _query: query,
            _matchingIDsFromSearch: new Set()
        });
        const globalSearchLoader = new GlobalSearchLoader(this._core.hostAssetContext.assetPool, this._core, this._core.hostAssetContext._globalSearchInterfaceV3, implicitFilter);
        return new ItemSource(this.assetPool, [globalSearchLoader], implicitFilter);
    }
    /**
     * Clients should use isOnline() to determine online/offline status so that SAM is responsible for encapsulating
     * which values of NetworkStatus mean "online" vs "offline".
     */
    get isOnline() {
        oneTimeWarning('AssetContext.isOnline', 'AssetContext.isOnline() is not fully implemented for web');
        return this.networkStatus !== NetworkStatus.Offline;
    }
    /**
     * Use for fine-grain network status (WAN vs Cellular). For offline/online, use isOnline() above.
     */
    get networkStatus() {
        oneTimeWarning('AssetContext.networkStatus', 'AssetContext.networkStatus is not fully implemented for web');
        return NetworkStatus.OnlineUnknown;
    }
    /**
     * Gets an Asset from cache, or loads it from the cloud. If ONS is disabled, it will also refresh
     * cached Assets as well.
     *
     * Uses @synchronized to ensure only one request for a particular Asset can be serviced at a time,
     * which prevents race conditions due to multiple load requests for the same Asset stepping on
     * one another.
     *
     * @param assetId {string}
     * @param resolveMetadata {RepositoryMetadata} -- For getAssetWith* calls that have already resolved the
     *        Asset repo metadata to get the assetId, so additional resolve requests are unnecessary.
     * @returns Promise<AdobeAsset>
     * @private
     */
    async _getAssetById(assetId, resolveMetadata) {
        const asset = this._assetPool.getLocalItemById(assetId);
        if (asset) {
            // Force refresh if ONS is disabled, because the data may be stale
            const forceRefresh = this._core.environment.getValue('DISABLE_ONS') || asset._needsRepoMetadata || false;
            if (forceRefresh) {
                // If the reload fails, we still return the asset but schedule a retry - that's equivalent to using the
                // cached data for the asset (e.g. if go offline), and having the asset update later.
                // @TODO Another potential optimisation here is to not load the project metadata again if we come to this
                // code path because the project lacks repo metadata and ONS is enabled.
                await asset._loadWithRetry();
            }
            else if (!asset._linksCameFromResolveAPI) {
                // No resolve metadata was passed in, or the links metadata came from listings,
                // which might be stale (esp. from USS). Assume that the metadata is likely up-to-date,
                // but refresh in the background in case it isn't.
                this._rapi.getAssetLinks(assetId).then((links) => {
                    const session = this._assetPool.createUpdateSession();
                    this._core.hostAssetContext._adobeAssetUpdater.updateLocalItem(session, asset, {
                        _links: links
                    });
                    asset._linksCameFromResolveAPI = true;
                    session.commit();
                });
            }
            return asset;
        }
        // AssetInterface (and links) aren't cached - need to fetch
        if (!resolveMetadata) {
            resolveMetadata = await this._rapi.getRepoMetadataById(assetId);
        }
        const libraryContext = await this._core.getLibraryContext();
        const projectInterface = new ProjectsInterface(this._core);
        // Check if this is a Project and populate some params for assetFromRepoMetadata.
        let projectMetadata = {};
        const assetInterface = new AssetInterface(this._rapi, assetId, resolveMetadata._links, resolveMetadata['repo:etag']);
        const permission = await assetInterface.getEffectivePermissions();
        const appMetadata = ((permission & Permission.ReadAppMetadata) === Permission.ReadAppMetadata) ? await assetInterface.getAppMetadata() : {};
        const isProject = appMetadataIsProject(appMetadata);
        if (isProject) {
            projectMetadata = await projectInterface.getProject(resolveMetadata['repo:assetId']);
        }
        const session = this._assetPool.createMutationSession();
        const newAsset = this._core._adobeAssetFactory.assetFromRepoMetadata(session, resolveMetadata, permission, appMetadata, libraryContext, isProject ? projectMetadata : undefined);
        session.commit();
        if (isProject) {
            await newAsset._load({ repoMetadata: resolveMetadata, appMetadata, effectivePermission: permission, projectMetadata });
        }
        else {
            await newAsset._load({ repoMetadata: resolveMetadata, appMetadata, effectivePermission: permission });
        }
        newAsset._linksCameFromResolveAPI = true;
        return newAsset;
    }
    /**
     * Returns the "/" folder for USM users and the "/users/<user-id>" folder for ESM user. For access to the
     * "/cloud-content" folder for the current user, use `getRootFolder()`
     */
    async getHomeFolder() {
        if (!this._homeFolder) {
            const homeDirectoryMetadata = await this._rapi.getHomeDirectoryMetadata();
            const session = this._assetPool.createMutationSession();
            this._homeFolder = this._core.hostAssetContext._adobeAssetUpdater.updateRemoteItem(session, homeDirectoryMetadata);
            session.commit();
            this._homeFolder.on(CHANGE, this._onHomeFolderChange);
        }
        return this._homeFolder;
    }
    /**
     * Gets the current user's `/cloud-content` folder. If the folder does not exist, it will be
     * created.
     */
    async getRootFolder() {
        if (!this._rootFolder) {
            this._rootFolder = await this.getAssetWithRepoPath('/cloud-content');
        }
        return this._rootFolder;
    }
    async getUserRepoID() {
        return this.getHomeFolder().then((asset) => asset.repositoryID);
    }
    async getAssetWithCloudID(assetId, confirmAccess = false) {
        if (confirmAccess) {
            await this._core.collaborationContext.confirmAccess(assetId);
        }
        return this._getAssetById(assetId);
    }
    /**
     * Resolves the Asset at the given assetPath. If repositoryId is not passed, it will look up the repositoryId
     * for the current user, and assume that the path is relative to the user's home directory (`/` for USM users,
     * `/users/<userId>` for ESM users). If the repositoryId is passed, then the path is assumed to be absolute.
     *
     * @example <caption>Assuming 'xyz' is the current userId and 'abc' is the repositoryId</caption>
     *    getAssetWithRepoPath('/cloud-content');                  // returns /users/xyz/cloud-content
     *    getAssetWithRepoPath('/users/xyz/cloud-content', 'abc'); // "
     *    getAssetWithRepoPath('/users/xyz/cloud-content');        // NOT_FOUND error
     *
     * @param assetPath {string}
     * @param repositoryId {string} (optional)
     * @return AdobeAsset
     */
    async getAssetWithRepoPath(assetPath, repositoryId) {
        if (!repositoryId) {
            // Since clients typically start by resolving a folder by path, also get the `/` folder so that it is
            // monitored for ONS changes.
            const homeFolder = await this.getHomeFolder();
            repositoryId = homeFolder.repositoryID;
            const userContentRootFolder = homeFolder.repoPath;
            assetPath = path.join(userContentRootFolder, assetPath);
        }
        try {
            const repositoryMetadata = await this._rapi.getRepoMetadataByPath(repositoryId, assetPath);
            return this._getAssetById(repositoryMetadata['repo:assetId'], repositoryMetadata);
        }
        catch (e) {
            const topLevelFolders = ['cloud-content', 'temp'];
            for (const topLevelFolder of topLevelFolders) {
                const topLevelPath = `/${topLevelFolder}`;
                const appearsOnlyOnce = assetPath.indexOf(topLevelPath) === assetPath.lastIndexOf(topLevelPath);
                if (e.code === SAMErrorCode.NOT_FOUND && appearsOnlyOnce && assetPath.endsWith(topLevelPath)) {
                    const homeFolder = await this.getHomeFolder();
                    // return the promise to create the folder
                    return homeFolder.createFolder(topLevelFolder);
                }
            }
            throw e;
        }
    }
    // including confirmAccess to match Seal bindings.
    async getAssetWithAssetRefString(assetRefStr, confirmAccess = false) {
        let parsedAssetRef;
        try {
            parsedAssetRef = JSON.parse(assetRefStr);
        }
        catch (error) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Incorrectly formatted AssetRefString', { underlyingError: error });
        }
        if (parsedAssetRef.cloud_id) {
            return this.getAssetWithCloudID(parsedAssetRef.cloud_id, confirmAccess);
        }
        else if (parsedAssetRef.path) {
            return this.getAssetWithRepoPath(parsedAssetRef.path, parsedAssetRef.repository_id);
        }
        else {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Invalid AssetRefString');
        }
    }
    /**
     * @package -- only for use in SAM.js
     */
    _createAssetFromMetadata(assetMetaData) {
        const session = this._assetPool.createUpdateSession();
        const localItem = this._core.hostAssetContext._adobeAssetUpdater.updateRemoteItem(session, assetMetaData);
        session.commit();
        return localItem;
    }
    async _repositoryNameFromID(id) {
        return await this._rapi.getRepositoryName(id);
    }
    /**
     * @package -- only for use in SAM.js
     */
    async _getListOfRepositoryIDs() {
        const response = await this._rapi.getRepositoriesData();
        if (!response) {
            return undefined;
        }
        const children = response.children;
        return children.map(repositoryInfo => repositoryInfo._embedded[RelType.PRIMARY]['repo:repositoryId']);
    }
    /**
     * XD layers their own artifact publishing system on top of CCAC Projects, backed by their own service. Clients like
     * CDO and Projects UI need to call this API in order to get metadata for rendering these special Artifacts.
     * @param assetId
     */
    async getXDCEData(assetId) {
        return this._core.environment.fetch(`https://${this._core.environment.endpoints.XDCE_HOST}/v2/document/${assetId}`)
            .then(response => response.json());
    }
    removeAssetsFromAssetsPool(assetIds) {
        this._assetPool.removeLocalItems(assetIds);
    }
    async permanentlyDeleteAssets(assets) {
        let assetResults;
        // If client wll try to resolve permanently deleted assets, SAM should not return the cached assets
        // from asset pool. Hence removing permanently deleted assets from asset pool.
        const onItemsChanged = (event) => {
            // Remove only successfully permanently deleted items from asset pool.
            if (event.discardedItems) {
                const permanentDeletedAssetsIds = event.discardedItems.filter(value => assets.includes(value)).map(item => item.id);
                if (permanentDeletedAssetsIds.length) {
                    this.removeAssetsFromAssetsPool(permanentDeletedAssetsIds);
                }
            }
        };
        // ITEMS_CHANGED event is triggered from item pool for all update/mutate sessions.
        // This event will be triggered by other sessions (sessions other than the session
        // created by this permanent delete operation) also. But in onItemsChanged we will
        // remove assets which were added in this session.
        this._assetPool.on(ITEMS_CHANGED, onItemsChanged);
        try {
            await ItemBase.runExclusiveTask('delete', assets, async () => {
                const assetsById = new Map();
                const ops = assets.map((asset) => {
                    assetsById.set(asset.id, asset);
                    const deleteOp = {
                        op: 'delete',
                        id: asset.id,
                        target: {
                            'repo:assetId': asset.id,
                        }
                    };
                    // repository id is optional field in target here
                    // because target is specified by assetId
                    if (asset.repositoryID) {
                        const target = deleteOp.target;
                        target['repo:repositoryId'] = asset.repositoryID;
                    }
                    deleteOp.recursive = asset.isFolder;
                    return deleteOp;
                });
                assetResults = await this._runOperations(ops, assetsById, (session, asset) => {
                    // Since we know the asset has been permanently deleted
                    // we should clear any renditions on it to prevent memory leaks.
                    asset._releaseRenditions();
                    // If the Asset was removed from the pool while the operation was underway (such as by ONS-
                    // triggered refresh), do not try to update it again, as it will get re-added to the pool.
                    if (!this._assetPool.getLocalItemById(asset.id)) {
                        return undefined;
                    }
                    // We update the effectivePermission here first to notify on "change" subscribers
                    // of AdobeAsset (such as AssetLink) that may need to know this assets permission is set to None before
                    // it gets discarded.
                    this._core.hostAssetContext._adobeAssetUpdater.updateLocalItem(session, asset, { effectivePermission: Permission.None });
                    session.queueDiscardedItem(asset);
                    return undefined;
                });
            });
        }
        finally {
            this._assetPool.off(ITEMS_CHANGED, onItemsChanged);
        }
        return assetResults;
    }
    async getCloudQuotaUsedAndMax() {
        return this._rapi.getCloudQuotaUsedAndMax();
    }
    // Note: Other props/methods in JS bindings that are only used in the SAM.cpp demo app have been removed.
    //       E.g. release(), appDidEnterBackground(), etc.
    // end [SAM-2245][SAM-2244][SAM-2242]
    async transfer(source, target) {
        let targetId;
        if (target instanceof Space) {
            const workingFiles = await target.getChild('working_files');
            if (!workingFiles) {
                throw SAMError.create(SAMErrorCode.NOT_FOUND, 'unable to find working_files for provided space');
            }
            targetId = workingFiles.id;
        }
        else {
            targetId = target.cloudID;
        }
        const payload = {
            source: { assetId: source.cloudID, repositoryId: source.repositoryID },
            target: { assetId: targetId, repositoryId: target.repositoryID }
        };
        const response = await this._assetTransferService.transfer(payload);
        const updater = this.assetPool.createUpdateSession();
        updater.queueDiscardedItem(source);
        updater.commit();
        return response;
    }
    /**
     * Internal helper method for moving assets (batch).
     * @param assets assets to be moved (in the same repository)
     * @param destFolder destination folder where assets are intended to be moved
     * @param retryOnConflict whether to retry the move op for failed assets in case of conflict
     * @param assetConflictMap map to save the newName and count used for all assets in batch
     * @returns the result for batch operation after retrying for failed assets if asked.
     */
    async _moveAssets(assets, destFolder, retryOnConflict = true, assetConflictMap) {
        let resultOps = { failedResults: [], newModels: [] };
        await ItemBase.runExclusiveTask('move', assets, async () => {
            // In case of Asset created from USS response, repo id is not present
            // Jira on USS https://jira.corp.adobe.com/browse/SRCH-1989
            if (!destFolder.repositoryID) {
                await destFolder._load();
            }
            if (assetConflictMap === undefined) {
                assetConflictMap = new Map();
            }
            const assetsById = new Map();
            const ops = assets.map((asset) => {
                assetsById.set(asset.id, asset);
                let newPath = `${destFolder.repoPath}/${asset.name}`;
                newPath = batchFormatFileName(this._core, asset.id, newPath, false, assetConflictMap);
                const op = {
                    op: 'move',
                    id: asset.id,
                    source: {
                        'repo:assetId': asset.id
                    },
                    target: {
                        'repo:path': newPath,
                        'repo:repositoryId': destFolder.repositoryID
                    }
                };
                if (asset.repositoryID) {
                    const source = op.source;
                    source['repo:repositoryId'] = asset.repositoryID;
                }
                // re-fetch repository name if the dest folder is in a different repository
                if (asset.repositoryID !== destFolder.repositoryID) {
                    asset._clearRepositoryName();
                }
                if (asset.isFolder) {
                    const session = this._assetPool.createUpdateSession();
                    updateCachedDescendents({
                        assetPool: this._assetPool,
                        asset,
                        session,
                        operation: { type: 'rename', newPath }
                    });
                    session.commit();
                }
                return op;
            });
            // We need to update the repo:path of all the descendants of the folder in case of move/rename.
            // https://jira.corp.adobe.com/browse/SAM-2290
            resultOps = await this._runOperations(ops, assetsById);
        });
        if (retryOnConflict) {
            return await retryFailedOpsDueToConflict(resultOps, destFolder, assetConflictMap, this._moveAssets.bind(this));
        }
        return resultOps;
    }
    /**
     * Public method to move assets to a folder
     * @param assets assets to be moved
     * @param destFolder destination folder where spaces are intended to be moved
     * @returns the result for batch operation after retrying for failed assets.
     */
    async moveAssets(assets, destFolder) {
        return this._moveAssets(assets, destFolder);
    }
    /**
     * Checks an array of the provided AdobeAssets have an outputs directory and returns true
     * if any of the assets have an output folder. This will also update each
     * AdobeAsset.hasArtifacts property to true if the asset does have it.
     *
     * Currently the UI is not expected
     * to show which folders have artificats individually but rather if any of the selected
     * assets contain artifacts.
     *
     * Usage of this API requires the user to have at least READ
     * permissions on the source document.
     *
     * @note This method is inteded to be a stop gap solution until simulated sub assets
     * are shipped. See https://wiki.corp.adobe.com/x/h8JNp
     *
     * @param assets {AdobeAsset}
     */
    async assetsHaveArtifacts(assets) {
        const assetIndentifiers = mapAssetsToAssetIndentifier(assets);
        const results = await this._assetReviewServiceInterface.hasOutputs(assetIndentifiers);
        if (results.length === 0) {
            return false;
        }
        const session = this.assetPool.createUpdateSession();
        this._core.hostAssetContext._adobeAssetUpdater.updateRemoteItems(session, results.map(result => {
            return { 'repo:assetId': result['repo:assetId'], 'hasArtifacts': true };
        }));
        session.commit();
        return true;
    }
    /**
     * The asset to delete the output folders for. Currently the
     * API only supports one asset at a time.
     *
     * @param asset Single asset to delete.
     * @package Only to be used within SAM.js
     */
    async _deleteAssetOutputsFolder(asset) {
        return this._assetReviewServiceInterface.discardOutputsForAsset(asset.id);
    }
    /**
     * @package -- Internal helper method for copying assets (batch).
     *
     * @param assets assets to be copied (in the same repository)
     * @param destFolder destination folder where assets are intended to be copied
     * @param retryOnConflict whether to retry the copy op for failed assets in case of conflict
     * @param assetConflictMap map to save the newName and count used for all assets in batch
     * @returns the result for batch operation after retrying for failed assets if asked.
     */
    async _copyAssets(assets, destFolder, retryOnConflict = true, assetConflictMap) {
        let resultOps = { failedResults: [], newModels: [] };
        await ItemBase.runExclusiveTask('copy', assets, async () => {
            // In case of Asset created from USS response, repo id is not present
            // Jira on USS https://jira.corp.adobe.com/browse/SRCH-1989
            if (!destFolder.repositoryID) {
                await destFolder._load();
            }
            if (assetConflictMap === undefined) {
                assetConflictMap = new Map();
            }
            const assetsById = new Map();
            const ops = assets.map((asset) => {
                assetsById.set(asset.id, asset);
                let newPath = `${destFolder.repoPath}/${asset.name}`;
                const sameFolder = destFolder.repoPath === asset.repoPath.substring(0, asset.repoPath.lastIndexOf('/'));
                newPath = batchFormatFileName(this._core, asset.id, newPath, sameFolder, assetConflictMap);
                // We only want to copy App metadata for PDFs and no other file format
                // This is a temporary solution and we will have a better solution on the service side
                const includeResources = [{ 'reltype': RelType.PRIMARY }];
                if (asset.mediaType === 'application/pdf') {
                    includeResources.push({ 'reltype': RelType.APP_METADATA });
                }
                const operation = {
                    op: 'copy',
                    id: asset.id,
                    source: {
                        'repo:assetId': asset.id,
                    },
                    target: {
                        'repo:path': newPath,
                        'repo:repositoryId': destFolder.repositoryID
                    },
                    // The following copies Content Resources for all formats
                    // and Application Metadata for PDFs only
                    includeResources
                };
                if (asset.repositoryID) {
                    const source = operation.source;
                    source['repo:repositoryId'] = asset.repositoryID;
                }
                return operation;
            });
            resultOps = await this._runOperations(ops, assetsById, (session, asset, remoteItem) => {
                if (!remoteItem) {
                    throw SAMError.create(SAMErrorCode.UNKNOWN, 'Missing metadata from asset bulk operation.');
                }
                const newAsset = this._core.hostAssetContext._adobeAssetUpdater.updateRemoteItem(session, remoteItem);
                return newAsset;
            });
        });
        if (retryOnConflict) {
            return await retryFailedOpsDueToConflict(resultOps, destFolder, assetConflictMap, this._copyAssets.bind(this));
        }
        return resultOps;
    }
    /**
     * Public method to copy assets to a folder
     * @param assets assets to be copied
     * @param destFolder destination folder where spaces are intended to be copied
     * @returns the result for batch operation after retrying for failed assets.
     */
    async copyAssets(assets, destFolder) {
        return await this._copyAssets(assets, destFolder);
    }
    async duplicateAssets(assets) {
        const parentRepoPath = assets[0].repoPath.substring(0, assets[0].repoPath.lastIndexOf('/'));
        const parentAsset = await this._core.hostAssetContext.getAssetWithRepoPath(parentRepoPath, assets[0].repositoryID);
        return await this._copyAssets(assets, parentAsset);
    }
    /**
     * @package -- for use in SAM.js only
     */
    async _runOperations(ops, assetsById, assetUpdater) {
        return await this._rapi.runOperationsForContext(ops, assetsById, this._assetPool, assetUpdater ? assetUpdater : (session, asset, updatedAssetMetaData) => {
            if (!updatedAssetMetaData) {
                throw SAMError.create(SAMErrorCode.UNKNOWN, 'Missing metadata from asset bulk operation.');
            }
            this._core.hostAssetContext._adobeAssetUpdater.updateLocalItem(session, asset, updatedAssetMetaData);
            return undefined;
        }, [
            {
                // If remote asset found to be SAMErrorCode.ACCESS_DENIED or SAMErrorCode.NOT_FOUND
                // that means we need to update local asset to get it in sync with remote
                match: (error) => error.code === SAMErrorCode.ACCESS_DENIED || error.code === SAMErrorCode.NOT_FOUND,
                action: async (asset, error) => {
                    await asset._load();
                    return { model: asset, error };
                }
            }
        ]);
    }
    /**
     * We are keeping the following library element methods here to support SAM.cpp and will
     * move them to LibraryContext after we get better support for library elements.
     */
    async restoreLibraryElement(elementId) {
        await this._libraryInterface.restoreLibraryElement(elementId);
    }
    async permanentlyDeleteLibraryElement(elementId) {
        await this._libraryInterface.permanentlyDeleteLibraryElement(elementId);
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     *    For internal use only.
     */
    getAssetInterface(repoMetadata) {
        return new AssetInterface(this._rapi, repoMetadata['repo:assetId'], repoMetadata._links, repoMetadata['repo:etag']);
    }
    /**
     * Asynchronous method that returns the CommentContext associated with the asset identified by assetId.
     *
     * Clients with an AdobeAsset instance should use AdobeAsset.getCommentContext(),
     * but when the Commenting UI has ( SESSION_TYPE === 'logged-out' ),
     * it isn't possible to construct an AdobeAsset, so they will call this method directly.
     *
     * CommentContext's are asset specific, and they are cached in a Map of WeakRef's,
     * so client code should ensure there is a pinning reference if they want the context
     * to be preserved.
     *
     * @param assetId {string}
     * @returns {Promise<CommentContent>}
     * @see {@link AdobeAsset.getCommentContext}
     */
    async getCommentContext(assetId) {
        var _a;
        let comments = (_a = this._commentsById.get(assetId)) === null || _a === void 0 ? void 0 : _a.deref();
        if (!comments) {
            comments = new CommentContext(this._core, assetId);
            this._commentsById.set(assetId, new WeakRef$1(comments));
        }
        return comments;
    }
}
__decorate([
    memoize
], AssetContext.prototype, "sharedSource", null);
__decorate([
    memoize
], AssetContext.prototype, "recentAssetsSource", null);
__decorate([
    memoize
], AssetContext.prototype, "MRUAssetsSource", null);
__decorate([
    memoize
], AssetContext.prototype, "publishedSource", null);
__decorate([
    memoize
], AssetContext.prototype, "discardedSource", null);
__decorate([
    synchronized,
    __param(0, key)
], AssetContext.prototype, "_getAssetById", null);
__decorate([
    synchronized
], AssetContext.prototype, "getHomeFolder", null);
__decorate([
    synchronized
], AssetContext.prototype, "getAssetWithRepoPath", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// Properties of a link reference that correspond to mutable asset metadata properties (e.g. name, path etc).
// These are properties that can change on the asset, but don't correspond to a content change
// (i.e. the link state would be MetadataOutOfDate rather than OutOfDate)
const assetMetadataProperties = [
    'repo:repositoryId',
    'repo:name',
    'repo:etag',
    'stRef:relativePath',
    'stRef:documentID'
];
function isBrokenAsset(asset) {
    // Need to know if permission exactly matches Ack or None, rather than contains, so can't use
    // hasPermission() here.
    const permission = asset['_permissions'];
    return permission === Permission.Ack
        || permission === Permission.None;
}
// Illustrator (prior to MAX 2021) does not update the instance ID on save, so we need to fall back to the etag in this case
// See https://jira.corp.adobe.com/browse/AI-4251857
const typesWithUnreliableInstanceID = [
    'application/illustrator'
];
class AssetLink extends EventEmitter {
    constructor(_assetLinkList, _data) {
        super();
        this._assetLinkList = _assetLinkList;
        this._data = _data;
        this._resolutionErrors = [];
        this._onChange = () => {
            var _a, _b;
            // Check if any of the resolved assets broke, and transfer their errors to the link
            // so we get the right link state (broken or restricted)
            const brokenAssets = (_a = this._resolvedAssets) === null || _a === void 0 ? void 0 : _a.filter(isBrokenAsset);
            if (brokenAssets === null || brokenAssets === void 0 ? void 0 : brokenAssets.length) {
                this._resolutionErrors = brokenAssets.map(asset => SAMError.create(asset['_permissions'] === Permission.Ack ? SAMErrorCode.ACCESS_DENIED : SAMErrorCode.NOT_FOUND, 'lost access'));
                this._resolvedAssets = (_b = this._resolvedAssets) === null || _b === void 0 ? void 0 : _b.filter(asset => !brokenAssets.includes(asset));
            }
            this.emit('change');
            this._assetLinkList._onChange();
        };
    }
    async _setLinkData(linkData, asset) {
        this._data = linkData;
        if (asset) {
            this._resolvedAssets = [asset];
        }
        this._onChange();
        if (!this.adobeAsset || this.assetID !== this.adobeAsset.cloudID) {
            await this._resolveWithRetry();
        }
        // TODO: Check if relative path changed
        return this;
    }
    async _setLinkedAsset(asset) {
        const data = this._getLinkDataForAsset(asset);
        return this._setLinkData(data, asset);
    }
    _getLinkDataForAsset(asset) {
        var _a, _b;
        const linkData = {
            'repo:assetId': asset.cloudID,
            'repo:repositoryId': asset.repositoryID,
            'repo:etag': asset.etag,
            'dc:format': asset.mediaType,
            'repo:name': asset.name,
            'stRef:documentID': (_a = asset.embeddedMetadata.data) === null || _a === void 0 ? void 0 : _a['xmpMM:DocumentID'],
            'stRef:instanceID': (_b = asset.embeddedMetadata.data) === null || _b === void 0 ? void 0 : _b['xmpMM:InstanceID'],
            'stRef:manager': 'Adobe Cloud'
        };
        // Add relative path if it exists
        const parent = this._assetLinkList.containerAsset;
        if (parent && parent.repositoryID === asset.repositoryID) {
            linkData['stRef:relativePath'] = path.relative(path.dirname(parent.repoPath), asset.repoPath);
        }
        return linkData;
    }
    // Ensures that link reference strings are always serialized with the keys in the same order
    _serializeLinkData(linkData) {
        return JSON.stringify(linkData, Object.keys(linkData).sort());
    }
    _resolveWithRetry() {
        return this._resolve();
    }
    async _resolve() {
        var _a;
        const resolvedAssets = [];
        const resolutionErrors = [];
        const handleError = (e) => {
            if ([SAMErrorCode.NOT_FOUND, SAMErrorCode.ACCESS_DENIED].includes(e.code)) {
                resolutionErrors.push(e);
            }
            else {
                throw e;
            }
        };
        try {
            const asset = await this._context.getAssetWithCloudID(this.assetID);
            if (!asset.embeddedMetadata.isLoaded) {
                // First time, we need to load - after that, it'll get updated whenever the asset is refreshed
                await asset.embeddedMetadata.load();
            }
            resolvedAssets.push(asset);
        }
        catch (e) {
            this._core.environment.error(`Failed to resolve link by id (${this.assetID}): ${e}`);
            handleError(e);
        }
        const parent = this._assetLinkList.containerAsset;
        if (this.relativePath && parent && parent.repositoryID === this.repositoryID) {
            // Resolve by relative path, but this only makes sense if the container asset and linked
            // asset are in the same repository.
            const repoPath = path.join(path.dirname(parent.repoPath), this.relativePath);
            try {
                const asset = await this._context.getAssetWithRepoPath(repoPath, this.repositoryID);
                if (!asset.embeddedMetadata.isLoaded) {
                    // First time, we need to load - after that, it'll get updated whenever the asset is refreshed
                    await asset.embeddedMetadata.load();
                }
                // Most of the time, resolution by path will match resolution by asset id - only add an alternate
                // if they're different.
                if (!resolvedAssets[0] || resolvedAssets[0].cloudID !== asset.cloudID) {
                    if ((_a = resolvedAssets[0]) === null || _a === void 0 ? void 0 : _a.isDiscarded) {
                        // Special case if the resolution by id is discarded - we want the path-based resolution
                        // to become the primary adobeAsset
                        resolvedAssets.unshift(asset);
                    }
                    else {
                        resolvedAssets.push(asset);
                    }
                }
            }
            catch (e) {
                this._core.environment.error(`Failed to resolve link by path: ${this.repositoryID}, ${repoPath}`);
                handleError(e);
            }
        }
        // Attach event listeners so we can notify the link when the asset changes
        this._resolvedAssets && this._resolvedAssets.forEach(asset => asset.off('change', this._onChange));
        resolvedAssets.forEach(asset => asset.on('change', this._onChange));
        this._resolvedAssets = resolvedAssets;
        this._resolutionErrors = resolutionErrors;
        this._onChange();
    }
    get _core() {
        return this._assetLinkList._core;
    }
    get _context() {
        return this._assetLinkList._manager._context;
    }
    /**
     * The asset that the link resolves to - this will not be defined if the linkStatus
     * is Restricted or Broken.
     */
    get adobeAsset() {
        var _a;
        return (_a = this._resolvedAssets) === null || _a === void 0 ? void 0 : _a[0];
    }
    /**
     * An alternative asset which can be used as link, in case there is ambiguity resolving the link.
     * This will usually be undefined.
     */
    get alternativeAdobeAsset() {
        var _a;
        return (_a = this._resolvedAssets) === null || _a === void 0 ? void 0 : _a[1];
    }
    /**
     * The current state of the link (OutOfDate, UpToDate, Restricted or Broken).
     */
    get linkStatus() {
        if (!this._resolvedAssets) {
            return 'UpToDate';
        }
        if (!this.adobeAsset) {
            // If we get a 403 resolving an asset, we know it exists but the user doesn't have access
            // This corresponds to the Restricted state - we can direct the user to request access via assets web.
            return this._resolutionErrors.some(error => error.code === SAMErrorCode.ACCESS_DENIED) ? 'Restricted' : 'Broken';
        }
        if (this.adobeAsset.isDiscarded) {
            // If the primary asset resolved is discarded, it means that we don't have an undiscarded alternative
            // (otherwise it would be the primary asset). We therefore treat the link state as Discarded.
            // This means that the user can be presented the option to restore the asset to fix the link.
            return 'Discarded';
        }
        // Check to see if the asset has changed - we check the xmpMM:InstanceID, which should be modified on every save.
        // There are two exceptions:
        // 1) If the asset doesn't have any XMP metadata (and neither does the link), we fall back to compare the repo:etag
        // 2) Illustrator has a bug that it doesn't update the XMP metadata on save - so we also fall back to the repo:etag
        //    for Illustrator types, in case they were edited in an older version of Illustrator.
        const instanceID = this.adobeAsset.embeddedMetadata.data['xmpMM:InstanceID'] || '';
        const shouldCheckEtag = (!instanceID && !this.instanceID) || typesWithUnreliableInstanceID.includes(this.type);
        const assetHasChanged = instanceID !== this.instanceID || (shouldCheckEtag && this.etag !== this.adobeAsset.etag);
        if (this.alternativeAdobeAsset || assetHasChanged) {
            return 'OutOfDate';
        }
        // Check whether any metadata is out of date
        const assetLinkData = this._getLinkDataForAsset(this.adobeAsset);
        const hasMetadataChanges = assetMetadataProperties.some(key => assetLinkData[key] !== this._data[key]);
        return hasMetadataChanges ? 'MetadataOutOfDate' : 'UpToDate';
    }
    /**
     * The fragment specifier which can be used by the client to link to a fragment within the asset.
     */
    get fragmentIdentifier() {
        return this._data['stRef:fromPart'] || '';
    }
    /**
     * The version of the asset to which the link is referring to. This is only used if linking to a specific
     * version, and the version must be specified at the time of link creation.
     */
    get version() {
        return this._data['repo:version'] || '';
    }
    /**
     * The asset ID of the asset which the link refers to.
     */
    get assetID() {
        return this._data['repo:assetId'];
    }
    /**
     * The repository ID of the asset which the link refers to.
     */
    get repositoryID() {
        return this._data['repo:repositoryId'] || '';
    }
    /**
     * The owner ID of the asset which the link refers to.
     */
    // get ownerID(): string {
    //     return this._data['stRef:fromPart'] || '';
    // }
    /**
     * The name of the asset when the link was created or last updated.
     */
    get name() {
        return this._data['repo:name'] || '';
    }
    /**
     * The type of the asset when the link was created.
     */
    get type() {
        return this._data['dc:format'] || '';
    }
    /**
     * The etag of the asset when the link was created or last updated.
     */
    get etag() {
        return this._data['repo:etag'] || '';
    }
    /**
     * The relative path of the asset when the link was created or last updated.
     */
    get relativePath() {
        return this._data['stRef:relativePath'] || '';
    }
    /**
     * The document id as per the XMP metadata when the link was created or last updated.
     */
    get documentID() {
        return this._data['stRef:documentID'] || '';
    }
    /**
     * The instance id as per the XMP metadata when the link was created or last updated.
     */
    get instanceID() {
        return this._data['stRef:instanceID'] || '';
    }
    /**
     * Obtain the asset link reference to be stored in the container document for the link.
     * Note: Clients can choose to store this as a string by calling getAssetLinkRefString() instead.
     */
    getAssetLinkRef() {
        return Object.assign({}, this._data);
    }
    /**
     * Obtain the asset link reference string to be stored in the container document for the link.
     * Note: Clients can choose to store this as an object by calling getAssetLinkRef() instead.
     */
    getAssetLinkRefString() {
        return this._serializeLinkData(this._data);
    }
    /**
     * Obtain the asset link reference corresponding to the given asset. This is useful for knowing
     * the new link reference that would be obtained by updating to the given asset
     */
    getNewAssetLinkRef(asset) {
        return this._getLinkDataForAsset(asset);
    }
    /**
     * Obtain the asset link reference string corresponding to the given asset. This is useful for knowing
     * the new linkReference string that would be obtained by updating to the given asset
     */
    getNewAssetLinkRefString(asset) {
        return this._serializeLinkData(this.getNewAssetLinkRef(asset));
    }
    /**
     * Called by the clients to accept the latest changes and get the new asset link reference
     * @param asset The new asset to update to (or the current asset to accept an update)
     * @returns The new linkReference string.
     */
    async updateWithAsset(asset) {
        await this._setLinkedAsset(asset);
        return this.getAssetLinkRefString();
    }
    /**
     * Called by the clients to accept the latest changes and get the new asset link reference
     * @param linkRef A new link reference to update to
     * @returns The new link reference.
     */
    async updateWithLinkRef(linkRef) {
        await this._setLinkData(linkRef);
        return linkRef;
    }
    /**
     * Called by the clients to accept the latest changes and get the new asset link reference
     * @param linkRef A new link reference string to update to
     * @returns The new link reference string.
     */
    async updateWithLinkRefString(linkRefString) {
        await this._setLinkData(JSON.parse(linkRefString));
        return linkRefString;
    }
    /**
     * Called by the clients to accept metadata updates when the link is in the OutOfDate state.
     * If called when the link is in a different state, this does nothing.
     * @returns The new linkReference string.
     */
    acceptMetadataChanges() {
        if (this.adobeAsset && this.linkStatus === 'MetadataOutOfDate') {
            const assetLinkData = this._getLinkDataForAsset(this.adobeAsset);
            // Note that we substitute fields rather than replacing the whole link reference, because the link reference
            // may include fragment data that we don't want to delete
            this._data = Object.assign(Object.assign({}, this._data), assetLinkData);
            this._onChange();
        }
        return this.getAssetLinkRefString();
    }
}
__decorate([
    synchronized
], AssetLink.prototype, "_setLinkData", null);
__decorate([
    retry
], AssetLink.prototype, "_resolveWithRetry", null);
__decorate([
    observable
], AssetLink.prototype, "adobeAsset", null);
__decorate([
    observable
], AssetLink.prototype, "alternativeAdobeAsset", null);
__decorate([
    observable
], AssetLink.prototype, "linkStatus", null);
__decorate([
    observable
], AssetLink.prototype, "fragmentIdentifier", null);
__decorate([
    observable
], AssetLink.prototype, "version", null);
__decorate([
    observable
], AssetLink.prototype, "assetID", null);
__decorate([
    observable
], AssetLink.prototype, "repositoryID", null);
__decorate([
    observable
], AssetLink.prototype, "name", null);
__decorate([
    observable
], AssetLink.prototype, "type", null);
__decorate([
    observable
], AssetLink.prototype, "etag", null);
__decorate([
    observable
], AssetLink.prototype, "relativePath", null);
__decorate([
    observable
], AssetLink.prototype, "documentID", null);
__decorate([
    observable
], AssetLink.prototype, "instanceID", null);
__decorate([
    observable
], AssetLink.prototype, "getAssetLinkRef", null);
__decorate([
    observable
], AssetLink.prototype, "getAssetLinkRefString", null);
__decorate([
    observable
], AssetLink.prototype, "getNewAssetLinkRef", null);
__decorate([
    observable
], AssetLink.prototype, "getNewAssetLinkRefString", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class AssetLinkList extends EventEmitter {
    constructor(_manager, _containerAsset) {
        super();
        this._manager = _manager;
        this._containerAsset = _containerAsset;
        this._links = new Set();
        // We store directly monitored asset ids as a map, where the key is the lower case
        // asset id, and the value is the asset id passed in. This allows us to easily look
        // up the original case of the asset id passed in, so we can send notifications in
        // the correct case, in case a client passed it in with the wrong case
        this._directlyMonitoredAssetIDs = new Map();
        this._onChange = () => {
            this.emit('change');
        };
        this._refreshLinks = (changedIDs = new Set(this.monitoredAssetIDs)) => {
            this.forEach(link => {
                if (!link.adobeAsset && changedIDs.has(link.assetID)) {
                    // Link is broken, but asset id changed - try re-resolving
                    link._resolveWithRetry();
                }
                // Note - we do not need to refresh any assets; this will be handled by AssetContext
                // since it's updating all the assets in the asset pool.
            });
        };
        this._onONSNotification = (changedIDs) => {
            this._refreshLinks(changedIDs);
            // As well as refreshing changed links, we emit a notified event, so clients can know that
            // we were notified of a change to these assets - this is useful if clients are only monitoring
            // asset ids directly, not via links.
            // Compute the intersection of changedIDs and the ones we're monitoring:
            const monitoredAssetIDs = this._monitoredAssetIDs;
            const monitoredChangedIDs = new Set();
            changedIDs.forEach(id => {
                if (monitoredAssetIDs.has(id)) {
                    monitoredChangedIDs.add(id);
                }
                // The client might have passed in an asset id in a different case to the normalized
                // platform case (e.g. clients like CC Libraries might have some older cached client
                // ids). We therefore need to map the asset id we get from the notification to the
                // one passed in.
                const normalizedId = this._directlyMonitoredAssetIDs.get(id.toLowerCase());
                if (normalizedId) {
                    monitoredChangedIDs.add(normalizedId);
                }
            });
            this.emit('notified', [...monitoredChangedIDs]);
        };
    }
    get _core() {
        return this._manager._core;
    }
    /**
     * Monitor an asset id directly, rather than going through an AssetLink. This allows you to monitor
     * a specific asset for changes without maintaining any state (i.e. it will not resolve the assetID
     * to an AdobeAsset).
     *
     * This is a lower-level API and should only be used if you're explicitly not wanting to load the
     * asset with SAM (for example, the CC Libraries API uses this since it fetches the asset data
     * from Melville, rather than R-API via SAM, so it just needs the raw notification).
     */
    addAssetID(assetID) {
        this._directlyMonitoredAssetIDs.set(assetID.toLowerCase(), assetID);
        this._onChange();
    }
    /**
     * Stop monitoring an asset id that was added via `addAssetId()`. Note that if any links in the list
     * also reference this asset id, this will not stop monitoring them.
     */
    removeAssetID(assetID) {
        this._directlyMonitoredAssetIDs.delete(assetID.toLowerCase());
        this._onChange();
    }
    /**
     * Add one or more existing links (from link reference or link reference strings) to the list.
     */
    add(linkReferences) {
        return Promise.all(linkReferences.map(async (linkRef) => {
            // TODO: Should check if already have duplicate link
            const linkReference = typeof linkRef === 'string' ? JSON.parse(linkRef) : linkRef;
            const link = new AssetLink(this, linkReference);
            this._links.add(link);
            this._onChange();
            // When adding an existing link we can always return an AssetLink even if it fails
            // We'll retry the resolution after which the link state will update.
            await link._resolveWithRetry();
            return link;
        }));
    }
    /**
     * Create a new link from an asset, and add it to the list.
     * @param asset
     * @param [fragmentSpecifier] An optional string containing the fragmentSpecifier within the asset.
     * @param [version] An optional string containing the version identifier of the Adobe asset.
     */
    async create(asset, fragmentSpecifier, version) {
        const assetID = typeof asset === 'string' ? asset : asset.cloudID;
        const link = new AssetLink(this, {
            'repo:assetId': assetID,
            'stRef:fromPart': fragmentSpecifier,
            'repo:version': version,
            'stRef:manager': 'Adobe Cloud'
        });
        this._links.add(link);
        this._onChange();
        // When creating a new link, we do _not_ retry on failure - this is because the link reference needs to be updated.
        // Errors are propagated to the client.
        await link._resolve();
        if (link.adobeAsset) {
            link._setLinkedAsset(link.adobeAsset);
        }
        return link;
    }
    /**
     * Remove an asset link from the list. This will cause it to stop being actively monitored for changes.
     */
    remove(link) {
        this._links.delete(link);
        this._onChange();
    }
    /**
     * Clear the whole list.
     */
    clear() {
        this._links.clear();
        this._onChange();
    }
    /**
     * Number of asset links in the list.
     */
    get count() {
        return this._links.size;
    }
    /**
     * Get an array of all the asset links in the list.
     */
    get links() {
        return [...this._links];
    }
    /**
     * Get a set of all the asset ids being monitored by the link list. This includes
     * asset ids in any links, as well as asset ids being directly monitored.
     */
    get _monitoredAssetIDs() {
        const assetIDs = new Set(this._directlyMonitoredAssetIDs.values());
        this.forEach(link => {
            assetIDs.add(link.assetID);
            // We also monitor the path-based resolution if it differs from asset-based:
            if (link.alternativeAdobeAsset) {
                assetIDs.add(link.alternativeAdobeAsset.cloudID);
            }
        });
        return assetIDs;
    }
    /**
     * Get an array of all the asset ids being monitored by the link list. This includes
     * asset ids in any links, as well as asset ids being directly monitored.
     */
    get monitoredAssetIDs() {
        return [...this._monitoredAssetIDs];
    }
    /**
     * Iterate through all the asset links in the list.
     */
    forEach(callback) {
        this._links.forEach(callback);
    }
    /**
     * Get the container asset for the link list (the document that contains the links)
     */
    get containerAsset() {
        return this._containerAsset;
    }
    /**
     * Marks a list as active so that the asset link manager can actively start monitoring all the asset links in it.
     * @param [duration] If a duration is specified (in ms), the list will automatically deactivate after this time. Otherwise it will remain active.
     * @deprecated This method is deprecated - lists are always monitored for notifications unless you pass in DISABLE_ONS when initializing SAM.
     *    Will be removed on or after 2024-10-01.
     */
    activate(duration = 0) {
        oneTimeWarning('AssetLinkList.activate', `AssetLinkList.activate(${duration}) is deprecated - lists are always monitored for notifications.`
            + `This method will be removed on or after 2024-10-01.`);
    }
    /**
     * Deactivate a list so that the asset link manager can stop monitoring all the asset links in it.
     * @deprecated This method is deprecated - lists are always monitored for notifications unless you pass in DISABLE_ONS when initializing SAM.
     *    Will be removed on or after 2024-10-01.
     */
    deactivate() {
        oneTimeWarning('AssetLinkList.deactivate', 'AssetLinkList.deactivate() is deprecated - lists are'
            + ' always monitored for notifications. This method will be removed on or after 2024-10-01.');
    }
    /**
     * Whether the list is active (asset links in it are actively monitored for changes or not).
     * @deprecated This method is deprecated - lists are always monitored for notifications unless you pass in DISABLE_ONS when initializing SAM.
     *    Will be removed on or after 2024-10-01.
     */
    get isActive() {
        oneTimeWarning('AssetLinkList.isActive', 'AssetLinkList.isActive is deprecated - lists are always'
            + ' monitored for notifications. This method will be removed on or after 2024-10-01.');
        return true;
    }
    /**
     * Change the container asset associated with this list (the document that contains the links in the list)
     * This will change relative path based resolution of the links
     */
    async updateContainerAsset(containerAsset) {
        this._containerAsset = containerAsset;
        this._onChange();
        // Need to resolve all the links again, because the relative path could have changed
        await Promise.all(Array.from(this._links).map(link => link._resolveWithRetry()));
    }
    /**
     * Clone the asset list for a another container asset (adding it to this list)
     */
    clone(assetLinkList) {
        // TODO: Need to trigger all links to re-resolve, since relative path resolution may have changed
        assetLinkList.links.forEach(link => this._links.add(link));
    }
}
__decorate([
    observable
], AssetLinkList.prototype, "count", null);
__decorate([
    observable
], AssetLinkList.prototype, "links", null);
__decorate([
    observable
], AssetLinkList.prototype, "monitoredAssetIDs", null);
__decorate([
    observable
], AssetLinkList.prototype, "forEach", null);
__decorate([
    observable
], AssetLinkList.prototype, "containerAsset", null);
__decorate([
    observable
], AssetLinkList.prototype, "isActive", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class AssetLinkManager extends EventEmitter {
    constructor(_context) {
        super();
        this._context = _context;
        this._lists = new Set();
        this._onListChange = () => {
            // We need to notify the AssetContext whenever a list changes, so it can
            // update the asset ids it's subscribing to in ONS (if a link is broken,
            // we still want to get updates even if the asset isn't in the asset pool)
            this._context._onAssetsChanged();
        };
    }
    get _core() {
        return this._context._core;
    }
    createList(containerAsset) {
        const list = new AssetLinkList(this, containerAsset);
        list.on('change', this._onListChange);
        this._lists.add(list);
        this.emit('change');
        return list;
    }
    removeList(list) {
        this._lists.delete(list);
        list.off('change', this._onListChange);
        this.emit('change');
    }
    /**
     * Get an array of all the asset link lists.
     */
    get lists() {
        return [...this._lists];
    }
}
__decorate([
    observable
], AssetLinkManager.prototype, "lists", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const WORKSPACE_INDEX_LINKS = {
    [RelType.WORKSPACES_CONTEXT]: [],
    [RelType.TEAMS_PAGE]: ['start', 'limit'],
    [RelType.WORKSPACES_PRIMARY]: ['id', 'selector'],
    [RelType.WORKSPACES_PAGE]: ['orderBy', 'start', 'limit', 'teamId', 'bookmarkFilter', 'teamAccess'],
    [RelType.REPOSITORIES_PAGE]: [],
    [RelType.TEAMS_CREATE]: [],
    [RelType.TEAM_PROVISION]: [],
    [RelType.WORKSPACES_BOOKMARKS_BATCH]: []
};

// Team provisioning can return both 200 and 201 depending on if
// the user already had the team created or not. We check for this ahead of time,
// but there is a chance the user has a pending or simultaneous call to provision
// the team from a different tab or application. In SAM we don't actually
// care about 200 or 201, but we want to make sure they are both valid codes.
const TEAM_PROVISION_SUCCESS_CODES = [200, 201];
const SPACE_FULL_SELECTOR = ['FULL'];
const DefaultTeamListingOptions = {
    start: 0,
    limit: 0,
    orderDirection: 'ascending',
    orderBy: 'name'
};
const DefaultSpacesListingOptions = {
    orderDirection: 'ascending',
    orderBy: 'name',
    bookmarked: false,
    teamID: undefined,
    start: 0,
    limit: 0
};
function getDefaultOptions$1(Defaults, options) {
    return Object.assign({}, Defaults, options);
}
function getDirectionValue$1(userOptions) {
    const directionFlag = userOptions.orderDirection === 'ascending' ? '+' : '-';
    return directionFlag + userOptions.orderBy;
}
function createPageTemplate$1(options) {
    const templateValues = {};
    if (options.start) {
        templateValues.start = options.start;
    }
    if (options.limit) {
        templateValues.limit = options.limit;
    }
    return templateValues;
}
function getTeamListingPath(links, options) {
    const userOptions = getDefaultOptions$1(DefaultTeamListingOptions, options);
    const templateValues = createPageTemplate$1(userOptions);
    templateValues.orderBy = getDirectionValue$1(userOptions);
    templateValues.nameContains = userOptions.nameContains;
    templateValues.state = userOptions.state;
    return getURLFromLinks(links, RelType.TEAMS_PAGE, templateValues);
}
function getSpacesListingPath(links, options) {
    const userOptions = getDefaultOptions$1(DefaultSpacesListingOptions, options);
    const templateValues = createPageTemplate$1(userOptions);
    templateValues.orderBy = getDirectionValue$1(userOptions);
    // The service sets bookmarkFilter to "true" by default, so pass "false" if bookmarked is false or undefined.
    if (!userOptions.bookmarked) {
        templateValues.bookmarkFilter = false;
    }
    // The only value that is implemented by the server is teamAccess=undefined or teamAccess=false.
    // teamAccess=undefined -> all spaces are returned
    // teamAccess=false -> returns only the spaces that are from teams the user is not part of.
    if (userOptions.teamAccess === false) {
        templateValues.teamAccess = false;
    }
    const { teamID } = userOptions;
    if (teamID) {
        templateValues.teamId = teamID;
    }
    return getURLFromLinks(links, RelType.WORKSPACES_PAGE, templateValues);
}
function validateRequiredProjectLinks(links) {
    return validateLinks(links, WORKSPACE_INDEX_LINKS);
}
class WorkspacesInterface extends InterfaceBase {
    _getHeaders(contentType) {
        const headers = {
            'x-service-features': 'CanvasMigration',
            // NOTE: [SAM-1222] disabling cache because /projects/{spaceId} sets an etag header that
            //      does not change when the user's bookmark preference changes, causing browsers to
            //      automatically add the etag header on requests and get the cached value of the
            //      bookmark field rather than the actual value.
            'Cache-Control': 'no-cache'
        };
        if (contentType) {
            headers['Content-Type'] = contentType;
        }
        return headers;
    }
    async _discoverLinks(rootHref) {
        const response = await this._getResponse(rootHref, this._getHeaders());
        return response._links;
    }
    _getEndpointHref(endpoint) {
        return `https://${this._core.environment.endpoints.PROJECTS_HOST}${endpoint}`;
    }
    async discoverLinks() {
        return this._discoverLinks(this._getEndpointHref(`/api/v2/index`));
    }
    async getTeams(options) {
        const links = await this.discoverLinks();
        const url = getTeamListingPath(links, options);
        return await this._getResponse(url, this._getHeaders());
    }
    /**
     * Get repositories where user can create CC Team
     */
    async getOrgRepositories() {
        const links = await this.discoverLinks();
        const url = getURLFromLinks(links, RelType.REPOSITORIES_PAGE, {});
        return await this._getResponse(url, this._getHeaders());
    }
    /**
     * Create a team under /content/assets folder.
     */
    async createTeam(data) {
        const links = await this.discoverLinks();
        const url = getURLFromLinks(links, RelType.TEAMS_CREATE, {});
        const response = await this._doPost(url, this._getHeaders(JSON_MIME_TYPE), JSON.stringify(data));
        return response.json();
    }
    /**
     * Permanently Delete a Team
     */
    async permanentlyDeleteTeam(links) {
        const url = getURLFromLinks(links, RelType.TEAMS_PRIMARY);
        await this._doDelete(url, this._getHeaders(), [204]);
    }
    /**
     * Archive/Discard Team
     */
    async archiveTeam(links) {
        const url = getURLFromLinks(links, RelType.TEAMS_DISCARD);
        await this._doPost(url, this._getHeaders(), undefined, [204]);
    }
    /**
     * Restore Team.
     */
    async restoreTeam(teamLinks) {
        const url = getURLFromLinks(teamLinks, RelType.TEAMS_RESTORE);
        await this._doPost(url, this._getHeaders(), undefined, [204]);
    }
    /**
     * Rename Team.
     */
    async renameTeam(teamLinks, name) {
        const url = getURLFromLinks(teamLinks, RelType.TEAMS_RENAME);
        await this._doPost(url, this._getHeaders(JSON_MIME_TYPE), JSON.stringify({ name }), [204]);
    }
    /**
     * Patch a Team
     */
    async patchTeam(teamLinks, data) {
        const url = getURLFromLinks(teamLinks, RelType.TEAMS_PROPERTIES);
        await this._doPatch(url, Object.assign(Object.assign({}, this._getHeaders(JSON_PATCH_MIME_TYPE)), { 'If-Match': '*' }), JSON.stringify(data), [204]);
    }
    /**
     * Fetches Properties for a team.
     */
    async getTeamProperties(teamLinks) {
        const url = getURLFromLinks(teamLinks, RelType.TEAMS_PROPERTIES);
        return await this._getResponse(url, this._getHeaders());
    }
    /**
     * Provisions a Team folder within User storage for a Type 1 user only. The provisioned team name is in format:
     * "Team {{USER_FIRST_NAME}}" depending on the localization of the requesting user.
     */
    async provisionTeam() {
        const links = await this.discoverLinks();
        const url = getURLFromLinks(links, RelType.TEAM_PROVISION, {});
        const response = await this._doPost(url, this._getHeaders(), undefined, TEAM_PROVISION_SUCCESS_CODES);
        return response.json();
    }
    async getSpaces(options) {
        const links = await this.discoverLinks();
        const url = getSpacesListingPath(links, options);
        return await this._getResponse(url, this._getHeaders());
    }
    async getSpace(id, selector = SPACE_FULL_SELECTOR) {
        const links = await this.discoverLinks();
        const spaceLink = getURLFromLinks(links, RelType.WORKSPACES_PRIMARY, { id, selector });
        return await this._getResponse(spaceLink, this._getHeaders());
    }
    async reloadSpace(spaceLinks, selector = SPACE_FULL_SELECTOR) {
        const spaceLink = getURLFromLinks(spaceLinks, RelType.WORKSPACES_PRIMARY, { selector });
        return await this._getResponse(spaceLink, this._getHeaders());
    }
    async bookmarkOp(data) {
        const links = await this.discoverLinks();
        const url = getURLFromLinks(links, RelType.WORKSPACES_BOOKMARKS_BATCH);
        const response = await this._doPost(url, this._getHeaders(JSON_MIME_TYPE), JSON.stringify(data), [200]);
        return response.json();
    }
    async createSpace(teamLinks, data) {
        const url = getURLFromLinks(teamLinks, RelType.WORKSPACES_CREATE);
        const response = await this._doPost(url, this._getHeaders(JSON_MIME_TYPE), JSON.stringify(data));
        return response.json();
    }
    async patchSpace(spaceLinks, data) {
        const url = getURLFromLinks(spaceLinks, RelType.WORKSPACES_PRIMARY);
        await this._doPatch(url, this._getHeaders(JSON_PATCH_MIME_TYPE), JSON.stringify(data), [200, 204]);
    }
    async getEnclosingSpaces(data) {
        const links = await this.discoverLinks();
        const url = getURLFromLinks(links, RelType.WORKSPACES_CONTEXT);
        const response = await this._doPost(url, this._getHeaders(JSON_MIME_TYPE), JSON.stringify(data), [200]);
        return response.json();
    }
}
__decorate([
    cache(validateRequiredProjectLinks),
    __param(0, key)
], WorkspacesInterface.prototype, "_discoverLinks", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const OrderFieldToIProjectsSpaceOrderBy = {
    'name': 'name',
    'created': 'createdDate',
};
const OrderFieldToComparator$b = {
    name(a, b) {
        return compareStrings(a.name, b.name);
    },
    created(a, b) {
        return compareValues(a.created, b.created);
    }
};
class SpaceOrder extends createItemOrderFactory(OrderFieldToComparator$b) {
}
SpaceOrder.default = new SpaceOrder('ascending', 'name');

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class SpaceLoader {
    constructor(_spacePool, _workspaceInterface, _teamRoleGetterFactory) {
        this._spacePool = _spacePool;
        this._workspaceInterface = _workspaceInterface;
        this._teamRoleGetterFactory = _teamRoleGetterFactory;
    }
    supportsFilter(filter) {
        // - only state === 'active' is supported.
        // - cannot handle teamRole === 'MEMBER' yet.
        if (!filter) {
            // If we don't provide a filter at all, then we cannot use the loader
            return false;
        }
        const { teamRole, state } = filter;
        return teamRole !== 'MEMBER' && state === 'active';
    }
    async loadPage(options) {
        return await this._loadPage(options);
    }
    async _loadPage(options, start) {
        var _a, _b, _c;
        const { order, filter, limit } = options;
        const teamRole = (_a = filter) === null || _a === void 0 ? void 0 : _a.teamRole;
        if (teamRole === 'MEMBER') {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Filtering only for spaces with reamRole MEMBER is not supported.');
        }
        // We will ignore the update if we had local changes sooner than this timestamp.
        const latency = this._spacePool._core.environment.getValue('PROJECTS_LIST_UPDATE_LATENCY');
        const mostRecentValidMutation = Date.now() - latency;
        const resultPromise = this._workspaceInterface.getSpaces({
            orderBy: OrderFieldToIProjectsSpaceOrderBy[order.field],
            orderDirection: order.direction,
            teamID: (_b = filter) === null || _b === void 0 ? void 0 : _b.teamID,
            bookmarked: (_c = filter) === null || _c === void 0 ? void 0 : _c.bookmarked,
            teamAccess: teamRole === 'NONE' ? false : undefined,
            start,
            limit
        });
        // If the teamRole was used in the request, then we trust the server resolved the correct value for the filter.
        // If we don't have teamRole in the filter, then we will lookup in the client side list of teams. If the teams are not
        // loaded yet, we will just fallback to 'NONE' until the teams are eventually loaded.
        // The team pool delegate in the space context will update the team role once the teams are known.
        const teamRoleFn = teamRole ? (() => teamRole) : await this._teamRoleGetterFactory();
        // Wait for the results after we start loading the role factory above. That will make the requests run in parallel when needed.
        const result = await resultPromise;
        const session = this._spacePool.createLoaderUpdateSession(mostRecentValidMutation);
        // Inject the teamRole in all the items that we load.
        const items = SpaceUpdater.updateRemoteItems(session, result.projects.map((item) => {
            return Object.assign(Object.assign({}, item), { teamRole: teamRoleFn(item.teamId), 
                // The `/projects` listing endpoint doesn't add the state property,
                // but it only returns active spaces anyways. We can inject
                // the property automatically to make sure any restored space,
                // gets back into the correct lists.
                state: 'active' });
        }));
        // Make sure we ignore any reload errors now that we have refreshed the listing of the space.
        for (const item of items) {
            item[clearLastReloadError]();
        }
        session.commit();
        // Check if we have another page to load.
        let loadNextPage;
        // Note that it is important to skip the entire page size,
        // even if we didn't get any items back.
        // The service is based on USS which might filter out items
        // on the fly when they are retrieved instead of ahead of time
        // when the total limit is computed. As a result, some pages might
        // even come in empty.
        const nextStart = result.pageSpec.start + result.pageSpec.limit;
        if (nextStart < result.pageSpec.totalLimit) {
            loadNextPage = (options) => this._loadPage(options, nextStart);
        }
        return {
            items,
            total: result.pageSpec.totalLimit,
            loadNextPage,
            mostRecentValidMutation
        };
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
var AvatarSizes;
(function (AvatarSizes) {
    AvatarSizes["Size50"] = "50";
    AvatarSizes["Size100"] = "100";
    AvatarSizes["Size115"] = "115";
    AvatarSizes["Size230"] = "230";
    AvatarSizes["Size138"] = "138";
    AvatarSizes["Size276"] = "276";
})(AvatarSizes || (AvatarSizes = {}));

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const CollaboratorRoles = ['owner', 'viewer', 'editor', 'none'];
const ContributorRoles = ['editor', 'owner'];
const CollaboratorTypes = [
    'imsUser',
    'addressBookGroup',
    'addressBookContact',
    'predefinedPrincipal',
    'imsGroup',
];
const InheritanceTypes = ['self', 'deep', 'deep_override'];
/**
 * A Collaborator represents the relationship between the user and the
 * asset. Users have other Profile information that is unique to the user.
 *
 * Eventually we will offer a Profile class with a Profile context that
 * offers information unrelated to an asset.
 */
class Collaborator extends ItemBase {
    constructor() {
        super(...arguments);
        this._role = 'viewer';
        this._type = 'imsUser';
        this._isDirect = false;
        this._assetID = '';
        this._canShare = false;
    }
    get assetId() {
        return this._assetID;
    }
    /**
     * Returns false if the Behance/PPS service
     * was unable to return a profile for this collaborator.
     * @deprecated -- clients should just check that profile info is not undefined (SAM-3420). Will be removed on or
     *                after 2024-10-01
     */
    get hasProfile() {
        oneTimeWarning('Collaborator.hasProfile', 'Collaborator.hasProfile is deprecated and will be'
            + ' removed on or after 2024-10-01');
        // This used to return the private property _hasProfile, but since _hasProfile was never
        // set correctly, we're just returning undefined here to avoid confusion.
        // This getter will eventually be removed by SAM-3420.
        return undefined;
    }
    // `imsUser id` if invited via user email directly
    // `addressBookContact id` (i.e urn.ab:contact:...) if members are invited via shareSheet's recommend dropdown
    // `addressBookGroup id` (i.e urn:ab:group:...) if groups are invited via shareSheet's recommend dropdown
    get collaboratorID() {
        return this.id;
    }
    // Track the imsUser id
    // - Same as 'id' for direct invited collaborators
    // - Represents the collaborator actual ims id for addressBookContact, which is useful to check (compare ims id)
    // for thing like if the logged in user is in the collaborator list ,...
    // - undefined for 'addressBookGroup'
    get imsUserId() {
        var _a;
        const type = this._type;
        if (type === 'imsUser') {
            return this.id;
        }
        return (_a = this._additionalData) === null || _a === void 0 ? void 0 : _a.linkedIdentity;
    }
    get email() {
        var _a;
        return (_a = this._additionalData) === null || _a === void 0 ? void 0 : _a.email;
    }
    // Collaborator name for imsUser and addressBookContact
    // Group name for addressBookGroup
    get name() {
        var _a, _b;
        const additionalData = this._additionalData;
        return ((_b = (_a = additionalData === null || additionalData === void 0 ? void 0 : additionalData.displayName) !== null && _a !== void 0 ? _a : additionalData === null || additionalData === void 0 ? void 0 : additionalData.groupName) !== null && _b !== void 0 ? _b : additionalData === null || additionalData === void 0 ? void 0 : additionalData.organizationName);
    }
    get role() {
        return this._role;
    }
    get type() {
        return this._type;
    }
    get inheritances() {
        return this._inheritances;
    }
    get isDirect() {
        return this._isDirect;
    }
    get hasContributorRole() {
        return ContributorRoles.includes(this._role);
    }
    get hasName() {
        return !!this.name;
    }
    get invited() {
        var _a;
        return (_a = this._inheritances) === null || _a === void 0 ? void 0 : _a.includes('deep');
    }
    get isAdmin() {
        var _a;
        return (_a = this._inheritances) === null || _a === void 0 ? void 0 : _a.includes('deep_override');
    }
    get canShare() {
        return this._canShare;
    }
    /**
     * Checks if this collaborator is the user that is currently logged in.
     *
     * **NOTE**: It is not safe to assume that Collaborator.id === currentUserId
     *           will work. Always use Collaborator.isCurrentUser instead.
     */
    get isCurrentUser() {
        return this._core.environment.getValue('USER_ID') === this.imsUserId;
    }
    /**
     * Returns a standard size of 138x138
     */
    get avatarURL() {
        return this.getAvatarURL(AvatarSizes.Size138);
    }
    getAvatarURL(size) {
        var _a, _b, _c;
        return (_c = (_b = (_a = this._additionalData) === null || _a === void 0 ? void 0 : _a.images) === null || _b === void 0 ? void 0 : _b[size]) !== null && _c !== void 0 ? _c : '';
    }
}
__decorate([
    observable(SharedCollaboratorChangedEvent)
], Collaborator.prototype, "email", null);
__decorate([
    observable(SharedCollaboratorChangedEvent)
], Collaborator.prototype, "name", null);
__decorate([
    observable(SharedCollaboratorChangedEvent)
], Collaborator.prototype, "avatarURL", null);
__decorate([
    observable(SharedCollaboratorChangedEvent)
], Collaborator.prototype, "getAvatarURL", null);
const CollaboratorUpdater = new ItemUpdater({
    localItemType: Collaborator,
    remoteIdProperty: 'id',
    changeEvent: SharedCollaboratorChangedEvent,
    propertyUpdater: new PropertyUpdater({
        '_type': { name: 'type', choices: CollaboratorTypes },
        '_role': { name: 'role', choices: CollaboratorRoles },
        '_inheritances': { name: 'inheritances', isArray: true, choices: InheritanceTypes },
        '_additionalData': { name: 'additionalData', format: 'object' },
        '_isDirect': { name: 'isDirect', format: 'boolean' },
        '_avatarURLs': { name: 'avatarURLs', format: 'object' },
        '_assetID': { name: 'assetID', format: 'string' },
        '_canShare': { name: 'canShare', format: 'boolean' }
    })
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const RepositoryOwnerTypes = ['org', 'user'];
const TeamStates = ['active', 'discarded'];
/**
 * Represents the notion of a "team" of Adobe account
 * holders that collaborate on projects.
 *
 * @example
 * // Instatiate list and load contents
 * const teamsList = core.spaceContext.getTeams().getList(...options);
 * await waitForListLoad(teamsList);
 *
 * // Get the first team in the list as an example.
 * const team = teamsList.items[0];
 *
 * // Perform some actions on a team.
 * await team.rename('newName');
 * await team.archive();
 * await team.restore();
 *
 * // log the teamID
 * console.log(team.teamID)
 *
 */
class Team extends ShareableItem {
    constructor() {
        super(...arguments);
        this._created = new Date();
        this._createdBy = '';
        this._name = '';
        this._repositoryID = '';
        this._repositoryName = '';
        this._state = 'active';
        this._role = 'none';
    }
    /**
     * The unique id that represents the team.
     */
    get teamID() {
        return this.id;
    }
    get created() {
        return this._created;
    }
    get createdBy() {
        return this._createdBy;
    }
    /**
     * The user-visible name assigned to the team
     */
    get name() {
        return this._name;
    }
    /**
     * The description property of this team.
     *
     * @note Only available after a call to ensureDescription()
     */
    get description() {
        return this._description;
    }
    /**
     * The unique id of the repository that owns this team
     */
    get repositoryID() {
        return this._repositoryID;
    }
    /**
     * The user-visible name of the repository (i.e. the organization name)
     * that owns this team
     */
    get repositoryName() {
        return this._repositoryName;
    }
    get repositoryOwnerID() {
        return this._repositoryOwnerID;
    }
    get repositoryOwnerEmail() {
        return this._repositoryOwnerEmail;
    }
    get repositoryOwnerType() {
        return this._repositoryOwnerType;
    }
    /**
     * The role or permissions of the user for this team.
     * 'editor' and 'owner' are combined for backward
     * compatibility.
     * @deprecated Use 'role' instead.
     */
    get collaboratorRole() {
        oneTimeWarning('collaboratorRoleDeprecated', 'The property collaboratorRole has been depecrated in favor of role.');
        return this._role === 'owner'
            ? 'editor' : this._role;
    }
    /**
     * The role or permissions of the user for this team.
     * can be any of the following values: 'editor', 'viewer'
     * 'owner'.
     *
     * @note The 'viewer' role is not set in any workflow currently.
     */
    get role() {
        return this._role;
    }
    get state() {
        return this._state;
    }
    /**
     * The links provided by the service for this team.
     *
     * @private
     */
    get _links() {
        return this.__links;
    }
    /**
     * Archive/Discard team
     *
     * @NOTE Currently there are some inconsistencies in how the API's are
     * named within Projects
     * Team API uses “discarded”, Project API uses “archived”
     */
    async archive() {
        await this._core.spaceContext.archiveTeam(this);
    }
    /**
     * Create a space for this team.
     *
     * @param data {IProjectsCreateData}
     */
    async createSpace(data) {
        return await this._core.spaceContext.createSpace(this, data);
    }
    /**
     * Permanently Delete a Team that has already been discarded.
     *
     */
    async permanentlyDelete() {
        await this._core.spaceContext.permanentlyDeleteTeam(this);
    }
    /**
     * Rename this Team.
     *
     * @param newName
     */
    async rename(newName) {
        await this._core.spaceContext.renameTeam(this, newName);
    }
    /**
     * Restore this Team
     */
    async restore() {
        await this._core.spaceContext.restoreTeam(this);
    }
    /**
     * Update properties on this team
     */
    async update(data) {
        await this._core.spaceContext.updateTeam(this, data);
    }
    /**
     * Ensures the description is loaded and available
     * on a team.
     */
    async ensureDescription() {
        await this._core.spaceContext.loadTeamProperties(this);
    }
}
__decorate([
    observable
], Team.prototype, "created", null);
__decorate([
    observable
], Team.prototype, "createdBy", null);
__decorate([
    observable
], Team.prototype, "name", null);
__decorate([
    observable
], Team.prototype, "description", null);
__decorate([
    observable
], Team.prototype, "repositoryID", null);
__decorate([
    observable
], Team.prototype, "repositoryName", null);
__decorate([
    observable
], Team.prototype, "repositoryOwnerID", null);
__decorate([
    observable
], Team.prototype, "repositoryOwnerEmail", null);
__decorate([
    observable
], Team.prototype, "repositoryOwnerType", null);
__decorate([
    observable
], Team.prototype, "collaboratorRole", null);
__decorate([
    observable
], Team.prototype, "role", null);
__decorate([
    observable
], Team.prototype, "state", null);
__decorate([
    observable
], Team.prototype, "_links", null);
const TeamUpdater = new ItemUpdater({
    localItemType: Team,
    remoteIdProperty: 'id',
    propertyUpdater: new PropertyUpdater({
        '_created': { name: 'createDate', format: 'date' },
        '_createdBy': 'createdBy',
        '_description': 'description',
        '_name': 'name',
        '_repositoryID': 'repositoryId',
        '_repositoryName': 'repositoryName',
        '_repositoryOwnerID': 'repositoryOwner.id',
        '_repositoryOwnerEmail': 'repositoryOwner.email',
        '_repositoryOwnerType': { name: 'repositoryOwner.type', choices: RepositoryOwnerTypes },
        '_role': { name: 'role', choices: CollaboratorRoles },
        '_state': { name: 'state', choices: TeamStates },
        '__links': { name: '_links', format: 'object' }
    })
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const OrderFieldToIProjectsTeamsOrderBy = {
    'name': 'name',
    'created': 'createDate',
};
const OrderFieldToComparator$a = {
    name(a, b) {
        return compareStrings(a.name, b.name);
    },
    created(a, b) {
        return compareValues(a.created, b.created);
    }
};
class TeamOrder extends createItemOrderFactory(OrderFieldToComparator$a) {
}
TeamOrder.default = new TeamOrder('ascending', 'name');

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class TeamLoader {
    constructor(_teamPool, _projectInterface) {
        this._teamPool = _teamPool;
        this._projectInterface = _projectInterface;
    }
    async loadPage(options) {
        return await this._loadPage(options);
    }
    async _loadPage(options, start) {
        var _a, _b;
        const { limit, filter, order } = options;
        // We will ignore the update if we had local changes sooner than this timestamp.
        const latency = this._teamPool._core.environment.getValue('PROJECTS_LIST_UPDATE_LATENCY');
        const mostRecentValidMutation = Date.now() - latency;
        const result = await this._projectInterface.getTeams({
            orderBy: OrderFieldToIProjectsTeamsOrderBy[order.field],
            orderDirection: order.direction,
            start,
            limit,
            nameContains: (_a = filter) === null || _a === void 0 ? void 0 : _a.nameContains,
            state: (_b = filter) === null || _b === void 0 ? void 0 : _b.state
        });
        const session = this._teamPool.createLoaderUpdateSession(mostRecentValidMutation);
        const items = TeamUpdater.updateRemoteItems(session, result.teams);
        session.commit();
        // Check if we have another page to load.
        let loadNextPage;
        // Note that it is important to skip the entire page size,
        // even if we didn't get any items back.
        // The service is based on USS which might filter out items
        // on the fly when they are retrieved instead of ahead of time
        // when the total limit is computed. As a result, some pages might
        // even come in empty.
        const nextStart = result.pageSpec.start + result.pageSpec.limit;
        if (nextStart < result.pageSpec.totalLimit) {
            loadNextPage = (options) => this._loadPage(options, nextStart);
        }
        return {
            items,
            total: result.pageSpec.totalLimit,
            loadNextPage,
            mostRecentValidMutation
        };
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class OrgRepository extends ItemBase {
    constructor() {
        super(...arguments);
        this._repositoryID = '';
    }
    get repositoryID() {
        return this._repositoryID;
    }
    /**
     * Create a team under /content/assets folder.
     */
    async createTeam(data) {
        return await this._core.spaceContext.createTeam(this, data);
    }
}
__decorate([
    observable
], OrgRepository.prototype, "repositoryID", null);
const OrgRepositoryUpdater = new ItemUpdater({
    localItemType: OrgRepository,
    remoteIdProperty: 'repositoryId',
    propertyUpdater: new PropertyUpdater({
        '_repositoryID': 'repositoryId'
    })
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class OrgRepositoryLoader {
    constructor(_orgRepositoryPool, _projectInterface) {
        this._orgRepositoryPool = _orgRepositoryPool;
        this._projectInterface = _projectInterface;
    }
    async loadPage() {
        const result = await this._projectInterface.getOrgRepositories();
        const session = this._orgRepositoryPool.createLoaderUpdateSession(Date.now());
        const items = OrgRepositoryUpdater.updateRemoteItems(session, result.repositories);
        session.commit();
        return { items };
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
var StringRelationResult;
(function (StringRelationResult) {
    StringRelationResult[StringRelationResult["DISJOINTED"] = 0] = "DISJOINTED";
    StringRelationResult[StringRelationResult["SUPERSET"] = 1] = "SUPERSET";
    StringRelationResult[StringRelationResult["SUBSET"] = 2] = "SUBSET";
    StringRelationResult[StringRelationResult["UNKNOWN"] = 3] = "UNKNOWN";
    StringRelationResult[StringRelationResult["IDENTICAL"] = 4] = "IDENTICAL";
})(StringRelationResult || (StringRelationResult = {}));
const whitespaceFilter = (w) => /\S/.test(w);
/**
 * Available when M2 is released, M1 currently
 * does not support this filter.
 * @experimental
 */
class TeamFilter {
    constructor(options) {
        this._nameContains = options === null || options === void 0 ? void 0 : options.nameContains;
        this._state = options === null || options === void 0 ? void 0 : options.state;
    }
    static TeamsContainingName(name) {
        return new TeamFilter({
            nameContains: name
        });
    }
    get nameContains() {
        return this._nameContains;
    }
    get state() {
        return this._state;
    }
    get hash() {
        var _a, _b;
        return ((_a = this._nameContains) !== null && _a !== void 0 ? _a : 'any-name') + ':'
            + ((_b = this._state) !== null && _b !== void 0 ? _b : 'any-state');
    }
    _getRelationToNameContains(name, otherName) {
        if (name === otherName) {
            return StringRelationResult.IDENTICAL;
        }
        const nameTerms = name.toLowerCase()
            .split(' ').filter(whitespaceFilter).sort();
        const otherNameTerms = otherName.toLowerCase()
            .split(' ').filter(whitespaceFilter).sort();
        if (nameTerms.length === 0 && otherNameTerms.length === 0) {
            return StringRelationResult.IDENTICAL;
        }
        if (nameTerms.length === 0 || otherNameTerms.length === 0) {
            return StringRelationResult.DISJOINTED;
        }
        if (nameTerms.length === otherNameTerms.length
            && nameTerms.every((n, i) => n === otherNameTerms[i])) {
            return StringRelationResult.IDENTICAL;
        }
        let subsetMatches = 0;
        let supersetMatches = 0;
        nameTerms.forEach((nameTerm) => {
            const findMatch = otherNameTerms.find((otherTerm) => {
                const superSet = otherTerm.startsWith(nameTerm);
                const subSet = nameTerm.startsWith(otherTerm);
                return superSet || subSet;
            });
            if (findMatch) {
                if (nameTerm.length <= findMatch.length) {
                    supersetMatches++;
                }
                if (nameTerm.length > findMatch.length) {
                    subsetMatches++;
                }
            }
        });
        if (nameTerms.length === subsetMatches) {
            return StringRelationResult.SUBSET;
        }
        if (nameTerms.length === supersetMatches) {
            return StringRelationResult.SUPERSET;
        }
        if (subsetMatches === 0 && supersetMatches === 0) {
            return StringRelationResult.DISJOINTED;
        }
        return StringRelationResult.UNKNOWN;
    }
    _searchTarget(searchString, targetString) {
        const searchTerms = searchString.toLowerCase()
            .split(' ').filter(whitespaceFilter);
        const targetTerms = targetString.toLowerCase()
            .split(' ').filter(whitespaceFilter);
        // Empty word or search term
        if (searchTerms.length === 0 || targetTerms.length === 0) {
            return false;
        }
        return searchTerms.every((searchTerm) => {
            return targetTerms.find((targetTerm) => {
                return targetTerm.startsWith(searchTerm);
            });
        });
    }
    matches(item) {
        const state = this._state;
        if (state && state !== item.state) {
            return false;
        }
        const nameContains = this._nameContains;
        if (nameContains
            && !this._searchTarget(nameContains, item.name)) {
            return false;
        }
        return true;
    }
    getRelationToFilter(other) {
        let searchMatch;
        if (this.nameContains && other.nameContains) {
            searchMatch = this._getRelationToNameContains(this.nameContains, other.nameContains);
            if (searchMatch === StringRelationResult.DISJOINTED) {
                return 'disjoint';
            }
        }
        if (this.state && other.state && this.state !== other.state) {
            return 'disjoint';
        }
        if (!this.state && other.state) {
            return 'unknown';
        }
        if (!this.nameContains && other.nameContains) {
            return 'unknown';
        }
        if (searchMatch && searchMatch === StringRelationResult.SUPERSET
            || searchMatch === StringRelationResult.UNKNOWN) {
            return 'unknown';
        }
        //------ All 'unknown' cases must be handled above this line
        return 'subset';
    }
}
TeamFilter.ActiveTeams = new TeamFilter({
    state: 'active'
});
TeamFilter.DiscardedTeams = new TeamFilter({
    state: 'discarded'
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class SpaceFilter {
    constructor(options) {
        this._bookmarked = false;
        this._bookmarked = Boolean(options === null || options === void 0 ? void 0 : options.bookmarked);
        this._teamID = options === null || options === void 0 ? void 0 : options.teamID;
        this._teamRole = options === null || options === void 0 ? void 0 : options.teamRole;
        this._state = options === null || options === void 0 ? void 0 : options.state;
        this._teamRole = options === null || options === void 0 ? void 0 : options.teamRole;
    }
    static ActiveSpacesFromTeam(teamID) {
        return new SpaceFilter({
            state: 'active',
            teamID
        });
    }
    get bookmarked() {
        return this._bookmarked;
    }
    get teamID() {
        return this._teamID;
    }
    get teamRole() {
        return this._teamRole;
    }
    get state() {
        return this._state;
    }
    get hash() {
        var _a, _b;
        return String(this.bookmarked) + ':'
            + (this.teamID || 'all-teams') + ':'
            + ((_a = this._teamRole) !== null && _a !== void 0 ? _a : 'all-roles') + ':'
            + ((_b = this._state) !== null && _b !== void 0 ? _b : 'any-state');
    }
    matches(item) {
        if (this._bookmarked && !item.isBookmarked) {
            return false;
        }
        const teamID = this._teamID;
        if (teamID && item.teamID !== teamID) {
            return false;
        }
        const teamRole = this._teamRole;
        if (teamRole && item.teamRole !== teamRole) {
            return false;
        }
        const state = this._state;
        if (state && state !== item.state) {
            return false;
        }
        return true;
    }
    getRelationToFilter(other) {
        const otherSpaceFilter = other;
        if (this._teamRole !== otherSpaceFilter._teamRole) {
            if (this._teamRole && otherSpaceFilter._teamRole) {
                // If both filters specify a completely different teamRole then the
                // lists are entirely disjoint.
                return 'disjoint';
            }
            // If only one filter specifically looks for the team role, then the other might be
            // able to borrow some items from it.
            return 'unknown';
        }
        if (this.teamID && otherSpaceFilter.teamID && this.teamID !== otherSpaceFilter.teamID) {
            // Teams are entirely different, no shared elements are expected.
            return 'disjoint';
        }
        if (!this.teamID && otherSpaceFilter.teamID) {
            // If the other list has a teamID and this filter doesn't then
            // this is list is going to have an unknown final state.
            return 'unknown';
        }
        // If the other list filtered for only bookmarked items and this doesn't,
        // then this is list is going to have an unknown final state.
        if (!this.bookmarked && other.bookmarked) {
            return 'unknown';
        }
        if (this._state !== other._state) {
            // If both filters expect a state and the states do not match, then
            // the lists are not going to have any items in common.
            if (this._state && other._state) {
                return 'disjoint';
            }
            // If only one list expects a specific state and the other can take any state,
            // then we could possible share some items.
            return 'unknown';
        }
        return 'subset';
    }
}
SpaceFilter.ActiveSpaces = new SpaceFilter({
    state: 'active'
});
SpaceFilter.ArchivedSpaces = new SpaceFilter({
    state: 'archived'
});
SpaceFilter.ActiveBookmarkedSpaces = new SpaceFilter({
    state: 'active',
    bookmarked: true
});
SpaceFilter.ActiveSpacesFromOtherTeams = new SpaceFilter({
    state: 'active',
    teamRole: 'NONE'
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// We want to refresh teams only if we didn't get the list in the past 5 minutes.
// This avoids loading teams again during auto-provisioning in case the client
// already fetched the teams.
const TEAM_REFRESH_TIMEOUT = 5 * 60 * 1000;
function createTeamRoleGetter(teams) {
    const map = new Set(teams.items.map((team) => team.id));
    return (teamID) => map.has(teamID) ? 'MEMBER' : 'NONE';
}
function getLinks(item) {
    const links = item._links;
    if (!links) {
        throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Object has not been loaded correctly, missing "_links" property.');
    }
    return links;
}
class SpaceContext {
    constructor(_core) {
        this._core = _core;
        this._orgRepositoryPool = new ItemPool(this._core, 'orgRepository');
        this._teamPool = new ItemPool(this._core, 'team');
        this._spacePool = new ItemPool(this._core, 'space');
        this._projectInterface = new WorkspacesInterface(this._core);
        this._rapi = new RepositoryInterface(this._core);
        this._hostAssetContext = this._core.hostAssetContext;
        this._teamRoleGetterFactory = async () => {
            const teams = await this._fetchTeams();
            return createTeamRoleGetter(teams);
        };
        this._teamPoolDelegate = {
            onItemsUpdated: () => {
                // Iterate on all the known spaces and update their teamRole based on what we know from the list of Teams.
                // Note that this might update spaces that have been discarded, but that's alright as they might be restored
                // at some point in the future. Since `this.getSpaces().getList()` will trigger fetching the Teams, it is
                // expected that the Team list will typically already be loaded, so it should not use `getTeams().getList()`
                // since that will cause it to fetch the Teams list again. Fetching teams as a side-effect of unit tests that
                // manipulate Teams using the teamPool will unexpectedly cause the teams list to load without any Nock mock
                // to provide it.
                const teams = this.getTeams()._getExistingList(TeamOrder.default, new EverythingFilter());
                // Teams List won't exist if manipulating the teamPool without loading the teams first.
                // Teams List will be empty when the TeamLoader converts the service response to Team models via
                // TeamUpdater.updateRemoteItems(), because it triggers ItemPool.onItemsUpdated() before it has actually
                // put the items in the List.
                if (!teams || !teams.items.length) {
                    this._core.environment.debug('[SpaceContext._teamPoolDelegate] skipping update of Space.teamRole -- no teams');
                    return;
                }
                const spacePool = this._spacePool;
                const getTeamRole = createTeamRoleGetter(teams);
                const spaceSession = spacePool.createUpdateSession('Updating teamRole across all spaces');
                this._spacePool.forEach((space) => {
                    const newTeamRole = getTeamRole(space.teamID);
                    if (space.teamRole !== newTeamRole) {
                        SpaceUpdater.updateLocalItem(spaceSession, space, { teamRole: newTeamRole });
                    }
                });
                spaceSession.commit();
            }
        };
        // Make sure we have a delegate to update all the teamRoles of the spaces once a new team is added.
        this._teamPool.addDelegate(this._teamPoolDelegate, 'context');
    }
    get projectInterface() {
        return this._projectInterface;
    }
    get orgRepositoryPool() {
        return this._orgRepositoryPool;
    }
    get teamPool() {
        return this._teamPool;
    }
    get spacePool() {
        return this._spacePool;
    }
    /**
     * Returns a cached list of links for the project service index route.
     */
    async discoverLinks() {
        return await this._projectInterface.discoverLinks();
    }
    getOrgRepositories() {
        const loader = new OrgRepositoryLoader(this._orgRepositoryPool, this._projectInterface);
        return new ItemSource(this._orgRepositoryPool, [loader]);
    }
    getTeams() {
        const loader = new TeamLoader(this._teamPool, this._projectInterface);
        return new ItemSource(this._teamPool, [loader], new TeamFilter());
    }
    getSpaces() {
        // Make sure that the space loader checks for team membership during load.
        const loader = new SpaceLoader(this._spacePool, this._projectInterface, this._teamRoleGetterFactory);
        return new ItemSource(this._spacePool, [loader], new SpaceFilter());
    }
    async _fetchTeams() {
        // Get the list with refreshTimeOut so that it will not immediately refresh if the list already exists
        const teams = this.getTeams().getList(TeamOrder.default, undefined, { refreshTimeout: TEAM_REFRESH_TIMEOUT });
        await waitForListLoad(teams);
        return teams;
    }
    async provisionType1Team() {
        // Auto-provision a team for CCI user if the user is not currently sponsoring a team.
        if (this._core.environment.getValue('ACCOUNT_TYPE') !== 'type1') {
            return undefined;
        }
        const teams = await this._fetchTeams();
        const userId = this._core.environment.getValue('USER_ID');
        let team = teams.items.find((team) => team.repositoryOwnerID === userId && team.repositoryOwnerType === 'user');
        if (!team) {
            // This should automatically add the team to the lists.
            const response = await this._projectInterface.provisionTeam();
            // The team might be just created, if we are refreshing the list of teams
            // it might not exist yet, so we have to keep it alive until USS is eventually consistent.
            const session = this._teamPool.createMutationSession();
            team = TeamUpdater.updateRemoteItem(session, response);
            session.commit();
        }
        return team;
    }
    async createTeam(orgRepository, data) {
        const response = await this._projectInterface.createTeam(Object.assign(Object.assign({}, data), { repositoryId: orgRepository.repositoryID }));
        const session = this._teamPool.createMutationSession();
        const team = TeamUpdater.updateRemoteItem(session, response);
        session.commit();
        return team;
    }
    async _renameAsset(id, newName) {
        if (!validatePlatformRepoName(newName)) {
            // Validate the name here to avoid resolving the asset if the name is invalid.
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Invalid name provided for the rename operation.');
        }
        // FIXME: `item._links[RelType.PRIMARY]` already has the resolved link for the asset.
        // We could reuse the link to avoid a separate resolve by id call.
        const asset = await this._hostAssetContext.getAssetWithCloudID(id);
        await asset.rename(newName);
        // Return the new name of the asset, just in case the server decided to change it.
        return asset;
    }
    async _renameTeam(team, update) {
        const name = update.name;
        if (!name) {
            return;
        }
        if (!validatePlatformRepoName(name)) {
            // Validate the name here to avoid resolving the asset if the name is invalid.
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Invalid name provided for the rename operation.');
        }
        const links = getLinks(team);
        await this._projectInterface.renameTeam(links, name);
        // Since the client could have both an instance to a Team
        // and the underlying AdobeAsset we should check that if the
        // AdobeAsset exists in the AssetContext ItemPool and if it does
        // update it and it's descendents as well.
        const adobeAssetPool = this._hostAssetContext.assetPool;
        const localAdobeAsset = adobeAssetPool.getLocalItemById(team.id);
        if (localAdobeAsset) {
            const newPath = renameFileInPath(name, localAdobeAsset);
            const adobeAssetSession = adobeAssetPool.createUpdateSession();
            updateCachedDescendents({
                assetPool: this._hostAssetContext.assetPool,
                asset: localAdobeAsset,
                session: adobeAssetSession,
                operation: { type: 'rename', newPath }
            });
            this._hostAssetContext._adobeAssetUpdater.updateLocalItem(adobeAssetSession, localAdobeAsset, {
                'repo:path': newPath,
                'repo:name': name
            });
            adobeAssetSession.commit();
        }
        const updateSession = this.teamPool.createMutationSession();
        TeamUpdater.updateLocalItem(updateSession, team, { name });
        updateSession.commit();
    }
    async renameTeam(team, newName) {
        await this.updateTeam(team, { name: newName });
    }
    async restoreTeam(team) {
        await ItemBase.runExclusiveTask('restore', [team], async () => {
            try {
                const links = getLinks(team);
                const updateSession = this.teamPool.createUpdateSession();
                await this._projectInterface.restoreTeam(links);
                TeamUpdater.updateLocalItem(updateSession, team, { state: 'active' });
                updateSession.commit();
            }
            catch (e) {
                // We want to avoid updating the pool if
                // there are errors like CONFLICT that could occur.
                throw SAMError.create(SAMErrorCode.UNKNOWN, 'An error occured restoring team', { underlyingError: e });
            }
        });
    }
    async archiveTeam(team) {
        await ItemBase.runExclusiveTask('archive', [team], async () => {
            // TODO: should this be createMutationSession?
            const updateSession = this.teamPool.createUpdateSession();
            try {
                const links = getLinks(team);
                await this._projectInterface.archiveTeam(links);
            }
            catch (error) {
                const e = error;
                // @TODO https://jira.corp.adobe.com/browse/SAM-2064
                // Temporary fallback, remove once M2 APIs are in prod
                if (e.code === SAMErrorCode.BAD_REQUEST) {
                    const asset = await this._hostAssetContext.getAssetWithCloudID(team.id);
                    await asset.discard();
                }
                else {
                    throw e;
                }
            }
            TeamUpdater.updateLocalItem(updateSession, team, { state: 'discarded' });
            updateSession.commit();
        });
    }
    /**
     * The Projects services accepts only
     * a string value with the length
     * less than or equal to 1000.
     */
    _validateDescription(value) {
        return value.length <= 1000;
    }
    _validateColor(value) {
        return /^#[A-Fa-f0-9]{6}$/.test(value);
    }
    _getPatchOP(value, path, existingValue, validator) {
        if (validator && !validator(value)) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, `Property ${path} has invalid value`);
        }
        // If there was a description and now there
        // is none, we should "remove".
        if (value.length === 0
            && existingValue && existingValue.length > 0) {
            return {
                op: 'remove',
                path
            };
        }
        return {
            op: existingValue ? 'replace' : 'add',
            value,
            path
        };
    }
    async _patchTeam(team, update) {
        const { description } = update;
        const bodyData = [];
        const localUpdate = {};
        if (description !== undefined) {
            bodyData.push(this._getPatchOP(description, '/description', team.description, this._validateDescription));
            localUpdate.description = description;
        }
        if (!bodyData.length) {
            return;
        }
        const links = getLinks(team);
        await this._projectInterface.patchTeam(links, bodyData);
        const updateSession = this.teamPool.createMutationSession();
        TeamUpdater.updateLocalItem(updateSession, team, localUpdate);
        updateSession.commit();
    }
    async updateTeam(team, update) {
        await ItemBase.runExclusiveTask('update', [team], async () => {
            const results = await Promise.allSettled([
                this._patchTeam(team, update),
                this._renameTeam(team, update)
            ]);
            // Checks for rejection and calls the handler
            // to delete the item because it has not been
            // found.
            this._checkForRejection(results, () => {
                const session = this.teamPool.createUpdateSession();
                session.queueDiscardedItem(team);
                session.commit();
            });
        });
    }
    async loadTeamProperties(team) {
        const links = getLinks(team);
        const properties = await this._projectInterface.getTeamProperties(links);
        if (!properties || Object.keys(properties).length === 0) {
            return;
        }
        const updateSession = this.teamPool.createMutationSession();
        TeamUpdater.updateLocalItem(updateSession, team, Object.assign({}, properties));
        updateSession.commit();
    }
    async permanentlyDeleteTeam(team) {
        if (team.state === 'active') {
            throw SAMError.create(SAMErrorCode.UNSUPPORTED, 'Teams that are not \'discarded\' cannot be permanently deleted.');
        }
        const onItemsChanged = (event) => {
            if (event.discardedItems) {
                const permDeleteTeamIDs = event.discardedItems
                    .filter(item => item.id === team.id)
                    .map(item => item.id);
                if (permDeleteTeamIDs.length) {
                    this._teamPool.removeLocalItems(permDeleteTeamIDs);
                }
            }
        };
        this._teamPool.on(ITEMS_CHANGED, onItemsChanged);
        try {
            await ItemBase.runExclusiveTask('delete', [team], async () => {
                const links = getLinks(team);
                await this._projectInterface.permanentlyDeleteTeam(links);
                const updateSession = this.teamPool.createUpdateSession();
                updateSession.queueDiscardedItem(team);
                updateSession.commit();
            });
        }
        finally {
            this.teamPool.off(ITEMS_CHANGED, onItemsChanged);
        }
    }
    // Reload space is an implementation detail of the space context,
    // external projects should use space.load() instead.
    async [reloadSpace](space) {
        const latency = this._core.environment.getValue('PROJECTS_ITEM_UPDATE_LATENCY');
        await space.waitForMutationPropagationIfNeeded(latency);
        const links = getLinks(space);
        const result = await this._projectInterface.reloadSpace(links, SPACE_FULL_SELECTOR);
        const session = this._spacePool.createUpdateSession();
        SpaceUpdater.updateLocalItem(session, space, result);
        session.commit();
    }
    /**
     * Internal helper method for moving spaces.
     * @param spaces spaces to be moved
     * @param team destination team where spaces are intended to be moved
     * @param retryOnConflict whether to retry the move op for failed spaces in case of conflict
     * @param assetConflictMap map to save the newName and count used for all assets in batch
     * @returns the result for batch operation after retrying for failed assets if asked.
     */
    async _moveSpaces(spaces, team, retryOnConflict = true, assetConflictMap) {
        let resultOps = { failedResults: [], newModels: [] };
        await ItemBase.runExclusiveTask('move', spaces, async () => {
            const targetAsset = await this._hostAssetContext.getAssetWithCloudID(team.id);
            if (assetConflictMap === undefined) {
                assetConflictMap = new Map();
            }
            const spacesById = new Map();
            const ops = spaces.map((space) => {
                spacesById.set(space.id, space);
                let newPath = `${targetAsset.repoPath}/${space.name}`;
                newPath = batchFormatFileName(this._core, space.id, newPath, false, assetConflictMap);
                return {
                    op: 'move',
                    id: space.id,
                    source: {
                        'repo:assetId': space.id,
                        'repo:repositoryId': space.repositoryID
                    },
                    target: {
                        'repo:path': newPath,
                        'repo:repositoryId': targetAsset.repositoryID
                    }
                };
            });
            resultOps = await this._runOperations(ops, spacesById, {
                teamId: team.id,
                teamName: team.name
            });
        });
        if (retryOnConflict) {
            return await retryFailedOpsDueToConflict(resultOps, team, assetConflictMap, this._moveSpaces.bind(this));
        }
        return resultOps;
    }
    /**
     * Public method to move spaces to a team
     * @param spaces spaces to be moved
     * @param team destination team where spaces are intended to be moved
     * @returns the result for batch operation after retrying for failed assets.
     */
    async moveSpaces(spaces, team) {
        return this._moveSpaces(spaces, team);
    }
    async archiveSpaces(spaces) {
        return await ItemBase.runExclusiveTask('archive', spaces, async () => {
            const spacesById = new Map();
            const ops = spaces.map((space) => {
                spacesById.set(space.id, space);
                return {
                    op: 'discard',
                    id: space.id,
                    target: {
                        'repo:assetId': space.id,
                        'repo:repositoryId': space.repositoryID
                    },
                };
            });
            return await this._runOperations(ops, spacesById);
        });
    }
    async restoreSpaces(spaces, restoreNameMap) {
        return await ItemBase.runExclusiveTask('restore', spaces, async () => {
            const spacesById = new Map();
            const ops = spaces.map((space) => {
                spacesById.set(space.id, space);
                return {
                    op: 'restore',
                    id: space.id,
                    target: {
                        'repo:assetId': space.id,
                        'repo:repositoryId': space.repositoryID
                    },
                    name: restoreNameMap === null || restoreNameMap === void 0 ? void 0 : restoreNameMap.get(space)
                };
            });
            return await this._runOperations(ops, spacesById);
        });
    }
    async _runOperations(ops, spacesById, additionalSpaceUpdates = {}) {
        return await this._rapi.runOperationsForContext(ops, spacesById, this._spacePool, (session, space, remoteItem) => {
            if (!remoteItem) {
                throw SAMError.create(SAMErrorCode.UNKNOWN, 'Missing metadata from asset bulk operation.');
            }
            // We want to update the repo:path of all the descendants of the space only in case of move/rename.
            const remoteRepoPath = remoteItem['repo:path'];
            if (space.pathName !== remoteRepoPath) {
                const adobeAssetPool = this._hostAssetContext.assetPool;
                const localAdobeAsset = adobeAssetPool.getLocalItemById(space.id);
                const assetSession = adobeAssetPool.createUpdateSession();
                if (localAdobeAsset) {
                    updateCachedDescendents({
                        assetPool: this._hostAssetContext.assetPool,
                        asset: localAdobeAsset,
                        session: assetSession,
                        operation: { type: 'rename', newPath: remoteRepoPath }
                    });
                    this._hostAssetContext._adobeAssetUpdater.updateLocalItem(assetSession, localAdobeAsset, {
                        'repo:path': remoteRepoPath
                    });
                    assetSession.commit();
                }
            }
            SpaceUpdater.updateLocalItem(session, space, Object.assign({ state: remoteItem['repo:state'] === 'ACTIVE' ? 'active' : 'archived', name: remoteItem['repo:name'], pathName: remoteItem['repo:path'] }, additionalSpaceUpdates));
            return undefined;
        }, [
            {
                // Refresh this space to make sure we have the latest version of it.
                // Note that we cannot use 404 - NOT_FOUND as a sign that the space has been
                // permanently deleted as we get that same error code when the space
                // is still active, just that the user is not allowed to archive/restore it.
                // It is better to just reload the space to validate we still have access to
                match: (error) => error.code === SAMErrorCode.ACCESS_DENIED || error.code === SAMErrorCode.NOT_FOUND,
                action: async (space) => {
                    return await space.load().catch((error) => {
                        return { model: space, error: error };
                    });
                }
            }
        ]);
    }
    async createSpaceBookmarks(spaces) {
        return this._bookmarkSpaces('ADD', spaces);
    }
    async removeSpaceBookmarks(spaces) {
        return this._bookmarkSpaces('REMOVE', spaces);
    }
    async _bookmarkSpaces(op, spaces) {
        await ItemBase.runExclusiveTask('bookmark', spaces, async () => {
            var _a;
            const userId = this._core.environment.getValue('USER_ID');
            // Attempt to make the change ahead of time.
            const change = {
                bookmarked: op === 'ADD'
            };
            const localSession = this.spacePool.createUpdateSession();
            const spaceById = new Map();
            const items = spaces.map((space) => {
                spaceById.set(space.id, { space, wasBookmarked: space.isBookmarked });
                SpaceUpdater.updateLocalItem(localSession, space, change);
                return {
                    projectUrn: space.id,
                    userId
                };
            });
            localSession.commit();
            let response;
            try {
                response = await this._projectInterface.bookmarkOp({ op, items });
            }
            finally {
                if (response) {
                    for (const item of response.responses) {
                        const info = spaceById.get((_a = item.request) === null || _a === void 0 ? void 0 : _a.projectUrn);
                        if (info) {
                            info.status = item.status;
                        }
                    }
                }
                // Revert all incomplete changes.
                const restoreSession = this.spacePool.createUpdateSession();
                const mutationSession = this.spacePool.createMutationSession();
                for (const { space, wasBookmarked, status } of spaceById.values()) {
                    if (status === 201) {
                        // Reapply the change, in case there was a different fetch in-between.
                        // This should also force the mutation timestamp to update on the space.
                        SpaceUpdater.updateLocalItem(mutationSession, space, change);
                    }
                    else {
                        // Revert the local change.
                        SpaceUpdater.updateLocalItem(restoreSession, space, {
                            bookmarked: wasBookmarked
                        });
                        // Check if the item was removed on a different device
                        // and remove it from internal lists too.
                        if (status === 404) {
                            restoreSession.queueDiscardedItem(space);
                        }
                    }
                }
                restoreSession.commit();
                mutationSession.commit();
            }
        });
    }
    _checkForRejection(results, onRemoveItem) {
        for (const result of results) {
            if (result.status === 'rejected') {
                const error = result.reason;
                // Remove the item entirely
                if (error instanceof SAMError && error.code === SAMErrorCode.NOT_FOUND) {
                    onRemoveItem();
                }
                throw error;
            }
        }
    }
    async updateSpace(space, update) {
        // Make both rename and patch go through the same exclusive task,
        // to allow them to execute in parallel.
        await ItemBase.runExclusiveTask('update', [space], async () => {
            const results = await Promise.allSettled([
                this._renameSpace(space, update),
                this._patchSpace(space, update)
            ]);
            this._checkForRejection(results, () => {
                const session = this.spacePool.createUpdateSession();
                session.queueDiscardedItem(space);
                session.commit();
            });
        });
    }
    async _renameSpace(space, update) {
        const { name } = update;
        if (name === undefined) {
            return;
        }
        const asset = await this._renameAsset(space.id, name);
        const updateSession = this.spacePool.createMutationSession();
        SpaceUpdater.updateLocalItem(updateSession, space, {
            name: asset.name,
            pathName: asset.repoPath
        });
        updateSession.commit();
    }
    async _patchSpace(space, update) {
        const { description, color } = update;
        const bodyData = [];
        const localUpdate = {};
        if (description !== undefined) {
            bodyData.push(this._getPatchOP(description, `/description`, space.description, this._validateDescription));
            localUpdate.description = description;
        }
        if (color !== undefined) {
            bodyData.push(this._getPatchOP(color, `/color`, space.color, this._validateColor));
            localUpdate.color = color;
        }
        if (!bodyData.length) {
            return;
        }
        const links = getLinks(space);
        // Ideally, the patch would return the updated state, but that's not the case here.
        await this._projectInterface.patchSpace(links, bodyData);
        // We will have to assume that a successful change means the changed values are the latest ones.
        const updateSession = this.spacePool.createMutationSession();
        SpaceUpdater.updateLocalItem(updateSession, space, localUpdate);
        updateSession.commit();
    }
    async createSpace(team, data) {
        const links = getLinks(team);
        this._validateColor(data.color);
        this._validateDescription(data.description);
        validatePlatformRepoName(data.name);
        const response = await this._projectInterface.createSpace(links, Object.assign(Object.assign({}, data), { parentId: team.id }));
        // A space might not show up until a few seconds later, so we must keep it alive during this time.
        const updateSession = this.spacePool.createMutationSession();
        const space = SpaceUpdater.updateRemoteItem(updateSession, Object.assign(Object.assign({}, response), { 
            // Make sure the service injects the teamId.
            teamId: team.id }));
        updateSession.commit();
        return space;
    }
    async getEnclosingSpace(assetId) {
        const spaceMap = await this.getEnclosingSpaces([assetId]);
        return spaceMap.get(assetId);
    }
    async getEnclosingSpaces(assetIds) {
        // The :context API uses the USS to lookup the enclosing space, so it suffers by the
        // same amount of latency as the listing APIs. We will ignore the info we get from this
        // API if the asset was mutated more recently than the maximum expected latency for USS.
        const latency = this._core.environment.getValue('PROJECTS_LIST_UPDATE_LATENCY');
        const mostRecentValidMutation = Date.now() - latency; // We will ignore the update if we had local changes sooner than this timestamp.
        const result = await this._projectInterface.getEnclosingSpaces(assetIds.map((id) => ({ 'repo:assetId': id })));
        const map = new Map();
        const updateSession = this.spacePool.createUpdateSession('get enclosing spaces', mostRecentValidMutation);
        for (const item of result.responses) {
            if (item.status !== 200) {
                continue;
            }
            const id = item.request['repo:assetId'];
            const space = SpaceUpdater.updateRemoteItem(updateSession, item.body);
            map.set(id, space);
        }
        updateSession.commit();
        return map;
    }
    async lookupSpaceById(id) {
        let space = this._spacePool.getLocalItemById(id);
        if (!space) {
            // Note that we don't have a local space, so we don't have to worry about eventual consistency on this item.
            const response = await this._projectInterface.getSpace(id, SPACE_FULL_SELECTOR);
            const session = this._spacePool.createUpdateSession();
            space = SpaceUpdater.updateRemoteItem(session, response);
            session.commit();
        }
        return space;
    }
    /**
     * This API is temporary and it is specifically for Projects UI to use with SS workflows.
     * It will eventually be deprecated in favor of using notifications
     * @param teamId
     */
    onUserLeftTeam(teamId) {
        const team = this._teamPool.getLocalItemById(teamId);
        if (team) {
            const session = this._teamPool.createUpdateSession();
            session.queueDiscardedItem(team);
            session.commit();
        }
    }
}
__decorate([
    memoize
], SpaceContext.prototype, "getOrgRepositories", null);
__decorate([
    memoize
], SpaceContext.prototype, "getTeams", null);
__decorate([
    memoize
], SpaceContext.prototype, "getSpaces", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class CollaboratorLoader {
    constructor(_collaboratorPool, _invitationInterface, _urn) {
        this._collaboratorPool = _collaboratorPool;
        this._invitationInterface = _invitationInterface;
        this._urn = _urn;
    }
    async loadPage() {
        // FIXME: Implement latency tracker once we move more collaborator operations into SAM.JS.
        // Note, this request is piggy-backed, so if we load both the collaborators and invited collaborators at the same
        // time, it only results in one network call
        const result = await this._invitationInterface.getCollaborators(this._urn);
        // Update the collaborators
        const collaboratorUpdateSession = this._collaboratorPool.createLoaderUpdateSession(Date.now());
        const directSet = new Set(result.directCollaborators.map(({ id }) => id));
        result.collaborators.forEach(collaborator => collaborator.isDirect = directSet.has(collaborator.id));
        const collaborators = CollaboratorUpdater.updateRemoteItems(collaboratorUpdateSession, result.collaborators);
        collaboratorUpdateSession.commit();
        return { items: collaborators };
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class InvitedCollaborator extends ItemBase {
    constructor() {
        super(...arguments);
        this._email = '';
        this._role = 'viewer';
        this._canComment = false;
        this._canShare = false;
        this._acceptanceRequired = false;
        this._created = new Date;
    }
    get invitationID() {
        return this.id;
    }
    // Invited email
    get email() {
        return this._email;
    }
    get role() {
        return this._role;
    }
    get canComment() {
        return this._canComment;
    }
    get canShare() {
        return this._canShare;
    }
    get acceptanceRequired() {
        return this._acceptanceRequired;
    }
    get inheritance() {
        return this._inheritance;
    }
    get created() {
        return this._created;
    }
}
const InvitedCollaboratorUpdater = new ItemUpdater({
    localItemType: InvitedCollaborator,
    remoteIdProperty: 'id',
    propertyUpdater: new PropertyUpdater({
        '_email': 'email',
        '_role': { name: 'role', choices: CollaboratorRoles },
        '_canComment': { name: 'canComment', format: 'boolean' },
        '_canShare': { name: 'canShare', format: 'boolean' },
        '_acceptanceRequired': { name: 'acceptanceRequired', format: 'boolean' },
        '_inheritance': { name: 'inheritance', choices: InheritanceTypes },
        '_created': { name: 'created', format: 'date_as_number' }
    })
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class InvitedCollaboratorLoader {
    constructor(_invitedCollaboratorPool, _invitationInterface, _urn) {
        this._invitedCollaboratorPool = _invitedCollaboratorPool;
        this._invitationInterface = _invitationInterface;
        this._urn = _urn;
    }
    async loadPage() {
        // FIXME: Implement latency tracker once we move more collaborator operations into SAM.JS.
        // Note, this request is piggy-backed, so if we load both the collaborators and invited collaborators at the same
        // time, it only results in one network call
        const result = await this._invitationInterface.getCollaborators(this._urn);
        // Update the pending invitations
        const invitedCollaboratorUpdateSession = this._invitedCollaboratorPool.createLoaderUpdateSession(Date.now());
        const invitedCollaborators = InvitedCollaboratorUpdater.updateRemoteItems(invitedCollaboratorUpdateSession, result.invitations);
        invitedCollaboratorUpdateSession.commit();
        return { items: invitedCollaborators };
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class CollaboratorsByAssetID extends ItemBase {
    constructor() {
        super(...arguments);
        this._collaborators = [];
        this._error = undefined;
    }
    /**
     * Since collaborators are a model around the relationship of a
     * particular user and an asset, it should always have an AssetID.
     */
    get getAssetID() {
        return this.id;
    }
    get collaborators() {
        return this._collaborators;
    }
    get error() {
        return this._error;
    }
}
const CollaboratorFormat = {
    parse: (value) => {
        return value;
    },
    isEqual: (a, b) => {
        return isDeepEqual(a, b);
    }
};
function getCollaborators(source, dest) {
    const collaboratorPool = new ItemPool(dest._core, 'collaborator');
    const session = collaboratorPool.createUpdateSession();
    // failed to fetch collaborators for this asset
    if (source.statusCode !== 200) {
        return [];
    }
    const response = JSON.parse(source.response);
    // Add the assetID to the response from the URN provided by
    // the service so Collaborators are tied to an asset.
    const collaboratorsWithID = response.collaborators.map((collab) => {
        return Object.assign(Object.assign({}, collab), { assetID: source.urn });
    });
    const collaborators = CollaboratorUpdater.updateRemoteItems(session, collaboratorsWithID);
    session.commit();
    return collaborators;
}
const ErrorFormat = {
    parse: (value) => {
        return value;
    },
    isEqual: (a, b) => {
        return isDeepEqual(a, b);
    }
};
function getError(source) {
    if (source.statusCode === 200) {
        return undefined;
    }
    // this is how a sample response looks like (got it from the sam.js demo)
    // response: '{"statusCode":404,"error":"Not Found"}'
    const response = JSON.parse(source.response);
    return SAMError.createWithRepositoryCode(response.statusCode, '', response.error);
}
const CollaboratorsByAssetIDUpdater = new ItemUpdater({
    localItemType: CollaboratorsByAssetID,
    remoteIdProperty: 'urn',
    propertyUpdater: new PropertyUpdater({
        '_collaborators': { get: getCollaborators, format: CollaboratorFormat },
        '_error': { get: getError, format: ErrorFormat }
    })
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class CollaboratorsByAssetIDLoader {
    constructor(_collaboratorsByAssetIDPool, _invitationInterface, _assetIDs) {
        this._collaboratorsByAssetIDPool = _collaboratorsByAssetIDPool;
        this._invitationInterface = _invitationInterface;
        this._assetIDs = _assetIDs;
    }
    async loadPage(options) {
        return await this._loadPage(options);
    }
    async _loadPage(options, start = 0) {
        let { limit } = options;
        limit = limit === undefined ? this._invitationInterface.batchRequestLimit : limit;
        // max limit supported by invitation service batch api is 50, capping limit
        limit = Math.min(limit, this._invitationInterface.batchRequestLimit);
        const slicedAssetIDs = this._assetIDs.slice(start, start + limit);
        const batchInvitationResponse = await this._invitationInterface.getCollaboratorListForUrns(slicedAssetIDs);
        const session = this._collaboratorsByAssetIDPool.createLoaderUpdateSession(Date.now());
        const collaboratorByAssetIDs = CollaboratorsByAssetIDUpdater.updateRemoteItems(session, batchInvitationResponse.result);
        session.commit();
        // Check if we have another page to load.
        let loadNextPage;
        if (start + collaboratorByAssetIDs.length < this._assetIDs.length) {
            loadNextPage = (options) => this._loadPage(options, start + collaboratorByAssetIDs.length);
        }
        return { items: collaboratorByAssetIDs, loadNextPage };
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class BatchOperationQueue {
    constructor(_batchProcessor, _maxItemsPerBatch = 50, _delay = 50, _maxInFlightBatches = 3) {
        this._batchProcessor = _batchProcessor;
        this._maxItemsPerBatch = _maxItemsPerBatch;
        this._delay = _delay;
        this._maxInFlightBatches = _maxInFlightBatches;
        // Sets are guaranteed to be iterated in FIFO order
        this._itemQueue = new Set();
        this._inFlightBatches = new Set();
        this._processQueue = async () => {
            clearTimeout(this._timer);
            this._timer = undefined;
            try {
                if (this.inFlightCount >= this._maxInFlightBatches) {
                    await Promise.race([...this._inFlightBatches].map(inFlightBatch => inFlightBatch.promise));
                }
            }
            catch (e) {
                // Do nothing
            }
            while (this.inFlightCount <= this._maxInFlightBatches && this._itemQueue.size > 0) {
                const batch = new Set();
                while (this._itemQueue.size > 0 && batch.size < this._maxItemsPerBatch) {
                    const item = this._itemQueue.values().next().value;
                    this._itemQueue.delete(item);
                    batch.add(item);
                }
                // self-deleting batch Promise
                const inFlightBatch = {
                    batch,
                    promise: this._batchProcessor(batch)
                        .finally(() => { this._inFlightBatches.delete(inFlightBatch); })
                };
                this._inFlightBatches.add(inFlightBatch);
            }
            if (this._itemQueue.size > 0 && !this._timer) {
                this._timer = setTimeout(this._processQueue, this._delay);
            }
        };
    }
    enqueue(item) {
        // Already in progress
        if (this.isInQueue(item) || this.isInFlight(item)) {
            return;
        }
        this._itemQueue.add(item);
        if (!this._timer) {
            this._timer = setTimeout(this._processQueue, this._delay);
        }
        // Don't wait to process the batch if a max-size batch request can be made
        if (this._itemQueue.size >= this._maxItemsPerBatch) {
            this._processQueue();
        }
    }
    dequeue(item) {
        this._itemQueue.delete(item);
    }
    get size() {
        return this._itemQueue.size;
    }
    get inFlightCount() {
        return this._inFlightBatches.size;
    }
    isInQueue(item) {
        return this._itemQueue.has(item);
    }
    isInFlight(item) {
        return !![...this._inFlightBatches].find(inFlightBatch => inFlightBatch.batch.has(item));
    }
    /**
     * Forces the queue to run all batches as fast as possible.
     *
     * @package -- for internal SAM.js use only
     */
    async _processNow() {
        if (!this._itemQueue.size && !this._inFlightBatches.size) {
            // No work to do
            return;
        }
        do {
            this._processQueue();
            try {
                await Promise.race([...this._inFlightBatches].map(batch => batch.promise));
            }
            catch (e) {
                // do nothing
            }
        } while (this._itemQueue.size);
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class CollaborationContext {
    constructor(_core) {
        this._core = _core;
        this._collaboratorSourceMap = new Map();
        this._invitationInterface = new InvitationInterface(this._core);
        this._collaboratorsByAssetIDPool = new ItemPool(this._core, 'collaboratorsByAssetID');
        this._accessConfirmedIDs = new Set();
        this._handleBatchRequest = async (items) => {
            const assetIds = [...items].map(item => item.id);
            const batchInvitationResponse = await this._invitationInterface.getCollaboratorListForUrns(assetIds);
            const results = batchInvitationResponse.result;
            const assetCollaborators = new Map();
            results.forEach((result) => {
                const assetId = result.urn;
                if (result.statusCode !== 200) {
                    assetCollaborators.set(assetId, []);
                    return;
                }
                const sources = this._getCollaboratorSources(assetId);
                const invitationResponse = JSON.parse(result.response);
                // Add the assetId and isDirect to the returned data.
                const collaboratorsWithAssetIds = invitationResponse.collaborators.map((collaboratorData) => {
                    const isDirectCollaborator = invitationResponse.directCollaborators.find((c) => c.id === collaboratorData.id);
                    return Object.assign(Object.assign({}, collaboratorData), { assetID: assetId, isDirect: !!isDirectCollaborator, hasProfile: false });
                });
                const collaboratorUpdateSession = sources.collaboratorPool.createUpdateSession();
                const collaborators = CollaboratorUpdater.updateRemoteItems(collaboratorUpdateSession, collaboratorsWithAssetIds);
                collaboratorUpdateSession.commit();
                assetCollaborators.set(assetId, collaborators);
                return;
            });
            // Iterate through all items and lookup the corresponding
            // updated collaborator information to see if an event
            // needs to be fired.
            items.forEach((item) => {
                const updatedCollaborator = assetCollaborators.get(item.id);
                if (!updatedCollaborator) {
                    return;
                }
                // Notify shareable item of changed collaborators
                item._setCollaboratorData(updatedCollaborator);
            });
        };
        this._collaboratorRequestQueue = new BatchOperationQueue(this._handleBatchRequest);
    }
    /**
     * For use-cases where we want to immediately fetch collaborators but using the batch system
     * instead of the collaborator listing.
     */
    _getCollaboratorsNow(asset) {
        this._collaboratorRequestQueue.enqueue(asset);
        return this._collaboratorRequestQueue._processNow();
    }
    /**
     * get a list of Collaborators
     * @param assetsIDs list of assetIDs (including from Teams and Spaces, not just AdobeAssets)
     */
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     *  Use {@link ShareableItem.collaboratorArray} instead.
     */
    getCollaboratorsForAssets(assetsIDs) {
        const loader = new CollaboratorsByAssetIDLoader(this._collaboratorsByAssetIDPool, this._invitationInterface, assetsIDs);
        const source = new ItemSource(this._collaboratorsByAssetIDPool, [loader], new EverythingFilter());
        return source;
    }
    /**
     * Get's all the ItemSources for Collaborators and InvitedCollaborators.
     * @note ItemPools for collaborators can't be shared between assets because they may have different roles per asset.
     * @note This no longer stores and returns WeakRefs due to how we have to update the collaborators, it causes
     * the refs to be reclaimed before we are able to update the collaborators with new information and causes
     * new item pools to be created everytime.
     * @package used internally only
     */
    _getCollaboratorSources(assetID) {
        const sources = this._collaboratorSourceMap.get(assetID);
        if (sources) {
            return sources;
        }
        // We cannot share the pools for Collaborator/InvitedCollaborator objects between
        // assets as they might have different roles for the same IDs.
        const collaboratorPool = new ItemPool(this._core, 'collaborator');
        const collaboratorLoader = new CollaboratorLoader(collaboratorPool, this._invitationInterface, assetID);
        const collaboratorSource = new ItemSource(collaboratorPool, [collaboratorLoader], new EverythingFilter());
        const invitedCollaboratorPool = new ItemPool(this._core, 'invitedCollaborator');
        const invitedCollaboratorLoader = new InvitedCollaboratorLoader(invitedCollaboratorPool, this._invitationInterface, assetID);
        const invitedCollaboratorSource = new ItemSource(invitedCollaboratorPool, [invitedCollaboratorLoader], new EverythingFilter());
        const collaboratorSources = { collaboratorPool, collaboratorSource, invitedCollaboratorPool, invitedCollaboratorSource };
        this._collaboratorSourceMap.set(assetID, collaboratorSources);
        return collaboratorSources;
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     *    Use {@link ShareableItem.collaboratorArray} instead
     */
    getCollaborators(assetID) {
        return this._getCollaboratorSources(assetID).collaboratorSource;
    }
    /**
     * Removes the provided Collaborator and updates the ItemPools.
     *
     * This eventually performs a PATCH request to the invitations service and removes the provided invited
     * collaborator.
     *
     * @throws If the request is not successful, an error is thrown.
     *
     * @param assetID
     * @param collaborator {Collaborator}
     */
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and renamed for internal use on or after
     * 2024-10-01.
     *   Use {@link ShareableItem.removeCollaborator} instead
     */
    async removeCollaborator(assetID, collaborator) {
        var _a, _b;
        const response = await this._invitationInterface.modifyCollaborators(assetID, {
            collaborators: [{ id: collaborator.id, action: 'remove' }]
        });
        const collaboratorResponse = (_a = response.collaborators) === null || _a === void 0 ? void 0 : _a.find((c) => c.id === collaborator.id);
        if (!collaboratorResponse) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Collaborator patch request unsuccessful');
        }
        if (collaboratorResponse.statusCode !== 200) {
            throw SAMError.createWithRepositoryCode(collaboratorResponse.statusCode, (_b = collaboratorResponse.errorSubcode) !== null && _b !== void 0 ? _b : '', collaboratorResponse.statusText);
        }
        const { collaboratorPool } = this._getCollaboratorSources(assetID);
        const updateSession = collaboratorPool.createUpdateSession();
        updateSession.queueDiscardedItem(collaborator);
        // If the current user is being removed from the collaboration list, remove the asset from accessConfirmedIDs
        // in case the user is re-invited to the asset and needs to confirm access again.
        if (collaborator.isCurrentUser) {
            this._accessConfirmedIDs.delete(assetID);
        }
        updateSession.commit();
    }
    /**
     * Removes the provided InvitedCollaborator and updates the ItemPools.
     *
     * This eventually performs a PATCH request to the invitations service and removes the provided invited
     * collaborator.
     *
     * @throws If the request is not successful, an error is thrown.
     *
     * @param assetID
     * @param invitedCollaborator {InvitedCollaborator}
     */
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and renamed for internal use on or after
     * 2024-10-01. Use {@link ShareableItem.removeInvitedCollaborator} instead
     */
    async removeInvitedCollaborator(assetID, invitedCollaborator) {
        var _a, _b;
        const response = await this._invitationInterface.modifyCollaborators(assetID, {
            invitations: [{ id: invitedCollaborator.id, action: 'remove' }]
        });
        const collaboratorResponse = (_a = response.invitations) === null || _a === void 0 ? void 0 : _a.find((c) => c.id === invitedCollaborator.id);
        if (!collaboratorResponse) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'InvitedCollaborator patch request unsuccessful');
        }
        if (collaboratorResponse.statusCode !== 200) {
            throw SAMError.createWithRepositoryCode(collaboratorResponse.statusCode, (_b = collaboratorResponse.errorSubcode) !== null && _b !== void 0 ? _b : '', collaboratorResponse.statusText);
        }
        const { invitedCollaboratorPool } = this._getCollaboratorSources(assetID);
        const updateSession = invitedCollaboratorPool.createUpdateSession();
        updateSession.queueDiscardedItem(invitedCollaborator);
        updateSession.commit();
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     *    It may be possible to use {@link ShareableItem.collaboratorArray}. If this does not work, contact the
     *    SAM.js team before removal.
     */
    getInvitedCollaborators(assetID) {
        return this._getCollaboratorSources(assetID).invitedCollaboratorSource;
    }
    /**
     * Used by ShareableItem and SAMCpp.ts patches
     *
     * @package -- only for use inside SAM.js
     */
    async _removeCurrentUserFromCollaborationList(asset) {
        // If collaborator array isn't loaded or needs refresh, load it immediately
        if (!asset._isCollaboratorDataLoaded) {
            await this._getCollaboratorsNow(asset);
        }
        const collaborator = asset.collaboratorArray.find(c => c.isCurrentUser);
        if (!collaborator) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, `Current user is not a collaborator on ${asset.id}`);
        }
        await this.removeCollaborator(asset.id, collaborator);
        // removeCollaborator() will remove the Collaborator from the pools, but it also needs to be removed from the
        // collaboratorArray, and the collaboration flags need to be re-calculated, and collaboration event listeners
        // notified.
        if (asset.collaboratorArray.includes(collaborator)) {
            const updatedCollaborators = asset.collaboratorArray.filter(c => c !== collaborator);
            asset._setCollaboratorData(updatedCollaborators);
        }
    }
    /**
     * @deprecated -- do not use, it does not work for users who were invited via an address book contact,
     * and may break for all use cases in the future. Instead, use
     * {@link ShareableItem.removeCurrentUserFromCollaborationList}. Will be removed on or after 2024-10-01.
     *
     *  https://jira.corp.adobe.com/browse/SAM-3564
     */
    removeCurrentUserFromCollaborationList(assetID) {
        oneTimeWarning('removeCurrentUserFromCollaborationList/getCollaborator', 'This method does not work correctly and will be removed. Use'
            + ' ShareableItem.removeCurrentUserFromCollaboratorList() insstead.');
        const collaborator = this.getCollaborator(assetID);
        return this.removeCollaborator(assetID, collaborator);
    }
    /**
     * @deprecated -- Do not use, it does not work unless collaborator data is already loaded, and the Collaborator
     * representing the current user was NOT invited via an address book contact. The Collaborator it returns in most
     * cases will have invalid data in it. Will be removed on or after 2024-10-01.
     *
     * https://jira.corp.adobe.com/browse/SAM-3564
     */
    getCollaborator(assetID) {
        var _a;
        oneTimeWarning('removeCurrentUserFromCollaborationList/getCollaborator', 'This method does not work correctly and will be removed. Use'
            + ' ShareableItem.collaboratorsArray.find(c => c.isCurrentUser()) instead.');
        const userID = this._core.environment.getValue('USER_ID');
        const collaborator = (_a = this._getCollaboratorSources(assetID).collaboratorPool.getLocalItemById(userID)) !== null && _a !== void 0 ? _a : new Collaborator(this._core, userID);
        return collaborator;
    }
    /**
     *
     * @package Used internally for now, please reach out to our team
     * if this is required externally.
     *
     * @param assetID
     * @param collaborators List of collaborators to add
     */
    _addCollaborators(assetID, collaborators, configuration, notification, captcha) {
        const request = Object.assign(Object.assign(Object.assign({ recipients: collaborators }, (!notification
            ? {
                notification: {
                    ans: {}
                }
            }
            : { notification })), (configuration ? { configuration } : {})), (captcha ? { captcha } : {}));
        this._invitationInterface.addCollaborators(assetID, request);
    }
    getPendingInvitations() {
        return this._invitationInterface.getPendingInvitations();
    }
    /**
     * A method that confirms access for a resource or asset and keeps
     * track of that confirmation for this session to prevent
     * more than one call being made per session per asset.
     *
     * Confirming access is not about permissions per-say, it's more
     * about accepting the invitation to a resource that requires acceptance.
     *
     * It also handles email/account ambiguity so let's say an email
     * has more than one adobe account associated with it, calling
     * confirm access will also resolve this issue at that time.
     *
     * @note This is really just a temporary measure until
     * the Invitations Service can provide a more efficient way
     * of checking multiple assets at a time to see if they
     * have already confirmed access for the current user token.
     *
     *
     * @param assetId Resource URN
     * @returns void
     */
    async confirmAccess(assetId) {
        if (this._accessConfirmedIDs.has(assetId)) {
            return;
        }
        await this._invitationInterface.confirmAccess(assetId);
        this._accessConfirmedIDs.add(assetId);
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// NOTE: since previous versions of SAM.js used override_map, this cannot be changed to override_set,
//       or it would defeat the purpose of tracking overrides applied by different versions of SAM.js.
const $OVERRIDE_SET = Symbol.for('com.adobe.ccac.samjs.extendclass.override_map');
const $EXTENSION_SET = Symbol.for('com.adobe.ccac.samjs.extendclass.extension_set');
const $OBSERVABLE_SET = Symbol.for('com.adobe.ccac.samjs.extendclass.observable_set');
const $EMITTER = Symbol.for('com.adobe.ccac.samjs.extendclass.emitter');
const $OVERRIDDEN_GETTERS = Symbol.for('com.adobe.ccac.samjs.extendclass.overridden_getters');
/**
 * Extends the provided `klass` with the properties with new behaviors, and ensures it supports the
 * EventEmitter interface. Optionally, it will make all getter properties observable.
 *
 * @param options: IExtendClassOptions
 * @example
 *     class MyClass {
 *         private _foo = 32;
 *         private _bar = 99;
 *         private _baz = {};
 *         refreshBaz(x) { some impl }
 *         get foo() { return this._foo; }
 *         get bar() { return this._bar; }
 *         get baz() { return this.baz; }
 *     }
 *     ExtendClass({
 *         klass: MyClass,
 *         extensions: {
 *             refreshBaz(x) { return x * 2; },       // ignored because it exists on MyClass
 *             newFn() { return this._foo; }          // Added
 *             // Getters/setters must use PropertyDescriptor syntax
 *             // NOTE: won't be automatically made observable
 *             aliasForFoo: {
 *                 get() {
 *                     // make this getter alias observable
 *                     this._core.environment.mobx.reportObserved('change');
 *                     return this._foo;
 *                 }
 *             }
 *         },
 *         overrides: {
 *             // replaces original refreshBaz
 *             refreshBaz(x) {
 *                 // Use the added TypedEventEmitter.dispatch()
 *                 this.dispatch({ type: 'bazRefresh' });
 *                 // invoke original oldFn
 *                 return this.oldFn.original(x);
 *             }
 *         },
 *         // When client subscribes to 'change' event, the extended class will proxy that event handler to the
 *         // 'incrementFoo' and 'incrementBar' events that the native implementation emits.
 *         eventMap: { change: [ 'incrementFoo', 'incrementBar' ] },
 *         observableMap: {
 *             // the `baz` getter will be made observable, bound to the 'bazRefresh' event
 *             'bazRefresh': [ 'baz' ]
 *         }
 *         // `foo` and `bar` getter will be made observable, bound to the 'change' event;
 *         defaultObservableEvent: 'change',
 *     });
 */
function ExtendClass({ klass, core, extensions = {}, overrides = {}, eventMap = {}, defaultObservableEvent, observableMap = {} }) {
    const originalProps = Object.getOwnPropertyDescriptors(klass.prototype);
    // SAM.cpp EventEmitter-ish interface is on the prototype's prototype
    let emitterProps = originalProps;
    if (typeof emitterProps.on === 'undefined') {
        emitterProps = Object.getOwnPropertyDescriptors(Object.getPrototypeOf(klass.prototype));
    }
    // Add reference to shared core if not already defined
    if (!originalProps._core) {
        Object.defineProperty(klass.prototype, '_core', { value: core });
    }
    // When overriding a getter, patch code may need access to the original using
    //    this[$OVERRIDDEN_GETTERS]['someProp'].call(this)
    if (!Object.getOwnPropertyDescriptor(klass.prototype, $OVERRIDDEN_GETTERS)) {
        Object.defineProperty(klass.prototype, $OVERRIDDEN_GETTERS, { value: {} });
    }
    // Some SAM.cpp classes (AssetList) have an event handling API similar to EventEmitter, and some don't
    // (Asset). Meanwhile, most SAM.js classes implement TypedEventEmitter. This section patches the
    // prototype to match the TypedEventEmitter API. If the SAM.cpp class doesn't have an event-handling
    // API, an actual instance of EventEmitter is used instead.
    if (!Object.getOwnPropertyDescriptor(klass.prototype, $EMITTER)) {
        Object.defineProperties(klass.prototype, {
            [$EMITTER]: {
                get() {
                    var _a;
                    // Redefine the $EMITTER getter property for `this` instance to an actual EventEmitter, or an
                    // adapter to the SAM.cpp equivalent. The $EMITTER getter on the prototype remains unchanged
                    // so the next instance will get its own properly bound $EMITTER.
                    let emitter;
                    if (typeof ((_a = emitterProps.on) === null || _a === void 0 ? void 0 : _a.value) === 'function') {
                        // SAM.cpp class has an event handling API already, so create an adapter bound
                        // to the SAM.cpp implementation
                        emitter = {
                            on: emitterProps.on.value.bind(this),
                            off: emitterProps.off.value.bind(this),
                            emit: emitterProps.trigger.value.bind(this),
                            addListener: emitterProps.on.value.bind(this),
                            removeListener: emitterProps.off.value.bind(this)
                        };
                    }
                    else {
                        // SAM.cpp class has no event handling API, so use an actual EventEmitter
                        emitter = new EventEmitter();
                    }
                    Object.defineProperty(this, $EMITTER, { value: emitter });
                    return this[$EMITTER];
                }
            },
            on: {
                value(eventName, ...rest) {
                    var _a;
                    // Some SAM.cpp events map to multiple SAM.js events. If there's no mapping, then subscribe
                    // to `eventName` only.
                    const mappedEvents = (_a = eventMap[eventName]) !== null && _a !== void 0 ? _a : [eventName];
                    mappedEvents.forEach(mappedEventName => this[$EMITTER].on(mappedEventName, ...rest));
                    return this;
                }
            },
            addListener: {
                value(...args) { return this.on(...args); }
            },
            off: {
                value(eventName, ...rest) {
                    var _a;
                    // Some SAM.cpp events map to multiple SAM.js events. If there's no mapping, then unsubscribe
                    // from `eventName` only.
                    const mappedEvents = (_a = eventMap[eventName]) !== null && _a !== void 0 ? _a : [eventName];
                    mappedEvents.forEach(mappedEventName => this[$EMITTER].off(mappedEventName, ...rest));
                    return this;
                }
            },
            removeListener: {
                value(...args) { return this.off(...args); }
            },
            emit: {
                value(...args) {
                    return this[$EMITTER].emit(...args);
                }
            },
            // SAM.js TypedEventEmitter behavior
            dispatch: {
                value(event) { return this.emit(event.type, event, this); }
            }
        });
    }
    // Allow shortcut of { someMethod(): {} } instead of { someMethod: { value: function() {} } }
    function makePropertyDesc(map, key) {
        const propertyDesc = map[key];
        if (typeof map[key] === 'function') {
            return { value: propertyDesc };
        }
        else if (['string', 'boolean', 'number'].includes(typeof map[key])) {
            // TypeScript doesn't block clients from passing a primitive, and
            // Object.defineProperty(obj, key, primitive) is a no-op.
            throw new Error('Cannot use primitives for PropertyDescriptor');
        }
        else {
            // An object. TypeScript will verify that it is a valid PropertyDescriptor.
            return propertyDesc;
        }
    }
    // Track which properties have been handled in this call to ExtendClass
    const handledProps = new Set();
    // Extensions should handle their own observability, thus should not be made observable automatically,
    // so extensions must be tracked across calls to ExtendClass.
    const extendedProps = klass.prototype[$EXTENSION_SET] || new Set();
    Object.keys(extensions).forEach(propertyName => {
        // cannot override existing properties with extensions
        if (originalProps[propertyName]) {
            return;
        }
        const propertyDesc = makePropertyDesc(extensions, propertyName);
        Object.defineProperty(klass.prototype, propertyName, propertyDesc);
        handledProps.add(propertyName);
        extendedProps.add(propertyName);
    });
    klass.prototype[$EXTENSION_SET] = extendedProps;
    const overriddenProps = klass.prototype[$OVERRIDE_SET] || new Set();
    Object.keys(overrides).forEach(propertyName => {
        if (overriddenProps.has(propertyName)) {
            // Overrides from a previous call to ExtendClass should not be overridden again, but this
            // is not an error.
            return;
        }
        if (handledProps.has(propertyName) || extendedProps.has(propertyName)) {
            throw new Error(`[ExtendClass] cannot override property '${propertyName}' because it is an extension.`);
        }
        // Since overridden functions might expect to use `this.originalFn.original()`, it's not safe to treat the
        // override as an extension. Different hosts could have different versions of SAM.cpp, so patching code should
        // be aware of what is needed.
        if (!originalProps[propertyName]) {
            throw new Error(`[ExtendClass] target class does not have property '${propertyName}' so it cannot `
                + 'be overridden.');
        }
        const propertyDesc = makePropertyDesc(overrides, propertyName);
        const original = originalProps[propertyName].value;
        if (typeof original === 'function') {
            // Replace the the original method with the override, then attach the original
            // to the override so you can call it with `this.override.original()`
            Object.defineProperty(klass.prototype, propertyName, {
                get() {
                    // Ensure `original` is bound to correct `this`
                    propertyDesc.value.original = original.bind(this);
                    return propertyDesc.value;
                }
            });
        }
        else {
            if (originalProps[propertyName].get) {
                klass.prototype[$OVERRIDDEN_GETTERS][propertyName] = originalProps[propertyName].get;
            }
            Object.defineProperty(klass.prototype, propertyName, propertyDesc);
        }
        overriddenProps.add(propertyName);
        handledProps.add(propertyName);
    });
    klass.prototype[$OVERRIDE_SET] = overriddenProps;
    // Validate the passed-in observableMap config does not contain coding errors. If a config defines
    // two events for the same property, or defines an event for an extension/override, that is likely
    // a bug.
    const propToEventMap = {};
    Object.keys(observableMap).reduce((seenPropNames, eventName) => {
        observableMap[eventName].forEach(propName => {
            // If a property is mapped multiple times, or was extended/overwritten, treat as a coding error,
            // otherwise the result would be difficult to debug
            if (seenPropNames.has(propName)) {
                throw new Error(`[ExtendClass] cannot have the same property (${propName}) appear multiple times in observableMap`);
            }
            if (handledProps.has(propName)) {
                throw new Error(`[ExtendClass] cannot make property (${propName}) observable as it was defined in extensions/overrides`);
            }
            seenPropNames.add(propName);
            propToEventMap[propName] = eventName;
        });
        return seenPropNames;
    }, new Set());
    // Add properties to be observed using the defaultObservableEvent to propToEventMap
    if (defaultObservableEvent) {
        Object.keys(originalProps)
            // only collect getter properties
            .filter(propName => !!originalProps[propName].get)
            .forEach(propName => propToEventMap[propName] = defaultObservableEvent);
    }
    // Get already-wrapped properties that were made observable by previous calls to ExtendClass, if any,
    // so that they won't be wrapped again.
    const observedProps = klass.prototype[$OBSERVABLE_SET] || new Set();
    // merge all extended/overridden/observed props (including from previous calls to ExtendClass) into handledProps
    [...extendedProps, ...overriddenProps, ...observedProps].forEach(propName => handledProps.add(propName));
    Object.entries(propToEventMap).forEach(([propName, eventName]) => {
        var _a;
        // Only wrap getters, and properties that have not already been patched, either from this
        // call to ExtendClass, or a previous call. If it is from a previous call, then this is not
        // error.
        if (!((_a = originalProps[propName]) === null || _a === void 0 ? void 0 : _a.get) || handledProps.has(propName)) {
            return;
        }
        Object.defineProperty(klass.prototype, propName, {
            get() {
                this._core.environment.mobx.reportObserved(this, eventName);
                return originalProps[propName].get.call(this);
            }
        });
        observedProps.add(propName);
    });
    klass.prototype[$OBSERVABLE_SET] = observedProps;
}

/*
NOTE when testing in UXP SAM Demo app:

When using Chrome debugger to refresh the JS source code, it does not destroy and recreate the SAM.cpp
JS prototypes, so any changes made to this file will not be applied despite appearing in the source
inspector. You have to quit the SAM.cpp demo app and restart.
*/
// Use a getter so that unit tests can inject a mock acplModel
function getACPLModel() {
    // NOTE: must use this syntax for accessing properties of `window`, or else it will break NodeJS clients
    // like Libraries.
    // @ts-ignore
    return typeof window !== 'undefined' ? window === null || window === void 0 ? void 0 : window.acplModel : undefined;
}
// Convert semver into a padded string that can be compared lexically
function parseVersion(version = '') {
    // Old SAM.cpp versions looked like 2.0.0-main.1234, where the final number was the
    // patch version. Current SAM.cpp uses standard semver major.minor.patch.
    const parts = version.split('.');
    // Coerce old SAM.cpp version into a standard semver, like 2.0.1234, by
    // deleting the '0-main' element
    if (parts[2] === '0-main') {
        parts.splice(2, 1);
    }
    // Ensure each part has some value
    const [major = '0', minor = '0', patch = '0'] = parts;
    // '2.0.1234' becomes '000200001234'
    return [major, minor, patch].map(part => part.padStart(4, '0')).join('');
}
function isSAMCppVer(comparison, version) {
    var _a;
    const aVer = parseVersion((_a = getACPLModel()) === null || _a === void 0 ? void 0 : _a.version);
    const bVer = parseVersion(version);
    switch (comparison) {
        case 'lt': return aVer < bVer;
        case 'gte': return aVer >= bVer;
        default: return false;
    }
}
class ThumbnailChangeEvent extends SAMEvent {
    constructor() {
        super('thumbnailChange');
    }
}
const SharedThumbnailChangeEvent = new ThumbnailChangeEvent();
class PermissionsChangeEvent extends SAMEvent {
    constructor() {
        super('permissionsChange');
    }
}
const SharedPermissionsChangeEvent = new PermissionsChangeEvent();
// See note above about testing in UXP SAM demo app
function patchAssetContext(core) {
    const acplModel = getACPLModel();
    // TODO: expose SAM.cpp AssetContext as acplModel.AssetContext
    // https://jira.corp.adobe.com/browse/SAM-1365
    const AssetContextJS = acplModel.applicationAssetContext.constructor;
    // TODO: update actual version when the issues below are fixed
    if (isSAMCppVer('lt', '9999.9999.9999')) {
        ExtendClass({
            klass: AssetContextJS,
            core,
            overrides: {
                // SAM.cpp does not support optional param
                getAssetWithCloudID(id, confirmAccess = false) {
                    return this.getAssetWithCloudID.original(id, confirmAccess);
                },
                // SAM.cpp does not support optional param
                getAssetWithAssetRefString(assetRefString, confirmAccess = false) {
                    return this.getAssetWithAssetRefString.original(assetRefString, confirmAccess);
                }
            },
            extensions: {
                // SAM.cpp hasn't implemented AssetContext#getUserRepoId(). This method was added to
                // address *SAM-1888*. This patch can be removed when SamCpp supports AssetContext#getUserRepoId()
                getUserRepoID() {
                    // Can't use hostAssetContext.getUserRepoID() because it needs hostAssetContext.itemPool,
                    // which doesn't exist on SAM.cpp hostAssetContext.
                    if (!this._userRepoID) {
                        return this._core._AssetContextJS.repoInterface.getHomeDirectoryMetadata()
                            .then((homeDirectoryMetadata) => {
                            this._userRepoID = homeDirectoryMetadata['repo:repositoryId'];
                            return this._userRepoID;
                        });
                    }
                    return Promise.resolve(this._userRepoID);
                },
                transfer(source, target) {
                    return (async () => {
                        let targetId;
                        if (target instanceof Space) {
                            const workingFiles = await target.getChild('working_files');
                            if (!workingFiles) {
                                throw SAMError.create(SAMErrorCode.NOT_FOUND, 'unable to find working_files for provided space');
                            }
                            targetId = workingFiles.id;
                        }
                        else {
                            targetId = target.cloudID;
                        }
                        // ACPL substitutes ~ for user's repoID
                        let sourceRepoID = source.repositoryID;
                        let targetRepoID = target.repositoryID;
                        if (sourceRepoID === '~') {
                            sourceRepoID = await this.getUserRepoID();
                        }
                        if (targetRepoID === '~') {
                            targetRepoID = await this.getUserRepoID();
                        }
                        const payload = {
                            source: { assetId: source.cloudID, repositoryId: sourceRepoID },
                            target: { assetId: targetId, repositoryId: targetRepoID }
                        };
                        return this._core._AssetContextJS._assetTransferService.transfer(payload);
                    })();
                },
                // For code in SAM.js that needs to use the ItemPool from SAM.js, like SpaceContext.moveSpace()
                assetPool: {
                    get() {
                        return this._core._AssetContextJS.assetPool;
                    }
                },
                isOnline: {
                    get() {
                        return this.networkStatus !== undefined ? this.networkStatus !== NetworkStatus.Offline : undefined;
                    }
                },
                // Most JS clients never used recentsSource, so use the SAM.js name
                recentAssetsSource: {
                    get() {
                        return this.recentsSource;
                    }
                },
                getRootFolder() {
                    return Promise.resolve(this.rootFolder);
                },
                assetsHaveArtifacts(assets) {
                    return (async () => {
                        // @ts-ignore
                        if (assets.some((a) => !a.routesToAcplV2)) {
                            return false;
                        }
                        const assetReviewServiceInterface = new AssetReviewServiceInterface(core);
                        const assetIndentifiers = mapAssetsToAssetIndentifier(assets);
                        const results = await assetReviewServiceInterface.hasOutputs(assetIndentifiers);
                        if (results.length === 0) {
                            return false;
                        }
                        results.forEach((asset) => {
                            const foundAsset = assets.find((a) => a.id === asset['repo:assetId']);
                            if (!foundAsset) {
                                return;
                            }
                            foundAsset['_hasArtifacts'] = true;
                            foundAsset.dispatch({ type: 'change' });
                        });
                        return true;
                    })();
                }
            },
            observableMap: {
                'networkChanged': ['networkStatus'],
                'stateChanged': ['cloudSyncStatus']
            }
        });
        const onAssetsChanged = ({ properties, status, previews }) => {
            var _a, _b;
            for (const asset of [...properties, ...status]) {
                (_a = asset._onAssetChanged) === null || _a === void 0 ? void 0 : _a.call(asset);
            }
            for (const asset of previews) {
                (_b = asset === null || asset === void 0 ? void 0 : asset._onRenditionChanged) === null || _b === void 0 ? void 0 : _b.call(asset);
            }
        };
        // only subscribe once
        if (!acplModel.applicationAssetContext._isForwardingAssetChangeEvents) {
            acplModel.applicationAssetContext.on('changedAssets', onAssetsChanged);
            acplModel.applicationAssetContext._isForwardingAssetChangeEvents = true;
        }
    }
}
// See note above about testing in UXP SAM demo app
function patchAssetSource(core) {
    const acplModel = getACPLModel();
    const dirMap = {
        ascending: OrderDirection.Ascending,
        descending: OrderDirection.Descending
    };
    const fieldMap = {
        name: OrderField.Name,
        modified: OrderField.Modified,
        created: OrderField.Created,
        shared: OrderField.Shared
    };
    const AssetSource = acplModel.applicationAssetContext.rootFolder.children.constructor;
    // TODO: update actual version when the issues below are fixed
    if (isSAMCppVer('lt', '9999.9999.9999')) {
        ExtendClass({
            klass: AssetSource,
            core,
            overrides: {
                // SAM.cpp doesn't support Order/Filter objects that SAM.js clients are using. Meanwhile,
                // clients like CDO are expecting the SAM.cpp call signature, so both APIs are supported here.
                getList(maybeOrder, maybeFilter) {
                    var _a, _b;
                    let field;
                    let dir;
                    // if both `direction` and `field` are defined, then it is an AdobeAssetOrder instance
                    const order = maybeOrder;
                    if ((_b = (_a = order.direction) !== null && _a !== void 0 ? _a : order.field) !== null && _b !== void 0 ? _b : true) {
                        dir = dirMap[order.direction];
                        field = fieldMap[order.field];
                    }
                    else {
                        dir = maybeOrder;
                        field = maybeFilter;
                        maybeFilter = undefined;
                    }
                    // Fallback to safe defaults
                    dir = (dir in OrderDirection) ? dir : OrderDirection.Descending;
                    field = (field in OrderField) ? field : OrderField.Modified;
                    const filter = maybeFilter;
                    let list;
                    // Note: if mediaTypes is undefined, it should use the default filter (SAM.cpp only)
                    // If mediaTypes is an empty array, that means "show everything".
                    if (filter === null || filter === void 0 ? void 0 : filter.mediaTypes) {
                        list = this.getListWithTypes(dir, field, [...filter.mediaTypes]);
                    }
                    else {
                        list = this.getList.original(dir, field);
                    }
                    // Note: must patch AssetList before patching AssetSource or else next line will throw an exception
                    list._addEventInterceptors();
                    return list;
                }
            }
        });
    }
}
// See note above about testing in UXP SAM demo app
function patchAssetList(core) {
    const acplModel = getACPLModel();
    const SAM_CPP_ITEM_EVENTS = ['refreshed', 'movedAsset', 'addedAsset', 'removedAsset'];
    const SAM_CPP_STATUS_EVENT = 'statusChanged';
    const DELAY_MS = 120;
    // TODO: expose AssetSource, AssetList as acplModel.AssetSource, acplModel.AssetList
    // https://jira.corp.adobe.com/browse/SAM-1365
    const assetSource = acplModel.applicationAssetContext.rootFolder.children;
    const AssetList = assetSource.getList(OrderDirection.Descending, OrderField.Modified).constructor;
    // TODO: update actual version when the issues below are fixed
    if (isSAMCppVer('lt', '9999.9999.9999')) {
        ExtendClass({
            klass: AssetList,
            core,
            extensions: {
                // SAM.cpp doesn't have loadNextPage, so map to loadMoreItems
                loadNextPage() {
                    return this.loadMoreItems();
                },
                // TODO: deprecate SAM.cpp load() and add refresh()
                //       https://jira.corp.adobe.com/browse/SAM-2099
                refresh() {
                    return this.load();
                },
                // patchAssetSource calls this
                _addEventInterceptors() {
                    // only subscribe once
                    if (!this._onError) {
                        this._onError = (error) => {
                            this._errors = this._errors || [];
                            this._errors.push(error);
                            this.dispatch(SharedStateChangedEvent);
                        };
                        this.on('error', this._onError);
                    }
                    if (!this._onListNotification) {
                        this._onListNotification = () => {
                            if (this._notificationTimer !== undefined) {
                                return;
                            }
                            this._notificationTimer = setTimeout(() => {
                                this._notificationTimer = undefined;
                                // Invoke the SAM.cpp items getter
                                this._items = this[$OVERRIDDEN_GETTERS]['items'].call(this);
                                this.dispatch(SharedListChangedEvent);
                                // Also fire status change so that items list is at the expected contents
                                // when the status changes. Clients may post-process and cache list items
                                // from SharedListChangedEvent, so SharedStateChangeEvent must be fired
                                // afterward to ensure their cache has all expected items when the status changes.
                                this.dispatch(SharedStateChangedEvent);
                            }, DELAY_MS);
                        };
                        // SAM.cpp fires 4 different events for list change, but SAM.js clients only expect 1.
                        SAM_CPP_ITEM_EVENTS.forEach(eventName => this.on(eventName, this._onListNotification));
                    }
                    if (!this._onStatusChange) {
                        this._onStatusChange = () => {
                            // After calling getList, clients should immediately receive the Loading
                            // status change event
                            if (this.status === AssetListStatus.Loading) {
                                this.dispatch(SharedStateChangedEvent);
                            }
                            else {
                                this._onListNotification();
                            }
                        };
                        this.on(SAM_CPP_STATUS_EVENT, this._onStatusChange);
                    }
                },
                errors: {
                    get() {
                        // Mobx observes under the SAM.js status event, rather than the CPP events
                        this._core.environment.mobx.reportObserved(this, SharedStateChangedEvent.type);
                        return this._errors;
                    }
                }
            },
            overrides: {
                load() {
                    this._errors = undefined;
                    this.load.original();
                    this.dispatch(SharedStateChangedEvent);
                },
                loadMoreItems() {
                    this.loadMoreItems.original();
                    this.dispatch(SharedStateChangedEvent);
                },
                // SAM.cpp creates a new Array instance and populates it every time clients call `list.items`, which
                // adds a small (but non-zero) performance hit, and has unexpected behavior like
                // `list.items !== list.items`. This patch caches the SAM.cpp items Array.
                items: {
                    get() {
                        this._core.environment.mobx.reportObserved(this, SharedListChangedEvent.type);
                        if (!this._items) {
                            // Invoke the SAM.cpp getter
                            this._items = this[$OVERRIDDEN_GETTERS]['items'].call(this);
                        }
                        return this._items;
                    }
                }
            },
            observableMap: {
                // This makes 'status' property observable under the SAM.js SharedStateChangedEvent
                [SharedStateChangedEvent.type]: ['status']
                // The 'items' property is made observable in the getter override under the SharedListChangeEvent
            }
        });
    }
}
// See note above about testing in UXP SAM demo app
function patchAdobeAsset(core) {
    const acplModel = getACPLModel();
    const ACPLAsset = acplModel.applicationAssetContext.rootFolder.constructor;
    // TODO: update actual version when the issues below are fixed
    if (isSAMCppVer('lt', '9999.9999.9999')) {
        ExtendClass({
            klass: ACPLAsset,
            core,
            overrides: {
                restore(name) {
                    if (this.isLibraryElement) {
                        // SAM.cpp doesn't support restoring CCLibrary Elements
                        return this._core._AssetContextJS.restoreLibraryElement(this.cloudID);
                    }
                    else {
                        return this.restore.original(name);
                    }
                },
                permanentlyDelete() {
                    if (this.isLibraryElement) {
                        // SAM.cpp doesn't support permanently deleting CCLibrary Elements
                        return this._core._AssetContextJS.permanentlyDeleteLibraryElement(this.cloudID);
                    }
                    else {
                        return this.permanentlyDelete.original();
                    }
                },
                duplicate() {
                    return this.duplicate.original(false /*overWriteExisting*/);
                }
            },
            extensions: {
                // SAM.cpp doesn't have a `isShared` property
                isShared: {
                    get() {
                        this._core.environment.mobx.reportObserved(this, COLLABORATOR_CHANGED);
                        // SWY Assets are shared, so no need to load collaborator data
                        // if isSharedWithYouAsset is false, it might not be known if it is a SWY item
                        if (this.isSharedWithYouAsset) {
                            return true;
                        }
                        const isShared = this._isShared;
                        if (isShared !== undefined) {
                            return isShared;
                        }
                        // Trigger the fetch for collaborators which enqueues the asset for the bulk fetch.
                        this._collaboratorDataIsDirty = true;
                        AdobeAsset.prototype._queueCollaborators.apply(this);
                    }
                },
                // SAM.cpp doesn't have a `_setCollaboratorData`, which is called when the bulk fetch response is received in _handleBatchRequest.
                _setCollaboratorData(updatedCollaborator) {
                    AdobeAsset.prototype._setCollaboratorData.apply(this, [updatedCollaborator]);
                },
                // SAM.cpp doesn't have a `directCollaboratorCount` property
                directCollaboratorCount: {
                    get() {
                        this._core.environment.mobx.reportObserved(this, COLLABORATOR_CHANGED);
                        if (this._directCollaboratorCount !== undefined) {
                            return this._directCollaboratorCount;
                        }
                        // Trigger the fetch for collaborators which enqueues the asset for the bulk fetch.
                        this._collaboratorDataIsDirty = true;
                        AdobeAsset.prototype._queueCollaborators.apply(this);
                        return this._directCollaboratorCount;
                    }
                },
                // SAM.cpp doesn't have a `collaboratorArray` property
                collaboratorArray: {
                    get() {
                        this._core.environment.mobx.reportObserved(this, COLLABORATOR_CHANGED);
                        if (this._collaboratorsArray !== undefined) {
                            return this._collaboratorsArray;
                        }
                        // Trigger the fetch for collaborators which enqueues the asset for the bulk fetch.
                        this._collaboratorDataIsDirty = true;
                        AdobeAsset.prototype._queueCollaborators.apply(this);
                        return this._collaboratorsArray;
                    }
                },
                // SAM.cpp doesn't have an `isPublicLink` property
                isPublicLink: {
                    get() {
                        this._core.environment.mobx.reportObserved(this, COLLABORATOR_CHANGED);
                        if (this._isPublicLink !== undefined) {
                            return this._isPublicLink;
                        }
                        // Trigger the fetch for collaborators which enqueues the asset for the bulk fetch.
                        this._collaboratorDataIsDirty = true;
                        AdobeAsset.prototype._queueCollaborators.apply(this);
                        return this._isPublicLink;
                    }
                },
                // SAM.cpp doesn't have an `id` property
                id: {
                    get() {
                        // `id` is observable because cloudID and localID are observable
                        // due to defaultObservableEvent
                        return this.cloudID || this.localID;
                    }
                },
                // Used internally by SAM.js
                _isCollaboratorDataLoaded: {
                    get() {
                        // Since patches can't create a new initializer, it must be inferred that
                        // _collaboratorDataIsDirty is true when undefined OR true
                        return this._collaboratorDataIsDirty === false;
                    }
                },
                // SAM.cpp doesn't expose leave
                removeCurrentUserFromCollaborationList() {
                    return this._core.collaborationContext._removeCurrentUserFromCollaborationList(this);
                },
                // SAM.cpp doesn't expose discard
                discard() {
                    // For Projects items, archive() will do a discard
                    return this.archive().then(() => {
                        if (!this.routesToAcplV2) {
                            oneTimeWarning('discardAsArchive', `Asset.discard(): '${this.repoPath}' will be archived rather than discarded`
                                + ' because it routes to ACPL v1 rather than V2.');
                        }
                        // Archive might not be complete in the cloud yet, so monitor via event listener
                        const discardOutputs = () => {
                            // If the asset is not discarded or does not have artifacts
                            // or does not route to ACPLv2 we don't want to attempt to discard
                            // the outputs folder.
                            if (!this.isDiscarded || !this._hasArtifacts || !this.routesToAcplV2) {
                                return;
                            }
                            const assetReviewServiceInterface = new AssetReviewServiceInterface(this._core);
                            assetReviewServiceInterface.discardOutputsForAsset(this.id).then(() => {
                                this._hasArtifacts = false;
                                this.off(SharedChangeEvent.type, discardOutputs);
                            });
                        };
                        this.on(SharedChangeEvent.type, discardOutputs);
                        // in case it did complete in the cloud
                        if (this.isDiscarded && this._hasArtifacts) {
                            discardOutputs();
                        }
                    });
                },
                // SAM.cpp doesn't have `links`
                links: {
                    get() {
                        // Can't infer the real links because there's no way to know the real region the Asset is
                        // hosted. Can't use the generalized endpoints, because when the service redirects to the
                        // regionalized endpoint, it can drop the auth headers, causing the redirect to fail. For
                        // now, return empty object to prevent `undefined` errors.
                        return {};
                    }
                },
                hasPermission(...permissions) {
                    this._core.environment.mobx.reportObserved(this, SharedPermissionsChangeEvent.type);
                    const hasPermissions = !!this._permissions && AdobeAsset.prototype.hasPermission.apply(this, permissions);
                    if (!this._hasLoadedPermissions) {
                        // prevent loading when already loading
                        this._hasLoadedPermissions = true;
                        this._core._AssetContextJS.repoInterface.resolveById(this.cloudID)
                            .then((assetInterface) => {
                            return assetInterface.getEffectivePermissions();
                        })
                            .then((permissions) => {
                            this._permissions = permissions;
                            this.dispatch(SharedPermissionsChangeEvent);
                        });
                    }
                    return hasPermissions;
                },
                // SAM.cpp doesn't have thumbnailURL
                thumbnailURL: {
                    get() {
                        var _a;
                        this._core.environment.mobx.reportObserved(this, SharedThumbnailChangeEvent.type);
                        // Library Elements do not work on SAM.cpp at all
                        if (this.isFolder || this.isLibraryElement) {
                            return '';
                        }
                        if (this._refreshThumbnail || !this._thumbnailInfo) {
                            // Prevent multiple calls to getThumbnailRendition. ACPLAsset.ts didn't retry
                            // on reject, so this code shouldn't need to.
                            this._refreshThumbnail = false;
                            this._thumbnailStatus = RenditionStatus.Fetching;
                            this.getThumbnailRendition()
                                .then(({ file, hash }) => {
                                var _a, _b;
                                this._thumbnailStatus = RenditionStatus.Done;
                                if (((_a = this._thumbnailInfo) === null || _a === void 0 ? void 0 : _a.file) === file && ((_b = this._thumbnailInfo) === null || _b === void 0 ? void 0 : _b.hash) === hash) {
                                    return; // no change
                                }
                                // `#` and `^` must be encoded separately to work in UXP
                                file = file.replace('#', '%23').replace('^', '%5E');
                                const url = new URL(`file://${file}`);
                                url.searchParams.append('force', hash);
                                this._thumbnailInfo = {
                                    file,
                                    hash,
                                    url: url.href
                                };
                                this.dispatch(SharedThumbnailChangeEvent);
                            })
                                .catch(() => {
                                this._thumbnailStatus = RenditionStatus.Error;
                                // Use empty thumbnailInfo so that re-render due to the status change doesn't try to
                                // fetch, which will probably still fail, getting stuck in a loop.
                                this._thumbnailInfo = {};
                                this.dispatch(SharedThumbnailChangeEvent);
                            });
                        }
                        // While waiting for thumbnail, return existing thumbnail (if it exists) so the
                        // UI doesn't go blank while waiting.
                        return ((_a = this._thumbnailInfo) === null || _a === void 0 ? void 0 : _a.url) || '';
                    }
                },
                thumbnailStatus: {
                    get() { var _a; return (_a = this._thumbnailStatus) !== null && _a !== void 0 ? _a : RenditionStatus.NotStarted; }
                },
                getRenditionURL() {
                    oneTimeWarning('getRenditionURL', 'SAM.cpp does not support all options of getRenditionURL. Clients that run on web'
                        + ' and desktop should use AdobeAsset.thumbnailURL instead.');
                    return this.thumbnailURL;
                },
                renditionURLStatus() { return this.thumbnailStatus; },
                clearRenditionError() {
                    if (this._thumbnailStatus === RenditionStatus.Error) {
                        this._thumbnailStatus = RenditionStatus.NotStarted;
                        this._thumbnailInfo = undefined;
                        this._refreshThumbnail = true;
                        this.dispatch(SharedThumbnailChangeEvent);
                    }
                },
                // SAM.cpp fires Asset events via AssetContext, but SAM.js emits them from AdobeAsset
                _onAssetChanged() {
                    this.dispatch(SharedChangeEvent);
                },
                _onRenditionChanged() {
                    this._refreshThumbnail = true;
                    // Raise event so only clients that are observing will refresh the thumbnail
                    this.dispatch(SharedThumbnailChangeEvent);
                },
                transferToSpace(target) {
                    return this._core.hostAssetContext.transfer(this, target);
                },
                // SAM.cpp doesn't have versions
                versions: {
                    get() {
                        if (this._assetVersionSource) {
                            return this._assetVersionSource;
                        } // memoize
                        const versionPool = new ItemPool(this._core, 'version');
                        const getAssetInterface = this._core._AssetContextJS.repoInterface.resolveById(this.id);
                        const loader = new AssetVersionLoader(versionPool, getAssetInterface);
                        this._assetVersionSource = new ItemSource(versionPool, [loader], new EverythingFilter());
                        return this._assetVersionSource;
                    }
                },
                // SAM.cpp doesn't have getCommentContext
                getCommentContext() {
                    if (!this._commentContext) {
                        // TODO: lazy-load CommentContext module
                        this._commentContext = new CommentContext(this._core, this.id);
                    }
                    return this._commentContext;
                },
                hasArtifacts: {
                    get() {
                        this._core.environment.mobx.reportObserved(this, 'change');
                        return this._hasArtifacts;
                    }
                }
            },
            // make all getter properties observable under the standard 'change' event
            defaultObservableEvent: SharedChangeEvent.type
        });
    }
}
function patchSpaceJS() {
    const acplModel = getACPLModel();
    const assetContext = acplModel.applicationAssetContext;
    // Prior to version 1524, SAM.cpp space search APIs were not working correctly
    // https://jira.corp.adobe.com/browse/SAM-1886
    // https://jira.corp.adobe.com/browse/SAM-2189
    if (isSAMCppVer('gte', '2.0.0-main.1524') && isSAMCppVer('lt', '9999.9999.9999')) {
        // Until SAM.cpp can supply everything needed from SpaceContext and Spaces UI is not
        // dependant on SAM.js internals, patching the Sources for Space contents to use SAM.cpp
        // Sources so Spaces UI can have caching/badging.
        Object.defineProperties(Space.prototype, {
            searchWorkingFiles: {
                value() {
                    if (this._searchWorkingFilesSource) {
                        return Promise.resolve(this._searchWorkingFilesSource);
                    }
                    this._searchWorkingFilesSource = new Promise((resolve, reject) => {
                        assetContext.spaceContext.lookupSpaceById(this.id)
                            .then((space) => {
                            // `space` is the SAM.cpp Space object
                            if (!space) {
                                reject(SAMError.create(SAMErrorCode.NOT_FOUND, `searchWorkingFiles could not find Space ${this.id}`));
                            }
                            resolve(space.searchWorkingFiles());
                        });
                    });
                    return this._searchWorkingFilesSource;
                }
            },
            searchArtifacts: {
                value(embedPermissions = false) {
                    if (this._searchArtifactsSource) {
                        return Promise.resolve(this._searchArtifactsSource);
                    }
                    this._searchArtifactsSource = new Promise((resolve, reject) => {
                        assetContext.spaceContext.lookupSpaceById(this.id)
                            .then((space) => {
                            // `space` is the SAM.cpp Space object
                            if (!space) {
                                reject(SAMError.create(SAMErrorCode.NOT_FOUND, `searchArtifacts could not find Space ${this.id}`));
                            }
                            resolve(space.searchArtifacts(embedPermissions));
                        });
                    });
                    return this._searchArtifactsSource;
                }
            }
        });
    }
    else if (isSAMCppVer('lt', '2.0.0-main.1524')) {
        // For the prior versions of SAM where we don't have space search
        // support in SAM.cpp we are using the temp code
        // to use _AssetContextJS for assetPool, USSInterface;
        // use hostAssetContext for workingFilesFolder or spaceAdobeAsset
        Object.defineProperties(Space.prototype, {
            searchWorkingFiles: {
                async value() {
                    const assetContext = this._core._AssetContextJS;
                    const { assetPool, ussInterface } = assetContext;
                    // We need to use the hostAssetContext as we need to get updates from SAM.cpp AdobeAsset so that the search
                    // filter works fine in case of updates to the space.
                    const workingFilesFolder = await this._core.hostAssetContext
                        .getAssetWithRepoPath(`${this.pathName}/working_files`, this.repositoryID);
                    const spaceSearchAssetLoader = new SpaceSearchAssetLoader(assetPool, this._core, workingFilesFolder, ussInterface, 'excludeArtifacts', true // always include permissions for 1-off invitees
                    );
                    const implicitFilter = new SpaceSearchAssetFilter({
                        asset: workingFilesFolder,
                        artifactFilter: 'excludeArtifacts'
                    });
                    return new ItemSource(assetPool, [spaceSearchAssetLoader], implicitFilter);
                }
            },
            searchArtifacts: {
                async value(embedPermissions = false) {
                    // SAM.cpp AssetContext doesn't expose assetPool/ussInterface, so need
                    // to fall back to the JS impl of AssetContext when present. This is
                    // required as SAM.cpp space search APIs are not patched for
                    // SAM versions prior to 1524
                    const assetContext = this._core._AssetContextJS;
                    const { assetPool, ussInterface } = assetContext;
                    // We need to use the hostAssetContext as we need to get updates from SAM.cpp AdobeAsset so that the search
                    // filter works fine in case of updates to the space.
                    const spaceAdobeAsset = await this._core.hostAssetContext.getAssetWithCloudID(this.id);
                    const spaceSearchAssetLoader = new SpaceSearchAssetLoader(assetPool, this._core, spaceAdobeAsset, ussInterface, 'onlyArtifacts', embedPermissions);
                    const implicitFilter = new SpaceSearchAssetFilter({
                        asset: spaceAdobeAsset,
                        artifactFilter: 'onlyArtifacts',
                        // Some artifacts may be in hidden folders
                        _includeHiddenRecursive: true
                    });
                    return new ItemSource(assetPool, [spaceSearchAssetLoader], implicitFilter);
                }
            }
        });
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
function computeGradientID(rawGradientId, languageCode) {
    return `${rawGradientId}-${languageCode}`;
}
/**
 * Takes the response from the service and computes the result
 * to add additional properties.
 *
 * @param rawGradientData Data directly from the service.
 * @param defaultGradientId The default gradient from the service.
 * @param languageCode The language code.
 * @returns
 */
function computeGradients(rawGradientData, defaultGradientId, languageCode) {
    return rawGradientData.map((gradient) => {
        return Object.assign(Object.assign({}, gradient), { computedID: computeGradientID(gradient['project:gradientId'], languageCode), 'project:gradientData': Object.assign(Object.assign({}, gradient['project:gradientData']), { isDefault: defaultGradientId === gradient['project:gradientId'], languageCode }) });
    });
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const AdobeAssetFilterName = 'AdobeAssetFilter';
/**
 * This is the only filter SAM.js client can use for filtering AdobeAsset objects, all other filters are
 * internal to SAM.js and cannot be used by clients. This filter can be used to filter AdobeAsset objects
 * by mediaType and excludeMediaType. If both mediaType and excludeMediaType are specified then only mediaType
 * will be considered for filtering. If neither mediaType nor excludeMediaType are specified then all mediaTypes
 * will be considered for filtering.
 */
class AdobeAssetFilter {
    constructor(options) {
        var _a, _b;
        this._mediaTypes = undefined;
        this._excludeMediaTypes = undefined;
        if (options === null || options === void 0 ? void 0 : options.mediaTypes) {
            this._mediaTypes = new Set(options.mediaTypes);
        }
        if (options === null || options === void 0 ? void 0 : options.excludeMediaTypes) {
            this._excludeMediaTypes = new Set(options.excludeMediaTypes);
        }
        if (((_a = options === null || options === void 0 ? void 0 : options.mediaTypes) === null || _a === void 0 ? void 0 : _a.length) && ((_b = options === null || options === void 0 ? void 0 : options.excludeMediaTypes) === null || _b === void 0 ? void 0 : _b.length)) {
            this._mediaTypes = new Set([]);
            console.warn('Both mediaTypes and excludeMediaTypes should not be specified while creating AdobeAssetFilter, setting default value for mediaTypes');
        }
    }
    get hash() {
        var _a, _b;
        let mediaTypeHashPart = 'mediatypes=' + (((_a = this._mediaTypes) === null || _a === void 0 ? void 0 : _a.size) ? `${[...this._mediaTypes].sort().join(',')}` : 'all-types');
        mediaTypeHashPart += ':excludemediatypes=' + (((_b = this._excludeMediaTypes) === null || _b === void 0 ? void 0 : _b.size) ? `${[...this._excludeMediaTypes].sort().join(',')}` : 'none');
        return mediaTypeHashPart;
    }
    get mediaTypes() {
        return this._mediaTypes;
    }
    get excludeMediaTypes() {
        return this._excludeMediaTypes;
    }
    get name() {
        return AdobeAssetFilterName;
    }
    matches(item) {
        var _a, _b;
        return (!((_a = this._excludeMediaTypes) === null || _a === void 0 ? void 0 : _a.size) || !this._excludeMediaTypes.has(item.mediaType))
            && (!((_b = this._mediaTypes) === null || _b === void 0 ? void 0 : _b.size) || this._mediaTypes.has(item.mediaType));
    }
    getRelationToFilter(other) {
        const mediaTypeRelation = this.getMediaRelationToFilter(other);
        if (mediaTypeRelation === 'disjoint') {
            return 'disjoint';
        }
        //----- All 'disjoint' cases must be handled above this line
        if (mediaTypeRelation === 'unknown') {
            // This filter shares some, but not all of the mediaTypes of other. They
            // could overlap, but can't know for sure.
            return 'unknown';
        }
        //------ All 'unknown' cases must be handled above this line
        // Since the filters can't be 'disjoint', there must be some overlap, leaving
        // only 'unknown' and 'subset' as a possibility. Since all 'unknown' cases have
        // been eliminated, the only remaining possibility is 'subset'.
        // NOTE: Returns mediaTypeRelation for verification in unit tests.
        return mediaTypeRelation;
    }
    /*
    Cases to be handled for include and exclude filter:
    ---------------------------------------------------------------------------------
    this_include | this_exclude | other_include | other_exclude | case no,
                                                                    possible values
                                                                    s=subset,
                                                                    u=unknown,
                                                                    d=disjoint
    ---------------------------------------------------------------------------------
        0               0               0               0           case 1 - s
    ---------------------------------------------------------------------------------
        0               0               0               1           case 2 - u
    ---------------------------------------------------------------------------------
        0               0               1               0           case 3 - u
    ---------------------------------------------------------------------------------
        0               0               1               1           na
    ---------------------------------------------------------------------------------
        0               1               0               0           case 4 - s
    ---------------------------------------------------------------------------------
        0               1               0               1           case 5 - s & u
    ---------------------------------------------------------------------------------
        0               1               1               0           case 6 - u & d
    ---------------------------------------------------------------------------------
        0               1               1               1           na
    ---------------------------------------------------------------------------------
        1               0               0               0           case 7 - s
    ---------------------------------------------------------------------------------
        1               0               0               1           case 8 - s, u & d
    ---------------------------------------------------------------------------------
        1               0               1               0           case 9 - s, u & d
    ---------------------------------------------------------------------------------
        1               0               1               1           na
    ---------------------------------------------------------------------------------
        1               1               0               0           na
    ---------------------------------------------------------------------------------
        1               1               0               1           na
    ---------------------------------------------------------------------------------
        1               1               1               0           na
    ---------------------------------------------------------------------------------
        1               1               1               1           na
    ---------------------------------------------------------------------------------

    There are some cases which are not applicable(na) because for now it is not allowed to specify both include
    and exclude mediatypes on a filter
    */
    getMediaRelationToFilter(other) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        let mediaTypeRelation;
        (function (mediaTypeRelation) {
            mediaTypeRelation[mediaTypeRelation["uninitialized"] = 0] = "uninitialized";
            mediaTypeRelation[mediaTypeRelation["subset"] = 1] = "subset";
            mediaTypeRelation[mediaTypeRelation["disjoint"] = 2] = "disjoint";
            mediaTypeRelation[mediaTypeRelation["intersaction"] = 3] = "intersaction";
        })(mediaTypeRelation || (mediaTypeRelation = {}));
        function getMediaTypeRelation(current, other) {
            let subset = true;
            let intersaction = false;
            for (const item of current) {
                if (other.has(item)) {
                    intersaction = true;
                }
                else {
                    subset = false;
                }
            }
            if (!intersaction) {
                return mediaTypeRelation.disjoint;
            }
            else if (subset) {
                return mediaTypeRelation.subset;
            }
            return mediaTypeRelation.intersaction;
        }
        let thisIncludeOtherInclude = mediaTypeRelation.uninitialized;
        let thisIncludeOtherExclude = mediaTypeRelation.uninitialized;
        let otherIncludeThisExclude = mediaTypeRelation.uninitialized;
        let otherExcludethisExclude = mediaTypeRelation.uninitialized;
        if (((_a = this._mediaTypes) === null || _a === void 0 ? void 0 : _a.size) && ((_b = other._mediaTypes) === null || _b === void 0 ? void 0 : _b.size)) {
            thisIncludeOtherInclude = getMediaTypeRelation(this._mediaTypes, other._mediaTypes);
        }
        if (((_c = this._mediaTypes) === null || _c === void 0 ? void 0 : _c.size) && ((_d = other._excludeMediaTypes) === null || _d === void 0 ? void 0 : _d.size)) {
            thisIncludeOtherExclude = getMediaTypeRelation(this._mediaTypes, other._excludeMediaTypes);
        }
        if (((_e = this._excludeMediaTypes) === null || _e === void 0 ? void 0 : _e.size) && ((_f = other._mediaTypes) === null || _f === void 0 ? void 0 : _f.size)) {
            otherIncludeThisExclude = getMediaTypeRelation(other._mediaTypes, this._excludeMediaTypes);
        }
        if (((_g = this._excludeMediaTypes) === null || _g === void 0 ? void 0 : _g.size) && ((_h = other._excludeMediaTypes) === null || _h === void 0 ? void 0 : _h.size)) {
            otherExcludethisExclude = getMediaTypeRelation(other._excludeMediaTypes, this._excludeMediaTypes);
        }
        // handling disjoint for case 6, 8, 9
        if (otherIncludeThisExclude === mediaTypeRelation.subset
            || thisIncludeOtherExclude === mediaTypeRelation.subset
            || thisIncludeOtherInclude === mediaTypeRelation.disjoint) {
            return 'disjoint';
        }
        // handling unknown for case 5, 6, 8, 9
        if (otherExcludethisExclude === mediaTypeRelation.intersaction
            || otherIncludeThisExclude === mediaTypeRelation.intersaction
            || thisIncludeOtherExclude === mediaTypeRelation.intersaction
            || thisIncludeOtherInclude === mediaTypeRelation.intersaction) {
            return 'unknown';
        }
        // handling for case 2, 3 - only possible value 'unknown'
        if (!((_j = this._mediaTypes) === null || _j === void 0 ? void 0 : _j.size) && !((_k = this._excludeMediaTypes) === null || _k === void 0 ? void 0 : _k.size)
            && (((_l = other._mediaTypes) === null || _l === void 0 ? void 0 : _l.size) || ((_m = other._excludeMediaTypes) === null || _m === void 0 ? void 0 : _m.size))) {
            // If the other list has mediaType/excludeMediaType and this filter doesn't then
            // this list is going to have an unknown final state.
            return 'unknown';
        }
        // we have handled all possible disjoint/unknown cases above and can safely return subset from here
        return 'subset';
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class ImplicitDirectoryListingAssetFilter {
    constructor(options) {
        const { asset, mediaTypes, excludeMediaTypes } = options, baseOpts = __rest(options, ["asset", "mediaTypes", "excludeMediaTypes"]);
        // ImplicitDirectoryListingFilter handles the ImplicitAdobeAssetFilterBase cases
        this._implicitDirectoryListingFilter = new ImplicitDirectoryListingFilter(Object.assign({ asset }, baseOpts));
        if (mediaTypes || excludeMediaTypes) {
            this._adobeAssetFilter = new AdobeAssetFilter({
                mediaTypes,
                excludeMediaTypes
            });
        }
    }
    get hash() {
        var _a, _b;
        return [
            (_a = this._implicitDirectoryListingFilter) === null || _a === void 0 ? void 0 : _a.hash,
            (_b = this._adobeAssetFilter) === null || _b === void 0 ? void 0 : _b.hash
        ].filter(hash => !!hash).join(':');
    }
    getRelationToFilter(other) {
        if (this._adobeAssetFilter && other._adobeAssetFilter && this._adobeAssetFilter.getRelationToFilter(other._adobeAssetFilter) === 'disjoint') {
            return 'disjoint';
        }
        if (this._implicitDirectoryListingFilter && other._implicitDirectoryListingFilter && this._implicitDirectoryListingFilter.getRelationToFilter(other._implicitDirectoryListingFilter) === 'disjoint') {
            return 'disjoint';
        }
        if (this._adobeAssetFilter && other._adobeAssetFilter && this._adobeAssetFilter.getRelationToFilter(other._adobeAssetFilter) === 'unknown') {
            return 'unknown';
        }
        if (this._implicitDirectoryListingFilter && other._implicitDirectoryListingFilter && this._implicitDirectoryListingFilter.getRelationToFilter(other._implicitDirectoryListingFilter) === 'unknown') {
            return 'unknown';
        }
        return 'subset';
    }
    matches(item) {
        if (this._adobeAssetFilter && !this._adobeAssetFilter.matches(item)) {
            return false;
        }
        if (this._implicitDirectoryListingFilter && !this._implicitDirectoryListingFilter.matches(item)) {
            return false;
        }
        // it matched both filters above
        return true;
    }
    get mediaTypes() {
        var _a;
        return (_a = this._adobeAssetFilter) === null || _a === void 0 ? void 0 : _a.mediaTypes;
    }
}

const $LAZY_LOAD_MANAGERS = Symbol('$LAZY_LOAD_MANAGERS');
var LoadStatus;
(function (LoadStatus) {
    LoadStatus[LoadStatus["NotLoaded"] = 1] = "NotLoaded";
    LoadStatus[LoadStatus["Pending"] = 2] = "Pending";
    LoadStatus[LoadStatus["Loading"] = 3] = "Loading";
    LoadStatus[LoadStatus["Loaded"] = 4] = "Loaded";
    LoadStatus[LoadStatus["Error"] = 5] = "Error";
})(LoadStatus || (LoadStatus = {}));
const MAX_RETRIES = 5;
const FETCH_START_DELAY_MS = 100;
/**
 * For managing lazy loading of data throughout SAM.js in a consistent manner. This involves
 *
 * 1. A new custom event for the data that is lazily fetched should be created. While it can work with the default
 *    'change' event, using a custom event will allow SAM.js to track what lazy-loaded data is actively being
 *    monitored by clients so that it only fetches when necessary.
 * 2. The class that has lazy-loaded data should call static method LazyLoadManager.manageLazyLoading() to instantiate
 *    the LazyLoadManager(s) and annotate the getters for the lazy-loaded data with @lazyLoaded
 *
 * @package -- only for use in SAM.js
 */
class LazyLoadManager {
    constructor(_target, event, _fetchData, _prefetchTimeout = FETCH_START_DELAY_MS, _retryErrorTimeout = Number.POSITIVE_INFINITY, _cancelFetchData) {
        this._target = _target;
        this.event = event;
        this._fetchData = _fetchData;
        this._prefetchTimeout = _prefetchTimeout;
        this._retryErrorTimeout = _retryErrorTimeout;
        this._cancelFetchData = _cancelFetchData;
        this._loadStatus = LoadStatus.NotLoaded;
        this._retriesRemaining = MAX_RETRIES;
        this._onBecomeUnobserved = (eventName) => {
            if (eventName !== this.event.type) {
                return;
            }
            if (this._loadStatus === LoadStatus.Pending) {
                this._prefetchResolve(true);
                return;
            }
            // Only cancel fetching if it is loading and there is a way to do it
            if (this._loadStatus === LoadStatus.Loading && this._cancelFetchData) {
                this._cancelFetchData.call(this._target);
                this.setLoadStatus(LoadStatus.NotLoaded);
            }
        };
        this._target.on(BECOME_UNOBSERVED, this._onBecomeUnobserved);
    }
    /**
     * Ideal way for client classes to instantiate LazyLoadManager(s)
     *
     * @param target {ItemBase} the class instance that has lazy-loaded data to manage
     * @param configs {ILazyLoadConfig[]} the config for each LazyLoadManager
     */
    static manageLazyLoading(target, configs) {
        const instance = target;
        instance[$LAZY_LOAD_MANAGERS] = instance[$LAZY_LOAD_MANAGERS] || new Map();
        configs.forEach(({ event, fetchData, prefetchTimeout, retryErrorTimeout, cancelFetchData }) => {
            instance[$LAZY_LOAD_MANAGERS].set(event, new LazyLoadManager(instance, event, fetchData, prefetchTimeout, retryErrorTimeout, cancelFetchData));
        });
    }
    /**
     * Primarily for internal use/testing. Finds the relevant LazyLoadManager for a specific event (by name or
     * by shared SAMEvent instance).
     *
     * @param target {ItemBase} the class instance that has lazy-loaded data to manage
     * @param event {IEvent|string} the SAMEvent instance or name
     */
    static getManager(target, event) {
        const map = target[$LAZY_LOAD_MANAGERS];
        if (!map) {
            return;
        }
        let foundEvent;
        if (typeof event === 'string') {
            foundEvent = [...map.keys()].find(e => e.type === event);
        }
        else {
            foundEvent = event;
        }
        return map.get(foundEvent);
    }
    /**
     * Used by code outside the _fetchData method that needs to modify the loadStatus.
     *
     * @param target {ItemBase} the target instance that has lazy-loaded data
     * @param eventName {string} the event name tied to the lazy-loaded data
     * @param loadStatus {LoadStatus}
     */
    static setLoadStatus(target, eventName, loadStatus) {
        const manager = LazyLoadManager.getManager(target, eventName);
        if (!manager) {
            throw new Error(`Target does not have a LazyLoadManager for event ${eventName}`);
        }
        manager.setLoadStatus(loadStatus);
    }
    /**
     * For cases where code modifies the lazy-load data outside of fetching.
     *
     * @param target {ItemBase} the target instance that has lazy-loaded data
     * @param eventName {string} the event name tied to the lazy-loaded data
     */
    static updatedExternally(target, eventName) {
        const manager = LazyLoadManager.getManager(target, eventName);
        manager.updatedExternally();
    }
    get loadStatus() { return this._loadStatus; }
    toString() {
        return `${this.event.type}: ${LoadStatus[this.loadStatus]}`;
    }
    async _prefetchDelay() {
        return new Promise((resolve) => {
            this._prefetchResolve = resolve;
            this.setLoadStatus(LoadStatus.Pending);
            setTimeout(() => {
                resolve(false);
                this._prefetchResolve = undefined;
            }, this._prefetchTimeout);
        });
    }
    async onPropertyAccess() {
        if (this._loadStatus === LoadStatus.NotLoaded) {
            if (this._prefetchTimeout) {
                const cancelled = await this._prefetchDelay();
                if (cancelled) {
                    this.setLoadStatus(LoadStatus.NotLoaded);
                    return;
                }
            }
            try {
                this.setLoadStatus(LoadStatus.Loading);
                // Note: uses `call` for better memory footprint. That way, the _fetchData implementation can be a
                // single instance of a function on the class prototype, rather than a method bound (via `=>` or
                // `.bind()`) on each instance of the class.
                await this._fetchData.call(this._target);
                // Don't transition to Loaded if it was cancelled
                // @ts-ignore -- TS doesn't understand that _loadStatus has changed
                if (this._loadStatus === LoadStatus.Loading) {
                    this.setLoadStatus(LoadStatus.Loaded);
                }
            }
            catch (e) {
                this.setLoadStatus(LoadStatus.Error);
            }
        }
    }
    setLoadStatus(loadStatus) {
        if (this._loadStatus === loadStatus) {
            return;
        }
        this._loadStatus = loadStatus;
        // Transitioning to NotLoaded means the data still needs to be loaded, so listeners should be notified so
        // they can trigger lazy-loading if they want.
        if (loadStatus === LoadStatus.NotLoaded || loadStatus === LoadStatus.Loaded) {
            // Reset remaining retries on successful load
            if (loadStatus === LoadStatus.Loaded) {
                this._retriesRemaining = MAX_RETRIES;
            }
            this._target.dispatch(this.event);
        }
        else if (loadStatus === LoadStatus.Error
            && this._retryErrorTimeout < Number.POSITIVE_INFINITY
            && this._retriesRemaining > 0) {
            // If retry-able, wait the designated time and then set back to NotLoaded, which will notify clients
            // so they can kick-off lazy loading again if desired
            setTimeout(() => {
                this._retriesRemaining--;
                if (this._loadStatus === LoadStatus.Error) {
                    this.setLoadStatus(LoadStatus.NotLoaded);
                }
            }, this._retryErrorTimeout);
        }
    }
    /**
     * For cases when something updates lazy-loaded data outside of a lazy-load fetch (such as mutating the value of a
     * field as part of a service operation), where the event must be fired and the loadStatus should be set to Loaded
     */
    updatedExternally() {
        if (this._loadStatus !== LoadStatus.Loaded) {
            // setLoadStatus will fire the event
            this.setLoadStatus(LoadStatus.Loaded);
        }
        else {
            this._target.dispatch(this.event);
        }
    }
}
/**
 * Annotation for marking a getter as the entry point for clients to access lazy-loaded fields, which
 * makes them observed and starts lazy-loading if needed.
 *
 * @param event {IEvent} the event instance that will be fired when the lazy-loaded data changes
 * @param privatePropNames {string[]} an array of the private property names that the annotated getter references.
 *        When ItemUpdater modifies one of these private fields, it will fire the event specified, rather than the
 *        default event for the ItemUpdater.
 */
function lazyLoaded(event, privatePropNames) {
    return function (target, key, descriptor) {
        if (descriptor.get) {
            const getter = descriptor.get;
            descriptor.get = function () {
                const manager = LazyLoadManager.getManager(this, event);
                if (!manager) {
                    throw new Error(`@lazyLoaded target is missing LazyLoadManager for event ${event.type}. Use`
                        + ' LazyLoadManager.manageLazyLoading() in constructor to configure');
                }
                manager.onPropertyAccess();
                return getter.call(this);
            };
        }
        else {
            // TODO: implement for methods when we need it
            throw new Error('@lazyLoaded only works with getters (TODO: implement for methods)');
        }
        // layer @observabile on top of @lazyLoaded
        privatePropNames = [].concat(privatePropNames);
        observableImpl(event, privatePropNames)(target, key, descriptor);
    };
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class Project extends AdobeAsset {
    constructor(_core, id, metadata) {
        super(_core, id, metadata);
        this._core = _core;
        this._description = '';
        // We have separate permissions for a Project and AdobeAsset.
        // AdobeAsset permissions for a project are the permissions on the folder (i.e. representing the project)
        // ESM users might not have permission on the parent folder for Projects, so the permissions cannot be derived
        // from the parent folder like normal folders.
        this._projectPermissions = Permission.All;
        this._assetClass = 'directory';
        this._projectInterface = new ProjectsInterface(this._core);
        this.isProject = true;
        this._repositoryOwner = { id: '', type: 'user' };
        /**
         * Determines if complete repo metadata for AdobeAsset has been loaded or not. For now, Project
         * service doesn't return all repo metadata, which is fine as long as clients don't need it to use this
         * Project class. But if they call `getAssetWithCloudID()`, they might need the rest of the data.
         * @package -- only for use in SAM.js
         */
        this._needsRepoMetadata = true;
        this._projectLinks = metadata === null || metadata === void 0 ? void 0 : metadata.projectLinks;
        LazyLoadManager.manageLazyLoading(this, [{
                event: SharedChangeEvent,
                fetchData: this._getGradient
            }]);
    }
    get gradientID() {
        var _a;
        return (_a = this._gradientID) !== null && _a !== void 0 ? _a : '';
    }
    /**
     * Lazy loads and returns the Gradient belonging to this
     * Project.
     *
     * @param languageCode
     * @returns
     */
    get gradient() {
        return this._gradient;
    }
    async _getGradient() {
        this._gradient = await this._core.projectContext.getGradient(this.gradientID);
    }
    /**
     * @TODO@PROJECT
     * @experimental API may change.
     */
    get description() {
        oneTimeWarning('Description Not yet Supported', 'Currently the description field is not supported by the service.');
        return this._description;
    }
    /**
     * @param repoMetadata {RepositoryMetadata} If passed in, it will be used instead of loading from the service
     * @param appMetadata {any} If passed in, it will be used instead of loading from the service
     *
     * @package -- only for use in SAM.js
     */
    async _load({ repoMetadata, appMetadata, effectivePermission, projectMetadata } = {}) {
        // First load the AdobeAsset so that once we get the project metadata we override any relevant
        // properties with the project metadata. One example of such field is createdBy. Etag is another one.
        // We need to take care of permissions. We will get the permissions from the project service
        // and we should handle them here as they are extended from RAPI effective permissions.
        // https://jira.corp.adobe.com/browse/SAM-3086
        // Also we need update the load method so that it does not load the permissions for a project
        // We can probably govern that with the help of an argument to the load method.
        await super._load({ repoMetadata, appMetadata, effectivePermission });
        // now that we have the AdobeAsset loaded, we can reset the _needsRepoMetadata flag
        this._needsRepoMetadata = false;
        // If we have the project metadata, then we will use that to update the project
        if (!projectMetadata) {
            // load the project metadata using the project service
            projectMetadata = await this._projectInterface.getProject(this.id);
        }
        const session = this._core.projectContext.assetPool.createMutationSession();
        this._core.projectContext._projectUpdater.updateRemoteItem(session, projectMetadata);
        session.commit();
    }
    // @TODO@PROJECT We should implement something similar to _ensureLink here.
    get projectLinks() {
        return this._projectLinks;
    }
    get creator() {
        return this._creator;
    }
    /**
     * The view that this project belongs to.
     *
     */
    get view() {
        return this._view;
    }
    async move(destFolder) {
        oneTimeWarning('[MOVE_UNSUPPORTED]', `Move on a project is not supported ${destFolder}`);
        return;
    }
    async rename(newName) {
        var _a;
        if (!((_a = this._projectLinks) === null || _a === void 0 ? void 0 : _a[RelType.PROJECTS_RENAME])) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Rename link is missing for this project.');
        }
        if (!validatePlatformRepoName(newName)) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Invalid name provided for asset rename operation.');
        }
        await this._projectInterface.renameProject(newName, this._projectLinks);
        const projectPool = this._core.projectContext.assetPool;
        // Update the repo:name and repo:path after a rename
        const session = projectPool.createUpdateSession();
        const newPath = renameFileInPath(newName, this);
        updateCachedDescendents({
            assetPool: this._core.hostAssetContext.assetPool,
            asset: this,
            session,
            operation: { type: 'rename', newPath }
        });
        this._core.projectContext._projectUpdater.updateLocalItem(session, this, { 'repo:name': newName, 'repo:path': newPath });
        session.commit();
    }
    /**
     * Discards a project through the Projects service instead of RAPI.
     *
     */
    async discard() {
        if (this.isDiscarded) {
            throw SAMError.create(SAMErrorCode.INVALID_OPERATION, 'Asset is already discarded');
        }
        await this._ensureLink(RelType.DISCARD);
        await this._projectInterface.discardProject(this.id, this._projectLinks);
        const projectPool = this._core.projectContext.assetPool;
        const session = projectPool.createUpdateSession();
        updateCachedDescendents({
            assetPool: this._core.hostAssetContext.assetPool,
            asset: this,
            session,
            operation: { type: 'discard' }
        });
        this._core.projectContext._projectUpdater.updateLocalItem(session, this, { 'repo:state': 'DISCARDED' });
        session.commit();
    }
    /**
     * Restores a project through the Projects service instead of RAPI.
     *
     */
    async restore() {
        if (!this.isDiscarded) {
            throw SAMError.create(SAMErrorCode.INVALID_OPERATION, 'Cannot restore an asset that is not discarded');
        }
        await this._ensureLink(RelType.RESTORE);
        await this._projectInterface.restoreProject(this.id, this._projectLinks);
        const projectPool = this._core.projectContext.assetPool;
        const session = projectPool.createUpdateSession();
        updateCachedDescendents({
            assetPool: this._core.hostAssetContext.assetPool,
            asset: this,
            session,
            operation: { type: 'restore' }
        });
        this._core.projectContext._projectUpdater.updateLocalItem(session, this, { 'repo:state': 'ACTIVE' });
        session.commit();
    }
    /**
     * @package
     **/
    async _updateProjectLinksFromService() {
        // load the project links using the project service
        const returnProject = await this._projectInterface.getProject(this.id);
        const { _links } = returnProject;
        const projectPool = this._core.projectContext.assetPool;
        const session = projectPool.createUpdateSession();
        this._core.projectContext._projectUpdater.updateLocalItem(session, this, { _links });
        session.commit();
    }
    /**
     * @package
     **/
    _isProjectLinkType(relType) {
        return _projectLinkKeys().includes(relType);
    }
    async _ensureLink(relType) {
        var _a;
        // if the link is in _projectLinks, copy the _projectLink into _links
        // relType is an AssetsLink index, it might be a link that isn't in IProjectLinks
        if (this._isProjectLinkType(relType)) {
            if (!((_a = this._projectLinks) === null || _a === void 0 ? void 0 : _a[relType])) {
                await this._updateProjectLinksFromService();
            }
            const links = Object.assign(Object.assign({}, this.__links), { [relType]: this._projectLinks[relType] });
            //using the AssetUpdater instead of ProjectUpdater because ProjectUpdater has no way to update _links
            const session = this._core.hostAssetContext.assetPool.createUpdateSession();
            this._core.hostAssetContext._adobeAssetUpdater.updateLocalItem(session, this, { '_links': links });
            session.commit();
            return;
        }
        //call out to fetch the missing link
        this._core.environment.debug(`ProjectLinkMissing ${relType} Link ${relType} missing on project, called out fetch it`);
        await super._ensureLink(relType);
    }
    /**
     * Validates whether this project can be renamed to the provided name
     * SAM will take in the name from the client and substitute this in the repo:path
     * of the project to get an absolute path. Project service would need the absolute
     * path to disambiguate between projects for accounts that contain projects in both
     * shared storage (/content/assets) and user storage (/projects). Right now both these
     * belong to the same repository and once we have the unified storage model they will
     * have separate repository ids and we will be able to identify them using repository id.
     * @param name name of the project to be validated for rename.
     */
    async validateProjectNameForRename(name) {
        if (name.toLowerCase() === this.name.toLowerCase()) {
            return true;
        }
        return await this._core.projectContext.validateProjectNameForCreate(name, this);
    }
    /**
     * @experimental API May change.
     * @deprecated - to be removed in a future major version on or after 2024-10-01. SAM-3333
     */
    async updateProperties( /* properties: IProjectProperties */) { }
    /**
     * Returns an enumeration with a more precise reason for why the user
     * can or cannot leave the Project. It may also return PENDING if we are still
     * loading collaborator information.
     *
     * @returns {ProjectLeaveReason} An enumeration of reasons why the user can or cannot leave.
     */
    leaveStatus() {
        // Check if we have collaborators fetched.
        // If not, we queue them up.
        // Make all the above checks and then return the result.
        const collaborators = this.collaboratorArray;
        // If there are no collaborators and it's marked dirty, that
        // means that we still need to fetch collaborators and we
        // can't continue to make the require determination.
        if (collaborators.length === 0 && this._collaboratorDataIsDirty) {
            // initiate the request to get collaborators.
            this._queueCollaborators();
            return ProjectLeaveReason.PENDING;
        }
        // If there are no collaborators returned, they can't leave anyway
        if (collaborators.length === 0) {
            return ProjectLeaveReason.CANNOT_LEAVE_NO_COLLABORATORS;
        }
        const currentUserCollaborator = collaborators.find((c) => c.isCurrentUser);
        // If the user doesn't exist in the collaborators list
        // this user has inherited access to this project and thus they can't
        // leave and we should return the address book group member error response.
        if (!currentUserCollaborator) {
            return ProjectLeaveReason.CANNOT_LEAVE_ADDRESS_BOOK_GROUP_MEMBER;
        }
        if (currentUserCollaborator.role === 'owner') {
            return ProjectLeaveReason.CANNOT_LEAVE_IS_OWNER;
        }
        // Admins of the org have access to all Projects. But sometimes, they can be directly invited by other members
        // to the Project, which in this case, they are allowed to leave the Project as direct collaborator.
        // This only removes their entry in the `directCollaborators` list, but won't change the permission as admin
        // still have access to all content. Without direct invite permission, on leave, we show toast message such
        // that admin can't leave Project.
        if (!currentUserCollaborator.isDirect && currentUserCollaborator.isAdmin) {
            return ProjectLeaveReason.CANNOT_LEAVE_IS_ADMIN_OF_PROJECT;
        }
        // At this point if they are the only collaborator then
        // they can't leave, this case happens when a CCI user creates
        // a Project and they are the only collaborator direct or not.
        if (collaborators.length === 1) {
            return ProjectLeaveReason.CANNOT_LEAVE_LAST_COLLABORATOR;
        }
        // User cannot leave if they are no more collaborators who can share.
        const anotherDirectCollabWithSharePermissions = collaborators.find((c) => c.canShare && c.isDirect && c !== currentUserCollaborator);
        // We also need to distinguish between whether it is a USM project or shared-storage project.
        // In shared-storage projects, the user can't leave if they are the last direct collaborator with share permissions.
        // In user storage projects, the user can leave as long as she is not the owner of the project.
        if (!anotherDirectCollabWithSharePermissions && this.storageAssignee.type !== 'user') {
            return ProjectLeaveReason.CANNOT_LEAVE_LAST_DIRECT_COLLAB_WITH_SHARE_PERMISSION;
        }
        // If the user is not a direct collaborator and is also
        // not and admin, they can't leave due to inheriting
        // access to this project and not getting directly invited.
        if (!currentUserCollaborator.isDirect) {
            return ProjectLeaveReason.CANNOT_LEAVE_ADDRESS_BOOK_GROUP_MEMBER;
        }
        return ProjectLeaveReason.CAN_LEAVE;
    }
    /**
     * Returns a boolean based on the userCanLeaveReason() response above.
     *
     * @deprecated -- Use {@link Project.leaveStatus instead} -- will be removed on or after 2024-10-01
     *
     * @returns Undefined while fetching collaborator data.
     */
    userCanLeaveProject() {
        oneTimeWarning('userCanLeaveProject Deprecated', 'Use Project.leaveStatus instead.'
            + ' This will be removed on or after 2024-10-01.');
        const reason = this.leaveStatus();
        switch (reason) {
            case ProjectLeaveReason.PENDING:
                return;
            case ProjectLeaveReason.CAN_LEAVE:
                return true;
            case ProjectLeaveReason.CANNOT_LEAVE_NO_COLLABORATORS:
            case ProjectLeaveReason.CANNOT_LEAVE_ADDRESS_BOOK_GROUP_MEMBER:
            case ProjectLeaveReason.CANNOT_LEAVE_LAST_COLLABORATOR:
            case ProjectLeaveReason.CANNOT_LEAVE_LAST_DIRECT_COLLAB_WITH_SHARE_PERMISSION:
            case ProjectLeaveReason.CANNOT_LEAVE_IS_ADMIN_OF_PROJECT:
            case ProjectLeaveReason.CANNOT_LEAVE_IS_OWNER:
                return false;
        }
    }
    hasPermission(...permissions) {
        return permissions.every((permission) => (this._projectPermissions & permission) === permission);
    }
    /**
     * Updates project data like gradientID
     *
     * @param data Data to be updated on a project.
     */
    async updateProject(data) {
        await this._core.projectContext.updateProject(this.id, data);
    }
    /**
     * @package For SAM.js internal use only
     * checks and sets the permission to restore the project
     */
    _applyRestorePermission(updateSession) {
        // We don't get the project permissions from USS right now so we try to infer the restore permissions
        // A user has restore permissions if she can create a project in the repository.
        const canRestore = this._core.projectContext._getCachedRestoreCapability(this.repositoryID);
        canRestore ? (this._projectPermissions |= Permission.Restore) : (this._projectPermissions &= ~Permission.Restore);
        this._core.projectContext._projectUpdater.updateLocalItem(updateSession, this, { projectPermissions: this._projectPermissions });
    }
    /**
    * @package -- only for use in SAM.js
    */
    // TODO: SAM-3499 will allow us to make this more efficient.
    async _reloadPermissions() {
        await this._load();
        super._reloadPermissions();
    }
    get librariesSource() {
        const directoryLoader = new AdobeAssetDirectoryLoader(this._core.hostAssetContext.assetPool, this, this._core, true, true);
        const implicitFilter = new ImplicitDirectoryListingAssetFilter({
            asset: this,
            mediaTypes: [libraryMediaTypeString],
            // Any Asset in a Project can also be SWY
            _isSharedWithYou: true
        });
        return new ItemSource(this._core.hostAssetContext.assetPool, [directoryLoader], implicitFilter);
    }
    get repositoryOwner() {
        return this._repositoryOwner;
    }
}
__decorate([
    observable
], Project.prototype, "gradientID", null);
__decorate([
    lazyLoaded(SharedChangeEvent, '_gradient')
], Project.prototype, "gradient", null);
__decorate([
    observable
], Project.prototype, "description", null);
__decorate([
    observable
], Project.prototype, "hasPermission", null);
__decorate([
    memoize
], Project.prototype, "librariesSource", null);
__decorate([
    observable
], Project.prototype, "repositoryOwner", null);
function getView(parsedSource) {
    switch (parsedSource['project:view']) {
        case 'sharedWithYou':
            return ProjectViews.SWY;
        case 'yours':
            return ProjectViews.Yours;
        default:
            return;
    }
}
function getPermission(source) {
    var _a;
    // From Project listing
    if ((_a = source._embedded) === null || _a === void 0 ? void 0 : _a[RelType.AC_EFFECTIVE]) {
        return parseProjectEffectivePermissions(source._embedded[RelType.AC_EFFECTIVE]);
    }
    // If the permissions are not present in the metadata then maintain the current value
    return NotFound;
}
const ProjectUpdater = new ItemUpdater({
    localItemType: Project,
    remoteIdProperty: 'repo:assetId',
    propertyUpdater: new PropertyUpdater({
        '_repositoryID': 'repo:repositoryId',
        '_path': 'repo:path',
        '_etag': 'repo:etag',
        '_view': { name: 'project:view', get: getView, format: 'number' },
        '_created': { name: 'repo:createDate', format: 'date' },
        '_description': 'description',
        '_name': 'repo:name',
        '_assetState': { name: 'repo:state', choices: AssetStateChoices },
        '_storageAssignee': { name: 'storage:assignee', format: StorageAssigneeFormat },
        '_projectLinks': { name: '_links', format: 'object' },
        '_creator': { name: `_embedded[${RelType.PROJECTS_CREATOR}]`, format: 'object' },
        '_createdBy': `_embedded[${RelType.PROJECTS_CREATOR}].id`,
        /**
         * @note Adding single quotes to ['project:rendition'] causes the property
         * name to be computed as ''project:rendition'' with two single quotes.
         * This obviously will cause the property updater to not find the requested property and
         * not update it so the property naming convention is [project:rendition]
         * without single quotes around the name.
         */
        '_gradientID': {
            name: `_embedded[${RelType.APP_METADATA}][project:rendition].gradientId`,
            set: (gradientID, dest) => {
                const project = dest;
                if (!gradientID) {
                    project['_gradientID'] = '';
                    return;
                }
                const computedGradientID = computeGradientID(gradientID, project._core.environment.getValue('LOCALE'));
                if (project['_gradientID'] !== computedGradientID) {
                    project['_gradientID'] = computedGradientID;
                    LazyLoadManager.setLoadStatus(project, CHANGE, LoadStatus.NotLoaded);
                }
            }
        },
        '_projectPermissions': { get: getPermission, format: 'number' },
        '_repositoryOwner': { name: `_embedded[${RelType.REPOSITORY}][repo:owner]`, format: StorageAssigneeFormat },
    })
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const OrderFieldToIProjectsOrderBy = {
    'name': 'repo:name',
    'created': 'repo:createDate',
};
const OrderFieldToComparator$9 = {
    name(a, b) {
        return compareStrings(a.name, b.name);
    },
    created(a, b) {
        return compareValues(a.created, b.created);
    }
};
class ProjectOrder extends createItemOrderFactory(OrderFieldToComparator$9) {
}
ProjectOrder.default = new ProjectOrder('ascending', 'name');

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class ProjectsListLoader {
    constructor(_projectPool, _projectInterface, _projectUpdater) {
        this._projectPool = _projectPool;
        this._projectInterface = _projectInterface;
        this._projectUpdater = _projectUpdater;
    }
    supportsFilter(filter) {
        return filter !== undefined;
    }
    async loadPage(options) {
        return await this._loadPage(options);
    }
    async _loadPage(options, pageLink) {
        const { order, limit, filter } = options;
        // We will ignore the update if we had local changes sooner than this timestamp.
        // This default latency is set to 3 seconds right now. We should revisit this value.
        const latency = this._projectPool._core.environment.getValue('PROJECTS_LIST_UPDATE_LATENCY');
        const mostRecentValidMutation = Date.now() - latency;
        let result;
        if (pageLink) {
            // We are loading the next page with the link provided.
            result = await this._projectInterface.getProjectsWithLink(pageLink);
        }
        else {
            const projectFilter = filter;
            const view = (projectFilter === null || projectFilter === void 0 ? void 0 : projectFilter.view) ? { view: projectEnumToString(projectFilter.view) } : {};
            const repositoryId = (projectFilter === null || projectFilter === void 0 ? void 0 : projectFilter.repositoryID) ? { repositoryId: projectFilter.repositoryID } : {};
            result = await this._projectInterface.getProjects(Object.assign(Object.assign({ orderBy: OrderFieldToIProjectsOrderBy[order.field], orderDirection: order.direction, limit }, view), repositoryId));
        }
        const session = this._projectPool.createLoaderUpdateSession(mostRecentValidMutation);
        const items = this._projectUpdater.updateRemoteItems(session, result.children);
        session.commit();
        // Check if we have another page to load.
        let loadNextPage;
        const nextPageLink = result._links.next;
        if (nextPageLink) {
            loadNextPage = (options) => this._loadPage(options, nextPageLink);
        }
        return {
            items,
            total: result._page.total,
            loadNextPage,
            mostRecentValidMutation
        };
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class ProjectFilter {
    constructor(options) {
        this._isDiscarded = false;
        this._view = ProjectViews.All;
        const { _isDiscarded, view, repositoryID } = options !== null && options !== void 0 ? options : {};
        this._isDiscarded = !!(_isDiscarded);
        if (view !== undefined) {
            this._view = view;
        }
        if (repositoryID) {
            this._repositoryID = repositoryID;
        }
    }
    get hash() {
        return `discarded=${this._isDiscarded}view=${this._view}repositoryId=${this._repositoryID}`;
    }
    get view() {
        return this._view;
    }
    get repositoryID() {
        return this._repositoryID;
    }
    matches(item) {
        if (!item.isProject) {
            return false;
        }
        if (item.isDiscarded !== this._isDiscarded) {
            return false;
        }
        // If the repositoryId exists on this filter and it doesn't
        // equal the item in question then return false.
        if (this._repositoryID && this._repositoryID !== item.repositoryID) {
            return false;
        }
        // Admin is a superset of all values so if it's an
        // admin view, then just return true because it's supposed
        // to match every possible view case.
        const isAdmin = this._view === ProjectViews.Admin;
        if (isAdmin) {
            return true;
        }
        // Since we know it's not admin, it could be all
        // and so we do our normal check here.
        const isAll = this._view === ProjectViews.All;
        if (!isAll && this._view && this._view !== item.view) {
            return false;
        }
        return true;
    }
    /**
     * This method determines the commonality between the already present filter (other)
     * and the new filter to be applied (this). The point of this is to
     * see if the new filter (this) can build some, all, or none of it's items
     * from (other) filter.
     *
     * Subset: Means that ALL items for this filter can be built from other
     * Disjoint: No items for this filter can be built from other.
     * Unknown: Some items for this filter can be built from other.
     *
     * @note we should always determine disjoint cases first before other cases.
     * @note this = NEW FILTER, other = EXISTING FILTER
     *
     * @param other The existing filter that we are getting the relation to.
     * @returns { ItemFilterRelation }
     */
    getRelationToFilter(other) {
        if (this._repositoryID !== other.repositoryID) {
            return 'disjoint';
        }
        if (this._isDiscarded !== other._isDiscarded) {
            return 'disjoint';
        }
        // The only case where the filters are disjoint is when the views are SWY and Yours.
        if (this._view !== other.view && [ProjectViews.SWY, ProjectViews.Yours].includes(this._view)
            && [ProjectViews.SWY, ProjectViews.Yours].includes(other.view)) {
            return 'disjoint';
        }
        // If the views are the same, then we can consider this a subset.
        if (this._view === other.view) {
            return 'subset';
        }
        // We always know that if that admin view is the the superset of everything.
        // So if the other view is admin, then we can consider this a subset.
        if (other.view === ProjectViews.Admin) {
            return 'subset';
        }
        // All the views are a subset of the all view except for admin.
        // For admin, it is unknown
        if (other.view === ProjectViews.All && this.view !== ProjectViews.Admin) {
            return 'subset';
        }
        // All the other cases would be unknown.
        return 'unknown';
    }
}
ProjectFilter.YourProjects = new ProjectFilter({
    view: ProjectViews.Yours
});
ProjectFilter.AllProjects = new ProjectFilter({
    view: ProjectViews.All
});
ProjectFilter.SWYProjects = new ProjectFilter({
    view: ProjectViews.SWY
});
ProjectFilter.AdminProjects = new ProjectFilter({
    view: ProjectViews.Admin
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class Gradient extends ItemBase {
    constructor() {
        super(...arguments);
        this._isDefault = false;
        this._type = '';
        this._angle = 0;
        this._stops = [];
        this._name = '';
        this._languageCode = '';
        this._gradientID = '';
        this._textAlpha = 1;
        this._textMode = 'RGB';
        this._textColorValue = { r: 0, g: 0, b: 0 };
        this._textType = 'process';
    }
    get isDefault() {
        return this._isDefault;
    }
    get type() {
        return this._type;
    }
    get angle() {
        return this._angle;
    }
    get stops() {
        return this._stops;
    }
    get name() {
        return this._name;
    }
    get languageCode() {
        return this._languageCode;
    }
    /**
     * The original non-computed gradient ID to be used when
     * assigning a gradient to a Project.
     */
    get gradientID() {
        return this._gradientID;
    }
    get textAlpha() {
        return this._textAlpha;
    }
    get textMode() {
        return this._textMode;
    }
    get textColorValue() {
        return this._textColorValue;
    }
    get textType() {
        return this._textType;
    }
}
const GradientUpdater = new ItemUpdater({
    localItemType: Gradient,
    /**
     * The computedID is property that is computed based on the
     * gradientId from the service, and the language code from the service.
     * It typically looks something like. 'FUSCHIA-en-US'. This helps
     * us store and retrieve the gradients by not just id but also language to
     * make it more performant if the user switches languages during runtime.
     * IGradientComputedItemResponse extends IGradientItemResponse to include
     * the computedID property.
     */
    remoteIdProperty: 'computedID',
    propertyUpdater: new PropertyUpdater({
        '_isDefault': { name: 'project:gradientData.isDefault', format: 'boolean' },
        '_type': { name: 'project:gradientData.type', format: 'string' },
        '_angle': { name: 'project:gradientData.angle', format: 'string' },
        '_name': { name: 'project:gradientName', format: 'string' },
        '_stops': { name: 'project:gradientData.stops', format: 'object' },
        '_languageCode': { name: 'project:gradientData.languageCode', format: 'string' },
        '_gradientID': 'project:gradientId',
        '_textAlpha': { name: 'project:gradientTextColor.alpha', format: 'number' },
        '_textMode': { name: 'project:gradientTextColor.mode', format: 'string' },
        '_textColorValue': { name: 'project:gradientTextColor.value', format: 'object' },
        '_textType': { name: 'project:gradientTextColor.type', format: 'string' }
    })
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
/**
 * Provides all Project related functionality like getting
 * lists of projects among other features.
 *
 * @experimental This API is in an experimental phase so expect that
 * API"s will not be final and there will be breaking changes
 * without bumping the major version until this comment is removed.
 */
class ProjectContext {
    constructor(_core, _assetPool) {
        this._core = _core;
        this._assetPool = _assetPool;
        this._projectInterface = new ProjectsInterface(this._core);
        this._gradientPool = new ItemPool(this._core, 'gradient');
        this._canRestoreProjectCache = new Map();
        /**
         * @package -- only for use in SAM.js
         * Updaters shouldn't be accessed directly.
         */
        this._projectUpdater = ProjectUpdater;
        // If the language configuration changes lets
        // just start refetching the gradients automatically.
        this._core.on(CONFIG_CHANGE, (event, sender, payload) => {
            if (payload.configurationKeyName === 'LOCALE') {
                this._getGradients();
            }
        });
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     */
    get assetPool() {
        return this._assetPool;
    }
    /**
     * @experimental API May change.
     *
     * @example
     * const projectContext = samCore.projectContext;
     * const projectsSource = projectsContext.projects;
     * const yourProjects = projectsSource.getList();
     *
     * @TODO SAM-2834
     */
    projects() {
        const loader = new ProjectsListLoader(this._assetPool, this._projectInterface, this._projectUpdater);
        return new ItemSource(this._assetPool, [loader], ProjectFilter.AllProjects);
    }
    /**
     * @experimental API May change.
     */
    async createProject(data) {
        const { name, gradientID, repositoryID } = data;
        if (!validatePlatformRepoName(name)) {
            // Validate the name here to avoid resolving the asset if the name is invalid.
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Invalid name provided for the rename operation.');
        }
        // Get the gradient and use the original gradient ID
        // provided by the services when creating a project.
        // The id for gradients is computed to include the language
        // code as well.
        const originalGradient = await this.getGradient(gradientID);
        const response = await this._projectInterface.createProject(Object.assign({ [RelType.APP_METADATA]: {
                'project:rendition': {
                    'gradientId': originalGradient.gradientID
                }
            }, 'repo:name': name }, (repositoryID ? { 'repo:repositoryId': repositoryID } : {})));
        const session = this._assetPool.createMutationSession();
        const project = this._projectUpdater.updateRemoteItem(session, response);
        session.commit();
        return project;
    }
    /**
     * Validates whether the user can create a new project with the provided name.
     * Project service is able to determine where to create the project for a user hence
     * the name is sufficient for this validation.
     * @param name name of the project to be validated for creation.
     */
    async validateProjectNameForCreate(name, project, repositoryID) {
        if (!validatePlatformRepoName(name)) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Invalid name provided for asset rename operation.');
        }
        let updatedName = name;
        if (project) {
            updatedName = renameFileInPath(name, project);
        }
        try {
            await this._projectInterface.validateProjectName(updatedName, repositoryID);
        }
        catch (e) {
            const samError = e;
            if (samError.code === SAMErrorCode.NOT_FOUND) {
                return true;
            }
            else if (samError.code === SAMErrorCode.CONFLICT) {
                return false;
            }
            // At this point the error is something else entirely and
            // not related to the the availability of the name. Could
            // be a networking error or something similar.
            throw e;
        }
        return false;
    }
    /**
     * Gets a project with the given ID.
     *
     * @note This method will automatically determine if it should call
     * to confirm access with the invitations service. We
     * have removed the ability to manually turn this on and off for clients
     * via the confirmAccess property.
     *
     * @param id ID of the project
     * @returns a promise that resolves to the Project with the given ID
     */
    async getProjectbyID(id) {
        let project = this._assetPool.getLocalItemById(id);
        if (project) {
            // Asynchronously confirm access since the user got here
            // from a list or confirmed it before since it's in the cache so
            // the call may not need to happen or may not affect the user.
            this._core.collaborationContext.confirmAccess(id);
            return project;
        }
        await this._core.collaborationContext.confirmAccess(id);
        const projectMetadata = await this._projectInterface.getProject(id);
        const session = this.assetPool.createMutationSession();
        project = this._projectUpdater.updateRemoteItem(session, projectMetadata);
        session.commit();
        return project;
    }
    async _getGradients() {
        const locale = this._core.environment.getValue('LOCALE');
        const gradientData = await this._projectInterface.getGradients(locale);
        const defaultGradientId = gradientData.default;
        const session = this._gradientPool.createUpdateSession();
        const computedGradients = computeGradients(gradientData.children, defaultGradientId, locale);
        const gradients = GradientUpdater.updateRemoteItems(session, computedGradients);
        session.commit();
        return gradients;
    }
    /**
     * Returns the list of available gradients.
     *
     *
     * @param languageCode Example "en-US"
     * @returns
     */
    async getGradients() {
        return await this._getGradients();
    }
    /**
     * Gets a single gradient by ID if that gradient does not
     * locally we fetch all the gradients and if it still does not
     * exist then we return the default gradient provided.
     *
     * @param id
     * @returns
     */
    async getGradient(id) {
        const gradient = this._gradientPool.getLocalItemById(id);
        const locale = this._core.environment.getValue('LOCALE');
        if (gradient && gradient.languageCode === locale) {
            return gradient;
        }
        await this._getGradients();
        const newGradient = this._gradientPool.getLocalItemById(id);
        if (!newGradient) {
            if (this._defaultGradient && this._defaultGradient.languageCode === locale) {
                return this._defaultGradient;
            }
            this._gradientPool.forEach((gradient) => {
                if (gradient.isDefault) {
                    this._defaultGradient = gradient;
                }
            });
            if (this._defaultGradient) {
                return this._defaultGradient;
            }
            // There should always be a default gradient but in the case
            // where maybe it doesn't exist we should still throw an error
            throw SAMError.create(SAMErrorCode.UNKNOWN, `Gradient ID does not exist ${id}`);
        }
        return newGradient;
    }
    async updateProject(id, data) {
        let newData = {};
        if (data.gradientID) {
            const gradient = await this.getGradient(data.gradientID);
            const project = await this.getProjectbyID(id);
            const links = project.projectLinks;
            newData = Object.assign(Object.assign({}, newData), { gradientID: gradient.gradientID });
            await this._projectInterface.patchProject(links, newData);
            const session = this._assetPool.createMutationSession();
            this._projectUpdater.updateRemoteItem(session, {
                'repo:assetId': project.id,
                _embedded: {
                    [RelType.APP_METADATA]: {
                        'project:rendition': {
                            /**
                             * @note when updating the local model with
                             * "remote" data, we should use the raw gradientID
                             * instead of the computed one.
                             */
                            gradientId: gradient.gradientID
                        }
                    }
                }
            });
            session.commit();
        }
    }
    /**
     * Used to hide and show the create button for users who may or may not
     * have the required privileges to do so.
     * @note Does not throw errors, use canCreateProjectReason to determine the specific
     * reason behind why a user can or cannot create a project.
     *
     * @param repositoryID Optional repositoryID for admins for who have access to multiple repos.
     * @returns {boolean}
     */
    async canCreateProjects(repositoryID) {
        try {
            await this._projectInterface.getCapability(ProjectCapabilities.CREATE, repositoryID);
            return true;
        }
        catch (e) {
            // If it's not a sam error then something else happened
            // and we should throw that error.
            if (!isSAMError(e)) {
                throw e;
            }
            return false;
        }
    }
    /**
     * @package for internal SAM.js use only.
     * @param repositoryID the repository id of the project
     * Restoring capability is same as the capability to create a project.
     */
    async _canRestoreProject(repositoryID) {
        if (this._canRestoreProjectCache.has(repositoryID)) {
            return this._canRestoreProjectCache.get(repositoryID);
        }
        const canRestore = await this.canCreateProjects(repositoryID);
        this._canRestoreProjectCache.set(repositoryID, canRestore);
        return canRestore;
    }
    /**
     * @package for internal SAM.js use only.
     * @param repositoryID the repository id of the project
     */
    _getCachedRestoreCapability(repositoryID) {
        return this._canRestoreProjectCache.get(repositoryID) || false;
    }
    /**
     * Used to determine the specific reasoning behind why a user can't
     * create projects. Returns unknown if the error is unrelated to
     * one of the "known" reasons.
     *
     * @param repositoryID
     * @returns
     */
    async canCreateProjectReason(repositoryID) {
        try {
            await this._projectInterface.getCapability(ProjectCapabilities.CREATE, repositoryID);
            return CanCreateProjectReason.ALLOWED;
        }
        catch (error) {
            if (!isSAMError(error)) {
                return CanCreateProjectReason.UNKNOWN;
            }
            switch (error.subCode) {
                case SAMErrorSubCode.PROJECTS_ADMIN_ONLY:
                    return CanCreateProjectReason.ADMIN_DISABLED;
                case SAMErrorSubCode.PROJECTS_NO_HOME_REPOSITORY:
                    return CanCreateProjectReason.NO_HOME_REPOSITORY;
                case SAMErrorSubCode.PROJECTS_INVALID_REPOSITORY:
                    return CanCreateProjectReason.INVALID_REPOSITORY;
                default:
                    return CanCreateProjectReason.UNKNOWN;
            }
        }
    }
    getProjectsSearchSource(query) {
        const implicitFilter = new AdobeAssetGlobalSearchFilter({
            _query: query,
            _projectsOnly: true
        });
        const globalSearchLoader = new GlobalSearchLoader(this._core.hostAssetContext.assetPool, this._core, this._core.hostAssetContext._globalSearchInterfaceV3, implicitFilter);
        return new ItemSource(this.assetPool, [globalSearchLoader], implicitFilter);
    }
    /**
     * It has been reported that sometimes a Project asset is missing the parent project id in the inherited property of the app
     * metadata [SAM-3590].
     * This method is used to call the Context API [CCXB-20015] which was implemented to address the issue in the following ways:
     * 1. Returns the parent project id so SAM.js can fix the inherited property of the local child asset.
     * 2. Fixes the inheritable property of the parent project if it is missing data.
     * 3. Fixes the inherited property of the child asset if it is missing data.
     * @param assetId
     * @param repoId
     * @package -- only for use in SAM.js
     */
    async _callContextAPI(assetId, repoId) {
        const discoveryLinks = await this._projectInterface.discoverLinks();
        const href = discoveryLinks[RelType.PROJECTS_CONTEXT].href;
        return await this._projectInterface._issueContextAPIRequest(assetId, repoId, href);
    }
}
__decorate([
    memoize
], ProjectContext.prototype, "projects", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
var LearningManagementSystems;
(function (LearningManagementSystems) {
    LearningManagementSystems["GC"] = "GC";
})(LearningManagementSystems || (LearningManagementSystems = {}));
var LearningManagementSystemRoles;
(function (LearningManagementSystemRoles) {
    LearningManagementSystemRoles["STUDENT"] = "STUDENT";
    LearningManagementSystemRoles["TEACHER"] = "TEACHER";
})(LearningManagementSystemRoles || (LearningManagementSystemRoles = {}));
var RosterImportState;
(function (RosterImportState) {
    RosterImportState["BLOCKED"] = "BLOCKED";
    RosterImportState["INVITED"] = "INVITED";
    RosterImportState["JOINED"] = "JOINED";
    RosterImportState["NOT_ENROLLED"] = "NOT_ENROLLED";
    RosterImportState["REMOVED"] = "REMOVED";
})(RosterImportState || (RosterImportState = {}));
var ImportSourceType;
(function (ImportSourceType) {
    ImportSourceType["CLEVER"] = "clever";
    ImportSourceType["CLASS_LINK"] = "classlink";
})(ImportSourceType || (ImportSourceType = {}));
var SubjectTypes;
(function (SubjectTypes) {
    SubjectTypes["noSubject"] = "";
    SubjectTypes["allsubjects"] = "allsubjects";
    SubjectTypes["careersTechnicalEducation"] = "careersTechnicalEducation";
    SubjectTypes["computerScience"] = "computerScience";
    SubjectTypes["english"] = "english";
    SubjectTypes["math"] = "math";
    SubjectTypes["otherLanguages"] = "otherLanguages";
    SubjectTypes["others"] = "others";
    SubjectTypes["science"] = "science";
    SubjectTypes["socialStudies"] = "socialStudies";
    SubjectTypes["visualPerformingArts"] = "visualPerformingArts";
})(SubjectTypes || (SubjectTypes = {}));
var ClassroomRole;
(function (ClassroomRole) {
    ClassroomRole["STUDENT"] = "STUDENT";
    ClassroomRole["TEACHER"] = "TEACHER";
})(ClassroomRole || (ClassroomRole = {}));

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class DovetailInterface extends InterfaceBase {
    /** Overrides the default behavior for a 202 response */
    async _monitorResponse(response) {
        return response;
    }
    _getUrl(endpoint) {
        return `https://${this._core.environment.endpoints.DOVETAIL_HOST}${endpoint}`;
    }
    /**
     * Gets a single classroom by class ID
     * @param classId The classroom ID associated with a single classroom
     * @returns A promise that resolves to a classroom
     */
    async getClassroom(classId) {
        return await this._getResponse(this._getUrl(`/classrooms/${classId}`));
    }
    /**
     * Gets a single classroom by class code. Typically, using getClassroom() is preferred.
     * @param classCode The class code associated with a single classroom
     * @returns A promise that resolves to a classroom
     */
    async getClassroomByCode(classCode) {
        return await this._getResponse(this._getUrl(`/classrooms/${classCode}`));
    }
    /**
     * Gets a list of all available classrooms
     * @param loadStudents flag to load student and teacher data in the classrooms.
     *                     If false, combined userCount is still returned.
     * @returns A promise that resolves to a list of classrooms
     */
    async getClassrooms(loadStudents = false) {
        return this._getResponse(this._getUrl(`/classrooms/?loadStudents=${loadStudents}`));
    }
    /**
     * Refreshes the classroom code for a classroom
     *
     * @param classroomId The ID of the classroom to have its code refreshed
     */
    async refreshClassroomCode(classroomId) {
        return this._getResponse(this._getUrl(`/edu/teacher/classroom/refreshCode?classId=${classroomId}`));
    }
    /** Deletes multiple classrooms in bulk */
    deleteClassrooms(classIds) {
        return this._doPost(this._getUrl(`/edu/teacher/classroom/bulkDelete`), { 'Content-Type': JSON_MIME_TYPE }, JSON.stringify(classIds), [202]);
    }
    /** Deletes a single classroom */
    deleteClassroom(classId) {
        return this._doDelete(this._getUrl(`/edu/teacher/classroom/${classId}`), undefined, [202]);
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const SubjectChoices = Object.values(SubjectTypes);
/**
 * Class representing the notion of a "classroom".  This is currently a read-only object
 * but this may be extended to support CRUD operations in the future.
 * @extends ItemBase
 *
 * @example
 * // Instatiate list and load contents
 * const classroomsList = core.classroomContext.getClassrooms().getList(...options);
 * await waitForListLoad(classroomsList);
 *
 * // Get a specific classroom
 * const load = useMemo(() => core.classroomContext.getClassroomById(id), [classroomContext, id]);
 * const { value: classroom, error, loading } = useLoading<Classroom | undefined>(undefined, load);
 */
class Classroom extends ItemBase {
    constructor() {
        super(...arguments);
        this._code = '';
        this._importSource = {};
        this._isDeleted = false;
        this._isVirtual = false;
        this._grades = [];
        this._name = '';
        this._period = '';
        this._students = []; // [ACPCEDU-1780] Will be implemented with membership endpoints when ready
        this._subject = SubjectTypes.noSubject;
        this._teachers = []; // [ACPCEDU-1780] Will be implemented with membership endpoints when ready
        this._inactiveStudents = []; // [ACPCEDU-1780] Will be implemented or dropped with membership endpoints when ready
        this._dovetailInterface = new DovetailInterface(this._core); // [ACPCEDU-1805] Remove with dovetail
    }
    get code() {
        return this._code;
    }
    get created() {
        if (!this._created) {
            return undefined;
        }
        // [ACPCEDU-1805] Will always be a string once dovetail is removed
        return (typeof this._created === 'string') ? new Date(this._created) : new Date(this._created * 1000);
    }
    get importSource() {
        return this._importSource;
    }
    get isDeleted() {
        return this._isDeleted;
    }
    get isVirtual() {
        return this._isVirtual;
    }
    get grades() {
        return this._grades;
    }
    get name() {
        return this._name;
    }
    get period() {
        return this._period;
    }
    // [ACPCEDU-1780] Will be implemented with membership endpoints when ready
    get students() {
        return this._students;
    }
    get subject() {
        return this._subject;
    }
    // [ACPCEDU-1780] Will be implemented with membership endpoints when ready
    get teachers() {
        return this._teachers;
    }
    get userCount() {
        return this._students.length + this._teachers.length || this._userCount;
    }
    // [ACPCEDU-1780] Will be implemented or dropped with membership endpoints when ready
    get inactiveStudents() {
        return this._inactiveStudents;
    }
    async delete() {
        return this._dovetailInterface.deleteClassroom(this.id);
    }
    async refreshCode() {
        return this._dovetailInterface.refreshClassroomCode(this.id);
    }
}
__decorate([
    observable
], Classroom.prototype, "code", null);
__decorate([
    observable
], Classroom.prototype, "created", null);
__decorate([
    observable
], Classroom.prototype, "importSource", null);
__decorate([
    observable
], Classroom.prototype, "isDeleted", null);
__decorate([
    observable
], Classroom.prototype, "isVirtual", null);
__decorate([
    observable
], Classroom.prototype, "grades", null);
__decorate([
    observable
], Classroom.prototype, "name", null);
__decorate([
    observable
], Classroom.prototype, "period", null);
__decorate([
    observable
], Classroom.prototype, "students", null);
__decorate([
    observable
], Classroom.prototype, "subject", null);
__decorate([
    observable
], Classroom.prototype, "teachers", null);
__decorate([
    observable
], Classroom.prototype, "userCount", null);
__decorate([
    observable
], Classroom.prototype, "inactiveStudents", null);
const ClassroomUpdater = new ItemUpdater({
    localItemType: Classroom,
    remoteIdProperty: 'classId',
    propertyUpdater: new PropertyUpdater({
        _code: { name: 'classCode', format: 'string' },
        _created: { name: 'createdOn', format: 'string' },
        _importSource: {
            name: 'importedFrom',
            format: 'object',
            set: (remoteImportSource, dest) => {
                const classroom = dest;
                classroom['_importSource'] = {
                    id: remoteImportSource.sourceId,
                    type: remoteImportSource.source,
                    synced: remoteImportSource.lastUpdatedAt ? new Date(remoteImportSource.lastUpdatedAt) : undefined,
                };
            },
        },
        _isDeleted: { name: 'deleted', format: 'boolean' },
        _isVirtual: { name: 'isVirtual', format: 'boolean' },
        _grades: { isArray: true, name: 'grades' },
        _name: { name: 'className', format: 'string' },
        _period: { name: 'period', format: 'string' },
        _students: { isArray: true, name: 'studentList', format: 'object' },
        _inactiveStudents: { isArray: true, name: 'inactiveStudentList', format: 'object' },
        _subject: { name: 'subject', format: 'string', choices: SubjectChoices },
        _teachers: { isArray: true, name: 'teacherList', format: 'object' },
        _userCount: { name: 'userCount', format: 'number' },
    }),
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class ClassroomListLoader {
    constructor(_dovetailClassroomPool, _classroomsInterface) {
        this._dovetailClassroomPool = _dovetailClassroomPool;
        this._classroomsInterface = _classroomsInterface;
    }
    async loadPage(options) {
        return await this._loadPage(options);
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async _loadPage(options, start) {
        const result = await this._classroomsInterface.getClassrooms();
        const session = this._dovetailClassroomPool.createLoaderUpdateSession(Date.now());
        const items = ClassroomUpdater.updateRemoteItems(session, result);
        session.commit();
        return {
            items,
            total: items.length,
            loadNextPage: undefined,
        };
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const API_ROOT = '/api/v1';
class ClassroomsInterface extends InterfaceBase {
    _getUrl(endpoint) {
        return `https://${this._core.environment.endpoints.CLASSROOMS_HOST}${endpoint}`;
    }
    /**
     * Gets a single classroom by class ID
     * @param id Either the classId or the classCode.  The endpoint queries both.
     * @returns A promise that resolves to a classroom
     */
    async getClassroom(id) {
        return await this._getResponse(this._getUrl(`${API_ROOT}/classrooms/${id}`));
    }
    /**
     * Gets a list of all available classrooms
     * @param loadStudents flag to load student and teacher data in the classrooms.
     *                     If false, combined userCount is still returned.
     * @returns A promise that resolves to a list of classrooms
     */
    async getClassrooms() {
        return this._getResponse(this._getUrl(`${API_ROOT}/classrooms`));
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class DovetailClassroomLoader {
    constructor(_dovetailClassroomPool, _dovetailInterface) {
        this._dovetailClassroomPool = _dovetailClassroomPool;
        this._dovetailInterface = _dovetailInterface;
    }
    async loadPage(options) {
        return await this._loadPage(options);
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async _loadPage(options, start) {
        const result = await this._dovetailInterface.getClassrooms(false);
        const session = this._dovetailClassroomPool.createLoaderUpdateSession(Date.now());
        const items = ClassroomUpdater.updateRemoteItems(session, result.classroomList);
        session.commit();
        return {
            items,
            total: result.classroomList.length,
            loadNextPage: undefined, // If paging is added to getClassrooms: addNextPage will be added in [ACPCEDU-170]
        };
    }
}

const ASSIGNMENTS_INDEX_LINKS = {
    [RelType.ASSIGNMENTS_COLLECTION]: ['classroomId'],
    [RelType.ASSIGNMENTS_CREATE]: ['classroomId'],
    [RelType.ASSIGNMENTS_PRIMARY]: ['assignmentId']
};
var AssignmentStatus;
(function (AssignmentStatus) {
    AssignmentStatus["DRAFT"] = "DRAFT";
    AssignmentStatus["ASSIGNED"] = "ASSIGNED";
})(AssignmentStatus || (AssignmentStatus = {}));
/**
 * A mapping of fields from the local assignment model to the remote assignment model
 * This is the inverse of AssignmentUpdater.
 */
const assignmentRemoteFieldMapping = {
    instructions: 'assignment:instructions',
    dueDate: 'assignment:dueDate',
    externalTemplateIds: 'assignment:externalTemplateIds',
    templateIds: 'assignment:templateIds',
    name: 'repo:name',
    tasks: 'assignment:tasks',
    status: 'assignment:status',
    referenceIds: 'assignment:referenceIds',
    externalTutorials: 'assignment:externalTemplateIds'
};
// exact same list as in https://git.corp.adobe.com/Horizon/hz/blob/main/features/tasks/src/typings/TaskIDEnums.ts#L26
// Perhaps in the future, to prevent maintaining 2 lists, we can pull in Horizon when it's extensively used in this repo.
var AssignmentTaskIDEnums;
(function (AssignmentTaskIDEnums) {
    AssignmentTaskIDEnums["albumCover"] = "album-cover";
    AssignmentTaskIDEnums["bannerPrint"] = "banner-print";
    AssignmentTaskIDEnums["bannerWeb"] = "banner-web";
    AssignmentTaskIDEnums["blogGraphic"] = "blog-graphic";
    AssignmentTaskIDEnums["blogHeader"] = "blog-header";
    AssignmentTaskIDEnums["bookCover"] = "book-cover";
    AssignmentTaskIDEnums["bookmark"] = "bookmark";
    AssignmentTaskIDEnums["brochure"] = "brochure";
    AssignmentTaskIDEnums["businessCard"] = "business-card";
    AssignmentTaskIDEnums["calendar"] = "calendar";
    AssignmentTaskIDEnums["cardGreeting"] = "card-greeting";
    AssignmentTaskIDEnums["certificate"] = "certificate";
    AssignmentTaskIDEnums["checklist"] = "checklist";
    AssignmentTaskIDEnums["classSchedule"] = "class-schedule";
    AssignmentTaskIDEnums["comicStrip"] = "comic-strip";
    AssignmentTaskIDEnums["conceptMap"] = "concept-map";
    AssignmentTaskIDEnums["couponSheet"] = "coupon-sheet";
    AssignmentTaskIDEnums["coverPage"] = "cover-page";
    AssignmentTaskIDEnums["displayAd"] = "display-ad";
    AssignmentTaskIDEnums["dividerPage"] = "divider-page";
    AssignmentTaskIDEnums["drawing"] = "drawing";
    AssignmentTaskIDEnums["drawingWorksheet"] = "drawing-worksheet";
    AssignmentTaskIDEnums["ebookCover"] = "ebook-cover";
    AssignmentTaskIDEnums["emailHeader"] = "email-header";
    AssignmentTaskIDEnums["etsyShopBanner"] = "etsy-shop-banner";
    AssignmentTaskIDEnums["etsyShopCover"] = "etsy-shop-cover";
    AssignmentTaskIDEnums["eventbriteHeader"] = "eventbrite-header";
    AssignmentTaskIDEnums["facebookAd"] = "facebook-ad";
    AssignmentTaskIDEnums["facebookEventCover"] = "facebook-event-cover";
    AssignmentTaskIDEnums["facebookGroupCover"] = "facebook-group-cover";
    AssignmentTaskIDEnums["facebookPageCover"] = "facebook-page-cover";
    AssignmentTaskIDEnums["facebookPost"] = "facebook-post";
    AssignmentTaskIDEnums["facebookProfileCover"] = "facebook-profile-cover";
    AssignmentTaskIDEnums["facebookShopCover"] = "facebook-shop-cover";
    AssignmentTaskIDEnums["facebookStory"] = "facebook-story";
    AssignmentTaskIDEnums["flashCard"] = "flash-card";
    AssignmentTaskIDEnums["flashCardSheet"] = "flash-card-sheet";
    AssignmentTaskIDEnums["flyer"] = "flyer";
    AssignmentTaskIDEnums["giftCertificate"] = "gift-certificate";
    AssignmentTaskIDEnums["giftTag"] = "gift-tag";
    AssignmentTaskIDEnums["googleClassroomBanner"] = "google-classroom-banner";
    AssignmentTaskIDEnums["graph"] = "graph";
    AssignmentTaskIDEnums["graphicOrganizer"] = "graphic-organizer";
    AssignmentTaskIDEnums["idCard"] = "id-card";
    AssignmentTaskIDEnums["infographic"] = "infographic";
    AssignmentTaskIDEnums["instagramAd"] = "instagram-ad";
    AssignmentTaskIDEnums["instagramCarousel"] = "instagram-carousel";
    AssignmentTaskIDEnums["instagramLandscapePost"] = "instagram-landscape-post";
    AssignmentTaskIDEnums["instagramPortraitPost"] = "instagram-portrait-post";
    AssignmentTaskIDEnums["instagramReel"] = "instagram-reel";
    AssignmentTaskIDEnums["instagramSquarePost"] = "instagram-square-post";
    AssignmentTaskIDEnums["instagramStory"] = "instagram-story";
    AssignmentTaskIDEnums["instagramStoryAd"] = "instagram-story-ad";
    AssignmentTaskIDEnums["invitation"] = "invitation";
    AssignmentTaskIDEnums["invoice"] = "invoice";
    AssignmentTaskIDEnums["journal"] = "journal";
    AssignmentTaskIDEnums["label"] = "label";
    AssignmentTaskIDEnums["labelSheet"] = "label-sheet";
    AssignmentTaskIDEnums["leafletTrifold"] = "leaflet-trifold";
    AssignmentTaskIDEnums["lessonPlan"] = "lesson-plan";
    AssignmentTaskIDEnums["letter"] = "letter";
    AssignmentTaskIDEnums["letterhead"] = "letterhead";
    AssignmentTaskIDEnums["lineAdSmall"] = "line-ad-small";
    AssignmentTaskIDEnums["lineAdSquare"] = "line-ad-square";
    AssignmentTaskIDEnums["lineAdSquareVideo"] = "line-ad-square-video";
    AssignmentTaskIDEnums["lineAdVertical"] = "line-ad-vertical";
    AssignmentTaskIDEnums["lineRichMenuLarge"] = "line-rich-menu-large";
    AssignmentTaskIDEnums["lineRichMenuSmall"] = "line-rich-menu-small";
    AssignmentTaskIDEnums["lineRichMessage"] = "line-rich-message";
    AssignmentTaskIDEnums["linkedinBlogPost"] = "linkedin-blog-post";
    AssignmentTaskIDEnums["linkedinPost"] = "linkedin-post";
    AssignmentTaskIDEnums["linkedinProfileCover"] = "linkedin-profile-cover";
    AssignmentTaskIDEnums["logo"] = "logo";
    AssignmentTaskIDEnums["magazine"] = "magazine";
    AssignmentTaskIDEnums["mediaKit"] = "media-kit";
    AssignmentTaskIDEnums["meme"] = "meme";
    AssignmentTaskIDEnums["memo"] = "memo";
    AssignmentTaskIDEnums["menu"] = "menu";
    AssignmentTaskIDEnums["mindmap"] = "mindmap";
    AssignmentTaskIDEnums["mobileVideo"] = "mobile-video";
    AssignmentTaskIDEnums["mug"] = "mug";
    AssignmentTaskIDEnums["nameTag"] = "name-tag";
    AssignmentTaskIDEnums["newsletter"] = "newsletter";
    AssignmentTaskIDEnums["noteHeaderImage"] = "note-header-image";
    AssignmentTaskIDEnums["onlineNewsletter"] = "online-newsletter";
    AssignmentTaskIDEnums["onlineReport"] = "online-report";
    AssignmentTaskIDEnums["pamphlet"] = "pamphlet";
    AssignmentTaskIDEnums["photoBook"] = "photo-book";
    AssignmentTaskIDEnums["pinterestIdeaPin"] = "pinterest-idea-pin";
    AssignmentTaskIDEnums["pinterestPost"] = "pinterest-post";
    AssignmentTaskIDEnums["pinterestSquare"] = "pinterest-square";
    AssignmentTaskIDEnums["pinterestVertical"] = "pinterest-vertical";
    AssignmentTaskIDEnums["placeCard"] = "place-card";
    AssignmentTaskIDEnums["planner"] = "planner";
    AssignmentTaskIDEnums["playlistCover"] = "playlist-cover";
    AssignmentTaskIDEnums["podcastCover"] = "podcast-cover";
    AssignmentTaskIDEnums["postcard"] = "postcard";
    AssignmentTaskIDEnums["poster"] = "poster";
    AssignmentTaskIDEnums["presentation"] = "presentation";
    AssignmentTaskIDEnums["printAd"] = "print-ad";
    AssignmentTaskIDEnums["program"] = "program";
    AssignmentTaskIDEnums["proposal"] = "proposal";
    AssignmentTaskIDEnums["recipeCard"] = "recipe-card";
    AssignmentTaskIDEnums["redditCover"] = "reddit-cover";
    AssignmentTaskIDEnums["report"] = "report";
    AssignmentTaskIDEnums["reportCard"] = "report-card";
    AssignmentTaskIDEnums["resume"] = "resume";
    AssignmentTaskIDEnums["rubric"] = "rubric";
    AssignmentTaskIDEnums["snapchatAd"] = "snapchat-ad";
    AssignmentTaskIDEnums["snapchatFilter"] = "snapchat-filter";
    AssignmentTaskIDEnums["soundcloudBanner"] = "soundcloud-banner";
    AssignmentTaskIDEnums["tableOfContents"] = "table-of-contents";
    AssignmentTaskIDEnums["ticket"] = "ticket";
    AssignmentTaskIDEnums["tiktokAd"] = "tiktok-ad";
    AssignmentTaskIDEnums["tiktokVideo"] = "tiktok-video";
    AssignmentTaskIDEnums["tshirt"] = "tshirt";
    AssignmentTaskIDEnums["tumblrBanner"] = "tumblr-banner";
    AssignmentTaskIDEnums["twitchBanner"] = "twitch-banner";
    AssignmentTaskIDEnums["twitchOverlay"] = "twitch-overlay";
    AssignmentTaskIDEnums["twitterHeader"] = "twitter-header";
    AssignmentTaskIDEnums["twitterPost"] = "twitter-post";
    AssignmentTaskIDEnums["twitterVideo"] = "twitter-video";
    AssignmentTaskIDEnums["video"] = "video";
    AssignmentTaskIDEnums["wallpaperDesktop"] = "wallpaper-desktop";
    AssignmentTaskIDEnums["wallpaperMobile"] = "wallpaper-mobile";
    AssignmentTaskIDEnums["webpage"] = "webpage";
    AssignmentTaskIDEnums["worksheet"] = "worksheet";
    AssignmentTaskIDEnums["youtubeBanner"] = "youtube-banner";
    AssignmentTaskIDEnums["youtubeDisplayAds"] = "youtube-display-ads";
    AssignmentTaskIDEnums["youtubeProfilePhoto"] = "youtube-profile-photo";
    AssignmentTaskIDEnums["youtubeShorts"] = "youtube-shorts";
    AssignmentTaskIDEnums["youtubeThumbnail"] = "youtube-thumbnail";
    AssignmentTaskIDEnums["youtubeVideo"] = "youtube-video";
    AssignmentTaskIDEnums["youtubeVideoAd"] = "youtube-video-ad";
    AssignmentTaskIDEnums["zoomBackground"] = "zoom-background";
})(AssignmentTaskIDEnums || (AssignmentTaskIDEnums = {}));

/*************************************************************************
   *
   * ADOBE CONFIDENTIAL
   * ___________________
   *
   *  Copyright 2024 Adobe Systems Incorporated
   *  All Rights Reserved.
   *
   * NOTICE:  All information contained herein is, and remains
   * the property of Adobe Systems Incorporated and its suppliers,
   * if any.  The intellectual and technical concepts contained
   * herein are proprietary to Adobe Systems Incorporated and its
   * suppliers and are protected by trade secret or copyright law.
   * Dissemination of this information or reproduction of this material
   * is strictly forbidden unless prior written permission is obtained
   * from Adobe Systems Incorporated.
   **************************************************************************/
function getDefaultOptions(Defaults, options) {
    return Object.assign({}, Defaults, options);
}
const DefaultAssignmentsListingOptions = {
    orderDirection: 'ascending',
    orderBy: 'repo:name',
    start: 0,
    limit: 0
};
function createPageTemplate(options) {
    const templateValues = {};
    if (options.start) {
        templateValues.start = options.start;
    }
    if (options.limit) {
        templateValues.limit = options.limit;
    }
    return templateValues;
}
function getDirectionValue(userOptions) {
    const directionFlag = userOptions.orderDirection === 'ascending' ? '+' : '-';
    return directionFlag + userOptions.orderBy;
}
function getAssignmentsListingPath(links, options) {
    const userOptions = getDefaultOptions(DefaultAssignmentsListingOptions, options);
    const templateValues = createPageTemplate(userOptions);
    templateValues.orderBy = getDirectionValue(userOptions);
    const { view, classroomId } = userOptions;
    if (view) {
        templateValues.view = view;
    }
    if (classroomId) {
        templateValues.classroomId = classroomId;
    }
    return getURLFromLinks(links, RelType.ASSIGNMENTS_COLLECTION, templateValues);
}
function validateRequiredAssignmentLinks(links) {
    return validateLinks(links, ASSIGNMENTS_INDEX_LINKS);
}
class AssignmentsInterface extends InterfaceBase {
    async _discoverLinks(rootHref) {
        const response = await this._getResponse(rootHref);
        return response._links;
    }
    _getEndpointHref(endpoint) {
        return `https://${this._core.environment.endpoints.ASSIGNMENTS_HOST}${endpoint}`;
    }
    async discoverLinks() {
        return this._discoverLinks(this._getEndpointHref(`/api/v1/index`));
    }
    /**
     * @description Fetches the list of Assignments based on the options provided
     * @param options : options to sort and filter the Assignments listing
     * @returns resolves to list of Assignments along with the page information and links to the next page
    */
    async getAssignments(options) {
        // options.classroomId is optional as the type is reused elsewhere, but really it's needed here to get assignments
        if (!options.classroomId) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'classroomId is required to fetch assignments');
        }
        const links = await this.discoverLinks();
        const url = getAssignmentsListingPath(links, options);
        return this._getResponse(url);
    }
    /**
     * @description Gets the metadata for a single assignment
     * @param id asset id that uniquely identifies the assignment
     * @returns resolves to assignment metadata for the given id
    */
    async getAssignment(id) {
        const links = await this.discoverLinks();
        const url = getURLFromLinks(links, RelType.ASSIGNMENTS_PRIMARY, { assignmentId: id });
        return await this._getResponse(url);
    }
    /**
     * @description Gets the metadata for a single assignment by code
     * @param code code that identifies assignment
     * @returns resolves to assignment metadata for the given code
    */
    async getAssignmentByCode(code) {
        const links = await this.discoverLinks();
        const url = `${getURLFromLinks(links, RelType.ASSIGNMENTS_PRIMARY)}code/${code}`;
        return await this._getResponse(url);
    }
    /**
     * Creates a new Assignment
     * @param data needed to create a Assignment like name and instructions
     * @returns the newly created Assignment
    */
    async createAssignment(data) {
        var _a, _b, _c;
        // [ACPCEDU-444]: When specified, status always takes precedence; if tasks present then assignment is ASSIGNED, otherwise it is DRAFT.
        const hasTasks = ((_b = (_a = data.tasks) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0;
        const status = data.status || (hasTasks ? AssignmentStatus.ASSIGNED : AssignmentStatus.DRAFT);
        const links = await this.discoverLinks();
        const url = new URL(getURLFromLinks(links, RelType.ASSIGNMENTS_CREATE));
        url.searchParams.append('classroomId', data.classroomId);
        url.searchParams.append('assign', status === AssignmentStatus.ASSIGNED ? 'true' : 'false');
        const response = await this._doPost(url.toString(), { 'Content-Type': JSON_MIME_TYPE }, JSON.stringify({
            'assignment:dueDate': (_c = data.dueDate) === null || _c === void 0 ? void 0 : _c.toISOString(),
            'assignment:instructions': data.instructions,
            'repo:name': data.name,
            'assignment:status': status,
            'assignment:tasks': data.tasks || [],
            'assignment:createdBy': data.createdBy,
            'assignment:externalTemplateIds': data.externalTemplateIds,
        }), [201]);
        // TODO: https://jira.corp.adobe.com/browse/ACPCEDU-403 Add error handling
        const jsonResponse = await response.json();
        return jsonResponse;
    }
    /**
     * Assigns an assignment to all students in the classroom
     * @param assignmentId The id of the Assignment to assign
     */
    async assignAssignment(assignmentId) {
        const links = await this.discoverLinks();
        const url = `${getURLFromLinks(links, RelType.ASSIGNMENTS_PRIMARY, { assignmentId })}/:assign`;
        await this._doPost(url, { 'Content-Type': JSON_MIME_TYPE }, JSON.stringify({}), [204]);
    }
    /**
     * Updates an assignment's data
     * @param id The id of the Assignment to update
     * @param data The data to update the Assignment with. Currently only the instructions, dueDate, tasks, and templateIds can be modified.
     */
    async patchAssignment(id, data) {
        const links = await this.discoverLinks();
        const ops = Object.entries(data)
            .filter(([key]) => key.startsWith('assignment:')) // We can only update the assignment metadata, not the asset metadata
            .map(([key, value]) => ({ op: 'replace', path: `/${key}`, value }));
        if (ops.length === 0) {
            return;
        }
        const url = getURLFromLinks(links, RelType.ASSIGNMENTS_PRIMARY, { assignmentId: id });
        await this._doPatch(url, { 'Content-Type': JSON_MIME_TYPE }, JSON.stringify(ops), [200, 204]);
    }
    /**
     * Discards an assignment -- from the DISCARDED state, an assignment can be permanently deleted or restored
     * @param id The id of the Assignment to discard
     */
    async discardAssignment(id) {
        const links = await this.discoverLinks();
        const url = `${getURLFromLinks(links, RelType.ASSIGNMENTS_PRIMARY, {
            assignmentId: id,
        })}/:discard`;
        await this._doPost(url, undefined, undefined, [204]);
    }
    /**
     * Restores an assignment
     * @param id The id of the Assignment to restore
     */
    async restoreAssignment(id) {
        const links = await this.discoverLinks();
        const url = `${getURLFromLinks(links, RelType.ASSIGNMENTS_PRIMARY, {
            assignmentId: id,
        })}/:restore`;
        await this._doPost(url, undefined, undefined, [204]);
    }
}
__decorate([
    cache(validateRequiredAssignmentLinks),
    __param(0, key)
], AssignmentsInterface.prototype, "_discoverLinks", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
var StudentAssignmentStatus;
(function (StudentAssignmentStatus) {
    StudentAssignmentStatus["NOT_STARTED"] = "NOT_STARTED";
    StudentAssignmentStatus["IN_PROGRESS"] = "IN_PROGRESS";
    StudentAssignmentStatus["RETURNED"] = "RETURNED";
    StudentAssignmentStatus["SUBMITTED"] = "SUBMITTED";
})(StudentAssignmentStatus || (StudentAssignmentStatus = {}));

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class StudentAssignment extends ItemBase {
    constructor(params) {
        super(params._core, params.id);
        this._assetClass = 'directory'; // this makes assignment a folder
        this._assignmentId = params.assignmentId;
        this._links = params.links;
        this._status = params.status || StudentAssignmentStatus.NOT_STARTED;
        this._student = params.student;
        this._studentDirectoryId = params.studentDirectoryId;
        this._submission = params.submission;
        this._submissionDate = params.submissionDate;
    }
    get assignmentId() {
        return this._assignmentId;
    }
    get status() {
        return this._status;
    }
    get student() {
        return this._student;
    }
    get studentDirectoryId() {
        return this._studentDirectoryId;
    }
    get submission() {
        return this._submission;
    }
    get submissionDate() {
        return this._submissionDate;
    }
    /**
     * Returns the files in the student's assignment directory. Optional parameter to specify the order of the files.
     */
    async getFiles({ order }) {
        return this._core.classroomContext.getStudentAssignmentFiles({ studentDirectoryId: this.studentDirectoryId, order });
    }
    canStart() {
        return this._status === StudentAssignmentStatus.NOT_STARTED;
    }
    canReturn() {
        return this._status === StudentAssignmentStatus.SUBMITTED;
    }
    /**
     * Marks this student assignment as returned.
     */
    async return() {
        var _a;
        const remoteStudentAssignment = await this._core.classroomContext._returnAssignment({
            links: this._links,
            studentAssignmentId: this.studentDirectoryId,
            submissionId: (_a = this.submission) === null || _a === void 0 ? void 0 : _a['repo:assetId'],
        });
        this._links = remoteStudentAssignment._links;
        this._status = remoteStudentAssignment['assignment:submissionStatus'];
        this.emit(CHANGE);
        return this;
    }
    /**
     * Marks this student assignment as started.
     */
    async start() {
        const remoteStudentAssignment = await this._core.classroomContext._startAssignment({
            assignmentId: this.assignmentId,
            links: this._links,
        });
        this._links = remoteStudentAssignment._links;
        this._status = remoteStudentAssignment['assignment:submissionStatus'];
        this._studentDirectoryId = remoteStudentAssignment['repo:assetId'];
        this.emit(CHANGE);
        return this;
    }
    /**
     * Marks this student assignment as submitted.
     * @param submissionId - The asset ID of the student's work to become the submission for this assignment.
     */
    async submit(submissionId) {
        // If the assignment has not yet been started, start it first
        if (this._status === StudentAssignmentStatus.NOT_STARTED) {
            await this.start();
        }
        // If asset associated with submissionId is not already in the student's directory, move it there
        const directory = await this._core.hostAssetContext.getAssetWithCloudID(this.studentDirectoryId); // Know it is defined because we just started the assignment
        // Issue with moving cross buckets to be fixed in [ACPCEDU-1876]
        // In the interim, we'll copy the submittedAsset to the student's work folder and delete the original
        let assetToSubmit, list;
        if (directory.children) {
            list = directory.children.getList(AdobeAssetOrder.default);
            await waitForListLoad(list);
        }
        if (list && list.items.find(asset => asset.id === submissionId) === undefined) {
            const submittedAsset = await this._core.hostAssetContext.getAssetWithCloudID(submissionId);
            // Match date formatting when submitting an assignment
            const formattedDate = this._core.classroomContext._getFormattedCreatedDateHelper({ includeFormattedDate: false });
            const assignment = await this._core.classroomContext.getAssignmentByID(this.assignmentId);
            const updatedName = `${this.student.displayName} ${assignment.name} ${formattedDate}`;
            assetToSubmit = await submittedAsset.copy(directory, updatedName);
            await submittedAsset.permanentlyDelete();
        }
        const remoteStudentAssignment = await this._core.classroomContext._submitAssignment({
            links: this._links,
            studentAssignmentId: this.studentDirectoryId,
            submissionId: (assetToSubmit === null || assetToSubmit === void 0 ? void 0 : assetToSubmit.id) || submissionId,
        });
        this._links = remoteStudentAssignment._links;
        this._status = remoteStudentAssignment['assignment:submissionStatus'];
        this._submission = {
            'repo:assetId': submissionId,
        };
        this._submissionDate = remoteStudentAssignment['assignment:submissionDate']
            ? new Date(remoteStudentAssignment['assignment:submissionDate'])
            : undefined;
        this.emit(CHANGE);
        return this;
    }
}
__decorate([
    observable
], StudentAssignment.prototype, "assignmentId", null);
__decorate([
    observable
], StudentAssignment.prototype, "status", null);
__decorate([
    observable
], StudentAssignment.prototype, "student", null);
__decorate([
    observable
], StudentAssignment.prototype, "studentDirectoryId", null);
__decorate([
    observable
], StudentAssignment.prototype, "submission", null);
__decorate([
    observable
], StudentAssignment.prototype, "submissionDate", null);

// Prioritize assignments that require teacher attention: SUBMITTED, IN_PROGRESS, RETURNED, NOT_STARTED
const StatusOrderValues$1 = {
    [StudentAssignmentStatus.SUBMITTED]: 0,
    [StudentAssignmentStatus.IN_PROGRESS]: 1,
    [StudentAssignmentStatus.RETURNED]: 2,
    [StudentAssignmentStatus.NOT_STARTED]: 3,
};
const compareStudentDisplayName = (a, b) => {
    return compareValues(a.student.displayName || '', b.student.displayName || '');
};
const OrderFieldToComparator$8 = {
    studentDisplayName(a, b) {
        return compareStudentDisplayName(a, b);
    },
    submissionDate(a, b) {
        let value;
        if (!a.submissionDate && !b.submissionDate) {
            value = 0;
        }
        else if (!a.submissionDate) {
            value = 1; // Undefined date should be sorted last in ascending order
        }
        else if (!b.submissionDate) {
            value = -1;
        }
        else {
            value = compareValues(a.submissionDate.getTime(), b.submissionDate.getTime());
        }
        if (value === 0) {
            // If equal submission date (probably both are undefined), sort by student display name
            return compareStudentDisplayName(a, b);
        }
        return value;
    },
    status(a, b) {
        const value = compareValues(StatusOrderValues$1[a.status], StatusOrderValues$1[b.status]);
        if (value === 0) {
            // Within the same status, sort by student display name
            return compareStudentDisplayName(a, b);
        }
        return value;
    }
};
class StudentAssignmentOrder extends createItemOrderFactory(OrderFieldToComparator$8) {
}
StudentAssignmentOrder.default = new StudentAssignmentOrder('ascending', 'studentDisplayName');

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const AssignmentStatusChoices = Object.values(AssignmentStatus);
class Assignment extends AdobeAsset {
    constructor(_core, id, metadata) {
        super(_core, id, metadata);
        this._core = _core;
        this._assetClass = 'directory'; // this makes assignment a folder
        this._assignmentInterface = new AssignmentsInterface(this._core);
        this._classroomId = '';
        this._code = '';
        this._externalTemplateIds = [];
        /**
         * This is the raw data from remote, we need to parse it to get the actual external tutorials.
         * Storing the raw data allows the property updater to know if the data has changed.
         * Both externalTemplateIds and externalTutorials are derived from this data.
         */
        this._externalTemplateIdsRemote = [];
        this._instructions = '';
        this._isAutoCreated = false;
        this._studentAssignments = [];
        this._externalTutorials = [];
        this._isExternalTutorialsDirty = false;
        // add a listener for when metadata changes
        this.appMetadata.on(CHANGE, () => {
            if (this.appMetadata.data) {
                const session = this._core.classroomContext.assignmentPool.createMutationSession();
                const remoteAssignment = this.appMetadata.data;
                const updatedMetadata = {
                    'repo:assetId': remoteAssignment['repo:assetId'],
                    'assignment:code': remoteAssignment['assignment:code'],
                    'assignment:classroomId': remoteAssignment['assignment:classroomId'],
                    'assignment:status': remoteAssignment['assignment:status'],
                    'assignment:dueDate': remoteAssignment['assignment:dueDate'],
                    'assignment:instructions': remoteAssignment['assignment:instructions'],
                    'assignment:tasks': remoteAssignment['assignment:tasks'],
                };
                AssignmentUpdater.updateLocalItem(session, this, updatedMetadata);
                session.commit();
            }
        });
    }
    get classroomId() {
        return this._classroomId;
    }
    /** Gets all student assignments for this assignment */
    get studentAssignments() {
        return this._studentAssignments;
    }
    // eslint-disable-next-line no-dupe-class-members
    getStudentAssignmentsByStatus(status) {
        if (Array.isArray(status)) {
            return this._studentAssignments.filter((sa) => status.includes(sa.status));
        }
        return this._studentAssignments.filter((sa) => sa.status === status);
    }
    /** Gets all student assignments for this assignment that are not NOT_STARTED */
    get existingStudentAssignments() {
        const existsList = [
            StudentAssignmentStatus.IN_PROGRESS,
            StudentAssignmentStatus.SUBMITTED,
            StudentAssignmentStatus.RETURNED
        ];
        return this.getStudentAssignmentsByStatus(existsList);
    }
    get code() {
        return this._code;
    }
    get isAssignment() {
        return true;
    }
    get instructions() {
        return this._instructions;
    }
    get isAutoCreated() {
        return this._isAutoCreated;
    }
    get dueDate() {
        return this._dueDate;
    }
    get status() {
        return this._status;
    }
    get tasks() {
        var _a;
        return (_a = this._tasks) !== null && _a !== void 0 ? _a : [];
    }
    get templateIds() {
        var _a;
        return (_a = this._templateIds) !== null && _a !== void 0 ? _a : [];
    }
    get externalTemplateIds() {
        if (this._isExternalTutorialsDirty) {
            this._externalTemplateIds = this._externalTemplateIdsRemote.filter((templateIdOrTutorial) => {
                try {
                    JSON.parse(templateIdOrTutorial);
                    // If it is an object then it is not a template ID, filter it out
                    return false;
                }
                catch (_a) {
                    return true;
                }
            });
        }
        return this._externalTemplateIds;
    }
    get externalTutorials() {
        if (this._isExternalTutorialsDirty) {
            this._externalTutorials = this._externalTemplateIdsRemote.reduce((acc, templateIdOrTutorial) => {
                try {
                    acc.push(JSON.parse(templateIdOrTutorial));
                }
                catch (_a) {
                    // Not a tutorial, ignore
                }
                return acc;
            }, []);
        }
        return this._externalTutorials;
    }
    get referenceIds() {
        var _a;
        return (_a = this._referenceIds) !== null && _a !== void 0 ? _a : [];
    }
    /**
     * @deprecated Please use @see {templateIds} instead -- will be removed on or after 2024-10-01
     */
    get templates() {
        oneTimeWarning('Assignment.templates', 'Assignment.templates is deprecated and will be removed'
            + ' on or after 2024-10-01. Please use Assignment.templateIds instead.');
        return [];
    }
    get materialsFolder() {
        return this._materialsFolder;
    }
    /**
   * @param repoMetadata {RepositoryMetadata} If passed in, it will be used instead of loading from the service
   * @param appMetadata {any} If passed in, it will be used instead of loading from the service
   *
   * @package -- only for use in SAM.js
   */
    async _load({ repoMetadata, appMetadata, assignmentMetadata, } = {}) {
        // Similar to what Project already has, first load the AdobeAsset so that once we get the assignment metadata we override any relevant
        // properties with the assignment metadata
        await super._load({ repoMetadata, appMetadata });
        // If we have the assignment metadata, then we will use that to update the assignment
        if (!assignmentMetadata) {
            // load the assignment metadata using the assignment service
            assignmentMetadata = await this._assignmentInterface.getAssignment(this.id);
        }
        const session = this._core.classroomContext.assignmentPool.createMutationSession();
        AssignmentUpdater.updateRemoteItem(session, assignmentMetadata);
        session.commit();
    }
    /**
     * Updates the metadata for an assignment. If template IDs are provided then they
     * will be cloned to the assignment's working folder with new asset IDs
     */
    async updateAssignment(data) {
        return this._core.classroomContext.updateAssignment(this.id, data);
    }
    /**
     * Clones the incoming assets to the assignment's working folder with new asset IDs
     * @param templateAssetIds - An array of asset IDs which will be used to copy the asset from the teacher to the assignment
     */
    async setTemplates(templateAssetIds) {
        return this._core.classroomContext.updateAssignment(this.id, { templateIds: templateAssetIds });
    }
    /**
     * Assigns an in-draft assignment to all students in the classroom
     */
    async assignAssignment() {
        return this._core.classroomContext._assignAssignment(this.id);
    }
    /**
     * Discards an assignment -- from discarded state, assignment can either be restored or permanently deleted.
     */
    async discard() {
        if (this.isDiscarded) {
            return;
        }
        await this._assignmentInterface.discardAssignment(this.id);
        const assignmentPool = this._core.classroomContext.assignmentPool;
        const session = assignmentPool.createUpdateSession();
        AssignmentUpdater.updateLocalItem(session, this, { 'repo:state': AssetStateChoices[1] });
        session.commit();
    }
    /**
     * Restores an assignment from the discarded state
     */
    async restore() {
        if (!this.isDiscarded) {
            return;
        }
        await this._assignmentInterface.restoreAssignment(this.id);
        const assignmentPool = this._core.classroomContext.assignmentPool;
        const session = assignmentPool.createUpdateSession();
        AssignmentUpdater.updateLocalItem(session, this, { 'repo:state': AssetStateChoices[0] });
        session.commit();
    }
}
__decorate([
    observable
], Assignment.prototype, "classroomId", null);
__decorate([
    observable
], Assignment.prototype, "studentAssignments", null);
__decorate([
    observable
], Assignment.prototype, "code", null);
__decorate([
    observable
], Assignment.prototype, "isAssignment", null);
__decorate([
    observable
], Assignment.prototype, "instructions", null);
__decorate([
    observable
], Assignment.prototype, "isAutoCreated", null);
__decorate([
    observable
], Assignment.prototype, "dueDate", null);
__decorate([
    observable
], Assignment.prototype, "status", null);
__decorate([
    observable
], Assignment.prototype, "tasks", null);
__decorate([
    observable
], Assignment.prototype, "templateIds", null);
__decorate([
    observable
], Assignment.prototype, "externalTemplateIds", null);
__decorate([
    observable
], Assignment.prototype, "externalTutorials", null);
__decorate([
    observable
], Assignment.prototype, "referenceIds", null);
__decorate([
    observable
], Assignment.prototype, "materialsFolder", null);
const AssignmentUpdater = new ItemUpdater({
    // perhaps we need the following props as well though at the moment we're not too sure about the format of the data
    // _createdBy, _etag(repo:etag), _storageAssignee (storage:assignee),
    // remoteIdProperty(repo:assetId), _repositoryID (repo:repositoryId), _name(repo:name), and _creator
    localItemType: Assignment,
    remoteIdProperty: 'repo:assetId',
    propertyUpdater: new PropertyUpdater({
        // things copied from AdobeAssetUpdater that should stay in sync (TODO: re-use AdobeAssetUpdater or its PropertyUpdater) are below
        _name: { name: 'repo:name', format: 'string' },
        _created: { name: 'repo:createDate', format: 'date' },
        // things TBD from Assignment service are below
        _assignmentLinks: { name: '_links', format: 'object' },
        _studentAssignments: {
            isArray: true,
            name: 'children',
            format: 'object',
            set: (studentAssignments, dest) => {
                const assignment = dest;
                // Clearing the collection in case this updater is called twice, to avoid duplicate student assignments.
                assignment.studentAssignments.length = 0;
                studentAssignments.forEach((studentAssignment) => {
                    const sa = new StudentAssignment({
                        _core: assignment._core,
                        assignmentId: assignment.id,
                        id: studentAssignment['repo:assetId'],
                        links: studentAssignment._links,
                        status: studentAssignment['assignment:submissionStatus'],
                        student: studentAssignment['assignment:assignee'],
                        studentDirectoryId: studentAssignment['repo:assetId'],
                        submission: studentAssignment['assignment:submission'],
                        submissionDate: studentAssignment['assignment:submissionDate']
                            ? new Date(studentAssignment['assignment:submissionDate'])
                            : undefined,
                    });
                    assignment.studentAssignments.push(sa);
                });
                const order = StudentAssignmentOrder.default;
                order.sortItems(assignment.studentAssignments);
            }
        },
        _tasks: { isArray: true, name: 'assignment:tasks' },
        _code: { name: 'assignment:code', format: 'string' },
        _classroomId: { name: 'assignment:classroomId', format: 'string' },
        _createdBy: { name: 'repo:createdBy', format: 'string' },
        _status: { name: 'assignment:status', format: 'string', choices: AssignmentStatusChoices },
        _dueDate: { name: 'assignment:dueDate', format: 'date' },
        _instructions: { name: 'assignment:instructions', format: 'string' },
        _materialsFolder: { name: 'assignment:materialsFolder', format: 'object' },
        _templateIds: { name: 'assignment:templateIds', isArray: true },
        _externalTemplateIdsRemote: {
            name: 'assignment:externalTemplateIds',
            isArray: true,
            set: (externalTemplateIdsAndTutorials, dest) => {
                const assignment = dest;
                assignment['_externalTemplateIdsRemote'] = externalTemplateIdsAndTutorials;
                assignment['_isExternalTutorialsDirty'] = true;
            }
        },
        _referenceIds: { name: 'assignment:referenceIds', isArray: true },
        _path: { name: 'repo:path', format: 'string' },
        _modifiedBy: { name: 'repo:modifiedBy', format: 'string' },
        _assetState: { name: 'repo:state', choices: AssetStateChoices },
        _isAutoCreated: { get: (source) => source['assignment:createdBy'] === 'adobe', format: 'boolean' },
    }),
});

class AssignmentsListLoader {
    constructor(_assignmentPool, _assignmentInterface) {
        this._assignmentPool = _assignmentPool;
        this._assignmentInterface = _assignmentInterface;
    }
    async loadPage(options) {
        return await this._loadPage(options);
    }
    async _loadPage(options, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: https://jira.corp.adobe.com/browse/ACPCEDU-405 add support for paging or remove this
    pageLink) {
        const result = await this._assignmentInterface.getAssignments(Object.assign(Object.assign({}, options), { classroomId: options.filter.classroomId }));
        const mostRecentValidMutation = Date.now(); // can add latency similar to what Project has
        // TBD: we probably want a "mutation" session here instead of "update" session
        // Details can be found in https://git.corp.adobe.com/torq/acpl-model/pull/3902#discussion_r7024503
        const session = this._assignmentPool.createLoaderUpdateSession(mostRecentValidMutation);
        const items = AssignmentUpdater.updateRemoteItems(session, result.children);
        session.commit();
        return {
            items,
            total: result.children.length,
            mostRecentValidMutation,
        };
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class StudentAssignmentsInterface extends InterfaceBase {
    /**
     * @description Calls the return API for a student assignment.
     * @param data The information necessary for calling the return API.
     * @returns A promise that the assignment has been returned.
     */
    async return(data) {
        try {
            const { links, studentAssignmentId, submissionId } = data;
            const url = getURLFromLinks(links, RelType.ASSIGNMENTS_RETURN, { studentAssignmentId, submissionId });
            const response = await this._doPost(url, undefined, undefined, [200]);
            const jsonResponse = await response.json();
            return jsonResponse;
        }
        catch (e) {
            this._core.environment.error(`Error was thrown in StudentAssignmentsInterface.return. ${e}`);
            throw e;
        }
    }
    /**
     * @description Calls the start API for a student assignment.
     * @param data The information necessary for calling the start API.
     * @returns A promise that resolves to the interface for the remote student assignment.
     */
    async start(data) {
        try {
            const { assignmentId, links } = data;
            const url = getURLFromLinks(links, RelType.ASSIGNMENTS_START, { assignmentId });
            const response = await this._doPost(url, undefined, undefined, [200]);
            const jsonResponse = await response.json();
            return jsonResponse;
        }
        catch (e) {
            this._core.environment.error(`Error was thrown in StudentAssignmentsInterface.start. ${e}`);
            throw e;
        }
    }
    /**
     * @description Calls the submit API for a student assignment.
     * @param data The information necessary for calling the submit API.
     * @returns A promise that the assignment has been submitted.
     */
    async submit(data) {
        try {
            const { links, studentAssignmentId, submissionId } = data;
            const url = getURLFromLinks(links, RelType.ASSIGNMENTS_SUBMIT, { studentAssignmentId, submissionId });
            const response = await this._doPost(url, undefined, undefined, [200]);
            const jsonResponse = await response.json();
            return jsonResponse;
        }
        catch (e) {
            this._core.environment.error(`Error was thrown in StudentAssignmentsInterface.submit. ${e}`);
            throw e;
        }
    }
}

const StatusOrderValues = {
    [AssignmentStatus.DRAFT]: 0,
    [AssignmentStatus.ASSIGNED]: 1,
};
const compareAssignmentNames = (a, b) => {
    return compareStrings(a.name || '', b.name || '');
};
const OrderFieldToComparator$7 = {
    name(a, b) {
        return compareAssignmentNames(a, b);
    },
    dueDate(a, b) {
        let value;
        if (!a.dueDate && !b.dueDate) {
            value = 0;
        }
        else if (!a.dueDate) {
            value = 1; // Undefined date should be sorted last in ascending order
        }
        else if (!b.dueDate) {
            value = -1;
        }
        else {
            value = compareValues(a.dueDate.getTime(), b.dueDate.getTime());
        }
        if (value === 0) {
            // Within the same dueDate, sort by name
            return compareAssignmentNames(a, b);
        }
        return value;
    },
    status(a, b) {
        const value = compareValues(StatusOrderValues[a.status], StatusOrderValues[b.status]);
        if (value === 0) {
            // Within the same status, sort by name
            return compareAssignmentNames(a, b);
        }
        return value;
    }
};
class AssignmentOrder extends createItemOrderFactory(OrderFieldToComparator$7) {
}
AssignmentOrder.default = new AssignmentOrder('ascending', 'name');

class AssignmentFilter {
    constructor(options) {
        this.classroomId = options.classroomId || '';
    }
    matches(item) {
        if (!item.isAssignment) {
            return false;
        }
        if (item.classroomId !== this.classroomId) {
            return false;
        }
        if (item.isDiscarded) {
            return false;
        }
        return true;
    }
    // will add more logics in next iterations
    getRelationToFilter() {
        return 'unknown';
    }
    get hash() {
        return `classroomId=${this.classroomId}`;
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// NOTE: This file will be refactored in ACPCEDU-1418, current version is to unblock UI work.
/**
 * Represents a file that is part of a student assignment.
 */
class StudentAssignmentFile {
    constructor(core, adobeAsset) {
        this._adobeAsset = adobeAsset;
        this._core = core;
    }
    get id() {
        return this._adobeAsset.id;
    }
    get 'repo:assetId'() {
        return this._adobeAsset.id;
    }
    get sourceAssets() {
        return this._adobeAsset.appMetadata.data['assignment:sourceAssets'];
    }
    get sourceTasks() {
        return this._adobeAsset.appMetadata.data['assignment:sourceTasks'];
    }
    get sourceTutorials() {
        return this._adobeAsset.appMetadata.data['assignment:sourceTutorials'];
    }
    get mediaType() {
        return this._adobeAsset.mediaType;
    }
    get adobeAsset() {
        return this._adobeAsset;
    }
    async update({ sourceAssets, sourceTasks, sourceTutorials }) {
        if (!sourceAssets && !sourceTasks && !sourceTutorials) {
            throw Error('Incorrect parameters: sourceAssetId, sourceTask, or sourceTutorials parameter required');
        }
        await this._core.classroomContext.updateStudentAssignmentFile(this, {
            'assignment:sourceAssets': sourceAssets,
            'assignment:sourceTasks': sourceTasks,
            'assignment:sourceTutorials': sourceTutorials
        });
        return this;
    }
    async patchAppMetadata(patches) {
        return this._adobeAsset.patchAppMetadata(patches);
    }
}
const StudentAssignmentFileUpdater = new ItemUpdater({
    localItemType: AdobeAsset,
    remoteIdProperty: 'id',
    propertyUpdater: new PropertyUpdater({})
});

/** Returns true if the object has a key. Can be used as a replacement to `Object.hasOwnProperty` and `Object.hasOwn`. */
const hasProperty = (obj, key) => {
    return Object.getOwnPropertyDescriptor(obj, key) !== undefined;
};

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class ClassroomContext {
    constructor(_core, _assetPool) {
        this._core = _core;
        this._assetPool = _assetPool;
        this._assignmentInterface = new AssignmentsInterface(this._core);
        this._classroomsInterface = new ClassroomsInterface(this._core);
        this._dovetailInterface = new DovetailInterface(this._core);
        this._studentAssignmentsInterface = new StudentAssignmentsInterface(this._core);
        /**
         * @package -- for SAM.js internal use only
         * Follow up on this in ACPCEDU-568, if necessary
         * [ACPCEDU-1805] Remove with dovetail support
         */
        this._dovetailClassroomPool = new ItemPool(this._core, 'classroom');
        /**
         * @package -- for SAM.js internal use only
         */
        this._classroomPool = new ItemPool(this._core, 'classroom');
    }
    get assignmentPool() {
        return this._assetPool;
    }
    /**
     * @description Gets a classroom with the given class ID.
     *
     * @param id comes from classId or classCode
     * @param {boolean} [useNewClassroomsService=false] Whether to use the new classrooms service or the dovetail service
     * @returns a promise that resolves to the Classroom
     */
    async getClassroomById(id, useNewClassroomsService = false) {
        const classroomInterface = useNewClassroomsService ? this._classroomsInterface : this._dovetailInterface;
        const pool = useNewClassroomsService ? this._classroomPool : this._dovetailClassroomPool;
        let classroom = pool.getLocalItemById(id);
        if (!classroom) {
            // Note that we don't have a local classroom, so we don't have to worry about eventual consistency on this item.
            const response = await classroomInterface.getClassroom(id);
            const session = pool.createUpdateSession();
            classroom = ClassroomUpdater.updateRemoteItem(session, useNewClassroomsService ? response : response.classroom);
            session.commit();
        }
        return classroom;
    }
    /**
     * @description Gets a classroom with the given class code. Typically, using getClassroomById is preferred.
     *
     * @param id comes from classCode
     * @param {boolean} [useNewClassroomsService=false] Whether to use the new classrooms service or the dovetail service
     * @returns a promise that resolves to the Classroom
     */
    async getClassroomByCode(classCode, useNewClassroomsService = false) {
        if (useNewClassroomsService) {
            // The interface passes to ID or CODE through the same endpoint and param.
            return this.getClassroomById(classCode, true);
        }
        let classroom = this._dovetailClassroomPool.toArray().find(element => element['_code'] === classCode);
        if (!classroom) {
            // Note that we don't have a local classroom, so we don't have to worry about eventual consistency on this item.
            const response = await this._dovetailInterface.getClassroomByCode(classCode);
            const session = this._dovetailClassroomPool.createUpdateSession();
            classroom = ClassroomUpdater.updateRemoteItem(session, response.classroom);
            session.commit();
        }
        return classroom;
    }
    /**
     * [ACPCEDU-1805] This function can be merged back into getClassrooms once dovetail is retired.
     * It is only necessary so that both each service can be memoized separately.
     */
    _getClassroomsFromNewService() {
        return new ItemSource(this._classroomPool, [new ClassroomListLoader(this._classroomPool, this._classroomsInterface)]);
    }
    /**
     * [ACPCEDU-1805] Remove with dovetail support
     */
    _getClassroomsFromDovetail() {
        return new ItemSource(this._dovetailClassroomPool, [new DovetailClassroomLoader(this._dovetailClassroomPool, this._dovetailInterface)]);
    }
    /**
     * @description Gets all classrooms a user has access to.
     *
     * @param {boolean} [useNewClassroomsService=false] Whether to use the new classrooms service or the dovetail service
     *
     * @example
     * const classroomContext = samCore.classroomContext;
     * const assignmentsSource = classroomContext.getClassrooms();
     */
    getClassrooms(useNewClassroomsService = false) {
        return useNewClassroomsService ? this._getClassroomsFromNewService() : this._getClassroomsFromDovetail();
    }
    /**
     * @example
     * const classroomContext = samCore.classroomContext;
     * const assignmentsSource = classroomContext.getAssignments();
     *
     * Gets all assignments for the classroom
     */
    getAssignments() {
        const loader = new AssignmentsListLoader(this._assetPool, this._assignmentInterface);
        return new ItemSource(this._assetPool, [loader]);
    }
    cleanAssignmentName(name) {
        return name.trimEnd();
    }
    /**
     * Creates a new Assignment
     * @param data creation data (e.g. title, instructions, etc)
     *
     * @returns a promise that resolves to the assignment recently and successfully created
     */
    async createAssignment(data) {
        const trimmedName = this.cleanAssignmentName(data.name);
        if (!validatePlatformRepoName(trimmedName)) {
            // Validate the name here to avoid resolving the asset if the name is invalid.
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Invalid name provided for the creation operation.');
        }
        // We will not create the assignment with the incoming template IDs
        // Instead updateAssignment() will clone them and save new asset IDs to this assignment
        const { templateIds, references } = data, createData = __rest(data, ["templateIds", "references"]);
        createData.name = trimmedName;
        // If status is not passed then we must default to draft
        if (!hasProperty(createData, 'status')) {
            createData.status = AssignmentStatus.DRAFT;
        }
        const response = await this._assignmentInterface.createAssignment(createData);
        const session = this._assetPool.createMutationSession();
        const assignment = AssignmentUpdater.updateRemoteItem(session, response);
        session.commit();
        const promises = [];
        const incomingTemplateIds = templateIds !== null && templateIds !== void 0 ? templateIds : [];
        const incomingReferences = references !== null && references !== void 0 ? references : [];
        const materialsUpdateData = {};
        if (incomingTemplateIds.length > 0) {
            materialsUpdateData.templateIds = incomingTemplateIds;
        }
        if (incomingReferences.length > 0) {
            materialsUpdateData.references = incomingReferences;
        }
        // Only update with materials that need copying. Other data is able to be assigned on creation
        if (Object.keys(materialsUpdateData).length > 0) {
            promises.push(this.updateAssignment(assignment.id, materialsUpdateData));
        }
        if (createData.status === AssignmentStatus.ASSIGNED) {
            promises.push(this._assignAssignment(assignment.id));
        }
        await Promise.all(promises);
        return assignment;
    }
    /**
     * Assigns a draft assignment to each member of the classroom
     * @param assignmentId ID of assignment to assign
     * @package Only for use in SAM.js
     */
    async _assignAssignment(assignmentId) {
        const assignment = this._assetPool.getLocalItemById(assignmentId);
        if (!assignment) {
            throw SAMError.create(SAMErrorCode.NOT_FOUND, `Could not find assignment to assign with ID of "${assignmentId}"`);
        }
        await this._assignmentInterface.assignAssignment(assignmentId);
        const updateSession = this.assignmentPool.createUpdateSession();
        AssignmentUpdater.updateLocalItem(updateSession, assignment, { 'assignment:status': AssignmentStatus.ASSIGNED });
        updateSession.commit();
    }
    /**
     * Gets a Assignment with the given ID.
     *
     * @note in the near future, we can call to check the access to this assignment given by an ID.
     *
     * @param id ID of the Assignment
     * @returns a promise that resolves to the Assignment with the given ID
     */
    async getAssignmentByID(id) {
        let assignment = this._assetPool.getLocalItemById(id);
        if (assignment) {
            return assignment;
        }
        const assignmentMetadata = await this._assignmentInterface.getAssignment(id);
        const session = this._assetPool.createMutationSession();
        assignment = AssignmentUpdater.updateRemoteItem(session, assignmentMetadata);
        session.commit();
        return assignment;
    }
    /**
     * This function gets an assignment by its code.
     *
     * @param code Code used to fetch assignment
     * @returns A promise that resolves to the assignment with the given code
     */
    async getAssignmentByCode(code) {
        const assignmentMetadata = await this._assignmentInterface.getAssignmentByCode(code);
        // If the assignment is already loaded, return it. Need to make API call first since we don't have the ID.
        let assignment = this._assetPool.getLocalItemById(assignmentMetadata['repo:assetId']);
        if (assignment) {
            return assignment;
        }
        const session = this._assetPool.createMutationSession();
        assignment = AssignmentUpdater.updateRemoteItem(session, assignmentMetadata);
        session.commit();
        return assignment;
    }
    /**
     * Discards the given assignment
     * This assignment will be eligible to be restored or permanentlyDeleted from this state
     * @param assignment Assignment to be discarded
     * @package Only for use in SAM.js
     */
    async _discardAssignment(assignment) {
        try {
            await assignment.discard();
        }
        catch (error) {
            const e = error;
            throw e;
        }
    }
    /**
     * Restores the given assignment
     * @param assignment Assignment to be restored
     * @package Only for use in SAM.js
     */
    async _restoreAssignment(assignment) {
        try {
            await assignment.restore();
        }
        catch (error) {
            const e = error;
            throw e;
        }
    }
    /**
     * Returns a StudentAssignment.
     * @param data The data necessary to return the assignment.
     * @returns a promise that resolves to the partial StudentAssignment object that was returned.
     * @package Only for use in SAM.js
     */
    async _returnAssignment(data) {
        return this._studentAssignmentsInterface.return(data);
    }
    /**
     * Starts a StudentAssignment.
     * @param data The data necessary to start the assignment.
     * @returns a promise that resolves to the partial StudentAssignment object that was started.
     * @package Only for use in SAM.js
     */
    async _startAssignment(data) {
        return this._studentAssignmentsInterface.start(data);
    }
    /**
     * Submits a StudentAssignment.
     * @param data The data necessary to submit the assignment.
     * @returns a promise that resolves to the partial StudentAssignment object that was submitted.
     * @package Only for use in SAM.js
     */
    async _submitAssignment(data) {
        return this._studentAssignmentsInterface.submit(data);
    }
    /**
     * Function to update a StudentAssignmentFile
     *
     * @param studentAssignmentFile StudentAssignmentFile that will be updated. Either the object or the ID can be passed.
     * @param data Data to update the file with. Note that sourceAssets OR sourceTasks are allowed, but not both in one update call.
     * @returns Updated StudentAssignmentFile
     * @experimental This function may change and is a work in progress
     */
    async updateStudentAssignmentFile(studentAssignmentFile, data) {
        var _a, _b, _c;
        (_a = data['assignment:sourceAssets']) !== null && _a !== void 0 ? _a : (data['assignment:sourceAssets'] = []);
        (_b = data['assignment:sourceTasks']) !== null && _b !== void 0 ? _b : (data['assignment:sourceTasks'] = []);
        (_c = data['assignment:sourceTutorials']) !== null && _c !== void 0 ? _c : (data['assignment:sourceTutorials'] = []);
        const patchData = [
            {
                op: 'add',
                path: '/assignment:sourceAssets',
                value: data['assignment:sourceAssets']
            },
            {
                op: 'add',
                path: '/assignment:sourceTasks',
                value: data['assignment:sourceTasks']
            },
            {
                op: 'add',
                path: '/assignment:sourceTutorials',
                value: data['assignment:sourceTutorials']
            },
        ];
        const assetToPatch = typeof studentAssignmentFile === 'string'
            ? await this._core.hostAssetContext.getAssetWithCloudID(studentAssignmentFile)
            : studentAssignmentFile;
        await assetToPatch.patchAppMetadata(patchData);
        return assetToPatch;
    }
    /**
     * Determine if the given name is valid for creating an assignment
     * @throws SAMError if the name is invalid - The error messages are in English and would need
     *         to be localized if exposed through the UI.
     */
    async validateAssignmentNameForCreate(name, classroomId) {
        await this._validateAssignmentName(name, classroomId);
    }
    /**
     * Determine if the given name is valid when updating an assignment
     * @throws SAMError if the name is invalid - The error messages are in English and would need
     *         to be localized if exposed through the UI.
     */
    async validateAssignmentNameForUpdate(name, classroomId, assignmentId) {
        await this._validateAssignmentName(name, classroomId, assignmentId);
    }
    /**
     * Determine if the given name is valid
     *  - Name cannot contain only whitespace
     *  - Name must adhere to asset naming rules
     *  - Name cannot end in a period
     *  - The name must be unique within the classroom
     * @throws SAMError if the name is invalid - The error messages are in English and would need
     *         to be localized if exposed through the UI.
     */
    async _validateAssignmentName(name, classroomId, assignmentId) {
        const trimmedName = name.trim();
        // Name cannot have leading or trailing whitespace
        if (trimmedName.length === 0) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'The assignment name cannot be blank.');
        }
        // Name must adhere to asset naming rules
        if (!validatePlatformRepoName(trimmedName)) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'The assignment name is an invalid platform repo name.');
        }
        // Name cannot end in a period
        if (trimmedName.endsWith('.')) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'The assignment name cannot end with a period.');
        }
        // Name must be unique within the classroom
        if (!classroomId) {
            // Assignment does not map to a specific class yet so the name is valid for this case
            return;
        }
        const assignments = this.getAssignments();
        const assignmentList = assignments.getList(AssignmentOrder.default, new AssignmentFilter({ classroomId }), {});
        const locale = this._core.environment.getValue('LOCALE');
        const matchingAssignment = assignmentList.items.find(assignment => assignment.name.toLocaleLowerCase(locale) === trimmedName.toLocaleLowerCase(locale));
        if (matchingAssignment) {
            if (!assignmentId || matchingAssignment.id !== assignmentId) {
                throw SAMError.create(SAMErrorCode.CONFLICT, 'The assignment name must be unique within the classroom.');
            }
        }
    }
    /**
     * Updates and Assignment's name
     * @param assignmentId The assignment ID
     * @param newName The new name
     * @returns
     */
    async _renameAssignment(assignmentId, newName) {
        const assignment = await this.getAssignmentByID(assignmentId);
        try {
            // repositoryID must be present for the rename operation to work
            if (!assignment.repositoryID) {
                await assignment._load();
            }
            return assignment.rename(newName);
        }
        catch (error) {
            if (error instanceof SAMError) {
                throw error;
            }
            throw SAMError.create(SAMErrorCode.INTERNAL_ERROR, `Could not rename asset "${assignmentId}"`);
        }
    }
    /**
     * Updates an Assignment's data and copies any given asset to the materials folder of the assignment
     * @param id The assignmentId of the Assignment to update
     * @param data The data to update the Assignment with
     * @returns
     */
    async updateAssignment(assignmentId, data) {
        // Extract references and templateIds because they should not be updated
        // unless _getUpdatedAssignmentMaterials determines that they should be
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const otherAssignmentData = __rest(data, ["references", "templateIds"]);
        const materialUpdates = await this._getUpdatedAssignmentMaterials(assignmentId, data);
        const patchData = otherAssignmentData;
        if (materialUpdates.shouldUpdateTemplates) {
            patchData.templateIds = materialUpdates.updatedTemplateIds;
        }
        if (materialUpdates.shouldUpdateReferences) {
            patchData.referenceIds = materialUpdates.updatedReferenceIds;
        }
        const promises = [];
        const shouldRename = hasProperty(data, 'name');
        // The update operation can happen in parallel with the rename operation
        if (shouldRename) {
            patchData.name = this.cleanAssignmentName(data.name);
            promises.push(this._renameAssignment(assignmentId, patchData.name));
        }
        promises.push(this._updateAssignmentMetadata(assignmentId, patchData));
        await Promise.all(promises);
        if (data.status === AssignmentStatus.ASSIGNED) {
            await this._assignAssignment(assignmentId);
        }
        // Cleaning up after setting the new templates in case the copy OR update fails
        // Note: If the discard operation fails on any asset then it will be orphaned forever
        // TODO: Determine how we can do a proper cleanup of any orphaned assets, not just on update
        this._discardAssets(materialUpdates.assetsToDiscard);
    }
    /**
     * Updates an Assignment's data
     * @param assignmentId The assignmentId of the Assignment to update
     * @param data The data to update the Assignment with
     * @returns
     */
    async _updateAssignmentMetadata(assignmentId, data) {
        if (Object.keys(data).length === 0) {
            // Nothing to update
            return;
        }
        const mappedData = Object.entries(assignmentRemoteFieldMapping).reduce((acc, [localKey, remoteKey]) => {
            const hasKey = hasProperty(data, localKey);
            if (hasKey) {
                const value = data[localKey];
                if (value instanceof Date) {
                    // @ts-expect-error
                    acc[remoteKey] = value.toISOString();
                }
                else {
                    // @ts-expect-error
                    acc[remoteKey] = value;
                }
            }
            return acc;
        }, { 'repo:assetId': assignmentId });
        await this._assignmentInterface.patchAssignment(assignmentId, mappedData);
        const session = this._assetPool.createMutationSession();
        AssignmentUpdater.updateRemoteItem(session, mappedData);
        session.commit();
    }
    /**
     * Gets the asset representing the materials folder of an assignment
     * @param assignmentId
     * @returns
     */
    async _getAssignmentMaterialsFolderAsset(assignmentId) {
        var _a;
        const assignment = await this.getAssignmentByID(assignmentId);
        const materialsFolder = (_a = assignment.materialsFolder) === null || _a === void 0 ? void 0 : _a['repo:assetId'];
        if (!materialsFolder) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, 'Assignment does not have a materials folder.');
        }
        // TODO: We really don't need to fetch the whole AdobeAsset here. The copy() method only needs
        // repoPath and repositoryID to work. We should overload the copy() method to accept just those.
        // https://wulff-pack.slack.com/archives/C028K2G4J4T/p1705439587609229
        const materialsFolderAsset = await this._core.hostAssetContext.getAssetWithCloudID(materialsFolder);
        if (!materialsFolderAsset) {
            throw SAMError.create(SAMErrorCode.BAD_REQUEST, `Could not find materials folder with ID of "${materialsFolder}"`);
        }
        return materialsFolderAsset;
    }
    /** Gets a list of assets by ID */
    async _getAssets(assetIds) {
        return (await Promise.allSettled(assetIds.map(assetId => this._core.hostAssetContext.getAssetWithCloudID(assetId)))).map((result, index) => {
            if (result.status === 'fulfilled') {
                return result.value;
            }
            throw SAMError.wrapErrorIfNeeded(SAMErrorCode.INTERNAL_ERROR, `Failed to get asset with ID of "${assetIds[index]}"`, result.reason);
        });
    }
    /** Discards a list of assets by ID */
    async _discardAssets(assetIds) {
        if (assetIds.length === 0) {
            return;
        }
        const assets = await this._getAssets(assetIds);
        await Promise.allSettled(assets.map((asset) => asset.discard()));
    }
    /**
     * Copies the given assets to the materials folder of the assignment
     * @param assignmentId The assignment ID to copy to
     * @param assetIds The assets to copy
     * @returns {string[]} The asset IDs of the copied assets
     */
    async _copyAssetToMaterialsFolder(assignmentId, assetIds) {
        if (assetIds.length === 0) {
            return [];
        }
        const materialsFolderAsset = await this._getAssignmentMaterialsFolderAsset(assignmentId);
        const assetsToCopy = await this._getAssets(assetIds);
        const assignment = await this.getAssignmentByID(assignmentId);
        const clonedAssets = (await Promise.allSettled(assetsToCopy.map(asset => asset.copy(materialsFolderAsset, `${assignment.name} ${this._getFormattedCreatedDateHelper({ includeFormattedDate: true })}`)))).map((result, index) => {
            if (result.status === 'fulfilled') {
                return result.value;
            }
            throw SAMError.wrapErrorIfNeeded(SAMErrorCode.INTERNAL_ERROR, `Failed to copy asset with ID of "${assetIds[index]}"`, result.reason);
        });
        return clonedAssets.map(asset => asset.id);
    }
    // Helper function to format the created date for the asset name
    _getFormattedCreatedDateHelper({ includeFormattedDate }) {
        const locale = this._core.environment.getValue('LOCALE');
        const dateTimeFormat = includeFormattedDate ? {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            fractionalSecondDigits: 3,
            hourCycle: 'h24'
        } : {
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
        };
        // Need to replace to convert from colons to periods, example: 14:28:00 to 14.28.00
        return new Intl.DateTimeFormat(locale, dateTimeFormat).format(new Date).replace(/[/:]/g, '.');
    }
    /** Determines which files need to be kept, cloned, and discarded when updating an assignment with new resources. */
    async _determineAssetDifferences(assignmentId, field, incomingAssetIds) {
        const assignment = await this.getAssignmentByID(assignmentId);
        if (!incomingAssetIds) {
            return {
                assetsToCopy: [],
                assetsToKeep: [],
                assetsToDiscard: assignment[field],
            };
        }
        const assetsToDiscard = assignment[field].filter(templateId => !incomingAssetIds.includes(templateId));
        const assetsToCopy = incomingAssetIds.filter(templateId => !assignment[field].includes(templateId));
        const assetsToKeep = assignment[field].filter(templateId => incomingAssetIds.includes(templateId));
        return {
            assetsToCopy,
            assetsToKeep,
            assetsToDiscard,
        };
    }
    /**
     * Based on incoming update data this will determine which assets need to be kept, cloned, and discarded.
     * Assets to be kept are returned as-is.
     * Assets to be cloned are copied to the materials folder of the assignment, and returned along with the kept asset IDs.
     * Assets to be discarded are returned and are expected to be discarded after calling this function.
     */
    async _getUpdatedAssignmentMaterials(assignmentId, data) {
        var _a, _b;
        const shouldUpdateTemplates = hasProperty(data, 'templateIds');
        const shouldUpdateReferences = hasProperty(data, 'references');
        const result = {
            updatedTemplateIds: [],
            updatedReferenceIds: [],
            assetsToDiscard: [],
            shouldUpdateTemplates,
            shouldUpdateReferences,
        };
        const assetsToCopy = [];
        let splitIdx = 0;
        if (shouldUpdateTemplates) {
            const templateDifferences = await this._determineAssetDifferences(assignmentId, 'templateIds', data.templateIds);
            result.assetsToDiscard.push(...templateDifferences.assetsToDiscard);
            assetsToCopy.push(...templateDifferences.assetsToCopy);
            result.updatedTemplateIds.push(...templateDifferences.assetsToKeep);
            splitIdx = assetsToCopy.length;
        }
        if (shouldUpdateReferences) {
            // Split the incoming references into IDs and ExternalAssets
            const { referenceIds, referencesToImport } = (_b = (_a = data.references) === null || _a === void 0 ? void 0 : _a.reduce((acc, cur) => {
                if (typeof cur === 'string') {
                    acc.referenceIds.push(cur);
                }
                else {
                    acc.referencesToImport.push(cur);
                }
                return acc;
            }, {
                referenceIds: [],
                referencesToImport: [],
            })) !== null && _b !== void 0 ? _b : { referenceIds: [], referencesToImport: [] };
            const referenceDifferences = await this._determineAssetDifferences(assignmentId, 'referenceIds', referenceIds);
            result.assetsToDiscard.push(...referenceDifferences.assetsToDiscard);
            assetsToCopy.push(...referenceDifferences.assetsToCopy);
            result.updatedReferenceIds.push(...referenceDifferences.assetsToKeep);
            // If there are any ExternalAssets to import then we need to import them into the materials folder
            // and store the new asset IDs in the references field
            if (referencesToImport.length > 0) {
                const importedReferenceAssetIds = [];
                const importResults = await this.importNewReferencesForAssignment(assignmentId, referencesToImport);
                importResults.forEach((result) => {
                    if (result.status === 'fulfilled') {
                        importedReferenceAssetIds.push(result.value.id);
                    }
                    // TODO: Need to handle the case where the import fails
                });
                result.updatedReferenceIds.push(...importedReferenceAssetIds);
            }
        }
        // Copy all assets at once to avoid multiple API calls
        // Split the result into templates and references
        const clonedAssets = (await this._copyAssetToMaterialsFolder(assignmentId, assetsToCopy));
        const [clonedTemplates, clonedReferences] = [clonedAssets.slice(0, splitIdx), clonedAssets.slice(splitIdx)];
        result.updatedTemplateIds.push(...clonedTemplates);
        result.updatedReferenceIds.push(...clonedReferences);
        return result;
    }
    /**
     * Imports each ExternalAsset as a file into the assignment's materials folder
     * @returns The AdobeAsset objects of the imported files
     */
    async importNewReferencesForAssignment(assignmentId, references) {
        const materialsFolder = await this._getAssignmentMaterialsFolderAsset(assignmentId);
        return Promise.allSettled(references.map(async (reference) => materialsFolder.importFile(reference.content, reference.name, reference.mediaType, true)));
    }
    /**
     * Function to get a list of StudentAssignmentFiles for a given StudentDirectoryId
     *
     * @param {} param Contains paramaters for StudentDirectoryId and order for the list
     * @returns List of StudentAssignmentFiles
     * @deprecated Fast follow to improve this function in ACPCEDU-1448
     * @experimental This function may change and is a work in progress
     */
    async getStudentAssignmentFiles({ studentDirectoryId, order }) {
        var _a;
        const asset = await this._core.hostAssetContext.getAssetWithCloudID(studentDirectoryId);
        const itemList = (_a = asset.children) === null || _a === void 0 ? void 0 : _a.getList(order || AdobeAssetOrder.default);
        if (!itemList) {
            return Promise.resolve([]);
        }
        const mapItems = async (items) => {
            return items.map((item) => new StudentAssignmentFile(this._core, item));
        };
        if (itemList.status === AssetListStatus.ReadyFullyLoaded) {
            return mapItems(itemList.items);
        }
        return new Promise((resolve) => {
            const handleFullyLoaded = async () => {
                const assignmentFiles = await mapItems(itemList.items);
                const session = this._assetPool.createMutationSession();
                // Update the local cache with the AdobeAsset items that are our files.
                StudentAssignmentFileUpdater.updateRemoteItems(session, itemList.items);
                session.commit();
                itemList.off(LIST_FULLY_LOADED, handleFullyLoaded);
                resolve(assignmentFiles);
            };
            itemList.on(LIST_FULLY_LOADED, handleFullyLoaded);
        });
    }
    /**
     * Refreshes the classroom codes for the given classroom IDs
     *
     * @param classIds list of classroomIds to refresh
     */
    async refreshClassroomCodes(classIds) {
        return await Promise.allSettled(classIds.map(id => this._dovetailInterface.refreshClassroomCode(id)));
    }
    /** Deletes one or many classrooms */
    deleteClassrooms(classIds) {
        if (Array.isArray(classIds)) {
            return this._dovetailInterface.deleteClassrooms(classIds);
        }
        return this._dovetailInterface.deleteClassroom(classIds);
    }
}
__decorate([
    synchronized
], ClassroomContext.prototype, "getClassroomById", null);
__decorate([
    synchronized
], ClassroomContext.prototype, "getClassroomByCode", null);
__decorate([
    memoize
], ClassroomContext.prototype, "_getClassroomsFromNewService", null);
__decorate([
    memoize
], ClassroomContext.prototype, "_getClassroomsFromDovetail", null);
__decorate([
    memoize
], ClassroomContext.prototype, "getAssignments", null);
__decorate([
    synchronized
], ClassroomContext.prototype, "createAssignment", null);
__decorate([
    synchronized
], ClassroomContext.prototype, "getAssignmentByID", null);
__decorate([
    synchronized
], ClassroomContext.prototype, "getAssignmentByCode", null);
__decorate([
    synchronized
], ClassroomContext.prototype, "_discardAssignment", null);
__decorate([
    synchronized
], ClassroomContext.prototype, "_restoreAssignment", null);
__decorate([
    synchronized
], ClassroomContext.prototype, "_returnAssignment", null);
__decorate([
    synchronized
], ClassroomContext.prototype, "_startAssignment", null);
__decorate([
    synchronized
], ClassroomContext.prototype, "_submitAssignment", null);
__decorate([
    synchronized
], ClassroomContext.prototype, "_validateAssignmentName", null);
__decorate([
    synchronized
], ClassroomContext.prototype, "_renameAssignment", null);
__decorate([
    synchronized
], ClassroomContext.prototype, "updateAssignment", null);
__decorate([
    synchronized
], ClassroomContext.prototype, "_updateAssignmentMetadata", null);
__decorate([
    synchronized
], ClassroomContext.prototype, "refreshClassroomCodes", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const FRAMEIO_AUTHORIZATION_CHANGE = 'frameioAuthorizationChange';
class FrameioAuthorizationChangeEvent extends SAMEvent {
    constructor() {
        super(FRAMEIO_AUTHORIZATION_CHANGE);
    }
}
const SharedFrameioAuthorizationChangeEvent = new FrameioAuthorizationChangeEvent();

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const FRAMEIO_REVIEW_LINKS_CHANGE = 'frameioReviewLinksChange';
class FrameioReviewLinksChangeEvent extends SAMEvent {
    constructor() {
        super(FRAMEIO_REVIEW_LINKS_CHANGE);
    }
}
const SharedFrameioReviewLinksChangeEvent = new FrameioReviewLinksChangeEvent();

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// Consolidate Frame.io links used by FrameioInterface into
// templated urls
const FRAMEIO_API_BASE = 'https://api.frame.io';
const FRAMEIO_LINKS = {
    ME: `${FRAMEIO_API_BASE}/v2/me`,
    ACCOUNT: `${FRAMEIO_API_BASE}/v2/accounts/{accountId}`,
    ACCOUNTS: `${FRAMEIO_API_BASE}/v2/accounts?join=collaborator`,
    TEAMS: `${FRAMEIO_API_BASE}/v2/accounts/{accountId}/teams?include=user_role&role=member`,
    TEAM: `${FRAMEIO_API_BASE}/v2/teams/{teamId}?include=account`,
    PROJECT: `${FRAMEIO_API_BASE}/v2/projects/{projectId}?include=string`,
    PROJECTS: `${FRAMEIO_API_BASE}/v2/teams/{teamId}/projects?filter[archived]`,
    SHAREDPROJECTS: `${FRAMEIO_API_BASE}/v2/projects/shared?filter[account_id]={accountId}`,
    REVIEWS: `${FRAMEIO_API_BASE}/v2/projects/{projectId}/review_links`,
    REVIEW: `${FRAMEIO_API_BASE}/v2/review_links/{reviewLinkId}`,
    ITEMCREATE: `${FRAMEIO_API_BASE}/v2/review_links/{reviewLinkId}/assets`,
    ASSETS: `${FRAMEIO_API_BASE}/v2/assets/{assetId}/children?include=cover_asset`,
    ASSET: `${FRAMEIO_API_BASE}/v2/assets/{assetId}?type=folder&include=cover_asset`,
    CREATEASSET: `${FRAMEIO_API_BASE}/v2/assets/{parentId}/children`,
    VERSION: `${FRAMEIO_API_BASE}/v2/assets/{assetId}/version`,
    CREATECOMMENT: `${FRAMEIO_API_BASE}/v2/assets/{assetId}/comments`,
    GETCOMMENT: `${FRAMEIO_API_BASE}/v2/comments/{commentId}?include=string`,
    GETCOMMENTS: `${FRAMEIO_API_BASE}/v2/assets/{assetId}/comments?include=string`,
    IMPRESSIONS: `${FRAMEIO_API_BASE}/v2/comments/{commentId}/impressions?include=string`,
    COMMENT: `${FRAMEIO_API_BASE}/v2/comments/{commentId}`,
    REPLY: `${FRAMEIO_API_BASE}/v2/comments/{commentId}/replies`,
    GETREPLIES: `${FRAMEIO_API_BASE}/v2/comments/{commentId}/replies?include=replies`,
    LIKE: `${FRAMEIO_API_BASE}/v2/comments/{commentId}/like`,
    COMPLETE: `${FRAMEIO_API_BASE}/v2/comments/{commentId}/complete`,
    FOLDERTREE: `${FRAMEIO_API_BASE}/v2/projects/{projectId}/folder_tree`,
    ANCESTORS: `${FRAMEIO_API_BASE}/v2/assets/{assetId}/ancestors`,
    ADOBE_SUSI_USER: `${FRAMEIO_API_BASE}/v2/auth/adobe/method`,
    ADOBE_SUSI: `${FRAMEIO_API_BASE}/v2/auth/adobe/callback`
};
function fillURLTemplate$1(url, params) {
    // such as fillURLTemplate('v2/accounts/{accountId}', { "accountId": "id-for-account"})
    const template = UriTemplate(url);
    return template.fill(params);
}
var IFrameioAuthenticationStatus;
(function (IFrameioAuthenticationStatus) {
    IFrameioAuthenticationStatus[IFrameioAuthenticationStatus["authLoggedOut"] = 0] = "authLoggedOut";
    IFrameioAuthenticationStatus[IFrameioAuthenticationStatus["authLoggedIn"] = 1] = "authLoggedIn";
    IFrameioAuthenticationStatus[IFrameioAuthenticationStatus["authExpired"] = 2] = "authExpired";
})(IFrameioAuthenticationStatus || (IFrameioAuthenticationStatus = {}));
// Values for credential caching.
const frameioCredentialsCacheKey = 'FRAMEIO_CREDENTIALS_';
// Local cache expires in 14 days (in ms)
const frameioCredentialsTTL = millisecondsPerHour * (24 * 14);
// Refresh token expiration (in second)
const frameioRefreshTokenDuration = secondsPerHour * (24 * 15);
// An Interface, in this context, is the RESTful interface implemented
// by a service, so most methods of FrameioInterface wrap an HTTP
// Request to Frame.io, and all requests we make to Frame.io should
// go through some method of FrameioInterface.
class FrameioInterface extends InterfaceBase {
    constructor(ctx) {
        super(ctx._core);
        this._frameioContext = ctx;
        // Set the Frame.io client ID used in request analytics.
        const { PRODUCT_ID } = this._core.environment.getValue('HOST_PRODUCT_DATA');
        const frameioClient = 'adobe+' + PRODUCT_ID;
        this._frameioClientId = frameioClient.toLowerCase();
    }
    /**
     * We use native code to login to Frame.io,
     * and authentication is specific to their service,
     * so we'll only access nativeContext methods
     * from FrameioInterface.
     */
    //----------------------------------------------------------------
    // Limit FrameioInterface to be the only client of IFrameioNativeContext,
    // the interface implementing the native support for frame.io.
    get nativeContext() {
        // @ts-ignore
        const acplModel = (typeof window !== 'undefined') && window.acplModel;
        if (!this._nativeContext && acplModel) {
            const workflows = acplModel.Workflows;
            if (workflows.createShareForReviewWorkflow) {
                const shareForReviewWorkflow = workflows.createShareForReviewWorkflow(acplModel.applicationAssetContext);
                this._nativeContext = shareForReviewWorkflow.getFrameioAssetContext();
                if (this._nativeContext) {
                    const self = this;
                    this._nativeContext.addEventListener(FRAMEIO_REVIEW_LINKS_CHANGE, (json) => {
                        self._frameioContext._handleReviewLinksChangeEvent(json);
                    });
                    this._nativeContext.addEventListener(FRAMEIO_AUTHORIZATION_CHANGE, async () => {
                        const nativeAuthInfo = self.nativeContext.authenticationInfo();
                        if (nativeAuthInfo.frameioToken) {
                            // logged in, or refreshed
                            await self._cacheCredentials(JSON.stringify(nativeAuthInfo));
                            // when the id changes, the context will clear user data
                            self._frameioContext._updateCurrentUserId(nativeAuthInfo.frameioUserId);
                        }
                        else {
                            // logged out
                            await self._core.environment.cache.deleteItem(self._cachedCredentialsId());
                            // clear user specific information.
                            self._frameioContext._updateCurrentUserId(undefined);
                        }
                        self._frameioContext.dispatch(SharedFrameioAuthorizationChangeEvent);
                    });
                }
            }
            else {
                oneTimeWarning('FrameioInterface.nativeContext', 'Share for Review workflows are not enabled for this application');
            }
        }
        if (!this._nativeContext) {
            oneTimeWarning('FrameioInterface.nativeContext', 'native Frameio context does not exist, web/browser mode is utilized for testing only');
        }
        return this._nativeContext;
    }
    // ID value to use as a key for authentication credentials.
    get _authenticationId() {
        return this.adobeId;
    }
    _cachedCredentialsId() {
        return frameioCredentialsCacheKey + this._authenticationId;
    }
    _getRefreshTokenExpiration(lastRefresh) {
        const lastRefreshVal = parseInt(lastRefresh);
        if (lastRefreshVal) {
            return lastRefreshVal + frameioRefreshTokenDuration;
        }
        return 0;
    }
    // Retrieve the cached string.
    async _getCachedCredentials() {
        const credentialsId = this._cachedCredentialsId();
        const existingCredentials = await this._core.environment.cache.getItem(credentialsId);
        if (existingCredentials !== NotFound$1) {
            return existingCredentials;
        }
        return '';
    }
    // Persist the credenital string in the browser cache. Eventually this
    // may be transfered to the native side and persisted in the device keychain.
    async _cacheCredentials(credentials) {
        return this._core.environment.cache.setItem(this._cachedCredentialsId(), credentials, frameioCredentialsTTL);
    }
    get adobeId() {
        // This is expected to return the Adobe user GUID.
        return this._core.environment.getValue('USER_ID');
    }
    get ccUserId() {
        // This is expected to return the Adobe CC ID (email address use to login)
        return this._core.environment.getValue('USER_EMAIL') || '';
    }
    get imsToken() {
        return this._core.environment.getValue('ACCESS_TOKEN');
    }
    get xFrameioClient() {
        return this._frameioClientId;
    }
    get productKey() {
        const { PRODUCT_ID } = this._core.environment.getValue('HOST_PRODUCT_DATA');
        // The Frame.io native module expect the product code to be the 4CC
        // client product identifier.  However, SAM.js seems to use the whole
        // product name, so we need to do a conversion here.
        // Currently only Photoshop(PHXS), Illustrator(ILST), InDesign(IDSN) and
        // the `dev` key are supported.
        if (PRODUCT_ID.includes('Photoshop')) {
            return 'PHXS';
        }
        else if (PRODUCT_ID.includes('Illustrator')) {
            return 'ILST';
        }
        else if (PRODUCT_ID.includes('InDesign')) {
            return 'IDSN';
        }
        else if (PRODUCT_ID.includes('SAMJSDemoApp')) {
            return 'dev';
        }
        else if (PRODUCT_ID.includes('/')) {
            // According to the SAM.js comments, this should be passed in as
            // client name/version - but we only need the client name, so drop
            // the rest. This handles the case where the client may pass in the
            // 4CC code with the version (i.e. 'PHXS/25.0') instead of the full
            // name handled above.
            return PRODUCT_ID.slice(0, PRODUCT_ID.indexOf('/'));
        }
        // If its an unsupported or demo application, then default to dev key.
        return 'dev';
    }
    async checkForCachedCredentials() {
        // Quick check if we have already set credentials at least once
        // that have not expired.
        const cachedCredentials = await this._getCachedCredentials();
        if (cachedCredentials !== '') {
            const nativeContext = this.nativeContext;
            const parsedCredentials = JSON.parse(cachedCredentials);
            const refreshExpiration = this._getRefreshTokenExpiration(parsedCredentials.frameioLastRefresh);
            if (refreshExpiration) {
                const nowSec = Math.floor(Date.now() / 1000); // ms -> s
                const expired = refreshExpiration - nowSec;
                if (expired <= 0) {
                    return IFrameioAuthenticationStatus.authExpired;
                }
            }
            // Always use the current app version no matter what version was cached.
            parsedCredentials.xFrameioClient = this.xFrameioClient;
            if (nativeContext) {
                // Not expired refresh so auto login.
                const token = await nativeContext.authenticateUser(parsedCredentials);
                // Check to see if there was a refresh on the token, and refresh
                // the cached credentials if there was.
                if (token !== parsedCredentials.token) {
                    const nativeAuthInfo = nativeContext.authenticationInfo();
                    await this._cacheCredentials(JSON.stringify(nativeAuthInfo));
                    return IFrameioAuthenticationStatus.authLoggedIn;
                }
            }
            // else not supported for web so issue logout.
        }
        return IFrameioAuthenticationStatus.authLoggedOut;
    }
    async isAuthenticated() {
        const authStatus = await this.authenticationStatus();
        return (authStatus === IFrameioAuthenticationStatus.authLoggedIn);
    }
    async authenticationStatus() {
        var _a, _b, _c;
        const nativeContext = this.nativeContext;
        if (nativeContext) {
            let nativeAuthStatus = nativeContext.authenticationStatus(this._authenticationId);
            if (nativeAuthStatus === IFrameioAuthenticationStatus.authLoggedOut) {
                // If the state is logged out - check for previous cached info.
                nativeAuthStatus = await this.checkForCachedCredentials();
            }
            return nativeAuthStatus;
        }
        if ((_a = this._authenticationInfo) === null || _a === void 0 ? void 0 : _a.frameioLastRefresh) {
            // Check for the last refresh expiration.  Frame.io refresh tokens
            // expire in 15 day, so set refresh an hour before.
            const kExpiresHours = (15 * 24) - 1;
            const now = new Date();
            const lastRefresh = new Date(0);
            lastRefresh.setUTCSeconds(parseInt((_b = this._authenticationInfo) === null || _b === void 0 ? void 0 : _b.frameioLastRefresh));
            // The `valueOf` looks weird here but it is needed to avoid the Typescript
            // error of: "The left-hand side of an arithmetic operation must be of type
            //   'any', 'number', 'bigint' or an enum type."
            const deltaT = Math.abs(now.valueOf() - lastRefresh.valueOf()) / 36e5;
            if (deltaT >= kExpiresHours) {
                return IFrameioAuthenticationStatus.authExpired;
            }
            return (((_c = this._authenticationInfo) === null || _c === void 0 ? void 0 : _c.frameioToken) ? IFrameioAuthenticationStatus.authLoggedIn : IFrameioAuthenticationStatus.authLoggedOut);
        }
        // If auth refresh is not set - assume logged out.
        return IFrameioAuthenticationStatus.authLoggedOut;
    }
    async getToken() {
        var _a;
        const nativeContext = this.nativeContext;
        if (nativeContext) {
            // The native code caches the token and refreshes it
            // when it's stale, so don't persistently store the
            // token in javascript as it will break the refresh.
            return await nativeContext.getToken();
        }
        return ((_a = this._authenticationInfo) === null || _a === void 0 ? void 0 : _a.frameioToken) || '';
    }
    // Execute a force refresh of the authentication token.
    async refreshToken() {
        var _a;
        const nativeContext = this.nativeContext;
        if (nativeContext) {
            // The native code caches the token and refreshes it
            // when it's stale, so don't persistently store the
            // token in javascript as it will break the refresh.
            const token = await nativeContext.getToken(true);
            // After refreshing to token, the save the new info to the store.
            const nativeAuthInfo = nativeContext.authenticationInfo();
            if (token === nativeAuthInfo.frameioToken) {
                await this._cacheCredentials(JSON.stringify(nativeAuthInfo));
            }
            return token;
        }
        // not supported for web based, so only return what we currently have.
        return ((_a = this._authenticationInfo) === null || _a === void 0 ? void 0 : _a.frameioToken) || '';
    }
    async authenticate(info) {
        const nativeContext = this.nativeContext;
        if (nativeContext) {
            const adobeId = this.adobeId;
            const ccUserId = this.ccUserId;
            const productKey = this.productKey;
            const xFrameioClient = this.xFrameioClient;
            if (!nativeContext.isUserAuthenticated(this._authenticationId)) {
                const nativeInfo = {
                    adobeId,
                    ccUserId,
                    productKey,
                    xFrameioClient,
                };
                if (info !== undefined) {
                    nativeInfo.frameioToken = info.frameioToken;
                    // non-required parameters
                    nativeInfo.frameioRefreshToken = (info === null || info === void 0 ? void 0 : info.frameioRefreshToken) || undefined;
                    nativeInfo.frameioTokenExpires = (info === null || info === void 0 ? void 0 : info.frameioTokenExpires) || undefined;
                    nativeInfo.frameioUserId = (info === null || info === void 0 ? void 0 : info.frameioUserId) || undefined;
                    nativeInfo.frameioLastRefresh = (info === null || info === void 0 ? void 0 : info.frameioLastRefresh) || undefined;
                }
                // Removing the following block for now as PM has decided they dont want to have
                // single sign on for the moment as to allow the user to select login account rather
                // than limiting it to the Adobe associated account.  This is subject to change and
                // so its better to leave the SUSI block here for the time being.
                // } else {
                //     // check for SUSI availability.
                //     const susiInfo = await this.getAdobeSusiInfo();
                //     if (susiInfo.susiAvailable) {
                //         const susiAuthInfo = await this.authenticateAdobeSusi();
                //         nativeInfo.frameioToken = susiAuthInfo.frameioToken;
                //         // non-required parameters
                //         nativeInfo.frameioRefreshToken = susiAuthInfo?.frameioRefreshToken || undefined;
                //         nativeInfo.frameioTokenExpires = susiAuthInfo?.frameioTokenExpires || undefined;
                //         nativeInfo.frameioUserId = susiAuthInfo?.frameioUserId || undefined;
                //         nativeInfo.frameioLastRefresh = susiAuthInfo?.frameioLastRefresh || undefined;
                //     }
                // }
                const token = await nativeContext.authenticateUser(nativeInfo);
                if (token !== '') {
                    const nativeAuthInfo = nativeContext.authenticationInfo();
                    await this._cacheCredentials(JSON.stringify(nativeAuthInfo));
                }
                return token;
            }
        }
        else {
            this._authenticationInfo = info || undefined;
        }
        return await this.getToken();
    }
    async deauthenticate() {
        this._authenticationInfo = undefined;
        await this._core.environment.cache.deleteItem(this._cachedCredentialsId());
        const nativeContext = this.nativeContext;
        if (nativeContext) {
            await nativeContext.deauthenticateUser(this._authenticationId);
        }
        return;
    }
    async openFile(filename) {
        const nativeContext = this.nativeContext;
        if (nativeContext) {
            return await nativeContext.openFile(filename);
        }
        throw SAMError.create(SAMErrorCode.UNSUPPORTED, 'Method is not supported in browser environment.');
    }
    // end of IFrameioNativeContext
    //----------------------------------------------------------------
    dispatchReviewLinksChangeEvent(json) {
        const nativeContext = this.nativeContext;
        if (!nativeContext) {
            throw SAMError.create(SAMErrorCode.UNSUPPORTED, 'Method is not supported in browser environment.');
        }
        nativeContext.dispatchReviewLinksChangeEvent(json);
    }
    // Methods related to using Adobe Single User Sign In (SUSI)
    async getAdobeSusiInfo() {
        const respJson = await this._adobeSusiUserRequest();
        const susiInfo = {
            userExists: respJson.exists,
            susiAvailable: (respJson.method === 'adobe'),
            restriction: respJson.restriction || undefined
        };
        return susiInfo;
    }
    async authenticateAdobeSusi() {
        const authInfo = await this._adobeSusiRequest();
        return authInfo;
    }
    async _adobeSusiUserRequest() {
        const response = await this._doPost(FRAMEIO_LINKS.ADOBE_SUSI_USER, { 'Content-Type': 'application/json' }, JSON.stringify({ access_token: this.imsToken }), [200]);
        return response.json();
    }
    async _adobeSusiRequest() {
        const response = await this._doPost(FRAMEIO_LINKS.ADOBE_SUSI, { 'Content-Type': 'application/json' }, JSON.stringify({ access_token: this.imsToken }), [200]);
        const respHeaders = response.headers;
        const respBody = await response.json();
        const token = respHeaders.get('authorization');
        const refreshToken = respHeaders.get('x-refresh-token');
        const tokenExpires = respHeaders.get('x-expires');
        const authInfo = {
            frameioToken: token ? token.replace('Bearer ', '') : '',
            frameioRefreshToken: refreshToken || undefined,
            frameioTokenExpires: tokenExpires || undefined,
            frameioUserId: respBody.id || undefined,
            frameioLastRefresh: Date.now().toString(),
            useAdobeSusi: true
        };
        return authInfo;
    }
    //----------------------------------------------------------------
    // InterfaceBase._fetch() lets subclasses adjust the fetch options
    // by calling _getFetchOptionOverrides().
    //
    // FrameioInterface specifies noOtherHeaders=true to block ACP
    // specific headers, such as _fetch() injecting it's own
    // ACP specific authorization token. Instead, we specify
    // the Frameio authorization token
    async _getFetchOptionOverrides(href, fetchOptions) {
        const token = await this.getToken();
        return {
            href,
            options: Object.assign(Object.assign({}, fetchOptions), { headers: Object.assign(Object.assign({}, fetchOptions.headers), { 
                    // Override Environment.fetch default auth header
                    'authorization': `Bearer ${token}`, 'x-frameio-client': this.xFrameioClient }), 
                // Do not allow Environment.fetch to supply its default headers
                noOtherHeaders: true })
        };
    }
    // Frameio endpoints that return Array<>'s can be paginated:
    //   https://developer.frame.io/docs/getting-started/key-concepts#h2-section-pagination
    //   NOTE: The default page size is 50.
    //
    // _getMultiPageResponse() will read each page in the response,
    // and return the result of combining them into one large array.
    async _getMultiPageResponse(href) {
        let result = [];
        const nextPageUri = (response) => {
            const linkHeader = response && response.headers && response.headers.get('Link');
            const link$1 = linkHeader && link.parse(linkHeader);
            const ref = link$1 && link$1.has('rel', 'next') && link$1.get('rel', 'next');
            const uri = ref && ref[0] && ref[0].uri;
            // For some reason, the link headers are specifying http,
            // but our requests fail with 403 unless we use https.
            return uri && uri.replace('http:', 'https:');
        };
        const readPage = async (url) => {
            const response = await this._doGet(url);
            const json = await response.json();
            const next = nextPageUri(response);
            // Accumulate the pages in result
            result = result.concat(json);
            // Use tail recursion to read the next page, or return
            // the final result if there are no more pages.
            return next ? readPage(next) : result;
        };
        return readPage(href);
    }
    //
    //----------------------------------------------------------------
    // See https://developer.frame.io/api/reference/operation/getMe/
    async getCurrentUser() {
        return this._getResponse(FRAMEIO_LINKS.ME);
    }
    // See https://developer.frame.io/api/reference/operation/getAccount/
    async getAccount(accountId) {
        return this._getResponse(fillURLTemplate$1(FRAMEIO_LINKS.ACCOUNT, { accountId }));
    }
    // See https://developer.frame.io/api/reference/operation/getAccounts/
    async getAccounts() {
        return this._getMultiPageResponse(FRAMEIO_LINKS.ACCOUNTS);
    }
    // See https://developer.frame.io/api/reference/operation/getTeamsByAccount/
    async getTeams(accountId) {
        return this._getMultiPageResponse(fillURLTemplate$1(FRAMEIO_LINKS.TEAMS, { accountId }));
    }
    // See https://developer.frame.io/api/reference/operation/getTeam/
    async getTeam(teamId) {
        return this._getResponse(fillURLTemplate$1(FRAMEIO_LINKS.TEAM, { teamId }));
    }
    // See https://developer.frame.io/api/reference/operation/getProject/
    async getProject(projectId) {
        return this._getResponse(fillURLTemplate$1(FRAMEIO_LINKS.PROJECT, { projectId }));
    }
    // See https://developer.frame.io/api/reference/operation/getProjectsByTeam/
    async getProjects(teamId) {
        return this._getMultiPageResponse(fillURLTemplate$1(FRAMEIO_LINKS.PROJECTS, { teamId }));
    }
    // See https://developer.frame.io/api/reference/operation/getSharedProjects/
    // NOTE: Using filter[account_id]={accountId} to restrict results to a specific account
    async getSharedProjects(accountId) {
        return this._getMultiPageResponse(fillURLTemplate$1(FRAMEIO_LINKS.SHAREDPROJECTS, { accountId }));
    }
    // See https://developer.frame.io/api/reference/operation/reviewLinksList/
    async getReviewLinks(projectId) {
        return this._getMultiPageResponse(fillURLTemplate$1(FRAMEIO_LINKS.REVIEWS, { projectId }));
    }
    // See https://developer.frame.io/api/reference/operation/reviewLinkGet/
    async getReviewLink(reviewLinkId) {
        return this._getResponse(fillURLTemplate$1(FRAMEIO_LINKS.REVIEW, { reviewLinkId }));
    }
    // See https://developer.frame.io/api/reference/operation/reviewLinkCreate/
    async createReviewLink(projectId, displayName) {
        const response = await this._doPost(fillURLTemplate$1(FRAMEIO_LINKS.REVIEWS, { projectId }), { 'Content-Type': 'application/json' }, JSON.stringify({ name: displayName }), [200]);
        return response.json();
    }
    // See https://developer.frame.io/api/reference/operation/createAsset/
    async createFolder(parentId, displayName) {
        const response = await this._doPost(fillURLTemplate$1(FRAMEIO_LINKS.CREATEASSET, { parentId }), { 'Content-Type': 'application/json' }, JSON.stringify({ name: displayName, type: 'folder' }), [200]);
        return response.json();
    }
    // See https://developer.frame.io/api/reference/operation/createAsset/
    // See also https://developer.frame.io/docs/workflows-assets/uploading-assets
    async createFile(parentId, displayName, fileSize, mimeType = 'application/pdf') {
        const response = await this._doPost(fillURLTemplate$1(FRAMEIO_LINKS.CREATEASSET, { parentId }), { 'Content-Type': 'application/json' }, JSON.stringify({
            name: displayName,
            type: 'file',
            filetype: mimeType,
            filesize: fileSize
        }), [200]);
        return response.json();
    }
    // See https://developer.frame.io/api/reference/operation/createAsset/
    // See also https://developer.frame.io/docs/workflows-assets/uploading-assets
    async uploadFile(url, bytes, mimeType = 'application/pdf') {
        // !!@ The upload urls are to AWS, so I'm just using a raw fetch for now...
        const init = {
            method: 'PUT',
            headers: {
                'Content-Type': mimeType,
                'x-amz-acl': 'private'
            },
            body: bytes
        };
        await fetch(url, init);
    }
    // See https://developer.frame.io/api/reference/operation/reviewLinkItemCreate/
    async reviewLinkAddAsset(reviewLinkId, assetId) {
        const response = await this._doPost(fillURLTemplate$1(FRAMEIO_LINKS.ITEMCREATE, { reviewLinkId }), { 'Content-Type': 'application/json' }, JSON.stringify({ asset_ids: [assetId] }), [200]);
        return response.json();
    }
    // See https://developer.frame.io/api/reference/operation/getAssets/
    async getChildren(assetId) {
        return this._getMultiPageResponse(fillURLTemplate$1(FRAMEIO_LINKS.ASSETS, { assetId }));
    }
    // See https://developer.frame.io/api/reference/operation/getAsset/
    async getAsset(assetId) {
        return this._getResponse(fillURLTemplate$1(FRAMEIO_LINKS.ASSET, { assetId }));
    }
    // See https://developer.frame.io/api/reference/operation/addVersionToAsset/
    async addVersionToAsset(assetId, nextAssetId) {
        const response = await this._doPost(fillURLTemplate$1(FRAMEIO_LINKS.VERSION, { assetId }), { 'Content-Type': 'application/json' }, JSON.stringify({ next_asset_id: nextAssetId }), [200]);
        return response.json();
    }
    // See https://developer.frame.io/api/reference/operation/createComment/
    async createComment(assetId, text) {
        const response = await this._doPost(fillURLTemplate$1(FRAMEIO_LINKS.CREATECOMMENT, { assetId }), { 'Content-Type': 'application/json' }, JSON.stringify({ text }), [200]);
        return response.json();
    }
    // See https://developer.frame.io/api/reference/operation/getComment/
    async getComment(commentId) {
        return this._getResponse(fillURLTemplate$1(FRAMEIO_LINKS.GETCOMMENT, { commentId }));
    }
    // See https://developer.frame.io/api/reference/operation/getComments/
    async getComments(assetId) {
        return this._getMultiPageResponse(fillURLTemplate$1(FRAMEIO_LINKS.GETCOMMENTS, { assetId }));
    }
    // See https://developer.frame.io/api/reference/operation/getCommentImpressions/
    async getImpressions(commentId) {
        return this._getMultiPageResponse(fillURLTemplate$1(FRAMEIO_LINKS.IMPRESSIONS, { commentId }));
    }
    // See https://developer.frame.io/api/reference/operation/updateComment/
    async updateComment(commentId, text) {
        const response = await this._doPut(fillURLTemplate$1(FRAMEIO_LINKS.COMMENT, { commentId }), { 'Content-Type': 'application/json' }, JSON.stringify({ text }), [200]);
        return response.json();
    }
    // See https://developer.frame.io/api/reference/operation/deleteComment/
    async deleteComment(commentId) {
        const response = await this._doDelete(fillURLTemplate$1(FRAMEIO_LINKS.COMMENT, { commentId }));
        return response.json();
    }
    // See https://developer.frame.io/api/reference/operation/createReply/
    async createReply(commentId, text) {
        const response = await this._doPost(fillURLTemplate$1(FRAMEIO_LINKS.REPLY, { commentId }), { 'Content-Type': 'application/json' }, JSON.stringify({ text }), [200]);
        return response.json();
    }
    // See https://developer.frame.io/api/reference/operation/getComments/
    async getReplies(commentId) {
        return this._getMultiPageResponse(fillURLTemplate$1(FRAMEIO_LINKS.GETREPLIES, { commentId }));
    }
    // See https://developer.frame.io/api/reference/operation/createCommentImpressionLike/
    async createLike(commentId) {
        const response = await this._doPost(fillURLTemplate$1(FRAMEIO_LINKS.LIKE, { commentId }), {}, undefined, [200]);
        return response.json();
    }
    // See https://developer.frame.io/api/reference/operation/deleteCommentImpressionLike/
    async deleteLike(commentId) {
        const response = await this._doDelete(fillURLTemplate$1(FRAMEIO_LINKS.LIKE, { commentId }));
        return response.json();
    }
    // See https://developer.frame.io/api/reference/operation/completeComment/
    async completeComment(commentId) {
        const response = await this._doPost(fillURLTemplate$1(FRAMEIO_LINKS.COMPLETE, { commentId }), {}, undefined, [200]);
        return response.json();
    }
    // See https://developer.frame.io/api/reference/operation/uncompleteComment/
    async uncompleteComment(commentId) {
        const response = await this._doDelete(fillURLTemplate$1(FRAMEIO_LINKS.COMPLETE, { commentId }));
        return response.json();
    }
    // This is an undocumented interface. It will not work with developer tokens
    // Loosely speaking, a tree of assets gets stringify()'ed as a dictionary.
    //
    // Each folder in the tree is added to the dictionary using the folder id
    // as a key, and an array of objects describing the folder's immediate children
    // as the value. The description of a child includes { id, parent_id, name },
    // but is not a full asset description ( IFrameioAssetResponse ).
    //
    // As the root folder has no parent to describe it, the value of data["root"]
    // is an array containing the root folder's description.
    async getFolderTree(projectId) {
        return this._getResponse(fillURLTemplate$1(FRAMEIO_LINKS.FOLDERTREE, { projectId }));
    }
    // This is an undocumented interface. It will not work with developer tokens.
    //
    // The response is an array of assets.
    //
    // If the asset's path relative to it's project is ./folder/asset,
    // then the array would be:
    //
    // array[0] = root_asset
    // array[1] = folder
    // array[2] = asset
    async getAncestors(assetId) {
        return this._getMultiPageResponse(fillURLTemplate$1(FRAMEIO_LINKS.ANCESTORS, { assetId }));
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class FrameioBase extends ItemBase {
    // ItemBase implements this.id for us
    /**
     * @package - not yet decided if context is internal use only.
     *  In the meantime, I'm decorating them to discourage it's use.
     */
    get context() {
        return this._core.frameioContext;
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// See https://developer.frame.io/api/reference/operation/getAccount/
//  for schema of the data FrameioAccount encapsulates.
class FrameioUser extends FrameioBase {
    constructor() {
        super(...arguments);
        this._accountId = '';
        this._email = '';
        this._name = '';
        this._profileImage = '';
        this._userDefaultColor = '';
    }
    get accountId() {
        return this._accountId;
    }
    get email() {
        return this._email;
    }
    get name() {
        return this._name;
    }
    get profileImage() {
        return this._profileImage;
    }
    get userDefaultColor() {
        return this._userDefaultColor;
    }
    async getAccount() {
        if (!this._account) {
            this._account = await this.context.getAccount(this.accountId);
        }
        return this._account;
    }
}
const FrameioUserUpdater = new ItemUpdater({
    localItemType: FrameioUser,
    remoteIdProperty: 'id',
    propertyUpdater: new PropertyUpdater({
        '_accountId': 'account_id',
        '_email': 'email',
        '_name': 'name',
        '_profileImage': 'profile_image',
        '_userDefaultColor': 'user_default_color'
    })
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// See https://developer.frame.io/api/reference/operation/getAsset/
//  for schema of IFrameioAssetResponse. FrameioFolder
//  encapsulates the data for assets of type="folder".
//
// See FrameioFile, FrameioVersionStack for other asset types.
class FrameioAsset extends FrameioBase {
    constructor() {
        super(...arguments);
        this._name = '';
        this._projectId = '';
        this._teamId = '';
    }
    /* All of a FrameioAsset's id's should be valid id's.
     *
     * But Frame.io indicates a root_asset folder with
     * (parent_id == null), as a root doesn't have a parent.
     *
     * SAM.js isn't a fan of the null's, so it's using
     * undefined for the same thing.
     */
    get insertedAt() {
        return this._insertedAt;
    }
    get name() {
        return this._name;
    }
    get parentId() {
        return this._parentId;
    }
    async getParent() {
        const ctx = this.context;
        const parentId = this.parentId;
        if (parentId) {
            return ctx.getAsset(parentId);
        }
        return undefined;
    }
    get projectId() {
        return this._projectId;
    }
    get teamId() {
        return this._teamId;
    }
    get updatedAt() {
        return this._updatedAt;
    }
    /*
     * If the path to an asset relative to it's project is
     *      ./folder/subfolder/asset
     *
     * Then
     *      ancestors[0] = folder : FrameioFolder
     *      ancestors[1] = subfolder : FrameioFolder
     *      ancestors[2] = asset : FrameioAsset
     */
    async getAncestors() {
        return this.context.getAncestors(this.id);
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// See https://developer.frame.io/api/reference/operation/createCommentImpressionLike/
//  for schema of IFrameioLikeResponse, aka response.type="like".
class FrameioLike extends FrameioBase {
    get anonymousUserId() {
        return this._anonymousUserId || '';
    }
    get comment() {
        return this._comment;
    }
    get commentId() {
        return this._commentId || '';
    }
    get deletedAt() {
        return this._deletedAt;
    }
    get insertedAt() {
        return this._insertedAt;
    }
    get updatedAt() {
        return this._updatedAt;
    }
    get userId() {
        return this._userId || '';
    }
}
const FrameioLikeUpdater = new ItemUpdater({
    localItemType: FrameioLike,
    remoteIdProperty: 'id',
    propertyUpdater: new PropertyUpdater({
        '_anonymousUserId': 'anonymous_user_id',
        '_comment': {
            // The comment property of an IFrameioLikeResponse is a nested
            // object of type IFrameioCommentResponse, so let's update it!
            set: (comment, dest) => {
                const like = dest;
                const context = like.context;
                const pool = context._objectPool;
                const session = pool.createUpdateSession();
                like['_comment'] = FrameioCommentUpdater.updateRemoteItem(session, comment);
                session.commit();
            },
            name: 'comment',
            format: 'object'
        },
        '_commentId': 'comment_id',
        '_deletedAt': { name: 'deleted_at', format: 'date' },
        '_insertedAt': { name: 'inserted_at', format: 'date' },
        '_updatedAt': { name: 'updated_at', format: 'date' },
        '_userId': 'user_id'
    })
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// See https://developer.frame.io/api/reference/operation/getAsset/
//  for schema of IFrameioAssetResponse. FrameioFile
//  encapsulates the data for assets of type="file".
//
// See FrameioFolder, FrameioVersionStack for other asset types.
class FrameioComment extends FrameioBase {
    constructor() {
        // Frame.io uses { myString : null } for unspecified
        // values, which causes our updater to set the string
        // property to undefined, but our getters substitute
        // '' for undefined, so our clients aren't exposed to this.
        super(...arguments);
        this._completed = false;
        this._hasReplies = false;
        this._likeCount = 0;
        this._private = false;
        this._readCount = 0;
        this._timestamp = 0;
        this._timestampMicroseconds = 0;
    }
    get assetId() {
        return this._assetId || '';
    }
    get completed() {
        return this._completed;
    }
    get completedAt() {
        return this._completedAt;
    }
    get completerId() {
        return this._completerId || '';
    }
    get deletedAt() {
        return this._deletedAt;
    }
    get hasReplies() {
        return this._hasReplies;
    }
    get insertedAt() {
        return this._insertedAt;
    }
    get likeCount() {
        return this._likeCount;
    }
    get owner() {
        return this._owner;
    }
    get ownerId() {
        return this._ownerId || '';
    }
    get page() {
        return this._page || '';
    }
    get parentId() {
        return this._parentId || '';
    }
    get private() {
        return this._private;
    }
    get readCount() {
        return this._readCount;
    }
    get reviewLinkId() {
        return this._reviewLinkId || '';
    }
    get targetAssetId() {
        return this._targetAssetId || '';
    }
    get text() {
        return this._text || '';
    }
    get textEditedAt() {
        return this._textEditedAt;
    }
    get timestamp() {
        return this._timestamp;
    }
    get timestampMicroseconds() {
        return this._timestampMicroseconds;
    }
    get updatedAt() {
        return this._updatedAt;
    }
    async updateComment(param) {
        const ctx = this.context;
        const response = await ctx._frameioInterface.updateComment(this.id, param.text);
        const session = ctx._objectPool.createUpdateSession();
        const comment = FrameioCommentUpdater.updateRemoteItem(session, response);
        session.commit();
        return comment;
    }
    async deleteComment() {
        const ctx = this.context;
        const response = await ctx._frameioInterface.deleteComment(this.id);
        const session = ctx._objectPool.createUpdateSession();
        const comment = FrameioCommentUpdater.updateRemoteItem(session, response);
        session.commit();
        return comment;
    }
    async createLike() {
        const ctx = this.context;
        const response = await ctx._frameioInterface.createLike(this.id);
        const session = ctx._objectPool.createUpdateSession();
        const like = FrameioLikeUpdater.updateRemoteItem(session, response);
        session.commit();
        return like;
    }
    async deleteLike() {
        const ctx = this.context;
        const response = await ctx._frameioInterface.deleteLike(this.id);
        const session = ctx._objectPool.createUpdateSession();
        const like = FrameioLikeUpdater.updateRemoteItem(session, response);
        session.commit();
        return like;
    }
    async createReply(param) {
        const ctx = this.context;
        const response = await ctx._frameioInterface.createReply(this.id, param.text);
        const session = ctx._objectPool.createUpdateSession();
        const reply = FrameioCommentUpdater.updateRemoteItem(session, response);
        session.commit();
        return reply;
    }
    async getReplies() {
        const ctx = this.context;
        const response = await ctx._frameioInterface.getReplies(this.id);
        const session = ctx._objectPool.createUpdateSession();
        const replies = FrameioCommentUpdater.updateRemoteItems(session, response);
        session.commit();
        return replies;
    }
    async completeComment() {
        const ctx = this.context;
        const response = await ctx._frameioInterface.completeComment(this.id);
        const session = ctx._objectPool.createUpdateSession();
        const comment = FrameioCommentUpdater.updateRemoteItem(session, response);
        session.commit();
        return comment;
    }
    async uncompleteComment() {
        const ctx = this.context;
        const response = await ctx._frameioInterface.uncompleteComment(this.id);
        const session = ctx._objectPool.createUpdateSession();
        const comment = FrameioCommentUpdater.updateRemoteItem(session, response);
        session.commit();
        return comment;
    }
}
const FrameioCommentUpdater = new ItemUpdater({
    localItemType: FrameioComment,
    remoteIdProperty: 'id',
    propertyUpdater: new PropertyUpdater({
        '_assetId': 'asset_id',
        '_completed': { name: 'completed', format: 'boolean' },
        '_completedAt': { name: 'completed_at', format: 'date' },
        '_completerId': 'completer_id',
        '_deletedAt': { name: 'deleted_at', format: 'date' },
        '_hasReplies': { name: 'has_replies', format: 'boolean' },
        '_insertedAt': { name: 'inserted_at', format: 'date' },
        '_likeCount': { name: 'like_count', format: 'number' },
        '_owner': {
            // The owner property of an IFrameioComment is a nested
            // IFrameioUserResponse, so let's update it!
            set: (owner, dest) => {
                const comment = dest;
                const context = comment.context;
                const pool = context._objectPool;
                const session = pool.createUpdateSession();
                comment['_owner'] = FrameioUserUpdater.updateRemoteItem(session, owner);
                session.commit();
            },
            name: 'owner',
            format: 'object'
        },
        '_ownerId': 'owner_id',
        '_page': 'page',
        '_parentId': 'parent_id',
        '_private': { name: 'private', format: 'boolean' },
        '_readCount': { name: 'read_count', format: 'number' },
        '_reviewLinkId': 'review_link_id',
        '_targetAssetId': 'target_asset_id',
        '_text': 'text',
        '_textEditedAt': { name: 'text_edited_at', format: 'date' },
        '_timestamp': { name: 'timestamp', format: 'number' },
        '_timestampMicroseconds': { name: 'timestamp_microseconds', format: 'number' },
        '_updatedAt': { name: 'updated_at', format: 'date' }
    })
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// See https://developer.frame.io/api/reference/operation/getAsset/
//  for schema of IFrameioAssetResponse. FrameioFile
//  encapsulates the data for assets of type="file".
//
// See FrameioFolder, FrameioVersionStack for other asset types.
class FrameioFile extends FrameioAsset {
    async createComment(param) {
        const ctx = this.context;
        const response = await ctx._frameioInterface.createComment(this.id, param.text);
        const session = ctx._objectPool.createUpdateSession();
        const comment = FrameioCommentUpdater.updateRemoteItem(session, response);
        session.commit();
        this._comments = undefined; // force it to refresh the next time it's called.
        return comment;
    }
    // https://developer.frame.io/docs/workflows-projects/gather-all-comments-from-a-project
    //  implies that only a file can have comments, folders and version stacks should
    //  enumerate their children for files with comments
    async getComments() {
        let comments = this._comments;
        if (!comments) {
            const ctx = this.context;
            const response = await ctx._frameioInterface.getComments(this.id);
            const session = ctx._objectPool.createUpdateSession();
            comments = FrameioCommentUpdater.updateRemoteItems(session, response);
            session.commit();
            this._comments = comments;
        }
        return comments;
    }
}
const FrameioFileUpdater = new ItemUpdater({
    localItemType: FrameioFile,
    remoteIdProperty: 'id',
    propertyUpdater: new PropertyUpdater({
        '_commentCount': 'comment_count',
        '_insertedAt': { name: 'inserted_at', format: 'date' },
        '_name': 'name',
        '_parentId': 'parent_id',
        '_projectId': 'project_id',
        '_teamId': 'team_id',
        '_updatedAt': { name: 'updated_at', format: 'date' }
    })
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// See https://developer.frame.io/api/reference/operation/getAsset/
//  for schema of IFrameioAssetResponse. FrameioFolder
//  encapsulates the data for assets of type="folder".
//
// See FrameioFile, FrameioVersionStack for other asset types.
class FrameioFolder extends FrameioAsset {
    // clients shouldn't need to deal with id's
    async getChildren() {
        if (!this._children) {
            const ctx = this.context;
            const response = await ctx._frameioInterface.getChildren(this.id);
            const session = ctx._objectPool.createUpdateSession();
            // IFrameioAssetResponse is a special case, as it corresponds to multiple classes
            // inheriting from FrameioAsset ( FrameioFile, FrameioFolder, FrameioVersionStack ),
            // so use a utility function to select the correct updater for the type.
            //
            // See also FrameioContext.getAncestors()
            this._children = response.map((asset) => frameioAssetUpdater(session, asset));
            session.commit();
        }
        return this._children;
    }
    async _uploadFile(urls, fp) {
        // Frame.io splits uploads into ~20MB chunks, whereas our expected
        // artifact size is ~5MB, so the most common case will be (numChunks == 1).
        const ctx = this.context;
        // limit the number of concurrent streams
        const maxStreams = 8;
        const fileSize = fp.size();
        const numChunks = urls.length;
        const chunkSize = Math.ceil(fileSize / numChunks); // round-down might lead to omitted bytes.
        let iChunk = 0;
        const nextChunk = async (bytes) => {
            const i = iChunk++;
            bytes = await fp.read((i) * chunkSize, bytes);
            return ctx._frameioInterface.uploadFile(urls[i], bytes).then(() => {
                return (iChunk < numChunks) ? nextChunk(bytes) : Promise.resolve();
            });
        };
        const limit = Math.min(numChunks, maxStreams);
        const promises = [];
        for (let i = 0; i < limit; i++) {
            promises.push(nextChunk(new ArrayBuffer(chunkSize)));
        }
        await Promise.all(promises);
    }
    async createFile(display_name, path) {
        const ctx = this.context;
        const fp = await ctx._frameioInterface.openFile(path);
        const fileSize = fp.size();
        // create the empty asset
        const response = await ctx._frameioInterface.createFile(this.id, display_name, fileSize);
        const session = ctx._objectPool.createUpdateSession();
        const file = FrameioFileUpdater.updateRemoteItem(session, response);
        session.commit();
        await this._uploadFile(response['upload_urls'], fp);
        fp.close();
        return file;
    }
    async createFolder(display_name) {
        const ctx = this.context;
        const response = await ctx._frameioInterface.createFolder(this.id, display_name);
        const session = ctx._objectPool.createUpdateSession();
        const folder = FrameioFolderUpdater.updateRemoteItem(session, response);
        session.commit();
        return folder;
    }
}
const FrameioFolderUpdater = new ItemUpdater({
    localItemType: FrameioFolder,
    remoteIdProperty: 'id',
    propertyUpdater: new PropertyUpdater({
        '_insertedAt': { name: 'inserted_at', format: 'date' },
        '_name': 'name',
        '_parentId': 'parent_id',
        '_projectId': 'project_id',
        '_teamId': 'team_id',
        '_updatedAt': { name: 'updated_at', format: 'date' }
    })
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// See https://developer.frame.io/api/reference/operation/getTeamsByAccount/
//  for schema of the data FrameioTeam encapsulates.
class FrameioTeam extends FrameioBase {
    constructor() {
        super(...arguments);
        this._email = '';
        this._name = '';
    }
    get email() {
        return this._email;
    }
    get name() {
        return this._name;
    }
    async getProjects() {
        let projects = this._projects;
        if (!projects) {
            const ctx = this.context;
            const projectsResponse = await this._core.frameioContext._frameioInterface.getProjects(this.id);
            const session = ctx._objectPool.createUpdateSession();
            projects = FrameioProjectUpdater.updateRemoteItems(session, projectsResponse);
            session.commit();
            this._projects = projects;
        }
        return projects;
    }
}
const FrameioTeamUpdater = new ItemUpdater({
    localItemType: FrameioTeam,
    remoteIdProperty: 'id',
    propertyUpdater: new PropertyUpdater({
        '_email': 'owner.email',
        '_name': 'name'
    })
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// See https://developer.frame.io/api/reference/operation/getAsset/
//  for schema of IFrameioAssetResponse. FrameioVersionStack
//  encapsulates the data for assets of type="version_stack".
//
// See FrameioFile, FrameioFolder for other asset types.
class FrameioVersionStack extends FrameioAsset {
    constructor() {
        super(...arguments);
        this._coverAssetId = '';
        this._versions = 0;
    }
    get coverAssetId() {
        return this._coverAssetId;
    }
    async getCoverAsset() {
        if (!this._coverAsset) {
            this._coverAsset = await this.context.getAsset(this.coverAssetId);
        }
        return this._coverAsset;
    }
    get versions() {
        return this._versions;
    }
    async getChildren() {
        if (!this._children) {
            const ctx = this.context;
            const response = await ctx._frameioInterface.getChildren(this.id);
            const session = ctx._objectPool.createUpdateSession();
            // IFrameioAssetResponse is a special case, as it corresponds to multiple classes
            // inheriting from FrameioAsset ( FrameioFile, FrameioFolder, FrameioVersionStack ),
            // so use a utility function to select the correct updater for the type.
            //
            // See also FrameioContext.getAncestors()
            this._children = response.map((asset) => frameioAssetUpdater(session, asset));
            session.commit();
        }
        return this._children;
    }
}
const FrameioVersionStackUpdater = new ItemUpdater({
    localItemType: FrameioVersionStack,
    remoteIdProperty: 'id',
    propertyUpdater: new PropertyUpdater({
        '_coverAsset': {
            // when the update reads response.root_asset
            // ( which matches name: 'root_asset' ),
            // it will invoke set: with the child object
            //
            name: 'cover_asset',
            format: 'object',
            set: (response, dest) => {
                const stack = dest;
                const pool = stack.context._objectPool;
                const session = pool.createUpdateSession();
                const asset = frameioAssetUpdater(session, response);
                session.commit();
                // @ts-ignore
                stack._coverAsset = asset;
            },
        },
        '_coverAssetId': 'cover_asset_id',
        '_insertedAt': { name: 'inserted_at', format: 'date' },
        '_name': 'name',
        '_parentId': 'parent_id',
        '_projectId': 'project_id',
        '_teamId': 'team_id',
        '_updatedAt': { name: 'updated_at', format: 'date' },
        '_versions': { name: 'versions', format: 'number' }
    })
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// See https://developer.frame.io/api/reference/operation/getTeamsByAccount/
//  for schema of the data FrameioTeam encapsulates.
class FrameioReviewLink extends FrameioBase {
    constructor() {
        super(...arguments);
        this._name = '';
        this._shortUrl = '';
        this._ownerId = '';
        this._projectId = '';
        this._active = false;
        this._inviteOnly = false;
    }
    get name() {
        return this._name;
    }
    get shortUrl() {
        return this._shortUrl;
    }
    get ownerId() {
        return this._ownerId;
    }
    get projectId() {
        return this._projectId;
    }
    get active() {
        return this._active;
    }
    get inviteOnly() {
        return this._inviteOnly;
    }
    get itemId() {
        return this._itemId;
    }
    /*
     * Returns the number of artifacts currently in the review.
     */
    async getArtifactCount() {
        const item = await this.getItem();
        if (item instanceof FrameioVersionStack) {
            return item.versions;
        }
        return 1;
    }
    /*
    * Recents the most recently published artifact,
    * a FrameioFile containing a PDF.
    */
    async getArtifact() {
        const item = await this.getItem();
        if (item instanceof FrameioVersionStack) {
            return (await item.getCoverAsset());
        }
        return item;
    }
    async getItem() {
        if (!this._item) {
            if (!this._itemId) {
                return Promise.reject(SAMError.create(SAMErrorCode.BAD_REQUEST, `FrameioReviewLink.getItem() could not find item ${this._itemId}`));
            }
            this._item = await this.context.getAsset(this._itemId);
        }
        return this._item;
    }
    _dispatchReviewLinksChangeEvent(source) {
        const ctx = this.context;
        // After a successful updateReviewLink(),
        // we use SAM.cpp to dispatch an event to all DOM's,
        // so they can each update FrameioContext._reviewLinksForDocument
        //
        // For example, when ShareSheet creates a new review link,
        // ShareSheet and Commenting want to update their cache.
        //
        // See also FrameioContext._handleReviewLinksChangeEvent()
        const param = {
            source,
            reviewLink: {
                id: this.id,
                name: this._name,
                short_url: this._shortUrl,
                owner_id: this._ownerId,
                project_id: this._projectId,
                is_active: (this._active) ? true : false,
                access_control: {
                    invite_only: (this._inviteOnly) ? true : false
                },
                'frameio:artifactId': this._itemId,
                'repo:etag': this._etag
            }
        };
        ctx._frameioInterface.dispatchReviewLinksChangeEvent(JSON.stringify(param));
    }
    async updateReviewLink(source, parent, artifactPath) {
        const file = await parent.createFile(this.name, artifactPath);
        if (!this._itemId) {
            // (_itemId === undefined) means this review has never been updated,
            // so the file asset should be added as an item to the review and remembered.
            const ctx = this.context;
            const response = await ctx._frameioInterface.reviewLinkAddAsset(this.id, file.id);
            const session = ctx._objectPool.createUpdateSession();
            FrameioReviewLinkUpdater.updateRemoteItem(session, response); // updates this
            session.commit();
            // Notify the Review Service of the newly created review.
            const result = await ctx._reviewServiceInterface.createReviewLink(Object.assign(Object.assign({}, source), { projectId: this.projectId, reviewLinkId: this.id, artifactId: file.id }));
            this._etag = result['repo:etag'];
            this._item = file;
            this._itemId = file.id;
        }
        else {
            // (_itemId !== undefined) means this isn't the first update.
            //
            // For the second update, we'll version the original file
            // (this._itemId) with the new file to create a stack.
            // We'll overwrite this._itemId with the stack's id and notify
            // the Review Service.
            //
            // For any remaining calls, this._itemId is already a stack,
            // and versioning a stack with a new file just adds the file
            // to the stack. As the stack doesn't change, there's no
            // further need to update the Review Service.
            const ctx = this.context;
            const response = await ctx._frameioInterface.addVersionToAsset(this._itemId, file.id);
            const session = ctx._objectPool.createUpdateSession();
            const item = FrameioVersionStackUpdater.updateRemoteItem(session, response);
            session.commit();
            if (this._itemId !== item.id) {
                // Notify the Review Service that the file
                // has been replaced with a version stack.
                const result = await ctx._reviewServiceInterface.updateReviewLink(Object.assign(Object.assign({}, source), { projectId: this.projectId, reviewLinkId: this.id, artifactId: item.id, etag: this._etag }));
                this._etag = result['repo:etag'];
                this._item = item;
                this._itemId = item.id;
            }
        }
        this._dispatchReviewLinksChangeEvent(source);
    }
}
const FrameioReviewLinkUpdater = new ItemUpdater({
    localItemType: FrameioReviewLink,
    remoteIdProperty: 'id',
    propertyUpdater: new PropertyUpdater({
        '_name': 'name',
        '_shortUrl': 'short_url',
        '_ownerId': 'owner_id',
        '_projectId': 'project_id',
        '_active': { name: 'is_active', format: 'boolean' },
        '_inviteOnly': { name: 'access_control.invite_only', format: 'boolean' },
        '_itemId': 'frameio:artifactId',
        '_etag': 'repo:etag'
    })
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// See https://developer.frame.io/api/reference/operation/getProjectsByTeam/
//  for schema of the data FrameioProject encapsulates.
class FrameioProject extends FrameioBase {
    constructor() {
        super(...arguments);
        this._rootAssetId = '';
        this._name = '';
        this._ownerId = '';
        this._teamId = '';
    }
    get name() {
        return this._name;
    }
    get ownerId() {
        return this._ownerId;
    }
    get rootAssetId() {
        return this._rootAssetId;
    }
    get teamId() {
        return this._teamId;
    }
    async getTeam() {
        if (!this._team) {
            this._team = await this.context.getTeam(this.rootAssetId);
        }
        return this._team;
    }
    async getRootFolder() {
        if (!this._rootFolder) {
            this._rootFolder = await this.context.getAsset(this.rootAssetId);
        }
        return this._rootFolder;
    }
    async getReviewLinks() {
        let reviews = this._reviews;
        if (!reviews) {
            const ctx = this.context;
            const response = await ctx._frameioInterface.getReviewLinks(this.id);
            const session = ctx._objectPool.createUpdateSession();
            reviews = FrameioReviewLinkUpdater.updateRemoteItems(session, response);
            session.commit();
            this._reviews = reviews;
        }
        return reviews;
    }
    createFolder(data, folder) {
        const ctx = this.context;
        // IFrameioFolderTreeFolderResponse only contains
        //      { id, name, parent_id }
        //
        // but FrameioFolderUpdater expects to read
        //      { id, name, parent_id, project_id, team_id }
        //
        // from a IFrameioAssetResponse, so copy { project_id, team_id }
        // from this project into the response so we can use the
        // updater.
        const response = folder;
        response['project_id'] = this.id;
        response['team_id'] = this.teamId;
        const session = ctx._objectPool.createUpdateSession();
        const that = FrameioFolderUpdater.updateRemoteItem(session, response);
        session.commit();
        that['_children'] = data[response.id].map((folder) => this.createFolder(data, folder));
        return that;
    }
    async getFolderTree() {
        // returns the rootFolder, but only after constructing FrameioFolder's for all of the
        // folders in project using an undocumented API.
        const response = await this._core.frameioContext._frameioInterface.getFolderTree(this.id);
        this._rootFolder = this.createFolder(response.data, response.data['root'][0]);
        return this._rootFolder;
    }
    async createReviewLink(display_name) {
        const ctx = this.context;
        const response = await ctx._frameioInterface.createReviewLink(this.id, display_name);
        const session = ctx._objectPool.createUpdateSession();
        const review = FrameioReviewLinkUpdater.updateRemoteItem(session, response);
        session.commit();
        if (this._reviews) {
            // update the cache of reviews owned by this project
            // by adding the newly created review.
            this._reviews.push(review);
        }
        return review;
    }
}
const FrameioProjectUpdater = new ItemUpdater({
    localItemType: FrameioProject,
    remoteIdProperty: 'id',
    propertyUpdater: new PropertyUpdater({
        '_name': 'name',
        '_ownerId': 'owner_id',
        '_rootAssetId': 'root_asset_id',
        '_rootFolder': {
            // when the update reads response.root_asset
            // ( which matches name: 'root_asset' ),
            // it will invoke set: with the child object
            //
            name: 'root_asset',
            format: 'object',
            set: (rootFolder, dest) => {
                const project = dest;
                const pool = project.context._objectPool;
                const session = pool.createUpdateSession();
                const folder = FrameioFolderUpdater.updateRemoteItem(session, rootFolder);
                session.commit();
                // @ts-ignore
                project._rootFolder = folder;
            },
        },
        '_team': {
            // The owner property of an IFrameioAccountResponse is a nested
            // object of type IFrameioUserResponse, so let's construct that
            // while we are here.
            set: (team, dest) => {
                const project = dest;
                const context = project.context;
                const pool = context._objectPool;
                const session = pool.createUpdateSession();
                project['_team'] = FrameioTeamUpdater.updateRemoteItem(session, team);
                session.commit();
            },
            name: 'team',
            format: 'object'
        },
        '_teamId': 'team_id'
    })
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// See https://developer.frame.io/api/reference/operation/getAccount/
//  for schema of the data FrameioAccount encapsulates.
class FrameioAccount extends FrameioBase {
    constructor() {
        super(...arguments);
        this._displayName = '';
        this._ownerId = '';
        this._version = 0;
    }
    /**
     * @package - not yet decided if id's are internal use only.
     *  In the meantime, I'm decorating them to discourage their use.
     */
    get ownerId() {
        return this._ownerId;
    }
    get owner() {
        return this._owner;
    }
    get displayName() {
        return this._displayName;
    }
    get version() {
        return this._version;
    }
    // getTeams() should really be a getter for property teams,
    // but then we'd need an async getter, and I'm not sure
    // of the convention for that.
    // https://medium.com/trabe/async-getters-and-setters-is-it-possible-c18759b6f7e4
    // see getProjectTree() below
    async getTeams() {
        let teams = this._teams;
        if (!teams) {
            const ctx = this.context;
            const teamsResponse = await ctx._frameioInterface.getTeams(this.id);
            const session = ctx._objectPool.createUpdateSession();
            teams = FrameioTeamUpdater.updateRemoteItems(session, teamsResponse);
            session.commit();
            this._teams = teams;
        }
        return teams;
    }
    async getSharedProjects() {
        let shared = this._sharedProjects;
        if (!shared) {
            const ctx = this.context;
            const projectsResponse = await ctx._frameioInterface.getSharedProjects(this.id);
            const session = ctx._objectPool.createUpdateSession();
            shared = FrameioProjectUpdater.updateRemoteItems(session, projectsResponse);
            session.commit();
            this._sharedProjects = shared;
        }
        return shared;
    }
    async getSharedTeams() {
        const projects = await this.getSharedProjects();
        const set = new Set();
        const teams = [];
        // The array we return should not contain duplicates,
        // but a team could have more than one shared projects,
        // so use a set to unique the teams we add to the array.
        for (const project of projects) {
            const id = project.teamId;
            if (!set.has(id)) {
                const team = await project.getTeam();
                set.add(id);
                teams.push(team);
            }
        }
        return teams;
    }
    // same return as getTeams(), but we pre-load the projects of each team
    // before resolving the promise.
    async getProjectTree() {
        const teams = await this.getTeams();
        const promises = [];
        teams.forEach((team) => {
            promises.push(team.getProjects());
        });
        await Promise.all(promises);
        return teams;
    }
}
const FrameioAccountUpdater = new ItemUpdater({
    localItemType: FrameioAccount,
    remoteIdProperty: 'id',
    propertyUpdater: new PropertyUpdater({
        '_displayName': 'display_name',
        '_owner': {
            // The owner property of an IFrameioAccountResponse is a nested
            // object of type IFrameioUserResponse, so let's construct that
            // while we are here.
            set: (owner, dest) => {
                const account = dest;
                const context = account.context;
                const pool = context._objectPool;
                const session = pool.createUpdateSession();
                account['_owner'] = FrameioUserUpdater.updateRemoteItem(session, owner);
                session.commit();
            },
            name: 'owner',
            format: 'object'
        },
        '_ownerId': 'owner_id',
        '_version': { name: 'version', format: 'number' }
    })
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
//----------------------------------------------------------------
class FrameioContext extends TypedEventEmitter {
    constructor(_core) {
        super();
        this._core = _core;
        /**
         * @package - internal use only
         */
        this._objectPool = new ItemPool(this._core, 'frameio');
        /**
         * @package - internal use only
         */
        this._frameioInterface = new FrameioInterface(this);
        /**
         * @package - internal use only
         */
        this._reviewServiceInterface = new AssetReviewServiceInterface(this._core);
        this._reviewLinksForDocument = new Map();
    }
    /**
     * @package
     *
     * We _updateUserId() whenever the native FrameioAssetContext::authInfo_
     * changes, to allow us to reset any user specific data when the user id
     * changes.
     */
    _updateCurrentUserId(id) {
        if (!id || (this._currentUserId !== id)) {
            this._currentUserId = id;
            this._currentUser = undefined;
            this._accounts = undefined;
            this._reviewLinksForDocument.clear();
        }
    }
    _mapKeyForSourceDocument(source) {
        // We need a hash key for a Map<>,
        // so use the assetId of cloud documents,
        // and the documentId of local documents.
        if (source.documentId) {
            return source.documentId;
        }
        // ignore the optional assetRegion and repositoryId
        return source.assetId;
    }
    _retainUntilTimeout(weak, milliseconds = 2 * 60 * 1000) {
        const obj = weak.deref();
        // obj will be retained until the timeout fires
        // because it's referenced in the handler.
        setTimeout(function () { return obj; }, milliseconds);
    }
    _handleReviewLinksChangeEvent(json) {
        // after a successful FrameioReviewLink.updateReviewLink(),
        // we use SAM.cpp to dispatch an event to all DOM's,
        // so they can each update FrameioContext._reviewLinksForDocument
        //
        // For example, when ShareSheet creates a new review link,
        // ShareSheet and Commenting want to update their cache.
        //
        // See also FrameioReviewLink._dispatchReviewLinksChangeEvent()
        const param = JSON.parse(json);
        const response = param.reviewLink;
        const key = this._mapKeyForSourceDocument(param.source);
        const session = this._objectPool.createUpdateSession();
        const reviewLink = FrameioReviewLinkUpdater.updateRemoteItem(session, response);
        session.commit();
        const weak = new WeakRef([{ reviewLink }]);
        this._reviewLinksForDocument.set(key, weak);
        this._retainUntilTimeout(weak);
        // let client know the reviewLinksForDocument has changed.
        this.dispatch(SharedFrameioReviewLinksChangeEvent);
    }
    async isAuthenticated() {
        return await this._frameioInterface.isAuthenticated();
    }
    async authenticationStatus() {
        return await this._frameioInterface.authenticationStatus();
    }
    // !!@ Consider making this package scoped
    async getToken() {
        return this._frameioInterface.getToken();
    }
    async refreshToken() {
        return this._frameioInterface.refreshToken();
    }
    /* Call with void parameters to rely on native authentication.
     * The return is a token.
     */
    async authenticate(info) {
        return await this._frameioInterface.authenticate(info);
    }
    async deauthenticate() {
        return await this._frameioInterface.deauthenticate();
    }
    async getCurrentUser() {
        let user = this._currentUser;
        if (!user) {
            const response = await this._frameioInterface.getCurrentUser();
            const session = this._objectPool.createUpdateSession();
            user = FrameioUserUpdater.updateRemoteItem(session, response);
            session.commit();
            this._currentUser = user;
        }
        return user;
    }
    async getAccounts() {
        let accounts = this._accounts;
        if (!accounts) {
            const response = await this._frameioInterface.getAccounts();
            const session = this._objectPool.createUpdateSession();
            accounts = FrameioAccountUpdater.updateRemoteItems(session, response);
            session.commit();
            this._accounts = accounts;
        }
        return accounts;
    }
    _getLocalAncestors(id) {
        // Based on prior usage of the package, it's possible
        // we can build ancestors from already local objects
        // in the _objectPool. If so, return the completed
        // array so getAncestors() can avoid the server call.
        //
        // However, if not all of the ancestors are locally available,
        // make the server call to get the entire array.
        const ancestors = new Array();
        for (;;) {
            const asset = this._objectPool.getLocalItemById(id);
            if (asset === undefined) {
                // At least one item isn't local,
                // so we'll need to GET the ancestors from the server.
                return undefined;
            }
            // add latest ancestor to front of array
            ancestors.unshift(asset);
            // walk up one link of the ancestor chain
            if (asset.parentId) {
                id = asset.parentId;
                continue;
            }
            // we've reached the root asset of the project
            break;
        }
        return ancestors;
    }
    async getAncestors(id) {
        // ideally, the whole ancestor chain is already local,
        // and we can fulfill this promise without a server call.
        let ancestors = this._getLocalAncestors(id);
        if (!ancestors) {
            const response = await this._frameioInterface.getAncestors(id);
            const session = this._objectPool.createUpdateSession();
            // IFrameioAssetResponse is a special case, as it corresponds to multiple classes
            // inheriting from FrameioAsset ( FrameioFile, FrameioFolder, FrameioVersionStack ),
            // so use a utility function to select the correct updater for the type.
            //
            // See also FrameioFolder.getChildren()
            ancestors = response.map((asset) => frameioAssetUpdater(session, asset));
            session.commit();
        }
        return ancestors;
    }
    async getReviewLinksForDocument(source, refresh = true) {
        const key = this._mapKeyForSourceDocument(source);
        const weak = this._reviewLinksForDocument.get(key);
        let result = weak ? weak.deref() : undefined;
        if (!refresh && result) {
            this._retainUntilTimeout(weak); // non-null result means weak is also non-null
            return result; // return cached result
        }
        // compute result from scratch
        result = [];
        // The Review Service maintains a database of FrameioReview links we've
        // created maintained by our calls to ReviewServiceInterface.createReviewLinks()
        // and ReviewServiceInterface.updateReviewLinks().
        // Get the list of review links
        const reviews = await this._reviewServiceInterface.getReviewLinks(source);
        // I'm uncertain of the list ordering, so sort it by date.
        reviews.sort((a, b) => (b['repo:createdDate'] - a['repo:createdDate']));
        // The id's associated with a review link may no longer be valid
        // ( i.e. the link has been deleted ) or not currently valid
        // ( i.e. we've logged into the wrong account ), so we need
        // to filter the list to only containing currently valid
        // review links.
        //
        // NOTE: As we cannot distinquish between no longer valid and
        // not currently valid, it's not safe to
        // ReviewServiceInterface.deleteReviewLinks() to clean up the
        // list.
        for (let i = 0; i < reviews.length; i++) {
            const review = reviews[i];
            const projectId = review['frameio:projectId'];
            const reviewLinkId = review['frameio:reviewLinkId'];
            const itemId = review['frameio:artifactId'];
            const etag = review['repo:etag'];
            // review is valid if we can obtain a reviewLink using reviewLinkId,
            // and we can obtain an item using itemId.
            //
            // ShareSheet potentially shows toast when the most recent reviewLinks
            // are invalid, so we push FrameioReviewLinkForDocumentError into
            // result when there's an error validating review.
            //
            // Because validation is expensive, and because reviews[] grows
            // unbounded, and because ShareSheet only cares whether there is
            // 0 or 1 valid reviews, we stop after adding a valid reviewLink
            // to result.
            let reviewLink = undefined;
            // obtain the reviewLink directly from Frame.io
            try {
                const response = await this._frameioInterface.getReviewLink(reviewLinkId);
                const session = this._objectPool.createUpdateSession();
                reviewLink = FrameioReviewLinkUpdater.updateRemoteItem(session, response);
                session.commit();
            }
            catch (e) {
                // we expect it to throw when a review has been disabled.
            }
            try {
                if (!reviewLink) {
                    // disabled reviewLink's are known to throw 403 errors,
                    // so as a fallback, try to get the project, the project's
                    // list of reviewLinks, and find reviewLinkId in the list
                    // (disabled reviewLink's are known to be reachable this way)
                    // project is cached in the object pool,
                    // and project._reviewLinks is memoized,
                    // so although we are in a loop,
                    // each method should never hit the network more than once.
                    const project = await this.getProject(projectId);
                    const reviewLinks = await project.getReviewLinks();
                    reviewLink = reviewLinks.find(({ id }) => (id === reviewLinkId));
                }
                if (reviewLink) {
                    // Copy data that's on the Review Service and not Frame.io
                    // into the reviewLink.
                    reviewLink['_itemId'] = itemId;
                    reviewLink['_etag'] = etag;
                    // getItem() rejects when itemId isn't valid.
                    await reviewLink.getItem();
                    result.push({ reviewLink });
                    break;
                }
                else {
                    const error = SAMError.create(SAMErrorCode.NOT_FOUND, `frameio:reviewLinkId = ${reviewLinkId} not found in projectId = ${projectId}`);
                    result.push({ error });
                }
            }
            catch (error) {
                result.push({ error });
            }
        }
        const ref = new WeakRef(result);
        // cache the newly computed result
        this._reviewLinksForDocument.set(key, ref);
        this._retainUntilTimeout(ref);
        return result;
    }
    async getAccount(id) {
        const accounts = await this.getAccounts();
        let account = accounts.find(element => (element.id === id));
        if (!account) {
            const response = await this._frameioInterface.getAccount(id);
            const session = this._objectPool.createUpdateSession();
            account = FrameioAccountUpdater.updateRemoteItem(session, response);
            session.commit();
        }
        return account;
    }
    async getAsset(id) {
        // avoid server call for local asset
        let asset = this._objectPool.getLocalItemById(id);
        if (!asset) {
            // fetch remote asset with a server call
            const response = await this._frameioInterface.getAsset(id);
            const session = this._objectPool.createUpdateSession();
            asset = frameioAssetUpdater(session, response);
            session.commit();
        }
        return asset;
    }
    async getTeam(id) {
        // avoid server call for local asset
        let team = this._objectPool.getLocalItemById(id);
        if (!team) {
            // fetch remote asset with a server call
            const response = await this._frameioInterface.getTeam(id);
            const session = this._objectPool.createUpdateSession();
            team = FrameioTeamUpdater.updateRemoteItem(session, response);
            session.commit();
        }
        return team;
    }
    async getProject(id) {
        // avoid server call for local asset
        let project = this._objectPool.getLocalItemById(id);
        if (!project) {
            // fetch remote asset with a server call
            const response = await this._frameioInterface.getProject(id);
            const session = this._objectPool.createUpdateSession();
            project = FrameioProjectUpdater.updateRemoteItem(session, response);
            session.commit();
        }
        return project;
    }
    async getComment(id) {
        // avoid server call for local asset
        let comment = this._objectPool.getLocalItemById(id);
        if (!comment) {
            // fetch remote asset with a server call
            const response = await this._frameioInterface.getComment(id);
            const session = this._objectPool.createUpdateSession();
            comment = FrameioCommentUpdater.updateRemoteItem(session, response);
            session.commit();
        }
        return comment;
    }
    async getAdobeSusiInfo() {
        return await this._frameioInterface.getAdobeSusiInfo();
    }
}
function frameioAssetUpdater(session, asset) {
    // frame.io multiplexes asset.type's 'file', 'folder', 'version_stack' and 'bundle' into IFrameioAssetResponse,
    // but we have separate FrameioFile, FrameioFolder, FrameioVersionStack classes that inherit from FrameioAsset.
    if (asset.type === 'file') {
        const fileSession = session;
        return FrameioFileUpdater.updateRemoteItem(fileSession, asset);
    }
    else if (asset.type === 'folder') {
        const folderSession = session;
        return FrameioFolderUpdater.updateRemoteItem(folderSession, asset);
    }
    else if (asset.type === 'version_stack') {
        const versionStackSession = session;
        return FrameioVersionStackUpdater.updateRemoteItem(versionStackSession, asset);
    }
    throw SAMError.create(SAMErrorCode.UNSUPPORTED, `Invalid Frameio asset type ${asset.type}`);
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const LIBRARY_DATA_CHANGED = 'library_data_changed';
class LibraryDataChangedEvent extends SAMEvent {
    constructor() {
        super(LIBRARY_DATA_CHANGED);
    }
}
const LibraryDataChangedEventInstance = new LibraryDataChangedEvent();

/*************************************************************************
*   ADOBE CONFIDENTIAL
*
*   Copyright 2024 Adobe
*   All Rights Reserved.
*
*   NOTICE:  All information contained herein is, and remains
*   the property of Adobe and its suppliers, if any. The intellectual
*   and technical concepts contained herein are proprietary to Adobe
*   and its suppliers and are protected by all applicable intellectual
*   property laws, including trade secret and copyright laws.
*   Dissemination of this information or reproduction of this material
*   is strictly forbidden unless prior written permission is obtained
*   from Adobe.
**************************************************************************/
const RESTRICT_EDIT_CHANGED = 'restrict_edit_changed';
class RestrictEditChangedEvent extends SAMEvent {
    constructor() {
        super(RESTRICT_EDIT_CHANGED);
    }
}
const RestrictEditChangedEventInstance = new RestrictEditChangedEvent();

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class Library extends AdobeAsset {
    constructor(_core, id, metadata) {
        super(_core, id, metadata);
        this._core = _core;
        this.isLibrary = true;
        this._libraryInterface = new LibraryInterface(this._core);
        this._libraryDataLoadInfo = {
            state: 'NOT_LOADED'
        };
        LazyLoadManager.manageLazyLoading(this, [{
                event: RestrictEditChangedEventInstance,
                fetchData: this._refreshEditingRestricted
            }]);
    }
    /**
     * @package -- for use in SAM.js only
     */
    // Check if melville data has been loaded at least once, not just repoMetadata
    get _libraryLoaded() {
        return this._libraryDataLoadInfo.state === 'LOADED';
    }
    /**
     * @package -- for use in SAM.js only
     * Set a promise to resolve when libraries are loaded.  Returns a resolve function that needs to be called after
     * the library is loaded, or undefined if something else is already calling for melville data.
     */
    _setLibraryLoadState(state) {
        const { state: priorState } = this._libraryDataLoadInfo;
        if (state === priorState) {
            return;
        }
        this._libraryDataLoadInfo.state = state;
        if (state === 'LOADED') {
            if (this._libraryDataLoadInfo.resolve) {
                this._libraryDataLoadInfo.resolve();
            }
        }
        else if (state === 'LOADING') {
            let resolve;
            this._libraryDataLoadInfo.promise = new Promise(r => { resolve = r; });
            this._libraryDataLoadInfo.resolve = resolve;
        }
        else if (state === 'NOT_LOADED') {
            // Transitioning to NOT_LOADED means the data still needs to be loaded. Notify listeners
            // in order to trigger lazy-loading. Unblock anything waiting on the Promise.
            if (this._libraryDataLoadInfo.resolve) {
                this._libraryDataLoadInfo.resolve();
            }
            this.emit(LIBRARY_DATA_CHANGED);
        }
    }
    /**
     * Starts loading Melville data for a single Library, if it hasn't already been loaded, or waits
     * for in-flight load to complete.
     *
     * @parameter canRetry {boolean} (optional) For cases in Library that need to ensure Library
     *         data loads, but it may have failed from a listing, it can try one extra time.
     *
     * @package -- for use in SAM.js only
     */
    async _waitForLibraryLoad(canRetry = false) {
        this._populateLibraryDataIfNotLoading();
        await this._libraryDataLoadInfo.promise;
        if (canRetry && !this._libraryLoaded) {
            await this._waitForLibraryLoad(false);
        }
        return this._libraryDataLoadInfo.promise;
    }
    get displayName() {
        this._populateLibraryDataIfNotLoading();
        // Melville will always have the correct name, whereas the RAPI .name will not have the name for libraries in sync groups.
        return this._displayName;
    }
    /**
     * Returns the Library's current roles.
     *
     */
    get roles() {
        this._populateLibraryDataIfNotLoading();
        return this._libraryRoles;
    }
    get renditionGrid() {
        this._populateLibraryDataIfNotLoading();
        return this._renditionGrid;
    }
    /**
     * Makes a service request to update the Library's metadata, then updates local Library model.
     *
     */
    async setRoles(roles) {
        const newRoles = { roles: Array.isArray(roles) ? roles : [roles] };
        await this._libraryInterface.updateMetadata(this.id, newRoles);
        this._setLibraryLoadState('NOT_LOADED');
        await this._populateLibraryDataIfNotLoading();
    }
    async _load({ repoMetadata, appMetadata, effectivePermission } = {}) {
        /* When we hit an ONS change, ONS emits a 'change' event, which triggers a re-render.  We want that re-render
         * to also re-render the library data from melville so we can get new rendition grids, roles, etc. that might
         * have been changed by other users.
         */
        await super._load({ repoMetadata, appMetadata, effectivePermission });
        this._setLibraryLoadState('NOT_LOADED');
    }
    /**
     * Return whether the library is bookmarked (aka followed) by the user.
     * Will return undefined if data has not been loaded yet.
    */
    // Blocked by CCXB-20127: Bookmark data may currently be out of date due to a caching issue in Melville.
    // CCXC-20748: If we eventually support loading unbookmarked public libraries, bookmark data will already
    // be present in the library data when unbookmarked. Thus, we will need to separately load the user's bookmarks to verify isBookmarked.
    get isBookmarked() {
        this._populateLibraryDataIfNotLoading();
        return this._libraryLoaded ? !!this._bookmark : undefined;
    }
    get _isPublic() {
        // Warning: `ownership` is 'public' for a public library in most melville responses,
        // such as to the 'public' end points, or GET /libraries/<manifestId>.
        // However, GET /libraries/<assetId> will return 'incoming' for a public library,
        // and there's no way to tell it is public from the response
        // -- even the `id` is no longer the public library id but a new id.
        // But as long as we don't fetch public libraries that way, this definition is fine.
        // Warning2: `isPublicLink` from USS loaders is false for public libraries.
        return this._libraryLoaded ? this._ownership === 'public' : undefined;
    }
    get libraryManifestId() {
        this._populateLibraryDataIfNotLoading();
        return this._libraryManifestId;
    }
    // move calls moveLibrary without specifying restrictEdit, thus getting the default of "team_view"
    async move(destFolder) {
        return this.moveLibrary(destFolder);
    }
    /**
     * Move a Library
     * Reqired properties
     * @param destFolder - The folder to move the library to
     * Optional properties
     * @param restrictEdit {boolean} (optional) If true, will tell Melville to move the library with TEAM_VIEW access.
     *      If false, will tell Melville to move with TEAM_EDIT access. If undefined, it is up to Melville to decide.
     */
    async moveLibrary(destFolder, restrictEdit) {
        const libraryData = await this._libraryInterface.moveLibrary(this.cloudID, destFolder.cloudID, restrictEdit);
        const session = this._core.hostAssetContext.assetPool.createUpdateSession('move library');
        const libraryContext = await this._core.getLibraryContext();
        if (libraryData.library_urn !== this.id) {
            // Melville currently does a "fake" move using copy and delete.
            // Melville team hopes to eventually turn this into a real move.
            // For now we will just discard the old library and rely on ONS to create a new one in our pool.
            session.queueDiscardedItem(this);
        }
        else {
            // If the cloudID didn't change, then it needs to be updated to reflect its new location
            libraryContext._libraryUpdater.updateLocalItem(session, this, libraryData);
        }
        session.commit();
    }
    async rename(newName) {
        await this._libraryInterface.updateMetadata(this.id, { name: newName });
        this._setLibraryLoadState('NOT_LOADED');
    }
    /**
     * Discard the library through melville, which also cleans up bookmarks.
     * This changes its repo:state to DISCARDED, but it can later be restored or permanently deleted.
     */
    async discard() {
        if (this.isDiscarded) {
            throw SAMError.create(SAMErrorCode.INVALID_OPERATION, 'Asset is already discarded');
        }
        await this._libraryInterface.discardLibrary(this.cloudID);
        const libraryContext = await this._core.getLibraryContext();
        const session = libraryContext._assetPool.createUpdateSession('discard library'); // tracks changes to the asset
        // Update the local item with values from the remote item
        this._core.hostAssetContext._adobeAssetUpdater.updateLocalItem(session, this, { 'repo:state': 'DISCARDED' });
        session.commit(); // fire notifications for tracked changes
    }
    async restore() {
        if (!this.isDiscarded) {
            throw SAMError.create(SAMErrorCode.INVALID_OPERATION, 'Cannot restore an asset that is not discarded');
        }
        await this._libraryInterface.restoreLibrary(this.cloudID);
        const session = this._core.hostAssetContext.assetPool.createUpdateSession('restore library');
        this._core.hostAssetContext._adobeAssetUpdater.updateLocalItem(session, this, { 'repo:state': 'ACTIVE' });
        session.commit();
    }
    /*
     * Override AdobeAsset._copy() so that both AdobeAsset.copy() and AdobeAsset.duplicate()
     * get handled by Melville. Melville handles rename on conflict.
     */
    async _copy(destFolder, destName) {
        return this.copyLibrary(destFolder, destName);
    }
    /**
     * Copy a Library
     * Optional properties
     * @param destFolder - If destFolder is undefined then the library will be copied in the User's personal library
     *     folder
     * @param destName - If destName is undefined then the current library name will be preserved
     * @param restrictEdit (optional) If true, copies the library with TEAM_VIEW access. If false, copies the library
     *      with TEAM_EDIT access. If undefined, Melville will determine the access.
     * @returns - The new copied Library
     */
    async copyLibrary(destFolder, destName, restrictEdit) {
        let newName = destName ? destName : this.name;
        // get name without file extension if any (this.name has .cclibc)
        newName = path.basename(newName, path.extname(newName));
        // Melville will fix any name conflicts, unlike R-API
        const libraryData = await this._libraryInterface.copyLibrary(this.cloudID, newName, destFolder === null || destFolder === void 0 ? void 0 : destFolder.cloudID, restrictEdit);
        const session = this._core.hostAssetContext.assetPool.createUpdateSession('copy library');
        const libraryContext = await this._core.getLibraryContext();
        const library = libraryContext._libraryUpdater.updateRemoteItem(session, libraryData);
        session.commit();
        return library;
    }
    /**
     * Makes a service request to bookmark the library, then updates local Library model.
     * Applies to project (aka team) and shared private libraries. Public libraries not yet supported.
     */
    async bookmark() {
        await this._waitForLibraryLoad(true);
        // Temporarily disabled isBookmarked check due to CCXB-20127: Bookmark data may currently be out of date due to a caching issue in Melville.
        // if (this.isBookmarked) { return; }
        let bookmarkData = {};
        if (this._isPublic) {
            return Promise.reject('Bookmarking public libraries not implemented yet.');
            // CCXC-20748: This code should be correct but we don't need it yet as we don't currently load unbookmarked public libraries,
            // and if we do there will be complications with isBookmarked as bookmark data will already be present in the library data.
            // bookmarkData = {
            //     id: this.bookmark?.id, // expects the libraryManifestId format, which is found in the bookmark on an unbookmarked public library 🤯
            //     type: 'public',
            //     url: `https://public.adobecc.com/libraries/{this.id}` // expects the legacy public library id format in the link. I am not sure it's ok to hard code this
            // };
        }
        else {
            bookmarkData = {
                id: this.libraryManifestId,
                type: 'collab',
                urn: this.cloudID
            };
        }
        const { bookmarks } = await this._libraryInterface.bookmarkLibraries([bookmarkData]);
        const bookmark = bookmarks.find(bookmark => bookmark.id === this.libraryManifestId);
        const libraryContext = await this._core.getLibraryContext();
        const session = libraryContext._assetPool.createUpdateSession('bookmark library');
        libraryContext._libraryUpdater.updateLocalItem(session, this, { bookmark });
        session.commit();
    }
    /**
     * Makes a service request to remove a bookmark from the library, then updates local Library model.
     */
    async unbookmark() {
        var _a;
        await this._waitForLibraryLoad(true);
        const bookmarkId = (_a = this._bookmark) === null || _a === void 0 ? void 0 : _a.id;
        if (!bookmarkId) {
            return;
        }
        await this._libraryInterface.unbookmarkLibrary(bookmarkId); // 404 means it's already unbookmarked
        const libraryContext = await this._core.getLibraryContext();
        const session = libraryContext._assetPool.createUpdateSession('unbookmark library');
        libraryContext._libraryUpdater.updateLocalItem(session, this, { bookmark: undefined });
        session.commit();
    }
    // calls out to melville to get the library data
    async _populateLibraryDataIfNotLoading() {
        if (this._libraryDataLoadInfo.state === 'NOT_LOADED') {
            this._setLibraryLoadState('LOADING');
            const libraryContext = await this._core.getLibraryContext();
            const libraryData = await this._libraryInterface.getLibraryData(this.id);
            const session = this._core.hostAssetContext.assetPool.createUpdateSession();
            libraryContext._libraryUpdater.updateLocalItem(session, this, libraryData);
            this._setLibraryLoadState('LOADED');
            session.commit();
        }
    }
    /**
     * Returns `true` if the Library has inherited editing/sharing rights blocked, `false` if it is not. Direct
     * collaborators and USM users are not affected.
     *
     * Lazy-loaded: the property will be `undefined` on initial access, and will contain the old value when it is being
     * updated. Use Mobx to monitor for changes, or subscribe to 'restrict_edit_changed' event.
     */
    get isEditingRestricted() {
        return this._isEditingRestricted;
    }
    // eslint-disable-next-line no-dupe-class-members
    async setEditingRestricted(restrictEdit, addCurrentUserAsEditor) {
        await this._ensureLink(RelType.AC_POLICY);
        const assetInterface = await this._getAssetInterface();
        await assetInterface.setEditingInheritanceRestricted(restrictEdit, addCurrentUserAsEditor);
        this._isEditingRestricted = restrictEdit;
        LazyLoadManager.updatedExternally(this, RESTRICT_EDIT_CHANGED);
    }
    async _refreshEditingRestricted() {
        await this._ensureLink(RelType.AC_POLICY);
        const assetInterface = await this._getAssetInterface();
        this._isEditingRestricted = await assetInterface.getEditingInheritanceRestricted();
    }
    /**
     * @package -- only for use in SAM.js
     */
    async _reloadPermissions() {
        // When policy change is detected, mark restrictEdit data stale. Meanwhile, leave the last known state of
        // _isEditingRestricted unchanged so it doesn't cause the UI to flash when the toggle is being changed.
        LazyLoadManager.setLoadStatus(this, RESTRICT_EDIT_CHANGED, LoadStatus.NotLoaded);
        return super._reloadPermissions();
    }
}
__decorate([
    observable(LibraryDataChangedEventInstance)
], Library.prototype, "displayName", null);
__decorate([
    observable(LibraryDataChangedEventInstance)
], Library.prototype, "roles", null);
__decorate([
    observable(LibraryDataChangedEventInstance)
], Library.prototype, "renditionGrid", null);
__decorate([
    observable(LibraryDataChangedEventInstance)
], Library.prototype, "isBookmarked", null);
__decorate([
    observable(LibraryDataChangedEventInstance)
], Library.prototype, "_isPublic", null);
__decorate([
    observable(LibraryDataChangedEventInstance)
], Library.prototype, "libraryManifestId", null);
__decorate([
    lazyLoaded(RestrictEditChangedEventInstance, '_isEditingRestricted')
], Library.prototype, "isEditingRestricted", null);
// These are properties specific to melville that are always safe to update as they will not overwrite RAPI data.
// These properties should match LibrarySpecificData.
const safePropertyUpdaterTypes = {
    '_bookmark': { name: 'bookmark', format: 'object' },
    '_libraryRoles': { name: 'roles', format: 'object', isArray: true },
    '_ownership': 'ownership',
    // TODO: When supporting public libraries, we should use the libraryResponse.bookmark?.id for the manifest id, as the `id` contains
    // the to-be-deprecated public lib id format. The format of the id is how Melville knows it is a public library.
    // Note: Public libs returned from melville lists will all have a bookmark,
    // though an unbookmarked lib can be fetched by id.
    '_libraryManifestId': 'id',
    '_renditionGrid': { name: 'rendition_grid', format: 'object' },
    '_displayName': 'name', // May differ from AdobeAsset.name for libraries in sync groups
};
// These meville properties will overwrite RAPI data. This is not always safe to do.
// Know which melville call you are making to know if the data may be stale, in which which case you should only pass LibrarySpecificData.
// Note that safePropertyUpdaterTypes and rapiOverridesPropertyUpdaterTypes should be mutually exclusive.
const rapiOverridesPropertyUpdaterTypes = {
    '_etag': 'manifest_etag',
    '_path': 'storage_path',
    '_version': 'version',
    '_created': { name: 'created_date', format: 'date_as_number' },
    '_modified': { name: 'modified_date', format: 'date_as_number' },
    '_discarded': { name: 'archived_date', format: 'date_as_number' },
    '_createdBy': 'created_by',
    '_discardedBy': 'archived_by',
    // TODO: These will need to be verified that they match the format of the repo metadata if we need them:
    // '_storageRegion': { name: 'region', get: getUSSStorageRegion, format: 'string' }, // getUSSStorageRegion will uppercase 'va6c2' for consistency. are the rest of the values matching USS?
    // '_type': 'dc:format', // document_type?
    // '_storageAssignee': { name: 'storage_assignee', format: StorageAssigneeFormat },
    // '_permissions': { get: getPermission, format: 'number' },
    // '_isShared': { name: 'isShared', format: 'boolean' },
    // '_isSharedWithYouAsset': { name: 'sharedViaInvite', format: 'boolean' },
    // PropertyUpdater reads from _appMetadata._data for comparison
    // '__links': { name: '_links', format: 'object' },
    // TODO: Fill in other LibraryData properties as needed
};
const propertyUpdaterTypes = Object.assign(Object.assign({}, safePropertyUpdaterTypes), rapiOverridesPropertyUpdaterTypes);
const libraryItemUpdaterOptions = {
    localItemType: Library,
    remoteIdProperty: 'library_urn',
    // Any changes to these properties will trigger watchers of LIBRARY_DATA_CHANGED.
    // Other properties updated will trigger the  default 'change' event.
    changeEventMap: new Map([
        ['_libraryRoles', LibraryDataChangedEventInstance],
        ['_displayName', LibraryDataChangedEventInstance],
        ['_libraryManifestId', LibraryDataChangedEventInstance],
        ['_bookmark', LibraryDataChangedEventInstance],
        ['_ownership', LibraryDataChangedEventInstance],
        ['_renditionGrid', LibraryDataChangedEventInstance],
    ]),
    // Mapping LibraryData to Library
    propertyUpdater: new PropertyUpdater(propertyUpdaterTypes)
};
const LibraryUpdater = new ItemUpdater(libraryItemUpdaterOptions);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
/**
 * Provides all Library related functionality like getting
 * lists of libraries among other features.
 *
 * @experimental This API is in an experimental phase so expect that
 * API"s will not be final and there will be breaking changes
 * without bumping the major version until this comment is removed.
 */
class LibraryContext {
    constructor(_core, __assetPool) {
        this._core = _core;
        this.__assetPool = __assetPool;
        /**
         * @package -- only for use in SAM.js
         * Updaters shouldn't be accessed directly.
         */
        this._libraryUpdater = LibraryUpdater;
    }
    /**
     * @package -- for use in SAM.js only
     */
    get _assetPool() {
        return this.__assetPool;
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class CustomModel extends AdobeAsset {
    constructor() {
        super(...arguments);
        this.isCustomModel = true;
        this._inferenceReady = false;
        this._publishedState = CustomModelPublishedState.UNPUBLISHED;
        this._samplePrompt = '';
        this._displayName = '';
    }
    get inferenceReady() {
        return this._inferenceReady;
    }
    get publishedState() {
        return this._publishedState;
    }
    get samplePrompt() {
        return this._samplePrompt;
    }
    get renditionUrl() {
        return this._renditionUrl;
    }
    get displayName() {
        return this._displayName;
    }
}
__decorate([
    observable
], CustomModel.prototype, "inferenceReady", null);
__decorate([
    observable
], CustomModel.prototype, "publishedState", null);
__decorate([
    observable
], CustomModel.prototype, "samplePrompt", null);
__decorate([
    observable
], CustomModel.prototype, "renditionUrl", null);
__decorate([
    observable
], CustomModel.prototype, "displayName", null);
const CustomModelUpdater = new ItemUpdater({
    localItemType: CustomModel,
    remoteIdProperty: 'assetId',
    propertyUpdater: new PropertyUpdater({
        '_inferenceReady': { name: 'inferenceReady', format: 'boolean' },
        // TODO: create a type with array of choices?
        '_publishedState': 'publishedState',
        '_samplePrompt': 'samplePrompt',
        '_renditionUrl': 'renditionUrl',
        '_displayName': 'displayName',
        // on AdobeAsset
        '_links': { name: '_links', format: 'object' },
        '_name': 'assetName',
        '_path': 'path',
        '_created': { name: 'createDate', format: 'date' },
        '_modified': { name: 'modifyDate', format: 'date' },
        '_version': 'version',
        '_etag': 'etag',
        '_storageRegion': 'region',
        '_size': { name: 'size', format: 'number' }
    }),
});

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const OrderFieldToCustomModelOrderBy = {
    'name': CustomModelOrderBy.NAME,
    'created': CustomModelOrderBy.CREATE_DATE,
    'modified': CustomModelOrderBy.MODIFY_DATE
};
const OrderFieldToComparator$6 = {
    name(a, b) {
        return compareStrings(a.name, b.name);
    },
    created(a, b) {
        return compareValues(a.created, b.created);
    },
    modified(a, b) {
        return compareValues(a.modified, b.modified);
    }
};
class CustomModelOrder extends createItemOrderFactory(OrderFieldToComparator$6) {
}
CustomModelOrder.default = new CustomModelOrder('descending', 'modified');

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class CustomModelLoader {
    constructor(_assetPool, _core, _customModelInterface) {
        this._assetPool = _assetPool;
        this._core = _core;
        this._customModelInterface = _customModelInterface;
    }
    async loadPage(options) {
        return await this._loadPage(options);
    }
    async _loadPage(options, startIndex) {
        var _a;
        const { order, limit, filter } = options;
        const customModelFilter = filter;
        const listOptions = Object.assign({ orderBy: OrderFieldToCustomModelOrderBy[order.field], limit }, customModelFilter.listingOptions);
        const result = await this._customModelInterface.getCustomModels(startIndex ? Object.assign(Object.assign({}, listOptions), { startIndex }) : listOptions);
        const session = this._assetPool.createLoaderUpdateSession(Date.now() - this._core.environment.getValue('USS_LIST_UPDATE_LATENCY'));
        const items = CustomModelUpdater.updateRemoteItems(session, result.items);
        session.commit();
        // Check if we have another page to load.
        let loadNextPage;
        const nextPageLink = (_a = result.nextPage) === null || _a === void 0 ? void 0 : _a.startIndex;
        if (nextPageLink) {
            loadNextPage = (options) => this._loadPage(options, nextPageLink);
        }
        return {
            items,
            loadNextPage
            // total item count will be available from FPS when this ticket is complete:
            // https://jira.corp.adobe.com/browse/GLOW-47
        };
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const REGION_MAP = {
    VA6C2: 'va6',
    VA6: 'va6',
    VA7: 'va6',
    JPN3: 'jp3',
    IRL1: 'irl1',
};
const fillURLTemplate = (url, queryParams) => {
    const template = UriTemplate(`${url}{?queryParams*}`);
    return template.fillFromObject({ queryParams });
};
class CustomModelInterface extends InterfaceBase {
    constructor(_core) {
        super(_core);
        this._core = _core;
    }
    async _getUrl(endpoint) {
        const homeFolder = await this._core.hostAssetContext.getHomeFolder();
        const region = REGION_MAP[homeFolder.storageRegion];
        const acpHost = this._core.environment.endpoints.ACP_CS_HOST;
        const subdomain = acpHost.split('.')[0];
        const regionalizedSubdomain = `${subdomain}-${region}`;
        // Replaces non-regionalized subdomain with its' regionalized version, using the region of the homeFolder eg.
        //
        //   when storage region = 'JPN3':
        //     platform-cs-stage.adobe.io =>
        //     platform-cs-stage-${REGION_MAP[JPN3]}.adobe.com =>
        //     platform-cs-stage-jpn3.adobe.com
        //
        //   when storage region = 'IRL1':
        //     platform-cs-stage.adobe.io =>
        //     platform-cs-stage-${REGION_MAP[IRL1]}.adobe.com =>
        //     platform-cs-stage-irl1.adobe.com
        return `https://${acpHost.replace(subdomain, regionalizedSubdomain)}/fps/${endpoint}`;
    }
    /**
   * Fetches the list of custom models based on the options provided, allowing search by custom model name
   * @param options : options to sort and filter the projects listing
   * @returns the list of custom models along with the page information and links to the next page
   */
    async getCustomModelsSearch(options) {
        const queryParams = options
            ? JSON.parse(JSON.stringify(options))
            : {};
        const url = fillURLTemplate(await this._getUrl('customModels'), queryParams);
        return this._getResponse(url);
    }
    /**
   * Fetches the list of custom models based on the options provided
   * @param options : options to sort and filter the projects listing
   * @returns the list of custom models along with the page information and links to the next page
   */
    async getCustomModels(options) {
        const queryParams = options
            ? JSON.parse(JSON.stringify(options))
            : {};
        const url = fillURLTemplate(await this._getUrl('customModels'), queryParams);
        return this._getResponse(url);
    }
}

class CustomModelSearchLoader {
    constructor(_assetPool, _core, _customModelInterface) {
        this._assetPool = _assetPool;
        this._core = _core;
        this._customModelInterface = _customModelInterface;
    }
    supportsFilter(filter) {
        // If we don't provide a filter at all, then we cannot use the loader
        return filter !== undefined;
    }
    async loadPage(options) {
        return this._loadPage(options);
    }
    async _loadPage(options, startIndex) {
        var _a;
        const { order, limit, implicitFilter } = options;
        const customModelSearchFilter = implicitFilter;
        const listOptions = Object.assign({ orderBy: OrderFieldToCustomModelOrderBy[order.field], limit }, customModelSearchFilter.listingOptions);
        const result = await this._customModelInterface.getCustomModelsSearch(startIndex ? Object.assign(Object.assign({}, listOptions), { startIndex }) : listOptions);
        const session = this._assetPool.createLoaderUpdateSession(Date.now() - this._core.environment.getValue('USS_LIST_UPDATE_LATENCY'));
        const items = CustomModelUpdater.updateRemoteItems(session, result.items);
        session.commit();
        // Check if we have another page to load.
        let loadNextPage;
        const nextPageLink = (_a = result.nextPage) === null || _a === void 0 ? void 0 : _a.startIndex;
        if (nextPageLink) {
            loadNextPage = (options) => this._loadPage(options, nextPageLink);
        }
        return {
            items,
            loadNextPage
            // total item count will be available from FPS when this ticket is complete:
            // https://jira.corp.adobe.com/browse/GLOW-47
        };
    }
}

class CustomModelSearchFilter extends ImplicitAdobeAssetFilterBase {
    constructor(listingOptions) {
        super();
        this._listingOptions = listingOptions;
    }
    get listingOptions() {
        return this._listingOptions;
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
/**
 * Provides Custom Model functionality such as listing.
 *
 * @experimental This API is in an experimental phase so expect that
 * API"s will not be final and there will be breaking changes
 * without bumping the major version until this comment is removed.
 */
class CustomModelContext {
    constructor(_core, _assetPool) {
        this._core = _core;
        this._assetPool = _assetPool;
        this._customModelInterface = new CustomModelInterface(this._core);
    }
    /**
   * @example
   * const customModelContext = samCore.customModelContext;
   * const customModelSource = customModelContext.customModelSearchSource;
   *
   * Search custom models a user has access to by name. The set of assets returned in a list is static once the
   * search is executed - ie newly created items which fit a list will not be added, edits to items which qualify
   * them to be part of a list will not have any effect. However, discard/delete/permission loss will still be
   * reflected.
   */
    customModelSearchSource(name) {
        // declare an implicitFilter
        const implicitFilter = new CustomModelSearchFilter({ name });
        // declare a CustomModelSearchLoader
        return new ItemSource(this._assetPool, [
            new CustomModelSearchLoader(this._core.hostAssetContext.assetPool, this._core, this._customModelInterface),
        ], implicitFilter);
    }
    /**
   * @example
   * const customModelContext = samCore.customModelContext;
   * const customModelSource = customModelContext.customModelSource;
   *
   * Gets all custom models a user has access to
   */
    get customModelSource() {
        return new ItemSource(this._assetPool, [
            new CustomModelLoader(this._core.hostAssetContext.assetPool, this._core, this._customModelInterface),
        ]);
    }
}
__decorate([
    memoize
], CustomModelContext.prototype, "customModelSearchSource", null);
__decorate([
    memoize
], CustomModelContext.prototype, "customModelSource", null);

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
// If multiple versions of SAM.js are loaded in the same app, they will have different
// contents in the closure of the module, so the Symbol used to store the global instances
// of SAMCore must be a global Symbol.
const $SAM_INSTANCE_MAP = Symbol.for('com.adobe.ccac-sam-js.instances');
// Matches ACPL log level enum
var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["Off"] = 0] = "Off";
    LogLevel[LogLevel["Err"] = 1] = "Err";
    LogLevel[LogLevel["Warn"] = 2] = "Warn";
    LogLevel[LogLevel["Info"] = 3] = "Info";
    LogLevel[LogLevel["Debug"] = 4] = "Debug";
})(LogLevel || (LogLevel = {}));
class SAMCore extends TypedEventEmitter {
    constructor(config, hooks = {}) {
        super();
        this._assetPool = new ItemPool(this, 'project or adobeasset');
        /**
         * @package -- for use in SAM.js only
         */
        this._adobeAssetFactory = new AdobeAssetFactory(this);
        this._fetchIndexState = {};
        this._environment = new Environment(this, config, hooks);
    }
    /**
     * @package -- for use in initializeSharedAssetModel
     */
    _setAssetContext(assetContext) {
        if (this._AssetContext) {
            // If multiple SAM.js instances invoke this, only the first gets to set it. This
            // is likely a temporary condition while SAM.js clients converge on compatible versions.
            console.error('[SAMCore] AssetContext already initialized');
            return;
        }
        this._AssetContext = assetContext;
        this._AssetContextJS = new AssetContext(this, this._assetPool);
    }
    /**
     * @package -- for use in initializeSharedAssetModel
     */
    _setSpaceContext(spaceContext) {
        if (this._SpaceContext) {
            // If multiple SAM.js instances invoke this, only the first gets to set it. This
            // is likely a temporary condition while SAM.js clients converge on compatible versions.
            console.error('[SAMCore] SpaceContext already initialized');
            return;
        }
        this._SpaceContext = spaceContext;
    }
    /**
     * @package -- for use in initializeSharedAssetModel
     */
    _setProjectContext(projectContext) {
        if (this._ProjectContext) {
            // If multiple SAM.js instances invoke this, only the first gets to set it. This
            // is likely a temporary condition while SAM.js clients converge on compatible versions.
            console.error('[SAMCore] ProjectContext already initialized');
            return;
        }
        this._ProjectContext = projectContext;
    }
    /**
     * @package -- for use in initializeSharedAssetModel
     */
    _setClassroomContext(classroomContext) {
        if (this._ClassroomContext) {
            // If multiple SAM.js instances invoke this, only the first gets to set it. This
            // is likely a temporary condition while SAM.js clients converge on compatible versions.
            console.error('[SAMCore] ClassroomContext already initialized');
            return;
        }
        this._ClassroomContext = classroomContext;
    }
    get hostAssetContext() {
        // If initializeSharedAssetModel didn't inject AssetContext, then create one
        if (!this._AssetContext) {
            this._AssetContext = new AssetContext(this, this._assetPool);
        }
        return this._AssetContext;
    }
    get assetLinkManager() {
        return new AssetLinkManager(this.hostAssetContext);
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and removed on or after 2024-10-01
     */
    get spaceContext() {
        // If initializeSharedAssetModel didn't inject SpaceContext, then create one
        if (!this._SpaceContext) {
            this._SpaceContext = new SpaceContext(this);
        }
        return this._SpaceContext;
    }
    get collaborationContext() {
        return new CollaborationContext(this);
    }
    /**
     * Provides all Project related functionality like getting
     * lists of projects among other features.
     */
    get projectContext() {
        // If initializeSharedAssetModel didn't inject ProjectContext, then create one
        if (!this._ProjectContext) {
            this._ProjectContext = new ProjectContext(this, this._assetPool);
        }
        return this._ProjectContext;
    }
    /**
     * Provides all Classroom related functionality.
     *
     * @experimental This API is in an experimental phase so expect that
     * APIs will not be final and there will be breaking changes
     * without bumping the major version until this comment is removed.
     */
    get classroomContext() {
        if (!this._ClassroomContext) {
            this._ClassroomContext = new ClassroomContext(this, this._assetPool);
        }
        return this._ClassroomContext;
    }
    get frameioContext() {
        return new FrameioContext(this);
    }
    async getLibraryContext() {
        if (!this._LibraryContext) {
            // When bundle splitting is implemented, we will need to lazily import the LibraryContext here.
            // See: https://wiki.corp.adobe.com/pages/viewpage.action?spaceKey=CCXCD&title=SAM.js+Bundle+Splitting+and+Optimizations
            this._LibraryContext = new LibraryContext(this, this._assetPool);
        }
        return this._LibraryContext;
    }
    async getCustomModelContext() {
        if (!this._CustomModelContext) {
            this._CustomModelContext = new CustomModelContext(this, this._assetPool);
        }
        return this._CustomModelContext;
    }
    makeObservable(createAtom) {
        return this._environment.mobx.register(createAtom);
    }
    /**
     * @TODO_Deprecation will be marked as deprecated on or after 2024-07-01 and renamed for internal use on or after
     * 2024-10-01
     */
    /**
     * @package -- for use in SAM.js only
     */
    get environment() {
        return this._environment;
    }
    updateConfig(config) {
        const keys = Object.keys(config);
        keys.forEach((key) => {
            const updatableKey = key;
            this._environment.setValue(updatableKey, config[updatableKey]);
        });
    }
}
__decorate([
    memoize
], SAMCore.prototype, "assetLinkManager", null);
__decorate([
    memoize
], SAMCore.prototype, "collaborationContext", null);
__decorate([
    memoize
], SAMCore.prototype, "frameioContext", null);
async function initializeSharedAssetModel(config, hooks = {}) {
    let samInstances = {};
    // SAM.js supports running in a node.js environment, so we can't assume that window is defined.
    if (typeof window !== 'undefined') {
        if (!window[$SAM_INSTANCE_MAP]) {
            window[$SAM_INSTANCE_MAP] = samInstances;
        }
        else {
            samInstances = window[$SAM_INSTANCE_MAP];
        }
    }
    // Check to see if any versions of SAM.js have already been loaded that are compatible with us (using semantic versioning, the
    // major version must match, and the minor.patch version must be later than or equal to the one we need)
    const version = (config._VERSION_OVERRIDE_FOR_TEST !== undefined && config._VERSION_OVERRIDE_FOR_TEST !== '')
        ? config._VERSION_OVERRIDE_FOR_TEST
        : packageJSON.version;
    const [major, minor, patch] = version.split('.').map(Number);
    const loadedVersions = Object.keys(samInstances);
    const compatibleLoadedVersion = loadedVersions.find(loadedVersion => {
        if (loadedVersion === version) {
            return true;
        }
        // If it's not an exact version match, we parse the versions and do a semantic version comparison - note that if
        // the major version is 0, it's assumed that all changes are breaking.
        const [loadedMajor, loadedMinor, loadedPatch] = loadedVersion.split('.').map(Number);
        return major !== 0 && loadedMajor === major && (loadedMinor > minor || (loadedMinor === minor && loadedPatch >= patch));
    });
    // If there's a compatible version, use it!
    if (compatibleLoadedVersion) {
        return samInstances[compatibleLoadedVersion];
    }
    // If not, create a new version (warn if this means there will be multiple SAM.js versions)
    if (loadedVersions.length > 0) {
        console.warn(`An instance of SAM.js already exists, but is not compatible with the requested version: ${version}. `
            + `Because of this, a new instance of SAM.js will be created. Loaded versions: ${loadedVersions.join(', ')}.`);
    }
    // Wait for host asset context, and patch SAM.cpp as needed
    // Presence of global acplModel tells us SAM.cpp JS bindings are present
    // @ts-ignore
    const acplModel = (typeof window !== 'undefined') && window.acplModel;
    const isPatchableVersion = () => {
        var _a, _b;
        // SAM.cpp 2.0.0-main.1301 is the earliest release that supports patching prototypes.
        const rootFolderProto = (_a = acplModel.applicationAssetContext) === null || _a === void 0 ? void 0 : _a.rootFolder.constructor.prototype;
        return Boolean(rootFolderProto) && ((_b = Object.getOwnPropertyDescriptor(rootFolderProto, 'name')) === null || _b === void 0 ? void 0 : _b.configurable) === true;
    };
    samInstances[version] = new Promise((resolve, reject) => {
        var _a;
        if (acplModel && !config._DISABLE_SAM_CPP) {
            // Either immediately resolve with existing applicationAssetContext, or wait for it
            Promise.race([
                (_a = acplModel.applicationAssetContext) !== null && _a !== void 0 ? _a : acplModel.waitForApplicationContextReady()
            ]).then(() => {
                try {
                    if (!isPatchableVersion()) {
                        resolve(new SAMCore(config, hooks));
                        return;
                    }
                    const core = new SAMCore(config, hooks);
                    // Patching should be done in this order, because the SAM.cpp prototypes aren't exposed in the global
                    // namespace, or on global `acplModel`, so the only way to get prototypes is to find an instance of
                    // the class and get a reference to `obj.constructor.prototype`. E.g., to patch AssetList, it needs
                    // to get it from `acplModel.applicationAssetContext.rootFolder.children.getList()`, so
                    // `AssetSource.getList()` cannot be patched yet.
                    patchAdobeAsset(core);
                    patchSpaceJS();
                    patchAssetList(core);
                    patchAssetSource(core);
                    patchAssetContext(core);
                    core._setAssetContext(acplModel.applicationAssetContext);
                    if (loadedVersions.length > 0) {
                        console.error('[initializeSharedAssetModel] SAM.cpp can only be observable by one'
                            + ' instance of SAMCore. Clients using SAM.js internal version ' + version + ' will not'
                            + ' see Mobx observables working. Clients on a host app should align on the same SAM.js'
                            + ' version before shipping to customers.');
                    }
                    resolve(core);
                }
                catch (e) {
                    console.error('[initializeSharedAssetModel] failed to patch SAM.cpp', e);
                    reject(e);
                }
            });
        }
        else {
            // No SAM.cpp present, use pure SAM.js implementation
            resolve(new SAMCore(config, hooks));
        }
    });
    // Do not cache failed initialization attempts
    try {
        await samInstances[version];
    }
    catch (e) {
        delete samInstances[version];
        throw e;
    }
    return samInstances[version];
}
/**
 * @deprecated Use {@link initializeSharedAssetModel} instead
 */
function createSharedAssetModel(config, hooks = {}) {
    console.warn(`createSharedAssetModel() is deprecated - call the asynchronous initializeSharedAssetModel() instead.`);
    return new SAMCore(config, hooks);
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const OrderFieldToComparator$5 = {
    created(a, b) {
        return compareValues(a.created, b.created);
    }
};
class AssetVersionOrder extends createItemOrderFactory(OrderFieldToComparator$5) {
}
AssetVersionOrder.default = new AssetVersionOrder('descending', 'created');

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const OrderFieldToComparator$4 = {
    name(a, b) {
        return compareStrings(a.name, b.name);
    },
    created(a, b) {
        let value;
        if (!a.created && !b.created) {
            value = 0;
        }
        else if (!a.created) {
            value = 1; // Undefined date should be sorted last in ascending order
        }
        else if (!b.created) {
            value = -1;
        }
        else {
            value = compareValues(a.created.getTime(), b.created.getTime());
        }
        if (value === 0) {
            // Within the same created, sort by name
            return compareStrings(a.name, b.name);
        }
        return value;
    },
};
class ClassroomOrder extends createItemOrderFactory(OrderFieldToComparator$4) {
}
ClassroomOrder.default = new ClassroomOrder('ascending', 'name');

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class MRUAdobeAssetOrder extends USSAdobeAssetOrder {
}
MRUAdobeAssetOrder.default = new MRUAdobeAssetOrder('descending', 'lastAccessed');

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class PublishedAdobeAssetOrder extends USSAdobeAssetOrder {
}
PublishedAdobeAssetOrder.default = new PublishedAdobeAssetOrder('descending', 'modified');

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const OrderFieldToComparator$3 = {
    id(a, b) {
        return compareStrings(a.id, b.id);
    }
};
class OrgRepositoryOrder extends createItemOrderFactory(OrderFieldToComparator$3) {
}
OrgRepositoryOrder.default = new OrgRepositoryOrder('ascending', 'id');

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const OrderFieldToComparator$2 = {
    name(a, b) {
        var _a, _b;
        return compareStrings((_a = a.name) !== null && _a !== void 0 ? _a : a.id, (_b = b.name) !== null && _b !== void 0 ? _b : a.id);
    }
};
class CollaboratorOrder extends createItemOrderFactory(OrderFieldToComparator$2) {
}
CollaboratorOrder.default = new CollaboratorOrder('ascending', 'name');

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const OrderFieldToComparator$1 = {
    email(a, b) {
        var _a, _b;
        return compareStrings((_a = a.email) !== null && _a !== void 0 ? _a : a.id, (_b = b.email) !== null && _b !== void 0 ? _b : a.id);
    }
};
class InvitedCollaboratorOrder extends createItemOrderFactory(OrderFieldToComparator$1) {
}
InvitedCollaboratorOrder.default = new InvitedCollaboratorOrder('ascending', 'email');

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
const OrderFieldToComparator = {
    // there is nothing in collaboratorListByUrn to order, this will keep order same
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    noop(a, b) {
        return 0;
    }
};
class CollaboratorsByAssetIDOrder extends createItemOrderFactory(OrderFieldToComparator) {
}
CollaboratorsByAssetIDOrder.default = new CollaboratorsByAssetIDOrder('ascending', 'noop');

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
class CustomModelFilter {
    constructor(listingOptions) {
        this._listingOptions = {};
        if (listingOptions) {
            this._listingOptions = listingOptions;
        }
    }
    get listingOptions() {
        return this._listingOptions;
    }
    get hash() {
        return `publishedState=${this._listingOptions.publishedState}`;
    }
    matches(item) {
        if (!item.isCustomModel) {
            return false;
        }
        if (this._listingOptions.publishedState === CustomModelPublishedState.ALL) {
            return true;
        }
        if (this._listingOptions.publishedState && (item.publishedState !== this._listingOptions.publishedState)) {
            return false;
        }
        return true;
    }
    /**
   * This method determines the commonality between the already present filter (other)
   * and the new filter to be applied (this). The point of this is to
   * see if the new filter (this) can build some, all, or none of it's items
   * from (other) filter.
   *
   * Subset: Means that ALL items for this filter can be built from other
   * Disjoint: No items for this filter can be built from other.
   * Unknown: Some items for this filter can be built from other.
   *
   * @note we should always determine disjoint cases first before other cases.
   * @note this = NEW FILTER, other = EXISTING FILTER
   *
   * @param other The existing filter that we are getting the relation to.
   * @returns { ItemFilterRelation }
   */
    getRelationToFilter(other) {
        if (this.listingOptions.publishedState !== other.listingOptions.publishedState && other.listingOptions.publishedState !== CustomModelPublishedState.ALL) {
            return 'disjoint';
        }
        if ((this._listingOptions.publishedState === other._listingOptions.publishedState) || other._listingOptions.publishedState === CustomModelPublishedState.ALL) {
            return 'subset';
        }
        return 'unknown';
    }
}

/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
/**
 * A Filter that extends AdobeAssetFilter and provides some USS Specific
 * filter options that don't apply to RAPI directory listings.
 */
class USSAdobeAssetFilter extends AdobeAssetFilter {
    constructor(options) {
        super(options);
        this._excludeInheritanceTypes = undefined;
        const { uploadedFrom, userId, excludeInheritanceTypes } = options !== null && options !== void 0 ? options : {};
        if (uploadedFrom !== undefined) {
            if (!uploadedFrom.length) {
                throw SAMError.create(SAMErrorCode.UNSUPPORTED, 'Empty uploadedFrom array is not currently supported.');
            }
            this._uploadedFrom = uploadedFrom;
        }
        if (userId) {
            this._userId = userId;
        }
        if (excludeInheritanceTypes) {
            this._excludeInheritanceTypes = new Set(excludeInheritanceTypes);
        }
    }
    get name() {
        return USSAssetFilterName;
    }
    get hash() {
        var _a;
        let hash = `${super.hash}:${this._uploadedFrom}:${this._userId}`;
        hash += ':excludeinheritancetypes=' + (((_a = this._excludeInheritanceTypes) === null || _a === void 0 ? void 0 : _a.size) ? `${[...this._excludeInheritanceTypes].sort().join(',')}` : 'none');
        return hash;
    }
    get uploadedFrom() {
        return this._uploadedFrom;
    }
    /**
     * Matches the provided userId to the value of storage:assignee.id in the service.
     *
     * @TODO_Deprecated [SAM-3813] will be removed in future releases in favor of using AdobeAsset.searchChildren searching
     * for items in users /cloud-content.
     *
     * {@link AdobeAsset.searchChildren()}
     */
    get userId() {
        return this._userId;
    }
    /**
     * Excludes assets that are inherited or are inheritable of the types included. For example including
     * "am.cc:inherited.cc:studentAssignment.repo:assetId" would exclude all assets that are within student
     * assignment directories. Including "am.cc:inheritable.cc:studentAssignment.repo:assetId" would exclude
     * all assets that ARE student assignment directories.
     */
    get excludeInheritanceTypes() {
        return this._excludeInheritanceTypes;
    }
    matches(item) {
        var _a, _b;
        if (this._userId && this._userId !== item.storageAssignee.id) {
            return false;
        }
        /** Filter       Item         createdFromUpload    Return value
         * -----------------------------------------------------------
         * undefined    *            *                    super.matches()
         * 'ALL'        'xyz'        true                 super.matches()
         * ['xyz']      'xyz'        true                 super.matches()
         * 'ALL'        *            false                false
         * ['xyz']      undefined    *                    false
         * ['xyz']      'pdq'        *                    false
         * ['xyz']      *            false                false
         *
         * */
        if (Array.isArray(this._uploadedFrom)
            && item.createdFromUpload
            && !this._uploadedFrom.includes((_a = item.createdByClientId) !== null && _a !== void 0 ? _a : '')) {
            return false;
        }
        if ((this._uploadedFrom === 'ALL'
            || Array.isArray(this._uploadedFrom))
            && !item.createdFromUpload) {
            return false;
        }
        if (((_b = this._excludeInheritanceTypes) === null || _b === void 0 ? void 0 : _b.has(FetchFields.StudentAssignmentAssetId)) && item.isChildOfStudentAssignment) {
            return false;
        }
        return super.matches(item);
    }
    getRelationToFilter(existingFilter) {
        if (existingFilter.name !== USSAssetFilterName) {
            // Currently we don't support switching between the AdobeAssetFilter and the
            // USSAssetFilter.
            oneTimeWarning('USSAdobeAssetFilter', 'Cannot switch between an AdobeAssetFilter and a USSAdobeAssetFilter');
            return 'disjoint';
        }
        // If they both have ID's and they both are not equal we can assume
        // they are disjointed.
        if (this._userId && existingFilter.userId && this._userId !== existingFilter.userId) {
            return 'disjoint';
        }
        if (this._excludeInheritanceTypes && existingFilter._excludeInheritanceTypes) {
            // Convert to a format that can be easily compared
            const thisFilterExcludeInheritanceTypes = [...this._excludeInheritanceTypes].sort();
            const existingFilterExcludeInheritanceTypes = [...existingFilter._excludeInheritanceTypes].sort();
            if (thisFilterExcludeInheritanceTypes !== existingFilterExcludeInheritanceTypes) {
                return 'disjoint';
            }
        }
        if ((this.excludeInheritanceTypes && !existingFilter.excludeInheritanceTypes) || (!this.excludeInheritanceTypes && existingFilter.excludeInheritanceTypes)) {
            return 'unknown';
        }
        const isFiltersArray = Array.isArray(this.uploadedFrom)
            && Array.isArray(existingFilter.uploadedFrom);
        // If both the new filter and the existing filter are arrays
        // meaning they are requesting a specific applications client id
        // and they are both different, this means the list is disjoint because
        // if let's say the existing filter is app 'xyz' and the new one is 'zzz'
        // they cannot share any items.
        if (isFiltersArray) {
            const arrayRelationship = compareFilterArrayValues(this.uploadedFrom, existingFilter.uploadedFrom);
            if (arrayRelationship === 'none') {
                return 'disjoint';
            }
            // If there are some items that match between the two array values
            // and the new filter has more filer values that means there are
            // some items that could be borrowed from the existing list.
            if (arrayRelationship === 'some'
                && this.uploadedFrom.length >= existingFilter.uploadedFrom.length) {
                return 'unknown';
            }
        }
        /**
         * If the new filter has uploadedFrom undefined and the existing filter
         * is defined that means we could possibly borrow some items from the new list.
         */
        if (!this.uploadedFrom && existingFilter.uploadedFrom) {
            return 'unknown';
        }
        if (this.uploadedFrom === 'ALL' && Array.isArray(existingFilter.uploadedFrom)) {
            return 'unknown';
        }
        if (!this.userId && existingFilter.userId) {
            return 'unknown';
        }
        // Call into super to handle all implicit filter cases.
        return super.getRelationToFilter(existingFilter);
    }
}

export { AdobeAssetFilter, USSAdobeAssetOrder as AdobeAssetGlobalSearchOrder, AdobeAssetOrder, AssetListStatus, AssetSyncStatus, AssetSyncSuspensionReason, AssetType, AssetVersion, AssetVersionFilter, AssetVersionOrder, AssignmentFilter, AssignmentOrder, AssignmentStatus, AssignmentTaskIDEnums, AvatarSizes, CHANGE, COLLABORATOR_CHANGED, CanCreateProjectReason, ClassroomOrder, CloudSyncStatus, CollaboratorOrder, CollaboratorUpdater, CollaboratorsByAssetIDOrder, Comment, CommentContext, CommitStatus, CustomModelFilter, CustomModelOrder, CustomModelPublishedState, Env, FRAMEIO_REVIEW_LINKS_CHANGE, FrameioContext, Gradient, IFrameioAuthenticationStatus, ITEM_LIST_STATE_CHANGED, ImportSourceType, InvitedCollaboratorOrder, ItemBase, LIBRARY_DATA_CHANGED, LIST_CHANGED, LIST_FULLY_LOADED, LearningManagementSystems, LogLevel, MESSAGE_LOGGED, MRUAdobeAssetOrder, NetworkStatus, OrderDirection, OrderField, OrgRepositoryOrder, OrgRepositoryUpdater, Permission, ProjectFilter, ProjectLeaveReason, ProjectOrder, ProjectViews, USSAdobeAssetOrder as ProjectsSearchOrder, PublishedAdobeAssetOrder, RESTRICT_EDIT_CHANGED, USSAdobeAssetOrder as RecentAdobeAssetOrder, RenditionSize, RenditionStatus, RenditionType, RosterImportState, SAMError, SAMErrorCode, SAMErrorSubCode, STATE_CHANGED, SWYAdobeAssetOrder, USSAdobeAssetOrder as SearchAdobeAssetOrder, SpaceFilter, SpaceOrder, SpaceUpdater, StudentAssignmentFile, StudentAssignmentOrder, StudentAssignmentStatus, SubjectTypes, Team, TeamFilter, TeamOrder, USSAdobeAssetFilter, createSharedAssetModel, getURLFromLinks, hasProperty, initializeSharedAssetModel, parseAsyncResponse, samJsUuid };
//# sourceMappingURL=index.js.map
